/* ═══════════════════════════════════════════════════════
   MXM MUSIC — FRONTEND APPLICATION v2.0
   ═══════════════════════════════════════════════════════ */

'use strict';

/** Класс для CSS: системный WebView в APK ≠ Chrome; лечим touch/скролл и схлопывание карточек. */
function isMxMDesktopApp() {
  try {
    return !!(typeof window !== 'undefined' && window.mxmDesktop && window.mxmDesktop.isDesktop);
  } catch (_) {
    return false;
  }
}

function isMxMNativeShell() {
  try {
    return !!(
      window.Capacitor &&
      typeof window.Capacitor.isNativePlatform === 'function' &&
      window.Capacitor.isNativePlatform()
    );
  } catch (_) {
    return false;
  }
}

/** Десктопный сайт в браузере (>900px): те же стили fullscreen, что в Electron-приложении. */
function refreshDesktopWebClass() {
  try {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    const isDesktopWeb =
      window.innerWidth > 900 && !isMxMDesktopApp() && !isMxMNativeShell();
    document.documentElement.classList.toggle('mxm-desktop-web', isDesktopWeb);
  } catch (_) {}
}

function refreshNativeShellClass() {
  try {
    if (typeof document === 'undefined') return;
    if (isMxMDesktopApp()) {
      document.documentElement.classList.add('mxm-electron-shell');
      document.documentElement.classList.add('mxm-desktop-shell');
      document.documentElement.classList.remove('mxm-desktop-web');
    }
    if (isMxMNativeShell()) {
      document.documentElement.classList.add('mxm-native-shell');
    }
    refreshDesktopWebClass();
  } catch (_) {}
}
refreshNativeShellClass();
document.addEventListener('DOMContentLoaded', refreshNativeShellClass);
window.addEventListener('resize', refreshDesktopWebClass, { passive: true });

// ─── STATE ────────────────────────────────────────────────────────────────────
const state = {
  user: null,
  token: localStorage.getItem('mxm_token'),
  queue: [],
  queueIndex: -1,
  prevTrackLocked: false,
  shuffle: false,
  repeat: 'none',       // 'none' | 'all' | 'one'
  currentTrack: null,
  favorites: { trackIds: [], albumIds: [], artistIds: [], dislikedTrackIds: [] },
  view: 'home',
  recentlyPlayed: [],
  /** Первичные artistId/name для лёгкого anti-repeat при shuffle */
  recentQueueArtists: [],
  // User playlists
  mePlaylists: [],
  renderPlaylistAddButtons: false,
  /** Подпись источника очереди для мини/полноэкранного плеера ({ title }) */
  queuePlayingFrom: null,
  /** Токен шаринга UPL (чтобы копирование ссылки не теряло user gesture после await). */
  uplShareTokenById: {},
  /** Прямой эфир MXM Radio (https://mxmfm.ru/radio/stream). */
  radioActive: false,
  /** Authoritative 1:1 Listen Together session from the server. */
  listenSession: null
};

/** Same-origin прокси (server.js → mxmfm.ru/radio/stream) — надёжнее для Web Audio / Electron. */
const MXM_RADIO_STREAM_URL = '/api/radio/stream';
const MXM_RADIO_TRACK_ID = '__mxm_radio__';
/** Подстройка тихого входного потока радио (до компрессора; ползунок громкости — отдельно). */
const MXM_RADIO_INPUT_GAIN = 1.5;

function isRadioPlaybackActive() {
  return !!state.radioActive || state.currentTrack?.id === MXM_RADIO_TRACK_ID;
}

const lyricsCache = new Map();  // ← ДОБАВТЬ ЭТУ СТРОКУ

let autocompleteArtists = [];
let autocompleteAlbums = [];
let _searchTimer = null;
let audioCtx = null;
let source = null;
let compressor = null;
let gainNode = null;
let autoGainNode = null;
let analyserNode = null;
let loudnessMonitorTimer = null;
let currentAutoGain = 1;
/** iOS/mobile в фоне: play() после смены src на событии `ended` часто режется; переключаем очередь чуть до конца, пока трек ещё играет */
let _lastEarlyHiddenQueueAdvanceAt = 0;
const EARLY_HIDDEN_ADVANCE_REMAINING_S = 0.28;
const EARLY_ADVANCE_ENDED_GUARD_MS = 750;
const TARGET_TRACK_DBFS = -18;
/** Нижняя граница автогейна: не прижимаем громкие треки — только поднимаем тихие к общему уровню. */
const AUTO_GAIN_FLOOR = 1;
const AUTO_GAIN_MAX = 2.05;
const TRACK_MENU_SHEET_BREAKPOINT = 900;
const QUEUE_SHEET_DESKTOP_BREAKPOINT = 680;
const TRACK_LOUDNESS_CACHE_KEY = 'mxm_track_loudness_cache';
const MAX_TRACK_LOUDNESS_ENTRIES = 250;
let viewportSyncBound = false;
let _iosStandaloneViewportLocked = false;
let _keyboardFocusGuardUntil = 0;
let _keyboardOverlayHoldUntil = 0;
let _keyboardClosingUntil = 0;
const trackLoudnessCache = new Map();
const scriptLoadCache = new Map();
const MAX_COLOR_CACHE_ENTRIES = 180;
let activeViewToken = 0;
let perfMonitoringEnabled = false;
let perfViewStartMs = 0;
let perfViewName = '';
const AVATAR_REV_KEY = 'mxm_avatar_rev';

/** Иконка «поделиться» — share-3.svg из /public/icons/ */
const MXM_SHARE_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13 4v4c-6.575 1.028 -9.02 6.788 -10 12c-.037 .206 5.384 -5.962 10 -6v4l8 -7l-8 -7"/></svg>';
const MXM_PUBLIC_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>';

/** Поиск и сообщения — как в /public/icons/search.svg и message-circle.svg */
const MXM_SEARCH_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" /><path d="M21 21l-6 -6" /></svg>';

const MXM_MESSAGE_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 20l1.3 -3.9c-2.324 -3.437 -1.426 -7.872 2.1 -10.374c3.526 -2.501 8.59 -2.296 11.845 .48c3.255 2.777 3.695 7.266 1.029 10.501c-2.666 3.235 -7.615 4.215 -11.574 2.293l-4.7 1" /></svg>';

// Queue map — stores named queues to avoid large inline JSON
const _queues = new Map();
let _queueCounter = 0;
let previousGradient = null;
let gradientChangeTimer = null;
let radioUpdateTimer = null;
let radioPlaybackPollTimer = null;
let _radioNowPlayingRaw = '';
let _radioPlayerUiSig = '';
let _radioPollInFlight = false;
let _radioTracksMapLoadPromise = null;
const RADIO_NOW_PLAYING_POLL_MS = 4000;
const RADIO_HOME_BADGE_POLL_MS = 20000;
let tracksMap = null; // для быстрого поиска треков
let titleTracksMap = null; // для поиска по названию
let allArtists = [];            // кэш всех артистов
let homeArtistsTimer = null;    // таймер обновления
/** Desktop-приложение: зафиксированные 6 артистов на главной до смены вкладки */
let desktopHomeArtistsPinned = null;
/** Дебаунс пересчёта сетки главной (альбомы + число исполнителей) при resize */
let _homeLayoutResizeTimer = null;
let homePlaylistsAll = [];
let homePopularAlbumsAll = [];
let gradientSwapTimer = null;  // ← ДОБАВТЬ эту строку
/** Данные открытого профиля (скрытие ссылки на профиль на странице этого профиля). */
let lastProfileViewData = null;
const navHistoryStack = [];
let suppressNavStackPush = false;
/** Публичная часть URL собеседника (логин или uuid), пока открыт тред */
let _mxmChatPeerPublicRef = null;
/** Не перерисовывать DOM очереди во время drag-reorder (избегает мерцания на мобильных). */
let _queueReorderDragging = false;

function syncPlayerClickBlockState() {
  const radio = isRadioPlaybackActive();
  document.documentElement.classList.toggle('player-radio-mode', radio);
  document.querySelectorAll('#progress-bar, #fullscreen-progress-bar').forEach((el) => {
    el.classList.toggle('is-disabled', radio);
    el.setAttribute('aria-disabled', radio ? 'true' : 'false');
  });
  const fs = document.getElementById('fullscreen-player');
  if (fs) fs.classList.toggle('fullscreen-radio-layout', radio);
}

function storeQueue(tracks) {
  const key = 'q' + (++_queueCounter);
  _queues.set(key, tracks);
  return key;
}
function getQueue(key) { return _queues.get(key) || []; }

function isPrevTrackLocked() {
  return !!state.prevTrackLocked && Number(state.queueIndex) <= 0;
}

function syncPrevTrackButtons() {
  const radio = isRadioPlaybackActive();
  const prevDisabled = radio || !state.currentTrack || isPrevTrackLocked();
  const nextDisabled = radio || !state.currentTrack;
  document.querySelectorAll('#player .ctrl-prev, #fullscreen-player .ctrl-prev').forEach(btn => {
    btn.disabled = prevDisabled;
    btn.classList.toggle('is-disabled', prevDisabled);
    btn.setAttribute('aria-disabled', prevDisabled ? 'true' : 'false');
  });
  document.querySelectorAll('#player .ctrl-next, #fullscreen-player .ctrl-next').forEach(btn => {
    btn.disabled = nextDisabled;
    btn.classList.toggle('is-disabled', nextDisabled);
    btn.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
  });
}

function setQueueState(tracks, index = 0, options = {}) {
  const raw = Array.isArray(tracks) ? tracks : [];
  let idx = Number.isFinite(index) ? index : -1;
  let arr = raw;
  if (!options.preserveBefore && idx > 0 && raw.length > idx) {
    arr = raw.slice(idx);
    idx = 0;
  }
  state.queue = arr;
  state.queueIndex = idx;
  state.prevTrackLocked = !!options.lockPrevAtStart;
  syncPrevTrackButtons();
}

function setQueuePlayingFromLabel(title) {
  const t = title != null ? String(title).trim() : '';
  state.queuePlayingFrom = t ? { title: t } : null;
  updateQueueSourceUI();
}

function updateQueueSourceUI() {
  const label = state.queuePlayingFrom?.title ? String(state.queuePlayingFrom.title).trim() : '';
  const mini = $('player-queue-source');
  if (!mini) return;
  if (!label) {
    mini.textContent = '';
    mini.classList.add('hidden');
    return;
  }
  mini.textContent = label;
  mini.classList.remove('hidden');
}

async function resolveTrackForMenuPortal(portal) {
  if (!portal) return null;
  const id = portal.dataset.trackId;
  if (!id) return null;
  const qk = portal.dataset.qKey;
  const qi = portal.dataset.qIdx;
  if (qk && qi !== undefined && qi !== '') {
    const idx = parseInt(qi, 10);
    if (Number.isFinite(idx)) {
      const q = getQueue(qk);
      if (q[idx]) return { ...q[idx] };
    }
  }
  try {
    return await GET(`/api/tracks/${id}`);
  } catch {
    return null;
  }
}

function addTrackPlayNext(track) {
  if (!track || !track.id) return;
  const t = { ...track };
  if (!state.queue.length) {
    setQueueState([t], 0);
    playTrack(t);
    toast('Воспроизведение', 'success');
    return;
  }
  let ins = Number.isFinite(state.queueIndex) && state.queueIndex >= 0
    ? state.queueIndex + 1
    : state.queue.length;
  ins = Math.max(0, Math.min(ins, state.queue.length));
  state.queue.splice(ins, 0, t);
  if (isQueueSheetOpen()) renderQueueSheet();
  toast('Будет играть далее', 'success');
}
window.addTrackPlayNext = addTrackPlayNext;

// ─── DOM ──────────────────────────────────────────────────────────────────────
// Global helper — must be on window for inline onclick handlers
window.$ = id => document.getElementById(id);
const $ = window.$;
const audio = $('audio-el');
const STORAGE_LAST_TRACK_ID = 'mxm_last_track_id'; 
const gradientCache = new Map();
const PERF_STATS = {
  viewLoads: [],
  longTasks: [],
  navStart: typeof performance !== 'undefined' ? performance.now() : Date.now(),
  tier: 'standard'
};
window.__mxmPerf = PERF_STATS;

const MOBILE_APP_GESTURE_GUARD_SELECTOR = [
  'button',
  '[role="button"]',
  '.track-row',
  '.queue-sheet-row',
  '.featured-card',
  '.album-card',
  '.artist-card',
  '.playlist-card',
  '.mobile-nav-item',
  '.nav-item',
  '#player',
  '#fullscreen-player .fullscreen-cover',
  '#fullscreen-player .fullscreen-controls',
  '.queue-sheet-header',
  '.modal-close'
].join(',');

function shouldGuardMobileBrowserGesture(target) {
  if (!isMobile() || !(target instanceof Element)) return false;
  if (target.closest('input, textarea, select, [contenteditable="true"]')) return false;
  if (target.closest('.lyrics-text, .fullscreen-lyrics, .mxm-chat-bubble, .support-message-text')) return false;
  return !!target.closest(MOBILE_APP_GESTURE_GUARD_SELECTOR);
}

document.addEventListener('selectstart', e => {
  if (shouldGuardMobileBrowserGesture(e.target)) e.preventDefault();
});

document.addEventListener('dragstart', e => {
  if (!isMobile()) return;
  const target = e.target;
  if (!(target instanceof Element)) return;
  if (target.closest('.queue-sheet-row-drag')) return;
  if (target.matches('img') || shouldGuardMobileBrowserGesture(target)) {
    e.preventDefault();
  }
});

document.addEventListener('contextmenu', e => {
  if (shouldGuardMobileBrowserGesture(e.target)) e.preventDefault();
});

document.addEventListener('dblclick', e => {
  if (shouldGuardMobileBrowserGesture(e.target)) e.preventDefault();
});

function isIosLikeDevice() {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const touchPoints = Number(navigator.maxTouchPoints || 0);
  return /iPad|iPhone|iPod/i.test(ua)
    || /iPad|iPhone|iPod/i.test(platform)
    || (platform === 'MacIntel' && touchPoints > 1);
}

/** iOS: фон + Web Audio с media element ломает фон. Android Capacitor: UI на https://localhost, трек на проде — без CORS Web Audio даёт тишину. */
function shouldUseNativeBackgroundAudioPath() {
  return isIosLikeDevice() || isCapacitorNativeApp();
}

/** Те же условия, что у обработчика `ended` перед вызовом playNext() (очередь, не похожие треки) */
function canAutoAdvanceQueueFromEndedRule() {
  if (state.repeat === 'one') return false;
  if (state.queue.length === 0) return false;
  return state.repeat === 'all' || state.queueIndex < state.queue.length - 1;
}

function tryEarlyHiddenQueueAdvance() {
  if (!document.hidden) return;
  if (!isMobile() && !shouldUseNativeBackgroundAudioPath()) return;
  if (audio.paused || !state.currentTrack) return;
  if (!audio.duration || !Number.isFinite(audio.duration)) return;
  if (!canAutoAdvanceQueueFromEndedRule()) return;

  const remaining = audio.duration - audio.currentTime;
  if (remaining > EARLY_HIDDEN_ADVANCE_REMAINING_S) return;

  _lastEarlyHiddenQueueAdvanceAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  playNext();
}

// ─── Capacitor: UI с https://localhost — API/медиа только с прода (иначе Capacitor ищет файлы в APK) ─────
const MXM_PRODUCTION_ORIGIN = 'https://music.mxmfm.ru';
const MXM_PRODUCTION_ORIGIN_TRIM = MXM_PRODUCTION_ORIGIN.replace(/\/$/, '');

function isCapacitorNativeApp() {
  try {
    return !!(window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

/** WebView (в т.ч. Xiaomi/MIUI): sync-копирование в том же клике, когда async Clipboard API недоступен. */
function copyPlainTextViaExecCommand(text) {
  try {
    const s = String(text ?? '');
    if (!s) return false;
    const ta = document.createElement('textarea');
    ta.value = s;
    ta.dataset.mxmClipboardTemp = '1';
    /* iOS: readonly мешает copy; 16px — меньше авто-зума при фокусе */
    ta.style.cssText =
      'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;font-size:16px;padding:0;border:0;margin:0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, s.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return !!ok;
  } catch (_) {
    return false;
  }
}

async function copyPlainTextToClipboard(text) {
  const s = String(text ?? '');
  if (!s) return false;
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(s);
      return true;
    }
  } catch (_) {}
  return copyPlainTextViaExecCommand(s);
}

function isLocalWebViewHost() {
  const h = String(window.location?.hostname || '').toLowerCase();
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '::1' ||
    h === '[::1]' ||
    h === ''
  );
}

/** Только nodemon/express на :3000 — медиа с того же хоста. Всё остальное на loopback = встроенный UI (Capacitor) → прод. */
function isLocalExpressDevOrigin() {
  try {
    const loc = window.location;
    if (!loc) return false;
    if (String(loc.protocol || '').toLowerCase() !== 'http:') return false;
    const h = String(loc.hostname || '').toLowerCase();
    if (h !== 'localhost' && h !== '127.0.0.1') return false;
    return String(loc.port || '') === '3000';
  } catch (_) {
    return false;
  }
}

/**
 * WebView Capacitor: origin https://localhost — без редиректа на прод Capacitor ищет /uploads в APK → ошибка asset URL.
 * Не полагаемся только на Capacitor.isNativePlatform() (на части сборок/тайминга даёт false).
 */
function shouldUseProductionMediaHost() {
  if (typeof window === 'undefined') return false;
  if (isLocalExpressDevOrigin()) return false;
  return isLocalWebViewHost();
}

function getAppBackendOrigin() {
  // В APK origin страницы = https://localhost — относительные /uploads в <img src> иначе резолвятся в localhost.
  if (isCapacitorNativeApp()) return MXM_PRODUCTION_ORIGIN_TRIM;
  if (shouldUseProductionMediaHost()) return MXM_PRODUCTION_ORIGIN_TRIM;
  return String(window.location.origin || '').replace(/\/$/, '');
}

/** https://localhost/uploads/... из API/кэша → прод (иначе Capacitor: Unable to open asset URL). */
function rewriteWrongLocalhostAssetUrl(url) {
  const s = String(url || '').trim();
  if (!s || !/^https?:\/\//i.test(s)) return s;
  if (!isCapacitorNativeApp() && !shouldUseProductionMediaHost()) return s;
  try {
    const u = new URL(s);
    const h = u.hostname.toLowerCase();
    if (h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1') {
      return MXM_PRODUCTION_ORIGIN_TRIM + u.pathname + u.search + u.hash;
    }
  } catch (_) {}
  return s;
}

function resolveStreamUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return rewriteWrongLocalhostAssetUrl(raw);
  return resolveMediaUrl(raw);
}

function getPublicSiteBase() {
  return getAppBackendOrigin();
}

function resolveClientRequestUrl(url) {
  const u = String(url || '').trim();
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  const path = u.startsWith('/') ? u : `/${u.replace(/^\.\//, '')}`;
  const base = getAppBackendOrigin();
  return `${base}${path}`;
}

function resolveMediaUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (/^(data:|blob:)/i.test(raw)) return raw;
  if (/^https?:\/\//i.test(raw)) return rewriteWrongLocalhostAssetUrl(raw);
  if (raw.startsWith('//')) return `${window.location.protocol}${raw}`;
  const origin = getAppBackendOrigin();
  if (raw.startsWith('/')) return `${origin}${raw}`;
  return `${origin}/${raw.replace(/^\.?\//, '')}`;
}

function appendCacheKey(url, key) {
  const raw = String(url || '').trim();
  if (!raw || !key) return raw;
  return `${raw}${raw.includes('?') ? '&' : '?'}v=${encodeURIComponent(key)}`;
}

function cacheAvatarUrl(url) {
  const resolved = resolveMediaUrl(url);
  if (!resolved) return '';
  localStorage.setItem('mxm_avatar_url', resolved);
  localStorage.setItem(AVATAR_REV_KEY, String(Date.now()));
  return resolved;
}

function getAvatarDisplayUrl(url) {
  const resolved = resolveMediaUrl(url || localStorage.getItem('mxm_avatar_url'));
  if (!resolved) return '';
  return appendCacheKey(resolved, localStorage.getItem(AVATAR_REV_KEY) || '1');
}

function renderUserAvatar() {
  const avatarEl = $('user-avatar');
  if (!avatarEl) return;
  const username = String(state.user?.username || '').trim();
  const fallbackLetter = (username.charAt(0) || '?').toUpperCase();
  const avatarUrl = getAvatarDisplayUrl(state.user?.avatarUrl);
  avatarEl.replaceChildren();

  if (avatarUrl) {
    avatarEl.classList.add('has-image');
    const img = document.createElement('img');
    img.src = avatarUrl;
    img.alt = username || 'avatar';
    img.loading = 'eager';
    img.decoding = 'async';
    img.referrerPolicy = 'no-referrer';
    img.onerror = () => {
      avatarEl.classList.remove('has-image');
      avatarEl.textContent = fallbackLetter;
    };
    avatarEl.appendChild(img);
    return;
  }

  avatarEl.classList.remove('has-image');
  avatarEl.textContent = fallbackLetter;
}

function markPerf(name, duration, extra) {
  if (!perfMonitoringEnabled) return;
  const entry = { name, duration: Number(duration.toFixed(1)), ts: Date.now(), ...(extra || {}) };
  if (PERF_STATS.viewLoads.length > 120) PERF_STATS.viewLoads.shift();
  PERF_STATS.viewLoads.push(entry);
}

function enablePerfTier() {
  const mem = Number(navigator.deviceMemory || 0);
  const cores = Number(navigator.hardwareConcurrency || 0);
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const isLowPower =
    saveData ||
    (mem > 0 && mem <= 4) ||
    (cores > 0 && cores <= 4) ||
    (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  PERF_STATS.tier = isLowPower ? 'low' : 'standard';
  document.documentElement.classList.toggle('perf-low', isLowPower);
}

function initPerfMonitoring() {
  perfMonitoringEnabled = localStorage.getItem('mxm_perf_monitor') === '1';
  enablePerfTier();
  if (!perfMonitoringEnabled || typeof PerformanceObserver === 'undefined') return;

  try {
    const obs = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (e.duration < 40) continue;
        PERF_STATS.longTasks.push({ duration: Number(e.duration.toFixed(1)), ts: Date.now() });
        if (PERF_STATS.longTasks.length > 80) PERF_STATS.longTasks.shift();
      }
    });
    obs.observe({ type: 'longtask', buffered: true });
  } catch (_) {}
}

function loadExternalScript(src) {
  if (scriptLoadCache.has(src)) return scriptLoadCache.get(src);
  const p = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  scriptLoadCache.set(src, p);
  return p;
}

async function ensureColorToolsLoaded() {
  if (window.Vibrant || window.ColorThief) return true;
  // На слабых девайсах не тратим CPU на динамическую загрузку и анализ обложки.
  if (document.documentElement.classList.contains('perf-low')) return false;
  try {
    await Promise.all([
      loadExternalScript('https://unpkg.com/node-vibrant@3.1.6/dist/vibrant.min.js'),
      loadExternalScript('https://cdnjs.cloudflare.com/ajax/libs/color-thief/2.3.2/color-thief.umd.js')
    ]);
    return true;
  } catch {
    return false;
  }
}

// ─── API ──────────────────────────────────────────────────────────────────────
function parseApiResponseBody(res, resolvedUrl, text) {
  const trimmed = String(text || '').trimStart();
  if (
    trimmed.startsWith('<!DOCTYPE') ||
    trimmed.startsWith('<!doctype') ||
    /^<\s*html[\s>]/i.test(trimmed)
  ) {
    const st = res.status || 0;
    let hint =
      st === 521 || st === 522 || st === 523
        ? ' (часто Cloudflare: origin недоступен)'
        : st >= 500
          ? ' (часто 502/503: Node упал, таймаут nginx или HTML error_page)'
          : '';
    if (st === 200) {
      hint +=
        ' Частая причина: в DevTools у запроса видно «(from disk cache)» и content-type: text/html — ' +
        'браузер отдал старую закэшированную страницу по этому URL. Обновите сайт с отключённым кэшем (Ctrl+Shift+R) ' +
        'или очистите данные для music.mxmfm.ru; в новой версии фронта для API включён cache: no-store.';
    }
    throw new Error(
      `Сервер вернул HTML вместо JSON (HTTP ${st || '?'}). URL: ${resolvedUrl}.${hint} ` +
        'Проверьте вкладку Сеть в DevTools (статус и тело ответа). ' +
        'Nginx: location ^~ /api/ → proxy_pass http://127.0.0.1:3000 и proxy_intercept_errors off.'
    );
  }
  if (!trimmed) return {};
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(
      res.ok
        ? `Ответ не JSON (${resolvedUrl})`
        : `Ошибка ${res.status}: ${resolvedUrl}`
    );
  }
}

function shouldUseCdnMethodTunnel() {
  if (typeof window === 'undefined') return false;
  return false;
}

async function apiFetch(method, url, body, isForm = false) {
  const upper = String(method || 'GET').toUpperCase();
  const httpMethod = upper;
  // Иначе Chrome может отдать старый ответ из дискового кэша (например text/html вместо JSON для GET /api/...).
  const opts = { method: httpMethod, headers: {}, cache: 'no-store', credentials: 'include' };
  if (state.token) opts.headers['Authorization'] = `Bearer ${state.token}`;
  if (body) {
    if (isForm) {
      opts.body = body;
    } else {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
  }
  const resolved = resolveClientRequestUrl(url);
  const res = await fetch(resolved, opts);
  const text = await res.text();
  const data = parseApiResponseBody(res, resolved, text);
  if (!res.ok) {
    const msg = data.error || data.message || `Ошибка запроса (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    const rts = data.retryAfterSec;
    if (rts != null && Number.isFinite(Number(rts))) err.retryAfterSec = Number(rts);
    const rr = data.resendsRemaining;
    if (rr != null && Number.isFinite(Number(rr))) err.resendsRemaining = Number(rr);
    throw err;
  }
  return data;
}

function isAuthFailureStatus(status) {
  return status === 401 || status === 403 || status === 404;
}

function clearAuthSessionLocal({ clearCookie = false } = {}) {
  state.token = null;
  state.user = null;
  localStorage.removeItem('mxm_token');
  localStorage.removeItem('mxm_avatar_url');
  localStorage.removeItem(AVATAR_REV_KEY);
  localStorage.removeItem(STORAGE_LAST_TRACK_ID);
  if (clearCookie) {
    try {
      fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(function () {});
    } catch (_) {}
  }
}

function applyAuthUser(me, token) {
  const syncToken = me?.syncToken || token || null;
  if (syncToken) {
    state.token = syncToken;
    localStorage.setItem('mxm_token', syncToken);
  }
  if (me && typeof me === 'object') {
    const { syncToken: _drop, token: _dropToken, ...user } = me;
    state.user = user;
  }
  if (state.user?.avatarUrl) state.user.avatarUrl = cacheAvatarUrl(state.user.avatarUrl);
  updateUserUI();
  loadFavorites();
  if (typeof mxmChatUpdateUnreadBadge === 'function') mxmChatUpdateUnreadBadge();
  if (typeof mxmChatConnectWs === 'function') mxmChatConnectWs();
}

async function fetchAuthMe({ useBearer = true } = {}) {
  const url = resolveClientRequestUrl('/api/auth/me');
  const headers = {};
  if (useBearer && state.token) headers.Authorization = `Bearer ${state.token}`;
  const res = await fetch(url, { headers, credentials: 'include', cache: 'no-store' });
  const text = await res.text();
  const data = parseApiResponseBody(res, url, text);
  if (!res.ok) {
    const err = new Error(data.error || data.message || `Ошибка запроса (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

/** Проверка сессии при старте / после сброса токена в другой вкладке. Не сносит localStorage при сетевых сбоях. */
async function bootstrapAuthFromServer() {
  const stored = localStorage.getItem('mxm_token');
  if (stored) state.token = stored;

  const maxAttempts = 2;
  if (state.token) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const me = await fetchAuthMe({ useBearer: true });
        applyAuthUser(me);
        void POST('/api/auth/sso/sync-cookie').catch(() => {});
        return true;
      } catch (e) {
        if (isAuthFailureStatus(e.status)) break;
        if (attempt < maxAttempts - 1) {
          await new Promise((r) => setTimeout(r, 400));
          continue;
        }
      }
    }
  }

  try {
    const me = await fetchAuthMe({ useBearer: false });
    applyAuthUser(me);
    void POST('/api/auth/sso/sync-cookie').catch(() => {});
    return true;
  } catch (e) {
    if (!isAuthFailureStatus(e.status)) return false;
    clearAuthSessionLocal({ clearCookie: true });
    return false;
  }
}

function bindAuthStorageSync() {
  if (bindAuthStorageSync._bound) return;
  bindAuthStorageSync._bound = true;
  window.addEventListener('storage', (e) => {
    if (e.key !== 'mxm_token') return;
    if (e.newValue) {
      state.token = e.newValue;
      if (!state.user) void bootstrapAuthFromServer();
      return;
    }
    if (!e.oldValue) return;
    state.token = null;
    void bootstrapAuthFromServer().then((ok) => {
      if (!ok) updateUserUI();
    });
  });
}
const GET  = url        => apiFetch('GET',    url);
const POST = (url, b)   => apiFetch('POST',   url, b);
const PUT = (url, b)    => apiFetch('PUT',    url, b);
const PATCH = (url, b)   => apiFetch('PATCH',  url, b);
const DEL  = url        => apiFetch('DELETE', url);
const FORM = (url, f)   => apiFetch('POST',   url, f, true);

/** Последний трек в БД — при старте воспроизведения (не путать с засчитыванием прослушивания ~50%). */
function syncLastPlayedTrackToServer() {
  const tr = state.currentTrack;
  if (!state.user || !tr?.id || !tr.audioUrl) return;
  PUT('/api/me/last-played', { trackId: tr.id }).catch(() => {});
}
let normTracksMap = null; // Map для нормализованных ключей

function formatSocialCount(n) {
  const value = Number(n) || 0;
  if (value > 9999) return `${Math.round(value / 1000)}K`;
  if (value > 999) return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(value);
}

function formatCommentDate(iso) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const MXM_ARTISTS_WITH_COMMA = [
  'Tyler, The Creator',
  'Earth, Wind & Fire',
  'Crosby, Stills, Nash & Young',
  'Crosby, Stills & Nash',
  'Blood, Sweat & Tears',
  'Emerson, Lake & Palmer',
  'Hamilton, Joe Frank & Reynolds',
  'Bell, Book & Candle'
];

function splitArtistNamesClient(input) {
  let s = String(input || '').trim();
  if (!s) return [];
  const placeholders = [];
  const protectedNames = MXM_ARTISTS_WITH_COMMA.slice().sort((a, b) => b.length - a.length);
  for (const name of protectedNames) {
    const re = new RegExp(name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig');
    s = s.replace(re, (match) => {
      const idx = placeholders.length;
      const canon =
        MXM_ARTISTS_WITH_COMMA.find((n) => n.toLowerCase() === match.toLowerCase()) || match;
      placeholders.push(canon);
      return `\u0001ART${idx}\u0001`;
    });
  }
  return s
    .split(',')
    .map((part) => {
      let p = String(part || '').trim();
      p = p.replace(/\u0001ART(\d+)\u0001/g, (_, i) => placeholders[Number(i)] || '');
      return p.trim();
    })
    .filter(Boolean);
}

function getTrackArtists(track) {
  if (!track) return [];
  const names = Array.isArray(track.artistNames) && track.artistNames.length
    ? track.artistNames
    : splitArtistNamesClient(track.artist);
  const ids = Array.isArray(track.artistIds) && track.artistIds.length
    ? track.artistIds
    : (track.artistId ? [track.artistId] : []);
  const result = [];
  const seen = new Set();
  for (let i = 0; i < names.length; i++) {
    const name = String(names[i] || '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ name, id: ids[i] || null });
  }
  return result;
}

function getTrackArtistText(track) {
  const artists = getTrackArtists(track);
  if (artists.length) return artists.map((a) => a.name).join(', ');
  return String(track?.artist || '').trim();
}

/** Подпись на карточке альбома: основной исполнитель; плюс фит — только если на сервере все треки с одним и тем же фитом (coverArtistLine). */
function getAlbumPrimaryArtistLabel(album) {
  if (!album) return '';
  const line = String(album.coverArtistLine || '').trim();
  if (line) return line;
  const names = album.artistNames;
  if (Array.isArray(names) && names.length && String(names[0] || '').trim()) {
    return String(names[0]).trim();
  }
  const raw = String(album.artist || '').split(',')[0].trim();
  return raw;
}

/** Сколько треков в релизе: сервер отдаёт trackCount (по фактическим трекам в каталоге); иначе длина массива id на альбоме. */
function albumTrackCountForUi(album) {
  if (!album) return 0;
  const tc = Number(album.trackCount);
  if (Number.isFinite(tc) && tc >= 0) return tc;
  const tracks = album.tracks;
  if (Array.isArray(tracks) && tracks.length > 0) return tracks.length;
  return 0;
}

/** Строка исполнителей на странице альбома — как на обложке карточки (coverArtistCredits), иначе запасной вариант. */
function renderAlbumHeroArtistsHtml(album) {
  const credits = album.coverArtistCredits;
  if (Array.isArray(credits) && credits.length) {
    return credits
      .map((artist) => {
        const safeName = esc(artist.name);
        if (!artist.id) {
          return `<span class="hero-artist">${safeName}</span>`;
        }
        const href = `#${buildHash('artist', { id: artist.id })}`;
        return `<a href="${href}" class="hero-artist clickable-artist" onclick="event.stopPropagation();">${safeName}</a>`;
      })
      .join(', ');
  }
  return renderTrackArtistsHtml(album, { className: 'hero-artist' });
}

function renderTrackArtistsHtml(track, options = {}) {
  const artists = getTrackArtists(track);
  const className = options.className || '';
  const stopPropagation = !!options.stopPropagation;
  if (!artists.length) return `<span class="${className}">—</span>`;
  return artists.map((artist) => {
    const safeName = esc(artist.name);
    if (!artist.id) {
      return `<span class="${className}">${safeName}</span>`;
    }
    const href = `#${buildHash('artist', { id: artist.id })}`;
    const stop = stopPropagation ? 'event.stopPropagation();' : '';
    return `<a href="${href}" class="${className} clickable-artist"${stopPropagation ? ` onclick="${stop}"` : ''}>${safeName}</a>`;
  }).join(', ');
}

/** SVG: минималистичный «квадрат со стрелкой» (внешняя ссылка на профиль). */
function uploaderProfileLinkIconHtml(size) {
  const s = Number(size) > 0 ? Number(size) : 12;
  return `<svg class="uploader-profile-link-icon" viewBox="0 0 24 24" width="${s}" height="${s}" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4h6v6"/><path d="M10 14 20 4"/><path d="M16 8v10a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h6"/></svg>`;
}

/** Компактная ссылка на профиль загрузившего (одобренная заявка «я исполнитель»). По умолчанию — только иконка. */
function htmlUploaderProfileLinkInline(track, options = {}) {
  const ua = track?.uploaderArtist;
  if (!ua?.id) return '';
  const stop = options.stopPropagation !== false ? 'event.stopPropagation();' : '';
  const iconOnly = options.iconOnly !== false;
  const baseClass = options.className || 'track-uploader-profile-btn';
  const cls = iconOnly ? `${baseClass} track-uploader-profile-btn--icon` : baseClass;
  const title = 'Профиль на сайте';
  const inner = iconOnly ? uploaderProfileLinkIconHtml(options.iconSize) : esc(options.label || 'Профиль');
  return ` <a href="#profile/${esc(userProfileRef(ua))}" class="${cls}" onclick="${stop}" title="${esc(title)}" aria-label="${esc(title)}">${inner}</a>`;
}

/** Строка мета: исполнители · ссылка на профиль загрузившего · альбом (если есть). */
function renderTrackMetaLineHtml(track, options = {}) {
  const className = options.className || 'track-artist';
  const stopPropagation = options.stopPropagation !== false;
  const album = options.album !== false;
  const artists = renderTrackArtistsHtml(track, { className, stopPropagation });
  const upl = options.hideUploaderProfile
    ? ''
    : htmlUploaderProfileLinkInline(track, {
        stopPropagation,
        className: options.uploaderClassName,
        iconOnly: options.uploaderIconOnly !== false,
        iconSize: options.uploaderIconSize
      });
  let albumPart = '';
  if (album && track.album) {
    const albumId = track.albumId ? String(track.albumId).trim() : '';
    const desktopAlbumLink =
      albumId &&
      options.albumLink !== false &&
      (typeof canLinkTrackAlbumInList === 'function' ? canLinkTrackAlbumInList() : false);
    if (desktopAlbumLink) {
      const stop = stopPropagation ? 'event.stopPropagation();' : '';
      albumPart = ` · <span class="track-meta-album track-meta-album--link" data-nav="album" data-nav-id="${esc(albumId)}" onclick="${stop}handleDataNavClick(event)" role="link" tabindex="0">${esc(track.album)}</span>`;
    } else {
      albumPart = ` · <span class="track-meta-album">${esc(track.album)}</span>`;
    }
  }
  const y = track.year;
  const yearPart =
    options.year !== false && Number.isFinite(Number(y)) && Number(y) >= 1900 && Number(y) <= 2100
      ? ` · <span class="track-meta-tech">${Number(y)}</span>`
      : '';
  return `${artists}${upl}${albumPart}${yearPart}`;
}

function renderUploaderProfileLinkToElement(el, track, options = {}) {
  if (!el) return;
  const ua = track?.uploaderArtist;
  if (!ua?.id) {
    el.innerHTML = '';
    el.classList.add('hidden');
    el.setAttribute('aria-hidden', 'true');
    return;
  }
  el.classList.remove('hidden');
  el.setAttribute('aria-hidden', 'false');
  const a = document.createElement('a');
  a.href = `#profile/${userProfileRef(ua)}`;
  const iconOnly = options.iconOnly !== false;
  a.className = iconOnly ? 'track-uploader-profile-btn track-uploader-profile-btn--icon' : 'track-uploader-profile-btn';
  const title = 'Профиль на сайте';
  a.title = title;
  a.setAttribute('aria-label', title);
  if (iconOnly) {
    a.innerHTML = uploaderProfileLinkIconHtml(options.iconSize || 12);
  } else {
    a.textContent = options.label || 'Профиль';
  }
  a.addEventListener('click', (e) => {
    e.stopPropagation();
    if (options.closeFullscreen && typeof closeFullscreenPlayer === 'function') {
      closeFullscreenPlayer();
    }
  });
  el.innerHTML = '';
  el.appendChild(a);
}

function renderTrackArtistsToElement(el, track, options = {}) {
  if (!el) return;
  const artists = getTrackArtists(track);
  const closeFullscreen = !!options.closeFullscreenOnNavigate;
  el.innerHTML = '';
  if (!artists.length) {
    el.textContent = '—';
    el.style.cursor = 'default';
    el.classList.remove('clickable-artist');
    return;
  }
  artists.forEach((artist, index) => {
    if (index > 0) el.appendChild(document.createTextNode(', '));
    if (artist.id) {
      const a = document.createElement('a');
      a.className = 'clickable-artist';
      a.href = `#${buildHash('artist', { id: artist.id })}`;
      a.textContent = artist.name;
      a.title = 'Перейти к исполнителю';
      a.addEventListener('click', (event) => {
        event.stopPropagation();
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        if (closeFullscreen && typeof closeFullscreenPlayer === 'function') {
          closeFullscreenPlayer();
        }
      });
      el.appendChild(a);
    } else {
      el.appendChild(document.createTextNode(artist.name));
    }
  });
}

async function loadAutocompleteData() {
  try {
    const [artists, albums] = await Promise.all([
      GET('/api/autocomplete/artists'),
      GET('/api/autocomplete/albums')
    ]);
    autocompleteArtists = artists;
    autocompleteAlbums = albums;
  } catch (err) {
    console.warn('Failed to load autocomplete data', err);
  }
}
async function loadTracksMap() {
  // Раньше тянули весь /api/tracks (~4MB) ради бейджа радио.
  // Матчинг теперь на сервере (/api/radio/now); карта больше не нужна.
  if (tracksMap) return;
  tracksMap = new Map();
  normTracksMap = new Map();
  titleTracksMap = new Map();
}

async function loadAllArtists() {
  try {
    allArtists = await GET('/api/artists');
    // Сетка «Исполнители» на главной строится из allArtists; без этого при первом заходе остаются только 6 из /api/home.
    // В desktop-приложении список фиксируется при открытии главной — не пересобираем при догрузке каталога.
    if (state.view === 'home' && allArtists.length > 0 && !isMxMDesktopApp()) {
      const grid = document.querySelector('.artists-grid');
      if (grid) updateHomeArtists(allArtists);
    }
  } catch (err) {
    console.warn('Failed to load all artists', err);
  }
}


/** Только артисты с реальным фото (главная; заглушка не считается). */
function hasArtistPhoto(a) {
  return artistHasRealPhoto(a);
}

/** Fisher–Yates: равномерное перемешивание. */
function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Дмитрий горевич — 40% шанс первой карточкой при наличии фото ? списке. */
const HOME_PRIORITY_ARTIST_ID = 'abfb7195-7591-43cc-98f1-cb4b8469300c';
const HOME_ARTISTS_GRID_SIZE_MOBILE = 6;
const HOME_ARTISTS_GRID_SIZE_DESKTOP_EXPANDED = 10;
/** Свёрнутый сайдбар — шире контент, показываем больше карточек */
const HOME_ARTISTS_GRID_SIZE_DESKTOP_COLLAPSED = 13;
/** Desktop-приложение (Electron): всегда ровно 6, без пересборки при resize */
const HOME_ARTISTS_GRID_SIZE_DESKTOP_APP = 6;

function resetDesktopHomeArtistsPin() {
  desktopHomeArtistsPinned = null;
}

function getHomeArtistsTargetCount() {
  if (isMxMDesktopApp()) return HOME_ARTISTS_GRID_SIZE_DESKTOP_APP;
  if (window.matchMedia('(max-width: 900px)').matches) return HOME_ARTISTS_GRID_SIZE_MOBILE;
  return document.body.classList.contains('sidebar-collapsed')
    ? HOME_ARTISTS_GRID_SIZE_DESKTOP_COLLAPSED
    : HOME_ARTISTS_GRID_SIZE_DESKTOP_EXPANDED;
}

function buildHomeArtistsSelection(artists, { forceReselect = false } = {}) {
  if (isMxMDesktopApp() && desktopHomeArtistsPinned && !forceReselect) {
    return desktopHomeArtistsPinned;
  }

  const pool = (artists || []).filter(
    (a) => a && a.id && String(a.name || '').trim() && hasArtistPhoto(a)
  );
  if (pool.length === 0) return [];

  const targetCount = getHomeArtistsTargetCount();
  const selected = [];
  const priorityArtist = pool.find((a) => a.id === HOME_PRIORITY_ARTIST_ID);
  if (priorityArtist && hasArtistPhoto(priorityArtist) && Math.random() < 0.4) {
    selected.push(priorityArtist);
  }

  const rest = shuffleArray(pool.filter((a) => !selected.some((s) => s.id === a.id)));
  const need = Math.max(0, targetCount - selected.length);
  selected.push(...rest.slice(0, need));

  if (isMxMDesktopApp() && selected.length) desktopHomeArtistsPinned = selected;
  return selected;
}

function homeArtistsGridShowsSelection(grid, selected) {
  if (!grid || !selected.length) return false;
  const ids = [...grid.querySelectorAll('.artist-card[data-nav-id]')].map((el) =>
    String(el.getAttribute('data-nav-id') || '')
  );
  if (ids.length !== selected.length) return false;
  return selected.every((a, i) => String(a.id) === ids[i]);
}

function updateHomeArtists(artists) {
  const artistsGrid = document.querySelector('.artists-grid');
  if (!artistsGrid) return;

  const selected = buildHomeArtistsSelection(artists);
  if (selected.length === 0) {
    artistsGrid.innerHTML = '';
    return;
  }

  if (isMxMDesktopApp() && homeArtistsGridShowsSelection(artistsGrid, selected)) return;

  const html = selected.map((a) => artistCard(a, { showFav: false })).join('');
  if (isMxMDesktopApp()) {
    artistsGrid.innerHTML = html;
    artistsGrid.style.opacity = '1';
    artistsGrid.style.transition = '';
    return;
  }

  artistsGrid.style.transition = 'opacity 0.3s';
  artistsGrid.style.opacity = '0';
  setTimeout(() => {
    artistsGrid.innerHTML = html;
    artistsGrid.style.opacity = '1';
  }, 300);
}

function isRadioAdBreakTrackName(name) {
  if (!name) return false;
  const cleaned = String(name)
    .replace(/^Сейчас играет:\s*/i, '')
    .replace(/\.mp3$/i, '')
    .trim();
  if (/^рекламная пауза$/i.test(cleaned)) return true;
  const compact = cleaned.toLowerCase().replace(/\s+/g, '');
  return /^(prebivka|reklama|dmitriy)(\d+|\(\d+\))?$/.test(compact);
}

function isRadioLiveOnAirName(name) {
  return String(name || '')
    .replace(/^Сейчас играет:\s*/i, '')
    .replace(/\.mp3$/i, '')
    .trim()
    .toLowerCase() === 'прямое включение';
}

function parseRadioStreamTrackLabel(raw) {
  const cleaned = String(raw || '')
    .replace(/^Сейчас играет:\s*/i, '')
    .replace(/\.mp3$/i, '')
    .trim();
  const trackInfoForLookup = stripBracketInfo(cleaned);
  let parsedArtist = '';
  let parsedTitle = cleaned;
  const sep = trackInfoForLookup.includes(' - ') ? ' - ' : trackInfoForLookup.includes(' — ') ? ' — ' : null;
  if (sep) {
    const i = trackInfoForLookup.indexOf(sep);
    parsedArtist = trackInfoForLookup.slice(0, i).trim();
    parsedTitle = trackInfoForLookup.slice(i + sep.length).trim();
  } else {
    const lastDashIndex = trackInfoForLookup.lastIndexOf('-');
    if (lastDashIndex !== -1) {
      parsedArtist = trackInfoForLookup.substring(0, lastDashIndex).trim();
      parsedTitle = trackInfoForLookup.substring(lastDashIndex + 1).trim();
    }
  }
  return { cleaned, trackInfoForLookup, parsedArtist, parsedTitle };
}

function radioCatalogArtistBlob(t) {
  const parts = [];
  if (Array.isArray(t && t.artistNames)) parts.push(...t.artistNames);
  if (t && t.artist) parts.push(t.artist);
  return normalizeTrackName(parts.filter(Boolean).join(' '));
}

function lookupRadioTrackInCatalog(trackInfoForLookup) {
  if (!tracksMap || !trackInfoForLookup) return null;
  const exactKey = trackInfoForLookup.toLowerCase();
  let trackData = tracksMap.get(exactKey);

  if (!trackData && normTracksMap) {
    const normKey = normalizeTrackName(exactKey);
    const matches = normTracksMap.get(normKey);
    if (matches && matches.length) trackData = matches[0];
  }

  if (!trackData && titleTracksMap) {
    const parsed = parseRadioStreamTrackLabel(trackInfoForLookup);
    const radioArtist = parsed.parsedArtist;
    const radioTitle = parsed.parsedTitle;
    if (radioArtist && radioTitle) {
      const normRadioTitle = normalizeTrackName(radioTitle);
      const normRadioArtist = normalizeTrackName(radioArtist);
      const candidates = titleTracksMap.get(normRadioTitle) || [];
      trackData = candidates.find((t) => {
        const blob = radioCatalogArtistBlob(t);
        const primary = Array.isArray(t.artistNames) && t.artistNames[0]
          ? normalizeTrackName(t.artistNames[0])
          : normalizeTrackName(String(t.artist || '').split(/\s*,\s*/)[0]);
        return primary === normRadioArtist
          || blob.includes(normRadioArtist)
          || (normRadioArtist && normRadioArtist.includes(primary));
      }) || null;
    }
  }
  return trackData || null;
}

async function ensureTracksMapForRadio() {
  // no-op: /api/radio/now резолвит каталог на сервере
  if (!tracksMap) {
    tracksMap = new Map();
    normTracksMap = new Map();
    titleTracksMap = new Map();
  }
}

async function fetchRadioNowPlayingRaw() {
  const url = resolveClientRequestUrl('/api/radio/now');
  const res = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  const txt = await res.text();
  let data;
  try {
    data = JSON.parse(txt);
  } catch {
    return null;
  }
  return data && data.ok ? data : null;
}

async function buildRadioNowPlayingMeta(pack) {
  if (!pack || !pack.ok) return null;
  const kind = String(pack.kind || '');
  if (kind === 'empty') return null;

  const isLiveOnAir = kind === 'live';
  const isAdBreak = kind === 'ad';
  const title = String(pack.title || '').trim();
  const artist = String(pack.artist || '').trim();
  const name = String(pack.name || '').trim() || (artist && title ? `${artist} — ${title}` : title || artist);
  const trackId = pack.trackId != null ? String(pack.trackId) : null;
  const coverUrl = pack.coverUrl ? String(pack.coverUrl) : '';
  const trackData =
    trackId && !isLiveOnAir && !isAdBreak
      ? {
          id: trackId,
          title: title || name,
          artist: artist || 'MXM Radio',
          coverUrl: coverUrl || null
        }
      : null;

  return {
    sig: name || title || kind,
    cleaned: name || title || '',
    trackData,
    isAdBreak,
    isLiveOnAir,
    parsedArtist: artist,
    parsedTitle: title,
    displayTitle: isLiveOnAir
      ? 'Прямое включение'
      : isAdBreak
        ? 'Рекламная пауза'
        : title || name || 'MXM Radio',
    displayArtist: isLiveOnAir || isAdBreak ? 'MXM Radio' : artist || 'Прямой эфир',
    coverUrl: isLiveOnAir || isAdBreak ? '' : coverUrl,
    coverGradient: pickGrad(trackId || 'mxm-radio'),
    catalogTrackId: trackId
  };
}

function renderRadioHomeBadge(meta) {
  const badge = $('radio-badge');
  if (!badge) return;
  if (!meta) {
    badge.classList.add('hidden');
    return;
  }

  badge.classList.remove('hidden');

  let coverHtml = meta.coverUrl
    ? `<img src="${esc(resolveMediaUrl(meta.coverUrl))}" class="radio-cover" alt="" loading="lazy" decoding="async">`
    : `<div class="radio-icon">♪</div>`;

  let nameHtml = `<span style="color: var(--text-3);">Сейчас на радио ·</span> ${esc(meta.cleaned)}`;
  if (meta.catalogTrackId && meta.trackData) {
    const artist = String(meta.trackData.artist || meta.parsedArtist || '').trim();
    const title = String(meta.trackData.title || meta.parsedTitle || meta.cleaned).trim();
    const titleLink = `<a href="#${buildHash('track', { id: meta.catalogTrackId })}">${esc(title)}</a>`;
    nameHtml = artist
      ? `<span style="color: var(--text-3);">Сейчас на радио ·</span> ${esc(artist)} — ${titleLink}`
      : `<span style="color: var(--text-3);">Сейчас на радио ·</span> ${titleLink}`;
  }
  if (meta.isLiveOnAir) {
    coverHtml = `<div class="radio-icon">▶</div>`;
    nameHtml = `<span style="color: var(--text-3);">Сейчас на радио ·</span> Прямое включение`;
  }
  if (meta.isAdBreak) {
    coverHtml = `<div class="radio-icon radio-icon-ad">AD</div>`;
    nameHtml = `<span style="color: var(--text-3);">Сейчас на радио ·</span> Рекламная пауза`;
  }

  badge.innerHTML = `<span class="radio-text">${nameHtml}</span>${coverHtml}`;
}

function applyRadioNowPlayingToPlayer(meta) {
  if (!meta || !isRadioPlaybackActive()) return;
  if (!state.currentTrack || state.currentTrack.id !== MXM_RADIO_TRACK_ID) return;

  const uiSig = `${meta.sig}|${meta.displayTitle}|${meta.displayArtist}|${meta.coverUrl || ''}`;
  if (uiSig === _radioPlayerUiSig) return;
  _radioPlayerUiSig = uiSig;

  const next = {
    ...state.currentTrack,
    id: MXM_RADIO_TRACK_ID,
    audioUrl: MXM_RADIO_STREAM_URL,
    title: meta.displayTitle,
    artist: meta.displayArtist,
    coverUrl: meta.coverUrl,
    coverGradient: meta.coverGradient,
    radioCatalogTrackId: meta.catalogTrackId
  };

  state.currentTrack = next;
  setPlayerTitleNeon($('player-title'), next);
  renderTrackArtistsToElement($('player-artist'), next);
  const ptags = $('player-tags');
  if (ptags) ptags.innerHTML = htmlPlayerTrackTags(next);
  if (meta.isAdBreak || meta.isLiveOnAir || meta.coverUrl) setPlayerCoverSmooth(next);
  applyMiniAmbientFromTrackCover(next);
  if (typeof applyMediaSessionForTrack === 'function') applyMediaSessionForTrack(next);
  if (typeof fullscreenActive !== 'undefined' && fullscreenActive && typeof updateFullscreenContent === 'function') {
    void updateFullscreenContent({ radioMetaOnly: true });
  }
}

async function tickRadioNowPlaying(opts = {}) {
  const { force = false, badgeOnly = false } = opts || {};
  if (_radioPollInFlight) return;
  if (!force && document.hidden) return;
  if (!force && badgeOnly && state.view !== 'home') return;
  if (!force && !badgeOnly && !isRadioPlaybackActive() && state.view !== 'home') return;

  _radioPollInFlight = true;
  try {
    const pack = await fetchRadioNowPlayingRaw();
    if (!pack || pack.kind === 'empty') {
      _radioNowPlayingRaw = '';
      if (state.view === 'home') renderRadioHomeBadge(null);
      return;
    }

    const meta = await buildRadioNowPlayingMeta(pack);
    if (!meta) return;
    _radioNowPlayingRaw = meta.sig || '';

    if (!badgeOnly && isRadioPlaybackActive()) applyRadioNowPlayingToPlayer(meta);
    if (state.view === 'home') renderRadioHomeBadge(meta);
  } catch (err) {
    console.warn('Radio now playing:', err);
    if (state.view === 'home') renderRadioHomeBadge(null);
  } finally {
    _radioPollInFlight = false;
  }
}

async function updateRadioNowPlaying() {
  await tickRadioNowPlaying({ force: true });
}

function startRadioPlaybackPolling() {
  if (radioPlaybackPollTimer) clearInterval(radioPlaybackPollTimer);
  _radioNowPlayingRaw = '';
  _radioPlayerUiSig = '';
  void tickRadioNowPlaying({ force: true });
  radioPlaybackPollTimer = setInterval(() => {
    if (!isRadioPlaybackActive()) {
      stopRadioPlaybackPolling();
      return;
    }
    void tickRadioNowPlaying();
  }, RADIO_NOW_PLAYING_POLL_MS);
}

function stopRadioPlaybackPolling() {
  if (radioPlaybackPollTimer) {
    clearInterval(radioPlaybackPollTimer);
    radioPlaybackPollTimer = null;
  }
  _radioNowPlayingRaw = '';
  _radioPlayerUiSig = '';
}

const MAX_INIT_ATTEMPTS = 10;

function normalizeTrackName(str) {
  return str
    .toLowerCase()
    .replace(/[.,/#!$%^&*;:{}=`~()\-—\s]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripBracketInfo(str) {
  return String(str || '')
    .replace(/\([^)]*\)|\[[^\]]*]|{[^}]*}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function loadTrackLoudnessCache() {
  try {
    const raw = localStorage.getItem(TRACK_LOUDNESS_CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;
    parsed.forEach(entry => {
      if (!entry || !entry.id || !Number.isFinite(entry.avgDb)) return;
      trackLoudnessCache.set(entry.id, {
        avgDb: entry.avgDb,
        samples: Number(entry.samples) || 1,
        updatedAt: Number(entry.updatedAt) || Date.now()
      });
    });
  } catch (_) {}
}

function saveTrackLoudnessCache() {
  try {
    const entries = Array.from(trackLoudnessCache.entries())
      .map(([id, value]) => ({ id, ...value }))
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
      .slice(0, MAX_TRACK_LOUDNESS_ENTRIES);
    localStorage.setItem(TRACK_LOUDNESS_CACHE_KEY, JSON.stringify(entries));
  } catch (_) {}
}

function updateTrackLoudnessSample(trackId, db) {
  if (!trackId || !Number.isFinite(db)) return;
  const prev = trackLoudnessCache.get(trackId);
  if (!prev) {
    trackLoudnessCache.set(trackId, { avgDb: db, samples: 1, updatedAt: Date.now() });
    return;
  }
  const nextSamples = Math.min((prev.samples || 1) + 1, 120);
  const avgDb = prev.avgDb + (db - prev.avgDb) / nextSamples;
  trackLoudnessCache.set(trackId, { avgDb, samples: nextSamples, updatedAt: Date.now() });
}

function estimateTrackAutoGain(trackId) {
  const entry = trackLoudnessCache.get(trackId);
  if (!entry || !Number.isFinite(entry.avgDb) || (entry.samples || 0) < 2) return 1;
  const desired = Math.pow(10, (TARGET_TRACK_DBFS - entry.avgDb) / 20);
  if (desired <= AUTO_GAIN_FLOOR) return AUTO_GAIN_FLOOR;
  return Math.min(AUTO_GAIN_MAX, desired);
}

/** 0…1: ползунок громкости (приоритет UI, затем localStorage). */
function getUserVolume01() {
  try {
    const slider = $('volume-range');
    if (slider && slider.value !== '' && slider.dataset.mxmVolSynced === '1') {
      const v = Number(slider.value);
      if (Number.isFinite(v)) return Math.max(0, Math.min(1, v / 100));
    }
    const raw = localStorage.getItem('mxm_volume');
    if (raw !== null && raw !== '') {
      const v = parseFloat(raw);
      if (Number.isFinite(v)) return Math.max(0, Math.min(1, v));
    }
    if (slider && slider.value !== '') {
      const v = Number(slider.value);
      if (Number.isFinite(v)) return Math.max(0, Math.min(1, v / 100));
    }
  } catch (_) {}
  return 0.8;
}

/**
 * Подтянуть UI + gainNode из mxm_volume до любого play/restore.
 * Иначе HTML value="80" даёт автогейну «среднюю» громкость при низкой сохранённой.
 */
function syncVolumeFromStorage() {
  let vol = 0.8;
  try {
    const raw = localStorage.getItem('mxm_volume');
    if (raw !== null && raw !== '') {
      const parsed = parseFloat(raw);
      if (Number.isFinite(parsed)) vol = Math.max(0, Math.min(1, parsed));
    }
  } catch (_) {}
  const volPercent = Math.round(vol * 100);
  const slider = $('volume-range');
  if (slider) {
    slider.value = String(volPercent);
    slider.dataset.mxmVolSynced = '1';
  }
  const fs = $('fullscreen-volume-range');
  if (fs) fs.value = String(volPercent);
  updateVolumeFill(volPercent);
  audio.muted = volPercent === 0;
  applyUserVolumeToGraph(vol);
  const iconVol = $('icon-vol');
  const iconMute = $('icon-mute');
  if (iconVol) iconVol.classList.toggle('hidden', vol === 0);
  if (iconMute) iconMute.classList.toggle('hidden', vol > 0);
  if (typeof syncMuteUI === 'function') syncMuteUI();
  return vol;
}

/** Подстройка громкости трека масштабируется ползунком: при низкой громкости не поднимаем тихие треки. */
function scaleAutoGainForUserVolume(rawAutoGain, userVol01) {
  const raw = Number(rawAutoGain);
  if (!Number.isFinite(raw) || raw <= 1) return AUTO_GAIN_FLOOR;
  const u = Math.max(0, Math.min(1, Number(userVol01) || 0));
  // Ниже ~35% автобуст выключен: иначе после F5 «пол» около средней громкости.
  if (u <= 0.35) return AUTO_GAIN_FLOOR;
  const scaled = 1 + (raw - 1) * u;
  return Math.max(AUTO_GAIN_FLOOR, Math.min(AUTO_GAIN_MAX, scaled));
}

/** Единая точка: ползунок → gainNode (и fallback audio.volume до Web Audio). */
function applyUserVolumeToGraph(vol01) {
  const v = vol01 != null && Number.isFinite(Number(vol01))
    ? Math.max(0, Math.min(1, Number(vol01)))
    : getUserVolume01();
  if (gainNode && audioCtx) {
    gainNode.gain.cancelScheduledValues(audioCtx.currentTime);
    gainNode.gain.setValueAtTime(v, audioCtx.currentTime);
  } else {
    audio.volume = v;
  }
  return v;
}

/** Останавливает фоновый замер — при смене трека перезапускаем, иначе «тянет» гейн между композициями. */
function stopLoudnessMonitor() {
  if (loudnessMonitorTimer) {
    clearInterval(loudnessMonitorTimer);
    loudnessMonitorTimer = null;
  }
}

/**
 * Громкость в колонках задаётся только ползунком (gainNode / mxm_volume).
 * Здесь — разовая подстройка под измеренную громкость **этого** трека (из кэша), без «живого» AGC по RMS.
 */
function resetTrackLoudnessNormalization(track) {
  stopLoudnessMonitor();
  const trackId = track?.id;
  const userVol = applyUserVolumeToGraph();
  const rawAuto = estimateTrackAutoGain(trackId);
  currentAutoGain = scaleAutoGainForUserVolume(rawAuto, userVol);
  if (autoGainNode && audioCtx) {
    autoGainNode.gain.cancelScheduledValues(audioCtx.currentTime);
    autoGainNode.gain.setTargetAtTime(currentAutoGain, audioCtx.currentTime, 0.09);
  }
  startLoudnessMonitor();
}

/** После смены ползунка — пересчитать автогейн текущего трека под новый уровень. */
function refreshAutoGainForUserVolume(explicitVol01) {
  if (!state.currentTrack || isRadioPlaybackActive()) return;
  const userVol =
    explicitVol01 != null && Number.isFinite(Number(explicitVol01))
      ? Math.max(0, Math.min(1, Number(explicitVol01)))
      : getUserVolume01();
  const rawAuto = estimateTrackAutoGain(state.currentTrack.id);
  currentAutoGain = scaleAutoGainForUserVolume(rawAuto, userVol);
  if (autoGainNode && audioCtx) {
    autoGainNode.gain.cancelScheduledValues(audioCtx.currentTime);
    autoGainNode.gain.setTargetAtTime(currentAutoGain, audioCtx.currentTime, 0.06);
  }
}

/**
 * Только пополняет кэш громкости по треку (для следующих воспроизведений). Не трогает autoGainNode —
 * иначе измерение после своего же усиления даёт обратную связь и перетирает уровень ползунка.
 */
function applyRadioInputGain() {
  stopLoudnessMonitor();
  currentAutoGain = MXM_RADIO_INPUT_GAIN;
  if (autoGainNode && audioCtx) {
    autoGainNode.gain.cancelScheduledValues(audioCtx.currentTime);
    autoGainNode.gain.setTargetAtTime(MXM_RADIO_INPUT_GAIN, audioCtx.currentTime, 0.06);
  }
}

function startLoudnessMonitor() {
  if (isRadioPlaybackActive()) return;
  if (!analyserNode || !autoGainNode) return;
  if (loudnessMonitorTimer) {
    clearInterval(loudnessMonitorTimer);
    loudnessMonitorTimer = null;
  }
  const samples = new Float32Array(analyserNode.fftSize);
  const isPerfLow = document.documentElement.classList.contains('perf-low');
  const intervalMs = isPerfLow ? 520 : 400;
  let ticksSinceSave = 0;

  loudnessMonitorTimer = setInterval(() => {
    if (!state.currentTrack || audio.paused || audio.ended || document.hidden) return;

    analyserNode.getFloatTimeDomainData(samples);
    let sum = 0;
    for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
    const rms = Math.sqrt(sum / samples.length);
    if (!Number.isFinite(rms) || rms <= 0.000001) return;

    const dbPost = 20 * Math.log10(rms);
    if (!Number.isFinite(dbPost)) return;

    const gainLin = Math.max(0.0001, Number(autoGainNode?.gain?.value) || Number(currentAutoGain) || 1);
    const dbPreAuto = dbPost - 20 * Math.log10(gainLin);
    updateTrackLoudnessSample(state.currentTrack.id, dbPreAuto);

    ticksSinceSave += 1;
    if (ticksSinceSave >= 20) {
      ticksSinceSave = 0;
      saveTrackLoudnessCache();
    }
  }, intervalMs);
}

let _lastPlayerAmbientGradientStops = null;

/** Мобильный мини-плеер использует backgroundImage; на десктопе (≥901px) он перекрывает стекло — убираем. */
function syncPlayerBarBackgroundImage() {
  const player = $('player');
  if (!player) return;
  if (window.matchMedia('(min-width: 901px)').matches) {
    player.style.backgroundImage = '';
  } else if (_lastPlayerAmbientGradientStops) {
    player.style.backgroundImage = `linear-gradient(${_lastPlayerAmbientGradientStops})`;
  }
}

/** Реальное значение env(safe-area-inset-top) в px (на части Android без выреза = 0 при рисовании под статус-баром). */
function getResolvedSafeAreaInsetTopPx() {
  try {
    const el = document.createElement('div');
    el.style.cssText =
      'position:fixed;left:0;top:0;padding-top:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none;width:0;height:0;overflow:hidden;';
    document.documentElement.appendChild(el);
    const v = parseFloat(window.getComputedStyle(el).paddingTop) || 0;
    el.remove();
    return Number.isFinite(v) ? v : 0;
  } catch (_) {
    return 0;
  }
}

function syncMobileViewportInsets() {
  const rootStyle = document.documentElement.style;
  const vv = window.visualViewport;
  const isMobileViewport = window.innerWidth <= 900;
  const visualHeight = Math.round(vv ? vv.height : window.innerHeight);
  rootStyle.setProperty('--mxm-vv-height', `${Math.max(0, visualHeight)}px`);
  let bottomOffset = 0;
  if (vv) {
    bottomOffset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
  }

  const active = document.activeElement;
  const isClipboardTempFocus = !!(active && active.dataset && active.dataset.mxmClipboardTemp === '1');
  const isEditableFocused = !!(
    active &&
    !isClipboardTempFocus &&
    (
      active.tagName === 'INPUT' ||
      active.tagName === 'TEXTAREA' ||
      active.isContentEditable
    )
  );
  const isSearchFieldFocused = !!(
    active &&
    (active.id === 'search-input-page' || active.id === 'search-input-mobile' || active.id === 'search-input')
  );
  const isTextareaFocused = !!(active && active.tagName === 'TEXTAREA');
  const keyboardLikelyOpenByFocus = !!(
    isMobileViewport &&
    isEditableFocused &&
    (
      isTextareaFocused ||
      isSearchFieldFocused ||
      !vv ||
      (window.innerHeight - vv.height) > 60
    )
  );
  const keyboardLikelyAnimating = !!(
    isMobileViewport &&
    isEditableFocused &&
    vv &&
    (
      (window.innerHeight - vv.height) > 92 ||
      bottomOffset > 28
    )
  );
  let keyboardLikelyOpen = keyboardLikelyOpenByFocus || keyboardLikelyAnimating;
  const now = Date.now();
  const viewportStillCompressed = !!(
    isMobileViewport &&
    vv &&
    ((window.innerHeight - vv.height) > 72 || bottomOffset > 20)
  );
  if (keyboardLikelyOpen) {
    _keyboardOverlayHoldUntil = now + 380;
  } else if (isEditableFocused && now < _keyboardOverlayHoldUntil) {
    // Гистерезис: не возвращаем плеер/низ во время закрытия клавиатуры (убирает "прыжок вверх-вниз").
    keyboardLikelyOpen = true;
  } else if (now < _keyboardClosingUntil && viewportStillCompressed) {
    // После focusout ждём фактического разворачивания viewport, иначе плеер "улетает" вверх.
    keyboardLikelyOpen = true;
  }
  // На мобильных при открытой клавиатуре не поднимаем плеер/нижнее меню вверх:
  // клавиатура должна накрывать их поверх (как в нативных приложениях).
  rootStyle.setProperty('--vv-bottom-offset', keyboardLikelyOpen ? '0px' : `${Math.round(bottomOffset)}px`);
  document.body.classList.toggle('mxm-keyboard-overlay', keyboardLikelyOpen);
  if (keyboardLikelyOpen && isEditableFocused) _keyboardFocusGuardUntil = Date.now() + 350;

  let extraTop = '0px';
  try {
    const mqMobile = window.matchMedia('(max-width: 900px)');
    if (mqMobile.matches) {
      const safeTop = getResolvedSafeAreaInsetTopPx();
      let nativeApp = false;
      try {
        nativeApp = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
      } catch (_) {}
      const standalone =
        (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
        window.navigator.standalone === true;
      const ua = String(window.navigator.userAgent || '').toLowerCase();
      const isIos = /iphone|ipad|ipod/.test(ua);
      const isAndroid = /android/.test(ua);
      // Вьюпорт почти на весь экран сверху — часто рисуют под статус-баром при env=0 (Chrome с скрытой панелью и т.п.).
      const looksFullBleedTop =
        vv && vv.offsetTop <= 2 && vv.height >= window.innerHeight * 0.93;
      if (safeTop < 12 && (nativeApp || standalone || looksFullBleedTop)) {
        const allowBrowserFallback = looksFullBleedTop && isAndroid; // iOS browser часто сам корректно даёт inset
        if (nativeApp || standalone || allowBrowserFallback) {
          let fallbackPx = 10;
          if (nativeApp && isAndroid) fallbackPx = 22;             // APK/WebView под статус-баром
          else if (nativeApp && isIos) fallbackPx = 16;            // iOS shell
          else if (standalone && isIos) fallbackPx = 12;           // iOS PWA из Safari
          else if (allowBrowserFallback) fallbackPx = 14;          // Android браузер с скрытой шапкой
          extraTop = `${fallbackPx}px`;
        }
      }
    }
  } catch (_) {}
  rootStyle.setProperty('--mxm-extra-top', extraTop);

  const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  document.body.classList.toggle('ios-standalone', !!standalone);
  applyIosStandaloneViewportLock(!!standalone);

  syncPlayerBarBackgroundImage();
  syncChatComposerKeyboardAlign();
}

let _viewportInsetsRaf = null;
function scheduleSyncMobileViewportInsets() {
  if (_viewportInsetsRaf != null) return;
  _viewportInsetsRaf = requestAnimationFrame(() => {
    _viewportInsetsRaf = null;
    syncMobileViewportInsets();
  });
}

function mxmFocusInsideEmojiPickerOrChatPanels(el) {
  if (!(el instanceof HTMLElement)) return false;
  try {
    const root = el.getRootNode();
    if (root instanceof ShadowRoot) {
      const host = root.host;
      if (host && String(host.tagName || '').toLowerCase() === 'emoji-picker') return true;
    }
  } catch (_) {}
  return !!(el.closest && (el.closest('#mxm-chat-emoji-slot') || el.closest('#mxm-chat-emoji-portal') || el.closest('#mxm-chat-tracks-panel')));
}

function scrollFocusedEditableIntoView() {
  if (typeof window === 'undefined' || window.innerWidth > 900) return;
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return;
  const isEditable = active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable;
  if (!isEditable) return;
  if (active.id === 'upl-create-title') return;
  const isChatComposer =
    active.id === 'mxm-chat-input' || !!(active.classList && active.classList.contains('mxm-chat-input'));
  // Поле чата выравниваем через visualViewport (syncChatComposerKeyboardAlign), без scrollIntoView —
  // иначе iOS/Android «подпрыгивают» и уносят инпут слишком высоко.
  if (isChatComposer) return;
  // Поиск в emoji-picker живёт в Shadow DOM — closest('.mxm-chat-app') не срабатывает; scrollIntoView поднимает #content.
  if (mxmFocusInsideEmojiPickerOrChatPanels(active)) return;
  // Любой другой input/textarea внутри мессенджера: scrollIntoView поднимает цепочку до #content и
  // прячет шапку диалога под глобальную шапку сайта.
  if (active.closest('.mxm-chat-app')) return;
  const inModal = !!active.closest('.modal-box');
  const isSearchField =
    active.id === 'search-input-page' || active.id === 'search-input-mobile' || active.id === 'search-input';
  const block = inModal || isSearchField ? 'nearest' : 'center';
  try {
    active.scrollIntoView({ block, inline: 'nearest', behavior: 'smooth' });
  } catch (_) {}
}

/** Чат: композер только в потоке flex + 100dvh (meta interactive-widget=resizes-content). Фиксированный bottom от vv на iOS давал двойной отступ. */
function syncChatComposerKeyboardAlign() {
  try {
    const body = document.body;
    const rootStyle = document.documentElement.style;
    body.classList.remove('mxm-chat-composer-lifted');
    rootStyle.removeProperty('--mxm-composer-lift');
    rootStyle.removeProperty('--mxm-composer-fixed-bottom');
    rootStyle.removeProperty('--mxm-chat-tools-h');
  } catch (_) {}
}

function isMobileKeyboardEditingActive() {
  if (typeof window === 'undefined' || window.innerWidth > 900) return false;
  if (!document.body.classList.contains('mxm-keyboard-overlay')) return false;
  const active = document.activeElement;
  if (!active) return false;
  if (active.dataset && active.dataset.mxmClipboardTemp === '1') return false;
  return active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable;
}

function applyIosStandaloneViewportLock(isStandalone) {
  try {
    const ua = String(window.navigator.userAgent || '').toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua);
    const nativeShell = isCapacitorNativeApp();
    if (!isIos && !nativeShell) return;
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;

    if (isStandalone || nativeShell) {
      if (_iosStandaloneViewportLocked) return;
      meta.dataset.originalContent = meta.getAttribute('content') || '';
      meta.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover, interactive-widget=resizes-content'
      );
      _iosStandaloneViewportLocked = true;
      return;
    }

    if (_iosStandaloneViewportLocked) {
      const original =
        meta.dataset.originalContent ||
        'width=device-width, initial-scale=1.0, viewport-fit=cover, interactive-widget=resizes-content';
      meta.setAttribute('content', original);
      _iosStandaloneViewportLocked = false;
    }
  } catch (_) {}
}

function bindViewportInsetsSync() {
  if (viewportSyncBound) return;
  viewportSyncBound = true;
  syncMobileViewportInsets();
  window.addEventListener('resize', scheduleSyncMobileViewportInsets, { passive: true });
  window.addEventListener('orientationchange', scheduleSyncMobileViewportInsets, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleSyncMobileViewportInsets, { passive: true });
    window.visualViewport.addEventListener('scroll', scheduleSyncMobileViewportInsets, { passive: true });
  }
  document.addEventListener('focusin', (ev) => {
    _keyboardOverlayHoldUntil = Date.now() + 520;
    syncMobileViewportInsets();
    const t = ev.target;
    const chatIn = t && t.id === 'mxm-chat-input';
    if (chatIn) {
      requestAnimationFrame(() => {
        syncChatComposerKeyboardAlign();
        syncMobileViewportInsets();
      });
      setTimeout(() => {
        syncChatComposerKeyboardAlign();
        syncMobileViewportInsets();
      }, 50);
      setTimeout(() => {
        syncChatComposerKeyboardAlign();
        syncMobileViewportInsets();
      }, 200);
      setTimeout(() => {
        syncChatComposerKeyboardAlign();
        syncMobileViewportInsets();
      }, 450);
    } else {
      setTimeout(scrollFocusedEditableIntoView, 40);
      setTimeout(scrollFocusedEditableIntoView, 180);
    }
  }, { passive: true });
  document.addEventListener('focusout', () => {
    // Даем время анимации закрытия клавиатуры, чтобы мини-плеер не всплывал раньше времени.
    _keyboardOverlayHoldUntil = Date.now() + 420;
    _keyboardClosingUntil = Date.now() + 950;
    setTimeout(syncMobileViewportInsets, 0);
    setTimeout(syncMobileViewportInsets, 140);
    setTimeout(syncMobileViewportInsets, 320);
    setTimeout(syncMobileViewportInsets, 620);
  }, { passive: true });
}

// Во время ввода текста на мобиле блокируем случайные тапы по трекам под пальцем.
document.addEventListener(
  'click',
  (e) => {
    if (!isMobileKeyboardEditingActive()) return;
    const now = Date.now();
    if (now > _keyboardFocusGuardUntil) return;
    const t = e.target;
    if (!(t instanceof Element)) return;
    const inEditableUi = t.closest('input, textarea, .upl-comment-form, .upl-comment-edit-wrap, .form-group, .modal-box');
    if (inEditableUi) return;
    const accidentalTrackTap = t.closest('.track-row, .track-row-w-cover, .queue-sheet-row, [data-track-id]');
    if (!accidentalTrackTap) return;
    e.preventDefault();
    e.stopPropagation();
  },
  true
);

/** Только нативное приложение + тот же origin, что и страница (типичный APK с server.url → music.mxmfm.ru). */
function registerNativeImageCacheServiceWorker() {
  try {
    if (!('serviceWorker' in navigator) || !window.isSecureContext) return;
    if (!window.Capacitor || typeof window.Capacitor.isNativePlatform !== 'function' || !window.Capacitor.isNativePlatform()) return;
    const path = '/sw-mxm-media.js';
    navigator.serviceWorker
      .register(path, { scope: '/' })
      .then((reg) => {
        reg.update().catch(() => {});
      })
      .catch(() => {});
  } catch (_) {}
}

function startRadioUpdates() {
  if (typeof isMxMDesktopApp === 'function' && isMxMDesktopApp()) return;
  if (radioUpdateTimer) clearInterval(radioUpdateTimer);
  radioUpdateTimer = setInterval(() => {
    if (state.view === 'home') void tickRadioNowPlaying({ badgeOnly: true });
  }, RADIO_HOME_BADGE_POLL_MS);
}

function stopRadioUpdates() {
  if (radioUpdateTimer) {
    clearInterval(radioUpdateTimer);
    radioUpdateTimer = null;
  }
}

try {
  window.updateRadioNowPlaying = updateRadioNowPlaying;
  window.tickRadioNowPlaying = tickRadioNowPlaying;
  window.startRadioUpdates = startRadioUpdates;
  window.stopRadioUpdates = stopRadioUpdates;
  window.startRadioPlaybackPolling = startRadioPlaybackPolling;
  window.stopRadioPlaybackPolling = stopRadioPlaybackPolling;
} catch (_) {}

document.addEventListener('visibilitychange', () => {
  if (document.hidden) return;
  if (isRadioPlaybackActive()) void tickRadioNowPlaying({ force: true });
  else if (state.view === 'home') void tickRadioNowPlaying({ badgeOnly: true, force: true });
});

// ─── UTILS ────────────────────────────────────────────────────────────────────
function esc(s) {
  return String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function profileJsId(id) {
  return String(id || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function looksLikeUserUuidClient(s) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(s || ''));
}

/** Ссылка в хэше: логин, если есть, иначе id (старые закладки). */
function userProfileRef(u) {
  if (!u) return '';
  const lg = String(u.login || '').trim();
  if (lg) return lg;
  return String(u.id || '').trim();
}

function fmtTime(s) {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2,'0')}`;
}
function goBack() {
  const current = (location.hash || '#home').replace(/^#/, '') || 'home';
  while (navHistoryStack.length) {
    const previous = navHistoryStack.pop();
    if (!previous || previous === current) continue;
    suppressNavStackPush = true;
    location.hash = previous;
    return;
  }
  if (current !== 'home') navigate('home');
}
const GRADIENTS = [
  '135deg,#7C3AED,#EC4899','135deg,#4F46E5,#7C3AED','135deg,#6D28D9,#C084FC',
  '135deg,#1D4ED8,#7C3AED','135deg,#9333EA,#E879F9','135deg,#7C3AED,#2563EB',
  '135deg,#8B5CF6,#D946EF','135deg,#5B21B6,#A855F7','135deg,#7E22CE,#F97316',
];
function pickGrad(seed) {
  let h = 0;
  for (const c of String(seed ?? '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
}

/** В WebView lazy+скролл внутри overflow часто не подгружает обложки (старый Chromium / MIUI / Electron). */
function coverImageLoadingAttr() {
  try {
    if (typeof isMxMDesktopApp === 'function' && isMxMDesktopApp()) return 'eager';
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform()) {
      return 'eager';
    }
  } catch (_) {}
  return 'lazy';
}

const NO_ARTIST_COVER_URL = '/images/no-artist-cover.webp';
const ARTIST_AVATAR_UPLOAD_HINT =
  'будем рады, если загрузите аватарку для этого артиста!';

function isPlaceholderArtistPhotoUrl(photoUrl) {
  const raw = String(photoUrl || '').trim();
  if (!raw) return true;
  return /\/no-artist-cover(?:-\d+)?\.(?:webp|png)$/i.test(raw);
}

/** WebP + PNG fallback для яйца MXM (лого / hero). */
function mxmEggPictureHtml(opts = {}) {
  const width = Number(opts.width) || 32;
  const height = Number(opts.height) || width;
  const hero = !!opts.hero;
  const className = opts.className || '';
  const alt = opts.alt != null ? String(opts.alt) : 'MXM';
  const fetchPriority = opts.fetchPriority ? String(opts.fetchPriority) : '';

  let webp = '/images/egg-64.webp';
  let png = '/images/egg-64.png';
  if (hero) {
    webp = '/images/egg.webp';
    png = '/images/egg.png';
  } else if (width > 40) {
    webp = '/images/egg-128.webp';
    png = '/images/egg-128.png';
  }

  const cls = className ? ` class="${esc(className)}"` : '';
  const fp = fetchPriority ? ` fetchpriority="${esc(fetchPriority)}"` : '';
  const loading = hero && fetchPriority !== 'high' ? ' loading="lazy"' : '';

  return `<picture><source srcset="${webp}" type="image/webp"><img src="${png}"${cls} alt="${esc(alt)}" width="${width}" height="${height}" decoding="async"${fp}${loading}></picture>`;
}

function artistHasRealPhoto(artistOrUrl) {
  if (artistOrUrl != null && typeof artistOrUrl === 'object') {
    return !isPlaceholderArtistPhotoUrl(artistOrUrl.photoUrl);
  }
  return !isPlaceholderArtistPhotoUrl(artistOrUrl);
}

function resolveArtistPhotoUrl(photoUrl) {
  const raw = String(photoUrl || '').trim();
  return raw || NO_ARTIST_COVER_URL;
}

/**
 * Аватар артиста: без фото — плейсхолдер no-artist-cover.webp.
 * @param {{ showUploadHint?: boolean }} [opts] — подсказка при hover только на странице артиста (hero).
 */
function artistAvatarHtml(artist, grad, cls = '', opts = {}) {
  const hasPhoto = artistHasRealPhoto(artist);
  const showUploadHint = !!opts.showUploadHint;
  const url = resolveArtistPhotoUrl(artist?.photoUrl);
  const g =
    grad ||
    (artist && artist.photoGradient) ||
    pickGrad((artist && (artist.id || artist.name)) || '');
  const imgCls = [cls, hasPhoto ? '' : 'artist-avatar-img--placeholder'].filter(Boolean).join(' ');
  const imgHtml = coverImg(url, g, imgCls);
  if (hasPhoto || !showUploadHint) return imgHtml;
  return `<span class="artist-avatar-wrap artist-avatar-wrap--no-photo">${imgHtml}<span class="artist-avatar-hint" role="tooltip">${esc(ARTIST_AVATAR_UPLOAD_HINT)}</span></span>`;
}

function mxmImgRetryOnError(img) {
  if (!img) return;
  const grad = String(img.dataset.mxmCoverGrad || '').trim();
  if (!img.dataset.mxmRetried) {
    img.dataset.mxmRetried = '1';
    const src = String(img.getAttribute('src') || '').trim();
    if (src) {
      img.src = src + (src.includes('?') ? '&' : '?') + '_r=' + Date.now();
      return;
    }
  }
  if (!grad) return;
  const wrap = img.parentElement;
  const fallback = document.createElement('div');
  fallback.className = String(img.className || '').replace(/\bfullscreen-cover-img\b/g, '').trim() + ' grad-cover';
  fallback.style.background = `linear-gradient(${grad})`;
  fallback.innerHTML = `<svg viewBox="0 0 24 24" stroke="rgba(255,255,255,.4)" stroke-width="1.5"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>`;
  if (wrap) wrap.replaceChild(fallback, img);
  else img.replaceWith(fallback);
}
window.mxmImgRetryOnError = mxmImgRetryOnError;

function coverImg(url, grad, cls = '') {
  const loadMode =
    cls.includes('fullscreen-cover-img') || coverImageLoadingAttr() === 'eager' ? 'eager' : 'lazy';
  const gradAttr = esc(grad || pickGrad(Math.random()));
  if (url) {
    return `<img src="${esc(resolveMediaUrl(url))}" class="${cls}" alt="" loading="${loadMode}" decoding="async" fetchpriority="${loadMode === 'eager' ? 'high' : 'auto'}" data-mxm-cover-grad="${gradAttr}" onerror="mxmImgRetryOnError(this)">`;
  }
  return `<div class="grad-cover" style="background:linear-gradient(${grad || pickGrad(Math.random())})">
    <svg viewBox="0 0 24 24" stroke="rgba(255,255,255,.4)" stroke-width="1.5">
      <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
    </svg></div>`;
}

// Плавная смена обложки в мини-плеере: старая остаётся до загрузки новой, затем кроссфейд
let _playerCoverSwapToken = 0;

function setPlayerCoverSmooth(track) {
  if (!track) return;
  const isRadio = track.id === MXM_RADIO_TRACK_ID;
  const coverUrl = track.coverUrl ? String(track.coverUrl).trim() : '';
  const current = $('player-cover');
  const hasCurrentImg = !!current?.querySelector('img');
  // Радио: не затираем загруженную обложку градиентом из playTrack / пустого meta
  if (isRadio && !coverUrl && hasCurrentImg) return;

  const fallbackGrad = track.coverGradient || pickGrad(track.id);
  const newHtml = coverImg(coverUrl, fallbackGrad);
  const wrap = document.querySelector('.player-cover-wrap');
  const newLayer = $('player-cover-new');
  if (!wrap || !current || !newLayer) return;

  const swapToken = ++_playerCoverSwapToken;
  const isFirst = !current.innerHTML.trim();
  if (isFirst) {
    current.innerHTML = newHtml;
    newLayer.innerHTML = '';
    wrap.classList.remove('cover-crossfade');
    return;
  }
  newLayer.innerHTML = newHtml;
  wrap.classList.remove('cover-crossfade');
  const img = newLayer.querySelector('img');
  const runCrossfade = () => {
    if (swapToken !== _playerCoverSwapToken) {
      wrap.classList.remove('cover-crossfade');
      return;
    }
    if (!state.currentTrack || state.currentTrack.id !== track.id) return;
    wrap.classList.add('cover-crossfade');
    setTimeout(() => {
      if (swapToken !== _playerCoverSwapToken) {
        wrap.classList.remove('cover-crossfade');
        return;
      }
      if (!state.currentTrack || state.currentTrack.id !== track.id) return;
      current.innerHTML = '';
      while (newLayer.firstChild) current.appendChild(newLayer.firstChild);
      wrap.classList.remove('cover-crossfade');
    }, 350);
  };
  if (img && coverUrl) {
    if (img.complete) runCrossfade();
    else {
      img.onload = runCrossfade;
      img.onerror = runCrossfade;
    }
  } else {
    requestAnimationFrame(runCrossfade);
  }
}

// Кэш для уже обработанных изображений (чтобы не пересчитывать)
const colorCache = new Map();
/** Один активный расчёт на URL: параллельные вызовы (мини-плеер + fullscreen) делят тот же Promise. */
const gradientFromImageInflight = new Map();

function hexToRgb(hex) {
  const raw = String(hex || '').replace('#', '').trim();
  if (!raw) return null;
  const normalized = raw.length === 3
    ? raw.split('').map((ch) => ch + ch).join('')
    : raw;
  if (!/^[\da-f]{6}$/i.test(normalized)) return null;
  const num = parseInt(normalized, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255
  };
}

function parseCssColor(color) {
  const raw = String(color || '').trim();
  if (!raw) return null;
  if (raw.startsWith('#')) return hexToRgb(raw);
  const rgbMatch = raw.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgbMatch) {
    return {
      r: Math.max(0, Math.min(255, Number(rgbMatch[1]))),
      g: Math.max(0, Math.min(255, Number(rgbMatch[2]))),
      b: Math.max(0, Math.min(255, Number(rgbMatch[3])))
    };
  }
  return null;
}

function rgbToHsl({ r, g, b }) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;

  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn: h = 60 * (((gn - bn) / d) % 6); break;
      case gn: h = 60 * (((bn - rn) / d) + 2); break;
      default: h = 60 * (((rn - gn) / d) + 4); break;
    }
  }

  return {
    h: (h + 360) % 360,
    s,
    l
  };
}

function hslToRgb({ h, s, l }) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r1 = 0, g1 = 0, b1 = 0;

  if (h < 60) [r1, g1, b1] = [c, x, 0];
  else if (h < 120) [r1, g1, b1] = [x, c, 0];
  else if (h < 180) [r1, g1, b1] = [0, c, x];
  else if (h < 240) [r1, g1, b1] = [0, x, c];
  else if (h < 300) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];

  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255)
  };
}

function rgbDistance(a, b) {
  return Math.sqrt(
    ((a.r || 0) - (b.r || 0)) ** 2
    + ((a.g || 0) - (b.g || 0)) ** 2
    + ((a.b || 0) - (b.b || 0)) ** 2
  );
}

function serializeRgb({ r, g, b }) {
  return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
}

function normalizeGradientColorForWhiteText(color) {
  const rgb = parseCssColor(color);
  if (!rgb) return color;
  const hsl = rgbToHsl(rgb);
  const isNeutral = hsl.s < 0.12;

  const next = {
    h: hsl.h,
    s: isNeutral ? hsl.s : Math.max(hsl.s, 0.24),
    l: Math.min(hsl.l, isNeutral ? 0.34 : 0.48)
  };

  if (next.l < 0.16) next.l = 0.16;
  return serializeRgb(hslToRgb(next));
}

function scoreGradientColor(color) {
  const rgb = parseCssColor(color);
  if (!rgb) return { raw: color, score: -Infinity, rgb: null, hsl: null };
  const hsl = rgbToHsl(rgb);
  const white = { r: 255, g: 255, b: 255 };
  const distanceFromWhite = rgbDistance(rgb, white);
  const chroma = Math.max(rgb.r, rgb.g, rgb.b) - Math.min(rgb.r, rgb.g, rgb.b);

  let score = distanceFromWhite * 0.62 + chroma * 1.15 + hsl.s * 135;
  if (hsl.l > 0.78) score -= 260;
  else if (hsl.l > 0.68) score -= 120;
  if (hsl.l < 0.08) score -= 40;

  return { raw: color, score, rgb, hsl, distanceFromWhite, chroma };
}

function pickReadableGradientColors(colors) {
  const scored = Array.from(new Set((colors || []).filter(Boolean)))
    .map(scoreGradientColor)
    .filter((entry) => entry.rgb)
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return colors;

  const preferred = scored.filter((entry) =>
    entry.hsl.l < 0.72 && (entry.hsl.s > 0.15 || entry.chroma > 28 || entry.distanceFromWhite > 170)
  );
  const pool = preferred.length ? preferred : scored;
  const first = pool[0];
  const second = pool.find((entry) =>
    entry !== first
    && (
      Math.abs(entry.hsl.h - first.hsl.h) > 18
      || Math.abs(entry.hsl.l - first.hsl.l) > 0.08
      || rgbDistance(entry.rgb, first.rgb) > 42
    )
  ) || pool[1] || first;

  return [
    normalizeGradientColorForWhiteText(first.raw),
    normalizeGradientColorForWhiteText(second.raw)
  ];
}

// Функция для получения градиента из изображения (с кэшем по URL и дедупом параллельных запросов)
async function getGradientFromImage(imgUrl) {
  if (!imgUrl) return null;
  const resolvedImgUrl = resolveMediaUrl(imgUrl);

  if (colorCache.has(resolvedImgUrl)) {
    const cached = colorCache.get(resolvedImgUrl);
    if (cached !== 'FAILED') return cached;
    colorCache.delete(resolvedImgUrl);
  }

  const existing = gradientFromImageInflight.get(resolvedImgUrl);
  if (existing) return existing;

  const work = (async () => {
    try {
      await ensureColorToolsLoaded();
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = resolvedImgUrl;

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      let colors = [];

      // Попытка 1: Vibrant
      try {
        if (window.Vibrant) {
          const vibrant = new Vibrant(img);
          const swatches = vibrant.swatches();
          if (swatches) {
            if (swatches.Vibrant) colors.push(swatches.Vibrant.getHex());
            if (swatches.Muted) colors.push(swatches.Muted.getHex());
            if (colors.length === 0 && swatches.DarkVibrant) colors.push(swatches.DarkVibrant.getHex());
            if (colors.length === 0 && swatches.LightVibrant) colors.push(swatches.LightVibrant.getHex());
          }
        }
      } catch (e) {
        console.warn('Vibrant failed', e);
      }

      // Попытка 2: Color Thief, если Vibrant не дал цветов
      if (colors.length === 0 && window.ColorThief) {
        try {
          const colorThief = new ColorThief();
          const palette = colorThief.getPalette(img, 2);
          if (palette && palette.length) {
            colors = palette.map((rgb) => `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`);
          }
        } catch (e) {
          console.warn('ColorThief failed', e);
        }
      }

      if (colors.length === 0) {
        try {
          const sampleCanvas = document.createElement('canvas');
          const sampleSize = 28;
          sampleCanvas.width = sampleSize;
          sampleCanvas.height = sampleSize;
          const ctx = sampleCanvas.getContext('2d', { willReadFrequently: true });
          if (ctx) {
            ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
            const { data } = ctx.getImageData(0, 0, sampleSize, sampleSize);
            let r1 = 0,
              g1 = 0,
              b1 = 0,
              c1 = 0;
            let r2 = 0,
              g2 = 0,
              b2 = 0,
              c2 = 0;
            for (let y = 0; y < sampleSize; y++) {
              for (let x = 0; x < sampleSize; x++) {
                const idx = (y * sampleSize + x) * 4;
                const alpha = data[idx + 3];
                if (alpha < 10) continue;
                if (x < sampleSize * 0.55) {
                  r1 += data[idx];
                  g1 += data[idx + 1];
                  b1 += data[idx + 2];
                  c1++;
                } else {
                  r2 += data[idx];
                  g2 += data[idx + 1];
                  b2 += data[idx + 2];
                  c2++;
                }
              }
            }
            if (c1 > 0) colors.push(`rgb(${Math.round(r1 / c1)},${Math.round(g1 / c1)},${Math.round(b1 / c1)})`);
            if (c2 > 0) colors.push(`rgb(${Math.round(r2 / c2)},${Math.round(g2 / c2)},${Math.round(b2 / c2)})`);
          }
        } catch (e) {
          console.warn('Canvas color sample failed', e);
        }
      }

      if (colors.length === 0) {
        colorCache.set(resolvedImgUrl, 'FAILED');
        return null;
      }

      if (isMobile()) {
        colors = pickReadableGradientColors(colors);
      }

      let gradient;
      if (colors.length >= 2) {
        gradient = `135deg, ${colors[0]}, ${colors[1]}`;
      } else {
        gradient = `135deg, ${colors[0]}, #000000`;
      }

      colorCache.set(resolvedImgUrl, gradient);
      if (colorCache.size > MAX_COLOR_CACHE_ENTRIES) {
        const oldestKey = colorCache.keys().next().value;
        if (oldestKey) colorCache.delete(oldestKey);
      }
      return gradient;
    } catch (e) {
      console.error('getGradientFromImage: error', e);
      colorCache.set(resolvedImgUrl, 'FAILED');
      return null;
    } finally {
      gradientFromImageInflight.delete(resolvedImgUrl);
    }
  })();

  gradientFromImageInflight.set(resolvedImgUrl, work);
  return work;
}

/** Клик по названию альбома в строке трека — только десктоп (сайт и Electron). */
function canLinkTrackAlbumInList() {
  try {
    if (typeof isMxMDesktopApp === 'function' && isMxMDesktopApp()) return true;
    return typeof window !== 'undefined' && window.innerWidth > 900;
  } catch {
    return false;
  }
}

// Закрытие меню плеера (вызывается при выборе пункта или клике снаружи)
function closePlayerMenu() {
  const menu = $('player-menu');
  const btn = $('player-menu-btn');
  if (menu) menu.classList.remove('show');
  if (btn) btn.classList.remove('active');
}

function syncPlayerAlbumMenuItem(track) {
  const btn = $('player-menu-album');
  if (!btn) return;
  const id = track?.albumId ? String(track.albumId).trim() : '';
  const show = !!(id && track?.id !== MXM_RADIO_TRACK_ID);
  btn.classList.toggle('hidden', !show);
  if (show) btn.dataset.albumId = id;
  else delete btn.dataset.albumId;
}

function goToCurrentTrackAlbum() {
  closePlayerMenu();
  const track = state.currentTrack;
  const id = track?.albumId ? String(track.albumId).trim() : '';
  if (!id || track?.id === MXM_RADIO_TRACK_ID) {
    toast('Альбом не указан', 'error');
    return;
  }
  navigate('album', { id });
}
window.goToCurrentTrackAlbum = goToCurrentTrackAlbum;

// Открытие/закрытие меню плеера
function togglePlayerMenu() {
  const menu = $('player-menu');
  const btn = $('player-menu-btn');
  if (!menu || !btn) return;

  if (menu.classList.contains('show')) {
    closePlayerMenu();
  } else {
    // Очередь выше по z-index и иначе перекрывает меню ⋯
    if (typeof closeQueueSheet === 'function') closeQueueSheet();
    menu.classList.add('show');
    btn.classList.add('active');
    const closeOnOutside = (e) => {
      if (!menu.contains(e.target) && !btn.contains(e.target)) {
        closePlayerMenu();
        document.removeEventListener('click', closeOnOutside);
      }
    };
    setTimeout(() => document.addEventListener('click', closeOnOutside), 10);
  }
}

// Обновление цветной полоски громкости (мини-плеер + fullscreen)
function updateVolumeFill(value) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  document.querySelectorAll('.volume-fill').forEach((fill) => {
    fill.style.width = `${v}%`;
  });
}

// Троттл только для localStorage и синхронизации при перетаскивании ползунка (звук меняется сразу)
let _volumeSyncTimer = null;
function scheduleVolumeSync(val) {
  if (_volumeSyncTimer) clearTimeout(_volumeSyncTimer);
  _volumeSyncTimer = setTimeout(() => {
    localStorage.setItem('mxm_volume', val / 100);
    const fsVolumeRange = $('fullscreen-volume-range');
    if (fsVolumeRange) fsVolumeRange.value = val;
    updateVolumeFill(val);
    syncMuteUI();
    _volumeSyncTimer = null;
  }, 80);
}

// Десктоп: громкость колёсиком мыши над блоком громкости (нижний и полноэкранный плеер)
function setupVolumeWheel() {
  const run = (slider, e) => {
    if (e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    const step = e.deltaY > 0 ? -4 : 4;
    const next = Math.max(0, Math.min(100, Number(slider.value) + step));
    slider.value = next;
    setVolume(next);
  };
  const ctrl = document.querySelector('.volume-ctrl');
  const slider = $('volume-range');
  if (ctrl && slider) {
    ctrl.addEventListener('wheel', (e) => run(slider, e), { passive: false });
  }
  const fsWrap = document.querySelector('.fullscreen-volume-wrap');
  if (fsWrap) {
    fsWrap.addEventListener('wheel', (e) => {
      const fsRange = $('fullscreen-volume-range');
      if (fsRange) run(fsRange, e);
    }, { passive: false });
  }
}

// нициализация заполнения при загрузке
document.addEventListener('DOMContentLoaded', () => {
  const volRange = $('volume-range');
  if (volRange) updateVolumeFill(volRange.value);
  setupVolumeWheel();
});


// Вспомогательная функция для затемнения цвета
function adjustBrightness(hex, percent) {
  // упрощённо: можно вернуть просто чёрный с прозрачностью или использовать библиотеку
  // Для простоты вернём чёрный с альфа-каналом
  return `rgba(0,0,0,${1 - percent})`;
}

function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

// Нормализация текста: убирает множественные пустые строки, оставляя не более одной между строками
function normalizeLyrics(text) {
  if (!text) return text;
  const lines = text.split('\n');
  const result = [];
  let lastWasEmpty = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line === '') {
      // Пустая строка: добавляем только если предыдущая не была пустой и это не самое начало
      if (!lastWasEmpty && result.length > 0) {
        result.push('');
        lastWasEmpty = true;
      }
    } else {
      result.push(line);
      lastWasEmpty = false;
    }
  }

  // Убираем пустые строки ? конце
  while (result.length > 0 && result[result.length - 1] === '') {
    result.pop();
  }

  return result.join('\n');
}

// Форматирование текста: [] и () — один класс .lyrics-section (приглушённый цвет; <i> ломал каскад из‑за color !important у .fullscreen-right)
function formatLyrics(lyrics) {
  if (!lyrics) return '';
  const escaped = esc(lyrics);
  // Квадратные скобки (куплеты / метки)
  let formatted = escaped.replace(/\[([^\]]+)\]/g, '<em class="lyrics-section">[$1]</em>');
  // Круглые скобки — те же стили, что у секций (em, не i)
  formatted = formatted.replace(/\(([^)]+)\)/g, '<em class="lyrics-section">($1)</em>');
  // Типографские скобки U+FF08 / U+FF09 (часто в копипасте)
  formatted = formatted.replace(/\uff08([^\uff09]+)\uff09/g, '<em class="lyrics-section">（$1）</em>');
  return formatted;
}
// ─── TOAST ────────────────────────────────────────────────────────────────────
const TOAST_LIFE_MS = 2600;
const TOAST_ERROR_LIFE_MS = 4200;
const TOAST_STACK_MAX = 4;
/** На узких экранах — короче и меньше стека, чтобы не забивать UI */
const TOAST_LIFE_MOBILE_MS = 2000;
const TOAST_ERROR_LIFE_MOBILE_MS = 3400;
const TOAST_STACK_MAX_MOBILE = 2;

function toast(msg, type = '') {
  const container = $('toast-container');
  if (!container || !msg) return;

  const narrow =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(max-width: 900px)').matches;
  const stackMax = narrow ? TOAST_STACK_MAX_MOBILE : TOAST_STACK_MAX;
  const lifeMs =
    type === 'error'
      ? narrow
        ? TOAST_ERROR_LIFE_MOBILE_MS
        : TOAST_ERROR_LIFE_MS
      : narrow
        ? TOAST_LIFE_MOBILE_MS
        : TOAST_LIFE_MS;

  const el = document.createElement('div');
  el.className = `toast${type ? ` ${type}` : ''}`;
  el.setAttribute('role', type === 'error' ? 'alert' : 'status');
  el.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');

  const text = document.createElement('span');
  text.className = 'toast-text';
  text.textContent = msg;

  if (type === 'success' || type === 'error') {
    const icon = document.createElement('span');
    icon.className = 'toast-icon';
    icon.setAttribute('aria-hidden', 'true');
    el.append(icon, text);
  } else {
    el.classList.add('toast--plain');
    el.appendChild(text);
  }
  if (narrow) {
    container.insertBefore(el, container.firstChild);
    while (container.children.length > stackMax) {
      container.removeChild(container.lastChild);
    }
  } else {
    container.appendChild(el);
    while (container.children.length > stackMax) {
      container.removeChild(container.firstChild);
    }
  }

  const dismiss = () => {
    if (!el.parentNode) return;
    el.classList.add('toast-out');
    const finish = () => {
      el.removeEventListener('animationend', finish);
      el.remove();
    };
    el.addEventListener('animationend', finish, { once: true });
    setTimeout(() => {
      if (el.parentNode) el.remove();
    }, 400);
  };

  window.setTimeout(dismiss, lifeMs);
}

// ─── AUTH MODAL ───────────────────────────────────────────────────────────────
let _authModalAllowClose = false;

function mxmDispatchUserChanged() {
  try {
    document.dispatchEvent(new CustomEvent('mxm:user-changed', { detail: { user: state.user } }));
  } catch (_) {}
}

/** Сброс вкладок/панелей входа перед показом desktop gate (в т.ч. после logout). */
function resetDesktopAuthGateForms(tab = 'login') {
  resetRegisterVerificationUi();
  const forgot = $('forgot-password-panel');
  if (forgot) forgot.classList.add('hidden');
  if (typeof switchAuthTab === 'function') switchAuthTab(tab);
}

/**
 * Полноэкранный gate на desktop: фон + форма, без интерфейса приложения.
 * Работает и до загрузки 07-electron (fallback для init/openAuthModal).
 */
function applyDesktopAuthGateShell(tab = 'login') {
  if (typeof isMxMDesktopApp !== 'function' || !isMxMDesktopApp() || state.user) return false;
  try {
    document.documentElement.classList.add('mxm-electron-shell', 'mxm-desktop-shell', 'mxm-desktop-auth-locked');
  } catch (_) {}
  const modal = document.getElementById('auth-modal');
  if (modal) {
    if (document.body && modal.parentElement !== document.body) document.body.appendChild(modal);
    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    const box = modal.querySelector('.modal-box');
    if (box) {
      box.style.animation = 'none';
      box.style.opacity = '1';
      box.style.transform = 'none';
      void box.offsetHeight;
      box.style.removeProperty('animation');
      box.style.removeProperty('opacity');
      box.style.removeProperty('transform');
    }
  }
  resetDesktopAuthGateForms(tab);
  return true;
}
window.applyDesktopAuthGateShell = applyDesktopAuthGateShell;
window.resetDesktopAuthGateForms = resetDesktopAuthGateForms;

function openAuthModal(tab = 'login') {
  if (typeof isMxMDesktopApp === 'function' && isMxMDesktopApp() && !state.user) {
    if (typeof window.mxmDesktopShowAuthGate === 'function') {
      window.mxmDesktopShowAuthGate(tab);
      return;
    }
    if (applyDesktopAuthGateShell(tab)) return;
  }
  $('auth-modal').classList.remove('hidden');
  switchAuthTab(tab);
}
function closeAuthModal() {
  const desktopGateLocked =
    typeof isMxMDesktopApp === 'function' &&
    isMxMDesktopApp() &&
    !state.user &&
    (document.documentElement.classList.contains('mxm-desktop-auth-locked') ||
      (typeof window.mxmDesktopAuthGateActive === 'function' && window.mxmDesktopAuthGateActive()));
  if (!_authModalAllowClose && desktopGateLocked) return;
  _authModalAllowClose = false;
  $('auth-modal').classList.add('hidden');
  resetRegisterVerificationUi();
  if (typeof window.mxmDesktopHideAuthGate === 'function') {
    window.mxmDesktopHideAuthGate();
  } else if (desktopGateLocked) {
    document.documentElement.classList.remove('mxm-desktop-auth-locked');
  }
}
function closeAuthModalAfterSuccess() {
  _authModalAllowClose = true;
  closeAuthModal();
}

// ─── SUPPORT MODAL ───────────────────────────────────────────────────────────
function openSupportModal() {
  const m = $('support-modal');
  if (!m) return;
  const subj = $('support-subject');
  const msg = $('support-message');
  const em = $('support-email');
  const err = $('support-error');
  const emailRow = $('support-email-row');
  const hint = $('support-logged-hint');
  if (subj) subj.value = '';
  if (msg) msg.value = '';
  if (em) em.value = '';
  if (err) err.classList.add('hidden');
  if (state.user) {
    if (emailRow) emailRow.classList.add('hidden');
    if (em) em.removeAttribute('required');
    if (hint) {
      hint.textContent = `Вы вошли как ${state.user.username || 'пользователь'} — к обращению будет привязан ваш аккаунт.`;
      hint.classList.remove('hidden');
    }
  } else {
    if (emailRow) emailRow.classList.remove('hidden');
    if (em) em.setAttribute('required', 'required');
    if (hint) hint.classList.add('hidden');
  }
  m.classList.remove('hidden');
  requestAnimationFrame(() => msg?.focus());
}
function closeSupportModal() {
  $('support-modal')?.classList.add('hidden');
}
window.openSupportModal = openSupportModal;
window.closeSupportModal = closeSupportModal;
async function submitSupportTicket(e) {
  e.preventDefault();
  const errEl = $('support-error');
  if (errEl) errEl.classList.add('hidden');
  const subject = String($('support-subject')?.value || '').trim();
  const message = String($('support-message')?.value || '').trim();
  const contactEmail = state.user ? '' : String($('support-email')?.value || '').trim();
  const body = { subject: subject || undefined, message };
  if (!state.user) body.contactEmail = contactEmail;
  const btn = e.target.querySelector('button[type="submit"]');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Отправка…';
  }
  try {
    await POST('/api/support/tickets', body);
    toast('Обращение отправлено. Спасибо!', 'success');
    closeSupportModal();
  } catch (err) {
    if (errEl) {
      errEl.textContent = err.message || 'Не удалось отправить';
      errEl.classList.remove('hidden');
    }
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Отправить';
    }
  }
}

function openWantToHearModal() {
  const m = $('want-to-hear-modal');
  if (!m) return;
  const msg = $('want-to-hear-message');
  const err = $('want-to-hear-error');
  if (msg) msg.value = '';
  if (err) err.classList.add('hidden');
  m.classList.remove('hidden');
  requestAnimationFrame(() => msg?.focus());
}

function closeWantToHearModal() {
  $('want-to-hear-modal')?.classList.add('hidden');
}

window.openWantToHearModal = openWantToHearModal;
window.closeWantToHearModal = closeWantToHearModal;

async function submitWantToHear(e) {
  e.preventDefault();
  const errEl = $('want-to-hear-error');
  if (errEl) errEl.classList.add('hidden');
  const message = String($('want-to-hear-message')?.value || '').trim();
  const btn = e.target.querySelector('button[type="submit"]');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Отправка…';
  }
  try {
    await POST('/api/want-to-hear', { message });
    toast('Спасибо! Пожелание отправлено.', 'success');
    closeWantToHearModal();
  } catch (err) {
    if (errEl) {
      errEl.textContent = err.message || 'Не удалось отправить';
      errEl.classList.remove('hidden');
    }
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Отправить';
    }
  }
}

/** Активная заявка на регистрацию (код на email), пока не закрыли модалку. */
let _mxmRegPendingId = null;
/** Сколько ещё раз можно нажать «Отправить код ещё раз» (сервер: макс. 2). */
let _mxmRegResendsRemaining = 2;
/** Не раньше этого времени (ms) разрешена следующая повторная отправка (30 с с сервера). */
let _mxmRegResendUnlockAt = 0;
let _mxmRegResendTimer = null;

function mxmClearRegResendTimer() {
  if (_mxmRegResendTimer) {
    clearInterval(_mxmRegResendTimer);
    _mxmRegResendTimer = null;
  }
}

function mxmUpdateRegResendButtonUi() {
  const btn = $('reg-verify-resend-btn');
  if (!btn) return;
  const now = Date.now();
  const locked = now < _mxmRegResendUnlockAt;
  const noMore = _mxmRegResendsRemaining <= 0;
  if (noMore) {
    btn.disabled = true;
    btn.textContent = 'Повторная отправка недоступна';
    mxmClearRegResendTimer();
    return;
  }
  if (locked) {
    const s = Math.max(1, Math.ceil((_mxmRegResendUnlockAt - now) / 1000));
    btn.disabled = true;
    btn.textContent = `Снова через ${s} с`;
    return;
  }
  btn.disabled = false;
  btn.textContent = 'Отправить код ещё раз';
}

function mxmStartRegResendCooldownTimer() {
  mxmClearRegResendTimer();
  mxmUpdateRegResendButtonUi();
  _mxmRegResendTimer = setInterval(() => {
    mxmUpdateRegResendButtonUi();
    const locked = Date.now() < _mxmRegResendUnlockAt;
    if (!locked) {
      mxmClearRegResendTimer();
      mxmUpdateRegResendButtonUi();
    }
  }, 300);
}

function resetRegisterVerificationUi() {
  _mxmRegPendingId = null;
  _mxmRegResendsRemaining = 2;
  _mxmRegResendUnlockAt = 0;
  mxmClearRegResendTimer();
  const verify = $('register-verify-panel');
  if (verify) verify.classList.add('hidden');
  const regForm = $('register-form');
  if (regForm) regForm.classList.remove('hidden');
  const resBtn = $('reg-verify-resend-btn');
  if (resBtn) {
    resBtn.disabled = false;
    resBtn.textContent = 'Отправить код ещё раз';
  }
  for (let i = 0; i < 4; i++) {
    const el = document.getElementById(`reg-code-${i}`);
    if (el) el.value = '';
  }
  const verr = $('reg-verify-error');
  if (verr) {
    verr.textContent = '';
    verr.classList.add('hidden');
  }
}

function showRegisterVerifyPanel(pendingId, emailMasked, meta) {
  _mxmRegPendingId = pendingId || null;
  _mxmRegResendsRemaining =
    meta && meta.resendsRemaining != null ? Math.max(0, Number(meta.resendsRemaining)) : 2;
  const coolSec = meta && meta.resendCooldownSec != null ? Math.max(1, Number(meta.resendCooldownSec)) : 30;
  _mxmRegResendUnlockAt = Date.now() + coolSec * 1000;
  const maskEl = $('reg-verify-email-mask');
  if (maskEl) maskEl.textContent = emailMasked || '…';
  const regForm = $('register-form');
  if (regForm) regForm.classList.add('hidden');
  const verify = $('register-verify-panel');
  if (verify) verify.classList.remove('hidden');
  mxmBindRegCodeCellsOnce();
  for (let i = 0; i < 4; i++) {
    const el = document.getElementById(`reg-code-${i}`);
    if (el) el.value = '';
  }
  const verr = $('reg-verify-error');
  if (verr) {
    verr.textContent = '';
    verr.classList.add('hidden');
  }
  mxmStartRegResendCooldownTimer();
  requestAnimationFrame(() => document.getElementById('reg-code-0')?.focus());
}

let _mxmRegCodeCellsBound = false;
function mxmBindRegCodeCellsOnce() {
  if (_mxmRegCodeCellsBound) return;
  _mxmRegCodeCellsBound = true;
  for (let i = 0; i < 4; i++) {
    const el = document.getElementById(`reg-code-${i}`);
    if (!el) continue;
    el.addEventListener('focus', () => {
      try {
        el.select();
      } catch (_) {}
    });
    el.addEventListener('input', () => mxmOnRegCodeCellInput(el));
    el.addEventListener('keydown', (ev) => mxmOnRegCodeCellKeydown(ev, el));
    el.addEventListener('paste', (ev) => mxmOnRegCodePaste(ev));
  }
}

function mxmRegCodeCellIndex(el) {
  const n = parseInt(el?.dataset?.regCodeIndex ?? el?.getAttribute('data-reg-code-index'), 10);
  return Number.isFinite(n) ? n : 0;
}

function mxmOnRegCodeCellInput(el) {
  let v = String(el.value || '').replace(/\D/g, '');
  if (v.length > 1) v = v.slice(-1);
  el.value = v;
  const idx = mxmRegCodeCellIndex(el);
  if (v && idx < 3) {
    document.getElementById(`reg-code-${idx + 1}`)?.focus();
  }
}

function mxmOnRegCodeCellKeydown(ev, el) {
  const idx = mxmRegCodeCellIndex(el);
  if (ev.key === 'Backspace' && !el.value && idx > 0) {
    ev.preventDefault();
    document.getElementById(`reg-code-${idx - 1}`)?.focus();
  }
}

function mxmOnRegCodePaste(ev) {
  ev.preventDefault();
  const text = (ev.clipboardData || window.clipboardData)?.getData('text') || '';
  const digits = String(text).replace(/\D/g, '').slice(0, 4);
  for (let i = 0; i < 4; i++) {
    const cell = document.getElementById(`reg-code-${i}`);
    if (cell) cell.value = digits[i] || '';
  }
  const focusIdx = Math.min(digits.length, 3);
  document.getElementById(`reg-code-${focusIdx}`)?.focus();
}

function mxmCollectRegCodeDigits() {
  let s = '';
  for (let i = 0; i < 4; i++) {
    s += String(document.getElementById(`reg-code-${i}`)?.value || '').replace(/\D/g, '');
  }
  return s.slice(0, 4);
}

function mxmPulseRegCodeRow() {
  for (let i = 0; i < 4; i++) {
    const el = document.getElementById(`reg-code-${i}`);
    if (!el) continue;
    el.classList.remove('reg-code-cell--pulse');
    void el.offsetWidth;
    el.classList.add('reg-code-cell--pulse');
  }
}

function switchAuthTab(tab) {
  document.querySelectorAll('.auth-tab').forEach((t) => t.classList.toggle('active', t.dataset.tab === tab));
  const forgot = $('forgot-password-panel');
  if (forgot) forgot.classList.add('hidden');
  const verify = $('register-verify-panel');
  if (tab === 'login') {
    if (verify) verify.classList.add('hidden');
    $('login-form')?.classList.remove('hidden');
    $('register-form')?.classList.add('hidden');
    return;
  }
  if (tab === 'register') {
    $('login-form')?.classList.add('hidden');
    if (verify && _mxmRegPendingId) {
      $('register-form')?.classList.add('hidden');
      verify.classList.remove('hidden');
    } else {
      if (verify) verify.classList.add('hidden');
      $('register-form')?.classList.remove('hidden');
    }
  }
}

function openForgotPasswordPanel() {
  const login = $('login-form');
  const forgot = $('forgot-password-panel');
  const err = $('forgot-error');
  const ok = $('forgot-success');
  const em = $('forgot-email');
  if (login) login.classList.add('hidden');
  if (forgot) forgot.classList.remove('hidden');
  document.querySelectorAll('.auth-tab').forEach((t) => t.classList.remove('active'));
  if (err) err.classList.add('hidden');
  if (ok) {
    ok.classList.add('hidden');
    ok.textContent = '';
  }
  if (em) {
    const le = $('login-email');
    if (le && le.value) em.value = le.value;
    em.focus();
  }
}

function closeForgotPasswordPanel() {
  const login = $('login-form');
  const forgot = $('forgot-password-panel');
  if (forgot) forgot.classList.add('hidden');
  if (login) login.classList.remove('hidden');
  const loginTab = document.querySelector('.auth-tab[data-tab="login"]');
  const regTab = document.querySelector('.auth-tab[data-tab="register"]');
  if (loginTab) loginTab.classList.add('active');
  if (regTab) regTab.classList.remove('active');
}

async function submitForgotPasswordRequest() {
  const em = $('forgot-email');
  const err = $('forgot-error');
  const ok = $('forgot-success');
  const email = String(em?.value || '').trim();
  if (!email) {
    if (err) {
      err.textContent = 'Укажите email';
      err.classList.remove('hidden');
    }
    return;
  }
  if (err) err.classList.add('hidden');
  if (ok) ok.classList.add('hidden');
  try {
    const d = await POST('/api/auth/forgot-password', { email });
    if (ok) {
      ok.textContent = d.message || 'Готово.';
      ok.classList.remove('hidden');
    }
  } catch (e) {
    if (ok) {
      ok.classList.add('hidden');
      ok.textContent = '';
    }
    if (err) {
      err.textContent = e.message || 'Не удалось отправить';
      err.classList.remove('hidden');
    }
  }
}

window.openForgotPasswordPanel = openForgotPasswordPanel;
window.closeForgotPasswordPanel = closeForgotPasswordPanel;
window.submitForgotPasswordRequest = submitForgotPasswordRequest;
async function handleLogin(e) {
  e.preventDefault();
  const errEl = $('login-error');
  errEl.classList.add('hidden');
  const btn = e.target.querySelector('button[type=submit]');
  btn.disabled = true; btn.textContent = 'Вхожу...';
  try {
    const d = await POST('/api/auth/login', { email: $('login-email').value, password: $('login-password').value });
    state.token = d.token; state.user = d.user;
    localStorage.setItem('mxm_token', d.token);
    if (d.user?.avatarUrl) state.user.avatarUrl = cacheAvatarUrl(d.user.avatarUrl);
    closeAuthModalAfterSuccess();
    updateUserUI();
    loadFavorites();
    if (typeof mxmChatUpdateUnreadBadge === 'function') mxmChatUpdateUnreadBadge();
    if (typeof mxmChatConnectWs === 'function') mxmChatConnectWs();
    if (typeof window.mxmDesktopOnAuthSuccess === 'function') {
      await window.mxmDesktopOnAuthSuccess();
    } else {
      void refreshActiveViewAfterAuth();
    }

  } catch (err) {
    errEl.textContent = err.message; errEl.classList.remove('hidden');
  } finally { btn.disabled = false; btn.textContent = 'Войти'; }
}
/** Логин в регистрации: как на сервере — только a–z, 0–9, _ (ввод в нижнем регистре). */
window.onRegLoginLatinInput = function (ev) {
  const el = ev?.target;
  if (!el || el.id !== 'reg-login') return;
  let v = String(el.value || '').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
  if (v.length > 24) v = v.slice(0, 24);
  if (el.value !== v) el.value = v;
};

window.toggleRegPasswordVisibility = function (btn) {
  const wrap = btn?.closest('.password-field');
  const input = wrap?.querySelector('input');
  if (!input || !wrap) return;
  const eye = btn.querySelector('.auth-pw-eye');
  const show = input.type === 'password';
  input.type = show ? 'text' : 'password';
  btn.setAttribute('aria-pressed', show ? 'true' : 'false');
  btn.setAttribute('aria-label', show ? 'Скрыть пароль' : 'Показать пароль');
  btn.title = show ? 'Скрыть пароль' : 'Показать пароль';
  wrap.classList.toggle('is-visible', show);
  if (eye) {
    eye.classList.remove('auth-pw-eye--peek');
    void eye.offsetWidth;
    if (show) {
      eye.classList.add('auth-pw-eye--peek');
      const done = () => {
        eye.classList.remove('auth-pw-eye--peek');
        eye.removeEventListener('animationend', done);
      };
      eye.addEventListener('animationend', done, { once: true });
    }
  }
};

async function handleRegister(e) {
  e.preventDefault();
  const errEl = $('reg-error');
  errEl.classList.add('hidden');
  const btn = e.target.querySelector('button[type=submit]') || $('reg-submit-btn');
  btn.disabled = true;
  btn.textContent = 'Отправляем код…';
  try {
    const loginClean = String($('reg-login').value || '')
      .replace(/[^a-zA-Z0-9_]/g, '')
      .toLowerCase()
      .slice(0, 24);
    if ($('reg-login').value !== loginClean) $('reg-login').value = loginClean;
    if (loginClean.length < 3) {
      errEl.textContent = 'Логин: минимум 3 символа (латиница a–z, цифры, _)';
      errEl.classList.remove('hidden');
      return;
    }
    const d = await POST('/api/auth/register-send-code', {
      username: $('reg-username').value,
      login: loginClean,
      email: $('reg-email').value,
      password: $('reg-password').value
    });
    showRegisterVerifyPanel(d.pendingId, d.emailMasked, {
      resendsRemaining: d.resendsRemaining,
      resendCooldownSec: d.resendCooldownSec
    });
  } catch (err) {
    errEl.textContent = err.message;
    errEl.classList.remove('hidden');
  } finally {
    btn.disabled = false;
    btn.textContent = 'Получить код на email';
  }
}

async function handleRegisterVerifySubmit(e) {
  e.preventDefault();
  const errEl = $('reg-verify-error');
  if (errEl) {
    errEl.classList.add('hidden');
    errEl.textContent = '';
  }
  const btn = $('reg-verify-submit-btn');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Создаём аккаунт…';
  }
  const code = mxmCollectRegCodeDigits();
  if (code.length !== 4) {
    if (errEl) {
      errEl.textContent = 'Введите все 4 цифры кода из письма';
      errEl.classList.remove('hidden');
    }
    mxmPulseRegCodeRow();
    document.getElementById('reg-code-0')?.focus();
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Создать аккаунт';
    }
    return;
  }
  if (!_mxmRegPendingId) {
    if (errEl) {
      errEl.textContent = 'Сессия подтверждения сброшена. Запросите код снова.';
      errEl.classList.remove('hidden');
    }
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Создать аккаунт';
    }
    return;
  }
  try {
    const d = await POST('/api/auth/register-verify', { pendingId: _mxmRegPendingId, code });
    state.token = d.token;
    state.user = d.user;
    localStorage.setItem('mxm_token', d.token);
    if (d.user?.avatarUrl) state.user.avatarUrl = cacheAvatarUrl(d.user.avatarUrl);
    _mxmRegPendingId = null;
    closeAuthModalAfterSuccess();
    updateUserUI();
    loadFavorites();
    if (typeof mxmChatUpdateUnreadBadge === 'function') mxmChatUpdateUnreadBadge();
    if (typeof mxmChatConnectWs === 'function') mxmChatConnectWs();
    if (typeof window.mxmDesktopOnAuthSuccess === 'function') {
      await window.mxmDesktopOnAuthSuccess();
    } else {
      void refreshActiveViewAfterAuth();
    }
  } catch (err) {
    mxmPulseRegCodeRow();
    if (errEl) {
      errEl.textContent = err.message || 'Не удалось подтвердить';
      errEl.classList.remove('hidden');
    }
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Создать аккаунт';
    }
  }
}

async function handleRegisterResendCode() {
  const errEl = $('reg-verify-error');
  if (errEl) {
    errEl.classList.add('hidden');
    errEl.textContent = '';
  }
  if (!_mxmRegPendingId) {
    if (errEl) {
      errEl.textContent = 'Сначала запросите код из формы регистрации.';
      errEl.classList.remove('hidden');
    }
    return;
  }
  const btn = $('reg-verify-resend-btn');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Отправка…';
  }
  try {
    const d = await POST('/api/auth/register-resend-code', { pendingId: _mxmRegPendingId });
    const maskEl = $('reg-verify-email-mask');
    if (maskEl && d.emailMasked) maskEl.textContent = d.emailMasked;
    if (d.resendsRemaining != null) _mxmRegResendsRemaining = Math.max(0, Number(d.resendsRemaining));
    const coolSec = d.resendCooldownSec != null ? Math.max(1, Number(d.resendCooldownSec)) : 30;
    _mxmRegResendUnlockAt = Date.now() + coolSec * 1000;
    mxmStartRegResendCooldownTimer();
    toast('Новый код отправлен на почту', 'success');
  } catch (err) {
    if (typeof err.retryAfterSec === 'number' && err.retryAfterSec > 0) {
      _mxmRegResendUnlockAt = Date.now() + err.retryAfterSec * 1000;
      mxmStartRegResendCooldownTimer();
    }
    if (typeof err.resendsRemaining === 'number') {
      _mxmRegResendsRemaining = Math.max(0, err.resendsRemaining);
      mxmUpdateRegResendButtonUi();
    }
    if (errEl) {
      errEl.textContent = err.message || 'Не удалось отправить';
      errEl.classList.remove('hidden');
    }
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Отправить код ещё раз';
    }
  }
}

function backToRegisterFormFromVerify() {
  resetRegisterVerificationUi();
}

window.handleRegisterVerifySubmit = handleRegisterVerifySubmit;
window.handleRegisterResendCode = handleRegisterResendCode;
window.backToRegisterFormFromVerify = backToRegisterFormFromVerify;
async function logout() {
  if (typeof mxmListenLeave === 'function' && state.listenSession) {
    await mxmListenLeave();
  }
  clearAuthSessionLocal({ clearCookie: true });
  state.uplShareTokenById = {};
  state.favorites = { trackIds: [], albumIds: [], artistIds: [], dislikedTrackIds: [] };
  try {
    mxmChatDisconnectWs();
  } catch (_) {}
  if (typeof window.mxmDesktopStopPlayback === 'function') window.mxmDesktopStopPlayback();
  updateUserUI();
  closeUserMenu();
  if (typeof isMxMDesktopApp === 'function' && isMxMDesktopApp()) {
    if (typeof window.mxmDesktopShowAuthGate === 'function') {
      window.mxmDesktopShowAuthGate('login');
    } else if (typeof applyDesktopAuthGateShell === 'function') {
      applyDesktopAuthGateShell('login');
    } else {
      openAuthModal('login');
    }
    return;
  }
  navigate('home');
}
function updateUserUI() {
  const logged = !!state.user;
  mxmDispatchUserChanged();
  $('user-guest').classList.toggle('hidden', logged);
  $('user-logged').classList.toggle('hidden', !logged);
  const avatarEl = $('user-avatar');
  if (!avatarEl) return;
  if (logged) {
    $('user-name').textContent = state.user.username;
    const cachedAvatarUrl = localStorage.getItem('mxm_avatar_url');
    if (!state.user.avatarUrl && cachedAvatarUrl) {
      state.user.avatarUrl = cachedAvatarUrl;
    }
    renderUserAvatar();
    refreshNotifBadge();
    startNotifPolling();
  } else {
    avatarEl.classList.remove('has-image');
    avatarEl.replaceChildren();
    avatarEl.textContent = '';
    if (notifPollTimer) clearInterval(notifPollTimer);
    notifPollTimer = null;
    closeNotifPanel();
    $('notif-bell-badge')?.classList.add('hidden');
    $('notif-bell-btn')?.classList.remove('notif-bell-btn--unread');
  }

  const userMenu = $('user-menu');
  if (!userMenu) return;
  const existingDivider = $('admin-divider');
  const existingLink = $('admin-link');
  if (state.user?.isAdmin || state.user?.isModerator) {
    if (!existingDivider) {
      const divider = document.createElement('hr');
      divider.id = 'admin-divider';
      userMenu.insertBefore(divider, userMenu.lastElementChild);
    }
    if (!existingLink) {
      const adminLink = document.createElement('a');
      adminLink.id = 'admin-link';
      adminLink.href = '/staff';
      adminLink.textContent = 'Админка';
      userMenu.insertBefore(adminLink, userMenu.lastElementChild);
    }
  } else {
    existingDivider?.remove();
    existingLink?.remove();
  }
}
function toggleUserMenu() { $('user-menu').classList.toggle('hidden'); }
function closeUserMenu() { $('user-menu').classList.add('hidden'); }

let notifPanelOpen = false;
let notifPollTimer = null;
let profileFriendSearchTimer = null;

function positionNotifPanel() {
  const panel = $('notif-panel');
  const btn = $('notif-bell-btn');
  if (!panel || !btn) return;
  const mobile = window.matchMedia('(max-width: 900px)').matches;
  if (mobile) {
    panel.style.top = '';
    panel.style.right = '';
    panel.style.left = '';
    panel.style.bottom = '';
    panel.style.width = '';
  } else {
    const r = btn.getBoundingClientRect();
    panel.style.top = `${Math.round(r.bottom + 8)}px`;
    panel.style.right = `${Math.round(Math.max(10, window.innerWidth - r.right))}px`;
    panel.style.left = 'auto';
    panel.style.bottom = '';
    panel.style.width = '';
  }
}

function setNotifBackdropVisible(show) {
  const bd = $('notif-panel-backdrop');
  if (!bd) return;
  const mobile = window.matchMedia('(max-width: 900px)').matches;
  if (show && mobile) bd.classList.remove('hidden');
  else bd.classList.add('hidden');
}

function toggleNotifPanel(ev) {
  ev?.stopPropagation?.();
  const panel = $('notif-panel');
  const btn = $('notif-bell-btn');
  if (!panel) return;
  notifPanelOpen = !notifPanelOpen;
  panel.classList.toggle('hidden', !notifPanelOpen);
  if (btn) btn.setAttribute('aria-expanded', notifPanelOpen ? 'true' : 'false');
  setNotifBackdropVisible(notifPanelOpen);
  if (notifPanelOpen) {
    positionNotifPanel();
    void markNotificationsSeenAndReload();
  }
}

/** Открытие панели = все текущие уведомления считаются просмотренными (readAt), без отдельной кнопки «прочитано». */
async function markNotificationsSeenAndReload() {
  await loadNotificationsList();
  try {
    await POST('/api/me/notifications/read', { all: true });
  } catch (_) {}
  refreshNotifBadge();
}

function closeNotifPanel() {
  notifPanelOpen = false;
  $('notif-panel')?.classList.add('hidden');
  $('notif-bell-btn')?.setAttribute('aria-expanded', 'false');
  setNotifBackdropVisible(false);
}

function formatNotifTime(iso) {
  return iso ? formatProfileCommentDate(iso) : '';
}

function notifFriendRequestId(payload) {
  const p = payload && typeof payload === 'object' ? payload : {};
  return String(p.requestId || p.request_id || '').trim();
}

function notifDismissBtnHtml(id) {
  const sid = profileJsId(id);
  return `<button type="button" class="notif-dismiss" onclick="event.stopPropagation(); deleteUserNotification('${sid}')" aria-label="Удалить уведомление" title="Удалить">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
  </button>`;
}

function renderNotifRowHtml(n) {
  const unread = !n.readAt;
  const p = n.payload || {};
  let text = '';
  let navView = '';
  let navId = '';
  if (n.type === 'friend_request') {
    text = `${p.fromUsername || 'Пользователь'} хочет добавить вас в друзья`;
  } else if (n.type === 'friend_accept') {
    text = `${p.username || 'Пользователь'} принял(а) заявку в друзья`;
    navView = 'profile';
    navId = p.userId || '';
  } else if (n.type === 'listen_invite') {
    text = `${p.fromUsername || 'Пользователь'} приглашает слушать музыку вместе`;
  } else if (n.type === 'playlist_like') {
    text = `${p.actorUsername || 'Кто-то'} лайкнул «${p.playlistTitle || 'плейлист'}»`;
    navView = 'user-playlist';
    navId = p.playlistId || '';
  } else if (n.type === 'playlist_comment') {
    text = `${p.actorUsername || 'Кто-то'} прокомментировал «${p.playlistTitle || 'плейлист'}»`;
    navView = 'user-playlist';
    navId = p.playlistId || '';
  } else if (n.type === 'comment_reply') {
    text = `${p.actorUsername || 'Кто-то'} ответил на ваш комментарий`;
    navView = 'user-playlist';
    navId = p.playlistId || '';
  } else if (n.type === 'want_to_hear_done') {
    const prev = String(p.messagePreview || '').trim();
    text = prev
      ? `Пожелание «Хочу услышать» исполнено: ${prev}`
      : 'Пожелание «Хочу услышать» исполнено';
  } else if (n.type === 'admin_broadcast') {
    const subj = String(p.subject || '').trim();
    const msg = String(p.message || '').trim();
    if (subj && msg) text = `${subj}: ${msg}`;
    else text = subj || msg || 'Сообщение от администрации';
  } else if (n.type === 'welcome') {
    text = String(p.message || '').trim() || 'Добро пожаловать на MXM Music!';
  } else {
    text = 'Уведомление';
  }
  const timeHtml = `<div class="notif-time">${esc(formatNotifTime(n.createdAt))}</div>`;
  const dismiss = notifDismissBtnHtml(n.id);
  const friendRequestId = notifFriendRequestId(p);
  const friendRequestPending = n.friendRequestPending !== false;
  if (n.type === 'friend_request' && friendRequestId && friendRequestPending) {
    const nid = profileJsId(n.id);
    const rid = profileJsId(friendRequestId);
    const fromUid = p.fromUserId ? profileJsId(p.fromUserId) : '';
    const fromArg = fromUid ? `,'${fromUid}'` : '';
    return `<div class="notif-row${unread ? ' notif-row--unread' : ''} notif-row--with-dismiss">
      <div class="notif-row-main">
        <div class="notif-text">${esc(text)}</div>
        ${timeHtml}
        <div class="notif-actions">
          <button type="button" class="btn-primary btn-sm" onclick="event.stopPropagation();respondFriendRequest('${nid}','${rid}',true${fromArg})">Принять</button>
          <button type="button" class="btn-ghost btn-sm" onclick="event.stopPropagation();respondFriendRequest('${nid}','${rid}',false${fromArg})">Отклонить</button>
        </div>
      </div>
      ${dismiss}
    </div>`;
  }
  if (n.type === 'listen_invite' && p.sessionId) {
    const nid = profileJsId(n.id);
    const sid = profileJsId(p.sessionId);
    return `<div class="notif-row${unread ? ' notif-row--unread' : ''} notif-row--with-dismiss">
      <div class="notif-row-main">
        <div class="notif-text">${esc(text)}</div>
        ${timeHtml}
        <div class="notif-actions">
          <button type="button" class="btn-primary btn-sm" onclick="event.stopPropagation();mxmListenAcceptNotification('${sid}','${nid}')">Принять</button>
          <button type="button" class="btn-ghost btn-sm" onclick="event.stopPropagation();mxmListenDeclineNotification('${sid}','${nid}')">Отклонить</button>
        </div>
      </div>
      ${dismiss}
    </div>`;
  }
  if (navView && navId) {
    return `<div class="notif-row notif-row--with-dismiss${unread ? ' notif-row--unread' : ''}">
      <div class="notif-row-main notif-row-main--click" role="button" tabindex="0" data-notif-id="${esc(
        n.id
      )}" data-nav-view="${navView}" data-nav-id="${esc(navId)}" onclick="handleNotifRowClick(event)">
        <div class="notif-text">${esc(text)}</div>
        ${timeHtml}
      </div>
      ${dismiss}
    </div>`;
  }
  return `<div class="notif-row notif-row--with-dismiss${unread ? ' notif-row--unread' : ''}">
    <div class="notif-row-main">
      <div class="notif-text">${esc(text)}</div>
      ${timeHtml}
    </div>
    ${dismiss}
  </div>`;
}

async function handleNotifRowClick(ev) {
  const row = ev.currentTarget;
  if (!row) return;
  const id = row.getAttribute('data-notif-id');
  const view = row.getAttribute('data-nav-view');
  const navId = row.getAttribute('data-nav-id');
  if (!view || !navId) return;
  try {
    if (id) await POST('/api/me/notifications/read', { id });
  } catch (_) {}
  closeNotifPanel();
  refreshNotifBadge();
  navigate(view, { id: navId });
}

function profileRefreshHeroFriendCtaIfVisible() {
  if (state.view !== 'profile' || !lastProfileViewData?.id) return;
  const wrap = document.querySelector('.profile-hero-cta-wrap');
  if (!wrap) return;
  const d = lastProfileViewData;
  wrap.innerHTML = profileOtherUserFriendCtaHtml(
    d.id,
    d.friendshipWithViewer,
    d.username || '',
    userProfileRef(d),
    d.incomingFriendRequestId || ''
  );
}

async function respondFriendRequest(notificationId, requestId, accept, fromUserId = '') {
  try {
    await POST('/api/friends/respond', { requestId, accept });
    if (notificationId) await POST('/api/me/notifications/read', { id: notificationId });
    await loadNotificationsList();
    refreshNotifBadge();
    toast(accept ? 'Заявка принята' : 'Заявка отклонена', 'success');
    const senderId = String(fromUserId || '').trim();
    if (senderId && lastProfileViewData && String(lastProfileViewData.id) === senderId) {
      lastProfileViewData.friendshipWithViewer = accept ? 'friends' : 'none';
      lastProfileViewData.incomingFriendRequestId = null;
      if (accept) {
        lastProfileViewData.friendCount = (Number(lastProfileViewData.friendCount) || 0) + 1;
        const cnt = $('profile-friends-count-label');
        if (cnt) cnt.textContent = formatFriendsCountRu(lastProfileViewData.friendCount);
      }
      profileRefreshHeroFriendCtaIfVisible();
    }
    if (accept && state.view === 'profile-friends' && state.user?.id) {
      const { params } = parseHash();
      if (params.id && String(params.id) === String(state.user.id)) {
        await profileRefreshFriendsPageList(params.id);
      }
    }
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
}

async function loadNotificationsList() {
  const list = $('notif-panel-list');
  if (!list) return;
  try {
    const rows = await GET('/api/me/notifications?take=40');
    if (!Array.isArray(rows) || !rows.length) {
      list.innerHTML = '<div class="notif-empty">Пока пусто</div>';
      return;
    }
    list.innerHTML = rows.map(renderNotifRowHtml).join('');
  } catch (e) {
    list.innerHTML = `<div class="notif-empty">${esc(e.message || 'Ошибка')}</div>`;
  }
}

async function deleteUserNotification(id) {
  if (!id || !state.user) return;
  try {
    try {
      await POST('/api/me/notifications/read', { id });
    } catch (_) {}
    await DEL(`/api/me/notifications/${encodeURIComponent(id)}`);
    await loadNotificationsList();
    refreshNotifBadge();
  } catch (e) {
    toast(e.message || 'Не удалось удалить', 'error');
  }
}

async function markAllNotificationsRead() {
  try {
    await POST('/api/me/notifications/read', { all: true });
    await loadNotificationsList();
    refreshNotifBadge();
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
}

async function refreshNotifBadge() {
  if (!state.user) return;
  try {
    const data = await GET('/api/me/notifications/unread-count');
    const n = Number(data.count) || 0;
    const badge = $('notif-bell-badge');
    const btn = $('notif-bell-btn');
    if (!badge) return;
    if (n > 0) {
      badge.textContent = n > 99 ? '99+' : String(n);
      badge.classList.remove('hidden');
      btn?.classList.add('notif-bell-btn--unread');
    } else {
      badge.classList.add('hidden');
      btn?.classList.remove('notif-bell-btn--unread');
    }
  } catch (_) {
    /* ignore */
  }
}

function startNotifPolling() {
  if (notifPollTimer) clearInterval(notifPollTimer);
  notifPollTimer = null;
  if (!state.user) return;
  notifPollTimer = setInterval(() => {
    if (state.user && !notifPanelOpen) refreshNotifBadge();
  }, 22000);
}

function formatFriendsCountRu(n) {
  const x = Math.max(0, Number(n) || 0);
  if (x === 0) return 'Нет друзей';
  const mod100 = x % 100;
  const mod10 = x % 10;
  if (mod100 >= 11 && mod100 <= 14) return `${x} друзей`;
  if (mod10 === 1) return `${x} друг`;
  if (mod10 >= 2 && mod10 <= 4) return `${x} друга`;
  return `${x} друзей`;
}

async function profileRefreshFriendsPageList(ownerUserId) {
  const listEl = $('profile-friends-list');
  const loadEl = $('profile-friends-list-loading');
  const id = String(ownerUserId || '').trim();
  if (!listEl || !id) return;
  if (loadEl) loadEl.classList.remove('hidden');
  try {
    const d = await GET(`/api/users/${encodeURIComponent(id)}/friends`);
    const friends = Array.isArray(d.friends) ? d.friends : [];
    const countRaw = Number(d.count);
    const count = Number.isFinite(countRaw) ? countRaw : friends.length;
    const countEl = $('profile-friends-page-count');
    if (countEl) countEl.textContent = formatFriendsCountRu(count);
    const emptyMine =
      '<div class="profile-friend-empty profile-friend-empty--in-panel">Пока никого — добавьте по нику ниже.</div>';
    const emptyGuest =
      '<div class="profile-friend-empty profile-friend-empty--in-panel">Пока нет друзей в списке.</div>';
    const mine = state.user && String(state.user.id) === id;
    listEl.innerHTML = friends.length
      ? profileFriendsListRowsHtml(friends, { canRemove: mine })
      : mine
        ? emptyMine
        : emptyGuest;
  } catch (e) {
    listEl.innerHTML = `<div class="profile-friend-empty">${esc(e.message || 'Ошибка')}</div>`;
  } finally {
    if (loadEl) loadEl.classList.add('hidden');
  }
}

async function renderProfileFriendsPage(userId) {
  const content = $('content');
  const id = String(userId || '').trim();
  if (!id) {
    content.innerHTML = `<div class="lib-empty" style="margin-top:48px"><h3>Некорректная ссылка</h3><p>Не указан пользователь.</p></div>`;
    setQueuePlayingFromLabel('Друзья');
    return;
  }

  content.innerHTML = loadingSpinner();
  try {
    const data = await GET(`/api/users/${encodeURIComponent(id)}/profile`);
    const mine = !!data.isOwner;
    const fc = Number(data.friendCount) || 0;
    let grad = pickGrad('profile-friends-' + data.id);
    if (data.avatarUrl) {
      try {
        const fromAvatar = await getGradientFromImage(data.avatarUrl);
        if (fromAvatar) grad = fromAvatar;
      } catch {
        /* оставляем pickGrad */
      }
    }

    const friendsAddBlock =
      mine && state.user
        ? `<div class="profile-friends-add-block">
          <span class="profile-friends-add-label">Добавить по нику</span>
          <input type="text" id="profile-friend-search" class="profile-friend-search-input" placeholder="Никнейм…" autocomplete="off" maxlength="32" oninput="onProfileFriendSearchInput(event)" />
          <div id="profile-friend-search-results" class="profile-friend-results"></div>
        </div>`
        : '';

    content.innerHTML = `
      <div id="profile-friends-page-shell" class="profile-friends-page-shell">
        <div class="page-hero profile-page-hero profile-friends-page-hero" style="--hero-grad:linear-gradient(${grad})">
          <button type="button" class="nav-back-btn" onclick="goBack()" title="Назад">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
            </svg>
          </button>
          <div class="hero-bg"></div>
          <div class="hero-content profile-friends-page-hero-inner">
            <div class="hero-type">Друзья</div>
            <h1 class="hero-title profile-friends-page-user">${esc(data.username)}</h1>
            <p class="hero-sub profile-friends-page-meta"><span id="profile-friends-page-count">${esc(formatFriendsCountRu(fc))}</span> · <a class="profile-friends-to-profile-link" href="#${buildHash('profile', { id: userProfileRef(data) })}">Профиль</a></p>
          </div>
        </div>
        <div class="profile-page-body profile-friends-page-body">
          <div id="profile-friends-list-loading" class="profile-friends-list-loading">Загрузка…</div>
          <div id="profile-friends-list" class="profile-friends-list profile-friends-list--page"></div>
          ${friendsAddBlock}
        </div>
      </div>`;

    await profileRefreshFriendsPageList(id);
    setQueuePlayingFromLabel(`Друзья: ${data.username}`);
  } catch (e) {
    content.innerHTML = `<div class="lib-empty" style="margin-top:32px"><h3>Не удалось загрузить</h3><p>${esc(e.message || '')}</p></div>`;
    setQueuePlayingFromLabel('Друзья');
  }
}

/** Иконки для блока действий на чужом профиле (как в мессенджерах: основная кнопка + квадратные). */
function _profileFriendCtaIcons() {
  return {
    msg:
      '<svg class="profile-friend-cta-ic" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>',
    userPlus:
      '<svg class="profile-friend-cta-ic" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>',
    userCheck:
      '<svg class="profile-friend-cta-ic" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 11l-3 3-2-2"/></svg>',
    clock:
      '<svg class="profile-friend-cta-ic" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>',
    bell:
      '<svg class="profile-friend-cta-ic" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 6h-3a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-3" /><path d="M14 7a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /></svg>',
    login:
      '<svg class="profile-friend-cta-ic" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>',
  };
}

function profileOtherUserFriendCtaHtml(
  profileId,
  relation,
  profileUsername = '',
  profilePublicRef = '',
  incomingRequestId = ''
) {
  const ic = _profileFriendCtaIcons();
  if (!state.user) {
    return `<div class="profile-friend-cta-bar profile-friend-cta-bar--single">
      <button type="button" class="profile-friend-cta-primary profile-friend-cta-primary--light" onclick="openAuthModal('login')" title="Войти, чтобы добавить в друзья" aria-label="Войти">
        ${ic.login}<span>Войти</span>
      </button>
    </div>`;
  }
  const rel = relation || 'none';
  const pid = profileJsId(profileId);
  const pun = profileJsId(profileUsername);
  if (rel === 'friends') {
    return `<div class="profile-friend-cta-bar profile-friend-cta-bar--single" role="group" aria-label="Действия с пользователем">
      <div class="profile-friend-cta-icons" role="group" aria-label="Дополнительно">
        <button type="button" class="profile-friend-cta-icon-btn profile-friend-cta-icon-btn--listen" title="Слушать вместе" aria-label="Слушать вместе" onclick="mxmListenInvite('${pid}')">
          <svg class="profile-friend-cta-ic" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18V5l10-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="16" cy="16" r="3"/><path d="M3 8a5 5 0 015-5M21 8a5 5 0 00-5-5"/></svg>
        </button>
        <button type="button" class="profile-friend-cta-icon-btn profile-friend-cta-icon-btn--friends" title="Вы в друзьях — нажмите, чтобы удалить" aria-label="Удалить из друзей" onclick="profileOpenRemoveFriendDialog('${pid}', '${pun}')">
          ${ic.userCheck}
        </button>
      </div>
    </div>`;
  }
  if (rel === 'outgoing_pending') {
    return `<div class="profile-friend-cta-bar profile-friend-cta-bar--single">
      <button type="button" class="profile-friend-cta-primary profile-friend-cta-primary--pending" title="Нажмите, чтобы отменить заявку" aria-label="Заявка отправлена. Отменить" onclick="profileCancelFriendRequestFromOtherProfile('${pid}')">
        ${ic.clock}<span>Заявка</span>
      </button>
    </div>`;
  }
  if (rel === 'incoming_pending') {
    const rid = String(incomingRequestId || '').trim();
    const ridJs = rid ? profileJsId(rid) : '';
    const acceptBtn = rid
      ? `<button type="button" class="profile-friend-cta-primary profile-friend-cta-primary--light" onclick="profileRespondIncomingFriend('${pid}','${ridJs}',true)" title="Принять заявку" aria-label="Принять в друзья">Принять</button>`
      : `<button type="button" class="profile-friend-cta-primary profile-friend-cta-primary--light" disabled title="Заявка не найдена">Принять</button>`;
    const declineBtn = rid
      ? `<button type="button" class="profile-friend-cta-primary profile-friend-cta-primary--decline" onclick="profileRespondIncomingFriend('${pid}','${ridJs}',false)" title="Отклонить заявку" aria-label="Отклонить заявку">Отклонить</button>`
      : `<button type="button" class="profile-friend-cta-primary profile-friend-cta-primary--decline" disabled>Отклонить</button>`;
    return `<div class="profile-friend-cta-stack">
      <span class="profile-friend-cta-hint">Хочет в друзья</span>
      <div class="profile-friend-cta-bar profile-friend-cta-bar--incoming">
        ${acceptBtn}
        ${declineBtn}
      </div>
    </div>`;
  }
  return `<div class="profile-friend-cta-bar profile-friend-cta-bar--single">
    <button type="button" class="profile-friend-cta-primary profile-friend-cta-primary--light" onclick="profileSendFriendRequestFromOtherProfile('${pid}')" title="Добавить в друзья" aria-label="Добавить в друзья">
      ${ic.userPlus}<span>В друзья</span>
    </button>
  </div>`;
}

async function profileRespondIncomingFriend(profileUserId, requestId, accept) {
  const pid = String(profileUserId || '').trim();
  const rid = String(requestId || '').trim();
  if (!pid || !rid || !state.user) return;
  try {
    await POST('/api/friends/respond', { requestId: rid, accept });
    toast(accept ? 'Заявка принята' : 'Заявка отклонена', 'success');
    refreshNotifBadge();
    if (lastProfileViewData && String(lastProfileViewData.id) === pid) {
      lastProfileViewData.friendshipWithViewer = accept ? 'friends' : 'none';
      lastProfileViewData.incomingFriendRequestId = null;
      if (accept) {
        lastProfileViewData.friendCount = (Number(lastProfileViewData.friendCount) || 0) + 1;
        const cnt = $('profile-friends-count-label');
        if (cnt) cnt.textContent = formatFriendsCountRu(lastProfileViewData.friendCount);
      }
    }
    profileRefreshHeroFriendCtaIfVisible();
    if (notifPanelOpen) await loadNotificationsList();
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
}

async function profileSendFriendRequestFromOtherProfile(toUserId) {
  const id = String(toUserId || '').trim();
  if (!id || !state.user) return;
  try {
    await POST('/api/friends/request', { toUserId: id });
    toast('Заявка отправлена', 'success');
    if (lastProfileViewData && String(lastProfileViewData.id) === id) {
      lastProfileViewData.friendshipWithViewer = 'outgoing_pending';
      profileRefreshHeroFriendCtaIfVisible();
    }
  } catch (e) {
    toast(e.message || 'Не удалось отправить', 'error');
  }
}

async function profileCancelFriendRequestFromOtherProfile(toUserId) {
  const id = String(toUserId || '').trim();
  if (!id || !state.user) return;
  try {
    await POST('/api/friends/cancel', { toUserId: id });
    toast('Заявка отменена', 'success');
    refreshNotifBadge();
    if (lastProfileViewData && String(lastProfileViewData.id) === id) {
      lastProfileViewData.friendshipWithViewer = 'none';
    }
    if (state.view === 'profile') {
      const wrap = document.querySelector('.profile-hero-cta-wrap');
      if (wrap && lastProfileViewData && String(lastProfileViewData.id) === id) {
        profileRefreshHeroFriendCtaIfVisible();
      }
    }
  } catch (e) {
    toast(e.message || 'Не удалось отменить', 'error');
  }
}

let _friendRemoveTargetId = null;

function closeFriendRemoveModal() {
  const m = $('friend-remove-modal');
  if (m) m.classList.add('hidden');
  _friendRemoveTargetId = null;
}

function ensureFriendRemoveModal() {
  let modal = $('friend-remove-modal');
  if (modal) return modal;
  modal = document.createElement('div');
  modal.id = 'friend-remove-modal';
  modal.className = 'modal modal--center hidden';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'friend-remove-modal-title');
  modal.innerHTML = `
    <div class="modal-overlay" onclick="closeFriendRemoveModal()"></div>
    <div class="modal-box friend-remove-modal-box">
      <button class="modal-close" type="button" onclick="closeFriendRemoveModal()" aria-label="Закрыть">
        <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
      </button>
      <div class="friend-remove-modal-inner">
        <div id="friend-remove-modal-title" class="friend-remove-modal-title">Удалить из друзей?</div>
        <p id="friend-remove-modal-text" class="friend-remove-modal-text"></p>
        <div class="friend-remove-modal-actions">
          <button type="button" class="btn-ghost" onclick="closeFriendRemoveModal()">Отмена</button>
          <button type="button" class="btn-primary friend-remove-confirm-btn" onclick="profileConfirmRemoveFriend()">Удалить</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(modal);
  return modal;
}

function profileOpenRemoveFriendDialog(userId, username) {
  const id = String(userId || '').trim();
  if (!id || !state.user) return;
  _friendRemoveTargetId = id;
  ensureFriendRemoveModal();
  const label = $('friend-remove-modal-text');
  const un = String(username || '').trim();
  if (label) {
    label.textContent = un
      ? `Удалить @${un} из списка друзей? Это действие нельзя отменить.`
      : 'Удалить этого пользователя из друзей? Это действие нельзя отменить.';
  }
  $('friend-remove-modal')?.classList.remove('hidden');
}

async function profileConfirmRemoveFriend() {
  const id = _friendRemoveTargetId;
  if (!id || !state.user) return;
  try {
    await POST('/api/friends/remove', { friendUserId: id });
    toast('Удалено из друзей', 'success');
    closeFriendRemoveModal();
    if (lastProfileViewData && String(lastProfileViewData.id) === id) {
      lastProfileViewData.friendshipWithViewer = 'none';
      lastProfileViewData.friendCount = Math.max(
        0,
        (Number(lastProfileViewData.friendCount) || 0) - 1
      );
    } else if (
      state.user &&
      lastProfileViewData &&
      String(lastProfileViewData.id) === String(state.user.id)
    ) {
      lastProfileViewData.friendCount = Math.max(
        0,
        (Number(lastProfileViewData.friendCount) || 0) - 1
      );
    }
    if (state.view === 'profile') {
      const wrap = document.querySelector('.profile-hero-cta-wrap');
      if (wrap && lastProfileViewData && String(lastProfileViewData.id) === id) {
        profileRefreshHeroFriendCtaIfVisible();
      }
      const cnt = $('profile-friends-count-label');
      if (cnt && lastProfileViewData && String(lastProfileViewData.id) === id) {
        cnt.textContent = formatFriendsCountRu(lastProfileViewData.friendCount || 0);
      }
    }
    if (state.view === 'profile-friends') {
      const { params } = parseHash();
      if (params?.id) await profileRefreshFriendsPageList(params.id);
    }
  } catch (e) {
    toast(e.message || 'Не удалось удалить', 'error');
  }
}

function onProfileFriendSearchInput(ev) {
  clearTimeout(profileFriendSearchTimer);
  const v = ev?.target?.value ?? '';
  profileFriendSearchTimer = setTimeout(() => profileRunFriendSearch(v), 320);
}

async function profileRunFriendSearch(q) {
  const box = $('profile-friend-search-results');
  if (!box) return;
  const query = String(q || '').trim();
  if (query.length < 2) {
    box.innerHTML = '';
    return;
  }
  if (!state.user) return;
  try {
    const rows = await GET(`/api/users/search?q=${encodeURIComponent(query)}`);
    if (!Array.isArray(rows)) {
      box.innerHTML = '';
      return;
    }
    if (!rows.length) {
      box.innerHTML = '<div class="profile-friend-empty">Никого не нашли</div>';
      return;
    }
    box.innerHTML = rows
      .map((u) => {
        const hash = buildHash('profile', { id: userProfileRef(u) });
        return `<div class="profile-friend-row">
      <a class="profile-friend-row-main" href="#${hash}" data-nav="profile" data-nav-id="${esc(userProfileRef(u))}" onclick="handleDataNavClick(event)">
        ${profileFriendListAvatarHtml(u)}
        <span class="profile-friend-name">${esc(u.username)}</span>
      </a>
      <button type="button" class="btn-primary btn-sm profile-friend-row-add-btn" onclick="profileSendFriendRequest('${profileJsId(u.id)}')">В друзья</button>
    </div>`;
      })
      .join('');
  } catch (e) {
    box.innerHTML = `<div class="profile-friend-empty">${esc(e.message || '')}</div>`;
  }
}

async function profileSendFriendRequest(toUserId) {
  const id = String(toUserId || '').trim();
  if (!id || !state.user) return;
  try {
    await POST('/api/friends/request', { toUserId: id });
    toast('Заявка отправлена', 'success');
    const box = $('profile-friend-search-results');
    const inp = $('profile-friend-search');
    if (box) box.innerHTML = '';
    if (inp) inp.value = '';
    if (state.view === 'profile-friends') {
      const { params } = parseHash();
      if (params.id) await profileRefreshFriendsPageList(params.id);
    }
  } catch (e) {
    toast(e.message || 'Не удалось отправить', 'error');
  }
}

function openAvatarUpload() {
  if (!state.user) {
    openAuthModal('login');
    return;
  }
  const input = $('avatar-upload-input');
  if (input) input.click();
}

function openProfileAvatarFlow() {
  if (!state.user) {
    openAuthModal('login');
    return;
  }
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.className = 'hidden';
  input.onchange = (ev) => {
    const f = ev.target?.files?.[0];
    input.remove();
    if (f) openAvatarCropModal(f);
  };
  document.body.appendChild(input);
  input.click();
}

/** Квадратный кроп аватара: зум + перетаскивание, загрузка WebP через сервер. */
function openAvatarCropModal(file) {
  if (!state.user || !file?.type?.startsWith('image/')) return;
  const VIEW = 300;
  const OUT = 512;
  let imgEl = null;
  let zoom = 1.15;
  let panX = 0;
  let panY = 0;
  let drag = null;

  const overlay = document.createElement('div');
  overlay.className = 'avatar-crop-overlay';
  overlay.innerHTML = `
    <div class="avatar-crop-panel">
      <div class="avatar-crop-head">
        <span class="avatar-crop-title">Обрезка аватара</span>
        <button type="button" class="avatar-crop-close" aria-label="Закрыть">×</button>
      </div>
      <p class="avatar-crop-hint">Перетащите изображение, настройте масштаб — будет сохранён квадрат ${OUT}×${OUT}.</p>
      <div class="avatar-crop-viewport" id="avatar-crop-viewport">
        <canvas id="avatar-crop-canvas" width="${VIEW}" height="${VIEW}" aria-hidden="true"></canvas>
      </div>
      <label class="avatar-crop-zoom-label">Масштаб</label>
      <input type="range" class="avatar-crop-zoom" id="avatar-crop-zoom" min="1" max="2.8" step="0.01" value="${zoom}" />
      <div class="avatar-crop-actions">
        <button type="button" class="btn-ghost" id="avatar-crop-cancel">Отмена</button>
        <button type="button" class="btn-primary" id="avatar-crop-apply">Сохранить</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const canvas = overlay.querySelector('#avatar-crop-canvas');
  const ctx = canvas.getContext('2d');
  const viewport = overlay.querySelector('#avatar-crop-viewport');
  const zoomInput = overlay.querySelector('#avatar-crop-zoom');

  const close = () => overlay.remove();

  function draw() {
    if (!imgEl) return;
    const iw = imgEl.naturalWidth;
    const ih = imgEl.naturalHeight;
    if (!iw || !ih) return;
    ctx.clearRect(0, 0, VIEW, VIEW);
    ctx.fillStyle = '#0c0c12';
    ctx.fillRect(0, 0, VIEW, VIEW);
    const m = Math.min(iw, ih);
    const side = m / zoom;
    let sx = (iw - side) / 2 + panX;
    let sy = (ih - side) / 2 + panY;
    sx = Math.max(0, Math.min(sx, iw - side));
    sy = Math.max(0, Math.min(sy, ih - side));
    ctx.drawImage(imgEl, sx, sy, side, side, 0, 0, VIEW, VIEW);
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, VIEW - 2, VIEW - 2);
    ctx.restore();
  }

  function clampPan() {
    if (!imgEl) return;
    const iw = imgEl.naturalWidth;
    const ih = imgEl.naturalHeight;
    const m = Math.min(iw, ih);
    const side = m / zoom;
    const maxPanX = (iw - side) / 2;
    const maxPanY = (ih - side) / 2;
    panX = Math.max(-maxPanX, Math.min(maxPanX, panX));
    panY = Math.max(-maxPanY, Math.min(maxPanY, panY));
  }

  const url = URL.createObjectURL(file);
  imgEl = new Image();
  imgEl.onload = () => {
    URL.revokeObjectURL(url);
    const iw = imgEl.naturalWidth;
    const ih = imgEl.naturalHeight;
    const m = Math.min(iw, ih);
    zoom = Math.max(1, Math.min(2.5, (m / Math.max(80, m * 0.85)) * 1.1));
    zoomInput.value = String(zoom);
    panX = 0;
    panY = 0;
    clampPan();
    draw();
  };
  imgEl.onerror = () => {
    URL.revokeObjectURL(url);
    toast('Не удалось открыть изображение', 'error');
    close();
  };
  imgEl.src = url;

  zoomInput.addEventListener('input', () => {
    zoom = Math.max(1, Math.min(2.8, parseFloat(zoomInput.value) || 1));
    clampPan();
    draw();
  });

  viewport.addEventListener(
    'pointerdown',
    (e) => {
      if (!imgEl) return;
      drag = { x: e.clientX, y: e.clientY, px: panX, py: panY };
      viewport.setPointerCapture(e.pointerId);
    },
    { passive: true }
  );
  viewport.addEventListener('pointermove', (e) => {
    if (!drag || !imgEl) return;
    const iw = imgEl.naturalWidth;
    const ih = imgEl.naturalHeight;
    const m = Math.min(iw, ih);
    const side = m / zoom;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    panX = drag.px - (dx / VIEW) * side;
    panY = drag.py - (dy / VIEW) * side;
    clampPan();
    draw();
  });
  viewport.addEventListener('pointerup', () => {
    drag = null;
  });
  viewport.addEventListener('pointercancel', () => {
    drag = null;
  });

  overlay.querySelector('.avatar-crop-close').onclick = close;
  overlay.querySelector('#avatar-crop-cancel').onclick = close;
  overlay.onclick = (e) => {
    if (e.target === overlay) close();
  };

  overlay.querySelector('#avatar-crop-apply').onclick = async () => {
    if (!imgEl) return;
    try {
      const iw = imgEl.naturalWidth;
      const ih = imgEl.naturalHeight;
      const m = Math.min(iw, ih);
      const side = m / zoom;
      let sx = (iw - side) / 2 + panX;
      let sy = (ih - side) / 2 + panY;
      sx = Math.max(0, Math.min(sx, iw - side));
      sy = Math.max(0, Math.min(sy, ih - side));
      const out = document.createElement('canvas');
      out.width = OUT;
      out.height = OUT;
      const octx = out.getContext('2d');
      octx.drawImage(imgEl, sx, sy, side, side, 0, 0, OUT, OUT);
      const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', 0.92));
      if (!blob) throw new Error('Не удалось подготовить файл');
      const form = new FormData();
      form.append('avatar', blob, 'avatar.jpg');
      const data = await FORM('/api/auth/avatar', form);
      state.user = { ...state.user, ...data.user, avatarUrl: data.avatarUrl || data.user?.avatarUrl };
      if (state.user.avatarUrl) state.user.avatarUrl = cacheAvatarUrl(state.user.avatarUrl);
      updateUserUI();
      if (state.view === 'profile') refreshProfileHeroAvatarInDom();
      toast('Аватар обновлён', 'success');
      close();
    } catch (err) {
      toast(err.message || 'Не удалось загрузить', 'error');
    }
  };
}

async function handleAvatarFileChange(e) {
  const input = e?.target;
  const file = input?.files?.[0];
  if (!file) return;
  if (!file.type.startsWith('image/')) {
    input.value = '';
    return;
  }
  input.value = '';
  openAvatarCropModal(file);
}

window.dislikeCurrentTrackFromPlayer = dislikeCurrentTrackFromPlayer;
window.openAvatarUpload = openAvatarUpload;
window.openProfileAvatarFlow = openProfileAvatarFlow;
window.openAvatarCropModal = openAvatarCropModal;
window.navigateToUserProfile = navigateToUserProfile;
window.handleAvatarFileChange = handleAvatarFileChange;

// ─── FAVORITES ────────────────────────────────────────────────────────────────
async function loadFavorites() {
  if (!state.token) return;
  try {
    const d = await GET('/api/favorites');
    state.favorites.trackIds = d.trackIds || [];
    state.favorites.albumIds = d.albumIds || [];
    state.favorites.artistIds = d.artistIds || [];
    state.favorites.dislikedTrackIds = d.dislikedTrackIds || [];
    refreshFavBtns();
  } catch {}
}
const isFavT = id => state.favorites.trackIds.includes(id);
const isFavA = id => state.favorites.albumIds.includes(id);
const isFavAr = (id) => state.favorites.artistIds.includes(id);
const isDislikedT = (id) => (state.favorites.dislikedTrackIds || []).includes(id);

async function dislikeCurrentTrackFromPlayer() {
  closePlayerMenu();
  if (!state.user) {
    openAuthModal('login');
    return;
  }
  const tr = state.currentTrack;
  if (!tr?.id) return;
  if (isDislikedT(tr.id)) {
    toast('Этот трек уже скрыт из подборок');
    return;
  }
  try {
    await POST(`/api/me/disliked-tracks/${encodeURIComponent(tr.id)}`);
    state.favorites.dislikedTrackIds = state.favorites.dislikedTrackIds || [];
    state.favorites.dislikedTrackIds.push(tr.id);
    if (isFavT(tr.id)) {
      try {
        await DEL(`/api/favorites/track/${tr.id}`);
        state.favorites.trackIds = state.favorites.trackIds.filter((x) => x !== tr.id);
      } catch (_) {}
    }
    refreshFavBtns();
    toast('Трек скрыт из рекомендаций', 'success');
    if (typeof playNext === 'function') playNext();
  } catch (e) {
    toast(e.message || 'Не удалось', 'error');
  }
}

function refreshPlayerDislikeUi() {
  const on =
    !!(state.currentTrack && (state.favorites.dislikedTrackIds || []).includes(state.currentTrack.id));
  const barBtn = $('player-dislike-btn');
  if (barBtn) {
    barBtn.disabled = !!on;
    barBtn.classList.toggle('player-dislike-btn--muted', !!on);
  }
  const menuMob = $('player-menu-dislike-mobile');
  if (menuMob) {
    menuMob.disabled = !!on;
    menuMob.classList.toggle('player-menu-item--muted', !!on);
    const lab = menuMob.querySelector('.player-menu-dislike-label');
    if (lab) lab.textContent = on ? 'Скрыто из подборок' : 'Не нравится';
  }
  const fsBtn = document.querySelector('#fullscreen-player .fullscreen-dislike');
  if (fsBtn) {
    fsBtn.disabled = !!on;
  }
}

async function toggleFavTrack(id, e) {
  if (e) e.stopPropagation();
  if (!state.user) { openAuthModal('login'); return; }
  try {
    if (isFavT(id)) {
      await DEL(`/api/favorites/track/${id}`);
      state.favorites.trackIds = state.favorites.trackIds.filter(x => x !== id);
      toast('Удалено из любимых');
    } else {
      await POST(`/api/favorites/track/${id}`);
      state.favorites.trackIds.push(id);
      toast('Добавлено в любимые', 'success');
    }
    refreshFavBtns();
  } catch {}
}
async function toggleFavAlbum(id, e) {
  if (e) e.stopPropagation();
  if (!state.user) { openAuthModal('login'); return; }
  try {
    if (isFavA(id)) {
      await DEL(`/api/favorites/album/${id}`);
      state.favorites.albumIds = state.favorites.albumIds.filter(x => x !== id);
      toast('Удалено из любимых');
    } else {
      await POST(`/api/favorites/album/${id}`);
      state.favorites.albumIds.push(id);
      toast('Добавлено в любимые', 'success');
    }
    refreshFavBtns();
  } catch {}
}

async function toggleFavArtist(id, e) {
  if (e) e.stopPropagation();
  if (!state.user) { openAuthModal('login'); return; }
  try {
    if (isFavAr(id)) {
      await DEL(`/api/favorites/artist/${id}`);
      state.favorites.artistIds = state.favorites.artistIds.filter((x) => x !== id);
      toast('Исполнитель убран из любимых');
    } else {
      await POST(`/api/favorites/artist/${id}`);
      state.favorites.artistIds.push(id);
      toast('Исполнитель в любимых', 'success');
    }
    refreshFavBtns();
  } catch {}
}
async function toggleCurrentTrackFav() {
  if (state.currentTrack) await toggleFavTrack(state.currentTrack.id);
}
function refreshFavBtns() {
  document.querySelectorAll('[data-fav-track]').forEach(el => {
    el.classList.toggle('liked', isFavT(el.dataset.favTrack));
    if (el.classList.contains('liked')) el.querySelector('svg path').setAttribute('fill','currentColor');
    else el.querySelector('svg path')?.removeAttribute('fill');
  });
  document.querySelectorAll('[data-fav-album]').forEach(el =>
    el.classList.toggle('liked', isFavA(el.dataset.favAlbum))
  );
  document.querySelectorAll('[data-fav-artist]').forEach((el) => {
    const on = isFavAr(el.dataset.favArtist);
    el.classList.toggle('liked', on);
    const path = el.querySelector('svg path');
    if (path) {
      if (on) path.setAttribute('fill', 'currentColor');
      else path.removeAttribute('fill');
    }
  });
  if (state.currentTrack) {
    $('player-like').classList.toggle('liked', isFavT(state.currentTrack.id));
  }
  if (fullscreenActive) {
  const likeBtn = $('fullscreen-player').querySelector('.fullscreen-like');
  if (likeBtn) {
    const liked = isFavT(state.currentTrack?.id);
    likeBtn.classList.toggle('liked', liked);
    likeBtn.setAttribute('aria-pressed', liked ? 'true' : 'false');
    likeBtn.setAttribute('aria-label', liked ? 'Убрать из любимого' : 'Добавить в любимое');
  }
}
  refreshPlayerDislikeUi();
}


// ─── PLAYER ───────────────────────────────────────────────────────────────────

function getRadioPseudoTrack() {
  return {
    id: MXM_RADIO_TRACK_ID,
    title: 'MXM Radio',
    artist: 'Прямой эфир',
    album: '',
    audioUrl: MXM_RADIO_STREAM_URL,
    coverUrl: '',
    coverGradient: pickGrad('mxm-radio'),
    moodTags: []
  };
}

/** Плейсхолдер обложки радио без кроссфейда — реальная обложка приходит из tickRadioNowPlaying. */
function resetPlayerCoverForRadio(track) {
  const wrap = document.querySelector('.player-cover-wrap');
  const current = $('player-cover');
  const newLayer = $('player-cover-new');
  if (!wrap || !current || !newLayer) return;
  _playerCoverSwapToken = (_playerCoverSwapToken || 0) + 1;
  const grad = track.coverGradient || pickGrad('mxm-radio');
  current.innerHTML = coverImg('', grad);
  newLayer.innerHTML = '';
  wrap.classList.remove('cover-crossfade');
}

function syncRadioPlayButtonState() {
  document.querySelectorAll('.mxm-radio-play-btn').forEach((btn) => {
    const live = isRadioPlaybackActive() && !audio.paused;
    btn.classList.toggle('is-live', live);
    btn.setAttribute('aria-pressed', live ? 'true' : 'false');
  });
}

/** Радио: без AGC по треку, чуть выше входной гейн, crossOrigin для потока. */
function prepareRadioAudioPlayback() {
  applyRadioInputGain();
  try {
    audio.crossOrigin = 'anonymous';
  } catch (_) {}
  audio.preload = 'none';
}

function startRadioPlayback(streamUrl) {
  if (!audioCtx) initWebAudio();
  prepareRadioAudioPlayback();

  const url = resolveStreamUrl(streamUrl || MXM_RADIO_STREAM_URL);
  if (audio.src !== url) {
    audio.src = url;
    try { audio.load(); } catch (_) {}
  }

  const playNow = () => {
    audio.play().catch((e) => {
      console.warn('Radio play failed:', e);
      toast('Не удалось запустить радио. Попробуйте ещё раз.', 'error');
      syncRadioPlayButtonState();
    });
  };

  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().then(playNow).catch(playNow);
  } else {
    playNow();
  }
}

function playRadioStream() {
  if (typeof closeQueueSheet === 'function') closeQueueSheet();
  if (typeof closePlayerMenu === 'function') closePlayerMenu();
  if (isRadioPlaybackActive() && state.currentTrack) {
    togglePlay();
    return;
  }
  setQueuePlayingFromLabel('Радио');
  playTrack(getRadioPseudoTrack());
}

window.playRadioStream = playRadioStream;

/** Клик по строке трека: игнор, если цель — ♥ / ⋯ / share / удаление / ссылки. */
function handleTrackRowPlayClick(ev, qKey, idx) {
  const t = ev?.target;
  if (t instanceof Element) {
    if (
      t.closest(
        'button, a, input, textarea, select, label, .track-fav-btn, .track-more-btn, .track-share-btn, .track-playlist-delete-btn, .track-cover-play, .artist-link, .album-link, [data-nav]'
      )
    ) {
      return;
    }
  }
  playFromQueue(qKey, idx);
}

window.handleTrackRowPlayClick = handleTrackRowPlayClick;

function playFromQueue(qKey, idx) {
  const q = getQueue(qKey);
  if (!q[idx]) return;
  const track = q[idx];
  
  // Если это тот же трек, что и текущий
  if (state.currentTrack && state.currentTrack.id === track.id) {
    togglePlay(); // поставить на паузу или продолжить
    return;
  }
  
  // наче начинаем новый трек
  setQueueState(q, idx);
  const recordHistory = ['artist', 'artist-tracks', 'collection', 'album'].includes(state.view) && !!state.user;
  playTrack(track, { recordHistory });
}

// ─── QUEUE SHEET (mobile) ─────────────────────────────────────────────────
let _queueSheetOriginParent = null;
let _queueOverlayOriginParent = null;

/** Один вызов цепочки getGradientFromImage на смену (трек + обложка); playTrack / prepareTrack / fullscreen делят один промис. */
let _coverAmbientWorkKey = null;
let _coverAmbientWorkPromise = null;

function getTrackCoverAmbientPromise(track) {
  const fallbackGrad = track.coverGradient || pickGrad(track.id);
  if (!track?.coverUrl) {
    _coverAmbientWorkKey = '';
    _coverAmbientWorkPromise = null;
    return Promise.resolve({ fromImage: null, gradient: fallbackGrad });
  }
  const key = `${track.id}\0${resolveMediaUrl(track.coverUrl)}`;
  if (_coverAmbientWorkKey === key && _coverAmbientWorkPromise) {
    return _coverAmbientWorkPromise;
  }
  _coverAmbientWorkKey = key;
  _coverAmbientWorkPromise = getGradientFromImage(track.coverUrl).then((fromImage) => ({
    fromImage,
    gradient: fromImage || fallbackGrad,
  }));
  return _coverAmbientWorkPromise;
}

function applyMiniAmbientFromTrackCover(track) {
  const keyForCb = track.coverUrl ? `${track.id}\0${resolveMediaUrl(track.coverUrl)}` : '';
  getTrackCoverAmbientPromise(track).then(({ fromImage, gradient }) => {
    if (_coverAmbientWorkKey !== keyForCb) return;
    if (!state.currentTrack || state.currentTrack.id !== track.id) return;
    setAmbientBg(gradient, { smooth: !!fromImage });
  });
}

function mountQueueSheetForViewport() {
  const sheet = $('queue-sheet');
  const overlay = $('queue-sheet-overlay');
  if (!sheet || !overlay) return;
  const mobile = typeof window !== 'undefined' && window.innerWidth <= 900;

  if (mobile) {
    if (!_queueSheetOriginParent) _queueSheetOriginParent = sheet.parentElement || null;
    if (!_queueOverlayOriginParent) _queueOverlayOriginParent = overlay.parentElement || null;
    if (sheet.parentElement !== document.body) document.body.appendChild(sheet);
    if (overlay.parentElement !== document.body) document.body.appendChild(overlay);
  } else {
    if (_queueSheetOriginParent && sheet.parentElement !== _queueSheetOriginParent && _queueSheetOriginParent.isConnected) {
      _queueSheetOriginParent.appendChild(sheet);
    }
    if (_queueOverlayOriginParent && overlay.parentElement !== _queueOverlayOriginParent && _queueOverlayOriginParent.isConnected) {
      _queueOverlayOriginParent.appendChild(overlay);
    }
  }
}

function isQueueSheetOpen() {
  const sheet = $('queue-sheet');
  return !!sheet && sheet.classList.contains('active');
}

let _queueOutsideCloseBound = false;

function unbindQueueOutsideClose() {
  if (!_queueOutsideCloseBound) return;
  document.removeEventListener('click', onQueueOutsideClick, true);
  _queueOutsideCloseBound = false;
}

function onQueueOutsideClick(e) {
  if (!isQueueSheetOpen()) {
    unbindQueueOutsideClose();
    return;
  }
  // Только десктоп-попап: на мобиле закрывает затемнённый оверлей.
  if (typeof window !== 'undefined' && window.innerWidth <= 900) return;
  const t = e.target;
  if (!t || typeof t.closest !== 'function') return;
  if (t.closest('#queue-sheet, .queue-sheet, #queue-open-btn, .queue-open-btn, .player-queue-anchor')) {
    return;
  }
  closeQueueSheet();
}

function bindQueueOutsideClose() {
  if (_queueOutsideCloseBound) return;
  if (typeof window !== 'undefined' && window.innerWidth <= 900) return;
  _queueOutsideCloseBound = true;
  // После текущего клика по кнопке очереди.
  setTimeout(() => {
    if (!isQueueSheetOpen()) {
      _queueOutsideCloseBound = false;
      return;
    }
    document.addEventListener('click', onQueueOutsideClick, true);
  }, 0);
}

function openQueueSheet() {
  mountQueueSheetForViewport();
  const sheet = $('queue-sheet');
  const overlay = $('queue-sheet-overlay');
  if (!sheet || !overlay) return;
  if (typeof closePlayerMenu === 'function') closePlayerMenu();
  sheet.classList.add('active');
  overlay.classList.add('active');
  renderQueueSheet();
  bindQueueOutsideClose();
}

function closeQueueSheet() {
  unbindQueueOutsideClose();
  _queueReorderDragging = false;
  document.querySelectorAll('.queue-sheet-drag-ghost').forEach(g => g.remove());
  const qList = $('queue-sheet-list');
  if (qList) {
    qList.querySelectorAll('.queue-sheet-row--drag-source').forEach(el => el.classList.remove('queue-sheet-row--drag-source'));
    qList.querySelectorAll('.queue-sheet-row-drag--active').forEach(el => el.classList.remove('queue-sheet-row-drag--active'));
    qList.querySelectorAll('.queue-sheet-row--shift-down, .queue-sheet-row--shift-up').forEach(el => {
      el.classList.remove('queue-sheet-row--shift-down', 'queue-sheet-row--shift-up');
    });
    qList.classList.remove('queue-sheet-list--reordering');
    qList.querySelector('.queue-sheet-drop-line')?.classList.remove('queue-sheet-drop-line--visible');
  }
  $('queue-sheet')?.classList.remove('queue-sheet--reorder-active');

  const sheet = $('queue-sheet');
  const overlay = $('queue-sheet-overlay');
  if (sheet) {
    sheet.classList.remove('active');
  }
  if (overlay) overlay.classList.remove('active');
  if (typeof window !== 'undefined' && window.innerWidth > 900) {
    mountQueueSheetForViewport();
  }
}

function toggleQueueSheet() {
  if (isQueueSheetOpen()) closeQueueSheet();
  else openQueueSheet();
}

window.addEventListener('resize', () => {
  mountQueueSheetForViewport();
}, { passive: true });

window.addEventListener('orientationchange', () => {
  mountQueueSheetForViewport();
});

function playQueueIndex(idx) {
  if (!state.queue || !state.queue[idx]) return;
  // Ручной выбор в панели очереди: не обрезаем список (в отличие от playNext / окончания трека).
  state.queueIndex = idx;
  syncPrevTrackButtons();
  const recordHistory = ['artist', 'artist-tracks', 'collection', 'album'].includes(state.view) && !!state.user;
  playTrack(state.queue[idx], { recordHistory });
  closeQueueSheet();
}

/** Остановка и сброс мини-плеера, когда из очереди убрали последний (в т.ч. текущий) трек. */
function clearPlayerAfterQueueEmptied() {
  if (fullscreenActive) {
    fullscreenActive = false;
    setFullscreenShellClass(false);
    const fs = $('fullscreen-player');
    if (fs) {
      fs.classList.add('hidden');
      fs.classList.remove('fullscreen-closing', 'fullscreen--dragging', 'fullscreen-mobile-lyrics-visible');
      deactivateFullscreenDialogA11y(fs);
      fs.style.transition = '';
      fs.style.transform = '';
      fs.style.opacity = '';
      fs.style.borderRadius = '';
      clearFullscreenContent();
    }
    document.body.style.overflow = '';
    if (_fsMobileCloseFallbackTimer != null) {
      clearTimeout(_fsMobileCloseFallbackTimer);
      _fsMobileCloseFallbackTimer = null;
    }
    if (_fsOpenMiniHideTimer != null) {
      clearTimeout(_fsOpenMiniHideTimer);
      _fsOpenMiniHideTimer = null;
    }
  }
  if (_lyricsOpen) closeLyrics();
  try {
    audio.pause();
    audio.src = '';
    audio.load();
  } catch (_) {}
  state.currentTrack = null;
  state.queueIndex = -1;
  state.prevTrackLocked = false;
  state.radioActive = false;
  if (typeof stopRadioPlaybackPolling === 'function') stopRadioPlaybackPolling();
  _playCountLoggedForTrackId = null;
  _skipLoggedForTrackId = null;
  _coverAmbientWorkKey = null;
  _coverAmbientWorkPromise = null;
  clearMediaSessionPositionState();
  try {
    if (navigator.mediaSession) {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = 'none';
    }
  } catch (_) {}
  setPlayUI(false);
  const player = $('player');
  if (player) {
    player.classList.add('hidden');
    syncPlayerClickBlockState();
  }
  const badge = $('no-audio-badge');
  if (badge) badge.classList.add('hidden');
  updateMiniProgressVisual(0, false);
  const tt = $('time-total');
  if (tt) tt.textContent = fmtTime(0);
  try {
    localStorage.removeItem(STORAGE_LAST_TRACK_ID);
  } catch (_) {}
  syncPrevTrackButtons();
  syncPlayerClickBlockState();
  syncRadioPlayButtonState();
  updatePlayingRows();
  refreshFavBtns();
  updateQueueSourceUI();
}

function removeQueueIndex(idx) {
  if (_queueReorderDragging) return;
  const q = state.queue;
  if (!Array.isArray(q) || idx < 0 || idx >= q.length) return;
  if (state.listenSession?.status === 'active' && q.length === 1) {
    toast('Общая очередь не может быть пустой');
    return;
  }
  const removed = q[idx];
  if (!removed) return;
  const curId = state.currentTrack?.id;
  const wasCurrent = !!(curId && removed.id === curId);

  q.splice(idx, 1);

  if (wasCurrent && q.length === 0) {
    clearPlayerAfterQueueEmptied();
    if (isQueueSheetOpen()) renderQueueSheet();
    return;
  }

  if (isQueueSheetOpen() && q.length > 0) {
    patchQueueSheetDomAfterRemove(idx);
  }

  if (wasCurrent) {
    const ni = Math.min(idx, q.length - 1);
    state.queueIndex = ni;
    syncPrevTrackButtons();
    playTrack(q[ni]);
  } else if (curId) {
    const ni = q.findIndex((t) => t.id === curId);
    if (ni >= 0) state.queueIndex = ni;
    syncPrevTrackButtons();
  } else {
    syncPrevTrackButtons();
  }

  updatePlayingRows();
  refreshFavBtns();
  if (typeof mxmListenOnLocalQueueChanged === 'function') mxmListenOnLocalQueueChanged();
}

/** Обновить индексы/классы строк без пересборки innerHTML (обложки не перезагружаются). */
function syncQueueSheetRowIndices(list) {
  if (!list) return;
  const rows = list.querySelectorAll(':scope > .queue-sheet-row');
  const curId = state.currentTrack?.id;
  const curIdx = state.queueIndex;
  const q = state.queue;
  rows.forEach((row, i) => {
    const track = q[i];
    if (!track) return;
    row.setAttribute('data-q-idx', String(i));
    row.setAttribute('data-track-id', String(track.id));
    const main = row.querySelector('.queue-sheet-row-main');
    if (main) main.setAttribute('onclick', `playQueueIndex(${i})`);
    const drag = row.querySelector('.queue-sheet-row-drag');
    if (drag) drag.setAttribute('data-q-idx', String(i));
    const rm = row.querySelector('.queue-sheet-row-remove');
    if (rm) {
      rm.setAttribute('data-q-idx', String(i));
      rm.setAttribute('onclick', `event.stopPropagation();removeQueueIndex(${i})`);
    }
    const num = row.querySelector('.queue-sheet-row-num');
    if (num) num.textContent = String(i + 1);
    const playing = (Number.isFinite(curIdx) && i === curIdx) || (curId && track.id === curId);
    row.classList.toggle('playing', !!playing);
  });
}

/**
 * Переставить уже отрисованные строки DOM как в state.queue — без сброса <img>.
 * from / insertBefore — те же, что у moveQueueItemInsertBefore до мутации массива.
 */
function patchQueueSheetDomAfterReorder(from, insertBefore) {
  const list = $('queue-sheet-list');
  if (!list || _queueReorderDragging) return;
  list.querySelectorAll('.queue-sheet-drop-line').forEach(el => el.remove());
  const rows = Array.from(list.querySelectorAll(':scope > .queue-sheet-row'));
  const q = state.queue;
  if (rows.length !== q.length) {
    renderQueueSheet();
    return;
  }
  if (insertBefore === from || insertBefore === from + 1) {
    syncQueueSheetRowIndices(list);
    return;
  }
  const [rowEl] = rows.splice(from, 1);
  let ins = insertBefore;
  if (from < insertBefore) ins--;
  rows.splice(ins, 0, rowEl);
  rows.forEach(r => list.appendChild(r));
  syncQueueSheetRowIndices(list);
}

/** Удалить одну строку из DOM без innerHTML — обложки остальных не перезагружаются. */
function patchQueueSheetDomAfterRemove(removedIdx) {
  const list = $('queue-sheet-list');
  if (!list || _queueReorderDragging) return;
  list.querySelectorAll('.queue-sheet-drop-line').forEach((el) => el.remove());
  const q = state.queue;
  const rows = Array.from(list.querySelectorAll(':scope > .queue-sheet-row'));
  if (q.length === 0) {
    renderQueueSheet();
    return;
  }
  if (rows.length !== q.length + 1) {
    renderQueueSheet();
    return;
  }
  if (removedIdx < 0 || removedIdx >= rows.length) {
    renderQueueSheet();
    return;
  }
  rows[removedIdx].remove();
  const after = list.querySelectorAll(':scope > .queue-sheet-row');
  if (after.length !== q.length) {
    renderQueueSheet();
    return;
  }
  syncQueueSheetRowIndices(list);
}

/** Подсветка текущего трека в открытой очереди без пересборки списка. */
function refreshQueueSheetPlayingState() {
  const list = $('queue-sheet-list');
  if (!list || _queueReorderDragging || !isQueueSheetOpen()) return;
  const q = Array.isArray(state.queue) ? state.queue : [];
  const rows = list.querySelectorAll(':scope > .queue-sheet-row');
  if (q.length === 0) {
    renderQueueSheet();
    return;
  }
  if (rows.length !== q.length) {
    renderQueueSheet();
    return;
  }
  syncQueueSheetRowIndices(list);
}

/** insertBefore: индекс позиции в итоговом списке (0…length), куда встает перетаскиваемый трек. */
function moveQueueItemInsertBefore(from, insertBefore) {
  const q = state.queue;
  const n = q.length;
  if (from < 0 || from >= n) return;
  if (insertBefore < 0 || insertBefore > n) return;
  if (insertBefore === from || insertBefore === from + 1) return;
  const [item] = q.splice(from, 1);
  let ins = insertBefore;
  if (from < insertBefore) ins--;
  q.splice(ins, 0, item);
  const curId = state.currentTrack?.id;
  if (curId) {
    const ni = q.findIndex(t => t.id === curId);
    if (ni >= 0) state.queueIndex = ni;
  }
  syncPrevTrackButtons();
  if (isQueueSheetOpen()) patchQueueSheetDomAfterReorder(from, insertBefore);
  if (typeof mxmListenOnLocalQueueChanged === 'function') mxmListenOnLocalQueueChanged();
}

function renderQueueSheet() {
  if (_queueReorderDragging) return;
  const list = $('queue-sheet-list');
  if (!list) return;
  const q = Array.isArray(state.queue) ? state.queue : [];
  const curId = state.currentTrack?.id;
  const curIdx = state.queueIndex;

  list.innerHTML = q.length
    ? q.map((t, i) => {
        const playing = (Number.isFinite(curIdx) && i === curIdx) || (curId && t && t.id === curId);
        const grad = t.coverGradient || pickGrad(t.id);
        return `
          <div class="queue-sheet-row ${playing ? 'playing' : ''}" data-q-idx="${i}" data-track-id="${esc(String(t.id))}">
            <div class="queue-sheet-row-main" onclick="playQueueIndex(${i})">
              <div class="queue-sheet-row-num">${i + 1}</div>
              <div class="queue-sheet-row-cover">${coverImg(t.coverUrl, grad)}</div>
              <div class="queue-sheet-row-title">
                <div class="t">${esc(t.title)}</div>
                <div class="a queue-sheet-artist-line">${renderTrackMetaLineHtml(t, { album: false })}</div>
              </div>
            </div>
            <button type="button" class="queue-sheet-row-remove" data-q-idx="${i}"
              onclick="event.stopPropagation();removeQueueIndex(${i})"
              aria-label="Убрать из очереди" title="Убрать из очереди">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <div class="queue-sheet-row-drag" role="button" tabindex="0" data-q-idx="${i}"
              aria-label="Переместить в очереди" title="Перетащите, чтобы изменить порядок">
              <span class="queue-sheet-row-drag-bars" aria-hidden="true"></span>
            </div>
          </div>`;
      }).join('')
    : `<div class="lib-empty" style="padding: 18px 12px;">
        <h3 style="margin-bottom: 6px;">Очередь пуста</h3>
        <p style="color: var(--text-2);">Начните слушать, и сюда попадут треки.</p>
      </div>`;
}

window.toggleQueueSheet = toggleQueueSheet;
window.closeQueueSheet = closeQueueSheet;
window.playQueueIndex = playQueueIndex;
window.removeQueueIndex = removeQueueIndex;

function bindQueueSheetDragClose() {
  const sh = $('queue-sheet');
  const hdr = sh?.querySelector('.queue-sheet-header');
  if (!sh || !hdr || hdr._mxmQueueDragBound) return;
  hdr._mxmQueueDragBound = true;
  let startY = 0;
  let dragging = false;
  let capId = null;

  const isMobileQueue = () => window.innerWidth <= 900;

  hdr.addEventListener('pointerdown', (e) => {
    if (!isMobileQueue() || !isQueueSheetOpen()) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragging = true;
    startY = e.clientY;
    capId = e.pointerId;
    sh.classList.add('queue-sheet--dragging');
    try {
      hdr.setPointerCapture(e.pointerId);
    } catch (_) {}
  });

  hdr.addEventListener('pointermove', (e) => {
    if (!dragging || !isMobileQueue()) return;
    const dy = e.clientY - startY;
    if (dy > 0) sh.style.transform = `translateY(${dy}px)`;
    else sh.style.transform = '';
  });

  const finish = (e, cancel) => {
    if (!dragging) return;
    dragging = false;
    sh.classList.remove('queue-sheet--dragging');
    const dy = e ? e.clientY - startY : 0;
    sh.style.transform = '';
    if (capId != null) {
      try {
        hdr.releasePointerCapture(capId);
      } catch (_) {}
      capId = null;
    }
    if (!cancel && dy > 72) closeQueueSheet();
  };

  hdr.addEventListener('pointerup', (e) => finish(e, false));
  hdr.addEventListener('pointercancel', (e) => finish(e, true));
}

/** Перетаскивание строк очереди: «призрак», линия вставки, pointer. */
function bindQueueListReorder() {
  const list = $('queue-sheet-list');
  if (!list || list._mxmQueueReorderBound) return;
  list._mxmQueueReorderBound = true;

  let from = -1;
  let dragging = false;
  let capEl = null;
  let capId = null;
  let ghost = null;
  let sourceRow = null;
  let grabDY = 0;
  let dropLine = null;
  let lastInsertBefore = -1;
  let ghostW = 0;
  let ghostH = 0;
  let ghostLockLeft = 0;

  const useRowShiftPreview = () =>
    typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 901px)').matches;

  /** Только вертикаль: горизонталь фиксирована (ghostLockLeft), не следует за пальцем влево/вправо. */
  const clampGhost = (_clientX, clientY) => {
    if (!ghost) return;
    const sh = $('queue-sheet');
    if (!sh) return;
    const pad = 4;
    const sr = sh.getBoundingClientRect();
    const h = ghostH || ghost.offsetHeight || 1;
    let top = clientY - grabDY;
    top = Math.min(sr.bottom - h - pad, Math.max(sr.top + pad, top));
    ghost.style.left = `${Math.round(ghostLockLeft)}px`;
    ghost.style.top = `${Math.round(top)}px`;
  };

  const ensureDropLine = () => {
    if (!dropLine || !dropLine.isConnected) {
      dropLine = document.createElement('div');
      dropLine.className = 'queue-sheet-drop-line';
      dropLine.setAttribute('aria-hidden', 'true');
      list.appendChild(dropLine);
    }
    return dropLine;
  };

  const insertIndexFromPoint = (clientX, clientY) => {
    const rows = list.querySelectorAll('.queue-sheet-row');
    if (!rows.length) return 0;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect();
      const mid = r.top + r.height / 2;
      if (clientY < mid) return i;
    }
    return rows.length;
  };

  const updateDropLine = (insertBefore) => {
    const line = ensureDropLine();
    const rows = list.querySelectorAll('.queue-sheet-row');
    const listRect = list.getBoundingClientRect();
    let y;
    if (!rows.length) {
      y = 0;
    } else if (insertBefore >= rows.length) {
      const last = rows[rows.length - 1];
      y = last.getBoundingClientRect().bottom - listRect.top + list.scrollTop;
    } else {
      y = rows[insertBefore].getBoundingClientRect().top - listRect.top + list.scrollTop;
    }
    line.style.top = `${Math.round(y)}px`;
    line.classList.toggle('queue-sheet-drop-line--visible', dragging && rows.length > 0);
  };

  const applyPreviewShift = (insertBefore) => {
    const rows = list.querySelectorAll('.queue-sheet-row');
    rows.forEach(row => row.classList.remove('queue-sheet-row--shift-down', 'queue-sheet-row--shift-up'));
    if (!useRowShiftPreview()) return;
    if (!dragging || from < 0) return;
    if (insertBefore === from || insertBefore === from + 1) return;
    rows.forEach((row, i) => {
      if (insertBefore <= from) {
        if (i >= insertBefore && i < from) row.classList.add('queue-sheet-row--shift-down');
      } else if (i > from && i < insertBefore) {
        row.classList.add('queue-sheet-row--shift-up');
      }
    });
  };

  const clearPreview = () => {
    list.querySelectorAll('.queue-sheet-row--shift-down, .queue-sheet-row--shift-up').forEach(el => {
      el.classList.remove('queue-sheet-row--shift-down', 'queue-sheet-row--shift-up');
    });
    if (dropLine) dropLine.classList.remove('queue-sheet-drop-line--visible');
  };

  const removeGhost = () => {
    ghost?.remove();
    ghost = null;
    sourceRow?.classList.remove('queue-sheet-row--drag-source');
    sourceRow = null;
  };

  list.addEventListener('pointerdown', (e) => {
    const h = e.target.closest('.queue-sheet-row-drag');
    if (!h || !list.contains(h)) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const fi = parseInt(h.getAttribute('data-q-idx'), 10);
    if (!Number.isFinite(fi)) return;
    e.preventDefault();
    e.stopPropagation();
    from = fi;
    dragging = true;
    _queueReorderDragging = true;
    lastInsertBefore = -1;
    capEl = h;
    capId = e.pointerId;
    list.classList.add('queue-sheet-list--reordering');
    $('queue-sheet')?.classList.add('queue-sheet--reorder-active');

    sourceRow = h.closest('.queue-sheet-row');
    if (sourceRow) {
      sourceRow.classList.add('queue-sheet-row--drag-source');
      const rowRect = sourceRow.getBoundingClientRect();
      grabDY = e.clientY - rowRect.top;

      ghost = sourceRow.cloneNode(true);
      ghost.classList.add('queue-sheet-drag-ghost');
      ghost.classList.remove('queue-sheet-row--drag-source', 'playing');
      ghost.querySelectorAll('.queue-sheet-row-drag--active').forEach(el => el.classList.remove('queue-sheet-row-drag--active'));
      ghost.querySelectorAll('[onclick]').forEach(el => el.removeAttribute('onclick'));
      const gw = Math.min(rowRect.width, list.getBoundingClientRect().width || rowRect.width);
      ghost.style.width = `${Math.round(gw)}px`;
      ghostW = Math.round(gw);
      ghostH = Math.round(rowRect.height);
      const sh0 = $('queue-sheet');
      const pad0 = 4;
      if (sh0) {
        const sr0 = sh0.getBoundingClientRect();
        ghostLockLeft = Math.min(sr0.right - ghostW - pad0, Math.max(sr0.left + pad0, rowRect.left));
      } else {
        ghostLockLeft = rowRect.left;
      }
      document.body.appendChild(ghost);
      clampGhost(e.clientX, e.clientY);
    }

    h.classList.add('queue-sheet-row-drag--active');

    ensureDropLine();
    const ib0 = insertIndexFromPoint(e.clientX, e.clientY);
    lastInsertBefore = ib0;
    updateDropLine(ib0);
    applyPreviewShift(ib0);
    try {
      h.setPointerCapture(e.pointerId);
    } catch (_) {}
  });

  list.addEventListener(
    'pointermove',
    (e) => {
      if (!dragging) return;
      e.preventDefault();

      if (ghost) clampGhost(e.clientX, e.clientY);

      const insertBefore = insertIndexFromPoint(e.clientX, e.clientY);
      if (insertBefore !== lastInsertBefore) {
        lastInsertBefore = insertBefore;
        updateDropLine(insertBefore);
        applyPreviewShift(insertBefore);
      }
    },
    { passive: false }
  );

  const end = (e) => {
    if (!dragging) return;
    const savedInsert = lastInsertBefore;
    dragging = false;
    lastInsertBefore = -1;
    clearPreview();

    let insertBefore = savedInsert;
    if (e && typeof e.clientX === 'number' && typeof e.clientY === 'number') {
      insertBefore = insertIndexFromPoint(e.clientX, e.clientY);
    }

    list.classList.remove('queue-sheet-list--reordering');
    list.querySelectorAll('.queue-sheet-row-drag--active').forEach(el => el.classList.remove('queue-sheet-row-drag--active'));

    if (capEl != null && capId != null) {
      try {
        capEl.releasePointerCapture(capId);
      } catch (_) {}
    }
    capEl = null;
    capId = null;

    const fromIdx = from;
    from = -1;
    ghostW = 0;
    ghostH = 0;
    ghostLockLeft = 0;
    removeGhost();

    $('queue-sheet')?.classList.remove('queue-sheet--reorder-active');
    _queueReorderDragging = false;

    if (fromIdx >= 0 && insertBefore >= 0) {
      moveQueueItemInsertBefore(fromIdx, insertBefore);
    }
  };

  list.addEventListener('pointerup', end);
  list.addEventListener('pointercancel', end);
}

/** Мобильный фуллскрин: смахнуть вниз, чтобы закрыть (как sheet). */
function bindFullscreenMobileSwipeClose() {
  const fs = $('fullscreen-player');
  if (!fs || fs._mxmFsSwipeBound) return;
  fs._mxmFsSwipeBound = true;

  let startY = 0;
  let dragging = false;
  let capId = null;
  let lastY = 0;
  let lastTs = 0;
  let velocityY = 0;
  let startedInLyricsHeader = false;

  const fsSwipeMobile = () =>
    typeof window !== 'undefined' &&
    window.innerWidth <= 900 &&
    fullscreenActive &&
    !fs.classList.contains('hidden');

  const swipeTargetOk = (target) => {
    if (!(target instanceof Element)) return false;
    if (target.closest('.fullscreen-controls')) return false;
    if (target.closest('button, a, input, textarea, select')) return false;
    if (target.closest('.fullscreen-progress-bar, #fullscreen-progress-bar')) return false;
    if (fs.classList.contains('fullscreen-mobile-lyrics-visible')) {
      if (target.closest('.fullscreen-lyrics-header')) return true;
      if (target.closest('.fullscreen-right, #fullscreen-lyrics')) return false;
    }
    return true;
  };

  fs.addEventListener('pointerdown', (e) => {
    if (!fsSwipeMobile()) return;
    if (!swipeTargetOk(e.target)) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragging = true;
    startY = e.clientY;
    lastY = e.clientY;
    lastTs = performance.now();
    velocityY = 0;
    startedInLyricsHeader = !!e.target.closest('.fullscreen-lyrics-header');
    capId = e.pointerId;
    fs.classList.add('fullscreen--dragging');
    try {
      fs.setPointerCapture(e.pointerId);
    } catch (_) {}
  });

  fs.addEventListener('pointermove', (e) => {
    if (!dragging || !fsSwipeMobile()) return;
    const dy = e.clientY - startY;
    const now = performance.now();
    const dt = Math.max(1, now - lastTs);
    velocityY = (e.clientY - lastY) / dt; // px/ms
    lastY = e.clientY;
    lastTs = now;
    if (dy > 0) {
      fs.style.transform = `translateY(${dy}px)`;
    } else {
      fs.style.transform = `translateY(${Math.round(dy * 0.22)}px)`;
    }
  });

  const finish = (e, cancelled) => {
    if (!dragging) return;
    dragging = false;
    fs.classList.remove('fullscreen--dragging');
    const dy = e ? e.clientY - startY : 0;
    if (capId != null) {
      try {
        fs.releasePointerCapture(capId);
      } catch (_) {}
      capId = null;
    }
    const threshold = Math.min(120, Math.round(window.innerHeight * 0.18));
    const fastFlingDown = velocityY > 0.55 && dy > 16;
    const lyricsOpen = fs.classList.contains('fullscreen-mobile-lyrics-visible');
    const openLyricsThreshold = Math.min(88, Math.round(window.innerHeight * 0.12));
    if (!cancelled && !lyricsOpen && dy < -openLyricsThreshold && mobileFsHasLyrics()) {
      fs.style.transition = '';
      fs.style.transform = '';
      mobileFsEnterLyricsMode();
      return;
    }
    if (!cancelled && lyricsOpen && startedInLyricsHeader && (dy > 52 || fastFlingDown)) {
      fs.style.transition = '';
      fs.style.transform = '';
      mobileFsExitLyricsMode();
      return;
    }
    if (!cancelled && (dy > threshold || fastFlingDown)) {
      const player = $('player');
      player?.classList.remove('hidden');
      fs.style.transition = 'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s ease';
      fs.style.opacity = '0.98';
      fs.style.transform = 'translateY(100%)';
      const finalizeClose = () => closeFullscreenPlayer({ skipMobileAnimation: true });
      fs.addEventListener(
        'transitionend',
        (ev) => {
          if (ev.target !== fs) return;
          if (ev.propertyName !== 'transform' && ev.propertyName !== '-webkit-transform') return;
          finalizeClose();
        },
        { once: true },
      );
      setTimeout(finalizeClose, 320);
      return;
    }
    fs.style.transition = 'transform 0.22s cubic-bezier(0.32, 0.72, 0, 1)';
    fs.style.transform = 'translateY(0)';
    let snapCleared = false;
    const clearSnap = () => {
      if (snapCleared) return;
      snapCleared = true;
      fs.style.transition = '';
      fs.style.transform = '';
    };
    fs.addEventListener(
      'transitionend',
      (ev) => {
        if (ev.target !== fs) return;
        if (ev.propertyName !== 'transform' && ev.propertyName !== '-webkit-transform') return;
        clearSnap();
      },
      { once: true },
    );
    setTimeout(clearSnap, 320);
  };

  fs.addEventListener('pointerup', (e) => finish(e, false));
  fs.addEventListener('pointercancel', (e) => finish(e, true));
}


/** Стабильный «первичный» артист трека для shuffle-guard (не сосед с тем же id/name). */
function primaryQueueArtistKey(track) {
  if (!track) return '';
  if (Array.isArray(track.artistIds) && track.artistIds.length) return String(track.artistIds[0]);
  if (track.artistId) return String(track.artistId);
  return String(track.artist || '')
    .trim()
    .toLowerCase();
}

function playTrack(track, opts = {}) {
  if (!track) return;
  if (!String(track.audioUrl || '').trim()) {
    toast('У этого трека нет аудиофайла', 'error');
    return;
  }

  const leaving = state.currentTrack;
  const leavingLogged = _playCountLoggedForTrackId;
  const leavingPos = Number(audio.currentTime);
  const leavingDur = Number(audio.duration);
  if (leaving && leaving.id && track.id !== leaving.id) {
    maybeSubmitSkipForLeavingTrack(leaving, leavingLogged, leavingPos, leavingDur);
  }

  const isRadio = track.id === MXM_RADIO_TRACK_ID;
  if (isRadio) {
    state.radioActive = true;
    state.queue = [];
    state.queueIndex = -1;
    state.prevTrackLocked = false;
  } else {
    state.radioActive = false;
    if (typeof stopRadioPlaybackPolling === 'function') stopRadioPlaybackPolling();
  }

  _playCountLoggedForTrackId = null;
  _skipLoggedForTrackId = null;
  state.currentTrack = track;
  syncPrevTrackButtons();
  syncPlayerClickBlockState();
  // Rolling history for anti-repetition in random/similar подборках.
  if (track?.id && !isRadio) {
    const maxRecent = 80;
    const idx = state.recentlyPlayed.indexOf(track.id);
    if (idx >= 0) state.recentlyPlayed.splice(idx, 1);
    state.recentlyPlayed.push(track.id);
    if (state.recentlyPlayed.length > maxRecent) {
      state.recentlyPlayed.splice(0, state.recentlyPlayed.length - maxRecent);
    }
    const ak = primaryQueueArtistKey(track);
    if (ak) {
      const maxA = 16;
      const ai = state.recentQueueArtists.indexOf(ak);
      if (ai >= 0) state.recentQueueArtists.splice(ai, 1);
      state.recentQueueArtists.push(ak);
      if (state.recentQueueArtists.length > maxA) {
        state.recentQueueArtists.splice(0, state.recentQueueArtists.length - maxA);
      }
    }
  }
  if (isQueueSheetOpen()) refreshQueueSheetPlayingState();
  if (!isRadio) localStorage.setItem(STORAGE_LAST_TRACK_ID, track.id);

  // Обновляем мини-плеер
  setPlayerTitleNeon($('player-title'), track);

  const artistEl = $('player-artist');
  renderTrackArtistsToElement(artistEl, track);
  renderUploaderProfileLinkToElement($('player-uploader-link'), track, { iconOnly: true });
  syncPlayerAlbumMenuItem(track);

  const ptags = $('player-tags');
  if (ptags) ptags.innerHTML = htmlPlayerTrackTags(track);

  if (isRadio) {
    resetPlayerCoverForRadio(track);
  } else {
    setPlayerCoverSmooth(track);
  }
  syncMiniPlayerVisibility();
  updateMiniProgressVisual(0, false);
  $('player-like').classList.toggle('liked', !isRadio && isFavT(track.id));
  if (!isRadio) applyMiniAmbientFromTrackCover(track);

  if (track.audioUrl) {
    $('no-audio-badge').classList.add('hidden');

    if (isRadio) {
      $('time-current').textContent = 'LIVE';
      $('time-total').textContent = '';
      const fsCur = document.getElementById('fullscreen-time-current');
      const fsTot = document.getElementById('fullscreen-time-total');
      if (fsCur) fsCur.textContent = 'LIVE';
      if (fsTot) fsTot.textContent = '';
      startRadioPlayback(track.audioUrl);
      if (typeof startRadioPlaybackPolling === 'function') startRadioPlaybackPolling();
    } else {
      if (!audioCtx) initWebAudio();
      resetTrackLoudnessNormalization(track);
      audio.src = resolveStreamUrl(track.audioUrl);
      if (audioCtx && audioCtx.state === 'suspended') {
        audioCtx.resume().then(() => audio.play()).catch(e => console.warn(e));
      } else {
        audio.play().catch(e => console.warn(e));
      }
      syncLastPlayedTrackToServer();
    }

    // Счётчик прослушиваний и история — только после ~50% трека (см. maybeSubmitPlayCountProgress).
  } else {
    audio.src = '';
    $('no-audio-badge').classList.remove('hidden');
  }
  
  updatePlayingRows();
  refreshFavBtns();
    if (fullscreenActive) {
        previousGradient = null;  // ← ДОБАВТЬ
        updateFullscreenContent();
    }
  if (_lyricsOpen) {
    $('lyrics-track-name').textContent = track.title;
    $('lyrics-artist-name').textContent = getTrackArtistText(track);
    // Текст песни может править любой залогиненный пользователь (не только загрузивший трек)
    const canEditLyrics = !!state.user;
    $('lyrics-edit-btn').classList.toggle('hidden', !canEditLyrics);
    loadLyrics(track.id);
  }
  
  updateQueueSourceUI();

  applyMediaSessionForTrack(track);
  syncRadioPlayButtonState();
  if (typeof mxmListenOnLocalTrack === 'function') mxmListenOnLocalTrack(track);
}

// Подготавливает трек к воспроизведению (обновляет UI, но не играет)
function prepareTrack(track) {
  if (!track) return;
  if (track.id === MXM_RADIO_TRACK_ID) {
    state.radioActive = true;
  } else {
    state.radioActive = false;
  }
  state.currentTrack = track;
  syncPrevTrackButtons();
  syncPlayerClickBlockState();

  // Update player UI
  setPlayerTitleNeon($('player-title'), track);

  // Настройка кликабельности для имени артиста
  const artistEl = $('player-artist');
  renderTrackArtistsToElement(artistEl, track);
  renderUploaderProfileLinkToElement($('player-uploader-link'), track, { iconOnly: true });
  syncPlayerAlbumMenuItem(track);

  const ptags = $('player-tags');
  if (ptags) ptags.innerHTML = htmlPlayerTrackTags(track);

  if (track.id === MXM_RADIO_TRACK_ID) {
    resetPlayerCoverForRadio(track);
  } else {
    setPlayerCoverSmooth(track);
  }
  syncMiniPlayerVisibility();
  $('player-like').classList.toggle('liked', isFavT(track.id));
  if (track.id !== MXM_RADIO_TRACK_ID) applyMiniAmbientFromTrackCover(track);

  if (track.audioUrl) {
    audio.src = resolveStreamUrl(track.audioUrl);
    $('no-audio-badge').classList.add('hidden');
    
    // нициализируем Web Audio граф при первом вызове (даже если не играем)
    if (!audioCtx) {
      initWebAudio();
    }
    resetTrackLoudnessNormalization(track);
    // НЕ вызываем audio.play()!
  } else {
    audio.src = '';
    $('no-audio-badge').classList.remove('hidden');
  }
  
  updatePlayingRows();
  refreshFavBtns();
  
  applyMediaSessionForTrack(track);
}

async function restoreLastTrack() {
  // Если пользователь залогинен — пробуем восстановить из БД.
  if (state.user) {
    try {
      const d = await GET('/api/me/last-played');
      const track = d?.track;
      if (track?.id && track.audioUrl) {
        localStorage.setItem(STORAGE_LAST_TRACK_ID, track.id);
        setQueueState([track], 0, { lockPrevAtStart: true });
        prepareTrack(track);
        return;
      }
    } catch {
      // fallback ниже
    }
  }

  const lastTrackId = localStorage.getItem(STORAGE_LAST_TRACK_ID);
  if (!lastTrackId) return;

  try {
    const track = await GET(`/api/tracks/${lastTrackId}`);
    if (track && track.audioUrl) {
      setQueueState([track], 0, { lockPrevAtStart: true });
      prepareTrack(track); // используем новую функцию
    } else {
      localStorage.removeItem(STORAGE_LAST_TRACK_ID);
    }
  } catch (e) {
    console.warn('Failed to restore last track', e);
    localStorage.removeItem(STORAGE_LAST_TRACK_ID);
  }
}
// Воспроизведение подборки вперемешку
async function shufflePlayCollectionById(id) {
  try {
    const playlist = await GET(`/api/playlists/${id}`);
    let tracks = playlist.tracks || [];
    if (!tracks.length) {
  
      return;
    }
    // Перемешиваем массив (алгоритм Фишера-Йетса)
    for (let i = tracks.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [tracks[i], tracks[j]] = [tracks[j], tracks[i]];
    }
    setQueueState(tracks, 0);
    playTrack(tracks[0]);
  } catch {
    toast('Ошибка загрузки', 'error');
  }
}

async function shufflePlayAlbumById(id) {
  try {
    const album = await GET(`/api/albums/${id}`);
    let tracks = [...(album.tracks || [])];
    if (!tracks.length) {
      toast('В альбоме нет треков');
      return;
    }
    for (let i = tracks.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [tracks[i], tracks[j]] = [tracks[j], tracks[i]];
    }
    setQueueState(tracks, 0);
    const recordHistory = ['artist', 'artist-tracks', 'collection', 'album'].includes(state.view) && !!state.user;
    playTrack(tracks[0], { recordHistory });
  } catch {
    toast('Ошибка загрузки', 'error');
  }
}

// Воспроизведение популярных треков артиста вперемешку
async function shufflePlayArtistById(id) {
  try {
    const artist = await GET(`/api/artists/${id}`);
    let tracks = artist.popularTracks || [];
    if (!tracks.length) {
  
      return;
    }
    for (let i = tracks.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [tracks[i], tracks[j]] = [tracks[j], tracks[i]];
    }
    setQueueState(tracks, 0);
    playTrack(tracks[0]);
  } catch {
    toast('Ошибка загрузки', 'error');
  }
}
function setAmbientBg(gradient, options) {
  const smooth = options && options.smooth;
  let el = $('ambient-bg');
  if (!el) {
    el = document.createElement('div'); el.id = 'ambient-bg';
    document.body.prepend(el);
  }
  el.style.background = `linear-gradient(${gradient})`;
  document.documentElement.style.setProperty('--now-playing-grad', `linear-gradient(${gradient})`);
  const player = $('player');
  if (!player) return;
  _lastPlayerAmbientGradientStops = gradient;
  player.style.setProperty('--player-grad-mobile', `linear-gradient(${gradient})`);
  syncPlayerBarBackgroundImage();
  if (smooth) {
    player.style.setProperty('--player-grad-new', `linear-gradient(${gradient})`);
    player.classList.add('player-grad-new-visible');
    setTimeout(() => {
      player.style.setProperty('--player-grad', `linear-gradient(${gradient})`);
      player.classList.remove('player-grad-new-visible');
    }, 350);
  } else {
    player.style.setProperty('--player-grad', `linear-gradient(${gradient})`);
  }
}

function updatePlayingRows() {
  document.querySelectorAll('.track-row').forEach(r => {
    r.classList.toggle('playing', r.dataset.trackId === state.currentTrack?.id);
  });
}

function setPlayUI(playing) {
  $('icon-play').classList.toggle('hidden', playing);
  $('icon-pause').classList.toggle('hidden', !playing);
  const btn = $('play-pause-btn');
  btn.classList.toggle('is-playing', playing);
  if (fullscreenActive) updateFullscreenPlayUI(playing);
  syncMediaSessionPlaybackState();
  syncMediaSessionPositionState(true);
}

function togglePlay() {
  if (!state.currentTrack) return;
  const streamSrc = audio.currentSrc || audio.src || '';
  if (!streamSrc.trim()) {
    toast('У этого трека нет аудиофайла', 'error');
    return;
  }

  const willPlay = audio.paused;
  if (typeof mxmListenOnLocalToggle === 'function') mxmListenOnLocalToggle(willPlay);
  if (willPlay) {
    // Если используется Web Audio и контекст приостановлен, сначала возобновляем его
    if (audioCtx && audioCtx.state === 'suspended') {
      audioCtx.resume().then(() => {
        audio.play().catch(e => console.warn('Play failed after resume:', e));
      }).catch(e => console.warn('Resume failed:', e));
    } else {
      audio.play().catch(e => console.warn('Play failed:', e));
    }
  } else {
    audio.pause();
  }
}

function playNext() {
  if (!state.currentTrack) return;
  if (isRadioPlaybackActive()) return;
  if (typeof mxmListenRequestStep === 'function' && mxmListenRequestStep('next')) return;

  // Если очередь пуста или только текущий трек – сразу подгружаем похожие
  if (state.queue.length === 0 || (state.queue.length === 1 && state.queue[0].id === state.currentTrack.id)) {
    getSimilarTracksAndFade(state.currentTrack.id);
    return;
  }

  let curIdx = state.queueIndex;
  if (
    !(curIdx >= 0 && curIdx < state.queue.length) ||
    state.queue[curIdx]?.id !== state.currentTrack?.id
  ) {
    curIdx = state.queue.findIndex((t) => t?.id === state.currentTrack?.id);
  }
  const curMatches =
    curIdx >= 0 &&
    curIdx < state.queue.length &&
    state.queue[curIdx]?.id === state.currentTrack?.id;

  if (state.shuffle) {
    const availableIndices = [];
    for (let i = 0; i < state.queue.length; i++) {
      if (i !== curIdx && !state.recentlyPlayed.includes(state.queue[i].id)) {
        availableIndices.push(i);
      }
    }

    if (availableIndices.length === 0) {
      getSimilarTracksAndFade(state.currentTrack.id);
      return;
    }

    const curArtist = primaryQueueArtistKey(state.currentTrack);
    let pickFrom = availableIndices;
    if (curArtist && availableIndices.length > 1) {
      const otherArtist = availableIndices.filter(
        (i) => primaryQueueArtistKey(state.queue[i]) !== curArtist
      );
      if (otherArtist.length) pickFrom = otherArtist;
    }

    const randomIndex = pickFrom[Math.floor(Math.random() * pickFrom.length)];
    state.queueIndex = randomIndex;
    playTrack(state.queue[randomIndex]);
    return;
  }

  if (curIdx < 0 || curIdx >= state.queue.length) {
    getSimilarTracksAndFade(state.currentTrack.id);
    return;
  }

  if (state.repeat === 'none' && curIdx === state.queue.length - 1) {
    getSimilarTracksAndFade(state.currentTrack.id);
    return;
  }

  if (state.repeat === 'all' && curIdx === state.queue.length - 1) {
    state.queueIndex = 0;
    playTrack(state.queue[0]);
    return;
  }

  const playIdx = state.repeat === 'one'
    ? curIdx
    : Math.min(curIdx + 1, state.queue.length - 1);
  state.queueIndex = playIdx;
  playTrack(state.queue[playIdx]);
}

function playPrev() {
  if (!state.currentTrack) return;
  if (isRadioPlaybackActive()) return;
  if (isPrevTrackLocked()) return;
  if (audio.currentTime <= 3 && typeof mxmListenRequestStep === 'function' && mxmListenRequestStep('prev')) return;

  // Если очередь пуста, но есть текущий трек – запускаем похожие
  if (state.queue.length === 0) {
    getSimilarTracksAndFade(state.currentTrack.id);
    return;
  }

  // Если ? очереди только один трек (текущий) – тоже запускаем похожие
  if (state.queue.length === 1) {
    getSimilarTracksAndFade(state.currentTrack.id);
    return;
  }

  // Обычное переключение
  let idx;
  if (audio.currentTime > 3) {
    audio.currentTime = 0;
    return;
  }
  if (state.shuffle) {
    // При shuffle выбираем случайный, исключая текущий
    do {
      idx = Math.floor(Math.random() * state.queue.length);
    } while (idx === state.queueIndex && state.queue.length > 1);
  } else {
    idx = (state.queueIndex - 1 + state.queue.length) % state.queue.length;
  }
  state.queueIndex = idx;
  playTrack(state.queue[idx]);
}

function toggleShuffle() {
  state.shuffle = !state.shuffle;
  $('shuffle-btn').classList.toggle('active', state.shuffle);
  const fsShuffle = document.querySelector('.fullscreen-controls .shuffle-btn');
  if (fsShuffle) {
    fsShuffle.classList.toggle('active', state.shuffle);
    fsShuffle.setAttribute('aria-pressed', state.shuffle ? 'true' : 'false');
  }
}

function cycleRepeat() {
  const m = ['none', 'all', 'one'];
  state.repeat = m[(m.indexOf(state.repeat) + 1) % 3];
  syncRepeatButtons();
}


// Обновление внешнего вида кнопки повтора
function updateRepeatButton(btn, mode) {
  if (!btn) return;
  // Удаляем старые классы и индикатор
  btn.classList.remove('active', 'repeat-one');
  const oldIndicator = btn.querySelector('.repeat-one-indicator');
  if (oldIndicator) oldIndicator.remove();

  if (mode === 'one') {
    btn.classList.add('active', 'repeat-one');
    const indicator = document.createElement('span');
    indicator.className = 'repeat-one-indicator';
    indicator.textContent = '1';
    btn.appendChild(indicator);
    btn.title = 'Повтор трека';
  } else if (mode === 'all') {
    btn.classList.add('active');
    btn.title = 'Повтор всех';
  } else {
    btn.title = 'Повтор: выкл';
  }
  btn.setAttribute('aria-label', btn.title);
  btn.setAttribute('aria-pressed', mode === 'none' ? 'false' : 'true');
}

// Синхронизация обеих кнопок повтора
function syncRepeatButtons() {
  updateRepeatButton($('repeat-btn'), state.repeat);
  const fsRepeatBtn = document.querySelector('.fullscreen-controls .repeat-btn');
  if (fsRepeatBtn) updateRepeatButton(fsRepeatBtn, state.repeat);
}

// Синхронизация иконок mute ? обоих плеерах
function syncMuteUI() {
  const isMuted = audio.muted;
  // Обычный плеер
  const volIcon = $('icon-vol');
  const muteIcon = $('icon-mute');
  if (volIcon && muteIcon) {
    volIcon.classList.toggle('hidden', isMuted);
    muteIcon.classList.toggle('hidden', !isMuted);
  }
  // Полноэкранный плеер
  const fsVolIcon = $('fullscreen-icon-vol');
  const fsMuteIcon = $('fullscreen-icon-mute');
  if (fsVolIcon && fsMuteIcon) {
    fsVolIcon.classList.toggle('hidden', isMuted);
    fsMuteIcon.classList.toggle('hidden', !isMuted);
  }
  const fsVolBtn = $('fullscreen-vol-btn');
  if (fsVolBtn) fsVolBtn.setAttribute('aria-label', isMuted ? 'Включить звук' : 'Выключить звук');
}

function getProgressPct(bar, e, rect) {
  const r = rect || bar.getBoundingClientRect();
  let clientX;
  if (e.touches && e.touches.length > 0) {
    clientX = e.touches[0].clientX;
  } else if (e.changedTouches && e.changedTouches.length > 0) {
    clientX = e.changedTouches[0].clientX;
  } else {
    clientX = e.clientX;
  }
  return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
}

function updateMiniProgressVisual(pct, updateTime) {
  const p = pct * 100;
  $('player')?.style?.setProperty('--player-progress-pct', p + '%');
  const topFill = $('player-top-progress-fill');
  if (topFill) topFill.style.width = p + '%';
  $('progress-fill').style.width = p + '%';
  $('progress-thumb').style.left = p + '%';
  if (updateTime && audio.duration) $('time-current').textContent = fmtTime(pct * audio.duration);
}

function updateFullscreenProgressVisual(pct, updateTime) {
  const p = (pct * 100).toFixed(2);
  const fsBar = document.getElementById('fullscreen-progress-bar');
  const fsFill = document.getElementById('fullscreen-progress-fill');
  const fsThumb = document.getElementById('fullscreen-progress-thumb');
  const fsTimeCur = document.getElementById('fullscreen-time-current');
  if (fsFill) fsFill.style.width = p + '%';
  if (fsThumb) fsThumb.style.left = p + '%';
  if (fsBar) fsBar.setAttribute('aria-valuenow', String(Math.round(Number(p))));
  if (updateTime && fsTimeCur && audio.duration) fsTimeCur.textContent = fmtTime(pct * audio.duration);
}

function seekTo(e) {
  if (isRadioPlaybackActive()) return;
  const bar = $('progress-bar');
  const pct = getProgressPct(bar, e);
  if (audio.duration) audio.currentTime = pct * audio.duration;
}

function seekFullscreenTo(e) {
  if (isRadioPlaybackActive()) return;
  const bar = document.getElementById('fullscreen-progress-bar');
  if (!bar || !audio.duration) return;
  const pct = getProgressPct(bar, e);
  audio.currentTime = pct * audio.duration;
}

function setVolume(val) {
  const v = Math.max(0, Math.min(100, Number(val)));
  updateVolumeFill(v);
  const volume = v / 100;
  const slider = $('volume-range');
  if (slider) {
    slider.value = String(v);
    slider.dataset.mxmVolSynced = '1';
  }
  // Ползунок громкости управляет mute-состоянием:
  // 0 = muted, любое значение > 0 снимает mute.
  if (v === 0) {
    audio.muted = true;
  } else if (audio.muted) {
    audio.muted = false;
  }
  applyUserVolumeToGraph(volume);
  refreshAutoGainForUserVolume(volume);
  scheduleVolumeSync(v);
  syncMuteUI();
}

// Progress drag: rAF для плавного перемещения поинтера без лагов (особенно ? fullscreen)
let _seekDragMini = false;
let _seekDragFs = false;
let _dragPct = 0;
let _seekRect = null;
let _seekRafId = null;
let _seekPendingPct = null;
let _seekPendingMini = false;
let _seekPendingFs = false;

function flushSeekVisual() {
  _seekRafId = null;
  if (_seekPendingMini && _seekPendingPct != null) {
    updateMiniProgressVisual(_seekPendingPct, false);
    _seekPendingMini = false;
  }
  if (_seekPendingFs && _seekPendingPct != null) {
    updateFullscreenProgressVisual(_seekPendingPct, false);
    _seekPendingFs = false;
  }
  _seekPendingPct = null;
}

function scheduleSeekVisual(isMini, isFs, pct) {
  _seekPendingPct = pct;
  if (isMini) _seekPendingMini = true;
  if (isFs) _seekPendingFs = true;
  if (_seekRafId == null) _seekRafId = requestAnimationFrame(flushSeekVisual);
}

const miniBar = $('progress-bar');
miniBar.addEventListener('mousedown', e => {
  if (isRadioPlaybackActive()) return;
  _seekDragMini = true;
  _seekRect = miniBar.getBoundingClientRect();
  _dragPct = getProgressPct(miniBar, e, _seekRect);
  updateMiniProgressVisual(_dragPct, true);
});
miniBar.addEventListener('touchstart', e => {
  if (isRadioPlaybackActive()) return;
  e.preventDefault();
  _seekDragMini = true;
  _seekRect = miniBar.getBoundingClientRect();
  _dragPct = getProgressPct(miniBar, e, _seekRect);
  updateMiniProgressVisual(_dragPct, true);
}, { passive: false });

document.addEventListener('mousemove', e => {
  if (_seekDragMini) {
    _dragPct = getProgressPct(miniBar, e, _seekRect);
    scheduleSeekVisual(true, false, _dragPct);
  } else if (_seekDragFs) {
    const bar = document.getElementById('fullscreen-progress-bar');
    if (bar) {
      _dragPct = getProgressPct(bar, e, _seekRect);
      scheduleSeekVisual(false, true, _dragPct);
    }
  }
});
document.addEventListener('mouseup', () => {
  if (_seekDragMini && audio.duration) {
    if (_seekRafId != null) cancelAnimationFrame(_seekRafId);
    flushSeekVisual();
    audio.currentTime = _dragPct * audio.duration;
    _seekDragMini = false;
    _seekRect = null;
  }
  if (_seekDragFs && audio.duration) {
    if (_seekRafId != null) cancelAnimationFrame(_seekRafId);
    flushSeekVisual();
    audio.currentTime = _dragPct * audio.duration;
    _seekDragFs = false;
    _seekRect = null;
  }
});

miniBar.addEventListener('touchmove', e => {
  if (_seekDragMini && e.touches.length) {
    e.preventDefault();
    _dragPct = getProgressPct(miniBar, e, _seekRect);
    scheduleSeekVisual(true, false, _dragPct);
  }
}, { passive: false });
miniBar.addEventListener('touchend', () => {
  if (_seekDragMini && audio.duration) {
    if (_seekRafId != null) cancelAnimationFrame(_seekRafId);
    flushSeekVisual();
    audio.currentTime = _dragPct * audio.duration;
    _seekDragMini = false;
    _seekRect = null;
  }
});

document.addEventListener('touchmove', e => {
  if (_seekDragMini && e.touches.length) {
    e.preventDefault();
    _dragPct = getProgressPct(miniBar, e, _seekRect);
    scheduleSeekVisual(true, false, _dragPct);
    return;
  }
  if (_seekDragFs && e.touches.length) {
    const bar = document.getElementById('fullscreen-progress-bar');
    if (!bar) return;
    e.preventDefault();
    _dragPct = getProgressPct(bar, e, _seekRect);
    scheduleSeekVisual(false, true, _dragPct);
  }
}, { passive: false });

document.addEventListener('touchend', () => {
  if (_seekDragMini && audio.duration) {
    if (_seekRafId != null) cancelAnimationFrame(_seekRafId);
    flushSeekVisual();
    audio.currentTime = _dragPct * audio.duration;
    _seekDragMini = false;
    _seekRect = null;
  }
  if (_seekDragFs && audio.duration) {
    if (_seekRafId != null) cancelAnimationFrame(_seekRafId);
    flushSeekVisual();
    audio.currentTime = _dragPct * audio.duration;
    _seekDragFs = false;
    _seekRect = null;
  }
});

var fullscreenProgressBar = document.getElementById('fullscreen-progress-bar');
if (fullscreenProgressBar) {
  fullscreenProgressBar.addEventListener('mousedown', e => {
    if (isRadioPlaybackActive()) return;
    _seekDragFs = true;
    _seekRect = fullscreenProgressBar.getBoundingClientRect();
    _dragPct = getProgressPct(fullscreenProgressBar, e, _seekRect);
    updateFullscreenProgressVisual(_dragPct, true);
  });
  fullscreenProgressBar.addEventListener('touchstart', e => {
    if (isRadioPlaybackActive()) return;
    e.preventDefault();
    _seekDragFs = true;
    _seekRect = fullscreenProgressBar.getBoundingClientRect();
    _dragPct = getProgressPct(fullscreenProgressBar, e, _seekRect);
    updateFullscreenProgressVisual(_dragPct, true);
  }, { passive: false });
  fullscreenProgressBar.addEventListener('touchmove', e => {
    if (_seekDragFs && e.touches.length) {
      e.preventDefault();
      _dragPct = getProgressPct(fullscreenProgressBar, e, _seekRect);
      scheduleSeekVisual(false, true, _dragPct);
    }
  }, { passive: false });
  fullscreenProgressBar.addEventListener('keydown', e => {
    if (isRadioPlaybackActive() || !audio.duration) return;
    let nextTime = audio.currentTime || 0;
    if (e.key === 'ArrowLeft') nextTime -= 5;
    else if (e.key === 'ArrowRight') nextTime += 5;
    else if (e.key === 'Home') nextTime = 0;
    else if (e.key === 'End') nextTime = audio.duration;
    else return;
    e.preventDefault();
    audio.currentTime = Math.max(0, Math.min(audio.duration, nextTime));
    updateFullscreenProgressVisual(audio.currentTime / audio.duration, true);
  });
}

// Audio events
let _audioUiRaf = null;
let _audioUiPendingPct = 0;
let _audioUiPendingCurrent = 0;
/** Один засчитанный «полный» прогон на трек за сессию (счётчик plays + история с 50% длительности). */
let _playCountLoggedForTrackId = null;
/** Уже отправили skip для этого трека (не дублировать при быстрых next). */
let _skipLoggedForTrackId = null;
const PLAY_COUNT_MIN_FRACTION = 0.5;
const PLAY_COUNT_TIME_EPS = 0.15;
const SKIP_MIN_LISTEN_SEC = 3;

function currentRecSessionId() {
  try {
    return String(sessionStorage.getItem('mxm_rec_session') || '').trim().slice(0, 64) || null;
  } catch (_) {
    return null;
  }
}

function maybeSubmitSkipForLeavingTrack(leavingTrack, leavingLoggedId, pos, dur) {
  if (isRadioPlaybackActive()) return;
  if (!state.user || !leavingTrack?.id || !leavingTrack.audioUrl) return;
  if (leavingTrack.id === MXM_RADIO_TRACK_ID) return;
  if (leavingLoggedId === leavingTrack.id) return;
  if (_skipLoggedForTrackId === leavingTrack.id) return;
  if (!Number.isFinite(dur) || dur <= 0 || !Number.isFinite(pos)) return;
  if (pos < SKIP_MIN_LISTEN_SEC) return;
  if (pos + PLAY_COUNT_TIME_EPS >= dur * PLAY_COUNT_MIN_FRACTION) return;

  _skipLoggedForTrackId = leavingTrack.id;
  const body = { positionSec: pos, durationSec: dur };
  const sid = currentRecSessionId();
  if (sid) body.recSession = sid;
  POST(`/api/tracks/${leavingTrack.id}/skip`, body).catch(() => {
    if (_skipLoggedForTrackId === leavingTrack.id) _skipLoggedForTrackId = null;
  });
}

function maybeSubmitPlayCountProgress(opts = {}) {
  if (isRadioPlaybackActive()) return;
  const forceEnded = opts.forceEnded === true;
  const tr = state.currentTrack;
  if (!tr?.id || !tr.audioUrl) return;
  if (_playCountLoggedForTrackId === tr.id) return;

  const dur = Number(audio.duration);
  if (!Number.isFinite(dur) || dur <= 0) return;

  const pos = forceEnded ? dur : Number(audio.currentTime);
  if (!Number.isFinite(pos)) return;

  const half = dur * PLAY_COUNT_MIN_FRACTION - PLAY_COUNT_TIME_EPS;
  if (pos < half) return;

  _playCountLoggedForTrackId = tr.id;
  const shouldRecordHistory = !!state.user;
  const playUrl = `/api/tracks/${tr.id}/play${shouldRecordHistory ? '?recordHistory=1' : ''}`;
  const body = { positionSec: pos, durationSec: dur };
  const sid = currentRecSessionId();
  if (sid) body.recSession = sid;
  POST(playUrl, body).catch(() => {
    _playCountLoggedForTrackId = null;
  });
}

function flushAudioProgressUi() {
  _audioUiRaf = null;
  const pct = _audioUiPendingPct;
  if (!_seekDragMini) {
    const topFill = $('player-top-progress-fill');
    if (topFill) topFill.style.width = pct + '%';
    $('player')?.style?.setProperty('--player-progress-pct', pct + '%');
    $('progress-fill').style.width = pct + '%';
    $('progress-thumb').style.left = pct + '%';
    $('time-current').textContent = fmtTime(_audioUiPendingCurrent);
  }
  if (fullscreenActive && !_seekDragFs) {
    const fsFill = document.getElementById('fullscreen-progress-fill');
    const fsThumb = document.getElementById('fullscreen-progress-thumb');
    const fsTimeCur = document.getElementById('fullscreen-time-current');
    if (fsFill) fsFill.style.width = pct + '%';
    if (fsThumb) fsThumb.style.left = pct + '%';
    if (fsTimeCur) fsTimeCur.textContent = fmtTime(_audioUiPendingCurrent);
    const fsBar = document.getElementById('fullscreen-progress-bar');
    if (fsBar) fsBar.setAttribute('aria-valuenow', String(Math.round(pct)));
  }
}

audio.addEventListener('timeupdate', () => {
  if (isRadioPlaybackActive()) return;
  if (!audio.duration) return;
  _audioUiPendingPct = (audio.currentTime / audio.duration) * 100;
  _audioUiPendingCurrent = audio.currentTime;
  if (_audioUiRaf == null) _audioUiRaf = requestAnimationFrame(flushAudioProgressUi);
  tryEarlyHiddenQueueAdvance();
  syncMediaSessionPositionState(false);
  maybeSubmitPlayCountProgress();
  tickFullscreenSyncedLyrics();
});
audio.addEventListener('loadedmetadata', () => {
  if (isRadioPlaybackActive()) {
    updateMiniProgressVisual(0, false);
    $('time-current').textContent = 'LIVE';
    $('time-total').textContent = '';
    const fsCur = document.getElementById('fullscreen-time-current');
    const fsTot = document.getElementById('fullscreen-time-total');
    if (fsCur) fsCur.textContent = 'LIVE';
    if (fsTot) fsTot.textContent = '';
    clearMediaSessionPositionState();
    return;
  }
  updateMiniProgressVisual(audio.duration ? (audio.currentTime / audio.duration) : 0, false);
  $('time-total').textContent = fmtTime(audio.duration);
  const fsTotal = document.getElementById('fullscreen-time-total');
  if (fsTotal) fsTotal.textContent = fmtTime(audio.duration);
  syncMediaSessionPositionState(true);
});
audio.addEventListener('seeked', () => {
  syncMediaSessionPositionState(true);
  if (!_fsSyncLines?.length) return;
  const time = audio.currentTime || 0;
  const idx = findSyncedLyricLineIndex(_fsSyncLines, time);
  _fsSyncActiveIndex = idx;
  if (_fsSyncScrollExpanded) {
    const beforeStart = fsSyncIsBeforeStart(_fsSyncLines, time);
    const follow = fsSyncMobileAutoFollowEnabled();
    if (follow) _fsSyncLastScrollFollowIdx = -1;
    fsSyncUpdateScrollActive(idx, { scroll: follow, smooth: follow, beforeStart });
    if (!fsSyncIsMobileLyricsOpen() || _fsSyncUserScrolledAway) fsSyncScheduleRecenter();
    return;
  }
  renderFullscreenSyncedLyrics(_fsSyncLines, idx, { animate: true });
});
audio.addEventListener('play',  () => { setPlayUI(true); syncRadioPlayButtonState(); });
audio.addEventListener('pause', () => { setPlayUI(false); syncRadioPlayButtonState(); });
audio.addEventListener('ended', () => {
  if (isRadioPlaybackActive()) {
    audio.play().catch(() => {});
    return;
  }
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (now - _lastEarlyHiddenQueueAdvanceAt < EARLY_ADVANCE_ENDED_GUARD_MS) {
    return;
  }

  maybeSubmitPlayCountProgress({ forceEnded: true });

  if (state.repeat === 'one') {
    _playCountLoggedForTrackId = null;
    audio.currentTime = 0;
    audio.play().catch(e => console.warn('Repeat play failed:', e));
    return;
  }

  // Если есть следующий трек ? очереди
  if (state.queue.length > 0) {
    if (state.repeat === 'all' || state.queueIndex < state.queue.length - 1) {
      playNext();
      return;
    }
  }

  // Если очередь закончилась (или repeat none и это последний)
  if (state.currentTrack) {
    getSimilarTracksAndFade(state.currentTrack.id);
  } else {
    setPlayUI(false);
  }
});
audio.addEventListener('error', () => {
  if (!isRadioPlaybackActive()) return;
  const code = audio.error?.code;
  console.warn('Radio audio error', code, audio.src);
  toast('Поток радио недоступен', 'error');
  syncRadioPlayButtonState();
});

// Media Session: метаданные, playbackState и position — иначе iOS (PWA, островик, CarPlay)
// часто не показывает стандартные кнопки «вперёд / назад», хотя перемотка по времени есть.
let _mediaSessionPositionSyncAt = 0;

function inferImageMimeTypeFromUrl(url) {
  const path = String(url || '').split('?')[0].toLowerCase();
  if (path.endsWith('.png')) return 'image/png';
  if (path.endsWith('.webp')) return 'image/webp';
  if (path.endsWith('.gif')) return 'image/gif';
  return 'image/jpeg';
}

function buildMediaSessionMetadataInit(track) {
  if (!track) return null;
  const meta = {
    title: track.title || '',
    artist: getTrackArtistText(track) || '',
  };
  if (track.album) meta.album = track.album;
  if (track.coverUrl) {
    const fullCoverUrl = resolveMediaUrl(track.coverUrl);
    if (fullCoverUrl) {
      const type = inferImageMimeTypeFromUrl(fullCoverUrl);
      meta.artwork = [
        { src: fullCoverUrl, sizes: '96x96', type },
        { src: fullCoverUrl, sizes: '128x128', type },
        { src: fullCoverUrl, sizes: '192x192', type },
        { src: fullCoverUrl, sizes: '256x256', type },
        { src: fullCoverUrl, sizes: '384x384', type },
        { src: fullCoverUrl, sizes: '512x512', type },
      ];
    }
  }
  return meta;
}

function applyMediaSessionForTrack(track) {
  if (!navigator.mediaSession || !track) return;
  try {
    const init = buildMediaSessionMetadataInit(track);
    if (!init) return;
    navigator.mediaSession.metadata = new MediaMetadata(init);
  } catch (e) {
    console.warn('MediaMetadata failed', e);
  }
}

function clearMediaSessionPositionState() {
  if (!navigator.mediaSession?.setPositionState) return;
  try {
    navigator.mediaSession.setPositionState(null);
  } catch (_) {}
}

function syncMediaSessionPositionState(force) {
  if (!navigator.mediaSession?.setPositionState) return;
  const d = audio.duration;
  if (!state.currentTrack || !audio.src || !Number.isFinite(d) || d <= 0) {
    clearMediaSessionPositionState();
    return;
  }
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (!force && now - _mediaSessionPositionSyncAt < 850) return;
  _mediaSessionPositionSyncAt = now;

  let pos = audio.currentTime;
  if (!Number.isFinite(pos) || pos < 0) pos = 0;
  if (pos > d) pos = d;
  try {
    navigator.mediaSession.setPositionState({
      duration: d,
      playbackRate: audio.playbackRate || 1,
      position: pos,
    });
  } catch (_) {
    clearMediaSessionPositionState();
  }
}

function syncMediaSessionPlaybackState() {
  if (!navigator.mediaSession) return;
  try {
    if (!state.currentTrack || !audio.src) {
      navigator.mediaSession.playbackState = 'none';
      return;
    }
    navigator.mediaSession.playbackState = audio.paused ? 'paused' : 'playing';
  } catch (_) {}
}

/** Только для не‑iOS: ±10 с по системным кнопкам. */
function mediaSessionSeekBackwardAction() {
  if (!Number.isFinite(audio.duration)) return;
  audio.currentTime = Math.max(0, audio.currentTime - 10);
  syncMediaSessionPositionState(true);
}

function mediaSessionSeekForwardAction() {
  if (!Number.isFinite(audio.duration)) return;
  audio.currentTime = Math.min(audio.duration, audio.currentTime + 10);
  syncMediaSessionPositionState(true);
}

function initMediaSessionActionHandlers() {
  if (!navigator.mediaSession) return;
  const set = (action, handler) => {
    try {
      navigator.mediaSession.setActionHandler(action, handler);
    } catch (_) {}
  };
  const ios = isIosLikeDevice();
  /* WebKit: нельзя одновременно держать seekforward/backward и next/prev — иначе везде «±10» вместо треков.
   * Убираем seek-* на iOS и оставляем только nexttrack/previoustrack (+ seekto для ползунка). */
  if (ios) {
    set('seekbackward', null);
    set('seekforward', null);
  } else {
    set('seekbackward', () => { if (!isRadioPlaybackActive()) mediaSessionSeekBackwardAction(); });
    set('seekforward', () => { if (!isRadioPlaybackActive()) mediaSessionSeekForwardAction(); });
  }
  set('play', () => {
    audio.play().catch(() => {});
  });
  set('pause', () => {
    audio.pause();
  });
  set('nexttrack', () => {
    if (!isRadioPlaybackActive()) playNext();
  });
  set('previoustrack', () => {
    if (!isRadioPlaybackActive()) playPrev();
  });
  set('seekto', (details) => {
    if (isRadioPlaybackActive()) return;
    const t = details?.seekTime;
    if (!Number.isFinite(t) || !Number.isFinite(audio.duration)) return;
    const clamped = Math.max(0, Math.min(t, audio.duration));
    if (details.fastSeek && typeof audio.fastSeek === 'function') {
      try {
        audio.fastSeek(clamped);
      } catch (_) {
        audio.currentTime = clamped;
      }
    } else {
      audio.currentTime = clamped;
    }
    syncMediaSessionPositionState(true);
  });
}

initMediaSessionActionHandlers();
/* WebKit: обработчики после старта воспроизведения надёжнее цепляются к системным кнопкам */
audio.addEventListener('playing', () => {
  if (isIosLikeDevice()) initMediaSessionActionHandlers();
});

document.addEventListener('visibilitychange', () => {
  if (document.hidden) return;
  if (audioCtx && audioCtx.state === 'suspended' && state.currentTrack && !audio.paused) {
    audioCtx.resume().catch(() => {});
  }
});

let fullscreenActive = false;
/** Только последний запрос смены обложки во fullscreen применяет кроссфейд (по id). */
let fullscreenCoverUpdateId = 0;

function setFullscreenShellClass(on) {
  try {
    document.documentElement.classList.toggle('mxm-fullscreen-player-open', !!on);
  } catch (_) {}
}

/** Мини-плеер скрыт, пока открыт fullscreen (playTrack не должен его снова показывать). */
function syncMiniPlayerVisibility() {
  const player = $('player');
  if (!player) return;
  if (fullscreenActive) {
    player.classList.add('hidden');
  } else if (state.currentTrack) {
    player.classList.remove('hidden');
  }
  syncPlayerClickBlockState();
}
/** Инкремент при каждом открытии fullscreen — отменяет завершение предыдущего «закрытия» (stale transitionend / таймер). */
let _fsSessionId = 0;
let _fsMobileCloseFallbackTimer = null;
let _fsDesktopCloseFallbackTimer = null;
let _fsOpenMiniHideTimer = null;
let _fullscreenReturnFocusEl = null;

function fullscreenFocusableElements(fs) {
  return Array.from(
    fs.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
  ).filter((el) => !el.classList.contains('hidden') && el.getClientRects().length > 0);
}

function activateFullscreenDialogA11y(fs) {
  if (!fs) return;
  fs.setAttribute('aria-hidden', 'false');
  fs._mxmInertedSiblings = Array.from(document.body.children)
    .filter((el) => el !== fs && !el.inert)
    .map((el) => {
      el.inert = true;
      return el;
    });
  if (!fs._mxmFocusTrapBound) {
    fs._mxmFocusTrapBound = true;
    fs.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !fullscreenActive) return;
      const focusable = fullscreenFocusableElements(fs);
      if (!focusable.length) {
        event.preventDefault();
        fs.focus({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    });
  }
  requestAnimationFrame(() => {
    if (!fullscreenActive) return;
    const preferred = fs.querySelector('.fullscreen-close:not(.hidden)') || fullscreenFocusableElements(fs)[0] || fs;
    preferred.focus({ preventScroll: true });
  });
}

function deactivateFullscreenDialogA11y(fs) {
  if (fs) {
    fs.setAttribute('aria-hidden', 'true');
    (fs._mxmInertedSiblings || []).forEach((el) => {
      if (document.contains(el)) el.inert = false;
    });
    fs._mxmInertedSiblings = [];
  }
  const returnTo = _fullscreenReturnFocusEl;
  _fullscreenReturnFocusEl = null;
  if (returnTo && typeof returnTo.focus === 'function' && document.contains(returnTo)) {
    requestAnimationFrame(() => returnTo.focus({ preventScroll: true }));
  }
}

function finishFullscreenCloseIfStale(closeForSession, fn) {
  if (closeForSession !== _fsSessionId || fullscreenActive) return;
  fn();
}

function getMiniPlayerMorphState() {
  const player = $('player');
  if (!player) return null;
  const rect = player.getBoundingClientRect();
  if (!rect || rect.width < 10 || rect.height < 10) return null;
  const vw = Math.max(1, window.innerWidth || document.documentElement.clientWidth || 1);
  const vh = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 1);
  const sx = Math.min(1, Math.max(0.05, rect.width / vw));
  const sy = Math.min(1, Math.max(0.05, rect.height / vh));
  return {
    tx: Math.round(rect.left),
    ty: Math.round(rect.top),
    sx,
    sy
  };
}

function toggleFullscreenPlayer() {
  if (fullscreenActive) closeFullscreenPlayer();
  else openFullscreenPlayer();
}

function clearFullscreenContent() {
  const fs = $('fullscreen-player');
  fs.classList.remove('fullscreen-mobile-lyrics-visible');
  $('fullscreen-title').textContent = '';
  $('fullscreen-artist').textContent = '';
  const coverContainer = $('fullscreen-cover');
  coverContainer.innerHTML = '';
  $('fullscreen-lyrics').innerHTML = '';
  const lyricsLive = $('fullscreen-lyrics-live');
  if (lyricsLive) lyricsLive.textContent = '';
  clearFullscreenSyncedLyricsState();
  const gradientLayers = fs.querySelectorAll('.fs-grad-layer');
  gradientLayers.forEach(layer => layer.remove());
  previousGradient = null;
  syncMobileFullscreenLyricsUi();
}

function fullscreenPrefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function mobileFsHasLyrics() {
  const lyrics = $('fullscreen-lyrics');
  if (!lyrics || isRadioPlaybackActive()) return false;
  if (_fsSyncLines?.length) return true;
  return !!lyrics.querySelector('.lyrics-text') && !lyrics.querySelector('.lyrics-empty');
}

function syncMobileLyricsFollowButton() {
  const btn = $('fullscreen-lyrics-follow-btn');
  if (!btn) return;
  const show =
    fsSyncIsMobileLyricsOpen() &&
    _fsSyncScrollExpanded &&
    _fsSyncUserScrolledAway &&
    !!_fsSyncLines?.length;
  btn.classList.toggle('hidden', !show);
}

function syncMobileFullscreenVolumeUi() {
  const fs = $('fullscreen-player');
  if (!fs) return;
  const unavailable = typeof isIosLikeDevice === 'function' && isIosLikeDevice();
  fs.classList.toggle('fullscreen-mobile-volume-unavailable', unavailable);
}

function syncMobileFullscreenLyricsUi() {
  const fs = $('fullscreen-player');
  if (!fs) return;
  const lyricsVisible = fs.classList.contains('fullscreen-mobile-lyrics-visible');
  const showBtn = document.getElementById('fullscreen-show-lyrics-btn');
  if (showBtn) {
    const available = mobileFsHasLyrics();
    const label = lyricsVisible ? 'Свернуть текст песни' : 'Показать текст песни';
    showBtn.classList.toggle('is-active', lyricsVisible);
    showBtn.classList.toggle('lyrics-unavailable', !available);
    showBtn.disabled = !available;
    showBtn.title = label;
    showBtn.setAttribute('aria-label', label);
    showBtn.setAttribute('aria-pressed', lyricsVisible ? 'true' : 'false');
  }
  syncMobileLyricsFollowButton();
  syncMobileFullscreenVolumeUi();
}

function mobileFsEnterLyricsMode() {
  const fs = $('fullscreen-player');
  if (!fs || !mobileFsHasLyrics()) return;
  fs.classList.add('fullscreen-mobile-lyrics-visible');
  _fsSyncUserScrolledAway = false;
  _fsSyncLastScrollFollowIdx = -1;
  syncMobileFullscreenLyricsUi();
  requestAnimationFrame(() => {
    const backBtn = $('fullscreen-player')?.querySelector('.fullscreen-lyrics-back-btn');
    if (backBtn && fsSyncIsMobileLyricsOpen()) backBtn.focus({ preventScroll: true });
  });
  fsSyncBindLyricsLayoutObserver();
  if (_fsSyncLines?.length) {
    fsSyncEnterScrollMode();
    const remasure = () => {
      const idx = findSyncedLyricLineIndex(_fsSyncLines, audio.currentTime || 0);
      _fsSyncLastScrollFollowIdx = idx;
      fsSyncUpdateScrollActive(idx, { scroll: true, smooth: false });
      fsSyncRemeasureAndRepaintIfNeeded();
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(remasure);
    });
    // После анимации раскрытия колонки слот пересчитывается — иначе строки накладываются
    const right = fs.querySelector('.fullscreen-right');
    let settled = false;
    const onSettled = () => {
      if (settled) return;
      settled = true;
      remasure();
    };
    if (right) {
      right.addEventListener('transitionend', onSettled, { once: true });
    }
    setTimeout(onSettled, 520);
  }
}

function mobileFsExitLyricsMode() {
  const fs = $('fullscreen-player');
  if (!fs) return;
  if (_fsSyncScrollExpanded) fsSyncExitScrollMode({ paintCarousel: false });
  _fsSyncCachedSlotPx = 0;
  fs.classList.remove('fullscreen-mobile-lyrics-visible');
  _fsSyncUserScrolledAway = false;
  _fsSyncLastScrollFollowIdx = -1;
  syncMobileFullscreenLyricsUi();
  requestAnimationFrame(() => {
    const closeBtn = fs.querySelector('.fullscreen-close');
    if (closeBtn && fullscreenActive) closeBtn.focus({ preventScroll: true });
  });
}

function toggleMobileFullscreenLyrics() {
  if (!isMobile()) return;
  const fs = $('fullscreen-player');
  if (fs.classList.contains('fullscreen-mobile-lyrics-visible')) mobileFsExitLyricsMode();
  else mobileFsEnterLyricsMode();
}

function resumeMobileLyricsFollow() {
  if (!fsSyncIsMobileLyricsOpen() || !_fsSyncLines?.length) return;
  _fsSyncUserScrolledAway = false;
  _fsSyncLastScrollFollowIdx = -1;
  fsSyncClearIdleTimer();
  syncMobileLyricsFollowButton();
  const idx = findSyncedLyricLineIndex(_fsSyncLines, audio.currentTime || 0);
  fsSyncUpdateScrollActive(idx, {
    scroll: true,
    smooth: !fullscreenPrefersReducedMotion(),
    beforeStart: fsSyncIsBeforeStart(_fsSyncLines, audio.currentTime || 0)
  });
}

const FULLSCREEN_DURATION_MS = 450;

function isMobile() {
  return typeof window !== 'undefined' && window.innerWidth <= 900;
}

function bindFullscreenResponsiveBehavior() {
  if (window._mxmFullscreenResponsiveBound) return;
  window._mxmFullscreenResponsiveBound = true;
  let timer = null;
  const refresh = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (!fullscreenActive) return;
      const fs = $('fullscreen-player');
      if (!fs) return;
      if (!isMobile() && fs.classList.contains('fullscreen-mobile-lyrics-visible')) {
        mobileFsExitLyricsMode();
      }
      fsSyncRemeasureAndRepaintIfNeeded();
      syncMobileFullscreenLyricsUi();
    }, 100);
  };
  window.addEventListener('resize', refresh, { passive: true });
  window.addEventListener('orientationchange', refresh, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', refresh, { passive: true });
  }
}

function isDesktopNav() {
  if (typeof isMxMDesktopApp === 'function' && isMxMDesktopApp()) return true;
  return typeof window !== 'undefined' && window.innerWidth > 900;
}

function getSearchInputEl() {
  const page = $('search-input-page');
  if (page) return page;
  if (isMobile()) {
    const m = $('search-input-mobile');
    if (m) return m;
  }
  return $('search-input');
}

async function openFullscreenPlayer() {
  if (!state.currentTrack) return;

  _fullscreenReturnFocusEl = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  _fsSessionId += 1;
  if (_fsMobileCloseFallbackTimer != null) {
    clearTimeout(_fsMobileCloseFallbackTimer);
    _fsMobileCloseFallbackTimer = null;
  }
  if (_fsDesktopCloseFallbackTimer != null) {
    clearTimeout(_fsDesktopCloseFallbackTimer);
    _fsDesktopCloseFallbackTimer = null;
  }

  fullscreenActive = true;
  setFullscreenShellClass(true);
  if (typeof syncMxmAiFab === 'function') syncMxmAiFab().catch(() => {});
  const fs = $('fullscreen-player');
  const player = $('player');

  await updateFullscreenContent();
  syncMiniPlayerVisibility();
  updateFullscreenPlayUI(!audio.paused);
  syncRepeatButtons();
  const fsShuffle = document.querySelector('.fullscreen-controls .shuffle-btn');
  if (fsShuffle) {
    fsShuffle.classList.toggle('active', state.shuffle);
    fsShuffle.setAttribute('aria-pressed', state.shuffle ? 'true' : 'false');
  }

  document.body.style.overflow = 'hidden';

  if (isMobile()) {
    if (_fsOpenMiniHideTimer != null) {
      clearTimeout(_fsOpenMiniHideTimer);
      _fsOpenMiniHideTimer = null;
    }

    fs.classList.remove('fullscreen--dragging', 'fullscreen-closing');
    fs.classList.remove('fullscreen-mobile-lyrics-visible');
    syncMobileFullscreenLyricsUi();
    const morph = getMiniPlayerMorphState();
    player.classList.remove('hidden');
    syncPlayerClickBlockState();
    fs.classList.remove('hidden');
    activateFullscreenDialogA11y(fs);

    fs.style.transition = 'none';
    fs.style.borderRadius = morph ? '16px' : '';
    fs.style.opacity = morph ? '0.92' : '';
    fs.style.transform = morph
      ? `translate3d(${morph.tx}px, ${morph.ty}px, 0) scale(${morph.sx}, ${morph.sy})`
      : 'translateY(100%)';
    void fs.offsetHeight;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fs.style.transition = 'transform 0.42s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.32s ease, border-radius 0.42s cubic-bezier(0.22, 1, 0.36, 1)';
        fs.style.transform = '';
        fs.style.opacity = '';
        fs.style.borderRadius = '0px';
      });
    });
    const clearOpenInline = () => {
      if (!fullscreenActive || fs.classList.contains('hidden')) return;
      fs.style.transition = '';
      fs.style.transform = '';
      fs.style.opacity = '';
      fs.style.borderRadius = '';
    };
    fs.addEventListener(
      'transitionend',
      (ev) => {
        if (ev.target !== fs) return;
        if (ev.propertyName !== 'transform' && ev.propertyName !== '-webkit-transform') return;
        clearOpenInline();
      },
      { once: true },
    );
    _fsOpenMiniHideTimer = setTimeout(() => {
      _fsOpenMiniHideTimer = null;
      if (fullscreenActive) {
        player.classList.add('hidden');
        syncPlayerClickBlockState();
      }
    }, 170);
    setTimeout(clearOpenInline, 480);
    return;
  }

  if (typeof document.startViewTransition === 'function') {
    document.startViewTransition(() => {
      player.classList.add('hidden');
      syncPlayerClickBlockState();
      fs.classList.remove('hidden');
      activateFullscreenDialogA11y(fs);
    }).finished.catch(() => {});
    return;
  }

  player.classList.add('player-reveal');
  setTimeout(() => {
    player.classList.add('hidden');
    syncPlayerClickBlockState();
    player.classList.remove('player-reveal');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fs.classList.remove('hidden');
        activateFullscreenDialogA11y(fs);
      });
    });
  }, FULLSCREEN_DURATION_MS);
}

function closeFullscreenPlayer(options = {}) {
  const { skipMobileAnimation = false } = options || {};
  if (gradientSwapTimer) {
    clearTimeout(gradientSwapTimer);
    gradientSwapTimer = null;
  }
  if (_fsDesktopCloseFallbackTimer != null) {
    clearTimeout(_fsDesktopCloseFallbackTimer);
    _fsDesktopCloseFallbackTimer = null;
  }

  const fs = $('fullscreen-player');
  const player = $('player');
  const closeForSession = _fsSessionId;
  fullscreenActive = false;
  deactivateFullscreenDialogA11y(fs);
  setFullscreenShellClass(false);
  if (typeof syncMxmAiFab === 'function') syncMxmAiFab().catch(() => {});

  function revealMiniPlayer() {
    player.classList.remove('hidden');
    player.classList.add('player-reveal');
    syncPlayerClickBlockState();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        player.classList.remove('player-reveal');
      });
    });
  }

  if (isMobile()) {
    if (skipMobileAnimation) {
      if (_fsMobileCloseFallbackTimer != null) {
        clearTimeout(_fsMobileCloseFallbackTimer);
        _fsMobileCloseFallbackTimer = null;
      }
      if (_fsOpenMiniHideTimer != null) {
        clearTimeout(_fsOpenMiniHideTimer);
        _fsOpenMiniHideTimer = null;
      }
      fs.classList.add('hidden');
      fs.classList.remove('fullscreen-closing', 'fullscreen--dragging');
      fs.style.transition = '';
      fs.style.transform = '';
      fs.style.opacity = '';
      fs.style.borderRadius = '';
      clearFullscreenContent();
      document.body.style.overflow = '';
      player.classList.remove('hidden');
      player.classList.remove('player-reveal');
      syncPlayerClickBlockState();
      return;
    }

    if (_fsMobileCloseFallbackTimer != null) {
      clearTimeout(_fsMobileCloseFallbackTimer);
      _fsMobileCloseFallbackTimer = null;
    }
    if (_fsOpenMiniHideTimer != null) {
      clearTimeout(_fsOpenMiniHideTimer);
      _fsOpenMiniHideTimer = null;
    }

    fs.classList.remove('fullscreen--dragging');
    fs.classList.add('fullscreen-closing');
    fs.style.transition = '';
    fs.style.transform = '';
    fs.style.opacity = '';
    fs.style.borderRadius = '';
    player.classList.remove('hidden');
    player.classList.remove('player-reveal');
    syncPlayerClickBlockState();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Детеминированное закрытие вниз без обратного "отскока".
        fs.style.transition = 'transform 0.34s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.24s ease';
        fs.style.opacity = '0.98';
        fs.style.transform = 'translateY(100%)';
      });
    });

    let closeSettled = false;
    const done = () => {
      if (closeSettled) return;
      if (closeForSession !== _fsSessionId) return;
      closeSettled = true;
      if (_fsMobileCloseFallbackTimer != null) {
        clearTimeout(_fsMobileCloseFallbackTimer);
        _fsMobileCloseFallbackTimer = null;
      }
      fs.classList.add('hidden');
      fs.classList.remove('fullscreen-closing');
      fs.style.transition = '';
      fs.style.transform = '';
      fs.style.opacity = '';
      fs.style.borderRadius = '';
      clearFullscreenContent();
      document.body.style.overflow = '';
      player.classList.remove('hidden');
      player.classList.remove('player-reveal');
      syncPlayerClickBlockState();
    };

    fs.addEventListener(
      'transitionend',
      (ev) => {
        if (ev.target !== fs) return;
        if (ev.propertyName !== 'transform' && ev.propertyName !== '-webkit-transform') return;
        done();
      },
      { once: true },
    );
    _fsMobileCloseFallbackTimer = setTimeout(() => {
      _fsMobileCloseFallbackTimer = null;
      done();
    }, 450);
    return;
  }

  if (typeof document.startViewTransition === 'function') {
    document.startViewTransition(() => {
      fs.classList.add('hidden');
      player.classList.remove('hidden');
      syncPlayerClickBlockState();
      document.body.style.overflow = '';
    }).finished.then(() => {
      finishFullscreenCloseIfStale(closeForSession, () => {
        clearFullscreenContent();
      });
    }).catch(() => {
      finishFullscreenCloseIfStale(closeForSession, () => {
        clearFullscreenContent();
        document.body.style.overflow = '';
        revealMiniPlayer();
      });
    });
    return;
  }

  const onTransitionEnd = () => {
    fs.removeEventListener('transitionend', onTransitionEnd);
    finishFullscreenCloseIfStale(closeForSession, () => {
      clearFullscreenContent();
      document.body.style.overflow = '';
      revealMiniPlayer();
      syncPlayerClickBlockState();
    });
  };

  fs.addEventListener('transitionend', onTransitionEnd);
  fs.classList.add('hidden');

  setTimeout(() => {
    if (closeForSession === _fsSessionId && !fullscreenActive) {
      document.body.style.overflow = '';
    }
  }, FULLSCREEN_DURATION_MS);

  _fsDesktopCloseFallbackTimer = setTimeout(() => {
    _fsDesktopCloseFallbackTimer = null;
    fs.removeEventListener('transitionend', onTransitionEnd);
    finishFullscreenCloseIfStale(closeForSession, () => {
      clearFullscreenContent();
      document.body.style.overflow = '';
      revealMiniPlayer();
    });
  }, FULLSCREEN_DURATION_MS + 100);
}

/** После выхода с экрана мессенджера: мини-плеер снова над таб-баром, с тем же мягким появлением, что при закрытии fullscreen. */
function revealMainPlayerLeavingMessenger() {
  const player = $('player');
  if (!player || player.classList.contains('hidden') || !state.currentTrack) return;
  player.classList.remove('player-reveal');
  player.classList.add('player-reveal');
  syncPlayerClickBlockState();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      player.classList.remove('player-reveal');
      syncPlayerClickBlockState();
    });
  });
}

function navigateFromPlayerTag(view) {
  if (fullscreenActive) closeFullscreenPlayer();
  navigate(view);
}

/**
 * Бирки «Ремикс» / «Мешап» в МИНИ-ПЛЕЕРЕ и FULLSCREEN — больше не показываем.
 * Их роль теперь играет анимированная неоновая обводка названия (см. setPlayerTitleNeon).
 * В списках треков бирки остаются — их рендерит htmlTrackListTagRow().
 */
function htmlPlayerRemixMashupTags(_track) {
  return '';
}

function htmlPlayerTrackTags(_track) {
  return '';
}

/**
 * Устанавливает заголовок трека в плеере с возможностью «неоновой» обводки.
 * Если track.isRemix или track.isMashup → имя становится кнопкой с анимированной
 * фиолетовой обводкой (см. CSS .mxm-neon-title), клик ведёт в раздел.
 * Если оба флага — приоритет у remix (как в существующем порядке бирок).
 *
 * @param {HTMLElement|null} el — контейнер (#player-title / #fullscreen-title)
 * @param {{title?: string, isRemix?: boolean, isMashup?: boolean}|null} track
 */
function setPlayerTitleNeon(el, track) {
  if (!el) return;
  const title = (track && typeof track.title === 'string' && track.title) ? track.title : '—';
  const target = track && track.isRemix ? 'remix' : (track && track.isMashup ? 'mashup' : null);
  if (!target) {
    el.textContent = title;
    return;
  }
  const variant = target === 'remix' ? 'is-remix' : 'is-mashup';
  const ariaLabel = target === 'remix'
    ? `${title} — открыть раздел «Ремиксы»`
    : `${title} — открыть раздел «Мешапы»`;
  el.textContent = '';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `mxm-neon-title ${variant}`;
  btn.dataset.navTarget = target;
  btn.setAttribute('aria-label', ariaLabel);
  btn.title = target === 'remix' ? 'Открыть раздел «Ремиксы»' : 'Открыть раздел «Мешапы»';
  const text = document.createElement('span');
  text.className = 'mxm-neon-title-text';
  text.textContent = title;
  btn.appendChild(text);
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigateFromPlayerTag(target);
  });
  el.appendChild(btn);
}

/** Ремикс / мешап в списках треков (над названием). */
function htmlTrackListTagRow(track) {
  const remixBadge = track.isRemix ? `<span class="track-tag remix">Ремикс</span>` : '';
  const mashupBadge = track.isMashup ? `<span class="track-tag mashup">Мешап</span>` : '';
  const inner = remixBadge + mashupBadge;
  return inner ? `<div class="track-tags">${inner}</div>` : '';
}

async function updateFullscreenContent(opts = {}) {
    const track = state.currentTrack;
    if (!track) return;
    const fs = $('fullscreen-player');
    const lyricsContainer = $('fullscreen-lyrics');
    const coverContainer = $('fullscreen-cover');
    const radioMetaOnly = !!opts.radioMetaOnly && isRadioPlaybackActive();

    // ─── 1. Текст и артист (мгновенно) ──────────────────────────────────────
    setPlayerTitleNeon($('fullscreen-title'), track);
    renderTrackArtistsToElement($('fullscreen-artist'), track, { closeFullscreenOnNavigate: true });
    renderUploaderProfileLinkToElement($('fullscreen-uploader-link'), track, {
      closeFullscreen: true,
      iconOnly: true,
      iconSize: 14
    });

    const fsTags = $('fullscreen-tags');
    if (fsTags) fsTags.innerHTML = htmlPlayerTrackTags(track);

    const fallbackGrad = track.coverGradient || pickGrad(track.id);
    let newGrad = fallbackGrad;

    // ─── 2. Градиент фона (при смене метаданных радио не пересчитываем — экономим ресурсы) ───
    if (!radioMetaOnly) {
    if (track.coverUrl) {
      const r = await getTrackCoverAmbientPromise(track);
      if (!state.currentTrack || state.currentTrack.id !== track.id) return;
      newGrad = r.gradient;
    }
    const fullscreenGradient = `linear-gradient(${newGrad})`;
    fs.style.setProperty('--fs-active-grad', fullscreenGradient);
    fs.style.background = fullscreenGradient;

    // Гарантируем наличие двух слоёв (один активный, один неактивный)
    if (!fs.querySelector('.fs-grad-layer')) {
        const layerA = document.createElement('div');
        layerA.className = 'fs-grad-layer old active';
        const layerB = document.createElement('div');
        layerB.className = 'fs-grad-layer new';
        fs.insertBefore(layerA, fs.firstChild);
        fs.insertBefore(layerB, fs.firstChild);
        layerA.style.background = fullscreenGradient;
        layerB.style.background = fullscreenGradient;
        previousGradient = newGrad;
    } else if (!fs.querySelector('.fs-grad-layer.active')) {
        const repairLayer = fs.querySelector('.fs-grad-layer.new') || fs.querySelector('.fs-grad-layer');
        if (repairLayer) {
            repairLayer.style.background = fullscreenGradient;
            repairLayer.classList.add('active');
        }
        previousGradient = newGrad;
    } else if (previousGradient === newGrad) {
        // Если градиент не изменился — обновляем активный слой
        const gradActive = fs.querySelector('.fs-grad-layer.active');
        if (gradActive) gradActive.style.background = fullscreenGradient;
    } else {
        // Новый градиент накладывается поверх старого: старый не трогаем, новый плавно появляется сверху
        const gradOld = fs.querySelector('.fs-grad-layer.old');
        const gradNew = fs.querySelector('.fs-grad-layer.new');

        const active = gradNew?.classList.contains('active') ? gradNew : gradOld;
        const inactive = active === gradNew ? gradOld : gradNew;

        if (inactive) {
            inactive.style.background = fullscreenGradient;
            inactive.style.zIndex = '2';
            active.style.zIndex = '1';
            void inactive.offsetHeight;
        }

        if (gradientSwapTimer) {
            clearTimeout(gradientSwapTimer);
            gradientSwapTimer = null;
        }

        requestAnimationFrame(() => {
            if (inactive) inactive.classList.add('active');
        });
        gradientSwapTimer = setTimeout(() => {
            gradientSwapTimer = null;
            active.classList.remove('active');
            if (!fs.querySelector('.fs-grad-layer.active')) {
                const layer = fs.querySelector('.fs-grad-layer');
                if (layer) layer.classList.add('active');
            }
        }, 820);

        previousGradient = newGrad;
    }
    }

    // ─── 3. Обложка: как ? мини-плеере — текущая видна, новая грузится, по классу плавно появляется поверх ───
    if (!coverContainer.querySelector('.cover-layer')) {
        coverContainer.innerHTML = `
            <div class="cover-layer fs-cover-current"></div>
            <div class="cover-layer fs-cover-new"></div>
        `;
    }

    const currentLayer = coverContainer.querySelector('.fs-cover-current');
    const newLayer = coverContainer.querySelector('.fs-cover-new');
    const updateId = ++fullscreenCoverUpdateId;
    const CROSSFADE_DURATION_MS = 850;
    const skipFsCrossfade = document.documentElement.classList.contains('perf-low');

    const runCrossfade = () => {
        if (updateId !== fullscreenCoverUpdateId) return;
        if (state.currentTrack && state.currentTrack.id !== track.id) return;

        const newCoverHtml = coverImg(track.coverUrl, newGrad, 'fullscreen-cover-img');
        const isFirst = !currentLayer.innerHTML.trim();

        if (isFirst || skipFsCrossfade) {
            currentLayer.innerHTML = newCoverHtml;
            newLayer.innerHTML = '';
            coverContainer.classList.remove('fs-cover-crossfade');
            return;
        }

        newLayer.innerHTML = newCoverHtml;
        coverContainer.classList.remove('fs-cover-crossfade');
        // Двойной rAF: часть WebView (MIUI) не применяет transition, если класс в том же кадре, что и innerHTML.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (updateId !== fullscreenCoverUpdateId) return;
                coverContainer.classList.add('fs-cover-crossfade');
            });
        });
        setTimeout(() => {
            if (updateId !== fullscreenCoverUpdateId) return;
            if (state.currentTrack && state.currentTrack.id !== track.id) return;
            currentLayer.innerHTML = '';
            while (newLayer.firstChild) currentLayer.appendChild(newLayer.firstChild);
            coverContainer.classList.remove('fs-cover-crossfade');
        }, CROSSFADE_DURATION_MS);
    };

    if (track.coverUrl) {
        const img = new Image();
        img.onload = runCrossfade;
        img.onerror = runCrossfade;
        img.src = resolveMediaUrl(track.coverUrl);
    } else {
        requestAnimationFrame(runCrossfade);
    }

    if (radioMetaOnly) {
      syncMobileFullscreenLyricsUi();
      return;
    }

    // ─── 4. Кнопка Like ─────────────────────────────────────────────────────
    const likeBtn = fs.querySelector('.fullscreen-like');
    if (likeBtn) {
        const liked = isFavT(track.id);
        likeBtn.classList.toggle('liked', liked);
        likeBtn.setAttribute('aria-pressed', liked ? 'true' : 'false');
        likeBtn.setAttribute('aria-label', liked ? 'Убрать из любимого' : 'Добавить в любимое');
    }
    const dislikeFsBtn = fs.querySelector('.fullscreen-dislike');
    if (dislikeFsBtn) {
        dislikeFsBtn.onclick = (e) => {
            e.stopPropagation();
            dislikeCurrentTrackFromPlayer();
        };
    }
    if (typeof refreshPlayerDislikeUi === 'function') refreshPlayerDislikeUi();

    if (isRadioPlaybackActive()) {
      lyricsContainer.scrollTop = 0;
      lyricsContainer.innerHTML = '';
      lyricsContainer.classList.remove('lyrics-fade-in');
      clearFullscreenSyncedLyricsState();
      fs.classList.remove('fullscreen-mobile-lyrics-visible');
      syncMobileFullscreenLyricsUi();
      return;
    }

    // ─── 5. Текст песни (с кэшированием) ────────────────────────────────────
    lyricsContainer.scrollTop = 0;
    lyricsContainer.innerHTML = '';
    lyricsContainer.classList.remove('lyrics-fade-in');

    if (_lyricsOpen) {
        const mainLyrics = $('lyrics-body')?.innerHTML || '';
        if (mainLyrics && !mainLyrics.includes('page-loading') && !mainLyrics.includes('lyrics-empty')) {
            lyricsContainer.innerHTML = `<div class="lyrics-text">${mainLyrics}</div>`;
            lyricsContainer.classList.add('lyrics-fade-in');
            lyricsContainer.scrollTop = 0;
        } else {
            loadFullscreenLyrics(track.id);
        }
    } else {
        loadFullscreenLyrics(track.id);
    }

    // Синхронизация полосы перемотки с текущим временем
    if (audio.duration) {
        const pct = (audio.currentTime / audio.duration) * 100;
        const fsFill = document.getElementById('fullscreen-progress-fill');
        const fsThumb = document.getElementById('fullscreen-progress-thumb');
        const fsTimeCur = document.getElementById('fullscreen-time-current');
        const fsTimeTotal = document.getElementById('fullscreen-time-total');
        if (fsFill) fsFill.style.width = pct + '%';
        if (fsThumb) fsThumb.style.left = pct + '%';
        if (fsTimeCur) fsTimeCur.textContent = fmtTime(audio.currentTime);
        if (fsTimeTotal) fsTimeTotal.textContent = fmtTime(audio.duration);
    }
}

let _fsSyncLyricsTrackId = null;
let _fsSyncLines = null;
let _fsSyncActiveIndex = -1;
let _fsSyncLastRenderedIndex = -1;
let _fsSyncAnimTimer = null;
let _fsSyncAnimId = 0;
let _fsSyncIsAnimating = false;
/** До первого таймкода: текст виден, но без активной строки */
let _fsSyncBeforeStart = false;
/** На десктопе: пользователь «зафиксировал» просмотр — показываем весь текст со скроллом */
let _fsSyncScrollExpanded = false;
let _fsSyncIdleTimer = null;
let _fsSyncScrollBuiltTrackId = null;
let _fsSyncScrollHandlersBound = false;
let _fsSyncPendingWheelDelta = 0;
let _fsSyncProgrammaticScroll = false;
/** Блокирует сброс таймера 4 с при программном скролле перед выходом в карусель */
let _fsSyncSuppressScrollActivity = false;
/** Мобильный: пользователь листает вручную — не подтягивать список к строке при воспроизведении */
let _fsSyncUserScrolledAway = false;
/** Последняя строка, к которой подтянули список (для плавного шага +1 на мобильном) */
let _fsSyncLastScrollFollowIdx = -1;
/** Последний корректный --fs-lyrics-slot (измерение при свёрнутой колонке даёт ~96px и наложение строк). */
let _fsSyncCachedSlotPx = 0;
let _fsSyncLayoutObserverBound = false;
let _fsSyncResizeTimer = null;
let _fsSyncScrollRenderedIndex = -1;

const FS_SYNC_LAYOUT_MIN_H = 72;
const FS_SYNC_LAYOUT_MIN_W = 48;

const FS_LYRICS_ANIM_MS = 460;
const FS_SYNC_RECENTER_MS = 4000;

const FS_SYNC_POS_CLASSES = [
  'fs-lyrics-sync-line--at-prev',
  'fs-lyrics-sync-line--at-active',
  'fs-lyrics-sync-line--at-next',
  'fs-lyrics-sync-line--off-above',
  'fs-lyrics-sync-line--off-below'
];
const FS_SYNC_TONE_CLASSES = [
  'fs-lyrics-sync-line--tone-active',
  'fs-lyrics-sync-line--tone-side',
  'fs-lyrics-sync-line--tone-hidden',
  'fs-lyrics-sync-line--tone-waiting'
];

function fsSyncIsMobileLyricsOpen() {
  if (!isMobile()) return false;
  const fs = $('fullscreen-player');
  return !!(fs && fs.classList.contains('fullscreen-mobile-lyrics-visible'));
}

function fsSyncMobileAutoFollowEnabled() {
  return fsSyncIsMobileLyricsOpen() && _fsSyncScrollExpanded && !_fsSyncUserScrolledAway;
}

function fsSyncLyricsLayoutReady(container) {
  const lyrics = container || $('fullscreen-lyrics');
  if (!lyrics) return false;
  const fs = $('fullscreen-player');
  if (!fs || fs.classList.contains('hidden')) return false;
  if (isMobile() && !fsSyncIsMobileLyricsOpen()) return false;
  const root = lyrics.querySelector('.fs-lyrics-sync') || lyrics;
  const rect = root.getBoundingClientRect();
  return rect.height >= FS_SYNC_LAYOUT_MIN_H && rect.width >= FS_SYNC_LAYOUT_MIN_W;
}

function fsSyncApplySlotPx(root, slotPx) {
  if (!root || !Number.isFinite(slotPx) || slotPx <= 0) return;
  _fsSyncCachedSlotPx = slotPx;
  root.style.setProperty('--fs-lyrics-slot', `${Math.round(slotPx)}px`);
}

function fsSyncBindLyricsLayoutObserver() {
  if (_fsSyncLayoutObserverBound || typeof ResizeObserver === 'undefined') return;
  const lyrics = $('fullscreen-lyrics');
  if (!lyrics) return;
  _fsSyncLayoutObserverBound = true;
  const ro = new ResizeObserver(() => {
    if (_fsSyncResizeTimer) clearTimeout(_fsSyncResizeTimer);
    _fsSyncResizeTimer = setTimeout(() => {
      _fsSyncResizeTimer = null;
      if (!fsSyncLyricsLayoutReady(lyrics)) return;
      fsSyncRemeasureAndRepaintIfNeeded();
    }, 80);
  });
  ro.observe(lyrics);
}

function fsSyncRemeasureAndRepaintIfNeeded() {
  if (!_fsSyncLines?.length || !fullscreenActive) return;
  if (!fsSyncLyricsLayoutReady()) return;
  const container = $('fullscreen-lyrics');
  if (!container) return;
  const time = audio.currentTime || 0;
  const idx = findSyncedLyricLineIndex(_fsSyncLines, time);
  _fsSyncActiveIndex = idx;
  if (_fsSyncScrollExpanded) {
    const root = container.querySelector('.fs-lyrics-sync');
    if (root) fsSyncMeasureScrollSlot(root, '', _fsSyncLines);
    fsSyncUpdateScrollActive(idx, { scroll: fsSyncMobileAutoFollowEnabled(), smooth: false });
    return;
  }
  _fsSyncLastRenderedIndex = -1;
  renderFullscreenSyncedLyrics(_fsSyncLines, idx, { animate: false });
}

function fsSyncCanExpandScroll() {
  try {
    if (window.matchMedia('(min-width: 901px)').matches) return true;
    return fsSyncIsMobileLyricsOpen();
  } catch {
    return false;
  }
}

function clearFullscreenSyncedLyricsState() {
  _fsSyncLyricsTrackId = null;
  _fsSyncLines = null;
  _fsSyncActiveIndex = -1;
  _fsSyncLastRenderedIndex = -1;
  _fsSyncIsAnimating = false;
  _fsSyncBeforeStart = false;
  _fsSyncScrollExpanded = false;
  _fsSyncScrollBuiltTrackId = null;
  _fsSyncSuppressScrollActivity = false;
  _fsSyncProgrammaticScroll = false;
  _fsSyncUserScrolledAway = false;
  _fsSyncLastScrollFollowIdx = -1;
  _fsSyncCachedSlotPx = 0;
  _fsSyncScrollRenderedIndex = -1;
  _fsSyncAnimId += 1;
  if (_fsSyncResizeTimer) {
    clearTimeout(_fsSyncResizeTimer);
    _fsSyncResizeTimer = null;
  }
  if (_fsSyncAnimTimer) {
    clearTimeout(_fsSyncAnimTimer);
    _fsSyncAnimTimer = null;
  }
  if (_fsSyncIdleTimer) {
    clearTimeout(_fsSyncIdleTimer);
    _fsSyncIdleTimer = null;
  }
}

function fsSyncClearIdleTimer() {
  if (_fsSyncIdleTimer) {
    clearTimeout(_fsSyncIdleTimer);
    _fsSyncIdleTimer = null;
  }
}

function fsSyncScheduleRecenter() {
  if (!_fsSyncScrollExpanded) return;
  if (fsSyncIsMobileLyricsOpen()) {
    syncMobileLyricsFollowButton();
    return;
  }
  fsSyncClearIdleTimer();
  _fsSyncIdleTimer = setTimeout(() => {
    _fsSyncIdleTimer = null;
    fsSyncRecenterAndExitScrollMode();
  }, FS_SYNC_RECENTER_MS);
}

/** После 4 с без действий: плавно к активной строке и возврат к карусели */
async function fsSyncRecenterAndExitScrollMode() {
  if (!_fsSyncScrollExpanded || !_fsSyncLines?.length) return;
  const stayInScrollList = fsSyncIsMobileLyricsOpen();
  _fsSyncSuppressScrollActivity = true;
  _fsSyncProgrammaticScroll = true;
  try {
    const time = audio.currentTime || 0;
    const beforeStart = fsSyncIsBeforeStart(_fsSyncLines, time);
    const idx = findSyncedLyricLineIndex(_fsSyncLines, time);
    _fsSyncActiveIndex = idx;
    fsSyncUpdateScrollActive(idx, { scroll: false, beforeStart });
    await fsSyncScrollToActiveInViewport(idx, {
      smooth: true,
      manageScrollGuard: false
    });
  } finally {
    _fsSyncProgrammaticScroll = false;
    _fsSyncSuppressScrollActivity = false;
    if (stayInScrollList) {
      _fsSyncUserScrolledAway = false;
      _fsSyncLastScrollFollowIdx = findSyncedLyricLineIndex(
        _fsSyncLines,
        audio.currentTime || 0
      );
      fsSyncClearIdleTimer();
    } else {
      fsSyncExitScrollMode();
    }
  }
}

function fsSyncOnScrollUserActivity() {
  if (!_fsSyncScrollExpanded) return;
  if (fsSyncIsMobileLyricsOpen()) {
    if (!_fsSyncProgrammaticScroll && !_fsSyncSuppressScrollActivity) {
      _fsSyncUserScrolledAway = true;
      _fsSyncLastScrollFollowIdx = -1;
      syncMobileLyricsFollowButton();
    }
    return;
  }
  fsSyncScheduleRecenter();
}

function fsSyncCancelViewportSmoothScroll() {
  const viewport = fsSyncGetScrollViewport();
  if (!viewport) return;
  const y = viewport.scrollTop;
  try {
    viewport.scrollTo({ top: y, behavior: 'auto' });
  } catch {
    viewport.scrollTop = y;
  }
}

function fsSyncBindScrollViewportTouch(viewport) {
  if (!viewport || viewport._mxmTouchBound) return;
  viewport._mxmTouchBound = true;

  viewport.addEventListener(
    'touchstart',
    () => {
      if (!fsSyncIsMobileLyricsOpen() || !_fsSyncScrollExpanded) return;
      if (_fsSyncProgrammaticScroll || _fsSyncSuppressScrollActivity) return;
      _fsSyncUserScrolledAway = true;
      _fsSyncLastScrollFollowIdx = -1;
      fsSyncClearIdleTimer();
      fsSyncCancelViewportSmoothScroll();
      syncMobileLyricsFollowButton();
    },
    { passive: true }
  );

  viewport.addEventListener(
    'touchend',
    () => {
      if (!fsSyncIsMobileLyricsOpen() || !_fsSyncScrollExpanded) return;
      if (_fsSyncProgrammaticScroll || _fsSyncSuppressScrollActivity) return;
      if (!_fsSyncUserScrolledAway) return;
      syncMobileLyricsFollowButton();
    },
    { passive: true }
  );
}

function fsSyncGetScrollViewport() {
  return $('fullscreen-lyrics')?.querySelector('.fs-lyrics-sync-viewport--scroll') || null;
}

function fsSyncApplyWheelToViewport(deltaY) {
  const viewport = fsSyncGetScrollViewport();
  if (!viewport || !Number.isFinite(deltaY)) return;
  viewport.scrollTop += deltaY;
}

function fsSyncEnterScrollMode() {
  if (!_fsSyncLines?.length || _fsSyncScrollExpanded || !fsSyncCanExpandScroll()) return;
  _fsSyncScrollExpanded = true;
  const idx =
    _fsSyncActiveIndex >= 0
      ? _fsSyncActiveIndex
      : findSyncedLyricLineIndex(_fsSyncLines, audio.currentTime || 0);
  _fsSyncActiveIndex = idx;
  const pending = _fsSyncPendingWheelDelta;
  _fsSyncPendingWheelDelta = 0;
  renderFullscreenSyncedLyricsScroll(_fsSyncLines, idx, { scroll: true, smooth: false });
  if (pending) fsSyncApplyWheelToViewport(pending);
  if (!fsSyncIsMobileLyricsOpen()) fsSyncScheduleRecenter();
}

function fsSyncExitScrollMode(opts = {}) {
  const { paintCarousel = true } = opts || {};
  if (!_fsSyncScrollExpanded || !_fsSyncLines?.length) return;
  _fsSyncScrollExpanded = false;
  _fsSyncScrollBuiltTrackId = null;
  fsSyncClearIdleTimer();
  const container = $('fullscreen-lyrics');
  if (container) {
    container.classList.remove('fs-lyrics-scroll-mode');
    container.scrollTop = 0;
    container.innerHTML = '';
  }
  if (!paintCarousel) return;
  if (isMobile() && !fsSyncIsMobileLyricsOpen()) return;

  const idx = findSyncedLyricLineIndex(_fsSyncLines, audio.currentTime || 0);
  _fsSyncActiveIndex = idx;
  _fsSyncLastRenderedIndex = -1;
  renderFullscreenSyncedLyrics(_fsSyncLines, idx, { animate: false });
}

function fsSyncBindScrollHandlers(container) {
  if (!container || _fsSyncScrollHandlersBound) return;
  _fsSyncScrollHandlersBound = true;

  container.addEventListener(
    'wheel',
    (e) => {
      if (!container.classList.contains('fs-lyrics-sync-mode') || !_fsSyncLines?.length) return;
      if (!fsSyncCanExpandScroll()) return;
      if (Math.abs(e.deltaY) < 1 && Math.abs(e.deltaX) < 1) return;

      if (_fsSyncScrollExpanded) {
        e.preventDefault();
        e.stopPropagation();
        fsSyncApplyWheelToViewport(e.deltaY);
        fsSyncOnScrollUserActivity();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      _fsSyncPendingWheelDelta = e.deltaY;
      fsSyncEnterScrollMode();
    },
    { passive: false }
  );

  container.addEventListener('click', (e) => {
    if (!_fsSyncScrollExpanded) return;
    const line = e.target.closest('.fs-lyrics-sync-scroll-inner .fs-lyrics-sync-line');
    if (!line) return;
    const start = Number(line.dataset.start);
    if (!Number.isFinite(start)) return;
    e.preventDefault();
    audio.currentTime = Math.max(0, start);
    if (audio.paused && state.currentTrack?.audioUrl) {
      audio.play().catch(() => {});
    }
    const idx = Number(line.dataset.index);
    if (Number.isFinite(idx)) {
      _fsSyncActiveIndex = idx;
      fsSyncUpdateScrollActive(idx, { scroll: false });
    }
    if (fsSyncIsMobileLyricsOpen()) {
      _fsSyncUserScrolledAway = false;
      _fsSyncLastScrollFollowIdx = Number.isFinite(idx) ? idx : -1;
      syncMobileLyricsFollowButton();
    }
    fsSyncScheduleRecenter();
  });
}

function fsSyncMeasureScrollSlot(root, sampleText, lines) {
  if (!root) return;
  const mobile = fsSyncIsMobileLyricsOpen();
  const minSlot = mobile ? 88 : 96;
  const maxSlot = mobile ? 520 : 420;
  const pad = mobile ? 20 : 44;

  if (!fsSyncLyricsLayoutReady()) {
    if (_fsSyncCachedSlotPx > 0) {
      root.style.setProperty('--fs-lyrics-slot', `${_fsSyncCachedSlotPx}px`);
    }
    return;
  }

  const probe = document.createElement('div');
  probe.className =
    'fs-lyrics-sync-line fs-lyrics-sync-line--tone-active fs-lyrics-sync-line--scroll-item';
  probe.setAttribute('aria-hidden', 'true');
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  probe.style.position = 'relative';
  probe.style.top = 'auto';
  probe.style.left = 'auto';
  probe.style.right = 'auto';
  probe.style.transform = 'none';
  probe.style.height = 'auto';
  probe.style.maxHeight = 'none';
  root.appendChild(probe);

  const texts = Array.isArray(lines) && lines.length
    ? lines
      .map((line) => (line?.text ? String(line.text) : ''))
      .sort((a, b) => b.length - a.length)
      .slice(0, 12)
    : [sampleText || 'Sample'];
  let maxH = 0;
  for (const text of texts) {
    probe.textContent = text || '\u00a0';
    maxH = Math.max(
      maxH,
      probe.offsetHeight || 0,
      probe.getBoundingClientRect().height || 0
    );
  }
  probe.remove();

  const slotPx = Math.max(
    minSlot,
    Math.min(maxSlot, Math.ceil(maxH + pad))
  );
  fsSyncApplySlotPx(root, slotPx);
}

function fsSyncBuildScrollDom(container, lines) {
  container.innerHTML = '';
  const root = document.createElement('div');
  root.className = 'fs-lyrics-sync';

  const viewport = document.createElement('div');
  viewport.className = 'fs-lyrics-sync-viewport fs-lyrics-sync-viewport--scroll';

  const inner = document.createElement('div');
  inner.className = 'fs-lyrics-sync-scroll-inner';
  inner.setAttribute('role', 'list');

  let sampleText = '';
  lines.forEach((line, i) => {
    const text = line && line.text ? String(line.text) : '';
    if (!sampleText && text) sampleText = text.slice(0, 48);
    const el = document.createElement('div');
    el.className = 'fs-lyrics-sync-line fs-lyrics-sync-line--tone-side fs-lyrics-sync-line--scroll-item';
    el.dataset.index = String(i);
    el.dataset.start = String(Number(line.start) || 0);
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '-1');
    el.setAttribute('aria-label', `${text || 'Пауза'}, перейти к ${fmtTime(Number(line.start) || 0)}`);
    el.textContent = text || '\u00a0';
    if (!text) el.classList.add('fs-lyrics-sync-line--empty');
    inner.appendChild(el);
  });

  viewport.appendChild(inner);
  root.appendChild(viewport);
  container.appendChild(root);
  fsSyncMeasureScrollSlot(root, sampleText, lines);

  viewport.addEventListener(
    'scroll',
    () => {
      if (
        !_fsSyncScrollExpanded ||
        _fsSyncProgrammaticScroll ||
        _fsSyncSuppressScrollActivity
      ) {
        return;
      }
      fsSyncOnScrollUserActivity();
    },
    { passive: true }
  );

  fsSyncBindScrollViewportTouch(viewport);
  fsSyncBindScrollLineHover(inner);
  inner.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const line = event.target.closest('.fs-lyrics-sync-line--scroll-item');
    if (!line) return;
    event.preventDefault();
    line.click();
  });

  _fsSyncScrollBuiltTrackId = _fsSyncLyricsTrackId;
  root._mxmScrollViewport = viewport;
}

function fsSyncBindScrollLineHover(inner) {
  if (!inner || inner._mxmHoverBound) return;
  inner._mxmHoverBound = true;
  inner.addEventListener('mouseover', (e) => {
    const line = e.target.closest('.fs-lyrics-sync-line--scroll-item');
    if (!line || !inner.contains(line)) return;
    inner.querySelectorAll('.fs-lyrics-sync-line--hover').forEach((el) => {
      if (el !== line) el.classList.remove('fs-lyrics-sync-line--hover');
    });
    line.classList.add('fs-lyrics-sync-line--hover');
  });
  inner.addEventListener('mouseleave', (e) => {
    if (e.relatedTarget && inner.contains(e.relatedTarget)) return;
    inner.querySelectorAll('.fs-lyrics-sync-line--hover').forEach((el) => {
      el.classList.remove('fs-lyrics-sync-line--hover');
    });
  });
}

function fsSyncMobileAnchorY(viewport) {
  return Math.round(viewport.clientHeight * 0.34);
}

function fsSyncComputeScrollTargetTop(viewport, activeEl) {
  const maxScroll = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
  let top;
  if (fsSyncIsMobileLyricsOpen()) {
    const anchorY = fsSyncMobileAnchorY(viewport);
    top = activeEl.offsetTop + activeEl.offsetHeight / 2 - anchorY;
  } else {
    top =
      activeEl.offsetTop -
      (viewport.clientHeight - activeEl.offsetHeight) / 2;
  }
  return Math.max(0, Math.min(maxScroll, Math.round(top)));
}

function fsSyncFinishViewportScroll(viewport, manageScrollGuard, resolve) {
  if (!manageScrollGuard) {
    resolve();
    return;
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      _fsSyncProgrammaticScroll = false;
      resolve();
    });
  });
}

function fsSyncViewportScrollBy(viewport, delta, opts = {}) {
  const smooth = opts.smooth !== false;
  const manageScrollGuard = opts.manageScrollGuard !== false;
  if (manageScrollGuard) _fsSyncProgrammaticScroll = true;

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      fsSyncFinishViewportScroll(viewport, manageScrollGuard, resolve);
    };

    try {
      viewport.scrollBy({ top: delta, behavior: smooth ? 'smooth' : 'auto' });
    } catch {
      viewport.scrollTop += delta;
    }

    if (!smooth) {
      finish();
      return;
    }
    setTimeout(finish, 720);
  });
}

function fsSyncScrollToActiveInViewport(activeIndex, opts = {}) {
  const viewport = fsSyncGetScrollViewport();
  const inner = $('fullscreen-lyrics')?.querySelector('.fs-lyrics-sync-scroll-inner');
  if (!viewport || !inner) return Promise.resolve();

  const idx = Math.max(0, Math.min(inner.children.length - 1, Number(activeIndex) || 0));
  const active = inner.children[idx];
  if (!active) return Promise.resolve();

  const smooth = opts.smooth !== false;
  const manageScrollGuard = opts.manageScrollGuard !== false;

  if (
    fsSyncIsMobileLyricsOpen() &&
    smooth &&
    _fsSyncLastScrollFollowIdx >= 0 &&
    idx === _fsSyncLastScrollFollowIdx + 1
  ) {
    const prev = inner.children[_fsSyncLastScrollFollowIdx];
    if (prev) {
      const delta = active.offsetTop - prev.offsetTop;
      if (delta > 0 && delta <= viewport.clientHeight * 1.35) {
        _fsSyncLastScrollFollowIdx = idx;
        return fsSyncViewportScrollBy(viewport, delta, { smooth: true, manageScrollGuard });
      }
    }
  }

  _fsSyncLastScrollFollowIdx = idx;
  const targetTop = fsSyncComputeScrollTargetTop(viewport, active);

  if (manageScrollGuard) _fsSyncProgrammaticScroll = true;

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      fsSyncFinishViewportScroll(viewport, manageScrollGuard, resolve);
    };

    try {
      viewport.scrollTo({
        top: targetTop,
        behavior: smooth ? 'smooth' : 'auto'
      });
    } catch {
      viewport.scrollTop = targetTop;
    }

    if (!smooth) {
      finish();
      return;
    }

    const onScrollEnd = () => {
      if (Math.abs(viewport.scrollTop - targetTop) < 8) finish();
    };
    if ('onscrollend' in viewport) {
      viewport.addEventListener('scrollend', onScrollEnd, { once: true });
    }
    setTimeout(finish, 850);
  });
}

function fsSyncFirstLineStart(lines) {
  if (!Array.isArray(lines) || !lines.length) return 0;
  return Number(lines[0].start) || 0;
}

function fsSyncIsBeforeStart(lines, t) {
  const first = fsSyncFirstLineStart(lines);
  if (first < 0.08) return false;
  return (Number(t) || 0) < first - 0.02;
}

function fsSyncSyncBeforeStartClass(container, beforeStart) {
  if (!container) return;
  container.classList.toggle('fs-lyrics-sync--before-start', !!beforeStart);
  const root = container.querySelector('.fs-lyrics-sync');
  if (root) root.classList.toggle('fs-lyrics-sync--before-start', !!beforeStart);
}

function fsSyncUpdateScrollActive(activeIndex, opts = {}) {
  const container = $('fullscreen-lyrics');
  const inner = container?.querySelector('.fs-lyrics-sync-scroll-inner');
  const viewport = fsSyncGetScrollViewport();
  if (!inner) return;
  const idx = Math.max(0, Math.min((inner.children.length || 1) - 1, Number(activeIndex) || 0));
  const beforeStart = !!opts.beforeStart;
  const previousIndex = _fsSyncScrollRenderedIndex;
  inner.querySelectorAll('.fs-lyrics-sync-line').forEach((el, i) => {
    el.classList.remove(...FS_SYNC_TONE_CLASSES);
    el.classList.toggle('fs-lyrics-sync-line--scroll-past', !beforeStart && i < idx);
    if (beforeStart) {
      el.classList.add(
        i === 0 ? 'fs-lyrics-sync-line--tone-waiting' : 'fs-lyrics-sync-line--tone-side'
      );
    } else if (i === idx) {
      el.classList.add('fs-lyrics-sync-line--tone-active');
    } else {
      el.classList.add('fs-lyrics-sync-line--tone-side');
    }
    const active = !beforeStart && i === idx;
    if (active) el.setAttribute('aria-current', 'true');
    else el.removeAttribute('aria-current');
    el.setAttribute('tabindex', active ? '0' : '-1');
  });
  _fsSyncScrollRenderedIndex = idx;
  if (!beforeStart && previousIndex !== idx) {
    const live = $('fullscreen-lyrics-live');
    const current = inner.children[idx];
    if (live && current) live.textContent = current.textContent || '';
  }
  if (opts.scroll) {
    fsSyncScrollToActiveInViewport(idx, { smooth: opts.smooth !== false });
  }
}

function renderFullscreenSyncedLyricsScroll(lines, activeIndex, opts = {}) {
  const container = $('fullscreen-lyrics');
  if (!container || !lines?.length) return;

  const idx = Math.max(0, Math.min(lines.length - 1, Number(activeIndex) || 0));
  const time = Number(opts.time ?? audio.currentTime) || 0;
  const beforeStart =
    opts.beforeStart != null ? !!opts.beforeStart : fsSyncIsBeforeStart(lines, time);
  _fsSyncBeforeStart = beforeStart;

  container.classList.add('lyrics-fade-in', 'fs-lyrics-sync-mode', 'fs-lyrics-scroll-mode');
  fsSyncSyncBeforeStartClass(container, beforeStart);
  fsSyncBindScrollHandlers(container);

  const needsRebuild =
    _fsSyncScrollBuiltTrackId !== _fsSyncLyricsTrackId ||
    !container.querySelector('.fs-lyrics-sync-viewport--scroll');
  if (needsRebuild) {
    fsSyncBuildScrollDom(container, lines);
  }

  fsSyncUpdateScrollActive(idx, {
    scroll: opts.scroll !== false,
    smooth: !!opts.smooth,
    beforeStart
  });
  _fsSyncActiveIndex = idx;
  _fsSyncLastRenderedIndex = idx;
  _fsSyncIsAnimating = false;
}

function fsSyncSetLineText(el, line) {
  if (!el) return;
  const text = line && line.text ? String(line.text) : '';
  el.textContent = text || '\u00a0';
  el.classList.toggle('fs-lyrics-sync-line--empty', !text);
}

function fsSyncApplyState(el, posCls, toneCls) {
  if (!el) return;
  el.classList.remove(...FS_SYNC_POS_CLASSES, ...FS_SYNC_TONE_CLASSES);
  if (posCls) el.classList.add(posCls);
  if (toneCls) el.classList.add(toneCls);
}

function fsSyncMeasureLineHeight(el) {
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  const h = Math.max(rect.height || 0, el.scrollHeight || 0, el.offsetHeight || 0);
  return Number.isFinite(h) ? h : 0;
}

function fsSyncUpdateDynamicSlot(root, refs) {
  if (!root || !refs) return;
  if (!fsSyncLyricsLayoutReady()) {
    if (_fsSyncCachedSlotPx > 0) {
      root.style.setProperty('--fs-lyrics-slot', `${_fsSyncCachedSlotPx}px`);
    }
    return;
  }
  // Берём максимум по видимым и incoming-строкам, чтобы крупные многострочные куплеты не накладывались.
  const maxLine = Math.max(
    fsSyncMeasureLineHeight(refs.prevEl),
    fsSyncMeasureLineHeight(refs.activeEl),
    fsSyncMeasureLineHeight(refs.nextEl),
    fsSyncMeasureLineHeight(refs.incomingEl)
  );
  const slotPx = Math.max(96, Math.min(420, Math.ceil(maxLine + 44)));
  fsSyncApplySlotPx(root, slotPx);
}

function ensureFsSyncNodes(container) {
  let root = container.querySelector('.fs-lyrics-sync');
  if (!root) {
    container.innerHTML = '';
    root = document.createElement('div');
    root.className = 'fs-lyrics-sync';
    root.setAttribute('aria-live', 'polite');
    root.innerHTML = `
      <div class="fs-lyrics-sync-viewport">
        <div class="fs-lyrics-sync-line fs-lyrics-sync-line--prev fs-lyrics-sync-line--at-prev fs-lyrics-sync-line--tone-side" aria-hidden="true"></div>
        <div class="fs-lyrics-sync-line fs-lyrics-sync-line--active fs-lyrics-sync-line--at-active fs-lyrics-sync-line--tone-active"></div>
        <div class="fs-lyrics-sync-line fs-lyrics-sync-line--next fs-lyrics-sync-line--at-next fs-lyrics-sync-line--tone-side" aria-hidden="true"></div>
        <div class="fs-lyrics-sync-line fs-lyrics-sync-line--incoming fs-lyrics-sync-line--off-below fs-lyrics-sync-line--tone-hidden" aria-hidden="true"></div>
      </div>`;
    container.appendChild(root);
  }

  if (!root._mxmSyncNodes) {
    root._mxmSyncNodes = {
      prevEl: root.querySelector('.fs-lyrics-sync-line--prev'),
      activeEl: root.querySelector('.fs-lyrics-sync-line--active'),
      nextEl: root.querySelector('.fs-lyrics-sync-line--next'),
      incomingEl: root.querySelector('.fs-lyrics-sync-line--incoming')
    };
  }

  return { root, ...root._mxmSyncNodes };
}

function fsSyncRenderStatic(nodes, prev, cur, next, opts = {}) {
  if (opts.beforeStart) {
    fsSyncRenderBeforeStartCarousel(nodes, opts.lines || (cur ? [cur] : []));
    return;
  }
  fsSyncSetLineText(nodes.prevEl, prev);
  fsSyncSetLineText(nodes.activeEl, cur);
  fsSyncSetLineText(nodes.nextEl, next);
  fsSyncSetLineText(nodes.incomingEl, null);
  fsSyncApplyState(nodes.prevEl, 'fs-lyrics-sync-line--at-prev', 'fs-lyrics-sync-line--tone-side');
  fsSyncApplyState(nodes.activeEl, 'fs-lyrics-sync-line--at-active', 'fs-lyrics-sync-line--tone-active');
  fsSyncApplyState(nodes.nextEl, 'fs-lyrics-sync-line--at-next', 'fs-lyrics-sync-line--tone-side');
  fsSyncApplyState(nodes.incomingEl, 'fs-lyrics-sync-line--off-below', 'fs-lyrics-sync-line--tone-hidden');
}

/** До старта: первая строка по центру с пульсом, следующая ниже — без «активной» подсветки */
function fsSyncRenderBeforeStartCarousel(nodes, lines) {
  const list = Array.isArray(lines) ? lines : [];
  const cur = list[0] || null;
  const next = list.length > 1 ? list[1] : null;
  fsSyncSetLineText(nodes.prevEl, null);
  fsSyncSetLineText(nodes.activeEl, cur);
  fsSyncSetLineText(nodes.nextEl, next);
  fsSyncSetLineText(nodes.incomingEl, null);
  fsSyncApplyState(nodes.prevEl, 'fs-lyrics-sync-line--at-prev', 'fs-lyrics-sync-line--tone-hidden');
  fsSyncApplyState(nodes.activeEl, 'fs-lyrics-sync-line--at-active', 'fs-lyrics-sync-line--tone-waiting');
  fsSyncApplyState(nodes.nextEl, 'fs-lyrics-sync-line--at-next', 'fs-lyrics-sync-line--tone-side');
  fsSyncApplyState(nodes.incomingEl, 'fs-lyrics-sync-line--off-below', 'fs-lyrics-sync-line--tone-hidden');
}

function findSyncedLyricLineIndex(lines, t) {
  if (!Array.isArray(lines) || !lines.length) return 0;
  const time = Number(t) || 0;
  let low = 0;
  let high = lines.length - 1;
  let match = 0;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (time >= Number(lines[mid].start || 0)) {
      match = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return match;
}

function applyFullscreenLyricsPayload(trackId, payload) {
  const container = $('fullscreen-lyrics');
  if (!container) return;
  const data = payload && typeof payload === 'object' ? payload : { lyrics: payload };
  if (data.timecoded && Array.isArray(data.lines) && data.lines.length) {
    _fsSyncLyricsTrackId = trackId;
    _fsSyncLines = data.lines;
    const time = audio.currentTime || 0;
    _fsSyncBeforeStart = fsSyncIsBeforeStart(_fsSyncLines, time);
    _fsSyncActiveIndex = findSyncedLyricLineIndex(_fsSyncLines, time);
    _fsSyncScrollExpanded = false;
    fsSyncBindScrollHandlers(container);
    fsSyncBindLyricsLayoutObserver();
    if (isMobile() && $('fullscreen-player')?.classList.contains('fullscreen-mobile-lyrics-visible')) {
      fsSyncEnterScrollMode();
    } else {
      renderFullscreenSyncedLyrics(_fsSyncLines, _fsSyncActiveIndex);
    }
    if (isMobile()) syncMobileFullscreenLyricsUi();
    return;
  }
  clearFullscreenSyncedLyricsState();
  container.classList.remove('fs-lyrics-sync-mode', 'fs-lyrics-scroll-mode', 'fs-lyrics-sync--before-start');
  renderFullscreenLyrics(data.lyrics);
  if (isMobile()) syncMobileFullscreenLyricsUi();
}

function renderFullscreenSyncedLyrics(lines, activeIndex, opts = {}) {
  if (_fsSyncScrollExpanded && fsSyncCanExpandScroll()) {
    renderFullscreenSyncedLyricsScroll(lines, activeIndex, opts);
    return;
  }

  if (isMobile() && !fsSyncIsMobileLyricsOpen()) {
    const idxOnly = Math.max(0, Math.min((lines?.length || 1) - 1, Number(activeIndex) || 0));
    _fsSyncActiveIndex = idxOnly;
    if (opts.beforeStart != null) _fsSyncBeforeStart = !!opts.beforeStart;
    return;
  }

  const container = $('fullscreen-lyrics');
  if (!container || !lines?.length) return;
  container.classList.remove('fs-lyrics-scroll-mode');

  const time = Number(opts.time ?? audio.currentTime) || 0;
  const beforeStart =
    opts.beforeStart != null ? !!opts.beforeStart : fsSyncIsBeforeStart(lines, time);
  _fsSyncBeforeStart = beforeStart;
  fsSyncSyncBeforeStartClass(container, beforeStart);

  const idx = Math.max(0, Math.min(lines.length - 1, Number(activeIndex) || 0));
  const prev = idx > 0 ? lines[idx - 1] : null;
  const cur = lines[idx];
  const next = idx < lines.length - 1 ? lines[idx + 1] : null;
  const animate = !!opts.animate && !beforeStart && !fullscreenPrefersReducedMotion();
  const step = _fsSyncLastRenderedIndex >= 0 ? idx - _fsSyncLastRenderedIndex : 0;
  const isForward = step > 0;
  const isBackward = step < 0;
  const canAnimate = animate && Math.abs(step) === 1;
  const nodes = ensureFsSyncNodes(container);
  const refs = nodes.root._mxmSyncNodes;
  container.classList.add('lyrics-fade-in', 'fs-lyrics-sync-mode');
  container.scrollTop = 0;

  if (_fsSyncAnimTimer) {
    clearTimeout(_fsSyncAnimTimer);
    _fsSyncAnimTimer = null;
  }
  _fsSyncAnimId += 1;

  if (!canAnimate) {
    if (beforeStart) {
      fsSyncRenderBeforeStartCarousel(refs, lines);
    } else {
      fsSyncRenderStatic(refs, prev, cur, next);
    }
    fsSyncUpdateDynamicSlot(nodes.root, refs);
    _fsSyncIsAnimating = false;
    _fsSyncLastRenderedIndex = idx;
    _fsSyncActiveIndex = idx;
    return;
  }

  _fsSyncIsAnimating = true;
  _fsSyncLastRenderedIndex = idx;
  const animId = _fsSyncAnimId;

  if (isForward) {
    fsSyncSetLineText(refs.incomingEl, next);
    fsSyncApplyState(refs.incomingEl, 'fs-lyrics-sync-line--off-below', 'fs-lyrics-sync-line--tone-hidden');
    void refs.incomingEl.offsetWidth;

    fsSyncApplyState(refs.prevEl, 'fs-lyrics-sync-line--off-above', 'fs-lyrics-sync-line--tone-hidden');
    fsSyncApplyState(refs.activeEl, 'fs-lyrics-sync-line--at-prev', 'fs-lyrics-sync-line--tone-side');
    fsSyncApplyState(refs.nextEl, 'fs-lyrics-sync-line--at-active', 'fs-lyrics-sync-line--tone-active');
    fsSyncApplyState(refs.incomingEl, 'fs-lyrics-sync-line--at-next', 'fs-lyrics-sync-line--tone-side');
  } else if (isBackward) {
    fsSyncSetLineText(refs.incomingEl, prev);
    fsSyncApplyState(refs.incomingEl, 'fs-lyrics-sync-line--off-above', 'fs-lyrics-sync-line--tone-hidden');
    void refs.incomingEl.offsetWidth;

    fsSyncApplyState(refs.nextEl, 'fs-lyrics-sync-line--off-below', 'fs-lyrics-sync-line--tone-hidden');
    fsSyncApplyState(refs.activeEl, 'fs-lyrics-sync-line--at-next', 'fs-lyrics-sync-line--tone-side');
    fsSyncApplyState(refs.prevEl, 'fs-lyrics-sync-line--at-active', 'fs-lyrics-sync-line--tone-active');
    fsSyncApplyState(refs.incomingEl, 'fs-lyrics-sync-line--at-prev', 'fs-lyrics-sync-line--tone-side');
  }

  // Обновляем шаг до запуска tween, чтобы переход шёл уже с корректным расстоянием.
  fsSyncUpdateDynamicSlot(nodes.root, refs);

  _fsSyncAnimTimer = setTimeout(() => {
    if (animId !== _fsSyncAnimId) return;
    if (isForward) {
      const recycled = refs.prevEl;
      refs.prevEl = refs.activeEl;
      refs.activeEl = refs.nextEl;
      refs.nextEl = refs.incomingEl;
      refs.incomingEl = recycled;
      fsSyncSetLineText(refs.incomingEl, null);
      fsSyncApplyState(refs.incomingEl, 'fs-lyrics-sync-line--off-below', 'fs-lyrics-sync-line--tone-hidden');
    } else if (isBackward) {
      const recycled = refs.nextEl;
      refs.nextEl = refs.activeEl;
      refs.activeEl = refs.prevEl;
      refs.prevEl = refs.incomingEl;
      refs.incomingEl = recycled;
      fsSyncSetLineText(refs.incomingEl, null);
      fsSyncApplyState(refs.incomingEl, 'fs-lyrics-sync-line--off-above', 'fs-lyrics-sync-line--tone-hidden');
    }
    fsSyncRenderStatic(refs, prev, cur, next);
    fsSyncUpdateDynamicSlot(nodes.root, refs);
    _fsSyncIsAnimating = false;
    _fsSyncAnimTimer = null;
  }, FS_LYRICS_ANIM_MS + 34);
}

function tickFullscreenSyncedLyrics() {
  if (!fullscreenActive || !_fsSyncLines?.length) return;
  const track = state.currentTrack;
  if (!track || track.id !== _fsSyncLyricsTrackId) return;

  const time = audio.currentTime || 0;
  const beforeStart = fsSyncIsBeforeStart(_fsSyncLines, time);
  const idx = findSyncedLyricLineIndex(_fsSyncLines, time);
  const container = $('fullscreen-lyrics');

  if (beforeStart) {
    if (_fsSyncBeforeStart && idx === _fsSyncActiveIndex) return;
    _fsSyncBeforeStart = true;
    _fsSyncActiveIndex = idx;
    if (_fsSyncScrollExpanded) {
      fsSyncSyncBeforeStartClass(container, true);
      fsSyncUpdateScrollActive(idx, { beforeStart: true, scroll: false });
      _fsSyncLastRenderedIndex = idx;
      return;
    }
    if (isMobile() && !fsSyncIsMobileLyricsOpen()) return;
    renderFullscreenSyncedLyrics(_fsSyncLines, idx, { beforeStart: true });
    return;
  }

  const wasBeforeStart = _fsSyncBeforeStart;
  _fsSyncBeforeStart = false;
  fsSyncSyncBeforeStartClass(container, false);

  if (!wasBeforeStart && idx === _fsSyncActiveIndex) return;

  _fsSyncActiveIndex = idx;
  if (_fsSyncScrollExpanded) {
    const autoFollow = fsSyncMobileAutoFollowEnabled();
    fsSyncUpdateScrollActive(idx, {
      scroll: autoFollow,
      smooth: autoFollow && !fullscreenPrefersReducedMotion(),
      beforeStart: false
    });
    _fsSyncLastRenderedIndex = idx;
    return;
  }
  if (isMobile() && !fsSyncIsMobileLyricsOpen()) return;
  renderFullscreenSyncedLyrics(_fsSyncLines, idx, { animate: true, beforeStart: false });
}

// Загрузка текста для полноэкранного плеера
async function loadFullscreenLyrics(trackId) {
    const container = $('fullscreen-lyrics');
    container.innerHTML = '';
    container.classList.remove(
      'lyrics-fade-in',
      'fs-lyrics-sync-mode',
      'fs-lyrics-scroll-mode',
      'fs-lyrics-sync--before-start'
    );
    container.scrollTop = 0;
    clearFullscreenSyncedLyricsState();

    if (lyricsCache.has(trackId)) {
        applyFullscreenLyricsPayload(trackId, lyricsCache.get(trackId));
        return;
    }

    container.innerHTML = generateLyricsSkeleton();
    container.scrollTop = 0;
    try {
        const data = await GET(`/api/tracks/${trackId}/lyrics`);
        lyricsCache.set(trackId, data);
        applyFullscreenLyricsPayload(trackId, data);
    } catch {
        clearFullscreenSyncedLyricsState();
        container.innerHTML = '<div class="lyrics-empty">Текста этого трека у нас пока нет, но вы всегда можете его добавить =)</div>';
        container.classList.add('lyrics-fade-in');
        container.scrollTop = 0;
        if (isMobile()) syncMobileFullscreenLyricsUi();
    }
}

// Отрисовка текста (единая версия)
function renderFullscreenLyrics(lyrics) {
  const container = $('fullscreen-lyrics');
  if (!container) return;
  container.classList.remove('fs-lyrics-sync-mode', 'fs-lyrics-scroll-mode');

  if (!lyrics) {
    container.innerHTML = '<div class="lyrics-empty">Текста этого трека у нас пока нет, но вы всегда можете его добавить =)</div>';
    container.classList.add('lyrics-fade-in');
    container.scrollTop = 0;
    if (isMobile()) syncMobileFullscreenLyricsUi();
    return;
  }

  const normalized = normalizeLyrics(lyrics);
  const formatted = formatLyrics(normalized);
  container.innerHTML = `<div class="lyrics-text">${formatted.replace(/\n/g, '<br>')}</div>`;
  container.classList.add('lyrics-fade-in');
  container.scrollTop = 0;
  if (isMobile()) syncMobileFullscreenLyricsUi();
}


// Синхронизация кнопки play/pause ? полноэкранном режиме
function updateFullscreenPlayUI(playing) {
  const playIcon = $('fullscreen-icon-play');
  const pauseIcon = $('fullscreen-icon-pause');
  playIcon.classList.toggle('hidden', playing);
  pauseIcon.classList.toggle('hidden', !playing);
  const btn = $('fullscreen-play-pause');
  if (btn) btn.setAttribute('aria-label', playing ? 'Пауза' : 'Воспроизвести');
}

// Переопределим setPlayUI, чтобы обновлялась и полноэкранная кнопка
const originalSetPlayUI = setPlayUI;
setPlayUI = function(playing) {
  originalSetPlayUI(playing);
  if (fullscreenActive) {
    updateFullscreenPlayUI(playing);
  }
};


function toggleMute() {
  audio.muted = !audio.muted;
  if (!audio.muted && Number($('volume-range')?.value || 0) === 0) {
    audio.muted = true;
  }
  syncMuteUI(); // обновить обе иконки
}

function toggleFullscreenMute() {
  audio.muted = !audio.muted;
  if (!audio.muted && Number($('fullscreen-volume-range')?.value || $('volume-range')?.value || 0) === 0) {
    audio.muted = true;
  }
  syncMuteUI(); // обновить обе иконки
}

function setFullscreenVolume(val) {
  const v = Math.max(0, Math.min(100, Number(val)));
  const volume = v / 100;
  if (v === 0) {
    audio.muted = true;
  } else if (audio.muted) {
    audio.muted = false;
  }
  applyUserVolumeToGraph(volume);
  refreshAutoGainForUserVolume(volume);
  localStorage.setItem('mxm_volume', volume);
  $('volume-range').value = v;
  updateVolumeFill(v);
  syncMuteUI();
  // Не держим фокус на слайдере громкости во fullscreen: Esc должен сразу закрывать плеер.
  const active = document.activeElement;
  if (active && active.id === 'fullscreen-volume-range') {
    active.blur();
  }
}

// Для кнопок shuffle и repeat можно просто вызывать те же функции, они обновят состояние
function toggleFullscreenShuffle() { toggleShuffle(); }
function toggleFullscreenRepeat() { cycleRepeat(); }


// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
const SIDEBAR_COLLAPSED_KEY = 'mxm-sidebar-collapsed';

/** Публичные установщики (положить в uploads/desktop-app на сервере). */
const MXM_DESKTOP_APP_URLS = {
  win: '/uploads/desktop-app/MXM-Music.exe',
  mac: '/uploads/desktop-app/MXM-Music.pkg'
};

function mxmDetectDesktopDownloadOs() {
  try {
    const ua = String(navigator.userAgent || '');
    const plat = String(navigator.platform || '');
    if (/Win(dows|32|64|CE)|Windows/i.test(ua) || /^Win/i.test(plat)) return 'win';
    if (/Mac|Macintosh|Mac OS|Darwin/i.test(ua) || /^Mac/i.test(plat)) return 'mac';
  } catch (_) {}
  return 'win';
}

/** Десктопный браузер на основном сайте (не Electron, не нативное приложение, не узкий viewport). */
function mxmShouldShowDesktopAppCta() {
  if (typeof isMxMDesktopApp === 'function' && isMxMDesktopApp()) return false;
  if (window.mxmDesktop?.isDesktop) return false;
  try {
    if (document.documentElement.classList.contains('mxm-electron-shell')) return false;
    if (document.documentElement.classList.contains('mxm-native-shell')) return false;
    if (window.Capacitor?.isNativePlatform?.()) return false;
  } catch (_) {}
  return window.matchMedia('(min-width: 901px)').matches;
}

function mountSidebarDesktopAppCta() {
  const wrap = document.getElementById('sidebar-desktop-app');
  const link = document.getElementById('sidebar-desktop-app-link');
  const label = document.getElementById('sidebar-desktop-app-label');
  if (!wrap || !link || !label) return;
  if (!mxmShouldShowDesktopAppCta()) {
    wrap.classList.add('hidden');
    wrap.setAttribute('aria-hidden', 'true');
    if (typeof mountSidebarMemorialPlaque === 'function') mountSidebarMemorialPlaque();
    return;
  }
  const os = mxmDetectDesktopDownloadOs();
  const isMac = os === 'mac';
  const href = isMac ? MXM_DESKTOP_APP_URLS.mac : MXM_DESKTOP_APP_URLS.win;
  const fileName = isMac ? 'MXM-Music.pkg' : 'MXM-Music.exe';
  link.href = href;
  link.setAttribute('download', fileName);
  label.textContent = isMac ? 'Скачай MXM на Mac' : 'Скачай MXM на Windows';
  wrap.classList.remove('hidden');
  wrap.setAttribute('aria-hidden', 'false');
  if (typeof mountSidebarMemorialPlaque === 'function') mountSidebarMemorialPlaque();
}
window.mountSidebarDesktopAppCta = mountSidebarDesktopAppCta;

function toggleSidebar() {
  const s = $('sidebar'), ov = $('sidebar-overlay'), app = $('app');
  if (window.innerWidth <= 900) {
    s.classList.toggle('open');
    ov?.classList.toggle('visible', s.classList.contains('open'));
  } else {
    app.classList.toggle('sidebar-collapsed');
    const collapsed = app.classList.contains('sidebar-collapsed');
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : ''); } catch (_) {}
    if (state.view === 'home' && allArtists.length > 0 && !isMxMDesktopApp()) {
      updateHomeArtists(allArtists);
    }
  }
}

function applySidebarCollapsedFromStorage() {
  if (window.innerWidth <= 900) return;
  try {
    const collapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    const app = $('app');
    app.classList.toggle('sidebar-collapsed', collapsed);
    document.body.classList.toggle('sidebar-collapsed', collapsed);
  } catch (_) {}
  if (typeof mountSidebarDesktopAppCta === 'function') mountSidebarDesktopAppCta();
}
function closeSidebar() {
  const s = $('sidebar');
  s.classList.remove('open');
  $('sidebar-overlay')?.classList.remove('visible');
  // Не сбрасываем sidebar-collapsed: на десктопе панель остаётся свёрнутой при переходе по разделам
}

// ─── SKELETON ─────────────────────────────────────────────────────────────────
function skeletonShellOpen(className, inner) {
  return `<div class="mxm-page-skeleton ${className}" aria-hidden="true" aria-busy="true">${inner}</div>`;
}

function skeletonSectionHead() {
  return `<div class="skeleton-section-head">
    <div class="skel skel-sec-title"></div>
    <div class="skel skel-sec-sub"></div>
  </div>`;
}

function skeletonCards(n = 6) {
  return `<div class="cards-grid">${Array(n).fill(`<div class="skeleton-card"><div class="skel skel-cover"></div><div class="skel skel-title"></div><div class="skel skel-sub"></div></div>`).join('')}</div>`;
}

function skeletonHorizontalStrip(n = 5) {
  return `<div class="skeleton-h-strip">${Array(Math.max(3, n))
    .fill('<div class="skel skel-strip-card"></div>')
    .join('')}</div>`;
}

function skeletonArtistsGrid(n = 6) {
  return `<div class="skeleton-artists-grid">${Array(Math.max(4, n))
    .fill('<div class="skel skel-artist-avatar"></div>')
    .join('')}</div>`;
}

function skeletonTrackList(n = 10) {
  const row = `<div class="skeleton-track-row" aria-hidden="true"><div class="skel skel-track-cover"></div><div class="skeleton-track-lines"><div class="skel skel-track-t1"></div><div class="skel skel-track-t2"></div></div></div>`;
  return `<div class="skeleton-track-list">${Array(Math.max(1, n)).fill(row).join('')}</div>`;
}

/** Главная: hero с «яйцом», лента подборок, треки, исполнители, альбомы. */
function skeletonHomePage() {
  return skeletonShellOpen(
    'skeleton-home-page',
    `<div class="skeleton-home-hero">
      <div class="skeleton-home-hero-text">
        <div class="skel skel-home-badge"></div>
        <div class="skel skel-home-title"></div>
        <div class="skel skel-home-title skel-home-title--sm"></div>
        <div class="skel skel-home-sub"></div>
        <div class="skeleton-home-actions">
          <span class="skel skel-home-btn skel-home-btn--lg"></span>
          <span class="skel skel-home-btn"></span>
          <span class="skel skel-home-btn"></span>
        </div>
      </div>
      <div class="skeleton-home-hero-visual">
        <div class="skel skel-home-egg"></div>
      </div>
    </div>
    <section class="skeleton-section-block">
      ${skeletonSectionHead()}
      ${skeletonHorizontalStrip(5)}
    </section>
    <section class="skeleton-section-block">
      ${skeletonSectionHead()}
      ${skeletonTrackList(isDesktopNav() ? 6 : 4)}
    </section>
    <section class="skeleton-section-block">
      ${skeletonSectionHead()}
      ${skeletonArtistsGrid(isDesktopNav() ? 8 : 4)}
    </section>
    <section class="skeleton-section-block">
      ${skeletonSectionHead()}
      ${skeletonCards(isDesktopNav() ? 6 : 4)}
    </section>`
  );
}

/** Обзор: intro, hero-блок, новинки-лента, чарт. */
function skeletonBrowsePage() {
  return skeletonShellOpen(
    'skeleton-browse-page',
    `<div class="skeleton-page-intro">
      <div class="skel skel-line-lg skel-page-title"></div>
      <div class="skel skel-line skel-page-sub"></div>
      <div class="skel skel-home-btn skel-page-cta"></div>
    </div>
    <div class="skeleton-browse-hero">
      <div class="skel skel-browse-cover"></div>
      <div class="skeleton-browse-hero-text">
        <div class="skel skel-hs-type"></div>
        <div class="skel skel-hs-title skel-browse-title"></div>
        <div class="skel skel-hs-sub"></div>
        <div class="skeleton-browse-cta">
          <span class="skel skel-hs-btn"></span>
          <span class="skel skel-hs-btn"></span>
        </div>
      </div>
    </div>
    <section class="skeleton-section-block">
      ${skeletonSectionHead()}
      ${skeletonHorizontalStrip(5)}
    </section>
    <section class="skeleton-section-block">
      ${skeletonSectionHead()}
      ${skeletonTrackList(6)}
    </section>`
  );
}

/** Подборки / collections: заголовок + сетка карточек. */
function skeletonCollectionsPage() {
  return skeletonShellOpen(
    'skeleton-collections-page',
    `<div class="skel skel-line-lg skel-page-title"></div>
    <section class="skeleton-section-block skeleton-section-block--tight">
      ${skeletonCards(8)}
    </section>`
  );
}

/** Медиатека: заголовок, поиск, вкладки, список треков. */
function skeletonLibraryPage() {
  return skeletonShellOpen(
    'skeleton-library-page',
    `<div class="skel skel-line-lg skel-page-title"></div>
    <div class="skel skel-line skel-page-sub"></div>
    <div class="skel skel-lib-search"></div>
    <div class="skeleton-lib-tabs">
      ${Array(4).fill('<span class="skel skel-lib-tab"></span>').join('')}
    </div>
    <section class="skeleton-section-block">
      ${skeletonSectionHead()}
      ${skeletonTrackList(8)}
    </section>`
  );
}

/** Ремиксы / мешапы: широкий hero без обложки + треки. */
function skeletonTagHeroPage(trackRows = 8) {
  return skeletonShellOpen(
    'skeleton-tag-hero-page',
    `<div class="skeleton-tag-hero">
      <div class="skeleton-tag-hero-inner">
        <div class="skel skel-hs-type"></div>
        <div class="skel skel-hs-title"></div>
        <div class="skel skel-hs-sub skel-tag-sub"></div>
      </div>
    </div>
    ${skeletonTrackList(trackRows)}`
  );
}

/** Скелет как у страниц с hero (альбом, плейлист, трек, подборка) — только на широком экране. */
function skeletonPageHeroShell(trackRows = 10) {
  return `<div class="page-hero-skeleton-shell profile-page-shell" aria-hidden="true">
    <div class="page-hero profile-page-hero page-hero-skeleton-hero">
      <div class="page-hero-skeleton-back skel"></div>
      <div class="page-hero-skeleton-body">
        <div class="page-hero-skeleton-cover skel"></div>
        <div class="page-hero-skeleton-lines">
          <div class="skel skel-hs-type"></div>
          <div class="skel skel-hs-title"></div>
          <div class="skel skel-hs-sub"></div>
          <div class="page-hero-skeleton-actions">
            <span class="skel skel-hs-btn"></span>
            <span class="skel skel-hs-btn skel-hs-btn--sq"></span>
            <span class="skel skel-hs-btn skel-hs-btn--sq"></span>
          </div>
        </div>
      </div>
    </div>
    ${skeletonTrackList(trackRows)}
  </div>`;
}

function skeletonSearchPageShell() {
  return `<div class="search-page-inner search-page-skeleton" aria-hidden="true">
    <div class="search-bar search-bar--page skel skel-search-bar"></div>
    <div class="search-skeleton-hint skel"></div>
    <div class="skel skel-line-lg skel-search-section-title"></div>
    ${skeletonTrackList(4)}
    <div class="skel skel-line-lg skel-search-section-title"></div>
    ${skeletonCards(4)}
  </div>`;
}

function getViewLoadingSkeleton(view) {
  const desk = isDesktopNav();
  switch (view) {
    case 'home':
      return skeletonHomePage();
    case 'browse':
      return skeletonBrowsePage();
    case 'collections':
      return skeletonCollectionsPage();
    case 'library':
    case 'my-tracks':
    case 'my-listen-history':
      return desk ? skeletonLibraryPage() : skeletonTrackList(10);
    case 'remix':
    case 'mashup':
      return desk ? skeletonTagHeroPage(10) : skeletonTrackList(10);
    case 'album':
    case 'artist-tracks':
    case 'user-playlist':
    case 'track':
    case 'collection':
      return desk ? skeletonPageHeroShell(10) : skeletonTrackList(10);
    case 'artist':
      return desk ? skeletonPageHeroShell(6) : skeletonTrackList(6);
    case 'search':
      return desk ? skeletonSearchPageShell() : loadingSpinner();
    case 'sync':
      return `<div class="sync-page-skeleton sync-step-enter" aria-hidden="true">
        <div class="skel skel-line-lg skel-page-title" style="max-width:220px"></div>
        <div class="skel skel-line" style="max-width:280px;margin-top:10px"></div>
        <div class="skel" style="height:120px;margin-top:24px;border-radius:12px"></div>
        <div class="skel" style="height:200px;margin-top:14px;border-radius:12px"></div>
      </div>`;
    case 'oliver-memorial':
      return `<div class="oliver-memorial-skeleton sync-step-enter" aria-hidden="true">
        <div class="skel" style="width:180px;height:220px;border-radius:24px;margin:0 auto"></div>
        <div class="skel skel-line-lg" style="max-width:260px;margin:24px auto 0"></div>
        <div class="skel skel-line" style="max-width:320px;margin:12px auto 0"></div>
        <div class="skel" style="height:180px;margin:28px auto 0;max-width:640px;border-radius:16px"></div>
      </div>`;
    case 'profile':
      return desk
        ? `<div class="profile-page-skeleton profile-page-skeleton--desktop" aria-hidden="true">
            <div class="page-hero profile-page-hero profile-page-skeleton-hero">
              <div class="profile-page-skeleton-hero-inner">
                <div class="skel skel-profile-avatar"></div>
                <div class="profile-page-skeleton-hero-text">
                  <div class="skel skel-line-lg"></div>
                  <div class="skel skel-line"></div>
                </div>
              </div>
            </div>
            ${skeletonCards(4)}
          </div>`
        : `<div class="profile-page-skeleton"><div class="skel skel-profile-avatar"></div><div class="skel skel-line-lg"></div><div class="skel skel-line"></div>${skeletonCards(4)}</div>`;
    case 'profile-friends':
      return desk
        ? skeletonPageHeroShell(8)
        : `<div class="profile-page-skeleton"><div class="skel skel-line-lg"></div><div class="skel skel-line"></div><div class="skeleton-track-list">${Array(6).fill('<div class="skeleton-track-row" aria-hidden="true"><div class="skel skel-track-cover"></div><div class="skeleton-track-lines"><div class="skel skel-track-t1"></div></div></div>').join('')}</div></div>`;
    case 'messages':
      return `<div class="mxm-chat-skeleton"><div class="skel skel-line-lg"></div><div class="mxm-chat-skel-cols"><div class="skel skel-chat-col"></div><div class="skel skel-chat-col skel-chat-col-wide"></div></div></div>`;
    case 'upload':
      return loadingSpinner();
    case 'reset-password':
      return skeletonCards(4);
    default:
      return loadingSpinner();
  }
}

function loadingSpinner() {
  return `<div class="page-loading"><div class="spinner"></div></div>`;
}

function generateLyricsSkeleton() {
    return `
    <div class="lyrics-skeleton">
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
    </div>`;
}

function playlistCard(playlist) {
  const grad = playlist.coverGradient || pickGrad(playlist.id);
  const bg = playlist.coverUrl
    ? `<img src="${esc(resolveMediaUrl(playlist.coverUrl))}" alt="" loading="lazy" decoding="async" onerror="mxmImgRetryOnError(this)">`
    : `<div class="featured-cover-fallback" style="background:linear-gradient(${grad})"></div>`;
  return `
    <div class="featured-card playlist-card" data-nav="collection" data-nav-id="${playlist.id}" onclick="handleDataNavClick(event)" title="${esc(playlist.title)}">
      <div class="featured-bg">${bg}</div>
      <div class="featured-overlay"></div>
      <div class="featured-play-btn" onclick="event.stopPropagation();playCollectionById('${playlist.id}')" onauxclick="event.stopPropagation()">
        <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
      </div>
    </div>`;
}

function userPlaylistSquareCard(p, { mine = false } = {}) {
  const grad = p.coverGradient || pickGrad(p.id);
  const bg = p.coverUrl
    ? `<img src="${esc(resolveMediaUrl(p.coverUrl))}" alt="" loading="lazy" decoding="async" onerror="mxmImgRetryOnError(this)">`
    : `<div class="featured-cover-fallback" style="background:linear-gradient(${grad})"></div>`;
  const owner = p.ownerUsername ? `@${esc(p.ownerUsername)}` : '';
  const likesCount = Number(p.likesCount) || 0;
  const commentsCount = Number(p.commentsCount) || 0;
  const socialStatsHtml = !mine
    ? `<span class="upl-stats">❤ ${formatSocialCount(likesCount)} · 💬 ${formatSocialCount(commentsCount)}</span>`
    : '';

  return `
    <div class="featured-card playlist-card upl-card" data-nav="user-playlist" data-nav-id="${p.id}" onclick="handleDataNavClick(event)" title="${esc(p.title)}">
      <div class="featured-bg">${bg}</div>
      <div class="featured-overlay"></div>
      ${mine ? `
        <button type="button" class="upl-more-btn" onclick="event.stopPropagation();toggleUserPlaylistCardMenu('${p.id}', this)" aria-label="Меню">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <circle cx="12" cy="5" r="2" fill="currentColor"/>
            <circle cx="12" cy="12" r="2" fill="currentColor"/>
            <circle cx="12" cy="19" r="2" fill="currentColor"/>
          </svg>
        </button>
        <div class="upl-menu hidden" id="upl-menu-${p.id}">
          <button type="button" onclick="event.stopPropagation();copyUserPlaylistShareLink('${p.id}');closeAllUserPlaylistMenus()"><span class="upl-menu-ic" aria-hidden="true">${MXM_SHARE_ICON_SVG.replace('<svg ', '<svg width="16" height="16" ')}</span>Поделиться</button>
          <button type="button" onclick="event.stopPropagation();toggleUserPlaylistShare('${p.id}', ${p.isShared ? 'true' : 'false'});closeAllUserPlaylistMenus()">
            ${p.isShared ? 'Снять шаринг' : 'Расшарить'}
          </button>
          <button type="button" onclick="event.stopPropagation();openUserPlaylistCoverUploadModal('${p.id}')">Сменить обложку</button>
          <button type="button" class="danger" onclick="event.stopPropagation();deleteUserPlaylist('${p.id}');closeAllUserPlaylistMenus()">Удалить</button>
        </div>
      ` : ''}
      <div class="upl-meta">
        <div class="upl-title">${esc(p.title)}</div>
        <div class="upl-owner-line">
          ${owner ? `<span class="upl-owner">${owner}</span>` : '<span class="upl-owner">Плейлист</span>'}
          ${socialStatsHtml}
        </div>
      </div>
    </div>`;
}

function updateHomePlaylistsStrip() {
  const strip = $('home-playlists-strip');
  if (!strip) return;
  strip.innerHTML = homePlaylistsAll.map((p) => playlistCard(p)).join('');
}

function refreshHomePopularAlbumsGrid() {
  const el = $('home-popular-albums-grid');
  if (!el || !homePopularAlbumsAll.length) return;
  const mobile = window.matchMedia('(max-width: 900px)').matches;
  // Мобильные: не больше 6 альбомов; десктоп: все из ответа /api/home
  const n = mobile ? Math.min(6, homePopularAlbumsAll.length) : homePopularAlbumsAll.length;
  const slice = homePopularAlbumsAll.slice(0, n);
  el.innerHTML = slice.map((a) => albumCard(a)).join('');
}


function shareItem(type, id, name = '') {
  const sid = String(id || '').trim();
  if (!sid) return;
  if (type === 'track') {
    openShareModal(buildShareDeepLink('track', sid), 'track', sid, name);
    return;
  }
  if (type === 'album' || type === 'collection' || type === 'artist') {
    void copyUrlToClipboardOrShare(buildShareDeepLink(type, sid), {
      shareTitle: String(name || '').trim() || 'MXM'
    });
    return;
  }
  openShareModal(buildShareDeepLink(type, sid), type, sid, name);
}

function openShareModal(url, type, id, name) {
  let el = $('mxm-share-modal');
  if (!el) {
    el = document.createElement('div');
    el.id = 'mxm-share-modal';
    el.className = 'mxm-modal-overlay hidden';
    el.innerHTML = `
      <div class="mxm-modal-box mxm-share-modal-box" role="dialog" aria-modal="true" aria-labelledby="mxm-share-title">
        <div class="mxm-modal-head mxm-share-modal-head">
          <span class="mxm-share-modal-title-inner">
            <span class="mxm-share-modal-title-ic" aria-hidden="true">${MXM_SHARE_ICON_SVG.replace('<svg ', '<svg width="22" height="22" ')}</span>
            <span id="mxm-share-title" class="mxm-modal-title">Поделиться</span>
          </span>
          <button type="button" class="mxm-modal-close" onclick="closeShareModal()" aria-label="Закрыть">×</button>
        </div>
        <p class="mxm-share-url" id="mxm-share-url-text"></p>
        <div class="mxm-share-actions">
          <button type="button" class="btn-primary" id="mxm-share-copy-btn">Копировать ссылку</button>
          <button type="button" class="btn-ghost" id="mxm-share-friend-btn">Отправить другу…</button>
        </div>
        <div id="mxm-share-friend-panel" class="mxm-share-friend-panel hidden"></div>
      </div>`;
    document.body.appendChild(el);
    el.addEventListener('click', (e) => {
      if (e.target === el) closeShareModal();
    });
    $('mxm-share-copy-btn').addEventListener('click', () => {
      const u = el.dataset.shareUrl || '';
      void copyUrlToClipboardOrShare(u, { shareTitle: 'MXM' });
    });
    $('mxm-share-friend-btn').addEventListener('click', () => mxmToggleShareFriendPanel());
  }
  el.dataset.shareUrl = url;
  el.dataset.shareType = type;
  el.dataset.shareId = id;
  el.dataset.shareName = name || '';
  const ut = $('mxm-share-url-text');
  if (ut) ut.textContent = url;
  const fp = $('mxm-share-friend-panel');
  if (fp) {
    fp.classList.add('hidden');
    fp.innerHTML = '';
  }
  const friendBtn = $('mxm-share-friend-btn');
  if (friendBtn) friendBtn.classList.toggle('hidden', type !== 'track');
  el.classList.remove('hidden');
}

function closeShareModal() {
  $('mxm-share-modal')?.classList.add('hidden');
}

async function mxmToggleShareFriendPanel() {
  const fp = $('mxm-share-friend-panel');
  if (!fp) return;
  if (!state.user) {
    openAuthModal('login');
    return;
  }
  if (!fp.classList.contains('hidden')) {
    fp.classList.add('hidden');
    fp.innerHTML = '';
    return;
  }
  try {
    const { friends } = await GET('/api/me/friends');
    if (!friends.length) {
      fp.innerHTML = '<p class="mxm-share-friend-empty">Нет друзей в списке</p>';
      fp.classList.remove('hidden');
      return;
    }
    fp.innerHTML =
      '<p class="mxm-share-friend-hint">Выберите друга</p>' +
      friends
        .map(
          (f) =>
            `<button type="button" class="mxm-share-friend-row" data-friend-id="${esc(userProfileRef(f))}">
            <span class="mxm-share-friend-name">${esc(f.username)}</span>
          </button>`
        )
        .join('');
    fp.querySelectorAll('.mxm-share-friend-row').forEach((btn) => {
      btn.addEventListener('click', () => {
        const fid = btn.getAttribute('data-friend-id');
        const modal = $('mxm-share-modal');
        const type = modal?.dataset.shareType || 'track';
        const sid = modal?.dataset.shareId || '';
        const sname = modal?.dataset.shareName || '';
        closeShareModal();
        mxmNavigateSendShareToFriend(fid, type, sid, sname);
      });
    });
    fp.classList.remove('hidden');
  } catch (e) {
    toast(e.message || 'Не удалось загрузить друзей', 'error');
  }
}

function mxmNavigateSendShareToFriend(friendId, shareType, shareId, shareName) {
  try {
    sessionStorage.setItem(
      'mxm_pending_chat_share',
      JSON.stringify({ shareType, shareId, shareName: String(shareName || '') })
    );
  } catch (_) {}
  navigate('messages', { id: friendId });
}

function shareCurrentTrack() {
  closePlayerMenu();
  if (state.currentTrack) {
    shareItem('track', state.currentTrack.id, state.currentTrack.title);
  }
}

// ─── NAVIGATION (с поддержкой хэша) ──────────────────────────────────────────

/** Убирает #desktop-site-footer из #content до innerHTML, иначе узел уничтожается. */
function parkDesktopSiteFooter() {
  const footer = $('desktop-site-footer');
  const parking = $('desktop-site-footer-parking');
  if (!footer || !parking) return;
  if ($('content')?.contains(footer)) {
    parking.appendChild(footer);
  }
}

/** На главной — сразу под блоком «Популярные альбомы» (в потоке скролла), иначе в конец. */
function attachDesktopSiteFooterInHome() {
  const footer = $('desktop-site-footer');
  const content = $('content');
  if (!footer || !content) return;
  const anchor = content.querySelector('.section-home-popular-albums');
  if (anchor) {
    anchor.after(footer);
  } else {
    content.appendChild(footer);
  }
}

/** Подвал сайта — только на главной (десктоп). */
function updateDesktopSiteFooterVisibility() {
  const el = $('desktop-site-footer');
  if (!el) return;
  const show = state.view === 'home';
  el.classList.toggle('desktop-site-footer--visible', show);
  el.toggleAttribute('hidden', !show);
}

// Парсит текущий хэш и возвращает { view, params }
/** Вход по /sync → единый маршрут SPA `/#sync` (без отдельного pathname). */
function mxmNormalizeSyncPath() {
  try {
    const p = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
    if (p !== '/sync') return;
    history.replaceState(null, '', `/${location.search || ''}#sync`.replace(/\/+/g, '/'));
  } catch (_) {}
}

function parseHash() {
  const hash = window.location.hash.slice(1) || 'home';
  const parts = hash.split('/');
  const view = parts[0];
  const params = {};
  if (parts[1]) params.id = parts[1];
  return { view, params };
}

// Загружает представление (без изменения хэша)
async function loadView(view, params) {
  if (homeRandomDesktopCloseTimer != null) {
    clearTimeout(homeRandomDesktopCloseTimer);
    homeRandomDesktopCloseTimer = null;
  }
  document.querySelectorAll('.home-random-portal').forEach((el) => el.remove());
  $('home-random-settings-btn')?.setAttribute('aria-expanded', 'false');

  if (view === 'user-playlists') {
    try {
      history.replaceState(null, '', `${window.location.pathname}${window.location.search}#/browse`);
    } catch {}
    view = 'browse';
  }

  const token = ++activeViewToken;
  perfViewName = view;
  perfViewStartMs = performance.now();
  if (view !== 'messages') {
    mxmChatStopPoll();
    mxmChatStopMiniPlayer();
  }
  state.view = view;
  if (state.listenSession?.status !== 'active') state.queuePlayingFrom = null;
  updateQueueSourceUI();
  closeSidebar();
  closeUserMenu();
  // Close any portaled dropdowns/menus before we re-render content
  try {
    closeAllUserPlaylistMenus?.();
    closeAllTrackMoreMenus?.();
  } catch {}

    if (homeArtistsTimer) {
    clearInterval(homeArtistsTimer);
    homeArtistsTimer = null;
  }
  if (view !== 'home' && isMxMDesktopApp()) resetDesktopHomeArtistsPin();

  document.querySelectorAll('.nav-item, .mobile-nav-item').forEach((el) => {
    el.classList.toggle('active', el.dataset.view === view);
  });

  const content = $('content');
  content.classList.remove('page-exit', 'page-enter');
  content.classList.add('page-exit');
  const pageExitWaitMs =
    typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 901px)').matches
      ? 128
      : 118;
  setTimeout(async () => {
    if (token !== activeViewToken) return;
    content.classList.remove(
      'content-home-view',
      'content-upload-view',
      'content-messages-view',
      'content-search-view',
      'content-browse-view',
      'content-artist-view',
      'content-sync-view',
      'content-memorial-view'
    );
    if (view !== 'sync' && typeof stopYandexSyncPoll === 'function') stopYandexSyncPoll();
    if (view !== 'oliver-memorial' && typeof stopOliverMemorialVideo === 'function') {
      stopOliverMemorialVideo();
    }
    document.body.classList.toggle('mxm-sync-route', view === 'sync');
    document.body.classList.toggle('mxm-memorial-route', view === 'oliver-memorial');
    if (view === 'home') content.classList.add('content-home-view');
    if (view === 'upload') content.classList.add('content-upload-view');
    if (view === 'messages') content.classList.add('content-messages-view');
    if (view === 'search') content.classList.add('content-search-view');
    if (view === 'browse') content.classList.add('content-browse-view');
    if (view === 'artist' || view === 'artist-tracks') content.classList.add('content-artist-view');
    if (view === 'sync') content.classList.add('content-sync-view');
    if (view === 'oliver-memorial') content.classList.add('content-memorial-view');
    const leavingMessenger =
      document.body.classList.contains('mxm-chat-active') && view !== 'messages';
    if (leavingMessenger && typeof window.mxmChatStopVoicePlayback === 'function') window.mxmChatStopVoicePlayback();
    document.body.classList.toggle('mxm-chat-active', view === 'messages');
    if (view !== 'messages') document.body.classList.remove('mxm-chat-thread-open');
    if (leavingMessenger) revealMainPlayerLeavingMessenger();
    content.classList.remove('page-exit');
    parkDesktopSiteFooter();
    content.innerHTML = getViewLoadingSkeleton(view);
    content.classList.add('page-enter');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        content.classList.remove('page-enter');
      });
    });
    try {
      switch (view) {
        case 'home':    await renderHome();           break;
        case 'browse':  await renderBrowse();         break;
        case 'search':  await renderSearch();         break;
        case 'library': await renderLibrary();        break;
        case 'my-tracks': await renderMyTracks();     break;
        case 'my-listen-history': await renderMyListenHistory(); break;
        case 'upload':  renderUpload();               break;
        case 'collection': await renderCollection(params.id); break;
        case 'collections': await renderCollections(); break;
        case 'album':   await renderAlbum(params.id); break;
        case 'artist':  await renderArtist(params.id);break;
        case 'artist-tracks': await renderArtistTracks(params.id); break;
        case 'track':   await renderTrack(params.id); break;
        case 'remix':   await renderRemix(); break;
        case 'mashup':  await renderMashup(); break;
        case 'user-playlist': await renderUserPlaylist(params.id); break;
        case 'profile': await renderUserProfile(params.id); break;
        case 'profile-friends': await renderProfileFriendsPage(params.id); break;
        case 'messages': await renderMessages(params.id); break;
        case 'sync': await renderSyncPage(); break;
        case 'oliver-memorial': renderOliverMemorialPage(); break;
        case 'reset-password': await renderResetPassword(params.id); break;
        default:
          content.classList.add('content-home-view');
          await renderHome();
      }
      if (token !== activeViewToken) return;
    } catch (err) {
      if (token !== activeViewToken) return;
      parkDesktopSiteFooter();
      content.innerHTML = `<div class="page-loading"><p style="color:var(--danger);font-size:15px">Ошибка загрузки: ${esc(err.message)}</p></div>`;
    }
    updatePlayingRows();
    refreshFavBtns();
    updateDesktopSiteFooterVisibility();
    markPerf('loadView', performance.now() - perfViewStartMs, { view: perfViewName });
  }, pageExitWaitMs);
}
async function renderTrack(id) {
  const content = $('content');
  const track = await GET(`/api/tracks/${id}`);
  const grad = track.coverGradient || pickGrad(track.id);
  const liked = isFavT(track.id);

  const artistHtml = renderTrackArtistsHtml(track, { className: 'hero-artist' });

  const uploaderHero = track.uploaderArtist
    ? htmlUploaderProfileLinkInline(track, {
        className: 'track-uploader-profile-btn track-uploader-profile-btn--hero',
        iconSize: 13
      })
    : '';

  const heroYear =
    track.year != null &&
    Number.isFinite(Number(track.year)) &&
    Number(track.year) >= 1900 &&
    Number(track.year) <= 2100
      ? ` · ${Number(track.year)}`
      : '';

  const albumHtml = track.albumId
    ? `<span class="hero-album" data-nav="album" data-nav-id="${track.albumId}" onclick="handleDataNavClick(event)">${esc(track.album)}</span>`
    : (track.album ? `<span>${esc(track.album)}</span>` : '');

  content.innerHTML = `
    <div class="profile-page-shell track-page-shell">
      <div class="page-hero profile-page-hero" style="--hero-grad:linear-gradient(${grad})">
        <button class="nav-back-btn" onclick="goBack()" title="Назад">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
          </svg>
        </button>
        <div class="hero-bg"></div>
        <div class="hero-content">
          <div class="hero-cover">
            ${coverImg(track.coverUrl, grad)}
          </div>
          <div class="hero-info">
            <div class="hero-type">Трек</div>
            <h1 class="hero-title">${esc(track.title)}</h1>
            <div class="hero-sub hero-sub--with-uploader">
              ${artistHtml}${uploaderHero}${heroYear}${track.album ? ` · ${albumHtml}` : ''}
            </div>
            <div class="hero-actions hero-actions--media-row">
              <button class="hero-play-btn" onclick="playTrackById('${track.id}')" title="Слушать" aria-label="Слушать">
                <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              </button>
              <button class="hero-fav-btn ${liked ? 'liked' : ''}" data-fav-track="${track.id}"
                onclick="toggleFavTrack('${track.id}',event)" title="В избранное" aria-label="В избранное">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" ${liked ? 'fill="currentColor"' : 'fill="none"'}/></svg>
              </button>
              <button class="hero-share-btn" type="button" onclick="shareItem('track', '${track.id}', '${esc(track.title)}')" title="Поделиться" aria-label="Поделиться">
                ${MXM_SHARE_ICON_SVG}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="track-list track-page-track-list" style="margin-top:8px">
      <div class="track-row" data-track-id="${track.id}" onclick="playTrackById('${track.id}')">
        <div class="track-cover-sm">${coverImg(track.coverUrl, grad)}</div>
        <div class="track-info">
          ${htmlTrackListTagRow(track)}
          <div class="track-title">${esc(track.title)}</div>
          <div class="track-meta">${renderTrackMetaLineHtml(track, { year: false })} · ${fmtTime(track.duration)}</div>
        </div>
      </div>
    </div>
  `;
  setQueuePlayingFromLabel(track.title);
}

async function playTrackById(id) {
  try {
    const track = await GET(`/api/tracks/${id}`);
    setQueueState([track], 0, { lockPrevAtStart: true });
    playTrack(track);
  } catch {
    toast('Не удалось воспроизвести трек', 'error');
  }
}

/** Воспроизведение трека из поиска: без очереди из выдачи; после окончания — похожие/продолжение по треку. */
async function playSearchResultTrack(id) {
  try {
    const track = await GET(`/api/tracks/${id}`);
    if (!track?.id) throw new Error('not found');
    setQueueState([track], 0, { lockPrevAtStart: true });
    setQueuePlayingFromLabel('Поиск');
    playTrack(track, { recordHistory: !!state.user });
  } catch {
    toast('Не удалось воспроизвести трек', 'error');
  }
}
window.playSearchResultTrack = playSearchResultTrack;
function buildHash(view, params = {}) {
  let hash = view;
  if (params.id) hash += '/' + params.id;
  return hash;
}

/** Абсолютная ссылка для шаринга: боты должны видеть серверные OG-роуты. */
function buildShareDeepLink(view, id) {
  const sid = encodeURIComponent(String(id || '').trim());
  const routeByView = {
    track: `/track/${sid}`,
    album: `/album/${sid}`,
    artist: `/artist/${sid}`,
    collection: `/collection/${sid}`,
    profile: `/user/${sid}`,
    user: `/user/${sid}`
  };
  const canonicalPath = routeByView[String(view || '').trim()];
  if (canonicalPath) return `${getPublicSiteBase()}${canonicalPath}`;

  const h = buildHash(view, { id: String(id) });
  try {
    const p = window.location.pathname || '/';
    const s = window.location.search || '';
    return `${getPublicSiteBase()}${p}${s}#${h}`;
  } catch {
    return `${getPublicSiteBase()}/#${h}`;
  }
}

/** Копирование URL; при allowNavigatorShare и неудаче буфера на мобилке — системный «Поделиться». */
async function copyUrlToClipboardOrShare(url, options = {}) {
  const u = String(url || '').trim();
  const {
    successToast = true,
    failToast = true,
    shareTitle = 'MXM',
    allowNavigatorShare = true
  } = options;
  if (!u) {
    if (failToast) toast('Не удалось скопировать ссылку', 'error');
    return false;
  }
  if (await copyPlainTextToClipboard(u)) {
    if (successToast) toast('Ссылка скопирована', 'success');
    return true;
  }
  if (allowNavigatorShare) {
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function' && isMobile()) {
        await navigator.share({ url: u, title: shareTitle });
        return true;
      }
    } catch (e) {
      if (e && e.name === 'AbortError') return true;
    }
  }
  if (failToast) toast('Не удалось скопировать ссылку', 'error');
  return false;
}

function navigate(view, params = {}) {
  location.hash = buildHash(view, params);
}

function navigateToUserProfile(userIdOrRef) {
  const id = String(userIdOrRef || '').trim();
  if (!id) return;
  navigate('profile', { id });
}

function openSameAppInNewTab(view, params = {}) {
  const hash = buildHash(view, params);
  const url = shouldUseProductionMediaHost()
    ? `${getPublicSiteBase()}/#${hash}`
    : `${location.pathname}${location.search}#${hash}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Прямая ссылка на профиль (для target=_blank у друзей). */
function profileFriendProfileUrl(userRef) {
  const hash = buildHash('profile', { id: userRef });
  return shouldUseProductionMediaHost()
    ? `${getPublicSiteBase()}/#${hash}`
    : `${location.pathname}${location.search}#${hash}`;
}

function profileFriendChipsHtml(friends) {
  const arr = Array.isArray(friends) ? friends : [];
  if (!arr.length) return '';
  return arr
    .map(
      (u) =>
        `<a href="${esc(profileFriendProfileUrl(userProfileRef(u)))}" target="_blank" rel="noopener noreferrer" class="profile-friend-chip">${esc(u.username)}</a>`
    )
    .join('');
}

function profileFriendListAvatarHtml(u) {
  const name = String(u?.username || '').trim();
  const letter = (name.charAt(0) || '?').toUpperCase();
  const raw = u?.avatarUrl ? resolveMediaUrl(u.avatarUrl) : '';
  if (raw) {
    return `<span class="profile-friends-list-av profile-friends-list-av--img"><img src="${esc(raw)}" alt="" loading="lazy" decoding="async" referrerpolicy="no-referrer"></span>`;
  }
  return `<span class="profile-friends-list-av profile-friends-list-av--letter">${esc(letter)}</span>`;
}

let profileFriendMenuOutsideBound = false;

function closeProfileFriendMoreMenus() {
  document.querySelectorAll('.profile-friend-more-menu').forEach((m) => m.classList.add('hidden'));
  document.querySelectorAll('.profile-friend-more-btn').forEach((b) => b.setAttribute('aria-expanded', 'false'));
}

function ensureProfileFriendMenuOutsideClose() {
  if (profileFriendMenuOutsideBound) return;
  profileFriendMenuOutsideBound = true;
  document.addEventListener('click', (e) => {
    if (e.target.closest('.profile-friend-row-actions')) return;
    closeProfileFriendMoreMenus();
  });
}

function toggleProfileFriendMoreMenu(btn, ev) {
  if (ev) {
    ev.preventDefault();
    ev.stopPropagation();
  }
  if (!btn) return;
  ensureProfileFriendMenuOutsideClose();
  const wrap = btn.closest('.profile-friend-row-actions');
  const menu = wrap?.querySelector('.profile-friend-more-menu');
  if (!menu) return;
  const wasOpen = !menu.classList.contains('hidden');
  closeProfileFriendMoreMenus();
  if (!wasOpen) {
    menu.classList.remove('hidden');
    btn.setAttribute('aria-expanded', 'true');
  }
}

function profileFriendMoreMenuDelete(ev, userId, username) {
  if (ev) {
    ev.preventDefault();
    ev.stopPropagation();
  }
  closeProfileFriendMoreMenus();
  profileOpenRemoveFriendDialog(userId, username);
}

document.addEventListener(
  'scroll',
  () => {
    if (document.querySelector('.profile-friend-more-menu:not(.hidden)')) closeProfileFriendMoreMenus();
  },
  true
);

/** Список друзей на экране «Друзья»: строки с аватаром и ником. */
function profileFriendsListRowsHtml(friends, { canRemove = false } = {}) {
  const arr = Array.isArray(friends) ? friends : [];
  if (!arr.length) return '';
  return `<ul class="profile-friends-list-rows" role="list">
${arr
  .map((u) => {
    const hash = buildHash('profile', { id: userProfileRef(u) });
    const removeBtn = canRemove
      ? `<div class="profile-friend-row-actions">
      <button type="button" class="profile-friend-more-btn" title="Действия" aria-label="Действия" aria-haspopup="true" aria-expanded="false" onclick="event.preventDefault(); event.stopPropagation(); toggleProfileFriendMoreMenu(this, event)">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="2"></circle>
          <circle cx="12" cy="12" r="2"></circle>
          <circle cx="12" cy="19" r="2"></circle>
        </svg>
      </button>
      <div class="profile-friend-more-menu hidden" role="menu">
        <button type="button" class="profile-friend-more-item profile-friend-more-item--danger" role="menuitem" onclick="profileFriendMoreMenuDelete(event, '${profileJsId(u.id)}', '${profileJsId(u.username)}')">Удалить</button>
      </div>
    </div>`
      : '';
    return `    <li class="profile-friends-list-item${canRemove ? ' profile-friends-list-item--with-remove' : ''}">
      <div class="profile-friends-list-row-inner">
      <a class="profile-friends-list-link" href="#${hash}" data-nav="profile" data-nav-id="${esc(userProfileRef(u))}" onclick="handleDataNavClick(event)">
        ${profileFriendListAvatarHtml(u)}
        <span class="profile-friends-list-name">${esc(u.username)}</span>
      </a>
      ${removeBtn}
      </div>
    </li>`;
  })
  .join('\n')}
  </ul>`;
}

function handleDataNavClick(event) {
  if (event.ctrlKey || event.metaKey) return;
  const el = event.currentTarget;
  const view = el.dataset.nav;
  const id = el.dataset.navId;
  navigate(view, id ? { id } : {});
}

function initModifierNavShortcuts() {
  document.addEventListener(
    'click',
    (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const el = e.target.closest('[data-nav]');
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      openSameAppInNewTab(el.dataset.nav, el.dataset.navId ? { id: el.dataset.navId } : {});
    },
    true
  );
  document.addEventListener(
    'auxclick',
    (e) => {
      if (e.button !== 1) return;
      const el = e.target.closest('[data-nav]');
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      openSameAppInNewTab(el.dataset.nav, el.dataset.navId ? { id: el.dataset.navId } : {});
    },
    true
  );
}

function parseHashFromHref(href) {
  try {
    const h = new URL(href || '', 'http://_').hash.replace(/^#/, '') || 'home';
    const parts = h.split('/');
    const view = parts[0] || 'home';
    const params = {};
    if (parts[1]) params.id = parts[1];
    return { view, params };
  } catch {
    return { view: 'home', params: {} };
  }
}

// Слушаем изменение хэша и загружаем соответствующее представление
window.addEventListener('hashchange', (event) => {
  const oldHash = (() => {
    try {
      const h = new URL(event.oldURL).hash.replace(/^#/, '');
      return h || 'home';
    } catch {
      return 'home';
    }
  })();
  const newHash = (location.hash || '#home').replace(/^#/, '') || 'home';
  const { view, params } = parseHash();
  const oldNav = parseHashFromHref(event.oldURL || '');
  if (
    view === 'messages' &&
    oldNav.view === 'messages' &&
    document.querySelector('.mxm-chat-app')
  ) {
    if (state.token) mxmChatConnectWs();
    if (params.id) {
      void mxmChatHandleHashPeerChange(String(params.id).trim());
    } else {
      _mxmChatActivePeerId = null;
      _mxmChatPeerPublicRef = null;
      mxmChatResetThreadUi();
    }
    if (!suppressNavStackPush && oldHash !== newHash) {
      navHistoryStack.push(oldHash);
    }
    suppressNavStackPush = false;
    return;
  }
  if (!suppressNavStackPush && oldHash !== newHash) {
    navHistoryStack.push(oldHash);
  }
  suppressNavStackPush = false;
  loadView(view, params);
});


// Функция для инициализации Web Audio графа
function initWebAudio() {
  if (audioCtx) return; // Уже инициализирован
  if (shouldUseNativeBackgroundAudioPath()) {
    const savedVolume = localStorage.getItem('mxm_volume');
    if (savedVolume) audio.volume = parseFloat(savedVolume);
    return;
  }

  try {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    source = audioCtx.createMediaElementSource(audio);

    // Выравнивание по треку (из кэша) + компрессор как мягкий лимитер; ползунок — только gainNode ниже.
    autoGainNode = audioCtx.createGain();
    autoGainNode.gain.value = 1;

    compressor = audioCtx.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-22, audioCtx.currentTime);
    compressor.knee.setValueAtTime(18, audioCtx.currentTime);
    compressor.ratio.setValueAtTime(5.5, audioCtx.currentTime);
    compressor.attack.setValueAtTime(0.012, audioCtx.currentTime);
    compressor.release.setValueAtTime(0.28, audioCtx.currentTime);

    analyserNode = audioCtx.createAnalyser();
    analyserNode.fftSize = document.documentElement.classList.contains('perf-low') ? 1024 : 2048;

    // Узел для пользовательской громкости (соответствует вашему ползунку)
    gainNode = audioCtx.createGain();
    // Берём сохранённый уровень, не HTML-дефолт 80.
    if (typeof syncVolumeFromStorage === 'function') syncVolumeFromStorage();
    else applyUserVolumeToGraph();

    // Строим граф: аудио -> auto gain -> компрессор -> громкость -> колонки
    source.connect(autoGainNode);
    autoGainNode.connect(compressor);
    compressor.connect(analyserNode);
    compressor.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    startLoudnessMonitor();
    console.log(`
♪                                                          ♫
                          ♪

    #         #   #         #   #         #
    ##       ##    #       #    ##       ##
    # #     # #     #     #     # #     # #
    #  #   #  #      #   #      #  #   #  #
    #   # #   #       # #       #   # #   #
    #    #    #        #        #    #    #
    #         #       # #       #         #
    #         #      #   #      #         #
    #         #     #     #     #         #
    #         #    #       #    #         #
    #         #   #         #   #         #

♫              ♪              ♫              ♪              ♫
`);
  } catch (e) {
    console.warn('Web Audio API не поддерживается, используется обычный <audio>', e);
    // Если не сработало, продолжаем работать как раньше
    audioCtx = null;
  }
}

/** Горизонтальная лента: только свайп/колёсико; полоса прокрутки — в CSS */
function wrapMxmHorizontalStrip(itemsHtml, extraStripClass = '') {
  const stripClasses = ['mxm-h-strip', extraStripClass].filter(Boolean).join(' ');
  return `<div class="${stripClasses.trim()}" role="list">${itemsHtml}</div>`;
}

/** Кнопки прокрутки горизонтальной ленты (stripId = id элемента с overflow-x) */
function mxmHorizontalStripNavHtml(stripId) {
  const chev = (d) =>
    `<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path d="${d}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const left = chev('M15 18l-6-6 6-6');
  const right = chev('M9 18l6-6-6-6');
  return `<div class="mxm-h-strip-arrows" role="group" aria-label="Прокрутка списка">
        <button type="button" class="mxm-h-strip-arrow" data-mxm-strip-target="${stripId}" data-mxm-strip-dir="-1" aria-label="Назад" onclick="mxmHorizontalStripStep(this)">${left}</button>
        <button type="button" class="mxm-h-strip-arrow" data-mxm-strip-target="${stripId}" data-mxm-strip-dir="1" aria-label="Вперёд" onclick="mxmHorizontalStripStep(this)">${right}</button>
      </div>`;
}

function mxmHorizontalStripStep(btn) {
  if (!btn) return;
  const id = btn.getAttribute('data-mxm-strip-target');
  const rawDir = btn.getAttribute('data-mxm-strip-dir');
  const dir = rawDir === '-1' ? -1 : 1;
  const el = id ? document.getElementById(id) : null;
  if (!el) return;
  const step = Math.max(160, Math.min(el.clientWidth * 0.72, 280));
  el.scrollBy({ left: step * dir, behavior: 'smooth' });
}
window.mxmHorizontalStripStep = mxmHorizontalStripStep;

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
async function renderHome() {
  const content = $('content');
  parkDesktopSiteFooter();
  content.innerHTML = getViewLoadingSkeleton('home');

  const data = await GET('/api/home');
  const {
    featured,
    playlists: playlistsFromApi = [],
    popularAlbums = [],
    popularTracks,
    popularTracksFromTaste = false,
    artists: artistsFromApi
  } = data;
  const artistsPool = (Array.isArray(artistsFromApi) ? artistsFromApi : []).filter(hasArtistPhoto);
  if (isMxMDesktopApp()) resetDesktopHomeArtistsPin();
  const artists = isMxMDesktopApp()
    ? buildHomeArtistsSelection(allArtists.length ? allArtists : artistsPool)
    : artistsPool;
  homePopularAlbumsAll = Array.isArray(popularAlbums) ? popularAlbums : [];
  homePlaylistsAll = Array.isArray(playlistsFromApi) && playlistsFromApi.length
    ? playlistsFromApi
    : (Array.isArray(featured) ? featured : []);
  const popularTracksForGrid = isMobile() ? popularTracks.slice(0, 6) : popularTracks;
  const qPop = storeQueue(popularTracksForGrid);

  const homeRandomPrefs = getHomeRandomPrefs();
  const homeRandomMoodSelected = homeRandomPrefs.moods[0] || null;
  const homeRandomDotActive = !!(
    homeRandomMoodSelected ||
    homeRandomPrefs.includeUnknown ||
    (homeRandomPrefs.langs && homeRandomPrefs.langs.length)
  );

  const popularStrip =
    popularTracks.length > 0
      ? `<section class="section mxm-home-section">
      <div class="mxm-section-head">
        <h2 class="section-title">Эти треки вам точно понравятся</h2>
        <p class="mxm-section-sub">${
          popularTracksFromTaste
            ? 'Популярное у исполнителей из ваших любимых треков и альбомов'
            : 'Самые популярные треки в каталоге'
        }</p>
      </div>
      <div class="home-popular-now-grid" role="list">
        ${popularTracksForGrid
          .map((t, i) => trackRowCover(t, qPop, i))
          .join('')}
      </div>
    </section>`
      : '';

  content.innerHTML = `
    <!-- HERO BANNER -->
    <div class="home-hero">
      <div class="home-hero-text">
        <div class="home-hero-badge">
          <span class="badge-dot"></span> MXM Music
        </div>
        <h1 class="home-hero-title">Музыка,<br>которая <em class="grad-text">вдохновляет</em></h1>
        <p class="home-hero-sub">Открывай новые треки и любимых исполнителей</p>
        
        <div class="home-hero-actions home-random-row">
          <div class="home-random-combo" role="group" aria-label="Случайная подборка и параметры">
            <button type="button" class="home-random-combo-main" onclick="playHomePersonalizedRandom()">Случайная подборка</button>
            <span class="home-random-combo-divider" aria-hidden="true"></span>
            <div class="home-random-settings-wrap">
            <button type="button" class="home-random-gear" id="home-random-settings-btn" onclick="toggleHomeRandomSettings(event)"
              aria-expanded="false" aria-haspopup="true" aria-label="Настройки подборки"
              title="Настройки подборки">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              <span class="home-random-gear-dot ${homeRandomDotActive ? 'is-active' : ''}" id="home-random-gear-dot" aria-hidden="true"></span>
            </button>
            <div class="home-random-portal" id="home-random-portal" style="display:none;">
              <button type="button" class="home-random-backdrop" id="home-random-backdrop" aria-label="Закрыть настройки" tabindex="-1" onclick="closeHomeRandomPopover(event)"></button>
            <div class="home-random-popover" id="home-random-popover" role="dialog" aria-label="Параметры случайной подборки" style="display:none;">
              <div class="home-random-popover-head">
                <span class="home-random-popover-title">Подборка</span>
                <div class="home-random-popover-actions">
                  <button type="button" class="home-random-icon-btn" onclick="resetHomeRandomSettings(event)" title="Сбросить" aria-label="Сбросить настройки">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-7.1L7 6M3 3v5h5"/></svg>
                  </button>
                  <button type="button" class="home-random-icon-btn" onclick="closeHomeRandomPopover(event)" title="Закрыть" aria-label="Закрыть">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" stroke-linecap="round"/></svg>
                  </button>
                </div>
              </div>
              <p class="home-random-section-label">Под настроение</p>
              <div class="home-random-pills" role="group" aria-label="Настроение">
                <button type="button" class="home-random-pill ${homeRandomMoodSelected === 'energetic' ? 'is-selected' : ''}" data-mood="energetic" aria-pressed="${homeRandomMoodSelected === 'energetic' ? 'true' : 'false'}" onclick="toggleHomeRandomMood(event,'energetic')">Бодрое</button>
                <button type="button" class="home-random-pill ${homeRandomMoodSelected === 'fun' ? 'is-selected' : ''}" data-mood="fun" aria-pressed="${homeRandomMoodSelected === 'fun' ? 'true' : 'false'}" onclick="toggleHomeRandomMood(event,'fun')">Весёлое</button>
                <button type="button" class="home-random-pill ${homeRandomMoodSelected === 'chill' ? 'is-selected' : ''}" data-mood="chill" aria-pressed="${homeRandomMoodSelected === 'chill' ? 'true' : 'false'}" onclick="toggleHomeRandomMood(event,'chill')">Спокойное</button>
                <button type="button" class="home-random-pill ${homeRandomMoodSelected === 'aggressive' ? 'is-selected' : ''}" data-mood="aggressive" aria-pressed="${homeRandomMoodSelected === 'aggressive' ? 'true' : 'false'}" onclick="toggleHomeRandomMood(event,'aggressive')">Агрессивное</button>
              </div>
              <p class="home-random-section-label">Язык текста</p>
              <div class="home-random-pills" role="group" aria-label="Язык текста песни">
                <button type="button" class="home-random-pill ${homeRandomPrefs.langs.includes('ru') ? 'is-selected' : ''}" data-lang="ru" aria-pressed="${homeRandomPrefs.langs.includes('ru') ? 'true' : 'false'}" onclick="toggleHomeRandomLang(event,'ru')">Русский</button>
                <button type="button" class="home-random-pill ${homeRandomPrefs.langs.includes('en') ? 'is-selected' : ''}" data-lang="en" aria-pressed="${homeRandomPrefs.langs.includes('en') ? 'true' : 'false'}" onclick="toggleHomeRandomLang(event,'en')">Английский</button>
              </div>
              <p class="home-random-section-label">Открытия</p>
              <button type="button" class="home-random-unknown-tile ${homeRandomPrefs.includeUnknown ? 'is-selected' : ''}" id="home-random-unknown-tile" aria-pressed="${homeRandomPrefs.includeUnknown ? 'true' : 'false'}" onclick="toggleHomeRandomUnknown(event)">
                <span class="home-random-unknown-icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="m12 3 1.6 5.5L19 12l-5.4 3.5L12 21l-1.6-5.5L5 12l5.4-3.5L12 3z"/></svg>
                  Незнакомое
                </span>
                <span class="home-random-unknown-hint">Только треки вне вашей истории и лайков — с ориентиром на то, что может зайти</span>
              </button>
            </div>
            </div>
          </div>
          </div>
          <a href="#library" class="btn-ghost">Моя музыка</a>
          <button type="button" class="btn-ghost mxm-radio-play-btn" onclick="playRadioStream()" title="Слушать MXM Radio" aria-label="Радио">
            <svg viewBox="0 0 24 24" width="15" height="15" style="display:inline;margin-right:6px" aria-hidden="true">
              <circle cx="12" cy="12" r="5"/>
              <path d="M3 12h2M19 12h2M12 3v2M12 19v2"/>
            </svg>
            Радио
          </button>
        </div>
      </div>

      <!-- Visual block with egg and floating notes -->
      <div class="home-hero-visual">
        <div class="egg-hero-wrap">
          <div class="egg-hero-ring-2"></div>
          <div class="egg-hero-ring"></div>
          ${mxmEggPictureHtml({ className: 'egg-hero-img', width: 512, height: 512, hero: true, alt: 'MXM' })}
          <div class="egg-hero-shadow"></div>
        </div>
        <div class="floating-notes">
          <span class="note n1">♪</span>
          <span class="note n2">♫</span>
          <span class="note n3">♩</span>
          <span class="note n4">♬</span>
        </div>
      </div>

      <!-- Radio badge (will be populated by updateRadioNowPlaying) -->
      <div id="radio-badge" class="radio-badge hidden"></div>
    </div>

    <!-- ПОДБОРК -->
    ${homePlaylistsAll.length ? `
    <section class="section mxm-home-section">
      <div class="section-header">
        <h2 class="section-title section-title-clickable" data-nav="browse" onclick="handleDataNavClick(event)">
          Для вас
          <span class="section-title-arrow">→</span>
        </h2>
      </div>
      <div class="playlist-home-carousel" id="home-playlists-carousel">
        <div id="home-playlists-strip" class="playlist-home-row"></div>
      </div>
    </section>` : ''}

    ${popularStrip}

    <!-- ARTISTS -->
    ${artists.length ? `
    <section class="section mxm-home-section">
      <div class="mxm-section-head">
        <h2 class="section-title">Исполнители</h2>
        <p class="mxm-section-sub">Новые лица каталога</p>
      </div>
      <div class="artists-grid">
        ${artists.map((a) => artistCard(a, { showFav: false })).join('')}
      </div>
    </section>` : ''}

    <!-- POPULAR ALBUMS -->
    ${homePopularAlbumsAll.length ? `
    <section class="section section-home-popular-albums mxm-home-section">
      <div class="mxm-section-head">
        <h2 class="section-title">Популярные альбомы</h2>
        <p class="mxm-section-sub">По прослушиваниям</p>
      </div>
      <div id="home-popular-albums-grid" class="cards-grid home-popular-albums-grid"></div>
    </section>` : ''}

  `;

  updateHomePlaylistsStrip();
  refreshHomePopularAlbumsGrid();
  attachDesktopSiteFooterInHome();

  // Запускаем обновление радио сразу после рендера
  updateRadioNowPlaying();
  // Динамический блок исполнителей (веб: пересборка и ротация; desktop-приложение: фикс. 6 до смены вкладки)
  if (!isMxMDesktopApp()) {
    if (allArtists.length > 0) updateHomeArtists(allArtists);
    if (homeArtistsTimer) clearInterval(homeArtistsTimer);
    homeArtistsTimer = setInterval(() => {
      if (allArtists.length > 0) updateHomeArtists(allArtists);
    }, 1 * 60 * 1000);
  }
  setQueuePlayingFromLabel('Главная');
}

/** После входа/регистрации — перезагрузить текущий экран с учётом сессии (главная меняет подборки). */
async function refreshActiveViewAfterAuth() {
  try {
    switch (state.view) {
      case 'home':
        await renderHome();
        break;
      case 'library':
        await renderLibrary();
        break;
      case 'browse':
        await renderBrowse();
        break;
      default:
        break;
    }
    refreshFavBtns();
    updatePlayingRows();
    updateDesktopSiteFooterVisibility();
  } catch (_) {}
}

const HOME_RANDOM_MOOD_KEYS = ['energetic', 'fun', 'chill', 'aggressive'];
const HOME_RANDOM_LANG_KEYS = ['ru', 'en'];

function normalizeHomeRandomMoods(arr) {
  const filtered = (Array.isArray(arr) ? arr : []).filter((k) => HOME_RANDOM_MOOD_KEYS.includes(k));
  return filtered.length ? [filtered[0]] : [];
}

function normalizeHomeRandomLangs(arr) {
  return (Array.isArray(arr) ? arr : []).filter((k) => HOME_RANDOM_LANG_KEYS.includes(k));
}

function getHomeRandomPrefs() {
  try {
    const raw = localStorage.getItem('mxm_home_random_moods');
    let moods = [];
    if (raw) {
      const parsed = JSON.parse(raw);
      moods = Array.isArray(parsed) ? parsed : [];
    } else {
      const legacyMood = localStorage.getItem('mxm_home_random_mood');
      if (HOME_RANDOM_MOOD_KEYS.includes(String(legacyMood || '').toLowerCase().trim())) {
        moods = [String(legacyMood).toLowerCase().trim()];
      }
    }
    moods = normalizeHomeRandomMoods(moods);
    const includeUnknown = localStorage.getItem('mxm_home_random_include_unknown') === '1'
      || localStorage.getItem('mxm_home_random_unknown') === '1';
    let langs = [];
    try {
      const rawLangs = localStorage.getItem('mxm_home_random_langs');
      if (rawLangs) {
        const parsedLangs = JSON.parse(rawLangs);
        langs = normalizeHomeRandomLangs(parsedLangs);
      }
    } catch {
      langs = [];
    }
    return { moods, includeUnknown, langs };
  } catch {
    return { moods: [], includeUnknown: false, langs: [] };
  }
}

function updateHomeRandomGearDot() {
  const dot = $('home-random-gear-dot');
  if (!dot) return;
  const p = getHomeRandomPrefs();
  dot.classList.toggle(
    'is-active',
    !!(p.moods?.length || p.includeUnknown || (p.langs && p.langs.length))
  );
}

function syncHomeRandomPopoverVisual() {
  const { moods, includeUnknown, langs } = getHomeRandomPrefs();
  const sel = moods[0] || null;
  document.querySelectorAll('.home-random-pill[data-mood]').forEach((btn) => {
    const on = btn.getAttribute('data-mood') === sel;
    btn.classList.toggle('is-selected', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  const langSet = new Set(langs || []);
  document.querySelectorAll('.home-random-pill[data-lang]').forEach((btn) => {
    const key = btn.getAttribute('data-lang');
    const on = langSet.has(key);
    btn.classList.toggle('is-selected', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  const unk = $('home-random-unknown-tile');
  if (unk) {
    unk.classList.toggle('is-selected', includeUnknown);
    unk.setAttribute('aria-pressed', includeUnknown ? 'true' : 'false');
  }
  updateHomeRandomGearDot();
}

function toggleHomeRandomMood(ev, key) {
  ev?.stopPropagation?.();
  if (!HOME_RANDOM_MOOD_KEYS.includes(key)) return;
  const { moods, includeUnknown } = getHomeRandomPrefs();
  const cur = moods[0] || null;
  const next = cur === key ? [] : [key];
  try {
    localStorage.setItem('mxm_home_random_moods', JSON.stringify(next));
  } catch {}
  syncHomeRandomPopoverVisual();
  if ($('home-random-portal')?.classList.contains('open')) positionHomeRandomPopover();
  scheduleHomeRandomAutoPlay();
}

function toggleHomeRandomLang(ev, key) {
  ev?.stopPropagation?.();
  if (!HOME_RANDOM_LANG_KEYS.includes(key)) return;
  const { moods, includeUnknown, langs } = getHomeRandomPrefs();
  const set = new Set(langs || []);
  if (set.has(key)) set.delete(key);
  else set.add(key);
  const next = HOME_RANDOM_LANG_KEYS.filter((k) => set.has(k));
  try {
    localStorage.setItem('mxm_home_random_langs', JSON.stringify(next));
    localStorage.setItem(
      'mxm_home_random_moods',
      JSON.stringify(Array.isArray(moods) ? moods : [])
    );
    localStorage.setItem('mxm_home_random_include_unknown', includeUnknown ? '1' : '0');
  } catch {}
  syncHomeRandomPopoverVisual();
  if ($('home-random-portal')?.classList.contains('open')) positionHomeRandomPopover();
  scheduleHomeRandomAutoPlay();
}

function toggleHomeRandomUnknown(ev) {
  ev?.stopPropagation?.();
  const { includeUnknown } = getHomeRandomPrefs();
  const next = !includeUnknown;
  try {
    localStorage.setItem('mxm_home_random_include_unknown', next ? '1' : '0');
  } catch {}
  syncHomeRandomPopoverVisual();
  if ($('home-random-portal')?.classList.contains('open')) positionHomeRandomPopover();
  scheduleHomeRandomAutoPlay();
}

function resetHomeRandomSettings(ev) {
  ev?.stopPropagation?.();
  try {
    localStorage.setItem('mxm_home_random_moods', '[]');
    localStorage.setItem('mxm_home_random_include_unknown', '0');
    localStorage.setItem('mxm_home_random_langs', '[]');
    sessionStorage.removeItem(HOME_RANDOM_EXCLUDE_KEY);
  } catch {}
  syncHomeRandomPopoverVisual();
  if ($('home-random-portal')?.classList.contains('open')) positionHomeRandomPopover();
  scheduleHomeRandomAutoPlay();
}

function isHomeRandomSheetMobile() {
  return window.matchMedia('(max-width: 900px)').matches;
}

const HOME_RANDOM_SHEET_TRANSITION_MS = 260;
/** Десктоп: подождать конец transition попапа перед display:none */
const HOME_RANDOM_DESKTOP_CLOSE_MS = 400;
let homeRandomPrevBodyOverflow = '';
let homeRandomDesktopCloseTimer = null;
let homeRandomAutoPlayTimer = null;
let homeRandomPlaySeq = 0;
let homeRandomDesktopOutsideCloseBound = false;

function scheduleHomeRandomAutoPlay() {
  if (state.view !== 'home') return;
  if (homeRandomAutoPlayTimer != null) {
    clearTimeout(homeRandomAutoPlayTimer);
    homeRandomAutoPlayTimer = null;
  }
  homeRandomAutoPlayTimer = setTimeout(() => {
    homeRandomAutoPlayTimer = null;
    playHomePersonalizedRandom({ silentError: true });
  }, 90);
}

function bindHomeRandomDesktopOutsideClose() {
  if (homeRandomDesktopOutsideCloseBound) return;
  homeRandomDesktopOutsideCloseBound = true;
  document.addEventListener('click', (ev) => {
    const portal = $('home-random-portal');
    const pop = $('home-random-popover');
    if (!portal || !pop) return;
    const isOpen =
      portal.classList.contains('open') &&
      portal.style.display !== 'none' &&
      pop.style.display !== 'none';
    if (!isOpen || portal.classList.contains('mobile-mode')) return;

    const t = ev.target;
    if (t && (t.closest('#home-random-popover') || t.closest('#home-random-settings-btn') || t.closest('.home-random-settings-wrap'))) {
      return;
    }
    closeHomeRandomPopover();
  });
}

function bindHomeRandomSheetSwipe() {
  const pop = $('home-random-popover');
  const portal = $('home-random-portal');
  if (!pop || !portal || pop.__mxmHomeRandomSwipeBound) return;
  pop.__mxmHomeRandomSwipeBound = true;

  let dragging = false;
  let startY = 0;
  let startX = 0;
  let lastDy = 0;

  const startNearTop = (clientY) => {
    const rect = pop.getBoundingClientRect();
    return clientY <= rect.top + 44;
  };

  pop.addEventListener(
    'touchstart',
    (e) => {
      if (!portal.classList.contains('open') || !portal.classList.contains('mobile-mode')) return;
      if (e.touches.length !== 1) return;

      const t = e.touches[0];
      // Let swipe-to-close work from the top area, but also allow it when the sheet is scrolled to the top.
      if (!startNearTop(t.clientY) && pop.scrollTop > 0) return;

      dragging = true;
      startY = t.clientY;
      startX = t.clientX;
      lastDy = 0;

      pop.style.transition = 'none';
      pop.style.transform = '';
    },
    { passive: true }
  );

  pop.addEventListener(
    'touchmove',
    (e) => {
      if (!dragging) return;
      if (e.touches.length !== 1) return;

      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      lastDy = dy;

      if (Math.abs(dx) > Math.abs(dy)) return; // horizontal drag
      if (dy < 0) return; // only closing swipe (down)

      e.preventDefault();
      pop.style.transition = 'none';
      pop.style.transform = `translateY(${dy}px)`;
    },
    { passive: false }
  );

  const onEnd = () => {
    if (!dragging) return;
    dragging = false;

    const dy = Math.max(0, lastDy || 0);
    const threshold = Math.min(140, pop.getBoundingClientRect().height * 0.35);
    if (dy > threshold) {
      closeHomeRandomPopover();
      return;
    }

    // Restore inline styles, CSS will animate back to translateY(0).
    pop.style.transition = '';
    pop.style.transform = '';
  };

  pop.addEventListener('touchend', onEnd, { passive: true });
  pop.addEventListener('touchcancel', onEnd, { passive: true });
}

function closeHomeRandomPopover(ev) {
  ev?.stopPropagation?.();
  const portal = $('home-random-portal');
  const pop = $('home-random-popover');
  const btn = $('home-random-settings-btn');
  btn?.setAttribute('aria-expanded', 'false');

  if (!portal || !pop) return;

  const isMobile = portal.classList.contains('mobile-mode');

  // Desktop: сначала снимаем .open (анимация закрытия), затем убираем из DOM-потока
  if (!isMobile) {
    if (homeRandomDesktopCloseTimer != null) {
      clearTimeout(homeRandomDesktopCloseTimer);
      homeRandomDesktopCloseTimer = null;
    }
    const finalize = () => {
      homeRandomDesktopCloseTimer = null;
      portal.classList.remove('mobile-mode');
      portal.style.display = 'none';
      pop.style.display = 'none';
      pop.style.left = '';
      pop.style.top = '';
      pop.style.width = '';
      pop.style.maxHeight = '';
      pop.style.transform = '';
      pop.style.transition = '';
    };
    if (portal.classList.contains('open')) {
      portal.classList.remove('open');
      homeRandomDesktopCloseTimer = setTimeout(finalize, HOME_RANDOM_DESKTOP_CLOSE_MS);
    } else {
      finalize();
    }
    return;
  }

  // Mobile: slide down first, hide after transition
  portal.classList.remove('open');
  pop.style.transition = '';
  pop.style.transform = 'translateY(110%)';

  setTimeout(() => {
    portal.classList.remove('mobile-mode');
    portal.style.display = 'none';
    pop.style.display = 'none';
    pop.style.left = '';
    pop.style.top = '';
    pop.style.width = '';
    pop.style.maxHeight = '';
    pop.style.transform = '';
    pop.style.transition = '';

    document.body.style.overflow = homeRandomPrevBodyOverflow;
  }, HOME_RANDOM_SHEET_TRANSITION_MS);
}

function positionHomeRandomPopover() {
  const portal = $('home-random-portal');
  const pop = $('home-random-popover');
  const btn = $('home-random-settings-btn');
  if (!portal?.classList.contains('open') || !pop || !btn) return;
  if (portal.classList.contains('mobile-mode')) {
    pop.style.left = '';
    pop.style.top = '';
    pop.style.width = '';
    pop.style.maxHeight = '';
    return;
  }
  const pad = 12;
  const gap = 10;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const bottomReserve = 112;
  const br = btn.getBoundingClientRect();
  const width = Math.min(320, vw - pad * 2);
  pop.style.width = `${width}px`;
  let left = Math.round(br.right - width);
  if (left < pad) left = pad;
  if (left + width > vw - pad) left = Math.max(pad, Math.round(vw - width - pad));
  let top = Math.round(br.bottom + gap);
  pop.style.left = `${left}px`;
  pop.style.top = `${top}px`;
  const maxBelow = Math.max(160, vh - top - bottomReserve);
  pop.style.maxHeight = `${Math.min(vh * 0.72, maxBelow)}px`;
  requestAnimationFrame(() => {
    const pr = pop.getBoundingClientRect();
    const overflowBottom = pr.bottom > vh - bottomReserve;
    if (overflowBottom && br.top > vh - br.bottom) {
      const flipTop = Math.max(pad, br.top - gap - pr.height);
      pop.style.top = `${Math.round(flipTop)}px`;
      const maxAbove = Math.max(160, br.top - gap - pad);
      pop.style.maxHeight = `${Math.min(vh * 0.72, maxAbove)}px`;
    }
  });
}

function toggleHomeRandomSettings(ev) {
  ev?.stopPropagation?.();
  bindHomeRandomDesktopOutsideClose();
  const portal = $('home-random-portal');
  const pop = $('home-random-popover');
  const btn = $('home-random-settings-btn');
  if (!portal || !pop || !btn) return;

  const expanded = btn.getAttribute('aria-expanded') === 'true';
  const desktopPopoverVisible =
    !portal.classList.contains('mobile-mode') &&
    portal.style.display !== 'none' &&
    pop.style.display !== 'none';
  const willOpen = !expanded && !portal.classList.contains('open') && !desktopPopoverVisible;

  // Closing
  if (!willOpen) {
    closeHomeRandomPopover(ev);
    return;
  }

  const mobile = isHomeRandomSheetMobile();

  btn.setAttribute('aria-expanded', 'true');

  if (homeRandomDesktopCloseTimer != null) {
    clearTimeout(homeRandomDesktopCloseTimer);
    homeRandomDesktopCloseTimer = null;
  }

  portal.classList.toggle('mobile-mode', mobile);
  portal.style.display = 'block';
  pop.style.display = 'block';

  document.body.appendChild(portal);

  // Clear previous positioning (desktop -> mobile)
  pop.style.left = '';
  pop.style.top = '';
  pop.style.width = '';
  pop.style.maxHeight = '';
  pop.style.transform = '';
  pop.style.transition = '';

  if (mobile) {
    // Start hidden then add `open` to animate slide-up.
    homeRandomPrevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    portal.classList.remove('open');
    requestAnimationFrame(() => {
      portal.classList.add('open');
      bindHomeRandomSheetSwipe();
    });
    return;
  }

  // Desktop open: кадр без `.open`, чтобы transition opacity/transform отработал
  portal.classList.remove('open');
  requestAnimationFrame(() => {
    portal.classList.add('open');
    positionHomeRandomPopover();
    requestAnimationFrame(() => positionHomeRandomPopover());
  });
}

const HOME_RANDOM_EXCLUDE_KEY = 'mxm_home_random_exclude';
/** Сколько id держим в sessionStorage (локально). */
const HOME_RANDOM_EXCLUDE_STORE_MAX = 150;
/**
 * Сколько id реально шлём в query: длинный exclude рвёт HTTP/2 (ERR_CONNECTION_CLOSED).
 * Сервер и так вычитает недавние QueueImpression (~120) для залогиненных.
 */
const HOME_RANDOM_EXCLUDE_QUERY_MAX = 60;

function loadHomeRandomExcludeIds() {
  try {
    const raw = sessionStorage.getItem(HOME_RANDOM_EXCLUDE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((id) => id && String(id).trim()) : [];
  } catch (_) {
    return [];
  }
}

function rememberHomeRandomPlaylist(trackIds) {
  try {
    const ids = (Array.isArray(trackIds) ? trackIds : []).map((id) => String(id).trim()).filter(Boolean);
    if (!ids.length) return;
    const merged = [...new Set([...ids, ...loadHomeRandomExcludeIds()])].slice(
      0,
      HOME_RANDOM_EXCLUDE_STORE_MAX
    );
    sessionStorage.setItem(HOME_RANDOM_EXCLUDE_KEY, JSON.stringify(merged));
  } catch (_) {}
}

async function playHomePersonalizedRandom(options = {}) {
  const { silentError = false } = options || {};
  const seq = ++homeRandomPlaySeq;
  try {
    const excludeSet = new Set(loadHomeRandomExcludeIds());
    if (state.currentTrack?.id) excludeSet.add(state.currentTrack.id);
    (state.recentlyPlayed || []).forEach((id) => excludeSet.add(id));
    (state.queue || []).forEach((t) => {
      if (t?.id) excludeSet.add(t.id);
    });
    // В URL — только хвост: свежие исключения важнее старых.
    const excludeIds = [...excludeSet].slice(0, HOME_RANDOM_EXCLUDE_QUERY_MAX).join(',');
    const { moods, includeUnknown, langs } = getHomeRandomPrefs();
    let url = `/api/tracks/personalized-random?exclude=${encodeURIComponent(excludeIds)}&nonce=${Date.now()}`;
    if (Array.isArray(moods) && moods.length) {
      url += `&moods=${encodeURIComponent(moods.join(','))}`;
    }
    if (Array.isArray(langs) && langs.length) {
      url += `&langs=${encodeURIComponent(langs.join(','))}`;
    }
    if (includeUnknown) {
      url += `&includeUnknown=1`;
    }
    try {
      const sid =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      sessionStorage.setItem('mxm_rec_session', sid);
      url += `&recSession=${encodeURIComponent(sid)}`;
    } catch (_) {}

    const tracks = await GET(url);
    if (seq !== homeRandomPlaySeq) return;
    if (!Array.isArray(tracks) || !tracks.length) {
      if (!silentError) toast('Не удалось собрать подборку', 'error');
      return;
    }
    rememberHomeRandomPlaylist(tracks.map((t) => t.id));
    setQueueState(tracks, 0, { lockPrevAtStart: true });
    playTrack(tracks[0]);
  } catch (e) {
    if (!silentError) toast('Ошибка подборки', 'error');
  }
}

window.playHomePersonalizedRandom = playHomePersonalizedRandom;
window.toggleHomeRandomSettings = toggleHomeRandomSettings;
window.toggleHomeRandomMood = toggleHomeRandomMood;
window.toggleHomeRandomLang = toggleHomeRandomLang;
window.toggleHomeRandomUnknown = toggleHomeRandomUnknown;
window.resetHomeRandomSettings = resetHomeRandomSettings;
window.closeHomeRandomPopover = closeHomeRandomPopover;

async function renderCollections() {
  const content = $('content');
  content.innerHTML = getViewLoadingSkeleton('collections');
  let playlists = [];
  try {
    playlists = await GET('/api/playlists');
  } catch {
    content.innerHTML = `<div class="page-loading"><p style="color:var(--danger)">Не удалось загрузить подборки</p></div>`;
    return;
  }
  if (!Array.isArray(playlists)) playlists = [];
  const headerSource = playlists.find((p) => p && p.coverUrl) || playlists[0] || null;
  const headerGrad = headerSource
    ? await resolveEditorialPlaylistGradient(headerSource)
    : pickGrad('collections');
  if (state.view !== 'collections') return;
  content.innerHTML = `
    <div class="collections-page">
      <div class="mxm-collections-intro" style="--collections-intro-grad:linear-gradient(${headerGrad})">
        <div class="mxm-collections-intro-bg" aria-hidden="true"></div>
        <h1 class="view-title">Подборки</h1>
      </div>
      <section class="section">
        <div class="cards-grid collections-playlist-grid">
          ${playlists.length ? playlists.map((p) => playlistCard(p)).join('') : '<p class="lib-empty" style="grid-column: 1 / -1;">Пока нет подборок</p>'}
        </div>
      </section>
    </div>
  `;
  setQueuePlayingFromLabel('Подборки');
}

function goToGenreSearchFromBtn(btn) {
  const raw = btn && btn.getAttribute('data-genre');
  if (!raw) return;
  let name = '';
  try {
    name = decodeURIComponent(raw);
  } catch {
    name = raw;
  }
  if (!name.trim()) return;
  navigate('search');
  setTimeout(() => {
    const inp = $('search-input-page') || $('search-input') || $('search-input-mobile');
    if (!inp) return;
    inp.value = name;
    $('search-clear-page')?.classList.toggle('hidden', !name.trim());
    $('search-clear')?.classList.toggle('hidden', !name.trim());
    $('search-clear-mobile')?.classList.toggle('hidden', !name.trim());
    performSearchAndRender(name);
  }, 60);
}

async function renderBrowse() {
  const content = $('content');
  content.innerHTML = getViewLoadingSkeleton('browse');
  let data;
  try {
    data = await GET('/api/browse');
  } catch {
    content.innerHTML = `<div class="page-loading"><p style="color:var(--danger)">Не удалось загрузить обзор</p></div>`;
    return;
  }
  const legacyPl = data.heroPlaylist;
  const hero =
    data.browseHero ||
    (legacyPl
      ? {
          type: 'playlist',
          id: legacyPl.id,
          title: legacyPl.title,
          subtitle: 'Слушайте подборку редакции',
          eyebrow: 'Подборка дня',
          coverUrl: legacyPl.coverUrl,
          coverGradient: legacyPl.coverGradient
        }
      : null);
  const newReleases = data.newReleases || [];
  const topCharts = data.topCharts || [];

  if (state.view !== 'browse') return;

  let sharedUserPlaylists = [];
  try {
    const upl = await GET('/api/user-playlists/shared');
    sharedUserPlaylists = Array.isArray(upl) ? upl : [];
  } catch {
    sharedUserPlaylists = [];
  }

  let heroGrad = pickGrad('browse');
  if (hero) {
    if (
      hero.coverUrl &&
      (hero.type === 'track' || hero.type === 'album' || hero.type === 'playlist')
    ) {
      const fromCover = await getGradientFromImage(hero.coverUrl);
      heroGrad = fromCover || hero.coverGradient || pickGrad(hero.id);
    } else {
      heroGrad = hero.coverGradient || pickGrad(hero.id);
    }
  }

  if (state.view !== 'browse') return;

  const heroCover = hero
    ? hero.coverUrl
      ? `<img class="browse-hero-cover" src="${esc(resolveMediaUrl(hero.coverUrl))}" alt="">`
      : `<div class="browse-hero-fallback" style="background:linear-gradient(${heroGrad})"></div>`
    : '';

  const browseHeroIdJs = hero ? String(hero.id).replace(/\\/g, '\\\\').replace(/'/g, "\\'") : '';
  const browseHeroArtistIdJs =
    hero && hero.type === 'track' && hero.artistId
      ? String(hero.artistId).replace(/\\/g, '\\\\').replace(/'/g, "\\'")
      : '';
  const browseHeroCtas = hero
    ? hero.type === 'playlist'
      ? `<div class="browse-hero-cta">
              <button type="button" class="btn-primary" onclick="playCollectionById('${browseHeroIdJs}')">Слушать</button>
              <button type="button" class="btn-ghost" onclick="shufflePlayCollectionById('${browseHeroIdJs}')">Перемешать</button>
            </div>`
      : hero.type === 'track'
        ? `<div class="browse-hero-cta">
              <button type="button" class="btn-primary" onclick="playTrackById('${browseHeroIdJs}')">Слушать</button>
              ${
                browseHeroArtistIdJs
                  ? `<button type="button" class="btn-ghost" onclick="navigate('artist', { id: '${browseHeroArtistIdJs}' })">К исполнителю</button>`
                  : ''
              }
            </div>`
        : hero.type === 'album'
          ? `<div class="browse-hero-cta">
              <button type="button" class="btn-primary" onclick="playAlbumById('${browseHeroIdJs}')">Слушать</button>
              <button type="button" class="btn-ghost" onclick="shufflePlayAlbumById('${browseHeroIdJs}')">Перемешать</button>
              <button type="button" class="btn-ghost" onclick="navigate('album', { id: '${browseHeroIdJs}' })">К альбому</button>
            </div>`
          : ''
    : '';

  const browseNewReleasesStripId = 'mxm-browse-new-releases-strip';
  const newReleasesItemsHtml = newReleases.length
    ? newReleases.map((a) => `<div class="mxm-h-strip-item" role="listitem">${albumCard(a)}</div>`).join('')
    : '';

  const topChartsForGrid = isMobile() ? topCharts.slice(0, 6) : topCharts;
  const qBrowseChart =
    topChartsForGrid.length > 0 ? storeQueue(topChartsForGrid.map((row) => row.track)) : '';

  const chartRows = topChartsForGrid.length
    ? `<div class="home-popular-now-grid" role="list">
        ${topChartsForGrid.map((row, i) => trackRowCover(row.track, qBrowseChart, i)).join('')}
      </div>`
    : '';

  const userPlaylistsSection = sharedUserPlaylists.length
    ? `<section class="section mxm-browse-section">
        <div class="mxm-section-head">
          <h2 class="section-title">Плейлисты пользователей</h2>
          <p class="mxm-section-sub">Подборки, которыми делится сообщество</p>
        </div>
        ${wrapMxmHorizontalStrip(
          sharedUserPlaylists
            .map(
              (p) =>
                `<div class="mxm-h-strip-item" role="listitem">${userPlaylistSquareCard(p, { mine: false })}</div>`
            )
            .join('')
        )}
      </section>`
    : '';

  const heroAria =
    hero && hero.type === 'playlist'
      ? 'Подборка редакции'
      : hero && hero.type === 'track'
        ? 'Рекомендованный трек'
        : hero && hero.type === 'album'
          ? 'Рекомендованный альбом'
          : 'Рекомендация';

  const heroInnerStyle = hero
    ? ` style="--browse-hero-cover-grad: linear-gradient(${heroGrad})"`
    : '';

  const heroBlock = hero
    ? `<section class="browse-hero-section mxm-browse-hero" aria-label="${esc(heroAria)}">
        <div class="browse-hero-inner"${heroInnerStyle}>
          <div class="browse-hero-visual">${heroCover}</div>
          <div class="browse-hero-text">
            <p class="mxm-section-eyebrow">${esc(hero.eyebrow || '')}</p>
            <h2 class="browse-hero-title">${esc(hero.title || '')}</h2>
            ${
              hero.type === 'track' && hero.artistName
                ? `<p class="browse-hero-artist">${esc(hero.artistName)}</p>`
                : ''
            }
            ${hero.subtitle ? `<p class="browse-hero-sub">${esc(hero.subtitle)}</p>` : ''}
            ${browseHeroCtas}
          </div>
        </div>
      </section>`
    : '';

  if (state.view !== 'browse') return;

  content.innerHTML = `
    <div class="browse-page">
      <div class="mxm-page-intro">
        <h1 class="view-title">Обзор</h1>
        <p class="view-subtitle">Новинки и чарты</p>
        <div class="mxm-page-intro-actions">
          <button type="button" class="mxm-want-hear-btn" onclick="openWantToHearModal()">Хочу услышать</button>
        </div>
      </div>
      ${heroBlock}
      ${
        newReleases.length
          ? `<section class="section mxm-browse-section mxm-browse-section--new-releases">
        <div class="mxm-section-head mxm-section-head--with-strip-nav">
          <h2 class="section-title">Новинки</h2>
          <p class="mxm-section-sub">Свежие релизы в каталоге</p>
          ${mxmHorizontalStripNavHtml(browseNewReleasesStripId)}
        </div>
        <div id="${browseNewReleasesStripId}" class="mxm-h-strip mxm-h-strip--new-releases" role="list">${newReleasesItemsHtml}</div>
      </section>`
          : ''
      }
      ${userPlaylistsSection}
      ${
        topCharts.length
          ? `<section class="section mxm-browse-section">
        <div class="mxm-section-head">
          <h2 class="section-title">Топ чарт</h2>
          <p class="mxm-section-sub">Популярные треки</p>
        </div>
        ${chartRows}
      </section>`
          : ''
      }
    </div>`;
  setQueuePlayingFromLabel('Обзор');
}

// ─── Remix / Mashup views ─────────────────────────────────────────────────
async function renderRemix() {
  const content = $('content');
  content.innerHTML = getViewLoadingSkeleton('remix');
  try {
    const tracks = await GET('/api/tracks/remix');
    const safeTracks = Array.isArray(tracks) ? tracks : [];
    if (!safeTracks.length) {
      content.innerHTML = `
        <div class="page-hero" style="--hero-grad:linear-gradient(${pickGrad('remix')})">
          <button class="nav-back-btn" onclick="goBack()" title="Назад">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
            </svg>
          </button>
          <div class="hero-bg"></div>
          <div class="hero-content">
            <div class="hero-info">
              <div class="hero-type">Теги</div>
              <h1 class="hero-title">Ремиксы</h1>
            </div>
          </div>
        </div>
        <div class="lib-empty">Пока нет ремиксов</div>`;
      setQueuePlayingFromLabel('Ремиксы');
      return;
    }
    const qKey = storeQueue(safeTracks);
    content.innerHTML = `
      <div class="page-hero" style="--hero-grad:linear-gradient(${pickGrad('remix')})">
        <button class="nav-back-btn" onclick="goBack()" title="Назад">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
          </svg>
        </button>
        <div class="hero-bg"></div>
        <div class="hero-content">
          <div class="hero-info">
            <div class="hero-type">Теги</div>
            <h1 class="hero-title">Ремиксы</h1>
            <div class="hero-sub">${safeTracks.length} ${pluralTracks(safeTracks.length)}</div>
          </div>
        </div>
      </div>
      <div class="track-list" style="margin-top: 10px">
        ${safeTracks.map((t, i) => trackRowCover(t, qKey, i)).join('')}
      </div>
    `;
    setQueuePlayingFromLabel('Ремиксы');
  } catch {
    content.innerHTML = `<h1 class="view-title">Ремиксы</h1><div class="lib-empty">Ошибка загрузки</div>`;
  }
}

async function renderMashup() {
  const content = $('content');
  content.innerHTML = getViewLoadingSkeleton('mashup');
  try {
    const tracks = await GET('/api/tracks/mashup');
    const safeTracks = Array.isArray(tracks) ? tracks : [];
    if (!safeTracks.length) {
      content.innerHTML = `
        <div class="page-hero" style="--hero-grad:linear-gradient(${pickGrad('mashup')})">
          <button class="nav-back-btn" onclick="goBack()" title="Назад">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
            </svg>
          </button>
          <div class="hero-bg"></div>
          <div class="hero-content">
            <div class="hero-info">
              <div class="hero-type">Теги</div>
              <h1 class="hero-title">Мешапы</h1>
            </div>
          </div>
        </div>
        <div class="lib-empty">Пока нет мешапов</div>`;
      setQueuePlayingFromLabel('Мешапы');
      return;
    }
    const qKey = storeQueue(safeTracks);
    content.innerHTML = `
      <div class="page-hero" style="--hero-grad:linear-gradient(${pickGrad('mashup')})">
        <button class="nav-back-btn" onclick="goBack()" title="Назад">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
          </svg>
        </button>
        <div class="hero-bg"></div>
        <div class="hero-content">
          <div class="hero-info">
            <div class="hero-type">Теги</div>
            <h1 class="hero-title">Мешапы</h1>
            <div class="hero-sub">${safeTracks.length} ${pluralTracks(safeTracks.length)}</div>
          </div>
        </div>
      </div>
      <div class="track-list" style="margin-top: 10px">
        ${safeTracks.map((t, i) => trackRowCover(t, qKey, i)).join('')}
      </div>
    `;
    setQueuePlayingFromLabel('Мешапы');
  } catch {
    content.innerHTML = `<h1 class="view-title">Мешапы</h1><div class="lib-empty">Ошибка загрузки</div>`;
  }
}

async function renderResetPassword(tokenRaw) {
  const content = $('content');
  const token = String(tokenRaw || '').replace(/[^a-f0-9]/gi, '');
  if (!token || token.length < 32) {
    content.innerHTML = `
      <div class="page-loading" style="padding:24px">
        <p>Некорректная ссылка. Запросите восстановление пароля ещё раз.</p>
        <button type="button" class="btn-primary" onclick="navigate('home')">На главную</button>
      </div>`;
    setQueuePlayingFromLabel('Ошибка');
    return;
  }
  content.innerHTML = `
    <div class="mxm-collection-page reset-password-page">
      <div class="page-hero mxm-collection-hero" style="--hero-grad:linear-gradient(135deg, #7C3AED, #EC4899)">
        <button type="button" class="nav-back-btn" onclick="navigate('home')" title="На главную">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
          </svg>
        </button>
        <div class="hero-bg"></div>
        <div class="hero-content">
          <div class="hero-info" style="max-width:420px">
            <div class="hero-type">Аккаунт</div>
            <h1 class="hero-title">Новый пароль</h1>
            <p class="hero-sub">Придумайте пароль не короче 6 символов.</p>
            <form id="reset-password-form" class="auth-form" style="margin-top:16px;text-align:left">
              <div class="form-group">
                <label for="reset-password-field">Пароль</label>
                <input type="password" id="reset-password-field" autocomplete="new-password" minlength="6" required placeholder="••••••••" />
              </div>
              <div id="reset-password-error" class="form-error hidden"></div>
              <button type="submit" class="btn-primary btn-full">Сохранить и войти позже</button>
            </form>
          </div>
        </div>
      </div>
    </div>`;
  setQueuePlayingFromLabel('Новый пароль');
  const form = $('reset-password-form');
  if (form) {
    form.addEventListener(
      'submit',
      async (e) => {
        e.preventDefault();
        const errEl = $('reset-password-error');
        const pw = String($('reset-password-field')?.value || '');
        if (errEl) errEl.classList.add('hidden');
        if (pw.length < 6) {
          if (errEl) {
            errEl.textContent = 'Минимум 6 символов';
            errEl.classList.remove('hidden');
          }
          return;
        }
        try {
          await POST('/api/auth/reset-password', { token, password: pw });
          toast('Пароль обновлён. Можно войти.', 'success');
          navigate('home');
          openAuthModal('login');
        } catch (err) {
          if (errEl) {
            errEl.textContent = err.message || 'Не удалось сохранить';
            errEl.classList.remove('hidden');
          }
        }
      },
      { once: false }
    );
  }
}

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────
function featuredCard(album) {
  const grad = album.coverGradient || pickGrad(album.id);
  const bg = album.coverUrl
    ? `<img src="${esc(resolveMediaUrl(album.coverUrl))}" alt="">`
    : `<div class="featured-cover-fallback" style="background:linear-gradient(${grad})"></div>`;
  const trackCount = albumTrackCountForUi(album);
  const y = Number(album.year);
  const yearBit =
    Number.isFinite(y) && y >= 1900 && y <= 2100 ? ` · ${y}` : '';
  const typeBit =
    trackCount === 1 ? ' · Сингл' : trackCount > 1 ? ' · Альбом' : '';
  return `
    <div class="featured-card" data-nav="album" data-nav-id="${album.id}" onclick="handleDataNavClick(event)">
      <div class="featured-bg">${bg}</div>
      <div class="featured-overlay"></div>
      <div class="featured-play-btn" onclick="event.stopPropagation();playAlbumById('${album.id}')" onauxclick="event.stopPropagation()">
        <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
      </div>
      <div class="featured-info">
        <div class="fi-title">${esc(album.title)}</div>
        <div class="fi-sub"><span class="fi-artist">${esc(getAlbumPrimaryArtistLabel(album))}</span><span class="fi-meta-tech">${yearBit}${typeBit}</span></div>
      </div>
    </div>`;
}

function albumCard(album) {
  return featuredCard(album);
}

/** @param {{ showFav?: boolean }} [opts] — на главной `showFav: false`, чтобы не показывать лайк исполнителя. */
function artistCard(artist, opts) {
  const showFav = !opts || opts.showFav !== false;
  const grad = artist.photoGradient || pickGrad(artist.name);
  const liked = showFav && typeof isFavAr === 'function' && isFavAr(artist.id);
  const favHtml = showFav
    ? `<button type="button" class="artist-card-fav-btn track-fav-btn ${liked ? 'liked' : ''}" data-fav-artist="${artist.id}"
          onclick="event.stopPropagation();toggleFavArtist('${artist.id}',event)" aria-label="В любимые исполнители" title="В любимые">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" ${liked ? 'fill="currentColor"' : 'fill="none"'}/></svg>
        </button>`
    : '';
  const sub =
    artist.relatedMatch && artist.relatedTo
      ? `Связан с ${artist.relatedTo}`
      : artist.genre || '';
  return `
    <div class="artist-card" data-nav="artist" data-nav-id="${artist.id}" onclick="handleDataNavClick(event)">
      <div class="artist-card-visual">
        ${favHtml}
        <div class="artist-avatar">${artistAvatarHtml(artist, grad)}</div>
      </div>
      <div class="artist-card-name">${esc(artist.name)}</div>
      <div class="artist-card-genre">${esc(sub)}</div>
    </div>`;
}

function trackListHdr() {
  return `<div class="track-list-header">
    <span>#</span><span>Название</span><span>Время</span>
  </div>`;
}

let _activeTrackMoreMenu = null;
let _activeTrackMoreBtn = null;

let _trackMoreMenuScrollLocked = false;
function setTrackMoreMenuScrollLocked(locked) {
  const shouldLock = !!locked && window.innerWidth <= TRACK_MENU_SHEET_BREAKPOINT;
  if (_trackMoreMenuScrollLocked === shouldLock) return;
  _trackMoreMenuScrollLocked = shouldLock;
  try {
    const content = document.getElementById('content');
    if (shouldLock) {
      if (content) content.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      if (content) {
        content.style.overflow = '';
        content.style.paddingRight = '';
      }
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.documentElement.style.overflow = '';
    }
  } catch {}
}

function getTrackMenuMode() {
  return 'popover';
}

function setTrackMorePanelMode(panel, mode) {
  if (!panel) return;
  panel.classList.remove('track-more-full', 'track-more-sheet', 'track-more-popover');
  panel.classList.add(mode === 'sheet' ? 'track-more-sheet' : 'track-more-popover');
}

function getTrackMenuViewportBottomInsetPx() {
  const root = getComputedStyle(document.documentElement);
  const n = (prop) => {
    const v = parseFloat(root.getPropertyValue(prop));
    return Number.isFinite(v) ? v : 0;
  };
  return n('--player-h') + n('--mobile-nav-h') + n('--safe-bottom') + n('--vv-bottom-offset') + 10;
}

function positionTrackMoreMenu(panel, btnEl, mode = getTrackMenuMode()) {
  if (!panel || !btnEl) return;
  panel.style.left = '';
  panel.style.top = '';
  panel.style.right = '';
  panel.style.bottom = '';
  panel.style.width = '';
  panel.style.height = '';
  panel.style.maxWidth = '';
  panel.style.maxHeight = '';
  panel.style.padding = '';
  panel.style.overflowY = '';

  setTrackMorePanelMode(panel, mode);

  panel.style.position = 'fixed';
  const r = btnEl.getBoundingClientRect?.();
  if (!r) return;
  const margin = 10;
  const gap = 10;
  const bottomInset = getTrackMenuViewportBottomInsetPx();
  const rect = panel.getBoundingClientRect();
  const menuWidth = rect.width || panel.offsetWidth || 0;
  const menuHeight = rect.height || panel.offsetHeight || 0;
  let left = r.left + (r.width / 2) - (menuWidth / 2);
  left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));
  const maxTopY = window.innerHeight - bottomInset - menuHeight - margin;
  let top = r.top - menuHeight - gap;
  if (top < margin) top = r.bottom + gap;
  if (top > maxTopY) top = r.top - menuHeight - gap;
  top = Math.max(margin, Math.min(top, maxTopY));
  panel.style.left = `${left}px`;
  panel.style.top = `${top}px`;
  panel.style.right = 'auto';
  panel.style.bottom = 'auto';
  panel.style.maxWidth = window.innerWidth > TRACK_MENU_SHEET_BREAKPOINT
    ? 'min(220px, calc(100vw - 24px))'
    : 'min(280px, calc(100vw - 24px))';
}

function bindTrackMorePortalIfNeeded(portal) {
  if (!portal || portal._mxmTrackMoreBound) return;
  portal._mxmTrackMoreBound = true;
  portal.addEventListener('click', async (e) => {
    const actionBtn = e.target.closest('[data-track-menu-action]');
    if (!actionBtn) return;
    e.preventDefault();
    e.stopPropagation();
    const action = actionBtn.dataset.trackMenuAction;
    const trackId = portal.dataset.trackId;
    const trackTitle = portal.dataset.trackTitle || '';

    if (action === 'play-next') {
      const track = await resolveTrackForMenuPortal(portal);
      closeAllTrackMoreMenus();
      if (track) addTrackPlayNext(track);
      return;
    }

    closeAllTrackMoreMenus();
    if (!trackId) return;
    if (action === 'share') {
      shareItem('track', trackId, trackTitle);
      return;
    }
    if (action === 'playlist') {
      addTrackToUserPlaylist(trackId);
    }
  });
}

function ensureTrackMorePortal() {
  let portal = document.getElementById('track-more-portal');
  if (portal) {
    portal.classList.remove('player-menu');
    bindTrackMorePortalIfNeeded(portal);
    return portal;
  }
  portal = document.createElement('div');
  portal.id = 'track-more-portal';
  portal.style.position = 'fixed';
  portal.style.top = '0';
  portal.style.left = '0';
  portal.style.width = '100%';
  portal.style.height = '100%';
  portal.style.zIndex = '10050';
  portal.className = 'track-more-menu track-more-portal hidden';
  portal.style.pointerEvents = 'none';
  portal.style.background = 'transparent';
  portal.style.inset = '0';
  portal.style.height = 'auto';
  portal.style.width = 'auto';
  portal.style.overflow = 'visible';
  portal.style.display = 'block';
  portal.style.position = 'fixed';
  portal.style.top = '0';
  portal.style.left = '0';
  portal.style.right = '0';
  portal.style.bottom = '0';
  portal.setAttribute('role', 'menu');
  bindTrackMorePortalIfNeeded(portal);
  document.body.appendChild(portal);
  return portal;
}

function closeAllTrackMoreMenus() {
  document.querySelectorAll('.track-more-menu').forEach((m) => {
    m.classList.remove('show', 'track-more-sheet', 'track-more-popover');
    m.classList.add('hidden');
  });
  document.querySelectorAll('.track-more-btn.is-open').forEach((b) => b.classList.remove('is-open'));
  const backdrop = document.getElementById('track-more-backdrop');
  if (backdrop) {
    backdrop.classList.remove('active');
    backdrop.classList.add('hidden');
  }
  const portal = document.getElementById('track-more-portal');
  if (portal) {
    portal.classList.add('hidden');
    portal.classList.remove('show', 'track-more-sheet', 'track-more-popover');
    portal.innerHTML = '';
    portal.dataset.trackId = '';
    portal.dataset.trackTitle = '';
    portal.dataset.qKey = '';
    portal.dataset.qIdx = '';
    portal.style.left = '';
    portal.style.top = '';
    portal.style.right = '';
    portal.style.bottom = '';
    portal.style.width = '';
    portal.style.maxWidth = '';
  }
  _activeTrackMoreMenu = null;
  _activeTrackMoreBtn = null;
  setTrackMoreMenuScrollLocked(false);
}

function toggleTrackMoreMenu(btnEl) {
  if (!btnEl) return;
  const trackRowEl = btnEl.closest('.track-row');
  const trackId = trackRowEl?.dataset?.trackId;
  if (!trackId) return;

  const alreadyOpen = _activeTrackMoreBtn === btnEl && _activeTrackMoreMenu;
  if (alreadyOpen) {
    closeAllTrackMoreMenus();
    return;
  }

  closeAllTrackMoreMenus();

  const titleGuess =
    trackRowEl?.querySelector('.track-title')?.textContent?.trim() || '';
  const portal = ensureTrackMorePortal();
  const mode = getTrackMenuMode();

  portal.dataset.trackId = trackId;
  portal.dataset.trackTitle = titleGuess;
  portal.dataset.qKey = trackRowEl?.dataset?.qKey || '';
  portal.dataset.qIdx =
    trackRowEl?.dataset?.qIdx != null && trackRowEl.dataset.qIdx !== ''
      ? String(trackRowEl.dataset.qIdx)
      : '';
  portal.innerHTML = `
    <div class="track-more-panel track-more-panel--compact" role="menu" aria-label="Действия">
      <button type="button" class="track-more-item" data-track-menu-action="play-next" role="menuitem">
        <span class="track-more-item-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 19V5"/><path d="M14 19V5"/><path d="M14 12h6"/><path d="M17 9l3 3-3 3"/></svg>
        </span>
        <span class="track-more-item-text">Играть далее</span>
      </button>
      <button type="button" class="track-more-item" data-track-menu-action="share" role="menuitem">
        <span class="track-more-item-icon" aria-hidden="true">
          ${MXM_SHARE_ICON_SVG.replace('<svg ', '<svg width="18" height="18" ')}
        </span>
        <span class="track-more-item-text">Поделиться</span>
      </button>
      <button type="button" class="track-more-item" data-track-menu-action="playlist" role="menuitem">
        <span class="track-more-item-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
        </span>
        <span class="track-more-item-text">В плейлист</span>
      </button>
    </div>
  `;

  portal.classList.remove('hidden');
  requestAnimationFrame(() => {
    portal.classList.add('show');
    positionTrackMoreMenu(portal.querySelector('.track-more-panel'), btnEl);
  });
  portal.style.zIndex = '10050';
  portal.style.pointerEvents = 'none';
  const panel = portal.querySelector('.track-more-panel');
  if (panel) {
    panel.style.pointerEvents = 'auto';
    positionTrackMoreMenu(panel, btnEl, mode);
  }
  portal.style.background = 'transparent';
  portal.style.inset = '0';
  portal.style.overflow = 'visible';
  portal.style.top = '0';
  portal.style.left = '0';
  portal.style.right = '0';
  portal.style.bottom = '0';
  portal.style.width = 'auto';
  portal.style.height = 'auto';
  portal.style.maxWidth = 'none';
  portal.style.maxHeight = 'none';
  if (panel) {
    panel.style.transform = '';
  }

  btnEl.classList.add('is-open');
  _activeTrackMoreMenu = portal;
  _activeTrackMoreBtn = btnEl;
  setTrackMoreMenuScrollLocked(true);
}

window.addEventListener('resize', () => {
  if (_activeTrackMoreMenu) closeAllTrackMoreMenus();
}, { passive: true });

window.addEventListener(
  'orientationchange',
  () => {
    if (_activeTrackMoreMenu) closeAllTrackMoreMenus();
  },
  { passive: true }
);

window.toggleTrackMoreMenu = toggleTrackMoreMenu;
window.closeAllTrackMoreMenus = closeAllTrackMoreMenus;

document.addEventListener(
  'scroll',
  () => {
    if (_activeTrackMoreMenu) closeAllTrackMoreMenus();
  },
  { capture: true, passive: true }
);

document.addEventListener('visibilitychange', () => {
  if (document.hidden) closeAllTrackMoreMenus();
});

document.addEventListener('click', (e) => {
  if (!_activeTrackMoreMenu) return;
  // Don't close when clicking the three-dots button itself
  if (e.target.closest('.track-more-btn')) return;

  if (e.target.closest('#track-more-portal')) return;

  closeAllTrackMoreMenus();
});

function trackRow(track, num, qKey, qIdx) {
  const liked = isFavT(track.id);
  const isPlaying = state.currentTrack?.id === track.id;
  const tagsHtml = htmlTrackListTagRow(track);
  return `
    <div class="track-row ${isPlaying ? 'playing' : ''}" data-track-id="${track.id}" data-q-key="${qKey}" data-q-idx="${qIdx}"
      onclick="handleTrackRowPlayClick(event,'${qKey}',${qIdx})">
      <div class="track-num-col">
        <span class="track-num-text">${num}</span>
        <div class="track-play-overlay">
          <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </div>
        <div class="track-anim-overlay">
          <div class="playing-bars"><span></span><span></span><span></span></div>
        </div>
      </div>
      <div class="track-info">
        ${tagsHtml}
        <div class="track-title">${esc(track.title)}</div>
        <div class="track-meta">${renderTrackMetaLineHtml(track)}</div>
      </div>
      <div class="track-right">
        <button class="track-fav-btn ${liked ? 'liked' : ''}" data-fav-track="${track.id}"
          onclick="event.stopPropagation();toggleFavTrack('${track.id}',event)" aria-label="В любимые">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" ${liked?'fill="currentColor"':'fill="none"'}/></svg>
        </button>
        <button class="track-more-btn" type="button"
          onclick="event.stopPropagation();toggleTrackMoreMenu(this)" aria-label="Действия">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <circle cx="12" cy="5" r="2"></circle>
            <circle cx="12" cy="12" r="2"></circle>
            <circle cx="12" cy="19" r="2"></circle>
          </svg>
        </button>
        <span class="track-duration">${fmtTime(track.duration)}</span>
      </div>
    </div>`;
}

function trackRowCover(track, qKey, qIdx, opts) {
  const liked = isFavT(track.id);
  const isPlaying = state.currentTrack?.id === track.id;
  const tagsHtml = htmlTrackListTagRow(track);
  const streakN = opts && Number(opts.playHistoryStreak) > 2 ? Math.floor(Number(opts.playHistoryStreak)) : 0;
  const streakHtml = streakN
    ? `<span class="play-history-streak-badge" title="${streakN} прослушиваний подряд" aria-label="${streakN} прослушиваний подряд">+${streakN}</span>`
    : '';
  const titleBlock = streakHtml
    ? `<div class="track-title-with-streak"><div class="track-title">${esc(track.title)}</div>${streakHtml}</div>`
    : `<div class="track-title">${esc(track.title)}</div>`;
  return `
    <div class="track-row track-row-w-cover ${isPlaying ? 'playing' : ''}" data-track-id="${track.id}" data-q-key="${qKey}" data-q-idx="${qIdx}" onclick="handleTrackRowPlayClick(event,'${qKey}',${qIdx})">
      <div class="track-cover-sm" style="position: relative;">
        ${coverImg(track.coverUrl, track.coverGradient || pickGrad(track.id))}
        <button type="button" class="track-cover-play" onclick="event.stopPropagation(); playFromQueue('${qKey}',${qIdx})" aria-label="Играть">
          <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </button>
      </div>
      <div class="track-info">
        ${tagsHtml}
        ${titleBlock}
        <div class="track-meta">${renderTrackMetaLineHtml(track)}</div>
      </div>
      <div class="track-right">
        <button type="button" class="track-fav-btn ${liked ? 'liked' : ''}" data-fav-track="${track.id}"
          onclick="event.stopPropagation();toggleFavTrack('${track.id}',event)" aria-label="В любимые">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" ${liked?'fill="currentColor"':'fill="none"'}/></svg>
        </button>
        <button class="track-more-btn" type="button"
          onclick="event.stopPropagation();toggleTrackMoreMenu(this)" aria-label="Действия">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <circle cx="12" cy="5" r="2"></circle>
            <circle cx="12" cy="12" r="2"></circle>
            <circle cx="12" cy="19" r="2"></circle>
          </svg>
        </button>
        <span class="track-duration">${fmtTime(track.duration)}</span>
      </div>
    </div>
  `;
}

/** Трек в поиске: один трек в очереди → после окончания подтянутся похожие. */
function searchTrackRowCover(track) {
  const liked = isFavT(track.id);
  const isPlaying = state.currentTrack?.id === track.id;
  const tagsHtml = htmlTrackListTagRow(track);
  const tid = String(track.id || '').replace(/'/g, '');
  return `
    <div class="track-row track-row-w-cover ${isPlaying ? 'playing' : ''}" data-track-id="${track.id}" onclick="playSearchResultTrack('${tid}')">
      <div class="track-cover-sm" style="position: relative;">
        ${coverImg(track.coverUrl, track.coverGradient || pickGrad(track.id))}
        <button type="button" class="track-cover-play" onclick="event.stopPropagation(); playSearchResultTrack('${tid}')" aria-label="Играть">
          <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </button>
      </div>
      <div class="track-info">
        ${tagsHtml}
        <div class="track-title">${esc(track.title)}</div>
        <div class="track-meta">${renderTrackMetaLineHtml(track)}</div>
      </div>
      <div class="track-right">
        <button type="button" class="track-fav-btn ${liked ? 'liked' : ''}" data-fav-track="${track.id}"
          onclick="event.stopPropagation();toggleFavTrack('${track.id}',event)" aria-label="В любимые">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" ${liked?'fill="currentColor"':'fill="none"'}/></svg>
        </button>
        <button class="track-more-btn" type="button"
          onclick="event.stopPropagation();toggleTrackMoreMenu(this)" aria-label="Действия">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <circle cx="12" cy="5" r="2"></circle>
            <circle cx="12" cy="12" r="2"></circle>
            <circle cx="12" cy="19" r="2"></circle>
          </svg>
        </button>
        <span class="track-duration">${fmtTime(track.duration)}</span>
      </div>
    </div>
  `;
}

/** Строка в блоке «Он реально написал эти треки»: без ссылки на профиль; в плеере ссылка всегда видна. */
function profileUploaderTrackRowHtml(track) {
  const liked = isFavT(track.id);
  const isPlaying = state.currentTrack?.id === track.id;
  const tagsHtml = htmlTrackListTagRow(track);
  const grad = track.coverGradient || pickGrad(track.id);
  return `
    <div class="track-row track-row-w-cover ${isPlaying ? 'playing' : ''}" data-track-id="${track.id}" onclick="playTrackById('${track.id}')">
      <div class="track-cover-sm" style="position: relative;">
        ${coverImg(track.coverUrl, grad)}
        <button type="button" class="track-cover-play" onclick="event.stopPropagation();playTrackById('${track.id}')" aria-label="Играть">
          <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </button>
      </div>
      <div class="track-info">
        ${tagsHtml}
        <div class="track-title">${esc(track.title)}</div>
        <div class="track-meta">${renderTrackMetaLineHtml(track, { hideUploaderProfile: true })}</div>
      </div>
      <div class="track-right">
        <button class="track-fav-btn ${liked ? 'liked' : ''}" data-fav-track="${track.id}"
          onclick="event.stopPropagation();toggleFavTrack('${track.id}',event)" aria-label="В любимые">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" ${liked ? 'fill="currentColor"' : 'fill="none"'}/></svg>
        </button>
        <span class="track-duration">${fmtTime(track.duration)}</span>
      </div>
    </div>`;
}

// ─── SEARCH VIEW ──────────────────────────────────────────────────────────────
function pickSearchTopResult(q, data) {
  const qq = String(q || '').trim().toLowerCase();
  if (!qq) return null;
  const playlists = data.playlists || [];
  for (const p of playlists) {
    if (String(p.title || '').trim().toLowerCase() === qq) return { kind: 'playlist', item: p };
  }
  for (const t of data.tracks || []) {
    if (String(t.title || '').trim().toLowerCase() === qq) return { kind: 'track', item: t };
  }
  for (const a of data.albums || []) {
    if (String(a.title || '').trim().toLowerCase() === qq) return { kind: 'album', item: a };
  }
  for (const a of data.artists || []) {
    if (String(a.name || '').trim().toLowerCase() === qq) return { kind: 'artist', item: a };
  }
  const users = data.users || [];
  const atQ = qq.startsWith('@') ? qq.slice(1) : qq;
  for (const u of users) {
    if (String(u.username || '').trim().toLowerCase() === atQ) return { kind: 'user', item: u };
  }
  if (data.tracks && data.tracks[0]) return { kind: 'track', item: data.tracks[0] };
  if (data.artists && data.artists[0]) return { kind: 'artist', item: data.artists[0] };
  if (data.albums && data.albums[0]) return { kind: 'album', item: data.albums[0] };
  if (playlists[0]) return { kind: 'playlist', item: playlists[0] };
  return null;
}

function searchTopResultCardHtml(hit, qKey = '', qIdx = 0) {
  if (!hit || !hit.item) return '';
  if (hit.kind === 'track') {
    const t = hit.item;
    const g = t.coverGradient || pickGrad(t.id);
    const playFn = `playSearchResultTrack('${t.id}')`;
    return `<div class="search-top-card search-top-card--track">
      <div class="search-top-cover" onclick="event.stopPropagation();${playFn}">
        ${coverImg(t.coverUrl, g)}
        <button type="button" class="track-cover-play" onclick="event.stopPropagation();${playFn}" aria-label="Играть">
          <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </button>
      </div>
      <div class="search-top-info search-top-info--link" role="link" tabindex="0" data-nav="track" data-nav-id="${esc(t.id)}" onclick="handleDataNavClick(event)">
        <div class="search-top-label">Лучшее совпадение · Трек</div>
        <div class="search-top-title">${esc(t.title)}</div>
        <div class="search-top-sub">${esc(getTrackArtistText(t))}</div>
      </div>
    </div>`;
  }
  if (hit.kind === 'album') return `<div class="search-top-wrap">${albumCard(hit.item)}</div>`;
  if (hit.kind === 'artist') return `<div class="search-top-wrap">${artistCard(hit.item)}</div>`;
  if (hit.kind === 'playlist') return `<div class="search-top-wrap">${playlistCard(hit.item)}</div>`;
  if (hit.kind === 'user') return `<div class="search-top-wrap search-top-wrap--user">${searchUserRowHtml(hit.item)}</div>`;
  return '';
}

async function renderSearch(query = '') {
  const content = $('content');

  const promptHint =
    'Введите запрос в поле ниже — треки, альбомы, исполнителей и профили по нику (например @username)';

  content.innerHTML = `
    <div class="search-page-inner">
    <div class="search-bar search-bar--page" id="search-page-bar">
      ${MXM_SEARCH_ICON_SVG.replace('<svg ', '<svg class="search-icon" ')}
      <input id="search-input-page" type="search" enterkeyhint="search" placeholder="Треки, альбомы, исполнители, @ник…"
        oninput="handleSearchInput(event)" onkeydown="handleSearchKey(event)"
        autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" />
      <button type="button" id="search-clear-page" class="search-clear hidden" onclick="clearSearch()" aria-label="Очистить">
        <svg viewBox="0 0 24 24" width="16" height="16"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
      </button>
    </div>
    <div class="search-prompt ${query ? 'fade-out' : ''}">
      <h3>Что ищем?</h3>
      <p>${promptHint}</p>
    </div>
    <div id="search-results" class="${query ? '' : 'hidden'}"></div>
    </div>
  `;

  const inp = $('search-input-page');
  if (inp) {
    inp.value = query;
    $('search-clear-page')?.classList.toggle('hidden', !String(query).trim());
    try {
      inp.focus({ preventScroll: true });
    } catch (_) {
      try {
        inp.focus();
      } catch (_) {}
    }
  }

  if (query) {
    const resultsEl = $('search-results');
    resultsEl.innerHTML = loadingSpinner();
    resultsEl.classList.remove('hidden');

    try {
      const data = await GET(`/api/search?q=${encodeURIComponent(query)}`);
      renderSearchResults(data, query);
    } catch (err) {
      resultsEl.innerHTML = `<div class="search-error">Ошибка загрузки</div>`;
    }
  }
  if (typeof injectMxmAiSearchEntry === 'function') {
    injectMxmAiSearchEntry().catch(() => {});
  }
}
// Функция для конвертации раскладки (упрощённая)
function convertKeyboardLayout(str) {
  const enToRu = {
    'q':'й','w':'ц','e':'у','r':'к','t':'е','y':'н','u':'г','i':'ш','o':'щ','p':'з','[':'х',']':'ъ',
    'a':'ф','s':'ы','d':'?','f':'а','g':'п','h':'р','j':'о','k':'л','l':'д',';':'ж',"'":'э',
    'z':'я','x':'ч','c':'с','v':'м','b':'и','n':'т','m':'ь',',':'б','.':'ю','/':' '
  };
  const ruToEn = Object.fromEntries(Object.entries(enToRu).map(([k,v]) => [v,k]));
  const isCyrillic = (ch) => {
    const code = ch ? ch.charCodeAt(0) : 0;
    return (code >= 0x0400 && code <= 0x04FF) || code === 0x0451 || code === 0x0401;
  };

  // Определяем, какая раскладка преобладает
  let ruCount = 0, enCount = 0;
  for (let ch of str) {
    if (isCyrillic(ch)) ruCount++;
    else if (/[a-zA-Z]/.test(ch)) enCount++;
  }

  const map = ruCount > enCount ? ruToEn : enToRu;
  let result = '';
  for (let ch of str) {
    let lower = ch.toLowerCase();
    if (map[lower]) {
      let replacement = map[lower];
      if (ch === ch.toUpperCase() && lower !== ch) {
        replacement = replacement.toUpperCase();
      }
      result += replacement;
    } else {
      result += ch;
    }
  }
  return result;
}

// Объединение результатов поиска (убирает дубликаты по id)
function mergeSearchResults(original, alt) {
  if (!alt) return original;
  const merged = {
    tracks: [...(original.tracks || [])],
    albums: [...(original.albums || [])],
    artists: [...(original.artists || [])],
    users: [...(original.users || [])],
    playlists: [...(original.playlists || [])]
  };
  const trackIds = new Set(merged.tracks.map((t) => t.id));
  (alt.tracks || []).forEach((t) => {
    if (!trackIds.has(t.id)) merged.tracks.push(t);
  });
  const albumIds = new Set(merged.albums.map((a) => a.id));
  (alt.albums || []).forEach((a) => {
    if (!albumIds.has(a.id)) merged.albums.push(a);
  });
  const artistIds = new Set(merged.artists.map((a) => a.id));
  (alt.artists || []).forEach((a) => {
    if (!artistIds.has(a.id)) merged.artists.push(a);
  });
  const userIds = new Set(merged.users.map((u) => u.id));
  (alt.users || []).forEach((u) => {
    if (u && !userIds.has(u.id)) merged.users.push(u);
  });
  const playlistIds = new Set(merged.playlists.map((p) => p.id));
  (alt.playlists || []).forEach((p) => {
    if (p && !playlistIds.has(p.id)) merged.playlists.push(p);
  });
  return merged;
}

/** Сброс результатов без пересборки страницы — иначе при очистке поля теряется фокус с input. */
function resetSearchEmptyState() {
  const resultsEl = $('search-results');
  if (resultsEl) {
    resultsEl.innerHTML = '';
    resultsEl.classList.add('hidden');
  }
  document.querySelector('.search-prompt')?.classList.remove('fade-out');
}

async function handleSearchInput(e) {
  const val = e.target.value.trim();
  $('search-clear-page')?.classList.toggle('hidden', !val);
  $('search-clear')?.classList.toggle('hidden', !val);
  $('search-clear-mobile')?.classList.toggle('hidden', !val);

  clearTimeout(_searchTimer);

  // Если мы не на странице поиска, переходим на неё
  if (state.view !== 'search') {
    navigate('search');
    _searchTimer = setTimeout(() => {
      if (val) performSearchAndRender(val);
      else renderSearch('');
    }, 300);
    return;
  }

  // Уже на странице поиска
  _searchTimer = setTimeout(async () => {
    if (!val) {
      resetSearchEmptyState();
      return;
    }
    await performSearchAndRender(val);
  }, 350);
}

// Функция для разбора запроса на артиста и название через дефис
function parseQuery(query) {
  const dashIndex = query.indexOf(' - ');
  if (dashIndex !== -1) {
    return {
      artist: query.substring(0, dashIndex).trim(),
      title: query.substring(dashIndex + 3).trim(),
      full: query
    };
  }
  return { full: query };
}

// Разделяет запрос на возможные пары (исполнитель, название) на основе пробелов
function splitQuery(query) {
  const parts = query.split(/\s+/).filter(p => p.length > 0);
  if (parts.length < 2) return [];

  const candidates = [];

  // 1. Разделение по последнему пробелу
  const lastSpaceIdx = query.lastIndexOf(' ');
  if (lastSpaceIdx !== -1) {
    const artist = query.substring(0, lastSpaceIdx).trim();
    const title = query.substring(lastSpaceIdx + 1).trim();
    candidates.push({ artist, title });
  }

  // 2. Если слов >=3, пробуем разделить по предпоследнему пробелу
  if (parts.length >= 3) {
    const secondLastSpaceIdx = query.lastIndexOf(' ', lastSpaceIdx - 1);
    if (secondLastSpaceIdx !== -1) {
      const artist = query.substring(0, secondLastSpaceIdx).trim();
      const title = query.substring(secondLastSpaceIdx + 1).trim();
      candidates.push({ artist, title });
    } else {
      // Альтернатива: по первому пробелу
      const firstSpaceIdx = query.indexOf(' ');
      const artist = query.substring(0, firstSpaceIdx).trim();
      const title = query.substring(firstSpaceIdx + 1).trim();
      candidates.push({ artist, title });
    }
  }

  return candidates;
}

async function performSearchAndRender(query) {
  try {
    let data = await GET(`/api/search?q=${encodeURIComponent(query)}`);
    data.users = data.users || [];
    data.playlists = data.playlists || [];
    let musicTotal =
      data.tracks.length + data.albums.length + data.artists.length + data.playlists.length;

    // Если мало музыкальных результатов, пробуем альтернативную раскладку
    if (musicTotal < 3) {
      const altQuery = convertKeyboardLayout(query);
      if (altQuery !== query) {
        const altData = await GET(`/api/search?q=${encodeURIComponent(altQuery)}`);
        if (altData) {
          altData.users = altData.users || [];
          altData.playlists = altData.playlists || [];
          data = mergeSearchResults(data, altData);
          musicTotal =
            data.tracks.length + data.albums.length + data.artists.length + data.playlists.length;
        }
      }
    }

    // Если всё ещё мало, пробуем разобрать запрос на артиста и название
    if (musicTotal < 3) {
      const parsed = parseQuery(query); // вариант с дефисом
      if (parsed.artist && parsed.title) {
        const [artistRes, titleRes] = await Promise.all([
          GET(`/api/search?q=${encodeURIComponent(parsed.artist)}`),
          GET(`/api/search?q=${encodeURIComponent(parsed.title)}`)
        ]);
        artistRes.users = artistRes.users || [];
        titleRes.users = titleRes.users || [];
        artistRes.playlists = artistRes.playlists || [];
        titleRes.playlists = titleRes.playlists || [];
        data = mergeSearchResults(data, artistRes);
        data = mergeSearchResults(data, titleRes);
        musicTotal =
          data.tracks.length + data.albums.length + data.artists.length + data.playlists.length;
      }

      // Если всё ещё мало, пробуем варианты с разделением по пробелам
      if (musicTotal < 3) {
        const candidates = splitQuery(query);
        for (const { artist, title } of candidates) {
          const [artistRes, titleRes] = await Promise.all([
            GET(`/api/search?q=${encodeURIComponent(artist)}`),
            GET(`/api/search?q=${encodeURIComponent(title)}`)
          ]);
          artistRes.users = artistRes.users || [];
          titleRes.users = titleRes.users || [];
          artistRes.playlists = artistRes.playlists || [];
          titleRes.playlists = titleRes.playlists || [];
          data = mergeSearchResults(data, artistRes);
          data = mergeSearchResults(data, titleRes);
        }
        musicTotal =
          data.tracks.length + data.albums.length + data.artists.length + data.playlists.length;
      }
    }

    data.users = data.users || [];
    data.playlists = data.playlists || [];
    renderSearchResults(data, query);
  } catch (err) {
    console.warn('Search error:', err);
    const resultsEl = $('search-results');
    if (resultsEl) resultsEl.innerHTML = '<div class="search-error">Ошибка загрузки</div>';
  }
}

function handleSearchKey(e) { if (e.key === 'Escape') clearSearch(); }
function clearSearch() {
  const p = $('search-input-page');
  const h = $('search-input');
  const m = $('search-input-mobile');
  if (p) p.value = '';
  if (h) h.value = '';
  if (m) m.value = '';
  $('search-clear-page')?.classList.add('hidden');
  $('search-clear')?.classList.add('hidden');
  $('search-clear-mobile')?.classList.add('hidden');
  if (state.view === 'search' && $('search-results')) {
    resetSearchEmptyState();
    ($('search-input-page') || getSearchInputEl())?.focus();
  } else {
    renderSearch('');
  }
}


function searchUserRowHtml(u) {
  const name = String(u.username || '').trim();
  const at = '@' + esc(name);
  const letter = (name.charAt(0) || '?').toUpperCase();
  const raw = u.avatarUrl ? resolveMediaUrl(u.avatarUrl) : '';
  const av = raw
    ? `<span class="search-user-av search-user-av--img"><img src="${esc(raw)}" alt="" loading="lazy" decoding="async" referrerpolicy="no-referrer"></span>`
    : `<span class="search-user-av search-user-av--letter">${esc(letter)}</span>`;
  const hash = buildHash('profile', { id: userProfileRef(u) });
  return `<a class="search-user-row" href="#${hash}" data-nav="profile" data-nav-id="${esc(userProfileRef(u))}" onclick="handleDataNavClick(event)">
    ${av}
    <span class="search-user-at">${at}</span>
  </a>`;
}

function renderSearchResults(data, q) {
  const el = $('search-results');
  if (!el) return;

  const prompt = document.querySelector('.search-prompt');
  if (prompt) prompt.classList.add('fade-out');

  el.classList.remove('hidden');
  data.playlists = data.playlists || [];
  const users = Array.isArray(data.users) ? data.users : [];
  const top = pickSearchTopResult(q, data);
  let tracks = [...(data.tracks || [])];
  let albums = [...(data.albums || [])];
  let artists = [...(data.artists || [])];
  let playlists = [...(data.playlists || [])];
  let userRows = [...users];
  if (top) {
    if (top.kind === 'track') tracks = tracks.filter((t) => t.id !== top.item.id);
    if (top.kind === 'album') albums = albums.filter((a) => a.id !== top.item.id);
    if (top.kind === 'artist') artists = artists.filter((a) => a.id !== top.item.id);
    if (top.kind === 'playlist') playlists = playlists.filter((p) => p.id !== top.item.id);
    if (top.kind === 'user')
      userRows = userRows.filter((u) => userProfileRef(u) !== userProfileRef(top.item));
  }
  const total =
    tracks.length + albums.length + artists.length + playlists.length + userRows.length + (top ? 1 : 0);
  if (!total) {
    el.innerHTML = `<div class="search-prompt"><h3>Ничего не найдено по &laquo;${esc(q)}&raquo;</h3><p>Попробуйте другой запрос</p></div>`;
    return;
  }
  let html = '';
  if (top) {
    html += `<section class="section search-top-section"><h2 class="section-title">Лучшее совпадение</h2>${searchTopResultCardHtml(top)}</section>`;
  }
  if (tracks.length) {
    html += `<section class="section"><div class="mxm-section-head"><h2 class="section-title">Треки</h2></div>
      ${wrapMxmHorizontalStrip(
        tracks
          .map((t) => `<div class="mxm-h-strip-item mxm-h-strip-item--track" role="listitem">${searchTrackRowCover(t)}</div>`)
          .join(''),
        'mxm-h-strip--tracks'
      )}</section>`;
  }
  if (albums.length) {
    html += `<section class="section"><div class="mxm-section-head"><h2 class="section-title">Альбомы</h2></div>
      ${wrapMxmHorizontalStrip(
        albums.map((a) => `<div class="mxm-h-strip-item" role="listitem">${albumCard(a)}</div>`).join('')
      )}</section>`;
  }
  if (artists.length) {
    html += `<section class="section"><div class="mxm-section-head"><h2 class="section-title">Исполнители</h2></div>
      ${wrapMxmHorizontalStrip(
        artists.map((a) => `<div class="mxm-h-strip-item" role="listitem">${artistCard(a)}</div>`).join(''),
        'mxm-h-strip--artists'
      )}</section>`;
  }
  if (playlists.length) {
    html += `<section class="section"><div class="mxm-section-head"><h2 class="section-title">Подборки</h2></div>
      ${wrapMxmHorizontalStrip(
        playlists.map((p) => `<div class="mxm-h-strip-item" role="listitem">${playlistCard(p)}</div>`).join('')
      )}</section>`;
  }
  if (userRows.length) {
    html += `<section class="section search-users-section"><h2 class="section-title">Профили</h2>
      <div class="search-users-list" role="list">${userRows
        .map((u) => `<div class="search-users-item" role="listitem">${searchUserRowHtml(u)}</div>`)
        .join('')}</div></section>`;
  }
  el.innerHTML = html;
}

function onArtistInput(input, suggestionsId) {
  const boxId = suggestionsId || 'artist-suggestions';
  const val = input.value.toLowerCase().trim();
  const suggestions = $(boxId);
  if (!suggestions) return;
  if (!val) {
    suggestions.classList.add('hidden');
    return;
  }
  const matches = autocompleteArtists.filter(a => 
    a.name.toLowerCase().includes(val)
  ).slice(0, 10);
  
  if (matches.length === 0) {
    suggestions.classList.add('hidden');
    return;
  }
  
  const inputId = input.id || 'up-artist';
  suggestions.innerHTML = matches.map(a =>
    `<div class="suggestion-item" onmousedown="event.preventDefault()" onclick="selectArtist('${a.id}', '${esc(a.name)}', '${inputId}', '${boxId}')">${esc(a.name)}</div>`
  ).join('');
  suggestions.classList.remove('hidden');
}

function onArtistFocus(input, suggestionsId) {
  if (!input.value.trim()) {
    onArtistInput(input, suggestionsId);
  }
}

function selectArtist(id, name, inputId, suggestionsId) {
  const inp = $(inputId || 'up-artist');
  if (inp) {
    inp.value = name;
    inp.dataset.artistId = id;
  }
  const box = $(suggestionsId || 'artist-suggestions');
  if (box) box.classList.add('hidden');
}

const GENRE_OPTIONS = [
  'Другое',
  'Electronic',
  'House',
  'Techno',
  'Ambient',
  'Synthwave',
  'Phonk',
  'Hyperpop',
  'Rock',
  'Alternative',
  'Metal',
  'Punk',
  'Pop',
  'Indie',
  'Hip-Hop',
  'Rap',
  'Trap',
  'Drill',
  'R&B',
  'Soul',
  'Jazz',
  'Blues',
  'Classical',
  'Folk',
  'Country',
  'Reggae',
  'Latin',
  'Disco',
  'Lo-Fi',
  'K-Pop',
  'Soundtrack',
  'Experimental'
];

function onGenreInput(input, suggestionsId) {
  const boxId = suggestionsId || 'genre-suggestions';
  const suggestions = $(boxId);
  if (!suggestions) return;

  const inputId = input.id || 'up-genre';
  suggestions.innerHTML = GENRE_OPTIONS.map((genre) =>
    `<div class="suggestion-item" onmousedown="event.preventDefault()" onclick="selectGenre('${esc(genre)}', '${inputId}', '${boxId}')">${esc(genre)}</div>`
  ).join('');
  suggestions.classList.remove('hidden');
}

function onGenreFocus(input, suggestionsId) {
  onGenreInput(input, suggestionsId);
}

function selectGenre(genre, inputId, suggestionsId) {
  const inp = $(inputId || 'up-genre');
  if (inp) inp.value = genre;
  const box = $(suggestionsId || 'genre-suggestions');
  if (box) box.classList.add('hidden');
}

// ─── Upload tag (single choice) ────────────────────────────────────────────
function onTagTypeFocus() {
  const box = $('tag-type-suggestions');
  if (box) box.classList.remove('hidden');
}

function setTagType(tagVal, label) {
  const input = $('up-tag-type');
  if (input) {
    input.value = label;
    input.dataset.tagVal = tagVal;
  }
  const box = $('tag-type-suggestions');
  if (box) box.classList.add('hidden');
}

// Аналогично для альбома
function onAlbumInput(input) {
  const val = input.value.toLowerCase().trim();
  const suggestions = $('album-suggestions');
  if (!val) {
    suggestions.classList.add('hidden');
    return;
  }
  const matches = autocompleteAlbums.filter(a => 
    a.display.toLowerCase().includes(val)
  ).slice(0, 10);
  
  if (matches.length === 0) {
    suggestions.classList.add('hidden');
    return;
  }
  
  // ВАЖНО: передаём coverUrl (экранируем, чтобы избежать проблем с кавычками)
  suggestions.innerHTML = matches.map(a => 
    `<div class="suggestion-item" onclick="selectAlbum('${a.id}', '${esc(a.title)}', '${esc(a.artist)}', '${a.coverUrl ? esc(a.coverUrl) : ''}')">${esc(a.display)}</div>`
  ).join('');
  suggestions.classList.remove('hidden');
}

function onAlbumFocus(input) {
  if (!input.value.trim()) {
    onAlbumInput(input);
  }
}

function hideSuggestionsWithDelay(id) {
  window.setTimeout(() => {
    $(id)?.classList.add('hidden');
  }, 120);
}

function selectAlbum(id, title, artist, coverUrl = '') {
  $('up-album').value = title;
  $('up-album').dataset.albumId = id;
  // Сохраняем URL обложки, если есть
  if (coverUrl) {
    $('up-album').dataset.coverUrl = coverUrl;
    // Сразу показываем обложку ? превью
    $('cover-thumb').innerHTML = `<img src="${esc(resolveMediaUrl(coverUrl))}" style="width:100%;height:100%;object-fit:cover;border-radius:inherit" alt="">`;
  } else {
    delete $('up-album').dataset.coverUrl;
    // Если обложки нет, оставляем иконку (не трогаем)
  }
  $('album-suggestions').classList.add('hidden');
}

// ─── LIBRARY VIEW ─────────────────────────────────────────────────────────────
let myMusicTracksAll = [];
let myMusicTracksRaw = [];
let myMusicQKeyForTracks = null;
let myMusicTracksExpanded = false;
let myMusicAlbumsAll = [];
let myMusicArtistsAll = [];
let myMusicTracksSortMode = 'recent';
let myMusicDislikedAll = [];
let myMusicQKeyForDisliked = null;
let myMusicDislikedExpanded = false;

const LIKED_TRACKS_SORT_LABELS = {
  recent: 'Сначала новые',
  alpha: 'По названию',
  artist: 'По исполнителю',
  popular: 'По популярности'
};

function loadLikedTracksSortMode() {
  try {
    const saved = localStorage.getItem('mxm_lib_tracks_sort');
    if (saved && LIKED_TRACKS_SORT_LABELS[saved]) return saved;
  } catch (_) {}
  return 'recent';
}

function saveLikedTracksSortMode(mode) {
  try {
    localStorage.setItem('mxm_lib_tracks_sort', mode);
  } catch (_) {}
}

function sortLikedTracksList(tracks, mode) {
  const list = Array.isArray(tracks) ? tracks.slice() : [];
  if (mode === 'alpha') {
    return list.sort((a, b) =>
      String(a.title || '').localeCompare(String(b.title || ''), 'ru', { sensitivity: 'base' })
    );
  }
  if (mode === 'artist') {
    return list.sort(
      (a, b) =>
        String(getTrackArtistText(a) || a.artist || '').localeCompare(
          String(getTrackArtistText(b) || b.artist || ''),
          'ru',
          { sensitivity: 'base' }
        ) ||
        String(a.title || '').localeCompare(String(b.title || ''), 'ru', { sensitivity: 'base' })
    );
  }
  if (mode === 'popular') {
    return list.sort((a, b) => (Number(b.plays) || 0) - (Number(a.plays) || 0));
  }
  // recent — порядок избранного (новые лайки сверху)
  return list;
}

function applyLikedTracksSort(mode, opts = {}) {
  const next =
    mode && LIKED_TRACKS_SORT_LABELS[mode] ? mode : loadLikedTracksSortMode() || 'recent';
  myMusicTracksSortMode = next;
  saveLikedTracksSortMode(next);
  const sorted = sortLikedTracksList(myMusicTracksRaw, next);
  myMusicTracksAll = sorted;
  myMusicQKeyForTracks = storeQueue(sorted);

  const label = $('liked-tracks-sort-label');
  if (label) label.textContent = LIKED_TRACKS_SORT_LABELS[next] || LIKED_TRACKS_SORT_LABELS.recent;
  document.querySelectorAll('#liked-tracks-sort-menu button[data-sort]').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.sort === next);
  });

  if (opts.rerenderList !== false) {
    const host = $('my-music-tracks-list');
    if (host) {
      const inp = $('lib-local-search');
      if (inp && String(inp.value || '').trim()) {
        filterLibraryLocalSearch({ target: inp });
      } else {
        host.innerHTML = renderMyMusicTracksGridHtml();
      }
    } else if (state.view === 'my-tracks') {
      const listRoot = $('my-tracks-full-list') || document.querySelector('.track-list');
      if (listRoot) {
        listRoot.innerHTML = sorted.length
          ? sorted.map((t, i) => trackRowCover(t, myMusicQKeyForTracks, i)).join('')
          : '';
      }
    }
  }
  return sorted;
}

function likedTracksSortControlsHtml() {
  const mode = myMusicTracksSortMode || loadLikedTracksSortMode();
  const label = LIKED_TRACKS_SORT_LABELS[mode] || LIKED_TRACKS_SORT_LABELS.recent;
  return `
    <div class="artist-sort-wrap liked-tracks-sort-wrap" id="liked-tracks-sort-wrap">
      <button class="btn-ghost artist-sort-btn lib-section-filter-btn" id="liked-tracks-sort-btn" type="button" aria-haspopup="true" aria-expanded="false" title="Сортировка" aria-label="Сортировка треков">
        <span id="liked-tracks-sort-label">${esc(label)}</span>
        <span class="artist-sort-caret" aria-hidden="true">▾</span>
      </button>
      <div class="artist-sort-menu hidden" id="liked-tracks-sort-menu" role="menu">
        <button type="button" data-sort="recent" role="menuitem"${mode === 'recent' ? ' class="active"' : ''}>Сначала новые</button>
        <button type="button" data-sort="alpha" role="menuitem"${mode === 'alpha' ? ' class="active"' : ''}>По названию</button>
        <button type="button" data-sort="artist" role="menuitem"${mode === 'artist' ? ' class="active"' : ''}>По исполнителю</button>
        <button type="button" data-sort="popular" role="menuitem"${mode === 'popular' ? ' class="active"' : ''}>По популярности</button>
      </div>
    </div>
  `;
}

function bindLikedTracksSortUI(root) {
  const scope = root || document;
  const sortWrap = scope.querySelector ? scope.querySelector('#liked-tracks-sort-wrap') : $('liked-tracks-sort-wrap');
  const sortBtn = scope.querySelector ? scope.querySelector('#liked-tracks-sort-btn') : $('liked-tracks-sort-btn');
  const sortMenu = scope.querySelector ? scope.querySelector('#liked-tracks-sort-menu') : $('liked-tracks-sort-menu');
  if (!sortBtn || !sortMenu) return;

  sortBtn.onclick = (e) => {
    e.stopPropagation();
    const open = sortMenu.classList.toggle('hidden') === false;
    sortBtn.classList.toggle('active', open);
    sortBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  sortMenu.querySelectorAll('button[data-sort]').forEach((option) => {
    option.onclick = (e) => {
      e.stopPropagation();
      applyLikedTracksSort(option.dataset.sort || 'recent');
      sortMenu.classList.add('hidden');
      sortBtn.classList.remove('active');
      sortBtn.setAttribute('aria-expanded', 'false');
    };
  });

  if (!bindLikedTracksSortUI._docBound) {
    bindLikedTracksSortUI._docBound = true;
    document.addEventListener('click', (e) => {
      const wrap = $('liked-tracks-sort-wrap');
      const menu = $('liked-tracks-sort-menu');
      const btn = $('liked-tracks-sort-btn');
      if (!wrap || !menu || !btn) return;
      if (e.target.closest('#liked-tracks-sort-wrap')) return;
      menu.classList.add('hidden');
      btn.classList.remove('active');
      btn.setAttribute('aria-expanded', 'false');
    });
  }
}

function renderMyMusicTracksGridHtml() {
  if (!Array.isArray(myMusicTracksAll) || !myMusicTracksAll.length) {
    return `<div class="lib-empty" style="padding: 24px 12px;">
      <p style="color: var(--text-2); padding: 0;">Начните слушать, и сюда попадут треки.</p>
    </div>`;
  }
  const total = myMusicTracksAll.length;
  const limit = myMusicTracksExpanded ? total : Math.min(6, total);
  const visible = myMusicTracksAll.slice(0, limit);
  return `
    <div class="my-music-tracks-grid">
      ${visible.map((t, i) => trackRowCover(t, myMusicQKeyForTracks, i)).join('')}
    </div>
  `;
}

function dislikedTrackRowHtml(track, qKey, qIdx) {
  const isPlaying = state.currentTrack?.id === track.id;
  return `
    <div class="track-row track-row-w-cover lib-disliked-row ${isPlaying ? 'playing' : ''}" data-track-id="${track.id}" data-q-key="${qKey}" data-q-idx="${qIdx}" onclick="handleTrackRowPlayClick(event,'${qKey}',${qIdx})">
      <div class="track-cover-sm" style="position: relative;">
        ${coverImg(track.coverUrl, track.coverGradient || pickGrad(track.id))}
        <button class="track-cover-play" onclick="event.stopPropagation(); playFromQueue('${qKey}',${qIdx})" aria-label="Играть">
          <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </button>
      </div>
      <div class="track-info">
        <div class="track-title">${esc(track.title)}</div>
        <div class="track-meta">${renderTrackMetaLineHtml(track)}</div>
      </div>
      <div class="track-right">
        <button type="button" class="lib-undislike-btn" onclick="event.stopPropagation();undislikeTrackFromLibrary('${track.id}')" title="Вернуть в рекомендации" aria-label="Вернуть в рекомендации">
          Вернуть
        </button>
        <span class="track-duration">${fmtTime(track.duration)}</span>
      </div>
    </div>
  `;
}

function renderMyMusicDislikedGridHtml(list) {
  const tracks = Array.isArray(list) ? list : myMusicDislikedAll;
  if (!tracks.length) {
    return `<div class="lib-empty" style="padding: 24px 12px;">
      <p style="color: var(--text-2); padding: 0;">Скрытых треков нет. Если отметить трек как «не нравится» в плеере — он появится здесь.</p>
    </div>`;
  }
  return `
    <div class="my-music-tracks-grid lib-disliked-grid">
      ${tracks.map((t, i) => dislikedTrackRowHtml(t, myMusicQKeyForDisliked, i)).join('')}
    </div>
  `;
}

function renderLibDislikedSegmentHtml(list) {
  const tracks = Array.isArray(list) ? list : myMusicDislikedAll || [];
  return `
    <section class="section my-music-section lib-disliked-section">
      <p class="lib-disliked-hint">Эти треки не попадают в случайные подборки, а другие треки тех же артистов рекомендуются реже. Можно вернуть в рекомендации.</p>
      <div id="my-music-disliked-list">${renderMyMusicDislikedGridHtml(tracks)}</div>
    </section>`;
}

async function undislikeTrackFromLibrary(trackId) {
  if (!state.user || !trackId) return;
  try {
    await DEL(`/api/me/disliked-tracks/${encodeURIComponent(trackId)}`);
    state.favorites.dislikedTrackIds = (state.favorites.dislikedTrackIds || []).filter(
      (id) => String(id) !== String(trackId)
    );
    myMusicDislikedAll = (myMusicDislikedAll || []).filter((t) => String(t.id) !== String(trackId));
    myMusicQKeyForDisliked = storeQueue(myMusicDislikedAll);
    const list = $('my-music-disliked-list');
    if (list) list.innerHTML = renderMyMusicDislikedGridHtml();
    const countEl = document.querySelector('#lib-tab-disliked .lib-count');
    if (countEl) countEl.textContent = String(myMusicDislikedAll.length);
    if (typeof refreshPlayerDislikeUi === 'function') refreshPlayerDislikeUi();
    toast('Трек снова в рекомендациях', 'success');
  } catch (e) {
    toast(e.message || 'Не удалось вернуть', 'error');
  }
}

function toggleMyMusicTracksExpanded() {
  if (!Array.isArray(myMusicTracksAll) || myMusicTracksAll.length <= 6) return;
  myMusicTracksExpanded = !myMusicTracksExpanded;

  const arrow = $('my-music-tracks-arrow');
  if (arrow) arrow.textContent = myMusicTracksExpanded ? '↑' : '→';

  const list = $('my-music-tracks-list');
  if (list) list.innerHTML = renderMyMusicTracksGridHtml();
}

function scrollToMyMusicPlaylistsSection() {
  ($('lib-seg-playlists') || $('my-music-playlists-section'))?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}

function myMusicAddPlaylistCardHtml() {
  return `
    <div class="featured-card upl-card my-music-add-playlist-card my-music-add-playlist-card--glass"
      role="button" tabindex="0" onclick="createUserPlaylist()" aria-label="Создать плейлист">
      <div class="featured-bg my-music-add-playlist-card-bg">
        <div class="featured-cover-fallback my-music-add-playlist-fallback" aria-hidden="true"></div>
      </div>
      <div class="my-music-add-playlist-plus" aria-hidden="true">+</div>
      <div class="my-music-add-playlist-text">Новый плейлист</div>
    </div>
  `;
}

function myMusicAddPlaylistRowHtml() {
  return `
    <div class="my-music-entity-row my-music-add-playlist-row my-music-add-playlist-row--glass" role="button" tabindex="0"
      onclick="createUserPlaylist()" aria-label="Создать плейлист">
      <div class="my-music-add-playlist-row-cover my-music-add-playlist-row-cover--glass" aria-hidden="true">
        <span class="my-music-add-playlist-row-plus">+</span>
      </div>
      <div class="my-music-entity-info">
        <div class="my-music-entity-title">Новый плейлист</div>
        <div class="my-music-entity-sub">Создать плейлист</div>
      </div>
    </div>
  `;
}

function myMusicAlbumListRowHtml(a) {
  const grad = a.coverGradient || pickGrad(a.id);
  const n = albumTrackCountForUi(a);
  const singleBit = n === 1 ? ' · Сингл' : '';
  return `
    <div class="my-music-entity-row my-music-album-row"
      data-nav="album" data-nav-id="${a.id}" onclick="handleDataNavClick(event)">
      <div class="my-music-entity-cover">${coverImg(a.coverUrl, grad)}</div>
      <div class="my-music-entity-info">
        <div class="my-music-entity-title">${esc(a.title)}</div>
        <div class="my-music-entity-sub">${esc(getAlbumPrimaryArtistLabel(a))}${singleBit}</div>
      </div>
      <div class="my-music-entity-chevron" aria-hidden="true">›</div>
    </div>
  `;
}

function myMusicPlaylistListRowHtml(p) {
  const grad = p.coverGradient || pickGrad(p.id);
  const owner = p.ownerUsername ? `@${esc(p.ownerUsername)}` : '';
  return `
    <div class="my-music-entity-row my-music-playlist-row upl-card"
      data-nav="user-playlist" data-nav-id="${p.id}" onclick="handleDataNavClick(event)" title="${esc(p.title)}">
      <div class="my-music-entity-cover">${coverImg(p.coverUrl, grad)}</div>
      <div class="my-music-entity-info">
        <div class="my-music-entity-title">${esc(p.title)}</div>
        ${owner ? `<div class="my-music-entity-sub">${owner}</div>` : ''}
      </div>

      <button type="button" class="upl-more-btn"
        onclick="event.stopPropagation();toggleUserPlaylistCardMenu('${p.id}', this)" aria-label="Меню">
        <svg viewBox="0 0 24 24" width="18" height="18">
          <circle cx="12" cy="5" r="2" fill="currentColor"/>
          <circle cx="12" cy="12" r="2" fill="currentColor"/>
          <circle cx="12" cy="19" r="2" fill="currentColor"/>
        </svg>
      </button>

      <div class="upl-menu hidden" id="upl-menu-${p.id}">
        <button type="button" onclick="event.stopPropagation();openUserPlaylistEditMetaModal('${p.id}')">Название и описание</button>
        <button type="button" onclick="event.stopPropagation();copyUserPlaylistShareLink('${p.id}');closeAllUserPlaylistMenus()"><span class="upl-menu-ic" aria-hidden="true">${MXM_SHARE_ICON_SVG.replace('<svg ', '<svg width="16" height="16" ')}</span>Поделиться</button>
        <button type="button" onclick="event.stopPropagation();toggleUserPlaylistShare('${p.id}', ${p.isShared ? 'true' : 'false'});closeAllUserPlaylistMenus()">
          ${p.isShared ? 'Снять шаринг' : 'Расшарить'}
        </button>
        <button type="button" onclick="event.stopPropagation();openUserPlaylistCoverUploadModal('${p.id}')">Сменить обложку</button>
        <button type="button" class="danger" onclick="event.stopPropagation();deleteUserPlaylist('${p.id}');closeAllUserPlaylistMenus()">Удалить</button>
      </div>
    </div>
  `;
}

function myMusicAlbumsStripHtml(albums) {
  if (!Array.isArray(albums) || !albums.length) {
    return `
      <div class="lib-empty">
        <div class="lib-empty-icon"><svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 13h6"/></svg></div>
        <h3>Нет любимых альбомов</h3><p>Нажмите ♥ на любом альбоме</p>
      </div>`;
  }
  return `
    <div class="playlist-home-carousel">
      <div class="playlist-home-row" id="my-music-albums-strip">
        ${albums.map((a) => albumCard(a)).join('')}
      </div>
    </div>
  `;
}

function myMusicAlbumsListHtml(albums) {
  if (!Array.isArray(albums) || !albums.length) {
    return `
      <div class="lib-empty">
        <div class="lib-empty-icon"><svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 13h6"/></svg></div>
        <h3>Нет любимых альбомов</h3><p>Нажмите ♥ на любом альбоме</p>
      </div>`;
  }
  return `
    <div class="my-music-entity-list" id="my-music-albums-list">
      ${albums.map((a) => myMusicAlbumListRowHtml(a)).join('')}
    </div>
  `;
}

function myMusicPlaylistsStripHtml(playlists) {
  const cards = Array.isArray(playlists) ? playlists.map((p) => userPlaylistSquareCard(p, { mine: true })).join('') : '';
  return `
    <div class="playlist-home-carousel">
      <div class="playlist-home-row" id="my-music-playlists-strip">
        ${cards}
        ${myMusicAddPlaylistCardHtml()}
      </div>
    </div>
  `;
}

function myMusicPlaylistsListHtml(playlists) {
  const rows = Array.isArray(playlists) && playlists.length
    ? playlists.map((p) => myMusicPlaylistListRowHtml(p)).join('')
    : '';
  return `
    <div class="my-music-entity-list" id="my-music-playlists-list">
      ${rows}
      ${myMusicAddPlaylistRowHtml()}
    </div>
  `;
}

/**
 * API отдаёт события от новых к старым. Подряд один и тот же трек — одна запись + streakCount.
 */
function collapseConsecutivePlayHistoryEvents(events) {
  const list = (events || []).filter((ev) => ev?.track?.id && ev?.track?.audioUrl);
  if (!list.length) return [];
  const out = [];
  let i = 0;
  while (i < list.length) {
    const tid = list[i].track.id;
    let n = 1;
    while (i + n < list.length && list[i + n].track.id === tid) n++;
    out.push({
      track: list[i].track,
      playedAt: list[i].playedAt,
      streakCount: n
    });
    i += n;
  }
  return out;
}

function playHistoryTotalPlays(collapsed) {
  return (collapsed || []).reduce((sum, e) => sum + (Number(e.streakCount) || 1), 0);
}

function pluralPlayHistoryPlaysRu(n) {
  const x = Math.abs(Number(n)) | 0;
  const k = x % 100;
  const d = x % 10;
  if (k > 10 && k < 20) return 'прослушиваний';
  if (d === 1) return 'прослушивание';
  if (d >= 2 && d <= 4) return 'прослушивания';
  return 'прослушиваний';
}

function pluralPlayHistoryRecordsRu(n) {
  const x = Math.abs(Number(n)) | 0;
  const k = x % 100;
  const d = x % 10;
  if (k > 10 && k < 20) return 'записей';
  if (d === 1) return 'запись';
  if (d >= 2 && d <= 4) return 'записи';
  return 'записей';
}

const RU_MONTHS_GEN = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря'
];
const RU_WEEKDAYS = [
  'воскресенье',
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота'
];

function _startOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function _ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * @param {string|Date} playedAt
 */
function formatPlayHistoryDateHeading(playedAt) {
  const d = new Date(playedAt);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const today0 = _startOfLocalDay(now);
  const d0 = _startOfLocalDay(d);
  const diff = Math.round((today0 - d0) / 86400000);
  const day = d.getDate();
  const mon = RU_MONTHS_GEN[d.getMonth()];
  if (diff === 0) return `Сегодня, ${day} ${mon}`;
  if (diff === 1) return `Вчера, ${day} ${mon}`;
  if (diff === 2) return `Позавчера, ${day} ${mon}`;
  const w = RU_WEEKDAYS[d.getDay()];
  const wcap = w.charAt(0).toUpperCase() + w.slice(1);
  if (d.getFullYear() === now.getFullYear()) {
    return `${wcap}, ${day} ${mon}`;
  }
  return `${day} ${mon} ${d.getFullYear()}`;
}

/**
 * @param {Array<{ track: any, playedAt: string, streakCount: number }>} collapsed
 * @returns {{ ymd: string, label: string, items: typeof collapsed }[]}
 */
function groupPlayHistoryByDate(collapsed) {
  if (!Array.isArray(collapsed) || !collapsed.length) return [];
  const groups = new Map();
  for (const ev of collapsed) {
    const t = new Date(ev.playedAt);
    if (Number.isNaN(t.getTime())) continue;
    const key = _ymd(t);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(ev);
  }
  const keys = [...groups.keys()].sort((a, b) => b.localeCompare(a));
  return keys.map((ymd) => {
    const items = groups.get(ymd);
    const firstAt = items[0].playedAt;
    return { ymd, label: formatPlayHistoryDateHeading(firstAt), items };
  });
}

/**
 * @param {Array<{ track: any, playedAt: string, streakCount: number }>} collapsed
 */
function renderPlayHistoryGroupedRowsHtml(collapsed) {
  const groups = groupPlayHistoryByDate(collapsed);
  if (!groups.length) {
    return `<div class="lib-empty" style="padding: 24px 12px;">
        <p style="color: var(--text-2); padding: 0;">История пока пуста</p>
      </div>`;
  }
  return groups
    .map(
      (g) => `<div class="play-history-day-group" data-ymd="${esc(g.ymd)}">
      <h3 class="play-history-day-heading">${esc(g.label)}</h3>
      <div class="play-history-day-tracks">
        ${g.items
          .map((ev) => {
            const track = ev.track;
            const singleKey = storeQueue([track]);
            const rowOpts = ev.streakCount > 2 ? { playHistoryStreak: ev.streakCount } : null;
            return trackRowCover(track, singleKey, 0, rowOpts);
          })
          .join('')}
      </div>
    </div>`
    )
    .join('');
}

function collectLibraryArtistsFromFavorites(tracks, albums) {
  const byId = new Map();
  for (const t of tracks) {
    for (const a of getTrackArtists(t)) {
      if (a.id && !byId.has(a.id)) {
        byId.set(a.id, { id: a.id, name: a.name, genre: '', photoUrl: null, photoGradient: null });
      }
    }
  }
  for (const al of albums) {
    const creds = al.coverArtistCredits;
    if (Array.isArray(creds) && creds.length) {
      for (const c of creds) {
        if (c.id && !byId.has(c.id)) {
          byId.set(c.id, {
            id: c.id,
            name: c.name,
            genre: al.genre || '',
            photoUrl: c.photoUrl || null,
            photoGradient: c.photoGradient || null
          });
        }
      }
    } else {
      const ids = Array.isArray(al.artistIds) ? al.artistIds : al.artistId ? [al.artistId] : [];
      const primary = getAlbumPrimaryArtistLabel(al);
      const aid = ids[0];
      if (aid && !byId.has(aid)) {
        byId.set(aid, { id: aid, name: primary, genre: al.genre || '', photoUrl: null, photoGradient: null });
      }
    }
  }
  const catalogById = new Map((Array.isArray(allArtists) ? allArtists : []).map((a) => [a.id, a]));
  return [...byId.values()]
    .map((stub) => {
      const full = catalogById.get(stub.id);
      if (!full) return stub;
      return {
        ...stub,
        name: full.name || stub.name,
        genre: full.genre || stub.genre,
        photoUrl: full.photoUrl || stub.photoUrl,
        photoGradient: full.photoGradient || stub.photoGradient
      };
    })
    .sort((a, b) => String(a.name).localeCompare(String(b.name), 'ru'));
}

/** Исполнители, отмеченные сердечком, попадают в медиатеку даже без треков/альбомов в избранном. */
function mergeFavArtistsIntoLibrary(baseList, favArtists) {
  const byId = new Map(baseList.map((a) => [a.id, a]));
  for (const a of favArtists || []) {
    if (a && a.id && !byId.has(a.id)) byId.set(a.id, a);
  }
  return [...byId.values()].sort((a, b) => String(a.name).localeCompare(String(b.name), 'ru'));
}

function filterLibraryLocalSearch(ev) {
  const q = String(ev?.target?.value || '').trim().toLowerCase();
  const seg = state.libraryTab || 'tracks';
  const root = $('lib-seg-' + seg);
  if (!root) return;
  if (seg === 'tracks') {
    const host = $('my-music-tracks-list');
    if (!host) return;
    if (!q) {
      host.innerHTML = renderMyMusicTracksGridHtml();
      return;
    }
    const matched = (myMusicTracksAll || []).filter((t) =>
      `${t.title || ''} ${getTrackArtistText(t)} ${t.album || ''}`.toLowerCase().includes(q)
    );
    host.innerHTML = matched.length
      ? `<div class="my-music-tracks-grid">${matched
          .map((t, i) => trackRowCover(t, myMusicQKeyForTracks, i))
          .join('')}</div>`
      : `<div class="lib-empty" style="padding: 24px 12px;"><p style="color: var(--text-2); padding: 0;">Ничего не найдено</p></div>`;
    return;
  }
  if (seg === 'disliked') {
    const list = myMusicDislikedAll || [];
    const matched = !q
      ? list
      : list.filter((t) =>
          `${t.title || ''} ${getTrackArtistText(t)} ${t.album || ''}`.toLowerCase().includes(q)
        );
    root.innerHTML = renderLibDislikedSegmentHtml(matched);
    return;
  }
  if (seg === 'albums') {
    const list = myMusicAlbumsAll || [];
    const matched = !q
      ? list
      : list.filter((a) =>
          `${a.title || ''} ${getAlbumPrimaryArtistLabel(a)} ${a.artist || ''}`.toLowerCase().includes(q)
        );
    root.innerHTML = isMobile() ? myMusicAlbumsListHtml(matched) : myMusicAlbumsStripHtml(matched);
    return;
  }
  if (seg === 'artists') {
    const list = myMusicArtistsAll || [];
    const matched = !q
      ? list
      : list.filter((a) => `${a.name || ''} ${a.genre || ''}`.toLowerCase().includes(q));
    root.innerHTML = matched.length
      ? `<div class="artists-grid">${matched.map((a) => artistCard(a)).join('')}</div>`
      : `<div class="lib-empty"><p style="color:var(--text-2)">Ничего не найдено</p></div>`;
    return;
  }
  if (seg === 'playlists') {
    const source = Array.isArray(state.mePlaylists) ? state.mePlaylists : [];
    const matched = !q
      ? source
      : source.filter((p) =>
          `${p.title || ''} ${p.description || ''} ${p.ownerUsername || ''}`.toLowerCase().includes(q)
        );
    root.innerHTML = isMobile() ? myMusicPlaylistsListHtml(matched) : myMusicPlaylistsStripHtml(matched);
    return;
  }
}

function libraryInitTrackRowSwipe() {
  const content = $('content');
  if (!content || content.dataset.mxmLibTrackSwipe === '1') return;
  content.dataset.mxmLibTrackSwipe = '1';
  const TH = 72;
  let s = null;
  content.addEventListener(
    'pointerdown',
    (e) => {
      if (state.view !== 'library' || window.innerWidth > 900) return;
      if (e.target.closest('button, a, .track-fav-btn, .track-more-btn, .track-cover-play')) return;
      const row = e.target.closest('.library-page .my-music-tracks-grid .track-row');
      if (!row) return;
      s = { row, x0: e.clientX, pid: e.pointerId, id: row.getAttribute('data-track-id') };
      try {
        row.setPointerCapture(e.pointerId);
      } catch (_) {}
    },
    true
  );
  content.addEventListener(
    'pointerup',
    (e) => {
      if (!s || e.pointerId !== s.pid) return;
      const dx = e.clientX - s.x0;
      const id = s.id;
      const row = s.row;
      s = null;
      try {
        row.releasePointerCapture(e.pointerId);
      } catch (_) {}
      if (dx > TH && id) {
        const t = (myMusicTracksAll || []).find((x) => String(x.id) === String(id));
        if (t && typeof addTrackPlayNext === 'function') {
          addTrackPlayNext(t);
          toast('В очереди', 'success');
        }
      }
    },
    true
  );
}

async function renderLibrary() {
  const content = $('content');
  if (!state.user) {
    content.innerHTML = `
      <div class="lib-empty">
        <div class="lib-empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572"/></svg>
        </div>
        <h3>Войдите в аккаунт</h3>
        <p>Чтобы видеть свою музыку и любимые треки</p>
        <button class="btn-primary" style="margin-top:16px" onclick="openAuthModal('login')">Войти</button>
      </div>`;
    return;
  }
  content.innerHTML = getViewLoadingSkeleton('library');
  if (!allArtists.length) await loadAllArtists();
  const data = await GET('/api/favorites');
  const tracksRaw = (data.tracks || []).reverse();
  const albums = data.albums || [];
  myMusicTracksSortMode = loadLikedTracksSortMode();
  myMusicTracksRaw = tracksRaw;
  const tracks = applyLikedTracksSort(myMusicTracksSortMode, { rerenderList: false });
  const qKey = myMusicQKeyForTracks;

  // User playlists
  let myPlaylists = [];
  try { myPlaylists = await GET('/api/me/user-playlists'); } catch {}
  state.mePlaylists = Array.isArray(myPlaylists) ? myPlaylists : [];
  state.renderPlaylistAddButtons = true;

  myMusicTracksAll = tracks;
  myMusicQKeyForTracks = qKey;
  myMusicTracksExpanded = false;

  const dislikedRaw = Array.isArray(data.dislikedTracks) ? data.dislikedTracks : [];
  myMusicDislikedAll = [...dislikedRaw].reverse();
  myMusicQKeyForDisliked = storeQueue(myMusicDislikedAll);
  myMusicDislikedExpanded = false;
  if (Array.isArray(data.dislikedTrackIds)) {
    state.favorites.dislikedTrackIds = data.dislikedTrackIds;
  }

  // История прослушивания (последние 30 дней)
  let playHistoryEvents = [];
  try {
    const d = await GET('/api/me/play-history?limit=120');
    playHistoryEvents = Array.isArray(d?.events) ? d.events : [];
  } catch (_) {
    playHistoryEvents = [];
  }

  const historyCollapsed = collapseConsecutivePlayHistoryEvents(playHistoryEvents);

  await Promise.all(state.mePlaylists.map((p) => prefetchUserPlaylistShareToken(p.id)));

  const historyPreview = historyCollapsed.slice(0, 6);
  const historyRowsPreviewHtml = historyPreview.length
    ? renderPlayHistoryGroupedRowsHtml(historyPreview)
    : `<div class="lib-empty" style="padding: 24px 12px;">
        <p style="color: var(--text-2); padding: 0;">История пока пуста</p>
      </div>`;

  const libArtists = mergeFavArtistsIntoLibrary(
    collectLibraryArtistsFromFavorites(tracks, albums),
    data.artists || []
  );
  myMusicAlbumsAll = albums;
  myMusicArtistsAll = libArtists;
  let initialTab = state.libraryTab;
  if (state.view === 'library') {
    try {
      const saved = localStorage.getItem('mxm_lib_tab');
      if (!initialTab && saved) initialTab = saved;
    } catch (_) {}
  }
  if (!['tracks', 'albums', 'artists', 'playlists', 'disliked'].includes(initialTab)) initialTab = 'tracks';
  const plCount = state.mePlaylists.length;
  state.libraryTab = initialTab;
  const plTabClass = 'lib-tab';
  const dislikedCount = myMusicDislikedAll.length;

  content.innerHTML = `
    <div class="library-page">
      <h1 class="view-title">Медиатека</h1>
      <p class="view-subtitle">Избранное и ваши плейлисты в одном месте</p>
      <p class="lib-sync-entry"><a href="#sync" class="lib-sync-entry-link">Синхронизация с Яндекс.Музыкой →</a></p>

      <div class="lib-toolbar">
        <input type="search" id="lib-local-search" class="lib-local-search" placeholder="Поиск в медиатеке…"
          autocomplete="off" oninput="filterLibraryLocalSearch(event)" aria-label="Поиск в медиатеке" />
      </div>

      <div class="lib-tabs" role="tablist" aria-label="Разделы медиатеки">
        <button type="button" class="lib-tab" role="tab" data-tab="tracks" id="lib-tab-tracks" aria-pressed="false" onclick="switchLibTab('tracks')">Треки <span class="lib-count">${tracks.length}</span></button>
        <button type="button" class="lib-tab" role="tab" data-tab="albums" id="lib-tab-albums" aria-pressed="false" onclick="switchLibTab('albums')">Альбомы <span class="lib-count">${albums.length}</span></button>
        <button type="button" class="lib-tab" role="tab" data-tab="artists" id="lib-tab-artists" aria-pressed="false" onclick="switchLibTab('artists')">Исполнители <span class="lib-count">${libArtists.length}</span></button>
        <button type="button" class="${plTabClass}" role="tab" data-tab="playlists" id="lib-tab-playlists" aria-pressed="false" onclick="switchLibTab('playlists')">Плейлисты <span class="lib-count">${plCount}</span></button>
        <button type="button" class="lib-tab" role="tab" data-tab="disliked" id="lib-tab-disliked" aria-pressed="false" onclick="switchLibTab('disliked')">Скрытые <span class="lib-count">${dislikedCount}</span></button>
      </div>
      ${
        plCount === 0
          ? `<p class="lib-playlists-hint" id="lib-playlists-hint" role="status">Создайте первый плейлист в разделе «Плейлисты» с компьютера или позже с телефона.</p>`
          : ''
      }

      <div id="lib-seg-tracks" class="lib-segment ${initialTab !== 'tracks' ? 'hidden' : ''}">
        <section class="section my-music-section">
          <div class="section-header lib-tracks-section-header">
            <h2 class="section-title lib-sticky-tracks-title" style="margin-bottom:0;">
              <span class="section-title-clickable" role="button" tabindex="0"
                onclick="navigate('my-tracks')"
                onkeydown="if(event.key==='Enter' || event.key===' '){navigate('my-tracks')}">
                Все треки <span class="section-title-arrow" aria-hidden="true">→</span>
              </span>
            </h2>
            ${likedTracksSortControlsHtml()}
          </div>
          <div id="my-music-tracks-list">${renderMyMusicTracksGridHtml()}</div>
        </section>
        <section class="section my-music-section" id="my-music-history-section">
          <div class="section-header">
            <h2 class="section-title" style="margin-bottom:0;">
              <span class="section-title-clickable" role="button" tabindex="0"
                onclick="navigate('my-listen-history')"
                onkeydown="if(event.key==='Enter' || event.key===' '){navigate('my-listen-history')}">
                История прослушивания <span class="section-title-arrow">→</span>
              </span>
              <span class="lib-count">${historyCollapsed.length}</span>
            </h2>
          </div>
          <div class="track-list play-history-list" id="my-music-history-list">${historyRowsPreviewHtml}</div>
        </section>
      </div>

      <div id="lib-seg-albums" class="lib-segment ${initialTab !== 'albums' ? 'hidden' : ''}">
        ${isMobile() ? myMusicAlbumsListHtml(albums) : myMusicAlbumsStripHtml(albums)}
      </div>

      <div id="lib-seg-artists" class="lib-segment ${initialTab !== 'artists' ? 'hidden' : ''}">
        ${
          libArtists.length
            ? `<div class="artists-grid">${libArtists.map((a) => artistCard(a)).join('')}</div>`
            : `<div class="lib-empty"><p style="color:var(--text-2)">Добавьте треки и альбомы в избранное или нажмите ❤️ на карточке исполнителя</p></div>`
        }
      </div>

      <div id="lib-seg-playlists" class="lib-segment ${initialTab !== 'playlists' ? 'hidden' : ''}">
        ${isMobile() ? myMusicPlaylistsListHtml(state.mePlaylists) : myMusicPlaylistsStripHtml(state.mePlaylists)}
      </div>

      <div id="lib-seg-disliked" class="lib-segment ${initialTab !== 'disliked' ? 'hidden' : ''}">
        ${renderLibDislikedSegmentHtml(myMusicDislikedAll)}
      </div>
    </div>`;

  switchLibTab(initialTab);
  bindLikedTracksSortUI(content);
  setQueuePlayingFromLabel('Медиатека');
  libraryInitTrackRowSwipe();
}

async function renderMyListenHistory() {
  const content = $('content');
  if (!state.user) {
    content.innerHTML = `
      <div class="lib-empty">
        <div class="lib-empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572"/></svg>
        </div>
        <h3>Войдите в аккаунт</h3>
        <p>Чтобы видеть историю прослушивания</p>
        <button class="btn-primary" style="margin-top:16px" onclick="openAuthModal('login')">Войти</button>
      </div>`;
    return;
  }

  content.innerHTML = loadingSpinner();
  let playHistoryEvents = [];
  try {
    const d = await GET('/api/me/play-history?limit=200');
    playHistoryEvents = Array.isArray(d?.events) ? d.events : [];
  } catch (_) {
    playHistoryEvents = [];
  }

  const historyCollapsed = collapseConsecutivePlayHistoryEvents(playHistoryEvents);
  const totalPlays = playHistoryTotalPlays(historyCollapsed);

  const rowsHtml = historyCollapsed.length
    ? renderPlayHistoryGroupedRowsHtml(historyCollapsed)
    : `<div class="lib-empty" style="padding: 24px 12px;">
        <p style="color: var(--text-2); padding: 0;">История пока пуста</p>
      </div>`;

  content.innerHTML = `
    <div class="page-hero" style="--hero-grad:linear-gradient(${pickGrad('my-listen-history')})">
      <button class="nav-back-btn" onclick="goBack()" title="Назад">
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
      </button>
      <div class="hero-bg"></div>
      <div class="hero-content">
        <div class="hero-info">
          <div class="hero-type">Моя музыка</div>
          <h1 class="hero-title">История прослушивания</h1>
          <div class="hero-sub">Последние 30 дней · ${totalPlays} ${pluralPlayHistoryPlaysRu(totalPlays)} · ${historyCollapsed.length} ${pluralPlayHistoryRecordsRu(historyCollapsed.length)}</div>
        </div>
      </div>
    </div>
    <div class="track-list play-history-list">${rowsHtml}</div>
  `;

  setQueuePlayingFromLabel('История');
}

async function renderMyTracks() {
  const content = $('content');
  if (!state.user) {
    content.innerHTML = `
      <div class="lib-empty">
        <div class="lib-empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572"/></svg>
        </div>
        <h3>Войдите в аккаунт</h3>
        <p>Чтобы видеть свою музыку и любимые треки</p>
        <button class="btn-primary" style="margin-top:16px" onclick="openAuthModal('login')">Войти</button>
      </div>`;
    return;
  }

  content.innerHTML = loadingSpinner();
  const data = await GET('/api/favorites');
  const tracksRaw = (data.tracks || []).reverse();
  myMusicTracksSortMode = loadLikedTracksSortMode();
  myMusicTracksRaw = tracksRaw;
  const tracks = applyLikedTracksSort(myMusicTracksSortMode, { rerenderList: false });
  const qKey = myMusicQKeyForTracks;

  myMusicTracksAll = tracks;
  myMusicQKeyForTracks = qKey;
  myMusicTracksExpanded = true;

  content.innerHTML = `
    <div class="page-hero" style="--hero-grad:linear-gradient(${pickGrad('my-tracks')})">
      <button class="nav-back-btn" onclick="goBack()" title="Назад">
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
      </button>
      <div class="hero-bg"></div>
      <div class="hero-content">
        <div class="hero-info">
          <div class="hero-type">Треки</div>
          <h1 class="hero-title">Все мои треки</h1>
          <div class="hero-sub">${tracks.length} ${pluralTracks(tracks.length)}</div>
        </div>
      </div>
    </div>

    <div class="section-header lib-tracks-section-header" style="margin: 8px 0 12px;">
      <h2 class="section-title" style="margin:0;">Список</h2>
      ${likedTracksSortControlsHtml()}
    </div>

    ${
      tracks.length
        ? `<div class="track-list" id="my-tracks-full-list">${tracks.map((t, i) => trackRowCover(t, qKey, i)).join('')}</div>`
        : `<div class="lib-empty">
            <div class="lib-empty-icon"><svg viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16"/></svg></div>
            <h3>Пока нет треков</h3>
            <p>Добавьте треки в избранное (♥)</p>
          </div>`
    }
  `;

  bindLikedTracksSortUI(content);
  setQueuePlayingFromLabel('Треки');
}

function switchLibTab(tab) {
  if (state.view !== 'library') return;
  state.libraryTab = tab;
  try {
    localStorage.setItem('mxm_lib_tab', tab);
  } catch (_) {}
  document.querySelectorAll('.lib-tab').forEach((t) => {
    const on = t.dataset.tab === tab;
    t.classList.toggle('active', on);
    t.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  ['tracks', 'albums', 'artists', 'playlists', 'disliked'].forEach((s) => {
    $('lib-seg-' + s)?.classList.toggle('hidden', tab !== s);
  });
  const inp = $('lib-local-search');
  if (inp && inp.value) filterLibraryLocalSearch({ target: inp });
}

// ─── User playlists (UI handlers) ──────────────────────────────────────────
function getUserPlaylistAccessTokenFromUrl() {
  try {
    return new URLSearchParams(location.search).get('upl') || '';
  } catch (_) {
    return '';
  }
}

function withUserPlaylistAccessToken(url) {
  const token = getUserPlaylistAccessTokenFromUrl();
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}upl=${encodeURIComponent(token)}`;
}

/** Токен шаринга для своих плейлистов — подгружаем заранее (не в обработчике клика). */
async function prefetchUserPlaylistShareToken(playlistId) {
  const id = String(playlistId || '').trim();
  if (!id || !state.user) return;
  if (state.uplShareTokenById[id]) return;
  try {
    const data = await GET(`/api/me/user-playlists/${id}/share-link-token`);
    if (data && data.token) state.uplShareTokenById[id] = data.token;
  } catch (_) {}
}

function warmUserPlaylistShareToken(playlistId) {
  void prefetchUserPlaylistShareToken(playlistId);
}

function buildUserPlaylistShareUrl(playlistId) {
  const pid = String(playlistId || '').trim();
  if (!pid) return '';
  let tokenPart = '';
  const cached = state.uplShareTokenById[pid];
  if (cached) {
    tokenPart = `?upl=${encodeURIComponent(cached)}`;
  } else {
    const uplFromBar = getUserPlaylistAccessTokenFromUrl();
    if (uplFromBar) tokenPart = `?upl=${encodeURIComponent(uplFromBar)}`;
  }
  return `${getPublicSiteBase()}${location.pathname}${tokenPart}#user-playlist/${pid}`;
}

/**
 * Ссылка на UPL. На iOS любой await до копирования ломает user activation — буфер не пишется.
 * Токен должен быть уже в state (prefetch при открытии страницы / списка).
 */
function copyUserPlaylistShareLink(id) {
  const pid = String(id || '').trim();
  if (!pid) return;
  const url = buildUserPlaylistShareUrl(pid);
  if (!url) return;

  if (copyPlainTextViaExecCommand(url)) {
    toast('Ссылка скопирована', 'success');
    return;
  }
  void copyPlainTextToClipboard(url).then((ok) => {
    if (ok) toast('Ссылка скопирована', 'success');
    else toast('Не удалось скопировать ссылку', 'error');
  });
}

async function createUserPlaylist() {
  openUserPlaylistCreateModal();
}

async function toggleUserPlaylistShare(playlistId, isCurrentlyShared) {
  const next = !isCurrentlyShared;
  try {
    await apiFetch('PUT', `/api/me/user-playlists/${playlistId}/share`, { isShared: next });
    toast(next ? 'Плейлист опубликован' : 'Плейлист скрыт', 'success');
    if (state.view === 'library') {
      await renderLibrary();
      scrollToMyMusicPlaylistsSection();
    }
    if (state.view === 'user-playlist') {
      const { params } = parseHash();
      if (params?.id) await syncUserPlaylistPageIfVisible(params.id);
    }
    if (state.view === 'browse') await renderBrowse();
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
}

async function ensureMyPlaylistsLoaded() {
  if (!state.user) return [];
  try {
    const my = await GET('/api/me/user-playlists');
    state.mePlaylists = Array.isArray(my) ? my : [];
    state.mePlaylists.forEach((p) => void prefetchUserPlaylistShareToken(p.id));
    return state.mePlaylists;
  } catch {
    state.mePlaylists = [];
    return [];
  }
}

function closeUserPlaylistModal(modalId) {
  const m = $(modalId);
  if (m) {
    if (m._coverObjectUrl) URL.revokeObjectURL(m._coverObjectUrl);
    m._coverObjectUrl = null;
    m._coverWebpBlob = null;
    m.classList.add('hidden');
  }
}

function openUserPlaylistCreateModal() {
  const modalId = 'user-playlist-create-modal';
  let modal = $(modalId);
  if (!modal) {
    modal = document.createElement('div');
    modal.id = modalId;
    modal.className = 'modal modal--center hidden';
    modal.innerHTML = `
      <div class="modal-overlay" onclick="closeUserPlaylistModal('${modalId}')"></div>
      <div class="modal-box">
        <button class="modal-close" type="button" onclick="closeUserPlaylistModal('${modalId}')">
          <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <div style="display:flex;flex-direction:column;gap:12px">
          <div>
            <div style="font-size:16px;font-weight:900;color:var(--text)">Создать плейлист</div>
            <div style="font-size:13.5px;color:var(--text-2);margin-top:6px">Введите название и готово.</div>
          </div>
          <div class="form-group">
            <label>Название</label>
            <input id="upl-create-title" type="text" placeholder="Например: Вечерний флоу" />
          </div>
          <div style="display:flex;gap:12px;align-items:center;justify-content:space-between">
            <div id="upl-create-cover-preview" style="width:56px;height:56px;border-radius:18px;overflow:hidden;border:1px solid rgba(255,255,255,.1)"></div>
            <button class="btn-primary" type="button" onclick="confirmCreateUserPlaylist()">Создать</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  const titleInput = $('upl-create-title');
  titleInput.value = '';
  const preview = $('upl-create-cover-preview');
  const grad = pickGrad('new-playlist');
  preview.innerHTML = coverImg(null, grad);
  titleInput.oninput = () => {
    const v = String(titleInput.value || '').trim();
    const g = v ? pickGrad(v) : grad;
    preview.innerHTML = coverImg(null, g);
  };

  modal.classList.remove('hidden');
  try {
    titleInput.focus({ preventScroll: true });
  } catch (_) {
    titleInput.focus();
  }
}

async function confirmCreateUserPlaylist() {
  const title = String($('upl-create-title')?.value || '').trim();
  if (!title) {
    toast('Введите название плейлиста', 'error');
    return;
  }
  try {
    const coverGradient = pickGrad(title);
    await POST('/api/me/user-playlists', { title, coverGradient });
    toast('Плейлист создан', 'success');
    closeUserPlaylistModal('user-playlist-create-modal');
    if (state.view === 'library') {
      await renderLibrary();
      scrollToMyMusicPlaylistsSection();
    }
    await ensureMyPlaylistsLoaded();
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
}

// (playlist actions moved to popover menu in cards)

function openDeleteUserPlaylistModal(playlistId) {
  const modalId = 'user-playlist-delete-modal';
  let modal = $(modalId);
  if (!modal) {
    modal = document.createElement('div');
    modal.id = modalId;
    modal.className = 'modal modal--center hidden';
    modal.innerHTML = `
      <div class="modal-overlay" onclick="closeUserPlaylistModal('${modalId}')"></div>
      <div class="modal-box">
        <button class="modal-close" type="button" onclick="closeUserPlaylistModal('${modalId}')">
          <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <div style="display:flex;flex-direction:column;gap:12px">
          <div>
            <div style="font-size:16px;font-weight:900;color:var(--text)">Удалить плейлист?</div>
            <div style="font-size:13.5px;color:var(--text-2);margin-top:6px">Это действие нельзя отменить.</div>
          </div>
          <div style="display:flex;gap:10px;justify-content:flex-end">
            <button class="btn-ghost" type="button" onclick="closeUserPlaylistModal('${modalId}')">Отмена</button>
            <button class="btn-primary" type="button" id="upl-del-confirm-btn">Удалить</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }
  const btn = $('upl-del-confirm-btn');
  if (btn) btn.onclick = () => deleteUserPlaylist(playlistId, { skipConfirm: true });
  modal.classList.remove('hidden');
}

async function deleteUserPlaylist(playlistId) {
  if (!playlistId) return;
  const opts = arguments[1] || {};
  if (!opts.skipConfirm) {
    openDeleteUserPlaylistModal(playlistId);
    return;
  }
  try {
    await apiFetch('DELETE', `/api/me/user-playlists/${playlistId}`);
    toast('Плейлист удалён', 'success');
    closeUserPlaylistModal('user-playlist-delete-modal');
    await ensureMyPlaylistsLoaded();
    if (state.view === 'library') {
      await renderLibrary();
      scrollToMyMusicPlaylistsSection();
    }
    if (state.view === 'user-playlist') navigate('browse');
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
}

async function addTrackToUserPlaylist(trackId) {
  if (!state.user) {
    toast('Войдите в аккаунт', 'error');
    return;
  }
  await ensureMyPlaylistsLoaded();
  openAddToPlaylistModal(trackId);
}

const MXM_MENU_BACKDROP_ID = 'mxm-menu-backdrop';

function ensureMxmMenuBackdropEl() {
  let el = $(MXM_MENU_BACKDROP_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = MXM_MENU_BACKDROP_ID;
    el.setAttribute('aria-hidden', 'true');
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      closeMxmCollectionPopovers();
      closeAllUserPlaylistMenus();
    });
    document.body.appendChild(el);
  }
  return el;
}

function openMxmMenuBackdrop() {
  if (!isMobile()) return;
  const el = ensureMxmMenuBackdropEl();
  requestAnimationFrame(() => el.classList.add('mxm-menu-backdrop--visible'));
}

function closeMxmMenuBackdrop() {
  const el = $(MXM_MENU_BACKDROP_ID);
  if (el) el.classList.remove('mxm-menu-backdrop--visible');
}

function clearMxmMenuSheetChrome(menuEl) {
  if (!menuEl) return;
  if (menuEl._mxmSheetDragAbo) {
    try {
      menuEl._mxmSheetDragAbo.abort();
    } catch (_) {}
    menuEl._mxmSheetDragAbo = null;
  }
  menuEl.classList.remove('mxm-menu-sheet', 'mxm-menu-sheet--dragging', 'mxm-menu-top');
  menuEl.style.transform = '';
  menuEl.style.removeProperty('transform');
  const h = menuEl.querySelector(':scope > .mxm-menu-sheet-handle');
  if (h) h.remove();
}

/** Меню «⋯» на страницах альбома/плейлиста: компактная карточка у верхнего края (не bottom sheet). */
function prepareMxmMobileCollectionTopMenu(menuEl) {
  if (!menuEl || !isMobile()) return;
  clearMxmMenuSheetChrome(menuEl);
  menuEl.classList.add('mxm-menu-top');
  menuEl.style.position = 'fixed';
  menuEl.style.zIndex = '10050';
  menuEl.style.left = '';
  menuEl.style.right = '';
  menuEl.style.top = '';
  menuEl.style.bottom = '';
  menuEl.style.width = '';
  menuEl.style.maxHeight = '';
  menuEl.style.transform = '';
  menuEl.style.removeProperty('transform');
  openMxmMenuBackdrop();
}

function bindMxmMenuSheetDragClose(menuEl) {
  if (!menuEl || !menuEl.classList.contains('mxm-menu-sheet')) return;
  if (menuEl._mxmSheetDragAbo) {
    try {
      menuEl._mxmSheetDragAbo.abort();
    } catch (_) {}
    menuEl._mxmSheetDragAbo = null;
  }
  const handle = menuEl.querySelector(':scope > .mxm-menu-sheet-handle');
  if (!handle) return;

  const ac = new AbortController();
  menuEl._mxmSheetDragAbo = ac;
  const signal = ac.signal;

  let startY = 0;
  let dragging = false;
  let capId = null;

  const onDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragging = true;
    startY = e.clientY;
    capId = e.pointerId;
    menuEl.classList.add('mxm-menu-sheet--dragging');
    try {
      handle.setPointerCapture(e.pointerId);
    } catch (_) {}
  };
  const onMove = (e) => {
    if (!dragging) return;
    const dy = e.clientY - startY;
    if (dy > 0) menuEl.style.setProperty('transform', `translateY(${dy}px)`, 'important');
    else {
      menuEl.style.removeProperty('transform');
    }
  };
  const finish = (e, cancelled) => {
    if (!dragging) return;
    dragging = false;
    menuEl.classList.remove('mxm-menu-sheet--dragging');
    const dy = e ? e.clientY - startY : 0;
    menuEl.style.removeProperty('transform');
    if (capId != null) {
      try {
        handle.releasePointerCapture(capId);
      } catch (_) {}
      capId = null;
    }
    if (!cancelled && dy > 72) {
      closeMxmCollectionPopovers();
      closeAllUserPlaylistMenus();
    }
  };

  handle.addEventListener('pointerdown', onDown, { signal });
  handle.addEventListener('pointermove', onMove, { signal });
  handle.addEventListener('pointerup', (e) => finish(e, false), { signal });
  handle.addEventListener('pointercancel', (e) => finish(e, true), { signal });
}

function prepareMxmMobileMenuSheet(menuEl) {
  if (!menuEl || !isMobile()) return;
  menuEl.classList.add('mxm-menu-sheet');
  let grip = menuEl.querySelector(':scope > .mxm-menu-sheet-handle');
  if (!grip) {
    grip = document.createElement('div');
    grip.className = 'mxm-menu-sheet-handle';
    grip.setAttribute('aria-hidden', 'true');
    menuEl.insertBefore(grip, menuEl.firstChild);
  }
  menuEl.style.position = 'fixed';
  menuEl.style.zIndex = '10050';
  menuEl.style.left = '';
  menuEl.style.top = '';
  menuEl.style.right = '';
  menuEl.style.bottom = '';
  menuEl.style.width = '';
  menuEl.style.transform = '';
  menuEl.style.removeProperty('transform');
  openMxmMenuBackdrop();
  bindMxmMenuSheetDragClose(menuEl);
}

function closeAllUserPlaylistMenus() {
  // Return any "ported" menus back to their cards
  document.querySelectorAll('.upl-menu[data-ported="1"]').forEach((m) => {
    clearMxmMenuSheetChrome(m);
    const originParent = m.__mxmOriginParent;
    if (originParent && originParent.isConnected) originParent.appendChild(m);
    m.removeAttribute('data-ported');
    m.__mxmOriginParent = null;
    m.style.position = '';
    m.style.left = '';
    m.style.top = '';
    m.style.right = '';
    m.style.bottom = '';
    m.style.width = '';
    m.style.transform = '';
    m.style.zIndex = '';
  });
  document.querySelectorAll('.upl-menu').forEach((m) => m.classList.add('hidden'));
  document.querySelectorAll('.upl-cover-picker').forEach((m) => m.classList.add('hidden'));
  closeUserPlaylistHeroOwnerMenu();
  closeMxmMenuBackdrop();
}

function closeUserPlaylistHeroOwnerMenu() {
  const menu = $('upl-hero-owner-menu');
  const btn = $('upl-hero-more-btn');
  if (btn) btn.setAttribute('aria-expanded', 'false');
  if (!menu) return;
  const wasOpen = !menu.classList.contains('hidden');
  menu.classList.add('hidden');
  menu.classList.remove('mxm-upl-hero-owner-menu--inline');
  if (menu.dataset.ported === '1') {
    const parent = menu.__mxmOriginParent;
    if (parent && parent.isConnected) parent.appendChild(menu);
    menu.__mxmOriginParent = null;
    menu.removeAttribute('data-ported');
    menu.style.cssText = '';
  }
}

function toggleUserPlaylistHeroOwnerMenu(ev, btn) {
  if (ev && ev.stopPropagation) ev.stopPropagation();
  const menu = $('upl-hero-owner-menu');
  const trigger = btn || $('upl-hero-more-btn');
  if (!menu || !trigger) return;
  if (!menu.classList.contains('hidden')) {
    closeUserPlaylistHeroOwnerMenu();
    return;
  }
  closeMxmCollectionPopovers();
  closeAllUserPlaylistMenus();
  trigger.setAttribute('aria-expanded', 'true');
  menu.classList.remove('hidden');

  if (isMobile()) {
    menu.classList.add('mxm-upl-hero-owner-menu--inline');
    requestAnimationFrame(() => {
      try {
        menu.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } catch (_) {
        /* ignore */
      }
    });
    return;
  }

  menu.classList.remove('mxm-upl-hero-owner-menu--inline');
  menu.__mxmOriginParent = menu.parentElement || null;
  document.body.appendChild(menu);
  menu.dataset.ported = '1';
  menu.style.position = 'fixed';
  menu.style.zIndex = '10050';
  const r = trigger.getBoundingClientRect();
  const margin = 10;
  const place = () => {
    const mw = menu.offsetWidth || 220;
    const mh = menu.offsetHeight || 56;
    let left = r.right - mw;
    left = Math.max(margin, Math.min(left, window.innerWidth - mw - margin));
    let top = r.bottom + 8;
    if (top + mh > window.innerHeight - margin) top = r.top - mh - 8;
    top = Math.max(margin, Math.min(top, window.innerHeight - mh - margin));
    menu.style.left = `${Math.round(left)}px`;
    menu.style.top = `${Math.round(top)}px`;
    menu.style.right = 'auto';
    menu.style.bottom = 'auto';
  };
  requestAnimationFrame(place);
  requestAnimationFrame(() => requestAnimationFrame(place));
}

let _uplHeroOwnerMenuResizeT = null;
window.addEventListener(
  'resize',
  () => {
    clearTimeout(_uplHeroOwnerMenuResizeT);
    _uplHeroOwnerMenuResizeT = setTimeout(() => {
      const menu = $('upl-hero-owner-menu');
      if (!menu || menu.classList.contains('hidden')) return;
      const ported = menu.dataset.ported === '1';
      const inline = menu.classList.contains('mxm-upl-hero-owner-menu--inline');
      if (isMobile() && ported) closeUserPlaylistHeroOwnerMenu();
      else if (!isMobile() && inline) closeUserPlaylistHeroOwnerMenu();
    }, 120);
  },
  { passive: true }
);

function toggleUserPlaylistCardMenu(playlistId, btnEl) {
  const menu = btnEl?.parentElement?.querySelector('.upl-menu') || $('upl-menu-' + playlistId);
  if (!menu) return;
  const wasHidden = menu.classList.contains('hidden');
  if (!wasHidden) {
    // Повторный клик по "..." должен скрывать меню.
    closeAllUserPlaylistMenus();
    return;
  }

  closeMxmCollectionPopovers();

  // Port menu to body so it won't be clipped by card overflow:hidden
  // Кнопка обычно является previous sibling к меню
  const btn = btnEl || (menu.previousElementSibling?.classList?.contains('upl-more-btn') ? menu.previousElementSibling : null);
  const r = (btnEl && btnEl.getBoundingClientRect) ? btnEl.getBoundingClientRect() : btn?.getBoundingClientRect?.();

  closeAllUserPlaylistMenus();
  menu.__mxmOriginParent = menu.parentElement || null;
  document.body.appendChild(menu);
  menu.dataset.ported = '1';
  menu.dataset.playlistId = playlistId;
  menu.classList.remove('hidden');
  menu.style.position = 'fixed';
  menu.style.zIndex = '10050';
  if (isMobile()) {
    const fromCollectionHero = !!(btnEl && btnEl.closest && btnEl.closest('.mxm-collection-hero-tools'));
    if (fromCollectionHero) prepareMxmMobileCollectionTopMenu(menu);
    else prepareMxmMobileMenuSheet(menu);
  } else {
    menu.style.transform = 'none'; // override CSS translateY
    if (r) {
      const margin = 10;
      const menuRect = menu.getBoundingClientRect();
      let left = r.right - menuRect.width;
      left = Math.max(margin, Math.min(left, window.innerWidth - menuRect.width - margin));
      let top = r.top - menuRect.height - 10;
      if (top < margin) top = r.bottom + 10;
      top = Math.max(margin, Math.min(top, window.innerHeight - menuRect.height - margin));
      menu.style.left = `${left}px`;
      menu.style.top = `${top}px`;
      menu.style.right = 'auto';
    }
  }
}

function toggleUserPlaylistCoverPicker(playlistId) {
  const box = $('upl-cover-' + playlistId);
  if (!box) return;
  box.classList.toggle('hidden');
}

async function setUserPlaylistCoverGrad(playlistId, grad) {
  try {
    await apiFetch('PUT', `/api/me/user-playlists/${playlistId}`, { coverGradient: grad });
    toast('Обложка обновлена', 'success');
    closeAllUserPlaylistMenus();
    await ensureMyPlaylistsLoaded();
    if (state.view === 'library') await renderLibrary();
    if (state.view === 'user-playlist') await syncUserPlaylistPageIfVisible(playlistId);
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
}

// ─── User playlist cover upload (client preview + webp) ─────────────────
function fileToWebpBlob(file, { maxDim = 900, quality = 0.88 } = {}) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('no file'));
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) throw new Error('bad image');

        const scale = Math.min(1, maxDim / Math.max(w, h));
        const tw = Math.max(1, Math.round(w * scale));
        const th = Math.max(1, Math.round(h * scale));

        const canvas = document.createElement('canvas');
        canvas.width = tw;
        canvas.height = th;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('no ctx');
        ctx.drawImage(img, 0, 0, tw, th);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return reject(new Error('webp conversion failed'));
          resolve(blob);
        }, 'image/webp', quality);
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(new Error('image load failed'));
    };
    img.src = url;
  });
}

async function onUserPlaylistCoverFileSelected() {
  const modal = $('user-playlist-cover-upload-modal');
  const input = $('upl-playlist-cover-file');
  const preview = $('upl-playlist-cover-preview');
  const saveBtn = $('upl-playlist-cover-save-btn');
  if (!modal || !input || !preview || !saveBtn) return;

  const f = input.files?.[0];
  if (!f) return;

  saveBtn.disabled = true;
  preview.innerHTML = `<div class="page-loading" style="padding:10px 0;justify-content:flex-start"><div class="spinner"></div></div>`;

  try {
    const webpBlob = await fileToWebpBlob(f);
    const objUrl = URL.createObjectURL(webpBlob);

    if (modal._coverObjectUrl) URL.revokeObjectURL(modal._coverObjectUrl);
    modal._coverWebpBlob = webpBlob;
    modal._coverObjectUrl = objUrl;

    preview.innerHTML = `
      <div class="upl-cover-upload-preview">
        <img src="${objUrl}" alt="Превью обложки" />
      </div>
    `;
    saveBtn.disabled = false;
  } catch (e) {
    toast(e.message || 'Ошибка конвертации', 'error');
    preview.innerHTML = `<div class="lib-empty" style="margin:0"><div class="lib-empty-icon"><svg viewBox="0 0 24 24"><path d="M19 7l-1.5-1.5L12 11l-5.5-5.5L5 7l7 7 7-7z"/></svg></div><h3 style="margin-top:10px">Не удалось обработать файл</h3></div>`;
    saveBtn.disabled = true;
  }
}

function openUserPlaylistCoverUploadModal(playlistId) {
  closeAllUserPlaylistMenus();
  const modalId = 'user-playlist-cover-upload-modal';
  let modal = $(modalId);
  if (!modal) {
    modal = document.createElement('div');
    modal.id = modalId;
    modal.className = 'modal modal--center hidden';
    modal.innerHTML = `
      <div class="modal-overlay" onclick="closeUserPlaylistModal('${modalId}')"></div>
      <div class="modal-box" style="max-width:540px">
        <button class="modal-close" type="button" onclick="closeUserPlaylistModal('${modalId}')">
          <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <div style="display:flex;flex-direction:column;gap:12px">
          <div>
            <div style="font-size:16px;font-weight:900;color:var(--text)">Сменить обложку</div>
            <div style="font-size:13.5px;color:var(--text-2);margin-top:6px">Выберите изображение — мы подготовим webp и покажем превью.</div>
          </div>

          <div class="form-group">
            <label>зображение</label>
            <input id="upl-playlist-cover-file" type="file" accept="image/*" class="hidden" />
            <button class="btn-ghost" type="button" onclick="$('upl-playlist-cover-file').click()">Выбрать изображение</button>
          </div>

          <div id="upl-playlist-cover-preview" class="upl-cover-upload-preview-host">
            Пока нет изображения
          </div>

          <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:4px">
            <button class="btn-ghost" type="button" onclick="closeUserPlaylistModal('${modalId}')">Отмена</button>
            <button class="btn-primary" type="button" id="upl-playlist-cover-save-btn" disabled onclick="confirmUserPlaylistCoverUpload()">Сохранить</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  modal.dataset.playlistId = playlistId;
  modal._coverWebpBlob = null;
  if (modal._coverObjectUrl) { URL.revokeObjectURL(modal._coverObjectUrl); modal._coverObjectUrl = null; }

  const input = $('upl-playlist-cover-file');
  const preview = $('upl-playlist-cover-preview');
  const saveBtn = $('upl-playlist-cover-save-btn');
  if (input) input.value = '';
  if (preview) preview.innerHTML = `Пока нет изображения`;
  if (saveBtn) saveBtn.disabled = true;

  modal.classList.remove('hidden');

  // wire-up (после возможного создания DOM)
  const inputEl = $('upl-playlist-cover-file');
  if (inputEl) inputEl.onchange = onUserPlaylistCoverFileSelected;
}

async function confirmUserPlaylistCoverUpload() {
  const modal = $('user-playlist-cover-upload-modal');
  if (!modal) return;
  const playlistId = modal.dataset.playlistId;
  const blob = modal._coverWebpBlob;
  if (!playlistId) return;
  if (!blob) {
    toast('Выберите изображение', 'error');
    return;
  }

  try {
    const fd = new FormData();
    fd.append('image', new File([blob], 'playlist-cover.webp', { type: 'image/webp' }));
    const res = await FORM(`/api/me/user-playlists/${playlistId}/cover`, fd);
    const newCoverUrl = res && res.url ? String(res.url) : '';
    if (newCoverUrl) {
      const grad = await getGradientFromImage(newCoverUrl);
      if (grad) {
        try {
          await apiFetch('PUT', `/api/me/user-playlists/${playlistId}`, { coverGradient: grad });
        } catch (_) {}
      }
    }

    toast('Обложка обновлена', 'success');
    closeUserPlaylistModal('user-playlist-cover-upload-modal');
    closeAllUserPlaylistMenus();
    await ensureMyPlaylistsLoaded();
    if (state.view === 'library') await renderLibrary();
    if (state.view === 'user-playlist') {
      const { params } = parseHash();
      if (params?.id) await syncUserPlaylistPageIfVisible(params.id);
    }
  } catch (e) {
    toast(e.message || 'Ошибка загрузки', 'error');
  }
}

function ensureUserPlaylistEditMetaModal() {
  const modalId = 'user-playlist-edit-meta-modal';
  let modal = $(modalId);
  if (modal) return modal;
  modal = document.createElement('div');
  modal.id = modalId;
  modal.className = 'modal modal--center hidden';
  modal.innerHTML = `
      <div class="modal-overlay" onclick="closeUserPlaylistModal('${modalId}')"></div>
      <div class="modal-box upl-edit-meta-modal-box" style="max-width:480px">
        <button class="modal-close" type="button" onclick="closeUserPlaylistModal('${modalId}')">
          <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <div style="display:flex;flex-direction:column;gap:14px">
          <div>
            <div style="font-size:16px;font-weight:900;color:var(--text)">Название и описание</div>
            <div style="font-size:13.5px;color:var(--text-2);margin-top:6px">Изменения увидят все, у кого есть доступ к плейлисту.</div>
          </div>
          <div class="form-group">
            <label for="upl-edit-meta-title">Название</label>
            <input id="upl-edit-meta-title" type="text" maxlength="120" autocomplete="off" />
          </div>
          <div class="form-group">
            <label for="upl-edit-meta-desc">Описание</label>
            <textarea id="upl-edit-meta-desc" maxlength="2000" rows="4" placeholder="Необязательно"></textarea>
          </div>
          <div style="display:flex;justify-content:flex-end;gap:10px">
            <button class="btn-ghost" type="button" onclick="closeUserPlaylistModal('${modalId}')">Отмена</button>
            <button class="btn-primary" type="button" onclick="confirmUserPlaylistEditMeta()">Сохранить</button>
          </div>
        </div>
      </div>
    `;
  document.body.appendChild(modal);
  return modal;
}

async function openUserPlaylistEditMetaModal(playlistId) {
  closeAllUserPlaylistMenus();
  const id = String(playlistId || '').trim();
  if (!id || !state.user) return;
  let pl = null;
  try {
    pl = await GET(`/api/user-playlists/${id}`);
  } catch (e) {
    toast(e.message || 'Не удалось загрузить плейлист', 'error');
    return;
  }
  if (!pl || String(pl.ownerId) !== String(state.user.id)) {
    toast('Нет прав на редактирование', 'error');
    return;
  }
  const modal = ensureUserPlaylistEditMetaModal();
  modal.dataset.playlistId = id;
  const titleIn = $('upl-edit-meta-title');
  const descIn = $('upl-edit-meta-desc');
  if (titleIn) titleIn.value = String(pl.title || '');
  if (descIn) descIn.value = String(pl.description || '');
  modal.classList.remove('hidden');
  if (titleIn) titleIn.focus();
}

async function confirmUserPlaylistEditMeta() {
  const modal = $('user-playlist-edit-meta-modal');
  const id = modal?.dataset?.playlistId;
  if (!id || !state.user) return;
  const titleIn = $('upl-edit-meta-title');
  const descIn = $('upl-edit-meta-desc');
  const title = String(titleIn?.value || '').trim();
  const description = String(descIn?.value || '').trim();
  if (!title) {
    toast('Введите название', 'error');
    return;
  }
  try {
    await apiFetch('PUT', `/api/me/user-playlists/${id}`, { title, description: description || null });
    toast('Сохранено', 'success');
    closeUserPlaylistModal('user-playlist-edit-meta-modal');
    await ensureMyPlaylistsLoaded();
    if (state.view === 'library') await renderLibrary();
    if (state.view === 'user-playlist') {
      const { params } = parseHash();
      if (params?.id === id) await syncUserPlaylistPageIfVisible(id);
    }
  } catch (e) {
    toast(e.message || 'Не удалось сохранить', 'error');
  }
}

let _uplAddTracksSearchTimer = null;

function uplTrackMatchesAddQuery(track, q) {
  const raw = String(q || '').trim().toLowerCase();
  if (!raw) return true;
  const blob = `${track.title || ''} ${track.artist || ''} ${track.album || ''}`.toLowerCase();
  return blob.includes(raw);
}

async function uplResolvePlaylistTrackIdSet(playlistId) {
  const cached = state.userPlaylistTrackIdCache?.[playlistId];
  if (Array.isArray(cached)) return new Set(cached);
  try {
    const pl = await GET(withUserPlaylistAccessToken(`/api/user-playlists/${playlistId}`));
    const ids = (Array.isArray(pl?.tracks) ? pl.tracks : []).map((t) => t.id).filter(Boolean);
    state.userPlaylistTrackIdCache = state.userPlaylistTrackIdCache || {};
    state.userPlaylistTrackIdCache[playlistId] = ids;
    return new Set(ids);
  } catch {
    return new Set();
  }
}

async function renderUplAddTracksList(playlistId) {
  const modal = $('user-playlist-add-tracks-modal');
  const listEl = $('upl-add-tracks-list');
  const qIn = $('upl-add-tracks-search');
  if (!modal || !listEl) return;
  const q = String(qIn?.value || '').trim();
  const suggestions = Array.isArray(modal._uplSuggestions) ? modal._uplSuggestions : [];
  const inSet = modal._uplInSet instanceof Set ? modal._uplInSet : new Set();

  let rows = [];
  if (!q) {
    rows = suggestions.slice();
  } else {
    rows = suggestions.filter((t) => uplTrackMatchesAddQuery(t, q));
    if (q.length >= 2) {
      try {
        const res = await GET(`/api/search?q=${encodeURIComponent(q)}`);
        const fromSearch = Array.isArray(res?.tracks) ? res.tracks : [];
        const seen = new Set(rows.map((t) => t.id));
        for (const t of fromSearch) {
          if (!t?.id || !t.audioUrl || inSet.has(t.id) || seen.has(t.id)) continue;
          rows.push(t);
          seen.add(t.id);
          if (rows.length >= 28) break;
        }
      } catch (_) {}
    }
  }

  if (!rows.length) {
    listEl.innerHTML = `<div class="lib-empty"><h3>Ничего не найдено</h3><p>Попробуйте другой запрос или очистите поиск, чтобы увидеть подсказки.</p></div>`;
    return;
  }
  listEl.innerHTML = `<div class="track-list upl-add-tracks-results">${rows.map((t) => userPlaylistAddSuggestionRow(t, playlistId)).join('')}</div>`;
}

function uplAddTracksSearchInputDebounced() {
  if (_uplAddTracksSearchTimer) clearTimeout(_uplAddTracksSearchTimer);
  _uplAddTracksSearchTimer = setTimeout(() => {
    _uplAddTracksSearchTimer = null;
    const modal = $('user-playlist-add-tracks-modal');
    const id = modal?.dataset?.playlistId;
    if (id && modal && !modal.classList.contains('hidden')) renderUplAddTracksList(id);
  }, 280);
}

document.addEventListener('click', (e) => {
  if (e.target.closest('.upl-more-btn') || e.target.closest('.upl-menu')) return;
  if (e.target.closest('.mxm-hero-overflow-trigger')) return;
  if (e.target.closest('.mxm-collection-popover')) return;
  closeAllUserPlaylistMenus();
  closeMxmCollectionPopovers();
});

function closeMxmCollectionPopovers() {
  closeUserPlaylistHeroOwnerMenu();
  const m = $('mxm-album-hero-menu');
  if (!m) {
    closeMxmMenuBackdrop();
    return;
  }
  clearMxmMenuSheetChrome(m);
  if (m.dataset.ported === '1') {
    const parent = m.__mxmOriginParent;
    if (parent && parent.isConnected) parent.appendChild(m);
    m.__mxmOriginParent = null;
    m.removeAttribute('data-ported');
    m.style.cssText = '';
  }
  m.classList.add('hidden');
  document.querySelectorAll('.mxm-hero-overflow-trigger[aria-expanded="true"]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
  closeMxmMenuBackdrop();
}

async function openAddCurrentTrackToPlaylist() {
  if (!state.currentTrack?.id) {
    toast('Сначала выберите трек', 'error');
    return;
  }
  await addTrackToUserPlaylist(state.currentTrack.id);
}

function openAddToPlaylistModal(trackId) {
  const modalId = 'user-playlist-add-track-modal';
  let modal = $(modalId);
  if (!modal) {
    modal = document.createElement('div');
    modal.id = modalId;
    modal.className = 'modal modal--center hidden';
    modal.innerHTML = `
      <div class="modal-overlay" onclick="closeUserPlaylistModal('${modalId}')"></div>
      <div class="modal-box" style="max-width:480px">
        <button class="modal-close" type="button" onclick="closeUserPlaylistModal('${modalId}')">
          <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <div style="display:flex;flex-direction:column;gap:14px">
          <div>
            <div style="font-size:16px;font-weight:900;color:var(--text)">Добавить в плейлист</div>
            <div style="font-size:13.5px;color:var(--text-2);margin-top:6px">Выберите существующий или создайте новый.</div>
          </div>
          <div class="form-group">
            <label>Плейлист</label>
            <div class="select-picker">
              <select id="upl-add-select" onchange="toggleNewPlaylistRow()"></select>
            </div>
          </div>
          <div id="upl-add-new-row" class="hidden">
            <div class="form-group">
              <label>Название нового плейлиста</label>
              <input id="upl-add-new-title" type="text" placeholder="Например: Утро без спешки" />
            </div>
          </div>
          <div style="display:flex;justify-content:flex-end;gap:10px">
            <button class="btn-ghost" type="button" onclick="closeUserPlaylistModal('${modalId}')">Отмена</button>
            <button class="btn-primary" type="button" onclick="confirmAddTrackToPlaylist()">Добавить</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  modal.dataset.trackId = trackId;
  const select = $('upl-add-select');
  const rows = Array.isArray(state.mePlaylists) ? state.mePlaylists : [];
  select.innerHTML = `
    ${rows.length ? rows.map(p => `<option value="${p.id}">${esc(p.title)}</option>`).join('') : ''}
    <option value="__new__">Создать новый</option>
  `;
  select.value = rows.length ? rows[0].id : '__new__';

  $('upl-add-new-row').classList.toggle('hidden', select.value !== '__new__');
  modal.classList.remove('hidden');

  // focus
  const newTitle = $('upl-add-new-title');
  if (newTitle && select.value === '__new__') newTitle.focus();
}

function toggleNewPlaylistRow() {
  const select = $('upl-add-select');
  $('upl-add-new-row').classList.toggle('hidden', select.value !== '__new__');
  if (select.value === '__new__') $('upl-add-new-title').focus();
}

async function confirmAddTrackToPlaylist() {
  const modal = $('user-playlist-add-track-modal');
  const trackId = modal?.dataset?.trackId;
  const select = $('upl-add-select');
  const newTitle = String($('upl-add-new-title')?.value || '').trim();
  if (!trackId) return;

  let playlistId = select.value;
  let playlist = null;

  try {
    if (playlistId === '__new__') {
      if (!newTitle) {
        toast('Введите название плейлиста', 'error');
        return;
      }
      const coverGradient = pickGrad(newTitle);
      playlist = await POST('/api/me/user-playlists', { title: newTitle, coverGradient });
      playlistId = playlist.id;
      // refresh cache in-place
      state.mePlaylists = Array.isArray(state.mePlaylists) ? state.mePlaylists : [];
      state.mePlaylists.push(playlist);
    } else {
      playlist = (Array.isArray(state.mePlaylists) ? state.mePlaylists : []).find(p => p.id === playlistId) || null;
    }

    const prev = Array.isArray(playlist?.tracks) ? playlist.tracks : [];
    const prevIds = prev.map((x) => (x && typeof x === 'object' ? x.id : x)).filter(Boolean).map(String);
    const nextTracks = Array.from(new Set([String(trackId), ...prevIds]));
    await apiFetch('PUT', `/api/me/user-playlists/${playlistId}/tracks`, { tracks: nextTracks });

    toast('Добавлено в плейлист', 'success');
    closeUserPlaylistModal('user-playlist-add-track-modal');

    await ensureMyPlaylistsLoaded();
    if (state.view === 'library') await renderLibrary();
    if (state.view === 'user-playlist') {
      const { params } = parseHash();
      if (params?.id) await syncUserPlaylistPageIfVisible(params.id);
    }
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
}

async function resolveUserPlaylistGradient(playlist) {
  if (!playlist) return pickGrad('user-playlist');
  let dynamicGrad = null;
  if (playlist.coverUrl) {
    dynamicGrad = await getGradientFromImage(playlist.coverUrl);
  }
  return dynamicGrad || playlist.coverGradient || pickGrad(playlist.id);
}

function applyUserPlaylistHeroGradient(root, grad) {
  const hero = root && root.querySelector('.page-hero');
  if (hero && grad) hero.style.setProperty('--hero-grad', `linear-gradient(${grad})`);
}

async function applyUserPlaylistPageFromData(playlist) {
  if (!playlist || !playlist.id) return;
  const tracks = Array.isArray(playlist.tracks) ? playlist.tracks : [];
  state.userPlaylistTrackIdCache = state.userPlaylistTrackIdCache || {};
  state.userPlaylistTrackIdCache[playlist.id] = tracks.map((t) => t.id);
  const canEdit = !!(state.user && playlist.ownerId && state.user.id === playlist.ownerId);
  const grad = await resolveUserPlaylistGradient(playlist);
  const qKey = storeQueue(tracks);

  const root = document.querySelector('.mxm-collection-page[data-upl-id]');
  if (!root || String(root.getAttribute('data-upl-id') || '') !== String(playlist.id)) return;

  applyUserPlaylistHeroGradient(root, grad);

  const h1 = root.querySelector('.hero-title');
  if (h1) h1.textContent = playlist.title || '';

  const desc = root.querySelector('.upl-hero-desc');
  if (desc) {
    desc.textContent = playlist.description
      ? String(playlist.description)
      : `${tracks.length} ${pluralTracks(tracks.length)}`;
  }

  const coverWrap = root.querySelector('.hero-cover');
  if (coverWrap) {
    coverWrap.innerHTML = playlist.coverUrl
      ? coverImg(playlist.coverUrl, grad)
      : `<div class="featured-cover-fallback" style="background:linear-gradient(${grad})"></div>`;
  }

  const lc = root.querySelector('#user-playlist-likes-count');
  if (lc) lc.textContent = String(Number(playlist.likesCount) || 0);
  const cc = root.querySelector('#user-playlist-comments-count');
  if (cc) cc.textContent = String(Number(playlist.commentsCount) || 0);

  const likeBtn = root.querySelector('#user-playlist-like-btn');
  if (likeBtn) {
    const liked = !!playlist.likedByMe;
    likeBtn.classList.toggle('liked', liked);
    likeBtn.dataset.liked = liked ? '1' : '0';
    likeBtn.setAttribute('aria-pressed', liked ? 'true' : 'false');
    const path = likeBtn.querySelector('svg path');
    if (path) path.setAttribute('fill', liked ? 'currentColor' : 'none');
    likeBtn.title = liked ? 'Убрать лайк' : 'Поставить лайк';
    likeBtn.setAttribute('aria-label', likeBtn.title);
    likeBtn.setAttribute('onclick', `toggleUserPlaylistLike('${playlist.id}', this.dataset.liked === '1')`);
  }

  const playBtn = root.querySelector('.mxm-collection-play-cta');
  if (playBtn) playBtn.setAttribute('onclick', `playFromQueue('${qKey}',0)`);

  const sharePub = root.querySelector('#upl-hero-toggle-share-btn');
  if (sharePub) {
    const shared = !!playlist.isShared;
    sharePub.setAttribute(
      'onclick',
      `toggleUserPlaylistShare('${playlist.id}', ${shared ? 'true' : 'false'})`
    );
    sharePub.classList.toggle('mxm-upl-hero-public-btn--active', shared);
    sharePub.title = shared ? 'В ленте — нажмите, чтобы скрыть' : 'Не в ленте — нажмите, чтобы опубликовать';
    sharePub.setAttribute(
      'aria-label',
      shared ? 'Опубликовано в общей ленте. Снять с публикации' : 'Не опубликовано. Опубликовать в общей ленте'
    );
  }

  const listEl = root.querySelector('.upl-track-list');
  if (listEl) {
    listEl.innerHTML = tracks.length
      ? tracks.map((t, i) => userPlaylistTrackRowCover(t, qKey, i, canEdit, playlist.id)).join('')
      : `<div class="lib-empty">В плейлисте пока нет треков</div>`;
  }

  setQueuePlayingFromLabel(playlist.title || 'Плейлист');
  updatePlayingRows();
  refreshFavBtns();
}

async function syncUserPlaylistPageIfVisible(playlistId) {
  const pid = String(playlistId || '').trim();
  if (!pid || state.view !== 'user-playlist') return;
  const { params } = parseHash();
  if (String(params?.id || '') !== pid) return;
  const root = document.querySelector('.mxm-collection-page[data-upl-id]');
  if (!root || String(root.getAttribute('data-upl-id') || '') !== pid) {
    await renderUserPlaylist(pid);
    return;
  }
  try {
    const playlist = await GET(withUserPlaylistAccessToken(`/api/user-playlists/${pid}`));
    if (playlist) await applyUserPlaylistPageFromData(playlist);
    else await renderUserPlaylist(pid);
  } catch {
    await renderUserPlaylist(pid);
  }
}

async function renderUserPlaylist(id) {
  const content = $('content');
  content.innerHTML = loadingSpinner();
  try {
    const playlist = await GET(withUserPlaylistAccessToken(`/api/user-playlists/${id}`));
    if (!playlist) {
      content.innerHTML = `<div class="lib-empty">Плейлист не найден</div>`;
      return;
    }
    const canEdit = !!(state.user && playlist.ownerId && state.user.id === playlist.ownerId);
    state.renderPlaylistAddButtons = false;
    const tracks = Array.isArray(playlist.tracks) ? playlist.tracks : [];
    const likesCount = Number(playlist.likesCount) || 0;
    const commentsCount = Number(playlist.commentsCount) || 0;
    const likedByMe = !!playlist.likedByMe;
    state.userPlaylistTrackIdCache = state.userPlaylistTrackIdCache || {};
    state.userPlaylistTrackIdCache[playlist.id] = tracks.map(t => t.id);
    const grad = await resolveUserPlaylistGradient(playlist);
    const qKey = storeQueue(tracks);
    const ownerProfRef = playlist.ownerLogin || playlist.ownerId || '';
    const ownerPid = ownerProfRef ? profileJsId(ownerProfRef) : '';
    await prefetchUserPlaylistShareToken(playlist.id);
    content.innerHTML = `
      <div class="mxm-collection-page" data-upl-id="${playlist.id}">
      <div class="page-hero mxm-collection-hero" style="--hero-grad:linear-gradient(${grad})">
        <button class="nav-back-btn" onclick="goBack()" title="Назад">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
          </svg>
        </button>
        <div class="hero-bg"></div>
        <div class="hero-content">
          <div class="hero-cover">
            ${playlist.coverUrl ? coverImg(playlist.coverUrl, grad) : `<div class="featured-cover-fallback" style="background:linear-gradient(${grad})"></div>`}
          </div>
          <div class="hero-info">
            <div class="hero-type">Плейлист</div>
            <h1 class="hero-title">${esc(playlist.title)}</h1>
            <div class="hero-sub mxm-collection-hero-sub">
              <div class="upl-hero-sub-line">
                ${
                  playlist.ownerUsername && ownerPid
                    ? `<button type="button" class="upl-hero-owner-btn" onclick="navigateToUserProfile('${ownerPid}')">
                        ${
                          playlist.ownerAvatarUrl
                            ? `<span class="upl-hero-owner-av"><img src="${esc(resolveMediaUrl(playlist.ownerAvatarUrl))}" alt=""></span>`
                            : `<span class="upl-hero-owner-av upl-hero-owner-av-fallback">${esc((playlist.ownerUsername || 'U').slice(0, 1).toUpperCase())}</span>`
                        }
                        <span class="upl-hero-owner-name">@${esc(playlist.ownerUsername)}</span>
                      </button>
                      <span class="upl-hero-owner-sep">·</span>`
                    : ''
                }
                <span class="upl-hero-desc">${playlist.description ? esc(playlist.description) : `${tracks.length} ${pluralTracks(tracks.length)}`}</span>
              </div>
              <div class="upl-hero-social">
                <span>❤ <span id="user-playlist-likes-count">${likesCount}</span></span>
                <span>💬 <span id="user-playlist-comments-count">${commentsCount}</span></span>
              </div>
            </div>
            ${canEdit ? `<div class="mxm-upl-hero-cta-wrap mxm-upl-hero-cta-wrap--owner">` : ''}
            <div class="hero-actions mxm-collection-actions hero-actions--media-row">
              <button class="hero-play-btn mxm-collection-play-cta" type="button" onclick="playFromQueue('${qKey}',0)" title="Слушать" aria-label="Слушать">
                <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              </button>
              <button id="user-playlist-like-btn" class="hero-fav-btn ${likedByMe ? 'liked' : ''}" data-liked="${likedByMe ? '1' : '0'}"
                type="button" aria-pressed="${likedByMe ? 'true' : 'false'}"
                onclick="toggleUserPlaylistLike('${playlist.id}', this.dataset.liked === '1')"
                title="${likedByMe ? 'Убрать лайк' : 'Поставить лайк'}" aria-label="${likedByMe ? 'Убрать лайк' : 'Поставить лайк'}">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" ${likedByMe ? 'fill="currentColor"' : 'fill="none"'}/></svg>
              </button>
              <button class="hero-share-btn" type="button" onclick="copyUserPlaylistShareLink('${playlist.id}')" title="Скопировать ссылку" aria-label="Скопировать ссылку">
                ${MXM_SHARE_ICON_SVG}
              </button>
            </div>
              ${canEdit ? `<div class="mxm-hero-owner-actions">
                <div class="mxm-upl-hero-owner-top-row">
                  <button id="upl-hero-toggle-share-btn" type="button"
                    class="hero-share-btn btn-ghost mxm-upl-hero-tool mxm-upl-hero-public-btn${playlist.isShared ? ' mxm-upl-hero-public-btn--active' : ''}"
                    onclick="toggleUserPlaylistShare('${playlist.id}', ${playlist.isShared ? 'true' : 'false'})"
                    title="${playlist.isShared ? 'В ленте — нажмите, чтобы скрыть' : 'Не в ленте — нажмите, чтобы опубликовать'}"
                    aria-label="${playlist.isShared ? 'Опубликовано в общей ленте. Снять с публикации' : 'Не опубликовано. Опубликовать в общей ленте'}">
                    <span class="mxm-upl-hero-public-btn-icon-wrap" aria-hidden="true">
                      ${MXM_PUBLIC_ICON_SVG.replace('<svg ', '<svg width="20" height="20" ')}
                    </span>
                    <span class="mxm-upl-hero-pub-dot" aria-hidden="true"></span>
                  </button>
                  <div class="mxm-upl-hero-overflow">
                    <button type="button" id="upl-hero-more-btn" class="hero-share-btn btn-ghost mxm-upl-hero-tool mxm-upl-hero-more-btn mxm-hero-overflow-trigger"
                      onclick="toggleUserPlaylistHeroOwnerMenu(event, this)" aria-expanded="false" aria-haspopup="menu" aria-controls="upl-hero-owner-menu"
                      title="Ещё" aria-label="Дополнительные действия с плейлистом">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                    </button>
                  </div>
                </div>
                <div id="upl-hero-owner-menu" class="mxm-upl-hero-owner-menu mxm-collection-popover hidden" role="menu" aria-label="Действия с плейлистом" data-playlist-id="${playlist.id}">
                  <div class="mxm-upl-hero-owner-menu-grid" role="none">
                    <button class="hero-share-btn btn-ghost mxm-upl-hero-tool" type="button" role="menuitem"
                      onclick="closeUserPlaylistHeroOwnerMenu();openUserPlaylistEditMetaModal('${playlist.id}')" title="Название и описание" aria-label="Название и описание">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                    </button>
                    <button class="hero-share-btn btn-ghost mxm-upl-hero-tool" type="button" role="menuitem"
                      onclick="closeUserPlaylistHeroOwnerMenu();openUserPlaylistCoverUploadModal('${playlist.id}')" title="Сменить обложку" aria-label="Сменить обложку">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                    </button>
                    <button class="hero-share-btn btn-ghost mxm-upl-hero-tool" type="button" role="menuitem"
                      onclick="closeUserPlaylistHeroOwnerMenu();openUserPlaylistAddTracksModal('${playlist.id}')" title="Добавить треки" aria-label="Добавить треки">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                    </button>
                    <button class="hero-share-btn btn-ghost mxm-upl-hero-tool mxm-upl-hero-tool--danger" type="button" role="menuitem"
                      onclick="closeUserPlaylistHeroOwnerMenu();deleteUserPlaylist('${playlist.id}')" title="Удалить плейлист" aria-label="Удалить плейлист">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                    </button>
                  </div>
                </div>
              </div>` : ''}
            ${canEdit ? `</div>` : ''}
          </div>
        </div>
      </div>
      <div class="mxm-collection-sheet">
      <div class="track-list upl-track-list mxm-collection-track-list" style="margin-top:10px">
        ${tracks.length
          ? tracks.map((t, i) => userPlaylistTrackRowCover(t, qKey, i, canEdit, playlist.id)).join('')
          : `<div class="lib-empty">В плейлисте пока нет треков</div>`}
      </div>
      <section class="section upl-comments-wrap mxm-collection-comments">
        <div class="section-header">
          <h2 class="section-title" style="margin-bottom:0;">Комментарии</h2>
        </div>
        <div class="upl-comment-form">
          <input id="upl-comment-input" type="text" maxlength="500" placeholder="${state.user ? 'Оставьте комментарий...' : 'Войдите, чтобы комментировать'}" ${state.user ? '' : 'disabled'}
            onkeydown="userPlaylistCommentInputKeydown(event, '${playlist.id}')">
          <button id="upl-comment-send-btn" class="btn-primary upl-comment-send-btn" type="button" ${state.user ? '' : 'disabled'}
            onclick="submitUserPlaylistComment('${playlist.id}')" aria-label="Отправить комментарий">
            <span class="upl-comment-send-label">Отправить</span>
            <svg class="upl-comment-send-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 18V6M12 6l-4 4M12 6l4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div id="upl-comments-list" class="upl-comments-list"></div>
      </section>
      </div>
      </div>
    `;
    setQueuePlayingFromLabel(playlist.title);
    renderUserPlaylistComments(playlist.id);
  } catch (e) {
    content.innerHTML = `<div class="lib-empty">Ошибка загрузки плейлиста</div>`;
  }
}

function openUserPlaylistAddTracksModal(playlistId) {
  const modalId = 'user-playlist-add-tracks-modal';
  let modal = $(modalId);
  if (modal && !$('upl-add-tracks-search')) {
    modal.remove();
    modal = null;
  }
  if (!modal) {
    modal = document.createElement('div');
    modal.id = modalId;
    modal.className = 'modal modal--center hidden';
    modal.innerHTML = `
      <div class="modal-overlay" onclick="closeUserPlaylistModal('${modalId}')"></div>
      <div class="modal-box upl-add-tracks-modal-box" style="max-width:520px">
        <button class="modal-close" type="button" onclick="closeUserPlaylistModal('${modalId}')">
          <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <div style="display:flex;flex-direction:column;gap:12px">
          <div>
            <div style="font-size:16px;font-weight:900;color:var(--text)">Добавить треки</div>
            <div style="font-size:13.5px;color:var(--text-2);margin-top:6px">Подсказки ниже — или найдите трек по названию и исполнителю.</div>
          </div>
          <div class="form-group upl-add-tracks-search-wrap">
            <label for="upl-add-tracks-search">Поиск</label>
            <input type="search" id="upl-add-tracks-search" placeholder="Название, исполнитель…" autocomplete="off"
              oninput="uplAddTracksSearchInputDebounced()" />
          </div>
          <div id="upl-add-tracks-list"></div>
          <div style="display:flex;justify-content:flex-end;gap:10px">
            <button class="btn-ghost" type="button" onclick="closeUserPlaylistModal('${modalId}')">Закрыть</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  modal.dataset.playlistId = playlistId;
  const searchIn = $('upl-add-tracks-search');
  if (searchIn) searchIn.value = '';
  modal._uplSuggestions = [];
  modal._uplInSet = new Set();

  const listEl = $('upl-add-tracks-list');
  if (listEl) listEl.innerHTML = loadingSpinner();
  modal.classList.remove('hidden');

  (async () => {
    try {
      const inSet = await uplResolvePlaylistTrackIdSet(playlistId);
      modal._uplInSet = inSet;
      const tracks = await GET(`/api/me/user-playlists/${playlistId}/add-suggestions`);
      const safe = Array.isArray(tracks) ? tracks : [];
      modal._uplSuggestions = safe;
      const qNow = String($('upl-add-tracks-search')?.value || '').trim();
      if (!safe.length && !qNow) {
        if (listEl) {
          listEl.innerHTML = `<div class="lib-empty"><h3>Подсказок нет</h3><p>Используйте поиск выше или добавьте треки из других разделов.</p></div>`;
        }
        return;
      }
      await renderUplAddTracksList(playlistId);
    } catch (e) {
      if (listEl) listEl.innerHTML = `<div class="lib-empty"><h3>Ошибка</h3></div>`;
    }
  })();
}

function userPlaylistAddSuggestionRow(track, playlistId) {
  const tagsHtml = htmlTrackListTagRow(track);
  const grad = track.coverGradient || pickGrad(track.id);
  return `
    <div class="track-row track-row-w-cover upl-suggest-row" data-track-id="${track.id}">
      <div class="track-cover-sm" style="position: relative;">
        ${coverImg(track.coverUrl, grad)}
      </div>
      <div class="track-info">
        ${tagsHtml}
        <div class="track-title">${esc(track.title)}</div>
        <div class="track-meta">${renderTrackMetaLineHtml(track)}</div>
      </div>
      <div class="track-right">
        <button class="track-playlist-add-btn" type="button"
          onclick="event.stopPropagation();addTrackToUserPlaylistDirect('${playlistId}','${track.id}')"
          aria-label="Добавить в плейлист" title="Добавить">+</button>
      </div>
    </div>
  `;
}

async function addTrackToUserPlaylistDirect(playlistId, trackId) {
  try {
    const prev = Array.isArray(state.userPlaylistTrackIdCache?.[playlistId])
      ? state.userPlaylistTrackIdCache[playlistId]
      : [];
    const prevIds = prev.map((x) => (x && typeof x === 'object' ? x.id : x)).filter(Boolean).map(String);
    const nextTracks = Array.from(new Set([String(trackId), ...prevIds]));

    await apiFetch('PUT', `/api/me/user-playlists/${playlistId}/tracks`, { tracks: nextTracks });
    state.userPlaylistTrackIdCache = state.userPlaylistTrackIdCache || {};
    state.userPlaylistTrackIdCache[playlistId] = nextTracks;
    toast('Добавлено в плейлист', 'success');
    closeUserPlaylistModal('user-playlist-add-tracks-modal');
    await syncUserPlaylistPageIfVisible(playlistId);
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
}

async function toggleUserPlaylistLike(playlistId, isLikedNow) {
  if (!state.user) {
    openAuthModal('login');
    return;
  }
  try {
    const data = isLikedNow
      ? await apiFetch('DELETE', withUserPlaylistAccessToken(`/api/user-playlists/${playlistId}/like`))
      : await apiFetch('POST', withUserPlaylistAccessToken(`/api/user-playlists/${playlistId}/like`));
    const btn = $('user-playlist-like-btn');
    if (btn) {
      const liked = !!data?.likedByMe;
      btn.classList.toggle('liked', liked);
      btn.setAttribute('aria-pressed', liked ? 'true' : 'false');
      btn.dataset.liked = liked ? '1' : '0';
      btn.title = liked ? 'Убрать лайк' : 'Поставить лайк';
    }
    const countEl = $('user-playlist-likes-count');
    if (countEl) countEl.textContent = String(Number(data?.likesCount) || 0);
  } catch (e) {
    toast(e.message || 'Не удалось поставить лайк', 'error');
  }
}

let userPlaylistReplyTargetId = null;
const userPlaylistCollapsedThreadIds = new Set();
/** Сворачивание ветки и «Показать N ответов» — только если прямых ответов не меньше этого числа. */
const UPL_THREAD_COLLAPSE_REPLY_MIN = 4;
/** Кэш списка комментариев для мгновенного UI без GET (сворачивание, «Ответить»). */
const userPlaylistCommentsCache = new Map();

function pluralRuReplies(n) {
  const abs = Math.abs(Number(n)) % 100;
  const rem = abs % 10;
  if (abs > 10 && abs < 20) return 'ответов';
  if (rem === 1) return 'ответ';
  if (rem >= 2 && rem <= 4) return 'ответа';
  return 'ответов';
}

function userPlaylistCommentInputKeydown(ev, playlistId) {
  if (ev.isComposing || ev.keyCode === 229) return;
  if (ev.key !== 'Enter' || ev.shiftKey) return;
  ev.preventDefault();
  submitUserPlaylistComment(playlistId);
}

function userPlaylistReplyTextareaKeydown(ev, playlistId, parentId) {
  if (ev.isComposing || ev.keyCode === 229) return;
  if (ev.key !== 'Enter' || ev.shiftKey) return;
  ev.preventDefault();
  submitUserPlaylistReply(playlistId, parentId);
}

function toggleUserPlaylistThreadCollapse(playlistId, commentId) {
  if (userPlaylistCollapsedThreadIds.has(commentId)) userPlaylistCollapsedThreadIds.delete(commentId);
  else userPlaylistCollapsedThreadIds.add(commentId);
  void renderUserPlaylistComments(playlistId, { silent: true, skipFetch: true });
}

function userPlaylistThreadRailKeydown(ev, playlistId, commentId) {
  if (ev.key !== 'Enter' && ev.key !== ' ') return;
  ev.preventDefault();
  toggleUserPlaylistThreadCollapse(playlistId, commentId);
}

function userPlaylistCommentItemHtml(comment, playlistId, commentsById) {
  const canManage = !!comment?.canManage;
  const username = comment?.username ? `@${esc(comment.username)}` : 'Пользователь';
  const isPlaylistOwner = !!comment?.isPlaylistOwner;
  const profileUid = comment?.userId ? profileJsId(comment.userId) : '';
  const avatar = comment?.avatarUrl
    ? `<img src="${esc(resolveMediaUrl(comment.avatarUrl))}" alt="${esc(username)}">`
    : `<div class="upl-comment-avatar-fallback">${esc((comment?.username || 'U').slice(0, 1).toUpperCase())}</div>`;
  const encodedText = encodeURIComponent(String(comment.text || ''));
  const likesCount = Number(comment?.likesCount) || 0;
  const dislikesCount = Number(comment?.dislikesCount) || 0;
  const myReaction = Number(comment?.myReaction) || 0;
  const voteScore = likesCount - dislikesCount;
  const replyOpen = userPlaylistReplyTargetId === comment.id;
  const upOn = myReaction === 1;
  const downOn = myReaction === -1;
  const voteUpSvg = `<svg class="upl-vote-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5L5 16h14L12 5z" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linejoin="round"/></svg>`;
  const voteDownSvg = `<svg class="upl-vote-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19l7-11H5l7 11z" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linejoin="round"/></svg>`;
  let replyContextRow = '';
  if (comment.parentId && commentsById) {
    const parent = commentsById.get(comment.parentId);
    if (parent) {
      const pName = parent.username ? `@${esc(parent.username)}` : 'Пользователь';
      replyContextRow = `
        <div class="upl-comment-reply-context">
          <span class="upl-comment-reply-context-text">Ответ <strong>${pName}</strong></span>
        </div>`;
    }
  }
  return `
    <div class="upl-comment-item" data-comment-id="${comment.id}">
      <div class="upl-comment-body">
        ${replyContextRow}
        <div class="upl-comment-head">
          <div class="upl-comment-author">
            ${profileUid ? `<button type="button" class="upl-comment-avatar-btn" onclick="event.stopPropagation();navigateToUserProfile('${profileUid}')" aria-label="Профиль">` : '<div class="upl-comment-avatar-wrap">'}
            <div class="upl-comment-avatar">${avatar}</div>
            ${profileUid ? '</button>' : '</div>'}
            <div class="upl-comment-meta">
              ${profileUid ? `<button type="button" class="upl-comment-user upl-comment-user-link" onclick="event.stopPropagation();navigateToUserProfile('${profileUid}')">${username}</button>` : `<span class="upl-comment-user">${username}</span>`}
              ${isPlaylistOwner ? '<span class="upl-comment-owner-badge">Автор плейлиста</span>' : ''}
              <span class="upl-comment-sep" aria-hidden="true">·</span>
              <span class="upl-comment-date">${formatCommentDate(comment.createdAt)}</span>
            </div>
          </div>
          ${canManage ? `
            <div class="upl-comment-menu-wrap">
              <button type="button" class="upl-comment-more-btn" onclick="event.stopPropagation();toggleUserPlaylistCommentMenu('${comment.id}', this)" aria-label="Меню комментария">⋯</button>
              <div class="upl-comment-menu hidden" id="upl-comment-menu-${comment.id}">
                <button type="button" onclick="event.stopPropagation();startInlineEditUserPlaylistComment('${playlistId}', '${comment.id}', '${encodedText}')">Редактировать</button>
                <button type="button" class="danger" onclick="event.stopPropagation();deleteUserPlaylistComment('${playlistId}', '${comment.id}')">Удалить</button>
              </div>
            </div>
          ` : ''}
        </div>
        <div class="upl-comment-text">${esc(comment.text || '')}</div>
        <div class="upl-comment-toolbar" aria-label="Действия">
          <div class="upl-comment-vote" aria-label="Голосование">
            <button type="button" class="upl-vote-btn upl-vote-up${upOn ? ' is-on' : ''}"
              aria-pressed="${upOn ? 'true' : 'false'}" title="Плюс 1"
              onclick="toggleUserPlaylistCommentReaction('${playlistId}','${comment.id}', 1, ${myReaction})">
              ${voteUpSvg}
            </button>
            <span class="upl-vote-score${voteScore > 0 ? ' is-pos' : voteScore < 0 ? ' is-neg' : ''}" title="Рейтинг">${voteScore}</span>
            <button type="button" class="upl-vote-btn upl-vote-down${downOn ? ' is-on' : ''}"
              aria-pressed="${downOn ? 'true' : 'false'}" title="Минус 1"
              onclick="toggleUserPlaylistCommentReaction('${playlistId}','${comment.id}', -1, ${myReaction})">
              ${voteDownSvg}
            </button>
          </div>
          <button type="button" class="upl-comment-reply-btn" onclick="toggleUserPlaylistReply('${playlistId}','${comment.id}')">Ответить</button>
        </div>
        ${replyOpen ? `
          <div class="upl-comment-reply-form">
            <textarea id="upl-reply-input-${comment.id}" maxlength="500" placeholder="Ваш ответ... (Enter — отправить, Shift+Enter — новая строка)"
              onkeydown="userPlaylistReplyTextareaKeydown(event, '${playlistId}', '${comment.id}')"></textarea>
            <div class="upl-comment-reply-actions">
              <button type="button" class="btn-ghost" onclick="toggleUserPlaylistReply('${playlistId}','${comment.id}')">Отмена</button>
              <button type="button" class="btn-primary" onclick="submitUserPlaylistReply('${playlistId}','${comment.id}')">Ответить</button>
            </div>
          </div>
        ` : ''}
      </div>
    </div>
  `;
}

function buildUserPlaylistCommentTree(comments) {
  const byParent = new Map();
  const commentsById = new Map(comments.map((c) => [c.id, c]));
  comments.forEach((c) => {
    const key = c.parentId || 'root';
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(c);
  });

  const renderNode = (comment, playlistId, depth = 0) => {
    const children = byParent.get(comment.id) || [];
    const hasChildren = children.length > 0;
    const canCollapseBranch = hasChildren && children.length >= UPL_THREAD_COLLAPSE_REPLY_MIN;
    const collapsed = canCollapseBranch && userPlaylistCollapsedThreadIds.has(comment.id);
    const isNested = !!comment.parentId;
    const railClickable = canCollapseBranch && !collapsed;
    const railClick =
      railClickable
        ? ` onclick="event.stopPropagation();toggleUserPlaylistThreadCollapse('${playlistId}','${comment.id}')" onkeydown="userPlaylistThreadRailKeydown(event,'${playlistId}','${comment.id}')"`
        : '';

    const collapsedHint =
      hasChildren && collapsed && canCollapseBranch
        ? `
        <div class="upl-thread-collapsed-hint">
          <button type="button" class="upl-thread-expand-link"
            onclick="event.stopPropagation();toggleUserPlaylistThreadCollapse('${playlistId}','${comment.id}')">
            Показать ${children.length} ${pluralRuReplies(children.length)}
          </button>
        </div>`
        : '';

    const childrenHtml =
      hasChildren && !collapsed
        ? `
        <div class="upl-comment-children-outer" style="--upl-parent-depth:${depth}">
          <div class="upl-comment-children-rail${railClickable ? ' is-clickable' : ''}"${railClickable ? ` role="button" tabindex="0" aria-label="Свернуть ответы" title="Свернуть ветку"` : ' aria-hidden="true"'}${railClick}></div>
          <div class="upl-comment-children">
            ${children.map((child) => renderNode(child, playlistId, depth + 1)).join('')}
          </div>
        </div>`
        : '';

    return `
      <div class="upl-comment-thread${collapsed ? ' is-collapsed' : ''}${isNested ? ' upl-comment-thread--nested' : ''}" data-thread-id="${comment.id}"${isNested ? ` style="--upl-thread-depth:${depth}"` : ''}>
        <div class="upl-thread-main">
          ${userPlaylistCommentItemHtml(comment, playlistId, commentsById)}
          ${collapsedHint}
          ${childrenHtml}
        </div>
      </div>
    `;
  };

  return (playlistId) => {
    const roots = byParent.get('root') || [];
    return roots.map((c) => renderNode(c, playlistId, 0)).join('');
  };
}

function closeAllUserPlaylistCommentMenus() {
  document.querySelectorAll('.upl-comment-menu[data-ported="1"]').forEach((menu) => {
    const originParent = menu.__mxmOriginParent;
    if (originParent && originParent.isConnected) originParent.appendChild(menu);
    menu.removeAttribute('data-ported');
    menu.__mxmOriginParent = null;
    menu.style.position = '';
    menu.style.left = '';
    menu.style.top = '';
    menu.style.right = '';
    menu.style.zIndex = '';
  });
  document.querySelectorAll('.upl-comment-menu').forEach((menu) => menu.classList.add('hidden'));
}

if (!window.__mxmUserPlaylistCommentMenuBound) {
  window.__mxmUserPlaylistCommentMenuBound = true;
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.upl-comment-menu-wrap')) closeAllUserPlaylistCommentMenus();
  });
}

function toggleUserPlaylistCommentMenu(commentId, btnEl) {
  const menu = $('upl-comment-menu-' + commentId);
  if (!menu) return;
  const willOpen = menu.classList.contains('hidden');
  closeAllUserPlaylistCommentMenus();
  if (!willOpen) return;

  const btn = btnEl || null;
  const r = btn?.getBoundingClientRect?.();
  menu.__mxmOriginParent = menu.parentElement || null;
  document.body.appendChild(menu);
  menu.dataset.ported = '1';
  menu.classList.remove('hidden');

  if (!r) return;
  const margin = 10;
  const menuRect = menu.getBoundingClientRect();
  let left = r.right - menuRect.width;
  left = Math.max(margin, Math.min(left, window.innerWidth - menuRect.width - margin));
  let top = r.bottom + 8;
  if (top + menuRect.height > window.innerHeight - margin) {
    top = Math.max(margin, r.top - menuRect.height - 8);
  }
  menu.style.position = 'fixed';
  menu.style.zIndex = '10060';
  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;
  menu.style.right = 'auto';
}

function startInlineEditUserPlaylistComment(playlistId, commentId, encodedText) {
  closeAllUserPlaylistCommentMenus();
  const item = document.querySelector(`.upl-comment-item[data-comment-id="${commentId}"]`);
  if (!item) return;
  const currentText = decodeURIComponent(String(encodedText || ''));
  const textEl = item.querySelector('.upl-comment-text');
  if (!textEl) return;
  if (item.querySelector('.upl-comment-edit-wrap')) return;

  textEl.classList.add('hidden');
  const editWrap = document.createElement('div');
  editWrap.className = 'upl-comment-edit-wrap';
  editWrap.innerHTML = `
    <textarea class="upl-comment-edit-input" maxlength="500"></textarea>
    <div class="upl-comment-edit-actions">
      <button type="button" class="btn-ghost" onclick="cancelInlineEditUserPlaylistComment('${commentId}')">Отмена</button>
      <button type="button" class="btn-primary" onclick="saveInlineEditUserPlaylistComment('${playlistId}','${commentId}')">Сохранить</button>
    </div>
  `;
  const bodyEl = item.querySelector('.upl-comment-body') || item;
  const toolbar = bodyEl.querySelector('.upl-comment-toolbar');
  if (toolbar) bodyEl.insertBefore(editWrap, toolbar);
  else bodyEl.appendChild(editWrap);
  const input = editWrap.querySelector('.upl-comment-edit-input');
  if (input) {
    input.value = currentText;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }
}

function cancelInlineEditUserPlaylistComment(commentId) {
  const item = document.querySelector(`.upl-comment-item[data-comment-id="${commentId}"]`);
  if (!item) return;
  item.querySelector('.upl-comment-edit-wrap')?.remove();
  item.querySelector('.upl-comment-text')?.classList.remove('hidden');
}

async function saveInlineEditUserPlaylistComment(playlistId, commentId) {
  const item = document.querySelector(`.upl-comment-item[data-comment-id="${commentId}"]`);
  const input = item?.querySelector('.upl-comment-edit-input');
  if (!input) return;
  const text = String(input.value || '').trim();
  if (!text) {
    toast('Комментарий не может быть пустым', 'error');
    return;
  }
  try {
    await apiFetch('PUT', withUserPlaylistAccessToken(`/api/user-playlists/${playlistId}/comments/${commentId}`), { text });
    await renderUserPlaylistComments(playlistId, { silent: true });
  } catch (e) {
    toast(e.message || 'Не удалось обновить комментарий', 'error');
  }
}

async function renderUserPlaylistComments(playlistId, options = {}) {
  const list = $('upl-comments-list');
  if (!list) return;
  const silent = options.silent === true;
  let skipFetch = options.skipFetch === true;

  if (!silent) {
    list.innerHTML = loadingSpinner();
  }

  try {
    let safe;
    if (skipFetch) {
      safe = userPlaylistCommentsCache.get(playlistId);
      if (!Array.isArray(safe)) skipFetch = false;
    }
    if (!skipFetch) {
      const comments = await GET(withUserPlaylistAccessToken(`/api/user-playlists/${playlistId}/comments`));
      safe = Array.isArray(comments) ? comments : [];
      userPlaylistCommentsCache.set(playlistId, safe);
    }

    const renderTree = buildUserPlaylistCommentTree(safe);
    list.innerHTML = safe.length
      ? renderTree(playlistId)
      : `<div class="upl-comments-empty">Пока нет комментариев. Будь первым!</div>`;
    const commentsCount = $('user-playlist-comments-count');
    if (commentsCount) commentsCount.textContent = String(safe.length);
  } catch (e) {
    if (!silent) {
      list.innerHTML = `<div class="upl-comments-empty">Не удалось загрузить комментарии</div>`;
    } else {
      toast(e?.message || 'Не удалось обновить комментарии', 'error');
    }
  }
}

async function submitUserPlaylistComment(playlistId) {
  if (!state.user) {
    openAuthModal('login');
    return;
  }
  const input = $('upl-comment-input');
  const sendBtn = $('upl-comment-send-btn');
  if (!input) return;
  const text = String(input.value || '').trim();
  if (!text) return;
  try {
    if (sendBtn) sendBtn.disabled = true;
    await POST(withUserPlaylistAccessToken(`/api/user-playlists/${playlistId}/comments`), { text });
    input.value = '';
    userPlaylistReplyTargetId = null;
    await renderUserPlaylistComments(playlistId, { silent: true });
  } catch (e) {
    toast(e.message || 'Не удалось отправить комментарий', 'error');
  } finally {
    if (sendBtn) sendBtn.disabled = false;
  }
}

function toggleUserPlaylistReply(playlistId, commentId) {
  userPlaylistReplyTargetId = userPlaylistReplyTargetId === commentId ? null : commentId;
  if (playlistId) void renderUserPlaylistComments(playlistId, { silent: true, skipFetch: true });
}

async function submitUserPlaylistReply(playlistId, parentId) {
  if (!state.user) {
    openAuthModal('login');
    return;
  }
  const input = $(`upl-reply-input-${parentId}`);
  const text = String(input?.value || '').trim();
  if (!text) return;
  try {
    await POST(withUserPlaylistAccessToken(`/api/user-playlists/${playlistId}/comments`), { text, parentId });
    userPlaylistReplyTargetId = null;
    await renderUserPlaylistComments(playlistId, { silent: true });
  } catch (e) {
    toast(e.message || 'Не удалось отправить ответ', 'error');
  }
}

async function toggleUserPlaylistCommentReaction(playlistId, commentId, nextReaction, currentReaction) {
  if (!state.user) {
    openAuthModal('login');
    return;
  }
  try {
    const value = Number(currentReaction) === Number(nextReaction) ? 0 : Number(nextReaction);
    await apiFetch(
      'PUT',
      withUserPlaylistAccessToken(`/api/user-playlists/${playlistId}/comments/${commentId}/reaction`),
      { value }
    );
    await renderUserPlaylistComments(playlistId, { silent: true });
  } catch (e) {
    toast(e.message || 'Не удалось обновить реакцию', 'error');
  }
}

async function deleteUserPlaylistComment(playlistId, commentId) {
  try {
    closeAllUserPlaylistCommentMenus();
    await apiFetch('DELETE', withUserPlaylistAccessToken(`/api/user-playlists/${playlistId}/comments/${commentId}`));
    await renderUserPlaylistComments(playlistId, { silent: true });
  } catch (e) {
    toast(e.message || 'Не удалось удалить комментарий', 'error');
  }
}

// ─── User profile ────────────────────────────────────────────────────────────

function formatProfileDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatProfileCommentDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function profileCommentKarmaHtml(c) {
  const likes = Number(c?.likesCount) || 0;
  const dislikes = Number(c?.dislikesCount) || 0;
  const karma = likes - dislikes;
  const cls = karma > 0 ? 'is-pos' : karma < 0 ? 'is-neg' : '';
  return `<span class="profile-comment-karma upl-vote-score ${cls}" title="Карма (плюсы и минусы)">${karma}</span>`;
}

function profileCommentCardHtml(c) {
  return `
          <div class="profile-comment-card" data-nav="user-playlist" data-nav-id="${esc(c.playlistId)}" onclick="handleDataNavClick(event)">
            <div class="profile-comment-pl">${esc(c.playlistTitle)}</div>
            <div class="profile-comment-meta">
              <span>${esc(formatProfileCommentDate(c.createdAt))}</span>
              <span class="profile-comment-sep" aria-hidden="true">·</span>
              ${profileCommentKarmaHtml(c)}
            </div>
            <div class="profile-comment-text">${esc(c.text)}</div>
          </div>`;
}

function profileHeroAvatarHtml(u, { editable = false } = {}) {
  const name = String(u.username || '').trim();
  const letter = (name.charAt(0) || '?').toUpperCase();
  const raw = u.avatarUrl ? resolveMediaUrl(u.avatarUrl) : '';
  const inner = raw
    ? `<div id="profile-page-avatar-root" class="profile-hero-avatar has-image"><img src="${esc(raw)}" alt="" loading="lazy" decoding="async" referrerpolicy="no-referrer"></div>`
    : `<div id="profile-page-avatar-root" class="profile-hero-avatar">${esc(letter)}</div>`;
  if (editable) {
    return `<button type="button" class="profile-hero-avatar-hit" onclick="openProfileAvatarFlow()" title="Сменить фото" aria-label="Сменить фото">${inner}</button>`;
  }
  return inner;
}

function profileIsEditMode() {
  const shell = $('profile-page-shell');
  return !!(shell && shell.classList.contains('profile-page--edit'));
}

function profileToggleEditMode() {
  const shell = $('profile-page-shell');
  if (!shell?.classList.contains('profile-page--owner')) return;
  const toEdit = !shell.classList.contains('profile-page--edit');
  if (toEdit) {
    shell.classList.remove('profile-page--view');
    shell.classList.add('profile-page--edit');
  } else {
    shell.classList.add('profile-page--view');
    shell.classList.remove('profile-page--edit');
    profileCancelBioEdit();
  }
  const input = $('profile-username-input');
  if (input && lastProfileViewData) input.value = lastProfileViewData.username || '';
  const titleEl = $('profile-hero-username-title');
  if (titleEl && lastProfileViewData) titleEl.textContent = lastProfileViewData.username || '';
  refreshProfileHeroAvatarInDom();
}

function shareUserProfile(userRef) {
  const id = String(userRef || '').trim();
  if (!id) return;
  const url = `${getPublicSiteBase() + window.location.pathname}user/${encodeURIComponent(id)}`;
  copyPlainTextToClipboard(url).then((ok) => {
    if (ok) toast('Ссылка на профиль скопирована', 'success');
    else toast('Не удалось скопировать', 'error');
  });
}

async function saveUserProfileUsername() {
  if (!lastProfileViewData?.isOwner) return;
  const input = $('profile-username-input');
  if (!input) return;
  try {
    await patchMyProfile({ username: input.value });
    toast('Никнейм сохранён', 'success');
    await profileSyncFromServer();
    const titleEl = $('profile-hero-username-title');
    if (titleEl && lastProfileViewData) titleEl.textContent = lastProfileViewData.username || '';
    input.value = lastProfileViewData.username || '';
    setQueuePlayingFromLabel(`Профиль: ${lastProfileViewData.username}`);
  } catch (e) {
    toast(e.message || 'Не удалось сохранить', 'error');
  }
}

function refreshProfileHeroAvatarInDom() {
  const innerEl = $('profile-page-avatar-root');
  if (!innerEl || !state.user || state.view !== 'profile') return;
  if (lastProfileViewData?.id && lastProfileViewData.id !== state.user.id) return;
  const editable = !!(
    lastProfileViewData?.isOwner &&
    state.user?.id &&
    lastProfileViewData?.id === state.user.id &&
    profileIsEditMode()
  );
  const html = profileHeroAvatarHtml(
    { username: lastProfileViewData?.username || state.user.username, avatarUrl: state.user.avatarUrl },
    { editable }
  );
  const hit = innerEl.closest('.profile-hero-avatar-hit');
  if (hit) hit.outerHTML = html;
  else innerEl.outerHTML = html;
  void refreshProfileHeroGradientInDom();
}

async function refreshProfileHeroGradientInDom() {
  const hero = document.querySelector('.page-hero.profile-page-hero');
  if (!hero || state.view !== 'profile') return;
  const owner = lastProfileViewData?.isOwner && state.user?.id === lastProfileViewData?.id;
  const avatarUrl = owner ? state.user?.avatarUrl : lastProfileViewData?.avatarUrl;
  let dynamicGrad = null;
  if (avatarUrl) {
    try {
      dynamicGrad = await getGradientFromImage(avatarUrl);
    } catch {
      dynamicGrad = null;
    }
  }
  const fallback = pickGrad('profile-' + (lastProfileViewData?.id || state.user?.id || 'guest'));
  const innerGrad = dynamicGrad || fallback;
  hero.style.setProperty('--hero-grad', `linear-gradient(${innerGrad})`);
}

function wrapShowcaseArtist(a, isOwner) {
  const inner = artistCard(a);
  const sid = profileJsId(a.id);
  if (!isOwner) return `<div class="profile-showcase-cell">${inner}</div>`;
  return `<div class="profile-showcase-cell">
    ${inner}
    <button type="button" class="profile-showcase-remove profile-edit-only" onclick="event.stopPropagation();profileRemoveShowcaseArtist('${sid}')" aria-label="Убрать из витрины">×</button>
  </div>`;
}

function wrapShowcaseAlbum(album, isOwner) {
  const inner = featuredCard(album);
  const sid = profileJsId(album.id);
  if (!isOwner) return `<div class="profile-showcase-cell profile-showcase-cell-wide">${inner}</div>`;
  return `<div class="profile-showcase-cell profile-showcase-cell-wide">
    ${inner}
    <button type="button" class="profile-showcase-remove profile-edit-only" onclick="event.stopPropagation();profileRemoveShowcaseAlbum('${sid}')" aria-label="Убрать из витрины">×</button>
  </div>`;
}

async function patchMyProfile(body) {
  const data = await PATCH('/api/me/profile', body);
  if (data.user && state.user && data.user.id === state.user.id) {
    state.user = { ...state.user, ...data.user };
    if (state.user.avatarUrl) state.user.avatarUrl = cacheAvatarUrl(state.user.avatarUrl);
    updateUserUI();
  }
  return data;
}

function profileApplyDomFromData(data) {
  const mine = !!data.isOwner;
  const artists = Array.isArray(data.showcaseArtists) ? data.showcaseArtists : [];
  const albums = Array.isArray(data.showcaseAlbums) ? data.showcaseAlbums : [];
  const playlists = Array.isArray(data.playlists) ? data.playlists : [];
  const comments = Array.isArray(data.comments) ? data.comments : [];

  const artSec = $('profile-showcase-artists-section');
  if (!artists.length) {
    artSec?.remove();
  } else {
    const sa = $('profile-showcase-artists');
    if (sa) sa.innerHTML = artists.map((a) => wrapShowcaseArtist(a, mine)).join('');
  }
  const albSec = $('profile-showcase-albums-section');
  if (!albums.length) {
    albSec?.remove();
  } else {
    const sal = $('profile-showcase-albums');
    if (sal) sal.innerHTML = albums.map((a) => wrapShowcaseAlbum(a, mine)).join('');
  }
  const plSec = $('profile-playlists-section');
  if (!playlists.length) {
    plSec?.remove();
  } else {
    const pg = $('profile-playlists-inner');
    if (pg) {
      pg.innerHTML = `<div class="upl-cards-grid profile-playlists-grid">${playlists.map((p) => userPlaylistSquareCard(p, { mine })).join('')}</div>`;
      if (mine && state.user) playlists.forEach((p) => void prefetchUserPlaylistShareToken(p.id));
    }
  }
  const cl = $('profile-comments-inner');
  if (cl) {
    cl.innerHTML = comments.length
      ? `<div class="profile-comments-list">${comments.map((c) => profileCommentCardHtml(c)).join('')}</div>`
      : `<div class="profile-empty-hint">Пока нет комментариев к плейлистам.</div>`;
  }
  const bioDisp = $('profile-bio-text-display');
  if (bioDisp) {
    if (!mine) {
      bioDisp.innerHTML = data.bio ? esc(data.bio) : 'Пользователь пока ничего не написал.';
    } else {
      const empty = !String(data.bio || '').trim();
      bioDisp.textContent = empty ? 'Нажмите «карандаш», чтобы рассказать о себе.' : String(data.bio || '');
      bioDisp.classList.toggle('profile-bio-placeholder', empty);
    }
  }
  const ta = $('profile-bio-text');
  if (ta && mine) ta.value = data.bio || '';
}

async function profileSyncFromServer() {
  if (!lastProfileViewData?.id || state.view !== 'profile') return;
  try {
    const data = await GET(`/api/users/${encodeURIComponent(lastProfileViewData.id)}/profile`);
    lastProfileViewData = data;
    profileApplyDomFromData(data);
  } catch (e) {
    toast(e.message || 'Не удалось обновить профиль', 'error');
  }
}

async function profileRemoveShowcaseArtist(id) {
  if (!lastProfileViewData?.isOwner || !id) return;
  const artistIds = (lastProfileViewData.showcaseArtists || []).map((x) => x.id).filter((x) => x !== id);
  const albumIds = (lastProfileViewData.showcaseAlbums || []).map((x) => x.id);
  try {
    await patchMyProfile({ showcaseArtistIds: artistIds, showcaseAlbumIds: albumIds });
    toast('Витрина обновлена', 'success');
    await profileSyncFromServer();
  } catch (e) {
    toast(e.message || 'Не удалось сохранить', 'error');
  }
}

async function profileRemoveShowcaseAlbum(id) {
  if (!lastProfileViewData?.isOwner || !id) return;
  const artistIds = (lastProfileViewData.showcaseArtists || []).map((x) => x.id);
  const albumIds = (lastProfileViewData.showcaseAlbums || []).map((x) => x.id).filter((x) => x !== id);
  try {
    await patchMyProfile({ showcaseArtistIds: artistIds, showcaseAlbumIds: albumIds });
    toast('Витрина обновлена', 'success');
    await profileSyncFromServer();
  } catch (e) {
    toast(e.message || 'Не удалось сохранить', 'error');
  }
}

async function profileCommitShowcaseBatch(kind, rawIds) {
  if (!lastProfileViewData?.isOwner || !rawIds?.length) return;
  const artistIds = [...(lastProfileViewData.showcaseArtists || []).map((x) => x.id)];
  const albumIds = [...(lastProfileViewData.showcaseAlbums || []).map((x) => x.id)];
  const max = 18;
  let added = 0;
  for (const rawId of rawIds) {
    if (kind === 'artist') {
      if (artistIds.includes(rawId) || artistIds.length >= max) continue;
      artistIds.push(rawId);
      added += 1;
    } else if (albumIds.includes(rawId) || albumIds.length >= max) {
      continue;
    } else {
      albumIds.push(rawId);
      added += 1;
    }
  }
  if (!added) {
    toast('Нечего добавлять (уже в витрине или лимит)');
    return;
  }
  try {
    await patchMyProfile({ showcaseArtistIds: artistIds, showcaseAlbumIds: albumIds });
    toast('Витрина обновлена', 'success');
    const uid = lastProfileViewData.id;
    const data = await GET(`/api/users/${encodeURIComponent(uid)}/profile`);
    lastProfileViewData = data;
    const a = Array.isArray(data.showcaseArtists) ? data.showcaseArtists : [];
    const b = Array.isArray(data.showcaseAlbums) ? data.showcaseAlbums : [];
    const needFull =
      (a.length && !document.getElementById('profile-showcase-artists-section')) ||
      (b.length && !document.getElementById('profile-showcase-albums-section'));
    if (needFull) await renderUserProfile(uid);
    else profileApplyDomFromData(data);
  } catch (e) {
    toast(e.message || 'Не удалось сохранить', 'error');
  }
}

function profileOpenShowcasePicker(kind) {
  if (!lastProfileViewData?.isOwner) return;
  const existing =
    kind === 'artist'
      ? new Set((lastProfileViewData.showcaseArtists || []).map((x) => x.id))
      : new Set((lastProfileViewData.showcaseAlbums || []).map((x) => x.id));
  const pending = new Map();
  const overlay = document.createElement('div');
  overlay.className = 'profile-showcase-overlay profile-showcase-overlay--rich';
  const kindLabel = kind === 'artist' ? 'исполнителей' : 'альбомов';
  overlay.innerHTML = `
    <div class="profile-showcase-dialog profile-showcase-dialog--wide" role="dialog" aria-label="Поиск">
      <div class="profile-showcase-dialog-head">
        <span class="profile-showcase-dialog-title">Добавить ${esc(kindLabel)}</span>
        <button type="button" class="profile-showcase-dialog-close" aria-label="Закрыть">×</button>
      </div>
      <p class="profile-showcase-dialog-lead">Введите запрос и нажмите на строку в результатах — она появится в списке ниже. Лишнее можно убрать из списка до сохранения.</p>
      <input type="search" class="profile-showcase-dialog-input" id="profile-showcase-q" placeholder="Поиск…" autocomplete="off" />
      <div class="profile-showcase-dialog-results" id="profile-showcase-results"></div>
      <div class="profile-showcase-preview-wrap">
        <div class="profile-showcase-preview-title">К добавлению</div>
        <div id="profile-showcase-preview" class="profile-showcase-preview"></div>
      </div>
      <div class="profile-showcase-dialog-footer">
        <button type="button" class="btn-ghost" id="profile-showcase-cancel">Отмена</button>
        <button type="button" class="btn-primary" id="profile-showcase-add-btn" disabled>Добавить</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const qEl = overlay.querySelector('#profile-showcase-q');
  const resultsEl = overlay.querySelector('#profile-showcase-results');
  const previewEl = overlay.querySelector('#profile-showcase-preview');
  const addBtn = overlay.querySelector('#profile-showcase-add-btn');
  let timer = null;

  const syncAddBtn = () => {
    const n = pending.size;
    addBtn.disabled = n === 0;
    addBtn.textContent = n ? `Добавить (${n})` : 'Добавить';
  };

  const renderPreview = () => {
    if (!pending.size) {
      previewEl.innerHTML = '<div class="profile-showcase-preview-empty">Пока пусто — выберите из поиска выше</div>';
      syncAddBtn();
      return;
    }
    previewEl.innerHTML = Array.from(pending.entries())
      .map(
        ([pid, label]) => `
      <div class="profile-showcase-preview-row">
        <span class="profile-showcase-preview-label">${esc(label)}</span>
        <button type="button" class="profile-showcase-preview-remove" data-rid="${esc(pid)}" aria-label="Убрать">×</button>
      </div>`
      )
      .join('');
    previewEl.querySelectorAll('.profile-showcase-preview-remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const rid = btn.getAttribute('data-rid');
        if (rid) pending.delete(rid);
        renderPreview();
        runSearch();
      });
    });
    syncAddBtn();
  };

  const runSearch = async () => {
    const q = (qEl.value || '').trim();
    if (q.length < 2) {
      resultsEl.innerHTML = '';
      return;
    }
    try {
      const r = await GET(`/api/search?q=${encodeURIComponent(q)}`);
      const items = kind === 'artist' ? r.artists || [] : r.albums || [];
      if (!items.length) {
        resultsEl.innerHTML = '<div class="profile-showcase-empty">Ничего не найдено</div>';
        return;
      }
      resultsEl.innerHTML = items
        .slice(0, 24)
        .map((it) => {
          const id = String(it.id || '');
          const inShow = existing.has(id);
          const inPending = pending.has(id);
          const labelHtml =
            kind === 'artist'
              ? esc(it.name || '')
              : `${esc(it.title || '')} — ${esc(it.artist || '')}`;
          return `<button type="button" class="profile-showcase-search-row${inShow ? ' is-in-showcase' : ''}${
            inPending ? ' is-pending' : ''
          }" data-pick-id="${esc(id)}" ${inShow ? 'disabled' : ''}>
            <span class="profile-showcase-search-row-text">${labelHtml}</span>
            ${inShow ? '<span class="profile-showcase-tag">в витрине</span>' : ''}
            ${!inShow && inPending ? '<span class="profile-showcase-tag">в списке</span>' : ''}
          </button>`;
        })
        .join('');
      resultsEl.querySelectorAll('.profile-showcase-search-row:not([disabled])').forEach((btn) => {
        btn.addEventListener('click', () => {
          const pid = btn.getAttribute('data-pick-id');
          if (!pid || existing.has(pid)) return;
          const labelText = btn.querySelector('.profile-showcase-search-row-text')?.textContent?.trim() || pid;
          if (pending.has(pid)) {
            pending.delete(pid);
            renderPreview();
            runSearch();
            return;
          }
          if (existing.size + pending.size >= 18) {
            toast('Не больше 18 позиций в витрине');
            return;
          }
          pending.set(pid, labelText);
          renderPreview();
          runSearch();
        });
      });
    } catch {
      resultsEl.innerHTML = '<div class="profile-showcase-empty">Ошибка поиска</div>';
    }
  };

  renderPreview();

  qEl.addEventListener(
    'input',
    () => {
      clearTimeout(timer);
      timer = setTimeout(runSearch, 280);
    },
    { passive: true }
  );
  overlay.querySelector('.profile-showcase-dialog-close').onclick = () => overlay.remove();
  overlay.querySelector('#profile-showcase-cancel').onclick = () => overlay.remove();
  addBtn.onclick = async () => {
    const ids = Array.from(pending.keys());
    overlay.remove();
    if (ids.length) await profileCommitShowcaseBatch(kind, ids);
  };
  /** Закрытие только по «честному» клику по фону: не при отпускании после выделения текста внутри окна. */
  let backdropPointerDown = false;
  overlay.addEventListener('pointerdown', (e) => {
    backdropPointerDown = e.target === overlay;
  });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay && backdropPointerDown) overlay.remove();
    backdropPointerDown = false;
  });
  qEl.focus();
}

function profileEnterBioEdit() {
  if (!lastProfileViewData?.isOwner) return;
  $('profile-bio-read-mode')?.classList.add('hidden');
  $('profile-bio-edit-mode')?.classList.remove('hidden');
  const ta = $('profile-bio-text');
  if (ta) ta.value = lastProfileViewData.bio || '';
  requestAnimationFrame(() => ta?.focus());
}

function profileCancelBioEdit() {
  $('profile-bio-read-mode')?.classList.remove('hidden');
  $('profile-bio-edit-mode')?.classList.add('hidden');
}

async function saveUserProfileBio() {
  if (!lastProfileViewData?.isOwner) return;
  const ta = $('profile-bio-text');
  if (!ta) return;
  try {
    await patchMyProfile({ bio: ta.value });
    toast('Описание сохранено', 'success');
    await profileSyncFromServer();
    profileCancelBioEdit();
  } catch (e) {
    toast(e.message || 'Не удалось сохранить', 'error');
  }
}

function goToMyProfile() {
  closeUserMenu();
  if (!state.user) {
    openAuthModal('login');
    return;
  }
  navigate('profile', { id: userProfileRef(state.user) });
}

async function renderUserProfile(userId) {
  const content = $('content');
  let id = userId;
  if (!id && state.user?.id) id = userProfileRef(state.user);
  if (!id) {
    content.innerHTML = `
      <div class="lib-empty" style="margin-top:48px">
        <h3>Нужен аккаунт</h3>
        <p>Войдите, чтобы открыть профиль.</p>
        <button class="btn-primary" style="margin-top:12px" onclick="openAuthModal('login')">Войти</button>
      </div>`;
    setQueuePlayingFromLabel('Профиль');
    return;
  }

  lastProfileViewData = null;
  content.innerHTML = loadingSpinner();
  try {
    const data = await GET(`/api/users/${encodeURIComponent(id)}/profile`);
    lastProfileViewData = data;
    let profileHeroGrad = pickGrad('profile-' + data.id);
    if (data.avatarUrl) {
      try {
        const fromAvatar = await getGradientFromImage(data.avatarUrl);
        if (fromAvatar) profileHeroGrad = fromAvatar;
      } catch {
        /* оставляем pickGrad */
      }
    }
    const reg = formatProfileDate(data.createdAt);
    const mine = !!data.isOwner;
    const artists = Array.isArray(data.showcaseArtists) ? data.showcaseArtists : [];
    const albums = Array.isArray(data.showcaseAlbums) ? data.showcaseAlbums : [];
    const playlists = Array.isArray(data.playlists) ? data.playlists : [];
    const uploaderTracks = Array.isArray(data.uploaderTracks) ? data.uploaderTracks : [];
    const comments = Array.isArray(data.comments) ? data.comments : [];

    const bioBlock = mine
      ? `<div id="profile-bio-card" class="profile-hero-bio-inner">
          <div id="profile-bio-read-mode" class="profile-bio-read-mode">
            <span class="profile-hero-bio-label">О себе</span>
            <div class="profile-bio-read-content">
              <span id="profile-bio-text-display" class="profile-bio-text-display"></span><button type="button" class="profile-bio-pencil profile-edit-only" onclick="profileEnterBioEdit()" title="Редактировать" aria-label="Редактировать">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/>
                </svg>
              </button>
            </div>
          </div>
          <div id="profile-bio-edit-mode" class="profile-bio-edit-mode hidden">
            <textarea id="profile-bio-text" class="profile-bio-textarea profile-bio-textarea--hero" maxlength="2000" rows="4" placeholder="Расскажите, что слушаете..."></textarea>
            <div class="profile-bio-edit-actions">
              <button type="button" class="btn-ghost" onclick="profileCancelBioEdit()">Отмена</button>
              <button type="button" class="btn-primary" onclick="saveUserProfileBio()">Сохранить</button>
            </div>
          </div>
        </div>`
      : `<div class="profile-hero-bio-inner profile-hero-bio-inner--guest">
          <span class="profile-hero-bio-label">О себе</span>
          <p id="profile-bio-text-display" class="profile-bio-text-display">${data.bio ? esc(data.bio) : 'Пользователь пока ничего не написал.'}</p>
        </div>`;

    const showcaseArtistsBlock = artists.length
      ? `<section id="profile-showcase-artists-section" class="profile-section profile-section--card">
        <div class="profile-section-head">
          <h3 class="profile-section-title">Любимые исполнители</h3>
          ${mine ? `<button type="button" class="btn-ghost btn-sm profile-edit-only" onclick="profileOpenShowcasePicker('artist')">Добавить</button>` : ''}
        </div>
        <div id="profile-showcase-artists" class="profile-showcase-strip">
          ${artists.map((a) => wrapShowcaseArtist(a, mine)).join('')}
        </div>
      </section>`
      : '';

    const showcaseAlbumsBlock = albums.length
      ? `<section id="profile-showcase-albums-section" class="profile-section profile-section--card">
        <div class="profile-section-head">
          <h3 class="profile-section-title">Любимые альбомы</h3>
          ${mine ? `<button type="button" class="btn-ghost btn-sm profile-edit-only" onclick="profileOpenShowcasePicker('album')">Добавить</button>` : ''}
        </div>
        <div id="profile-showcase-albums" class="profile-showcase-strip profile-showcase-strip-albums">
          ${albums.map((a) => wrapShowcaseAlbum(a, mine)).join('')}
        </div>
      </section>`
      : '';

    const uploaderTracksBlock = uploaderTracks.length
      ? `<section id="profile-uploader-tracks-section" class="profile-section profile-section--card">
        <h3 class="profile-section-title">${mine ? 'Мои треки' : 'Треки'}</h3>
        <p class="profile-uploader-tracks-hint">Он реально написал эти треки</p>
        <div class="track-list profile-uploader-tracks-list">
          ${uploaderTracks.map((t) => profileUploaderTrackRowHtml(t)).join('')}
        </div>
      </section>`
      : '';

    const playlistsBlock = playlists.length
      ? `<section id="profile-playlists-section" class="profile-section profile-section--card">
        <h3 class="profile-section-title">${mine ? 'Мои плейлисты' : 'Плейлисты'}</h3>
        <div id="profile-playlists-inner">
          <div class="upl-cards-grid profile-playlists-grid">${playlists.map((p) => userPlaylistSquareCard(p, { mine })).join('')}</div>
        </div>
      </section>`
      : '';

    const fc = Number(data.friendCount) || 0;
    const friendsHash = buildHash('profile-friends', { id: userProfileRef(data) });
    const friendsBlockHero = `<div id="profile-hero-friends" class="profile-hero-friends profile-hero-friends--compact">
          <a class="profile-friends-count-btn profile-friends-section-link" href="#${friendsHash}" data-nav="profile-friends" data-nav-id="${esc(userProfileRef(data))}" onclick="handleDataNavClick(event)" title="Список друзей">
            <span id="profile-friends-count-label">${esc(formatFriendsCountRu(fc))}</span>
            <svg class="profile-friends-section-chevron" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
          </a>
        </div>`;

    const heroSubHtml = reg ? `На сайте с ${esc(reg)}` : '';
    const showFriendCta = !mine && String(data.id) !== String(state.user?.id || '');
    const friendCtaInner = showFriendCta
      ? profileOtherUserFriendCtaHtml(
          data.id,
          data.friendshipWithViewer,
          data.username || '',
          userProfileRef(data),
          data.incomingFriendRequestId || ''
        )
      : '';
    const metaRowHtml =
      heroSubHtml || showFriendCta
        ? `<div class="profile-hero-meta-row">
        <div class="profile-hero-sub-wrap">${heroSubHtml ? `<div class="hero-sub">${heroSubHtml}</div>` : ''}</div>
        ${showFriendCta ? `<div class="profile-hero-cta-wrap">${friendCtaInner}</div>` : ''}
      </div>`
        : '';

    const commentsBlock = `
      <section class="profile-section profile-section--card">
        <h3 class="profile-section-title">Комментарии</h3>
        <div id="profile-comments-inner">
        ${
          comments.length
            ? `<div class="profile-comments-list">${comments.map((c) => profileCommentCardHtml(c)).join('')}</div>`
            : `<div class="profile-empty-hint">Пока нет комментариев к плейлистам.</div>`
        }
        </div>
      </section>`;

    if (mine && playlists.length && state.user) {
      await Promise.all(playlists.map((p) => prefetchUserPlaylistShareToken(p.id)));
    }

    content.innerHTML = `
      <div id="profile-page-shell" class="profile-page-shell${mine ? ' profile-page--owner profile-page--view' : ''}">
      <div class="page-hero profile-page-hero" style="--hero-grad:linear-gradient(${profileHeroGrad})">
        <button class="nav-back-btn" onclick="goBack()" title="Назад">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
          </svg>
        </button>
        <div class="profile-hero-toolbar" role="toolbar" aria-label="Действия профиля">
          <button type="button" class="profile-toolbar-btn" onclick="shareUserProfile('${profileJsId(userProfileRef(data))}')" title="Поделиться профилем" aria-label="Поделиться профилем">
            ${MXM_SHARE_ICON_SVG}
          </button>
          ${mine ? `<button type="button" class="profile-toolbar-btn profile-toolbar-btn--edit" onclick="profileToggleEditMode()" title="Изменить профиль" aria-label="Редактировать профиль">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
          </button>` : ''}
        </div>
        <div class="hero-bg"></div>
        <div class="hero-content profile-hero-row">
          ${profileHeroAvatarHtml(data, { editable: false })}
          <div class="hero-info profile-hero-text">
            <div class="hero-type">Профиль</div>
            <h1 id="profile-hero-username-title" class="hero-title profile-view-only">${esc(data.username)}</h1>
            ${mine ? `
            <div class="profile-username-edit profile-edit-only">
              <label class="profile-username-label" for="profile-username-input">Никнейм</label>
              <div class="profile-username-row">
                <input id="profile-username-input" class="profile-username-input" type="text" autocomplete="username" maxlength="32" value="${esc(data.username)}" />
                <button type="button" class="btn-primary btn-sm" onclick="saveUserProfileUsername()">Сохранить</button>
              </div>
            </div>` : ''}
            ${metaRowHtml}
            <div class="profile-hero-bio">${bioBlock}</div>
            ${friendsBlockHero}
          </div>
        </div>
      </div>
      ${mine ? `<div class="profile-edit-banner profile-edit-only" role="status">
        <div class="profile-edit-banner-inner">
          <span class="profile-edit-banner-dot" aria-hidden="true"></span>
          <p class="profile-edit-banner-text"><strong>Редактирование профиля</strong> — фото, ник, описание, витрина. Нажмите «Готово», когда закончите.</p>
          <button type="button" class="btn-primary profile-edit-banner-done" onclick="profileToggleEditMode()">Готово</button>
        </div>
      </div>` : ''}
      <div class="profile-page-body">
        ${showcaseArtistsBlock}
        ${showcaseAlbumsBlock}
        ${uploaderTracksBlock}
        ${playlistsBlock}
        ${commentsBlock}
      </div>
      </div>`;

    if (mine) {
      profileApplyDomFromData(data);
    }

    const wantRef = userProfileRef(data);
    if (wantRef && String(id).trim() !== String(wantRef) && looksLikeUserUuidClient(String(id).trim())) {
      try {
        suppressNavStackPush = true;
        history.replaceState(null, '', '#' + buildHash('profile', { id: wantRef }));
      } catch (_) {
        /* ignore */
      } finally {
        suppressNavStackPush = false;
      }
    }

    setQueuePlayingFromLabel(`Профиль: ${data.username}`);
  } catch (e) {
    content.innerHTML = `<div class="lib-empty" style="margin-top:32px"><h3>Не удалось загрузить профиль</h3><p>${esc(e.message || '')}</p></div>`;
    setQueuePlayingFromLabel('Профиль');
  }
}

function userPlaylistTrackRowCover(track, qKey, qIdx, canEdit, playlistId) {
  const liked = isFavT(track.id);
  const isPlaying = state.currentTrack?.id === track.id;
  const tagsHtml = htmlTrackListTagRow(track);
  const deleteBtn = canEdit
    ? `<button class="track-playlist-delete-btn" type="button"
        onclick="event.stopPropagation();removeTrackFromUserPlaylist('${playlistId}','${track.id}')"
        aria-label="Удалить из плейлиста" title="Удалить из плейлиста">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
          <polyline points="3 6 5 6 21 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <line x1="10" y1="11" x2="10" y2="17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <line x1="14" y1="11" x2="14" y2="17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>`
    : '';

  return `
    <div class="track-row track-row-w-cover ${isPlaying ? 'playing' : ''}" data-track-id="${track.id}" data-q-key="${qKey}" data-q-idx="${qIdx}"
      onclick="handleTrackRowPlayClick(event,'${qKey}',${qIdx})">
      <div class="track-cover-sm" style="position: relative;">
        ${coverImg(track.coverUrl, track.coverGradient || pickGrad(track.id))}
        <button type="button" class="track-cover-play" onclick="event.stopPropagation(); playFromQueue('${qKey}',${qIdx})" aria-label="Играть">
          <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </button>
      </div>
      <div class="track-info">
        ${tagsHtml}
        <div class="track-title">${esc(track.title)}</div>
        <div class="track-meta">${renderTrackMetaLineHtml(track)}</div>
      </div>
      <div class="track-right">
        <button type="button" class="track-fav-btn ${liked ? 'liked' : ''}" data-fav-track="${track.id}"
          onclick="event.stopPropagation();toggleFavTrack('${track.id}',event)" aria-label="В любимые">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" ${liked?'fill="currentColor"':'fill="none"'}/></svg>
        </button>
        <button class="track-more-btn" type="button"
          onclick="event.stopPropagation();toggleTrackMoreMenu(this)" aria-label="Действия">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <circle cx="12" cy="5" r="2"></circle>
            <circle cx="12" cy="12" r="2"></circle>
            <circle cx="12" cy="19" r="2"></circle>
          </svg>
        </button>
        ${deleteBtn}
        <span class="track-duration">${fmtTime(track.duration)}</span>
      </div>
    </div>
  `;
}

async function removeTrackFromUserPlaylist(playlistId, trackId) {
  try {
    let prev = Array.isArray(state.userPlaylistTrackIdCache?.[playlistId])
      ? state.userPlaylistTrackIdCache[playlistId]
      : [];
    let prevIds = prev
      .map((x) => (x && typeof x === 'object' ? x.id : x))
      .filter(Boolean)
      .map(String);
    const removeId = String(trackId);

    // Если кэш ещё не заполнен — не шлём пустой список (иначе сотрём весь плейлист).
    if (!prevIds.length) {
      const live = await apiFetch('GET', withUserPlaylistAccessToken(`/api/user-playlists/${playlistId}`));
      const liveTracks = Array.isArray(live?.tracks) ? live.tracks : [];
      prevIds = liveTracks
        .map((x) => (x && typeof x === 'object' ? x.id : x))
        .filter(Boolean)
        .map(String);
    }

    const nextTracks = prevIds.filter((id) => id !== removeId);
    await apiFetch('PUT', `/api/me/user-playlists/${playlistId}/tracks`, { tracks: nextTracks });
    state.userPlaylistTrackIdCache = state.userPlaylistTrackIdCache || {};
    state.userPlaylistTrackIdCache[playlistId] = nextTracks;
    toast('Трек удалён', 'success');
    await syncUserPlaylistPageIfVisible(playlistId);
    if (state.view === 'library') await renderLibrary();
    await ensureMyPlaylistsLoaded();
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
}

window.removeTrackFromUserPlaylist = removeTrackFromUserPlaylist;

window.copyUserPlaylistShareLink = copyUserPlaylistShareLink;
window.createUserPlaylist = createUserPlaylist;
window.toggleUserPlaylistShare = toggleUserPlaylistShare;
window.toggleUserPlaylistHeroOwnerMenu = toggleUserPlaylistHeroOwnerMenu;
window.closeUserPlaylistHeroOwnerMenu = closeUserPlaylistHeroOwnerMenu;
window.toggleUserPlaylistLike = toggleUserPlaylistLike;
window.submitUserPlaylistComment = submitUserPlaylistComment;
window.deleteUserPlaylistComment = deleteUserPlaylistComment;
window.toggleUserPlaylistCommentMenu = toggleUserPlaylistCommentMenu;
window.startInlineEditUserPlaylistComment = startInlineEditUserPlaylistComment;
window.cancelInlineEditUserPlaylistComment = cancelInlineEditUserPlaylistComment;
window.saveInlineEditUserPlaylistComment = saveInlineEditUserPlaylistComment;
window.userPlaylistThreadRailKeydown = userPlaylistThreadRailKeydown;
window.addTrackToUserPlaylist = addTrackToUserPlaylist;
window.renderUserPlaylist = renderUserPlaylist;
window.goToMyProfile = goToMyProfile;
window.saveUserProfileBio = saveUserProfileBio;
window.profileEnterBioEdit = profileEnterBioEdit;
window.profileCancelBioEdit = profileCancelBioEdit;
window.profileRemoveShowcaseArtist = profileRemoveShowcaseArtist;
window.profileRemoveShowcaseAlbum = profileRemoveShowcaseAlbum;
window.profileOpenShowcasePicker = profileOpenShowcasePicker;
window.toggleProfileFriendMoreMenu = toggleProfileFriendMoreMenu;
window.profileFriendMoreMenuDelete = profileFriendMoreMenuDelete;
async function resolveEditorialPlaylistGradient(playlist) {
  if (!playlist) return pickGrad('collection');
  let dynamicGrad = null;
  if (playlist.coverUrl) {
    dynamicGrad = await getGradientFromImage(playlist.coverUrl);
  }
  return dynamicGrad || playlist.coverGradient || pickGrad(playlist.id);
}

// ─── COLLECTION VIEW ─────────────────────────────────────────────────────────
async function renderCollection(id) {
  const content = $('content');
  const playlist = await GET(`/api/playlists/${id}`);
  const tracks = playlist.tracks || [];
  const grad = await resolveEditorialPlaylistGradient(playlist);
  const qKey = storeQueue(tracks);

  const collDesc = String(playlist.description || '').trim();
  const collMeta = `${tracks.length} ${pluralTracks(tracks.length)}`;
  content.innerHTML = `
    <div class="mxm-collection-page">
    <div class="page-hero mxm-collection-hero" style="--hero-grad:linear-gradient(${grad})">
      <button class="nav-back-btn" onclick="goBack()" title="Назад">
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
      </button>
      <div class="hero-bg"></div>
      <div class="hero-content">
        <div class="hero-cover">
          ${coverImg(playlist.coverUrl, grad)}
        </div>
        <div class="hero-info">
          <div class="hero-type">Плейлист</div>
          <h1 class="hero-title">${esc(playlist.title)}</h1>
          <div class="hero-sub mxm-collection-hero-sub">
            ${collDesc ? `<span class="mxm-collection-hero-desc">${esc(collDesc)}</span>` : ''}
            <span class="mxm-collection-hero-sub-count">${esc(collMeta)}</span>
          </div>
          <div class="hero-actions mxm-collection-actions mxm-collection-actions--editorial hero-actions--media-row">
            <button class="hero-play-btn mxm-collection-play-cta" type="button" onclick="playCollectionById('${id}')" title="Слушать" aria-label="Слушать">
              <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </button>
            <button class="hero-shuffle-btn" type="button" onclick="shufflePlayCollectionById('${id}')" title="Перемешать и играть" aria-label="Перемешать и играть">
              <svg viewBox="0 0 24 24"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></svg>
            </button>
            <button class="hero-share-btn" type="button" onclick="shareItem('collection', '${playlist.id}', '${esc(playlist.title)}')" title="Скопировать ссылку" aria-label="Скопировать ссылку">
              ${MXM_SHARE_ICON_SVG}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="mxm-collection-sheet">
    <div class="track-list mxm-collection-track-list" style="margin-top:8px">
      ${tracks.map((t,i) => trackRowCover(t, qKey, i)).join('')}
    </div>
    </div>
    </div>`;
  setQueuePlayingFromLabel(playlist.title);
}

async function playCollectionById(id) {
  try {
    const playlist = await GET(`/api/playlists/${id}`);
    const tracks = playlist.tracks || [];
    if (!tracks.length) { toast('Подборка пуста'); return; }
    setQueueState(tracks, 0);
    const recordHistory = ['artist', 'artist-tracks', 'collection', 'album'].includes(state.view) && !!state.user;
    playTrack(tracks[0], { recordHistory });
  } catch { toast('Ошибка загрузки', 'error'); }
}
// ─── ALBUM VIEW ───────────────────────────────────────────────────────────────
async function renderAlbum(id) {
  const content = $('content');
  const album = await GET(`/api/albums/${id}`);
  const tracks = album.tracks || [];
  let dynamicGrad = null;
  if (album.coverUrl) {
    dynamicGrad = await getGradientFromImage(album.coverUrl);
  }
  const grad = dynamicGrad || album.coverGradient || pickGrad(id);
  const liked = isFavA(id);
  const qKey = storeQueue(tracks);

  const artistHtml = renderAlbumHeroArtistsHtml(album);

  content.innerHTML = `
    <div class="mxm-collection-page">
    <div class="page-hero mxm-collection-hero" style="--hero-grad:linear-gradient(${grad})">
      <button class="nav-back-btn" onclick="goBack()" title="Назад">
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
      </button>
      <div class="hero-bg"></div>
      <div class="hero-content">
        <div class="hero-cover">
          ${coverImg(album.coverUrl, grad)}
        </div>
        <div class="hero-info">
          <div class="hero-type">${tracks.length === 1 ? 'Сингл' : 'Альбом'}</div>
          <h1 class="hero-title">${esc(album.title)}</h1>
          <div class="hero-sub mxm-collection-hero-sub">
            <div class="mxm-collection-hero-sub-inner" aria-label="Исполнитель и детали релиза">
              <span class="mxm-collection-hero-sub-artists">${artistHtml}</span>
              ${album.year ? `<span class="mxm-hero-meta-sep" aria-hidden="true">·</span><span class="mxm-collection-hero-sub-year">${album.year}</span>` : ''}
              <span class="mxm-hero-meta-sep" aria-hidden="true">·</span>
              <span class="mxm-collection-hero-sub-count">${tracks.length} ${pluralTracks(tracks.length)}</span>
            </div>
          </div>
          <div class="hero-actions mxm-collection-actions hero-actions--media-row">
            <button class="hero-play-btn mxm-collection-play-cta" type="button" onclick="playAlbumById('${id}')" title="Слушать" aria-label="Слушать">
              <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </button>
            <button class="hero-shuffle-btn" type="button" onclick="shufflePlayAlbumById('${id}')" title="Перемешать" aria-label="Перемешать">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M16 3h5v5M4 20l21-17M21 16v5h-5M15 15l6 6M4 4l9 9"/></svg>
            </button>
            <button class="hero-fav-btn ${liked ? 'liked' : ''}" data-fav-album="${id}"
              type="button" onclick="toggleFavAlbum('${id}',event)" title="Избранное" aria-label="Избранное">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" ${liked?'fill="currentColor"':'fill="none"'}/></svg>
            </button>
            <button class="hero-share-btn" type="button" onclick="shareItem('album', '${album.id}', '${esc(album.title)}')" title="Скопировать ссылку" aria-label="Скопировать ссылку">
              ${MXM_SHARE_ICON_SVG}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="mxm-collection-sheet">
    <div class="track-list mxm-collection-track-list" style="margin-top:8px">
      ${tracks.map((t,i) => trackRowCover(t, qKey, i)).join('')}
    </div>
    </div>
    </div>`;
  setQueuePlayingFromLabel(album.title);
}

async function playAlbumById(id) {
  try {
    const album = await GET(`/api/albums/${id}`);
    const tracks = (album.tracks || []).filter((t) => t && t.audioUrl);
    if (!tracks.length) {
      toast('В альбоме нет треков с аудиофайлом', 'error');
      return;
    }
    const qKey = storeQueue(tracks);
    setQueueState(tracks, 0);
    const recordHistory = ['artist', 'artist-tracks', 'collection', 'album'].includes(state.view) && !!state.user;
    playTrack(tracks[0], { recordHistory });
  } catch { toast('Ошибка загрузки', 'error'); }
}

// ─── ARTIST VIEW ──────────────────────────────────────────────────────────────
let artistBioSuggestContext = null;
let artistBioSuggestPendingPhotoUrl = null;
let artistUploaderEditPendingPhotoUrl = null;

function ensureArtistBioSuggestPhotoBlock() {
  if ($('artist-bio-suggest-photo-input')) return;
  const ta = $('artist-bio-suggest-text');
  if (!ta?.parentNode) return;
  const wrap = document.createElement('div');
  wrap.className = 'artist-bio-suggest-photo-wrap';
  wrap.innerHTML = `
    <p class="artist-bio-suggest-photo-hint">По желанию приложите портрет исполнителя — его тоже проверит модератор.</p>
    <div class="artist-bio-suggest-photo-row">
      <img id="artist-bio-suggest-photo-preview" class="artist-bio-suggest-photo-preview hidden" alt="">
      <div class="artist-bio-suggest-photo-controls">
        <label class="btn-ghost artist-bio-suggest-photo-label">
          <input type="file" id="artist-bio-suggest-photo-input" class="artist-bio-suggest-photo-file-input" accept="image/*">
          Выбрать фото
        </label>
        <button type="button" id="artist-bio-suggest-photo-clear" class="btn-ghost hidden" onclick="clearArtistBioSuggestPhoto()">Убрать фото</button>
      </div>
    </div>`;
  ta.parentNode.insertBefore(wrap, ta);
}

function bindArtistBioSuggestPhotoInputOnce() {
  const fin = $('artist-bio-suggest-photo-input');
  if (fin && !fin._mxmBioPhoto) {
    fin._mxmBioPhoto = true;
    fin.addEventListener('change', onArtistBioSuggestPhotoPick);
  }
}

function clearArtistBioSuggestPhoto() {
  artistBioSuggestPendingPhotoUrl = null;
  const prev = $('artist-bio-suggest-photo-preview');
  if (prev) {
    prev.removeAttribute('src');
    prev.classList.add('hidden');
  }
  $('artist-bio-suggest-photo-clear')?.classList.add('hidden');
  const fin = $('artist-bio-suggest-photo-input');
  if (fin) fin.value = '';
}

async function onArtistBioSuggestPhotoPick(ev) {
  const f = ev.target?.files?.[0];
  if (!f) return;
  if (!state.user || !state.token) {
    openAuthModal('login');
    ev.target.value = '';
    return;
  }
  if (!f.type.startsWith('image/')) {
    toast('Нужен файл изображения', 'error');
    ev.target.value = '';
    return;
  }
  const fd = new FormData();
  fd.append('photo', f);
  try {
    const d = await FORM('/api/artist-bio-suggest/photo', fd);
    artistBioSuggestPendingPhotoUrl = d.url || null;
    const prev = $('artist-bio-suggest-photo-preview');
    if (prev && d.url) {
      prev.src = resolveMediaUrl(d.url);
      prev.classList.remove('hidden');
    }
    $('artist-bio-suggest-photo-clear')?.classList.toggle('hidden', !artistBioSuggestPendingPhotoUrl);
    toast('Фото прикреплено', 'success');
  } catch (e) {
    toast(e.message || 'Не удалось загрузить фото', 'error');
  }
  ev.target.value = '';
}

function openArtistBioSuggestModal() {
  if (!artistBioSuggestContext) return;
  let modal = $('artist-bio-suggest-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'artist-bio-suggest-modal';
    modal.className = 'modal modal--center';
    modal.innerHTML = `
      <div class="modal-overlay" onclick="closeArtistBioSuggestModal()"></div>
      <div class="modal-box artist-bio-suggest-modal-box">
        <button type="button" class="modal-close" onclick="closeArtistBioSuggestModal()" aria-label="Закрыть">
          <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <h3 id="artist-bio-suggest-title" class="artist-bio-suggest-heading">Предложить описание</h3>
        <p class="artist-bio-suggest-note">Текст попадёт в каталог после проверки модератором. Войдите в аккаунт — заявка будет привязана к вашему нику.</p>
        <p class="artist-bio-suggest-photo-hint">По желанию приложите портрет исполнителя — его тоже проверит модератор.</p>
        <div class="artist-bio-suggest-photo-row">
          <img id="artist-bio-suggest-photo-preview" class="artist-bio-suggest-photo-preview hidden" alt="">
          <div class="artist-bio-suggest-photo-controls">
            <label class="btn-ghost artist-bio-suggest-photo-label">
              <input type="file" id="artist-bio-suggest-photo-input" class="artist-bio-suggest-photo-file-input" accept="image/*">
              Выбрать фото
            </label>
            <button type="button" id="artist-bio-suggest-photo-clear" class="btn-ghost hidden" onclick="clearArtistBioSuggestPhoto()">Убрать фото</button>
          </div>
        </div>
  <textarea id="artist-bio-suggest-text" class="artist-bio-suggest-textarea" rows="10" maxlength="8000" placeholder="Расскажите об исполнителе…"></textarea>
        <div class="artist-bio-suggest-actions">
          <button type="button" class="btn-ghost" onclick="closeArtistBioSuggestModal()">Отмена</button>
          <button type="button" class="btn-primary" onclick="submitArtistBioSuggestion()">Отправить на модерацию</button>
        </div>
      </div>`;
    document.body.appendChild(modal);
  } else {
    ensureArtistBioSuggestPhotoBlock();
  }
  bindArtistBioSuggestPhotoInputOnce();
  clearArtistBioSuggestPhoto();
  const titleEl = $('artist-bio-suggest-title');
  if (titleEl) titleEl.textContent = `Описание: ${artistBioSuggestContext.name}`;
  const ta = $('artist-bio-suggest-text');
  if (ta) {
    ta.value = artistBioSuggestContext.bio || '';
    requestAnimationFrame(() => ta.focus());
  }
  modal.classList.remove('hidden');
}

function closeArtistBioSuggestModal() {
  $('artist-bio-suggest-modal')?.classList.add('hidden');
  clearArtistBioSuggestPhoto();
}

async function submitArtistBioSuggestion() {
  const ctx = artistBioSuggestContext;
  const ta = $('artist-bio-suggest-text');
  if (!ctx || !ta) return;
  const bio = ta.value.trim();
  const payload = { bio };
  if (artistBioSuggestPendingPhotoUrl) payload.proposedPhotoUrl = artistBioSuggestPendingPhotoUrl;
  try {
    await POST(`/api/artists/${encodeURIComponent(ctx.id)}/bio-submissions`, payload);
    toast('Заявка отправлена на модерацию', 'success');
    closeArtistBioSuggestModal();
  } catch (e) {
    toast(e.message || 'Не удалось отправить', 'error');
  }
}

function openArtistPageCardAction() {
  if (!artistBioSuggestContext) return;
  if (artistBioSuggestContext.canEditAsUploader) openArtistUploaderEditModal();
  else openArtistBioSuggestModal();
}

function clearArtistUploaderEditPhoto() {
  artistUploaderEditPendingPhotoUrl = '';
  const prev = $('artist-uploader-edit-photo-preview');
  if (prev) {
    prev.removeAttribute('src');
    prev.classList.add('hidden');
  }
  $('artist-uploader-edit-photo-clear')?.classList.add('hidden');
  const fin = $('artist-uploader-edit-photo-input');
  if (fin) fin.value = '';
}

async function onArtistUploaderEditPhotoPick(ev) {
  const ctx = artistBioSuggestContext;
  const f = ev.target?.files?.[0];
  if (!ctx?.id || !f) return;
  if (!state.user || !state.token) {
    openAuthModal('login');
    ev.target.value = '';
    return;
  }
  if (!f.type.startsWith('image/')) {
    toast('Нужен файл изображения', 'error');
    ev.target.value = '';
    return;
  }
  const fd = new FormData();
  fd.append('photo', f);
  try {
    const d = await FORM(`/api/artists/${encodeURIComponent(ctx.id)}/uploader-photo`, fd);
    artistUploaderEditPendingPhotoUrl = d.url || null;
    const prev = $('artist-uploader-edit-photo-preview');
    if (prev && d.url) {
      prev.src = resolveMediaUrl(d.url);
      prev.classList.remove('hidden');
    }
    $('artist-uploader-edit-photo-clear')?.classList.toggle('hidden', !artistUploaderEditPendingPhotoUrl);
    toast('Фото загружено', 'success');
  } catch (e) {
    toast(e.message || 'Не удалось загрузить фото', 'error');
  }
  ev.target.value = '';
}

function closeArtistUploaderEditModal() {
  $('artist-uploader-edit-modal')?.classList.add('hidden');
  clearArtistUploaderEditPhoto();
}

function openArtistUploaderEditModal() {
  const ctx = artistBioSuggestContext;
  if (!ctx?.canEditAsUploader) return;
  if (!state.user) {
    openAuthModal('login');
    return;
  }
  let modal = $('artist-uploader-edit-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'artist-uploader-edit-modal';
    modal.className = 'modal modal--center';
    modal.innerHTML = `
      <div class="modal-overlay" onclick="closeArtistUploaderEditModal()"></div>
      <div class="modal-box artist-bio-suggest-modal-box">
        <button type="button" class="modal-close" onclick="closeArtistUploaderEditModal()" aria-label="Закрыть">
          <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <h3 id="artist-uploader-edit-title" class="artist-bio-suggest-heading">Редактировать карточку</h3>
        <p class="artist-bio-suggest-note">Вы отметили «я исполнитель» на своём треке — изменения сразу попадут в каталог.</p>
        <label class="artist-uploader-edit-label" for="artist-uploader-edit-genre">Жанр</label>
        <input type="text" id="artist-uploader-edit-genre" class="artist-uploader-edit-genre" maxlength="120" placeholder="Например: Hip-hop" autocomplete="off">
        <div class="artist-bio-suggest-photo-row">
          <img id="artist-uploader-edit-photo-preview" class="artist-bio-suggest-photo-preview hidden" alt="">
          <div class="artist-bio-suggest-photo-controls">
            <label class="btn-ghost artist-bio-suggest-photo-label">
              <input type="file" id="artist-uploader-edit-photo-input" class="artist-bio-suggest-photo-file-input" accept="image/*" onchange="onArtistUploaderEditPhotoPick(event)">
              Фото исполнителя
            </label>
            <button type="button" id="artist-uploader-edit-photo-clear" class="btn-ghost hidden" onclick="clearArtistUploaderEditPhoto()">Убрать фото</button>
          </div>
        </div>
        <textarea id="artist-uploader-edit-bio" class="artist-bio-suggest-textarea" rows="10" maxlength="8000" placeholder="Описание исполнителя…"></textarea>
        <div class="artist-bio-suggest-actions">
          <button type="button" class="btn-ghost" onclick="closeArtistUploaderEditModal()">Отмена</button>
          <button type="button" class="btn-primary" onclick="submitArtistUploaderEdit()">Сохранить</button>
        </div>
      </div>`;
    document.body.appendChild(modal);
  }
  clearArtistUploaderEditPhoto();
  const titleEl = $('artist-uploader-edit-title');
  if (titleEl) titleEl.textContent = `Карточка: ${ctx.name}`;
  const genreEl = $('artist-uploader-edit-genre');
  if (genreEl) genreEl.value = ctx.genre || '';
  const ta = $('artist-uploader-edit-bio');
  if (ta) {
    ta.value = ctx.bio || '';
    requestAnimationFrame(() => ta.focus());
  }
  const prev = $('artist-uploader-edit-photo-preview');
  if (prev) {
    if (ctx.photoUrl) {
      prev.src = resolveMediaUrl(ctx.photoUrl);
      prev.classList.remove('hidden');
      artistUploaderEditPendingPhotoUrl = ctx.photoUrl;
      $('artist-uploader-edit-photo-clear')?.classList.remove('hidden');
    } else {
      prev.classList.add('hidden');
    }
  }
  modal.classList.remove('hidden');
}

async function submitArtistUploaderEdit() {
  const ctx = artistBioSuggestContext;
  const ta = $('artist-uploader-edit-bio');
  const genreEl = $('artist-uploader-edit-genre');
  if (!ctx?.id || !ta) return;
  const payload = {
    bio: ta.value.trim(),
    genre: genreEl ? genreEl.value.trim() : ''
  };
  payload.photoUrl =
    artistUploaderEditPendingPhotoUrl !== null
      ? artistUploaderEditPendingPhotoUrl
      : ctx.photoUrl || '';
  try {
    await PATCH(`/api/artists/${encodeURIComponent(ctx.id)}`, payload);
    toast('Карточка исполнителя сохранена', 'success');
    closeArtistUploaderEditModal();
    await renderArtist(ctx.id);
  } catch (e) {
    toast(e.message || 'Не удалось сохранить', 'error');
  }
}

window.openArtistPageCardAction = openArtistPageCardAction;
window.openArtistUploaderEditModal = openArtistUploaderEditModal;
window.closeArtistUploaderEditModal = closeArtistUploaderEditModal;
window.submitArtistUploaderEdit = submitArtistUploaderEdit;
window.onArtistUploaderEditPhotoPick = onArtistUploaderEditPhotoPick;
window.clearArtistUploaderEditPhoto = clearArtistUploaderEditPhoto;

async function renderArtist(id) {
  const content = $('content');
  artistBioSuggestContext = null;
  const artist = await GET(`/api/artists/${id}`);
  
  // Попытаемся получить градиент из фото
  let dynamicGrad = null;
  if (artist.photoUrl) {
    dynamicGrad = await getGradientFromImage(artist.photoUrl);
  }
  const grad = dynamicGrad || artist.photoGradient || pickGrad(artist.name);
  const pop = artist.popularTracks || [];
  const qKey = storeQueue(pop);
  const likedArtist = isFavAr(id);

  const playsText = artist.totalPlays ? ` · ${formatNumber(artist.totalPlays)} прослушиваний` : '';

  // Биография с кнопкой для модалки
  let bioHtml = '';
  if (artist.bio) {
    const shortBio = artist.bio.length > 100 ? artist.bio.slice(0, 100) + '…' : artist.bio;
    const fullBio = artist.bio;
    // Не вставляем текст ? onclick — переносы/кавычки ломают парсер JS (SyntaxError).
    const bioPayload = encodeURIComponent(fullBio);
    bioHtml = `
      <div class="artist-bio-wrapper">
        <span class="artist-bio-short">${esc(shortBio)}</span>
        ${artist.bio.length > 100 ? `<button type="button" class="artist-bio-toggle btn-link" data-bio-encoded="${bioPayload}">Читать полностью</button>` : ''}
      </div>
    `;
  }

  content.innerHTML = `
    <div class="artist-page">
      <div class="page-hero" style="--hero-grad:linear-gradient(${grad})">
          <button class="nav-back-btn" onclick="goBack()" title="Назад">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
            </svg>
          </button>
          <div class="hero-bg"></div>
          <div class="hero-content">
            <div class="hero-cover circle">${artistAvatarHtml(artist, grad, '', { showUploadHint: true })}</div>
            <div class="hero-info">
          <div class="hero-type">Исполнитель</div>
          <h1 class="hero-title">${esc(artist.name)}</h1>
          <div class="hero-sub">${esc(artist.genre || '')}${playsText}</div>
          ${bioHtml}
          <div class="hero-actions hero-actions--media-row">
            <button class="hero-play-btn" type="button" data-id="${id}" title="Слушать" aria-label="Слушать">
              <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </button>
            <button class="hero-shuffle-btn" type="button" onclick="shufflePlayArtistById('${id}')" title="Перемешать и играть" aria-label="Перемешать и играть">
              <svg viewBox="0 0 24 24"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></svg>
            </button>
            <button class="hero-fav-btn ${likedArtist ? 'liked' : ''}" data-fav-artist="${id}" type="button" onclick="toggleFavArtist('${id}',event)" title="В любимые исполнители" aria-label="В любимые исполнители">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" ${likedArtist ? 'fill="currentColor"' : 'fill="none"'}/></svg>
            </button>
            <button class="hero-share-btn" type="button" onclick="shareItem('artist', '${artist.id}', '${esc(artist.name)}')" title="Скопировать ссылку" aria-label="Скопировать ссылку">
              ${MXM_SHARE_ICON_SVG}
            </button>
            <button type="button" class="artist-bio-suggest-icon-btn" onclick="openArtistPageCardAction()" title="${artist.canEditAsUploader ? 'Редактировать карточку исполнителя' : 'Предложить описание (после модерации)'}" aria-label="${artist.canEditAsUploader ? 'Редактировать карточку исполнителя' : 'Предложить описание исполнителя'}">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
    ${pop.length ? `
    <section class="section artist-page-section artist-page-section--popular">
      <div class="section-header">
        <h2 class="section-title section-title-clickable" data-nav="artist-tracks" data-nav-id="${id}" onclick="handleDataNavClick(event)">
          Популярное <span class="section-title-arrow" aria-hidden="true">→</span>
        </h2>
        <span class="see-all" data-nav="artist-tracks" data-nav-id="${id}" onclick="handleDataNavClick(event)">Все треки</span>
      </div>
      <div class="track-list artist-page-popular-tracks">
        ${pop.map((t,i) => trackRowCover(t, qKey, i)).join('')}
      </div>
    </section>` : ''}
    ${artist.albums?.length ? `
    <section class="section artist-page-section artist-page-section--albums">
      <h2 class="section-title">Альбомы</h2>
      <div class="cards-grid artist-page-albums-grid">${artist.albums.map(a => albumCard(a)).join('')}</div>
    </section>` : ''}
    ${artist.featuredAlbums?.length ? `
    <section class="section artist-page-section artist-page-section--featured">
      <h2 class="section-title">Участие в альбомах</h2>
      <div class="cards-grid artist-page-albums-grid">${artist.featuredAlbums.map(a => albumCard(a)).join('')}</div>
    </section>` : ''}
    ${artist.relatedArtists?.length ? `
    <section class="section artist-page-section artist-page-section--related">
      <h2 class="section-title">Вам так же может понравиться</h2>
      <div class="artists-grid artist-page-related-grid">${artist.relatedArtists.map((a) => artistCard(a)).join('')}</div>
    </section>` : ''}
    </div>`;

  // Обработчик для кнопки воспроизведения
  const playBtn = document.querySelector('.hero-play-btn[data-id]');
  if (playBtn) {
    playBtn.onclick = (e) => {
      e.stopPropagation();
      playArtistById(playBtn.dataset.id);
    };
  }
  const bioBtn = content.querySelector('.artist-bio-toggle[data-bio-encoded]');
  if (bioBtn) {
    bioBtn.onclick = () => {
      const raw = bioBtn.getAttribute('data-bio-encoded');
      if (raw != null) openArtistBioModal(decodeURIComponent(raw));
    };
  }
  artistBioSuggestContext = {
    id,
    name: artist.name || '',
    bio: artist.bio || '',
    genre: artist.genre || '',
    photoUrl: artist.photoUrl || '',
    canEditAsUploader: !!artist.canEditAsUploader
  };
  setQueuePlayingFromLabel(artist.name || '');
  if (typeof injectMxmAiArtistButton === 'function') {
    injectMxmAiArtistButton(artist).catch(() => {});
  }
}
async function playArtistById(id) {
  try {
    const artist = await GET(`/api/artists/${id}`);
    const tracks = artist.popularTracks || [];
    if (!tracks.length) { toast('Нет треков'); return; }
    setQueueState(tracks, 0);
    const recordHistory = ['artist', 'artist-tracks', 'collection', 'album'].includes(state.view) && !!state.user;
    playTrack(tracks[0], { recordHistory });
  } catch { toast('Ошибка', 'error'); }
}

async function renderArtistTracks(id) {
  const content = $('content');
  const data = await GET(`/api/artists/${id}/tracks`);
  const artist = data.artist || {};
  const tracks = data.tracks || [];
  const popularTracks = [...tracks];
  const alphaTracks = [...tracks].sort((a, b) =>
    String(a.title || '').localeCompare(String(b.title || ''), 'ru', { sensitivity: 'base' })
  );
  const yearTracks = [...tracks].sort((a, b) => {
    const ya = Number(a.year);
    const yb = Number(b.year);
    const aOk = Number.isFinite(ya) && ya >= 1900 && ya <= 2100;
    const bOk = Number.isFinite(yb) && yb >= 1900 && yb <= 2100;
    if (aOk && bOk) return yb - ya;
    if (aOk && !bOk) return -1;
    if (!aOk && bOk) return 1;
    return String(a.title || '').localeCompare(String(b.title || ''), 'ru', { sensitivity: 'base' });
  });

  let dynamicGrad = null;
  if (artist.photoUrl) {
    dynamicGrad = await getGradientFromImage(artist.photoUrl);
  }
  const grad = dynamicGrad || artist.photoGradient || pickGrad(artist.name || id);
  const playsText = artist.totalPlays ? ` · ${formatNumber(artist.totalPlays)} прослушиваний` : '';

  content.innerHTML = `
    <div class="artist-page">
    <div class="page-hero" style="--hero-grad:linear-gradient(${grad})">
      <button class="nav-back-btn" onclick="goBack()" title="Назад">
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
      </button>
      <div class="hero-bg"></div>
      <div class="hero-content">
        <div class="hero-cover circle">${artistAvatarHtml(artist, grad, '', { showUploadHint: true })}</div>
        <div class="hero-info">
          <div class="hero-type">Исполнитель</div>
          <h1 class="hero-title">${esc(artist.name || '')}</h1>
          <div class="hero-sub">${esc(artist.genre || '')}${playsText}</div>
        </div>
      </div>
    </div>
    <section class="section artist-page-section artist-page-section--all-tracks">
      <div class="section-header">
        <h2 class="section-title">Все треки</h2>
        <div class="artist-tracks-header-actions">
          <div class="artist-sort-wrap" id="artist-sort-wrap">
            <button class="btn-ghost artist-sort-btn" id="artist-sort-btn" type="button">
              <span id="artist-sort-label">По популярности</span>
              <span class="artist-sort-caret" aria-hidden="true">▾</span>
            </button>
            <div class="artist-sort-menu hidden" id="artist-sort-menu">
              <button class="active" data-sort="popular" type="button">По популярности</button>
              <button data-sort="alpha" type="button">По названию</button>
              <button data-sort="year" type="button">По годам</button>
            </div>
          </div>
        </div>
      </div>
      <div id="artist-tracks-list"></div>
    </section>
    </div>
  `;

  const listEl = $('artist-tracks-list');
  const sortWrap = $('artist-sort-wrap');
  const sortBtn = $('artist-sort-btn');
  const sortLabel = $('artist-sort-label');
  const sortMenu = $('artist-sort-menu');
  const sortOptions = sortMenu ? sortMenu.querySelectorAll('button[data-sort]') : [];
  if (!listEl) return;
  let currentSort = 'popular';

  const renderSortedTracks = (sortMode) => {
    const sortedTracks =
      sortMode === 'alpha' ? alphaTracks : sortMode === 'year' ? yearTracks : popularTracks;
    const qKey = storeQueue(sortedTracks);
    listEl.innerHTML = sortedTracks.length
      ? `<div class="track-list artist-page-all-tracks">${sortedTracks.map((t, i) => trackRowCover(t, qKey, i)).join('')}</div>`
      : `<div class="lib-empty"><h3>Нет треков</h3></div>`;
  };

  const applySort = (sortMode) => {
    currentSort =
      sortMode === 'alpha' ? 'alpha' : sortMode === 'year' ? 'year' : 'popular';
    if (sortLabel) {
      sortLabel.textContent =
        currentSort === 'alpha'
          ? 'По названию'
          : currentSort === 'year'
            ? 'По годам'
            : 'По популярности';
    }
    sortOptions.forEach((option) => {
      option.classList.toggle('active', option.dataset.sort === currentSort);
    });
    renderSortedTracks(currentSort);
  };

  if (sortBtn && sortMenu) {
    sortBtn.addEventListener('click', () => {
      sortMenu.classList.toggle('hidden');
      sortBtn.classList.toggle('active', !sortMenu.classList.contains('hidden'));
    });
  }

  sortOptions.forEach((option) => {
    option.addEventListener('click', () => {
      applySort(option.dataset.sort || 'popular');
      if (sortMenu) sortMenu.classList.add('hidden');
      if (sortBtn) sortBtn.classList.remove('active');
    });
  });

  content.addEventListener('click', (e) => {
    if (!sortWrap || !sortMenu || !sortBtn) return;
    if (e.target.closest('#artist-sort-wrap')) return;
    sortMenu.classList.add('hidden');
    sortBtn.classList.remove('active');
  });

  applySort('popular');
  const af = artist.name ? `${artist.name} · все треки` : 'Все треки';
  setQueuePlayingFromLabel(af);
}
// ─── UPLOAD VIEW ──────────────────────────────────────────────────────────────

// State for album upload
const albumUploadState = {
  pendingTracks: [],    // {id, file, title, lyrics, featArtist, removed}
  albumId: null,
  albumTitle: '',
  albumArtist: '',
  albumArtistId: null,
  albumCoverFile: null,
  albumCoverUrl: null,
  existingTracks: []
};

function resetAlbumUploadState() {
  albumUploadState.pendingTracks = [];
  albumUploadState.albumId = null;
  albumUploadState.albumTitle = '';
  albumUploadState.albumArtist = '';
  albumUploadState.albumArtistId = null;
  albumUploadState.albumCoverFile = null;
  albumUploadState.albumCoverUrl = null;
  albumUploadState.existingTracks = [];
}

function renderUpload(mode) {
  const content = $('content');
  const uploadMode = mode || 'track';

  if (!state.user) {
    content.innerHTML = `
      <div class="lib-empty">
        <div class="lib-empty-icon">
          <svg viewBox="0 0 24 24">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        </div>
        <h3>Необходима авторизация</h3>
        <p>Войдите, чтобы загружать музыку</p>
        <button class="btn-primary" style="margin-top:16px" onclick="openAuthModal('login')">Войти</button>
      </div>
    `;
    return;
  }

  const tabTrackActive = uploadMode === 'track' ? 'active' : '';
  const tabAlbumActive = uploadMode === 'album' ? 'active' : '';

  content.innerHTML = `
    <div class="upload-wrap">
      <div class="upload-header-row">
        <div>
          <h1 class="view-title">Загрузка</h1>
          <p class="view-subtitle">Поделитесь своей музыкой со всеми</p>
        </div>
        <button class="upload-faq-btn" onclick="toggleUploadFaq()" title="Частые вопросы">
          <svg viewBox="0 0 24 24" width="18" height="18"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          FAQ
        </button>
      </div>

      <div id="upload-faq" class="upload-faq hidden">
        <button class="upload-faq-close" onclick="toggleUploadFaq()">&times;</button>
        <h3>Частые вопросы</h3>
        <div class="faq-item">
          <b>Загрузка трека</b>
          <p>Выберите аудиофайл, укажите название и исполнителя. Обложка и альбом — необязательны. Год релиза можно указать по желанию. Если выбрать существующий альбом из подсказок, его обложка автоматически применится к треку.</p>
        </div>
        <div class="faq-item">
          <b>Фиты (второй исполнитель)</b>
          <p>В форме трека можно указать поле "Фит". В интерфейсе артисты отображаются через запятую, и по каждому можно перейти на отдельную страницу. Если у фит-исполнителя не указан ID, система ищет его по имени: если артист уже есть, привяжется существующая страница; если нет — артист будет создан автоматически.</p>
        </div>
        <div class="faq-item">
          <b>Загрузка альбома</b>
          <p>Укажите название альбома и исполнителя, затем добавьте треки — по одному или сразу пачкой. Все треки получат обложку и артиста альбома. Если указать год релиза альбома, он будет присвоен всем загружаемым трекам. На каждом треке можно поменять название или добавить текст песни через меню (три точки).</p>
        </div>
        <div class="faq-item">
          <b>Существующий альбом</b>
          <p>При вводе названия альбома появятся подсказки. Если выбрать существующий — загрузятся его обложка и список уже добавленных треков. Новые треки добавятся в тот же альбом.</p>
        </div>
        <div class="faq-item">
          <b>Обложка</b>
          <p>Можно загрузить обложку до или после добавления треков. Если обложка не добавлена — появится предупреждение, но загрузка всё равно сработает. Обложка применяется ко всем трекам альбома.</p>
        </div>
        <div class="faq-item">
          <b>Форматы</b>
          <p>Аудио: MP3, WAV, OGG, FLAC, M4A, AAC (до 100 МБ). Обложки: JPG, PNG, WebP.</p>
        </div>
      </div>

      <div class="upload-tabs">
        <button class="upload-tab ${tabTrackActive}" onclick="renderUpload('track')">Загрузить трек</button>
        <button class="upload-tab ${tabAlbumActive}" onclick="renderUpload('album')">Загрузить альбом</button>
      </div>

      <div id="upload-tab-content"></div>
    </div>
  `;

  if (uploadMode === 'track') {
    renderTrackUploadForm();
  } else {
    renderAlbumUploadForm();
  }
}

function toggleUploadFaq() {
  const el = $('upload-faq');
  if (el) el.classList.toggle('hidden');
}
window.toggleUploadFaq = toggleUploadFaq;

function renderTrackUploadForm() {
  const mobileNativeSelect = isMobile();
  const genreOptionsHtml = GENRE_OPTIONS.map((genre) => `<option value="${esc(genre)}">${esc(genre)}</option>`).join('');
  const trackGenreField = mobileNativeSelect
    ? `
          <div class="select-picker">
            <select id="up-genre" aria-label="Жанр">
              ${genreOptionsHtml}
            </select>
          </div>`
    : `
          <div class="autocomplete-wrapper genre-picker">
            <input type="text" id="up-genre" class="genre-picker-input" value="Другое"
                   onfocus="onGenreFocus(this, 'genre-suggestions')"
                   onblur="hideSuggestionsWithDelay('genre-suggestions')"
                   readonly
                   autocomplete="off">
            <div id="genre-suggestions" class="suggestions-box hidden"></div>
          </div>`;
  const trackTagField = mobileNativeSelect
    ? `
          <div class="select-picker" style="max-width:220px">
            <select id="up-tag-type" aria-label="Тег" style="width: 170px; max-width: 100%;">
              <option value="none">Нет</option>
              <option value="mashup">Мешап</option>
              <option value="remix">Ремикс</option>
            </select>
          </div>`
    : `
          <div class="autocomplete-wrapper genre-picker tag-type-picker" style="max-width:220px">
            <input type="text"
              id="up-tag-type"
              class="genre-picker-input"
              value="Нет"
              data-tag-val="none"
              readonly
              onfocus="onTagTypeFocus()"
              onblur="hideSuggestionsWithDelay('tag-type-suggestions')"
              style="width: 170px; max-width: 100%;" />
            <div id="tag-type-suggestions" class="suggestions-box hidden" style="min-width:170px">
              <div class="suggestion-item" onmousedown="event.preventDefault()" onclick="setTagType('none','Нет')">Нет</div>
              <div class="suggestion-item" onmousedown="event.preventDefault()" onclick="setTagType('mashup','Мешап')">Мешап</div>
              <div class="suggestion-item" onmousedown="event.preventDefault()" onclick="setTagType('remix','Ремикс')">Ремикс</div>
              <div class="suggestion-item" onmousedown="event.preventDefault()" onclick="setTagType('skit','Скит')">Скит</div>
            </div>
          </div>`;
  $('upload-tab-content').innerHTML = `
    <form class="upload-form" onsubmit="handleUpload(event)">

      <!-- Drop zone for audio -->
      <div class="file-drop" id="audio-drop"
        onclick="$('audio-file').click()"
        ondragover="event.preventDefault(); this.classList.add('drag-over')"
        ondragleave="this.classList.remove('drag-over')"
        ondrop="handleFileDrop(event, 'audio')">
        <svg viewBox="0 0 24 24">
          <path d="M9 18V5l12-2v13"/>
          <circle cx="6" cy="18" r="3"/>
          <circle cx="18" cy="16" r="3"/>
        </svg>
        <p>Перетащите аудиофайл сюда</p>
        <span class="drop-hint">MP3, WAV, OGG, FLAC, M4A · до 100 МБ</span>
      </div>
      <input type="file" id="audio-file" accept=".mp3,.wav,.ogg,.flac,.m4a,.aac" class="hidden"
        onchange="onAudioSel(this)">
      <div id="audio-selected" class="hidden"></div>

      <!-- Cover upload -->
      <div class="cover-row">
        <div class="cover-thumb" id="cover-thumb" onclick="$('cover-file').click()" title="Добавить обложку">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
        </div>
        <div class="form-group" style="flex:1">
          <label>Обложка (необязательно)</label>
          <p class="form-hint">Подгрузится автоматически при выборе существующего альбома</p>
          <input type="file" id="cover-file" accept=".jpg,.jpeg,.png,.webp" class="hidden"
            onchange="onCoverSel(this)">
          <button type="button" class="btn-ghost" onclick="$('cover-file').click()">Выбрать файл</button>
        </div>
      </div>

      <!-- Row 1: Title and Artist -->
      <div class="form-row">
        <div class="form-group">
          <label>Название *</label>
          <input type="text" id="up-title" placeholder="Название трека" required>
        </div>
        <div class="form-group">
          <label>Исполнитель *</label>
          <div class="autocomplete-wrapper">
            <input type="text" id="up-artist" placeholder="Имя исполнителя" required
                   oninput="onArtistInput(this)"
                   onfocus="onArtistFocus(this)"
                   autocomplete="off">
            <div id="artist-suggestions" class="suggestions-box hidden"></div>
          </div>
        </div>
        <div class="form-group">
          <label>Фит (необязательно)</label>
          <div class="autocomplete-wrapper">
            <input type="text" id="up-feat-artist" placeholder="Второй исполнитель"
                   oninput="onArtistInput(this, 'feat-artist-suggestions')"
                   onfocus="onArtistInput(this, 'feat-artist-suggestions')"
                   onblur="hideSuggestionsWithDelay('feat-artist-suggestions')"
                   autocomplete="off">
            <div id="feat-artist-suggestions" class="suggestions-box hidden"></div>
          </div>
        </div>
      </div>

      <!-- Row 2: Album and Genre -->
      <div class="form-row">
        <div class="form-group">
          <label>Альбом</label>
          <div class="autocomplete-wrapper">
            <input type="text" id="up-album" placeholder="Название альбома"
                   oninput="onAlbumInput(this)"
                   onfocus="onAlbumFocus(this)"
                   onblur="hideSuggestionsWithDelay('album-suggestions')"
                   autocomplete="off">
            <div id="album-suggestions" class="suggestions-box hidden"></div>
          </div>
        </div>
        <div class="form-group">
          <label>Жанр</label>
          ${trackGenreField}
        </div>
        <div class="form-group">
          <label>Год релиза (необязательно)</label>
          <input type="number" id="up-year" min="1900" max="2100" placeholder="например, 2024" inputmode="numeric">
        </div>
      </div>

      <!-- Row 2.5: Remix / Mashup tags -->
      <div class="form-row">
        <div class="form-group" style="grid-column: 1 / -1;">
          <label>Тег (необязательно)</label>
          ${trackTagField}
        </div>
      </div>

      <!-- Lyrics -->
      <div class="form-group">
        <label>Текст песни (необязательно)</label>
        <div style="display: flex; gap: 8px;">
          <textarea id="up-lyrics" rows="6" placeholder="[Куплет 1]&#10;Строки текста...&#10;&#10;[Припев]&#10;..." style="flex: 1;"></textarea>
          <button type="button" class="btn-ghost fetch-lyrics-btn" onclick="fetchLyricsAuto()" style="align-self: flex-end; white-space: nowrap;">Автоматически</button>
        </div>
      </div>

      <div class="form-group upload-i-am-artist-wrap">
        <div class="upload-i-am-artist-card">
          <label class="upload-i-am-artist-label">
            <input type="checkbox" id="up-i-am-artist" autocomplete="off">
            <span class="upload-i-am-artist-text">Я исполнитель — после одобрения модератором к треку привяжется мой профиль (бейдж в каталоге и плеере)</span>
          </label>
        </div>
      </div>

      <!-- Messages and submit -->
      <div id="upload-msg"></div>
      <button type="submit" class="btn-primary btn-full" id="upload-btn">
        <svg viewBox="0 0 24 24" width="15" height="15" style="display:inline;margin-right:6px">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        Загрузить трек
      </button>
    </form>
  `;
}

// ─── ALBUM UPLOAD ─────────────────────────────────────────────────────────────

function renderAlbumUploadForm() {
  resetAlbumUploadState();
  const mobileNativeSelect = isMobile();
  const genreOptionsHtml = GENRE_OPTIONS.map((genre) => `<option value="${esc(genre)}">${esc(genre)}</option>`).join('');
  const albumGenreField = mobileNativeSelect
    ? `
          <div class="select-picker">
            <select id="aup-genre" aria-label="Жанр альбома">
              ${genreOptionsHtml}
            </select>
          </div>`
    : `
          <div class="autocomplete-wrapper genre-picker">
            <input type="text" id="aup-genre" class="genre-picker-input" value="Другое"
                   onfocus="onGenreFocus(this, 'aup-genre-suggestions')"
                   onblur="hideSuggestionsWithDelay('aup-genre-suggestions')"
                   readonly
                   autocomplete="off">
            <div id="aup-genre-suggestions" class="suggestions-box hidden"></div>
          </div>`;
  $('upload-tab-content').innerHTML = `
    <div class="album-upload-form">

      <!-- Album info -->
      <div class="form-row">
        <div class="form-group">
          <label>Название альбома *</label>
          <div class="autocomplete-wrapper">
            <input type="text" id="aup-album" placeholder="Название альбома" required
                   oninput="onAlbumUploadAlbumInput(this)"
                   onfocus="onAlbumUploadAlbumInput(this)"
                   onblur="hideSuggestionsWithDelay('aup-album-suggestions')"
                   autocomplete="off">
            <div id="aup-album-suggestions" class="suggestions-box hidden"></div>
          </div>
        </div>
        <div class="form-group">
          <label>Исполнитель *</label>
          <div class="autocomplete-wrapper">
            <input type="text" id="aup-artist" placeholder="Имя исполнителя" required
                   oninput="onArtistInput(this, 'aup-artist-suggestions')"
                   onfocus="onArtistInput(this, 'aup-artist-suggestions')"
                   onblur="hideSuggestionsWithDelay('aup-artist-suggestions')"
                   autocomplete="off">
            <div id="aup-artist-suggestions" class="suggestions-box hidden"></div>
          </div>
        </div>
        <div class="form-group">
          <label>Жанр альбома</label>
          ${albumGenreField}
        </div>
        <div class="form-group">
          <label>Год релиза (необязательно)</label>
          <input type="number" id="aup-year" min="1900" max="2100" placeholder="например, 2024" inputmode="numeric">
        </div>
      </div>

      <!-- Cover -->
      <div class="cover-row">
        <div class="cover-thumb" id="aup-cover-thumb" onclick="$('aup-cover-file').click()" title="Добавить обложку">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
        </div>
        <div class="form-group" style="flex:1">
          <label>Обложка альбома</label>
          <p class="form-hint">Применится ко всем трекам альбома</p>
          <input type="file" id="aup-cover-file" accept=".jpg,.jpeg,.png,.webp" class="hidden"
            onchange="onAlbumCoverSel(this)">
          <button type="button" class="btn-ghost" onclick="$('aup-cover-file').click()">Выбрать файл</button>
        </div>
      </div>
      <div id="aup-no-cover-warning" class="aup-warning hidden">
        <svg viewBox="0 0 24 24" width="16" height="16"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" fill="none" stroke="currentColor" stroke-width="2"/><line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" stroke-width="2"/><line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" stroke-width="2"/></svg>
        Обложка не добавлена
      </div>

      <!-- Existing tracks (shown when selecting existing album) -->
      <div id="aup-existing-tracks" class="hidden">
        <h3 class="aup-section-title">Уже в альбоме</h3>
        <div id="aup-existing-list" class="aup-track-list"></div>
      </div>

      <!-- Add tracks area -->
      <h3 class="aup-section-title" style="margin-top:20px">Треки для загрузки</h3>
      <div class="file-drop" id="aup-audio-drop"
        onclick="$('aup-audio-files').click()"
        ondragover="event.preventDefault(); this.classList.add('drag-over')"
        ondragleave="this.classList.remove('drag-over')"
        ondrop="handleAlbumFileDrop(event)">
        <svg viewBox="0 0 24 24">
          <path d="M9 18V5l12-2v13"/>
          <circle cx="6" cy="18" r="3"/>
          <circle cx="18" cy="16" r="3"/>
        </svg>
        <p>Перетащите аудиофайлы сюда</p>
        <span class="drop-hint">Можно выбрать несколько файлов · MP3, WAV, OGG, FLAC, M4A · до 100 МБ каждый</span>
      </div>
      <input type="file" id="aup-audio-files" accept=".mp3,.wav,.ogg,.flac,.m4a,.aac" multiple class="hidden"
        onchange="onAlbumAudioSel(this)">

      <!-- Pending tracks list -->
      <div id="aup-pending-list" class="aup-track-list aup-pending-list"></div>

      <div class="form-group upload-i-am-artist-wrap" style="margin-top:12px;">
        <div class="upload-i-am-artist-card">
          <label class="upload-i-am-artist-label">
            <input type="checkbox" id="aup-i-am-artist" autocomplete="off">
            <span class="upload-i-am-artist-text">Я исполнитель — заявка на привязку профиля ко всем загружаемым трекам альбома</span>
          </label>
        </div>
      </div>

      <!-- Messages and submit -->
      <div id="aup-upload-msg"></div>
      <div id="aup-progress" class="aup-progress hidden"></div>
      <button type="button" class="btn-primary btn-full" id="aup-upload-btn" onclick="handleAlbumUpload()">
        <svg viewBox="0 0 24 24" width="15" height="15" style="display:inline;margin-right:6px">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        Загрузить альбом
      </button>
    </div>
  `;
  bindAlbumPendingTrackDrag();
}

// Album autocomplete
function onAlbumUploadAlbumInput(input) {
  const val = input.value.toLowerCase().trim();
  const suggestions = $('aup-album-suggestions');
  if (albumUploadState.albumId && val !== albumUploadState.albumTitle.toLowerCase().trim()) {
    albumUploadState.albumId = null;
    albumUploadState.existingTracks = [];
    renderExistingAlbumTracks();
  }
  albumUploadState.albumTitle = input.value;

  if (!val) {
    suggestions.classList.add('hidden');
    albumUploadState.albumId = null;
    albumUploadState.existingTracks = [];
    renderExistingAlbumTracks();
    return;
  }

  const matches = autocompleteAlbums.filter(a =>
    a.display.toLowerCase().includes(val)
  ).slice(0, 10);

  if (matches.length === 0) {
    suggestions.classList.add('hidden');
    return;
  }

  suggestions.innerHTML = matches.map(a =>
    `<div class="suggestion-item" onclick="selectAlbumForUpload('${a.id}', '${esc(a.title)}', '${esc(a.artist)}', '${a.coverUrl ? esc(a.coverUrl) : ''}')">${esc(a.display)}</div>`
  ).join('');
  suggestions.classList.remove('hidden');
}

async function selectAlbumForUpload(id, title, artist, coverUrl) {
  $('aup-album').value = title;
  $('aup-artist').value = artist;
  albumUploadState.albumId = id;
  albumUploadState.albumTitle = title;
  albumUploadState.albumArtist = artist;
  $('aup-album-suggestions').classList.add('hidden');

  // Set artist ID from autocomplete data
  const matchedArtist = autocompleteArtists.find(a => a.name.toLowerCase() === artist.toLowerCase());
  if (matchedArtist) {
    albumUploadState.albumArtistId = matchedArtist.id;
    $('aup-artist').dataset.artistId = matchedArtist.id;
  }

  if (coverUrl) {
    albumUploadState.albumCoverUrl = coverUrl;
    $('aup-cover-thumb').innerHTML = `<img src="${esc(resolveMediaUrl(coverUrl))}" style="width:100%;height:100%;object-fit:cover;border-radius:inherit" alt="">`;
    $('aup-no-cover-warning').classList.add('hidden');
  }

  // Fetch existing tracks for this album
  try {
    const album = await GET(`/api/albums/${id}`);
    albumUploadState.existingTracks = album.tracks || [];
    const yEl = $('aup-year');
    if (yEl && album.year != null && album.year !== '') {
      yEl.value = String(album.year);
    }
    renderExistingAlbumTracks();
  } catch (_) {}
}

function renderExistingAlbumTracks() {
  const container = $('aup-existing-tracks');
  const list = $('aup-existing-list');
  if (!albumUploadState.existingTracks.length) {
    container.classList.add('hidden');
    return;
  }
  container.classList.remove('hidden');
  list.innerHTML = albumUploadState.existingTracks.map((t, i) => `
    <div class="aup-track-item aup-track-existing">
      <span class="aup-track-num">${i + 1}</span>
      <span class="aup-track-title">${esc(t.title)}</span>
      <span class="aup-track-artist">${esc(t.artist)}</span>
    </div>
  `).join('');
}

// Album cover selection
function onAlbumCoverSel(input) {
  const f = input.files[0]; if (!f) return;
  albumUploadState.albumCoverFile = f;
  albumUploadState.albumCoverUrl = null;
  const r = new FileReader();
  r.onload = e => {
    $('aup-cover-thumb').innerHTML = `<img src="${e.target.result}" style="width:100%;height:100%;object-fit:cover;border-radius:inherit" alt="">`;
    $('aup-no-cover-warning').classList.add('hidden');
  };
  r.readAsDataURL(f);
}

/** Название трека из имени файла (одиночная загрузка и альбом — одна логика). */
function titleFromAudioFilename(name) {
  let base = String(name || '')
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]/g, ' ')
    .trim();
  // Убрать ведущую нумерацию: «01. Bleed A River», «1 - Title», «03_Track» → после _ уже «03 Track»
  const stripped = base.replace(/^\d{1,3}(?:\s*[\.\)\-–—]\s*|\s+)/, '').trim();
  if (stripped) base = stripped;
  return base;
}

// Add audio files to pending list
function onAlbumAudioSel(input) {
  const files = Array.from(input.files);
  if (!files.length) return;
  addPendingTracks(files);
  input.value = '';
}

function handleAlbumFileDrop(e) {
  e.preventDefault();
  $('aup-audio-drop').classList.remove('drag-over');
  const files = Array.from(e.dataTransfer.files).filter(f =>
    /\.(mp3|mpeg|mpga|wav|ogg|oga|flac|m4a|aac|opus|weba)$/i.test(f.name)
  );
  if (files.length) addPendingTracks(files);
}

function addPendingTracks(files) {
  for (const f of files) {
    const title = titleFromAudioFilename(f.name);
    albumUploadState.pendingTracks.push({
      id: 'pt_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      file: f,
      title: title,
      lyrics: '',
      featArtist: '',
      featArtistId: '',
      tagType: 'none',
      removed: false
    });
  }
  renderPendingTracks();
}

function renderPendingTracks() {
  const list = $('aup-pending-list');
  const tracks = albumUploadState.pendingTracks.filter(t => !t.removed);
  if (!tracks.length) {
    list.innerHTML = '';
    return;
  }
  list.innerHTML = tracks.map((t, i) => `
    <div class="aup-track-item" data-ptid="${t.id}">
      <button type="button" class="aup-track-drag" aria-label="Перетащите для изменения порядка">
        <span class="aup-track-drag-bars" aria-hidden="true"></span>
      </button>
      <span class="aup-track-num">${albumUploadState.existingTracks.length + i + 1}</span>
      <span class="aup-track-title" id="aup-title-${t.id}" onclick="startEditTrackTitle('${t.id}')">${esc(t.title)}</span>
      <span class="aup-track-size">${(t.file.size / 1048576).toFixed(1)} МБ</span>
      ${t.featArtist ? `<span class="aup-track-has-lyrics" title="Добавлен фит">F</span>` : ''}
      ${t.lyrics ? '<span class="aup-track-has-lyrics" title="Текст добавлен">T</span>' : ''}
      ${t.tagType === 'skit' ? '<span class="aup-track-has-lyrics aup-track-skit-mark" title="Скит">S</span>' : ''}
      <button type="button" class="aup-track-menu-btn" onclick="toggleTrackMenu('${t.id}')">
        <svg viewBox="0 0 24 24" width="18" height="18">
          <circle cx="12" cy="5" r="1.5" fill="currentColor"/>
          <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
          <circle cx="12" cy="19" r="1.5" fill="currentColor"/>
        </svg>
      </button>
      <div class="aup-track-menu hidden" id="aup-menu-${t.id}">
        <button onclick="startEditTrackTitle('${t.id}')">Переименовать</button>
        <button onclick="openTrackFeatEdit('${t.id}')">Добавить фит</button>
        <button onclick="openTrackLyricsEdit('${t.id}')">Добавить текст</button>
        <button onclick="togglePendingTrackSkit('${t.id}')">${t.tagType === 'skit' ? 'Снять метку «Скит»' : 'Пометить как скит'}</button>
        <button class="danger" onclick="removePendingTrack('${t.id}')">Удалить</button>
      </div>
    </div>
  `).join('');
}

function closeAupPendingEditors() {
  const list = $('aup-pending-list');
  if (!list) return;
  list.querySelectorAll('.aup-lyrics-editor, .aup-feat-editor').forEach(el => el.remove());
}

function movePendingTrackInsertBefore(fromIdx, insertBefore) {
  const arr = albumUploadState.pendingTracks;
  const active = arr.filter(t => !t.removed);
  const n = active.length;
  if (fromIdx < 0 || fromIdx >= n) return;
  if (insertBefore < 0 || insertBefore > n) return;
  if (insertBefore === fromIdx || insertBefore === fromIdx + 1) return;
  const [item] = active.splice(fromIdx, 1);
  let ins = insertBefore;
  if (fromIdx < insertBefore) ins--;
  active.splice(ins, 0, item);
  const removed = arr.filter(t => t.removed);
  albumUploadState.pendingTracks = [...active, ...removed];
  closeAupPendingEditors();
  renderPendingTracks();
}

function bindAlbumPendingTrackDrag() {
  const list = $('aup-pending-list');
  if (!list || list._aupDndBound) return;
  list._aupDndBound = true;

  let dragging = false;
  let from = -1;
  let lastInsertBefore = -1;
  let capEl = null;
  let capId = null;
  let sourceRow = null;
  let ghost = null;
  let grabDY = 0;
  let ghostW = 0;
  let ghostH = 0;
  let dropLine = null;

  const trackRows = () => list.querySelectorAll(':scope > .aup-track-item');

  const insertIndexFromPoint = (clientY) => {
    const rows = trackRows();
    if (!rows.length) return 0;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect();
      const mid = r.top + r.height / 2;
      if (clientY < mid) return i;
    }
    return rows.length;
  };

  const ensureDropLine = () => {
    if (dropLine) return dropLine;
    dropLine = document.createElement('div');
    dropLine.className = 'aup-track-drop-line';
    dropLine.setAttribute('aria-hidden', 'true');
    return dropLine;
  };

  const updateDropLine = (insertBefore) => {
    const line = ensureDropLine();
    const rows = trackRows();
    const listRect = list.getBoundingClientRect();
    let y;
    if (!rows.length) {
      y = listRect.top;
    } else if (insertBefore >= rows.length) {
      y = rows[rows.length - 1].getBoundingClientRect().bottom;
    } else {
      y = rows[insertBefore].getBoundingClientRect().top;
    }
    line.style.top = `${Math.round(y - listRect.top + list.scrollTop)}px`;
    if (!line.parentElement) list.appendChild(line);
    line.classList.add('aup-track-drop-line--visible');
  };

  const clearDropLine = () => {
    dropLine?.classList.remove('aup-track-drop-line--visible');
  };

  const removeGhost = () => {
    ghost?.remove();
    ghost = null;
    sourceRow?.classList.remove('aup-track-item--drag-source');
    sourceRow = null;
  };

  const clampGhost = (clientX, clientY) => {
    if (!ghost) return;
    const listRect = list.getBoundingClientRect();
    const left = Math.min(listRect.right - ghostW - 8, Math.max(listRect.left + 8, listRect.left + 8));
    ghost.style.left = `${Math.round(left)}px`;
    ghost.style.top = `${Math.round(clientY - grabDY)}px`;
  };

  list.addEventListener('pointerdown', (e) => {
    const h = e.target.closest('.aup-track-drag');
    if (!h || !list.contains(h)) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const row = h.closest('.aup-track-item');
    if (!row) return;
    const rows = Array.from(trackRows());
    const fi = rows.indexOf(row);
    if (fi < 0) return;
    e.preventDefault();
    e.stopPropagation();
    from = fi;
    dragging = true;
    lastInsertBefore = -1;
    capEl = h;
    capId = e.pointerId;
    list.classList.add('aup-pending-list--reordering');

    sourceRow = row;
    sourceRow.classList.add('aup-track-item--drag-source');
    const rowRect = sourceRow.getBoundingClientRect();
    grabDY = e.clientY - rowRect.top;

    ghost = sourceRow.cloneNode(true);
    ghost.classList.add('aup-track-drag-ghost');
    ghost.classList.remove('aup-track-item--drag-source');
    ghost.querySelectorAll('.aup-track-drag--active').forEach(el => el.classList.remove('aup-track-drag--active'));
    ghost.querySelectorAll('[onclick]').forEach(el => el.removeAttribute('onclick'));
    ghostW = Math.round(rowRect.width);
    ghostH = Math.round(rowRect.height);
    ghost.style.width = `${ghostW}px`;
    ghost.style.height = `${ghostH}px`;
    document.body.appendChild(ghost);
    clampGhost(e.clientX, e.clientY);

    h.classList.add('aup-track-drag--active');
    const ib0 = insertIndexFromPoint(e.clientY);
    lastInsertBefore = ib0;
    updateDropLine(ib0);
    try {
      h.setPointerCapture(e.pointerId);
    } catch (_) {}
  });

  list.addEventListener(
    'pointermove',
    (e) => {
      if (!dragging) return;
      e.preventDefault();
      if (ghost) clampGhost(e.clientX, e.clientY);
      const insertBefore = insertIndexFromPoint(e.clientY);
      if (insertBefore !== lastInsertBefore) {
        lastInsertBefore = insertBefore;
        updateDropLine(insertBefore);
      }
    },
    { passive: false }
  );

  const end = (e) => {
    if (!dragging) return;
    const savedInsert = lastInsertBefore;
    dragging = false;
    lastInsertBefore = -1;
    clearDropLine();

    let insertBefore = savedInsert;
    if (e && typeof e.clientY === 'number') {
      insertBefore = insertIndexFromPoint(e.clientY);
    }

    list.classList.remove('aup-pending-list--reordering');
    list.querySelectorAll('.aup-track-drag--active').forEach(el => el.classList.remove('aup-track-drag--active'));

    if (capEl != null && capId != null) {
      try {
        capEl.releasePointerCapture(capId);
      } catch (_) {}
    }
    capEl = null;
    capId = null;

    const fromIdx = from;
    from = -1;
    removeGhost();

    if (fromIdx >= 0 && insertBefore >= 0) {
      movePendingTrackInsertBefore(fromIdx, insertBefore);
    }
  };

  list.addEventListener('pointerup', end);
  list.addEventListener('pointercancel', end);
}

function toggleTrackMenu(ptid) {
  const menu = $('aup-menu-' + ptid);
  const wasHidden = menu.classList.contains('hidden');
  // Close all menus first
  document.querySelectorAll('.aup-track-menu').forEach(m => m.classList.add('hidden'));
  if (wasHidden) menu.classList.remove('hidden');
}

function startEditTrackTitle(ptid) {
  document.querySelectorAll('.aup-track-menu').forEach(m => m.classList.add('hidden'));
  const t = albumUploadState.pendingTracks.find(x => x.id === ptid);
  if (!t) return;
  const el = $('aup-title-' + ptid);
  const current = t.title;
  el.outerHTML = `<input class="aup-track-title-input" id="aup-title-${ptid}" value="${esc(current)}"
    onblur="finishEditTrackTitle('${ptid}')" onkeydown="if(event.key==='Enter'){this.blur()}">`;
  const inp = $('aup-title-' + ptid);
  inp.focus();
  inp.select();
}

function finishEditTrackTitle(ptid) {
  const inp = $('aup-title-' + ptid);
  const t = albumUploadState.pendingTracks.find(x => x.id === ptid);
  if (t && inp) {
    t.title = inp.value.trim() || t.title;
  }
  renderPendingTracks();
}

function openTrackLyricsEdit(ptid) {
  document.querySelectorAll('.aup-track-menu').forEach(m => m.classList.add('hidden'));
  const t = albumUploadState.pendingTracks.find(x => x.id === ptid);
  if (!t) return;
  const el = document.querySelector(`[data-ptid="${ptid}"]`);
  if (!el) return;

  // If lyrics editor already open, close it
  const existing = document.getElementById('aup-lyrics-editor-' + ptid);
  if (existing) { existing.remove(); return; }

  const editor = document.createElement('div');
  editor.id = 'aup-lyrics-editor-' + ptid;
  editor.className = 'aup-lyrics-editor';
  editor.innerHTML = `
    <textarea placeholder="Текст песни..." rows="5">${esc(t.lyrics || '')}</textarea>
    <div class="aup-lyrics-editor-actions">
      <button class="btn-ghost" onclick="closeTrackLyricsEdit('${ptid}')">Отмена</button>
      <button class="btn-primary" onclick="saveTrackLyrics('${ptid}')">Сохранить</button>
    </div>
  `;
  el.after(editor);
}

function openTrackFeatEdit(ptid) {
  document.querySelectorAll('.aup-track-menu').forEach(m => m.classList.add('hidden'));
  const t = albumUploadState.pendingTracks.find(x => x.id === ptid);
  if (!t) return;
  const el = document.querySelector(`[data-ptid="${ptid}"]`);
  if (!el) return;

  const existing = document.getElementById('aup-feat-editor-' + ptid);
  if (existing) { existing.remove(); return; }

  const sugId = 'aup-feat-sug-' + ptid;
  const inpId = 'aup-feat-' + ptid;
  const dataArtist = t.featArtistId ? ` data-artist-id="${esc(t.featArtistId)}"` : '';
  const editor = document.createElement('div');
  editor.id = 'aup-feat-editor-' + ptid;
  editor.className = 'aup-feat-editor aup-lyrics-editor';
  editor.innerHTML = `
    <label class="aup-feat-label">Фит-исполнитель</label>
    <div class="autocomplete-wrapper">
      <input type="text" id="${inpId}" class="aup-feat-input" placeholder="Начните вводить имя из каталога…" value="${esc(t.featArtist || '')}"${dataArtist}
        autocomplete="off"
        oninput="onAlbumPendingFeatInput(this, '${sugId}')"
        onfocus="onAlbumPendingFeatFocus(this, '${sugId}')"
        onblur="hideSuggestionsWithDelay('${sugId}')">
      <div id="${sugId}" class="suggestions-box hidden"></div>
    </div>
    <div class="aup-lyrics-editor-actions">
      <button type="button" class="btn-ghost" onclick="closeTrackFeatEdit('${ptid}')">Отмена</button>
      <button type="button" class="btn-primary" onclick="saveTrackFeat('${ptid}')">Сохранить</button>
    </div>
  `;
  el.after(editor);
}

function onAlbumPendingFeatFocus(input, suggestionsId) {
  onArtistInput(input, suggestionsId);
}

function onAlbumPendingFeatInput(input, suggestionsId) {
  delete input.dataset.artistId;
  onArtistInput(input, suggestionsId);
}

function saveTrackLyrics(ptid) {
  const editor = document.getElementById('aup-lyrics-editor-' + ptid);
  const t = albumUploadState.pendingTracks.find(x => x.id === ptid);
  if (t && editor) {
    t.lyrics = editor.querySelector('textarea').value;
  }
  if (editor) editor.remove();
  renderPendingTracks();
}

function saveTrackFeat(ptid) {
  const editor = document.getElementById('aup-feat-editor-' + ptid);
  const t = albumUploadState.pendingTracks.find(x => x.id === ptid);
  if (t && editor) {
    const input = editor.querySelector('input');
    t.featArtist = input ? input.value.trim() : '';
    t.featArtistId = input?.dataset?.artistId ? String(input.dataset.artistId) : '';
    if (!t.featArtist) t.featArtistId = '';
  }
  if (editor) editor.remove();
  renderPendingTracks();
}

function closeTrackLyricsEdit(ptid) {
  const editor = document.getElementById('aup-lyrics-editor-' + ptid);
  if (editor) editor.remove();
}

function closeTrackFeatEdit(ptid) {
  const editor = document.getElementById('aup-feat-editor-' + ptid);
  if (editor) editor.remove();
}

function togglePendingTrackSkit(ptid) {
  const t = albumUploadState.pendingTracks.find((x) => x.id === ptid);
  if (!t) return;
  t.tagType = t.tagType === 'skit' ? 'none' : 'skit';
  const menu = document.getElementById('aup-menu-' + ptid);
  if (menu) menu.classList.add('hidden');
  renderPendingTracks();
}

function removePendingTrack(ptid) {
  const t = albumUploadState.pendingTracks.find(x => x.id === ptid);
  if (t) t.removed = true;
  renderPendingTracks();
}

// Close menus on outside click
document.addEventListener('click', (e) => {
  if (!e.target.closest('.aup-track-menu-btn') && !e.target.closest('.aup-track-menu')) {
    document.querySelectorAll('.aup-track-menu').forEach(m => m.classList.add('hidden'));
  }
});

// Make album upload functions global
window.onAlbumUploadAlbumInput = onAlbumUploadAlbumInput;
window.selectAlbumForUpload = selectAlbumForUpload;
window.onAlbumCoverSel = onAlbumCoverSel;
window.onAlbumAudioSel = onAlbumAudioSel;
window.handleAlbumFileDrop = handleAlbumFileDrop;
window.toggleTrackMenu = toggleTrackMenu;
window.startEditTrackTitle = startEditTrackTitle;
window.finishEditTrackTitle = finishEditTrackTitle;
window.openTrackFeatEdit = openTrackFeatEdit;
window.saveTrackFeat = saveTrackFeat;
window.closeTrackFeatEdit = closeTrackFeatEdit;
window.openTrackLyricsEdit = openTrackLyricsEdit;
window.saveTrackLyrics = saveTrackLyrics;
window.closeTrackLyricsEdit = closeTrackLyricsEdit;
window.togglePendingTrackSkit = togglePendingTrackSkit;
window.removePendingTrack = removePendingTrack;
window.onAlbumPendingFeatFocus = onAlbumPendingFeatFocus;
window.onAlbumPendingFeatInput = onAlbumPendingFeatInput;
window.renderUpload = renderUpload;
window.handleAlbumUpload = handleAlbumUpload;

// ─── Album Upload Logic ──────────────────────────────────────────────────────

async function handleAlbumUpload() {
  const albumTitle = $('aup-album').value.trim();
  const albumArtist = $('aup-artist').value.trim();
  const albumGenre = $('aup-genre')?.value || 'Другое';
  if (!albumTitle || !albumArtist) {
    $('aup-upload-msg').innerHTML = '<div class="form-error">Укажите название альбома и исполнителя</div>';
    return;
  }

  const tracks = albumUploadState.pendingTracks.filter(t => !t.removed);
  if (!tracks.length) {
    $('aup-upload-msg').innerHTML = '<div class="form-error">Добавьте хотя бы один трек</div>';
    return;
  }

  // Check for cover warning
  const hasCover = albumUploadState.albumCoverFile || albumUploadState.albumCoverUrl;
  if (!hasCover) {
    $('aup-no-cover-warning').classList.remove('hidden');
  }

  const btn = $('aup-upload-btn');
  const progressEl = $('aup-progress');
  const msgEl = $('aup-upload-msg');
  btn.disabled = true;
  btn.innerHTML = '<div class="spinner" style="width:18px;height:18px;border-width:2px;margin:0 auto"></div>';
  progressEl.classList.remove('hidden');
  msgEl.innerHTML = '';

  const artistId = $('aup-artist').dataset.artistId || albumUploadState.albumArtistId || '';
  let currentAlbumId = albumUploadState.albumId || '';
  let uploadedCount = 0;

  for (let i = 0; i < tracks.length; i++) {
    const t = tracks[i];
    progressEl.textContent = `Загрузка ${i + 1} из ${tracks.length}...`;

    const form = new FormData();
    form.append('audio', t.file);
    form.append('title', t.title);
    form.append('artist', albumArtist);
    form.append('album', albumTitle);
    form.append('genre', albumGenre);
    form.append('trackNumber', String(albumUploadState.existingTracks.length + i + 1));

    if (artistId) form.append('artistId', artistId);
    if (currentAlbumId) form.append('albumId', currentAlbumId);
    if (t.featArtist) form.append('featArtist', t.featArtist);
    if (t.featArtistId) form.append('featArtistId', t.featArtistId);
    if (t.lyrics) form.append('lyrics', t.lyrics);
    form.append('isRemix', '0');
    form.append('isMashup', '0');
    form.append('isSkit', t.tagType === 'skit' ? '1' : '0');
    if ($('aup-i-am-artist')?.checked) form.append('iAmArtist', '1');
    const aupYear = $('aup-year')?.value?.trim();
    if (aupYear) form.append('year', aupYear);

    // Send cover only with the first track (creates the album)
    if (i === 0 && albumUploadState.albumCoverFile && !albumUploadState.albumCoverUrl) {
      form.append('cover', albumUploadState.albumCoverFile);
    }

    try {
      const data = await FORM('/api/upload', form);
      if (!data?.track?.audioUrl) {
        throw new Error('Сервер не сохранил аудиофайл — проверьте формат и размер');
      }
      uploadedCount++;
      if (data.track.albumId && !currentAlbumId) {
        currentAlbumId = data.track.albumId;
        albumUploadState.albumId = currentAlbumId;
      }
    } catch (err) {
      msgEl.innerHTML += `<div class="form-error">Ошибка при загрузке "${esc(t.title)}": ${esc(err.message)}</div>`;
    }
  }

  progressEl.classList.add('hidden');
  if (uploadedCount === tracks.length) {
    msgEl.innerHTML = `<div class="form-success">Альбом загружен! ${uploadedCount} ${pluralTracks(uploadedCount)} добавлено.</div>`;
    toast('Альбом загружен!', 'success');
    resetAlbumUploadState();
    renderAlbumUploadForm();
  } else if (uploadedCount > 0) {
    msgEl.innerHTML += `<div class="form-success">Загружено ${uploadedCount} из ${tracks.length} треков.</div>`;
  }
  btn.disabled = false;
  btn.innerHTML = `
    <svg viewBox="0 0 24 24" width="15" height="15" style="display:inline;margin-right:6px">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
    Загрузить альбом
  `;
}

async function fetchLyricsAuto() {
  const artist = $('up-artist').value.trim();
  const title = $('up-title').value.trim();
  if (!artist || !title) {
    toast('Сначала заполните исполнителя и название', 'error');
    return;
  }

  const btn = event.target; // кнопка, по которой кликнули
  btn.disabled = true;
  btn.textContent = 'Поиск...';

  try {
    const res = await fetch(resolveClientRequestUrl('/api/fetch-lyrics'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ artist, title })
    });
    const data = await res.json();
    if (data.lyrics) {
      $('up-lyrics').value = data.lyrics;
      toast('Текст найден!', 'success');
    } else {
      toast('Текст не найден', 'error');
    }
  } catch (err) {
    toast('Ошибка при поиске', 'error');
  } finally {
    btn.disabled = false;
    btn.textContent = 'Автоматически';
  }
}

// Делаем функцию глобальной (если нужно)
window.fetchLyricsAuto = fetchLyricsAuto;


async function fetchLyricsAutoForCurrentTrack() {
  if (!state.currentTrack) {
    toast('Нет текущего трека', 'error');
    return;
  }

  const artist = state.currentTrack.artist;
  const title = state.currentTrack.title;
  const btn = event.target; // кнопка, по которой кликнули
  btn.disabled = true;
  btn.textContent = 'Поиск...';

  try {
    const res = await fetch(resolveClientRequestUrl('/api/fetch-lyrics'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ artist, title })
    });
    const data = await res.json();
    if (data.lyrics) {
      $('lyrics-textarea').value = data.lyrics;
      toast('Текст найден!', 'success');
    } else {
      toast('Текст не найден', 'error');
    }
  } catch (err) {
    toast('Ошибка при поиске', 'error');
  } finally {
    btn.disabled = false;
    btn.textContent = 'Автоматически';
  }
}

// Делаем функцию глобальной
window.fetchLyricsAutoForCurrentTrack = fetchLyricsAutoForCurrentTrack;


function onAudioSel(input) {
  const f = input.files[0]; if (!f) return;
  $('audio-selected').innerHTML = `
    <div class="file-selected">
      <svg viewBox="0 0 24 24" width="18" height="18"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
      <span>${esc(f.name)}</span>
      <span class="file-size">${(f.size/1048576).toFixed(1)} МБ</span>
    </div>`;
  $('audio-selected').classList.remove('hidden');
  $('audio-drop').classList.add('hidden');
  const titleEl = $('up-title');
  if (titleEl) {
    const t = titleFromAudioFilename(f.name);
    if (t) titleEl.value = t;
  }
}

async function getSimilarTracksAndFade(trackId) {
  try {
    const exclude = state.recentlyPlayed.join(',');
    const similar = await GET(`/api/tracks/similar/${trackId}?exclude=${encodeURIComponent(exclude)}`);
    if (!similar.length) {
      toast('Похожих треков не найдено');
      return;
    }
    setQueueState(similar, 0, { lockPrevAtStart: true });
    playTrack(similar[0], { crossfade: true });
  } catch (err) {
    toast('Ошибка загрузки похожих треков', 'error');
  }
}

function onCoverSel(input) {
  const f = input.files[0]; if (!f) return;
  const r = new FileReader();
  r.onload = e => {
    $('cover-thumb').innerHTML = `<img src="${e.target.result}" style="width:100%;height:100%;object-fit:cover;border-radius:inherit" alt="">`;
    // Если ранее была предустановленная обложка альбома, удаляем пометку
    delete $('up-album').dataset.coverUrl;
  };
  r.readAsDataURL(f);
}

function handleFileDrop(e, type) {
  e.preventDefault();
  document.getElementById(`${type}-drop`).classList.remove('drag-over');
  const file = e.dataTransfer.files[0]; if (!file) return;
  const input = $(`${type}-file`);
  const dt = new DataTransfer(); dt.items.add(file); input.files = dt.files;
  if (type === 'audio') onAudioSel(input); else onCoverSel(input);
}

function resetUploadForm() {
  const title = $('up-title');
  const artist = $('up-artist');
  const featArtist = $('up-feat-artist');
  const album = $('up-album');
  const genre = $('up-genre');
  const lyrics = $('up-lyrics');
  const tagType = $('up-tag-type');
  if (title) title.value = '';
  if (artist) artist.value = '';
  if (featArtist) featArtist.value = '';
  if (album) album.value = '';
  if (genre) genre.value = 'Другое';
  if (lyrics) lyrics.value = '';
  const upYear = $('up-year');
  if (upYear) upYear.value = '';
  if (tagType) {
    if (tagType.tagName === 'SELECT') {
      tagType.value = 'none';
    } else {
      tagType.value = 'Нет';
      tagType.dataset.tagVal = 'none';
    }
  }
  const iAm = $('up-i-am-artist');
  if (iAm) iAm.checked = false;

  const audioInput = $('audio-file');
  const coverInput = $('cover-file');
  if (audioInput) audioInput.value = '';
  if (coverInput) coverInput.value = '';

  const audioSel = $('audio-selected');
  const audioDrop = $('audio-drop');
  if (audioSel) audioSel.classList.add('hidden');
  if (audioDrop) audioDrop.classList.remove('hidden');

  const coverThumb = $('cover-thumb');
  if (coverThumb) coverThumb.innerHTML = `<svg viewBox="0 0 24 24" width="22" height="22">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>`;

  if (artist) delete artist.dataset.artistId;
  if (featArtist) delete featArtist.dataset.artistId;
  if (album) { delete album.dataset.albumId; delete album.dataset.coverUrl; }

  const msg = $('upload-msg');
  if (msg) msg.innerHTML = '';
}

function resetUploadButton() {
  const btn = $('upload-btn');
  if (!btn) return;
  btn.disabled = false;
  btn.innerHTML = `
    <svg viewBox="0 0 24 24" width="15" height="15" style="display:inline;margin-right:6px">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
    Загрузить трек
  `;
}

async function handleUpload(e) {
  e.preventDefault();
  const msgEl = $('upload-msg'), btn = $('upload-btn');
  const af = $('audio-file').files[0];
  if (!af) { msgEl.innerHTML = '<div class="form-error">Выберите аудиофайл</div>'; return; }

  const form = new FormData();
  form.append('audio', af);
  const cf = $('cover-file').files[0];
  if (cf) form.append('cover', cf);
  form.append('title',  $('up-title').value);
  form.append('artist', $('up-artist').value);
  form.append('featArtist', $('up-feat-artist')?.value || '');
  form.append('album',  $('up-album').value);
  form.append('genre',  $('up-genre').value);
  const tagInput = $('up-tag-type');
  const tagVal = tagInput
    ? (tagInput.tagName === 'SELECT' ? (tagInput.value || 'none') : (tagInput.dataset?.tagVal || 'none'))
    : 'none';
  form.append('isRemix', tagVal === 'remix' ? '1' : '0');
  form.append('isMashup', tagVal === 'mashup' ? '1' : '0');
  form.append('isSkit', tagVal === 'skit' ? '1' : '0');
  
  // ✅ Добавляем ID, сохранённые при выборе из подсказок
  const artistId = $('up-artist').dataset.artistId || '';
  const featArtistId = $('up-feat-artist')?.dataset.artistId || '';
  const albumId = $('up-album').dataset.albumId || '';
  if (artistId) form.append('artistId', artistId);
  if (featArtistId) form.append('featArtistId', featArtistId);
  if (albumId) form.append('albumId', albumId);

  const lyricsVal = $('up-lyrics')?.value;
  if (lyricsVal) form.append('lyrics', lyricsVal);
  const upYearVal = $('up-year')?.value?.trim();
  if (upYearVal) form.append('year', upYearVal);
  form.append('iAmArtist', $('up-i-am-artist')?.checked ? '1' : '0');

  btn.disabled = true; btn.innerHTML = '<div class="spinner" style="width:18px;height:18px;border-width:2px;margin:0 auto"></div>';
  msgEl.innerHTML = '';

  try {
    const data = await FORM('/api/upload', form);
    if (!data?.track?.audioUrl) {
      throw new Error('Сервер не сохранил аудиофайл — проверьте формат и размер');
    }
    msgEl.innerHTML = '<div class="form-success">✓ Трек успешно загружен!</div>';
    toast('Трек загружен!', 'success');
    if (data.uploaderArtistClaimSubmitted) {
      toast('Заявка «я исполнитель» отправлена на проверку', 'success');
    }
    resetUploadForm(); // очищаем форму
    resetUploadButton(); // восстанавливаем кнопку
  } catch (err) {
    msgEl.innerHTML = `<div class="form-error">${esc(err.message)}</div>`;
    resetUploadButton();
    btn.disabled = false; btn.textContent = 'Загрузить трек';
  }
}




// ─── HELPERS ──────────────────────────────────────────────────────────────────
function pluralTracks(n) {
  const m = n % 10, h = n % 100;
  if (h >= 11 && h <= 19) return 'треков';
  if (m === 1) return 'трек';
  if (m >= 2 && m <= 4) return 'трека';
  return 'треков';
}

// ─── LYRICS ───────────────────────────────────────────────────────────────────
let _lyricsOpen = false;

function toggleLyrics() {
  closePlayerMenu();
  if (_lyricsOpen) closeLyrics();
  else openLyrics();
}

function openLyrics() {
  if (!state.currentTrack) { toast('Сначала выберите трек'); return; }
  _lyricsOpen = true;
  const panel = $('lyrics-panel');
  panel.classList.remove('hidden');
  panel.classList.add('visible');
  $('lyrics-body').scrollTop = 0;
  $('lyrics-track-name').textContent = state.currentTrack.title;
  $('lyrics-artist-name').textContent = getTrackArtistText(state.currentTrack);
  const canEditLyrics = !!state.user;
  $('lyrics-edit-btn').classList.toggle('hidden', !canEditLyrics);
  loadLyrics(state.currentTrack.id);
}

function closeLyrics() {
  _lyricsOpen = false;
  $('lyrics-panel').classList.remove('visible');
  $('lyrics-panel').classList.add('hidden');
  cancelLyricsEdit();
}

async function loadLyrics(trackId) {
  $('lyrics-body').innerHTML = '<div class="page-loading"><div class="spinner"></div></div>';
  $('lyrics-edit-form').classList.add('hidden');
  $('lyrics-body').classList.remove('hidden');
  try {
    const data = await GET(`/api/tracks/${trackId}/lyrics`);
    renderLyricsContent(data.lyrics);
  } catch {
    $('lyrics-body').innerHTML = `<div class="lyrics-empty">
      <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
      <h3>Ошибка загрузки</h3><p>Не удалось получить текст</p>
    </div>`;
  }
}

function renderLyricsContent(lyrics) {
  if (!lyrics) {
    $('lyrics-body').innerHTML = `
      <div class="lyrics-empty">
        <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
        <h3>Текст не добавлен</h3>
        <p>Нажмите «Редактировать» чтобы добавить текст</p>
      </div>`;
    return;
  }

  // Нормализуем текст (убираем множественные пустые строки, оставляем не более одной)
  const normalized = normalizeLyrics(lyrics);
  // Форматируем: экранируем, выделяем квадратные скобки жирным, круглые – курсивом
  const formatted = formatLyrics(normalized);
  // Заменяем переносы строк на <br> и вставляем ? контейнер
  const body = $('lyrics-body');
  body.innerHTML = `<div class="lyrics-text">${formatted.replace(/\n/g, '<br>')}</div>`;
  body.scrollTop = 0;
}

function openLyricsEdit() {
  if (!state.user) { openAuthModal('login'); return; }
  $('lyrics-body').classList.add('hidden');
  $('lyrics-edit-form').classList.remove('hidden');
  // Pre-fill with current lyrics
  GET(`/api/tracks/${state.currentTrack.id}/lyrics`).then(d => {
    $('lyrics-textarea').value = d.lyrics || '';
    $('lyrics-textarea').focus();
  }).catch(() => {});
}

function cancelLyricsEdit() {
  $('lyrics-edit-form').classList.add('hidden');
  $('lyrics-body').classList.remove('hidden');
}

async function saveLyrics() {
  if (!state.currentTrack) return;
  const lyrics = $('lyrics-textarea').value;
  const btn = $('lyrics-edit-form').querySelector('.btn-primary');
  btn.disabled = true; btn.textContent = 'Сохраняю...';
  try {
    await apiFetch('PUT', `/api/tracks/${state.currentTrack.id}/lyrics`, { lyrics });
    // Update local track object
    state.currentTrack.lyrics = lyrics.trim() || null;
    cancelLyricsEdit();
    renderLyricsContent(state.currentTrack.lyrics);
    toast('Текст сохранён', 'success');
  } catch (err) {
    toast(err.message, 'error');
  } finally {
    btn.disabled = false; btn.textContent = 'Сохранить';
  }
}

// ─── KEYBOARD ─────────────────────────────────────────────────────────────────
document.addEventListener('keydown', e => {
  // Esc должен всегда закрывать fullscreen, даже если фокус на input/select/range.
  if (e.key === 'Escape' && fullscreenActive) {
    closeFullscreenPlayer();
    e.preventDefault();
    return;
  }

  if (e.key === 'Escape' && isQueueSheetOpen()) {
    closeQueueSheet();
    e.preventDefault();
    return;
  }

  const tag = e.target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
  if (e.code === 'Space')       { e.preventDefault(); togglePlay(); }
  if (e.code === 'ArrowRight')  { audio.currentTime = Math.min((audio.duration||0), audio.currentTime + 10); }
  if (e.code === 'ArrowLeft')   { audio.currentTime = Math.max(0, audio.currentTime - 10); }
  if (e.code === 'KeyM')        { toggleMute(); }
  if (e.key === '/') {
    e.preventDefault();
    if (state.view !== 'search') {
      navigate('search');
      setTimeout(() => getSearchInputEl()?.focus(), 120);
    } else {
      getSearchInputEl()?.focus();
    }
  }
  if (e.key === 'f' || e.key === 'F' || e.key === 'а') {
  if (!e.ctrlKey && !e.altKey && !e.metaKey) {
    e.preventDefault();
    toggleFullscreenPlayer();
  }
}
});

// ─── CLICK OUTSIDE ────────────────────────────────────────────────────────────
document.addEventListener('click', e => {
  const um = $('user-menu'), ab = document.querySelector('.avatar-btn');
  if (um && !um.classList.contains('hidden') && !um.contains(e.target) && !ab?.contains(e.target))
    closeUserMenu();
});

// ─── SIDEBAR OVERLAY + восстановление состояния из localStorage ───────────────
function syncOfflineBanner() {
  let el = document.getElementById('mxm-offline-banner');
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (!offline) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.id = 'mxm-offline-banner';
    el.setAttribute('role', 'status');
    el.style.cssText = [
      'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:100002',
      'padding:10px 14px', 'text-align:center', 'font:14px/1.35 system-ui,sans-serif',
      'background:#1a1030', 'color:#e9d5ff', 'border-bottom:1px solid #6d28d9'
    ].join(';');
    document.body.prepend(el);
  }
  el.textContent = 'Нет сети: данные и музыка с сервера недоступны. Интерфейс работает из приложения.';
}

window.addEventListener('online', () => syncOfflineBanner());
window.addEventListener('offline', () => syncOfflineBanner());

document.addEventListener('DOMContentLoaded', () => {
  if (!$('sidebar-overlay')) {
    const ov = document.createElement('div');
    ov.id = 'sidebar-overlay'; ov.onclick = closeSidebar;
    $('app').appendChild(ov);
  }
  applySidebarCollapsedFromStorage();
  // Fix auth tabs data attr
  document.querySelectorAll('.auth-tab').forEach((t, i) => {
    t.dataset.tab = i === 0 ? 'login' : 'register';
  });
  syncOfflineBanner();
});


// Открыть модальное окно с полным описанием артиста
function openArtistBioModal(bio) {
  let modal = $('artist-bio-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'artist-bio-modal';
    modal.className = 'modal modal--center hidden';
    modal.innerHTML = `
      <div class="modal-overlay" onclick="closeArtistBioModal()"></div>
      <div class="modal-box artist-bio-modal-box">
        <button class="modal-close" onclick="closeArtistBioModal()">
          <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <div class="modal-logo">
          ${mxmEggPictureHtml({ className: 'logo-egg', width: 36, height: 36, alt: 'MXM' })}
          <span>Об исполнителе</span>
        </div>
        <div class="artist-bio-content" style="white-space: pre-wrap; max-height: 60vh; overflow-y: auto; padding: 10px 0;"></div>
      </div>
    `;
    document.body.appendChild(modal);
  }
  modal.querySelector('.artist-bio-content').textContent = bio;
  modal.classList.remove('hidden');
}

function closeArtistBioModal() {
  const modal = $('artist-bio-modal');
  if (modal) modal.classList.add('hidden');
}

// ═══ MXM CHAT (WebSocket; шифрование на сервере) ═══════════════════════════════
/**
 * @typedef {Object} MessageEntity
 * @property {string} id
 * @property {string} fromId server field `senderId`
 * @property {string} toId active `peerId` in UI
 * @property {string} [text] — дешифрованный текст, если `text`
 * @property {string} [mediaType]
 * @property {string} [mediaUrl]
 * @property {string|Date} [timestamp] — `createdAt`
 * @property {string|Date|null} [readAt] last read
 * @property {Array<{emoji: string, count: number, userIds: string[]}>} [reactions]
 */
let _mxmChatWs = null;
let _mxmChatPollTimer = null;
let _mxmChatActivePeerId = null;
let _mxmChatConversations = [];
let _mxmChatMessagesCache = [];
let _mxmChatStickerManifest = null;
let _mxmChatPeerLastReadAt = null;
let _mxmChatReplyTo = null;
let _mxmChatEditMsg = null;
let _mxmChatMiniPlayerTimer = null;
let _mxmChatMobileThreadOpen = false;
let _mxmChatPeerBlocked = false;
let _mxmChatPeerIsFriend = true;
let _mxmChatSidebarQuery = '';
let _mxmChatSearchReqSeq = 0;
let _mxmChatThreadMenuOpen = false;
let _mxmChatThreadMenuOpenedAt = 0;
let _mxmChatTracksCloseTimer = null;
let _mxmChatTracksPanelAnchor = null;
/** Монотонный счётчик открытия треда — отменяет докрутку после быстрой смены диалога. */
let _mxmChatOpenSeq = 0;
let _mxmChatThreadMyLastReadAt = null;
let _mxmChatOpenHadUnread = false;
let _mxmChatPeerTyping = false;
let _mxmChatPeerLastSeenMap = new Map();
let _mxmChatPeerStatusFetchedAt = new Map();
let _mxmChatPeerStatusPending = new Map();
let _mxmChatComposerResizeObserver = null;
let _mxmChatComposerObservedTools = null;

const _MXM_SVG_KCAMERA =
  '<svg class="mxm-chat-kind-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><path d="M4 8h2l1.2-1.5A1 1 0 0 1 8 6h8a1 1 0 0 1 .8.4L19 8h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"/><circle cx="12" cy="13" r="3" fill="none" stroke="currentColor"/></svg>';
const _MXM_SVG_KPLAY =
  '<svg class="mxm-chat-kind-icon" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M6 4l14 8-14 8V4z"/></svg>';
const _MXM_SVG_KNOTE =
  '<svg class="mxm-chat-kind-icon" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M4 4h7l5 5v11H4V4z"/><path d="M12 3v4h3" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';
const _MXM_SVG_KFILE =
  '<svg class="mxm-chat-kind-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M4 4h8l4 4v12H4V4Z"/><path d="M12 3v4h2"/></svg>';
const _MXM_SVG_KMIC =
  '<svg class="mxm-chat-kind-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Z"/><path d="M6 10v1a6 6 0 0 0 5 5.9"/><path d="M12 17v3"/><path d="M9 20h6"/></svg>';
const _MXM_SVG_CHAT_MUSIC =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>';

function _mxmChatIsMobile() { return window.innerWidth <= 900; }

function mxmChatConversationKey(a, b) {
  return [String(a), String(b)].sort().join(':');
}

/* ── WebSocket ─────────────────────────────────────────────────────────────── */
let _mxmChatWsRetries = 0;
const _mxmChatWsMaxRetries = 5;
/** Резервный опрос полной ленты: реже при живом WS (события приходят по сокету), чаще без него. */
const _MXM_CHAT_POLL_MS_WS_OK = 90000;
const _MXM_CHAT_POLL_MS_WS_DOWN = 5000;
const _MXM_CHAT_POLL_MS_TAB_HIDDEN = 120000;

function _mxmChatPollDelayMs() {
  if (typeof document !== 'undefined' && document.hidden) return _MXM_CHAT_POLL_MS_TAB_HIDDEN;
  if (_mxmChatWs && _mxmChatWs.readyState === WebSocket.OPEN) return _MXM_CHAT_POLL_MS_WS_OK;
  return _MXM_CHAT_POLL_MS_WS_DOWN;
}

function mxmChatConnectWs() {
  if (!state.token) return;
  if (_mxmChatWs && (_mxmChatWs.readyState === WebSocket.OPEN || _mxmChatWs.readyState === WebSocket.CONNECTING)) return;
  if (_mxmChatWsRetries >= _mxmChatWsMaxRetries) return;
  try {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${location.host}/ws/chat?token=${encodeURIComponent(state.token)}`;
    const ws = new WebSocket(url);
    _mxmChatWs = ws;
    ws.onopen = async () => {
      _mxmChatWsRetries = 0;
      if (state.view === 'messages' && _mxmChatActivePeerId) mxmChatPollNewMessages();
      if (typeof mxmListenRestoreActive === 'function') await mxmListenRestoreActive();
      if (typeof mxmListenFlushPending === 'function') mxmListenFlushPending();
    };
    ws.onclose = () => {
      if (_mxmChatWs === ws) _mxmChatWs = null;
      _mxmChatWsRetries++;
      if (_mxmChatWsRetries >= _mxmChatWsMaxRetries) return;
      const delay = Math.min(4000 * Math.pow(2, _mxmChatWsRetries - 1), 60000);
      setTimeout(() => { if (state.token) mxmChatConnectWs(); }, delay);
    };
    ws.onerror = () => {};
    ws.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d.type === 'chat_message') mxmChatOnServerMessage(d.message);
        if (d.type === 'chat_edit') mxmChatOnServerEdit(d.message);
        if (d.type === 'chat_delete') mxmChatOnServerDelete(d.messageId);
        if (d.type === 'typing') mxmChatOnTypingRemote(d);
        if (d.type === 'presence') mxmChatUpdateStatusUi(d.userId, d.online, null);
        if (d.type === 'chat_pin') { _mxmChatPinnedMsg = d.message; mxmChatUpdatePinnedBar(); }
        if (d.type === 'chat_unpin') { _mxmChatPinnedMsg = null; mxmChatUpdatePinnedBar(); }
        if (d.type === 'chat_read') mxmChatOnPeerReadWs(d);
        if (d.type === 'chat_reaction') mxmChatUpdateReactionsForMsg(d.messageId, d.reactions);
        if (d.type.startsWith('listen_') && typeof mxmListenHandleWs === 'function') {
          void mxmListenHandleWs(d);
        }
      } catch (_) {}
    };
  } catch (_) {}
}

function mxmChatDisconnectWs() {
  mxmChatStopPoll();
  mxmChatStopMiniPlayer();
  try { _mxmChatWs?.close(); } catch (_) {}
  _mxmChatWs = null;
  _mxmChatWsRetries = 0;
}

function mxmChatOnPeerReadWs(d) {
  if (!d || state.view !== 'messages' || !_mxmChatActivePeerId) return;
  if (String(d.fromUserId || '') !== String(_mxmChatActivePeerId)) return;
  if (d.lastReadAt) _mxmChatPeerLastReadAt = new Date(d.lastReadAt);
  mxmChatRefreshReadTicks();
}

/* ── Polling (редкий при открытом WS — лента идёт по сокету; частый только без WS) ── */
function mxmChatStopPoll() {
  if (_mxmChatPollTimer) {
    clearTimeout(_mxmChatPollTimer);
    _mxmChatPollTimer = null;
  }
}

const _MXM_CHAT_NEAR_BOTTOM_PX = 140;
const _MXM_CHAT_INPUT_MIN_HEIGHT = 44;
const _MXM_CHAT_INPUT_MAX_HEIGHT = 160;
const _MXM_CHAT_STATUS_CACHE_TTL_MS = 90_000;
const _MXM_CHAT_STATUS_PREFETCH_LIMIT = 24;
const _MXM_CHAT_INITIAL_STICK_MS = 1600;

function mxmChatWaitFrames(count = 1) {
  return new Promise((resolve) => {
    const step = () => {
      if (count-- <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
}

function mxmChatWaitForMediaReady(nodes, timeoutMs = 900) {
  const pending = [];
  for (const node of nodes || []) {
    if (!(node instanceof HTMLElement)) continue;
    if (node instanceof HTMLImageElement) {
      if (!node.currentSrc && !node.getAttribute('src')) continue;
      if (node.complete) continue;
      pending.push(
        new Promise((resolve) => {
          const done = () => resolve();
          node.addEventListener('load', done, { once: true });
          node.addEventListener('error', done, { once: true });
        })
      );
      continue;
    }
    if (node instanceof HTMLVideoElement) {
      if (!node.currentSrc && !node.getAttribute('src')) continue;
      if (node.readyState >= 1) continue;
      pending.push(
        new Promise((resolve) => {
          const done = () => resolve();
          node.addEventListener('loadedmetadata', done, { once: true });
          node.addEventListener('error', done, { once: true });
        })
      );
    }
  }
  if (!pending.length) return Promise.resolve();
  return Promise.race([
    Promise.all(pending),
    new Promise((resolve) => setTimeout(resolve, timeoutMs))
  ]);
}

function mxmChatMaybeStickBottom(scroll, force = false) {
  if (!scroll) return;
  const shouldStick =
    force ||
    scroll.dataset.mxmStickBottom === '1' ||
    mxmChatThreadNearBottom(scroll, _MXM_CHAT_NEAR_BOTTOM_PX);
  if (!shouldStick) {
    mxmChatUpdateScrollDownBtn();
    return;
  }
  const apply = () => {
    scroll.scrollTop = Math.max(0, scroll.scrollHeight - scroll.clientHeight);
    mxmChatLockShellScroll();
    mxmChatUpdateScrollDownBtn();
  };
  apply();
  requestAnimationFrame(apply);
}

function mxmChatScheduleStickBottomRelease(scroll, passedOpenSeq) {
  if (!scroll) return;
  scroll.dataset.mxmStickBottom = '1';
  clearTimeout(scroll._mxmStickBottomTimer);
  scroll._mxmStickBottomTimer = setTimeout(() => {
    if (
      passedOpenSeq !== _mxmChatOpenSeq ||
      !document.contains(scroll) ||
      state.view !== 'messages'
    ) {
      return;
    }
    delete scroll.dataset.mxmStickBottom;
  }, _MXM_CHAT_INITIAL_STICK_MS);
}

async function mxmChatPrepareThreadOpenScroll(scroll, passedOpenSeq) {
  if (!scroll) return;
  const isStale = () =>
    passedOpenSeq !== _mxmChatOpenSeq ||
    !document.contains(scroll) ||
    state.view !== 'messages';
  scroll.classList.add('mxm-chat-thread-scroll--preparing');
  scroll.setAttribute('aria-busy', 'true');
  mxmChatScheduleStickBottomRelease(scroll, passedOpenSeq);
  mxmChatApplyInitialThreadScroll(scroll, passedOpenSeq, null);
  await mxmChatWaitFrames(2);
  if (isStale()) return;
  await mxmChatWaitForMediaReady(scroll.querySelectorAll('img, video'));
  if (isStale()) return;
  mxmChatMaybeStickBottom(scroll, true);
  scroll.classList.remove('mxm-chat-thread-scroll--preparing');
  scroll.setAttribute('aria-busy', 'false');
}

function mxmChatThreadNearBottom(el, gapPx = _MXM_CHAT_NEAR_BOTTOM_PX) {
  if (!el) return true;
  return el.scrollHeight - el.scrollTop - el.clientHeight <= gapPx;
}

/** Сбрасывает прокрутку внешней колонки (#content): иначе после отправки/вложения лента тянет предка вверх. */
function mxmChatLockShellScroll() {
  try {
    const c = $('content');
    if (c && c.scrollTop) c.scrollTop = 0;
    const mw = $('main-wrapper');
    if (mw && mw.scrollTop) mw.scrollTop = 0;
  } catch (_) {}
}

/**
 * После смены высоты композера (ответ, эмодзи) лента и #content не должны «подпрыгивать».
 * nearBottom / keepTop — снимок до изменения разметки (иначе flex уже сдвинул scroll).
 */
function mxmChatStabilizeThreadAfterComposerChange(nearBottom, keepTop) {
  const scroll = $('mxm-chat-thread-scroll');
  if (!scroll) {
    requestAnimationFrame(() => mxmChatLockShellScroll());
    return;
  }
  const near =
    typeof nearBottom === 'boolean' ? nearBottom : mxmChatThreadNearBottom(scroll, 200);
  const keep = typeof keepTop === 'number' ? keepTop : scroll.scrollTop;

  const apply = () => {
    mxmChatLockShellScroll();
    const s = $('mxm-chat-thread-scroll');
    if (!s) return;
    const maxS = Math.max(0, s.scrollHeight - s.clientHeight);
    if (near) {
      mxmChatMaybeStickBottom(s, true);
    } else {
      s.scrollTop = Math.min(Math.max(0, keep), maxS);
    }
    mxmChatUpdateScrollDownBtn();
  };
  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(apply);
  });
}

function mxmChatFocusComposer() {
  const ta = $('mxm-chat-input');
  if (!ta) return;
  try {
    ta.focus({ preventScroll: true });
  } catch (_) {
    try {
      ta.focus();
    } catch (_) {}
  }
}

function mxmChatResizeComposerInput(input, force = false) {
  if (!input) return _MXM_CHAT_INPUT_MIN_HEIGHT;
  const nextHeight = input.value.trim()
    ? Math.min(Math.max(input.scrollHeight, _MXM_CHAT_INPUT_MIN_HEIGHT), _MXM_CHAT_INPUT_MAX_HEIGHT)
    : _MXM_CHAT_INPUT_MIN_HEIGHT;
  const prevHeight = parseInt(input.style.height || '', 10);
  if (force || !Number.isFinite(prevHeight) || prevHeight !== nextHeight) {
    input.style.height = nextHeight + 'px';
  }
  input.style.overflowY = input.scrollHeight > _MXM_CHAT_INPUT_MAX_HEIGHT ? 'auto' : 'hidden';
  return nextHeight;
}

function mxmChatResetComposerInput(input, clearValue = false) {
  const ta = input || $('mxm-chat-input');
  if (!ta) return;
  if (clearValue) ta.value = '';
  ta.style.height = _MXM_CHAT_INPUT_MIN_HEIGHT + 'px';
  ta.style.overflowY = 'hidden';
}

function mxmChatSyncComposerMetrics() {
  const scroll = $('mxm-chat-thread-scroll');
  const tools = $('mxm-chat-tools');
  if (!scroll || !tools) return;
  const toolsHeight = Math.round(tools.offsetHeight || 0);
  const nearBottom = scroll.dataset.mxmStickBottom === '1' || mxmChatThreadNearBottom(scroll, 56);
  scroll.style.setProperty('--mxm-chat-composer-height', `${toolsHeight}px`);
  scroll.style.setProperty('--mxm-chat-composer-gap', `${Math.round(toolsHeight * 0.08)}px`);
  if (nearBottom) mxmChatMaybeStickBottom(scroll, true);
  else mxmChatUpdateScrollDownBtn();
}

function mxmChatBindComposerResizeObserver() {
  const tools = $('mxm-chat-tools');
  if (!tools) return;
  if (typeof ResizeObserver === 'undefined') {
    mxmChatSyncComposerMetrics();
    return;
  }
  if (_mxmChatComposerObservedTools === tools) {
    mxmChatSyncComposerMetrics();
    return;
  }
  try {
    _mxmChatComposerResizeObserver?.disconnect();
  } catch (_) {}
  _mxmChatComposerObservedTools = tools;
  _mxmChatComposerResizeObserver = new ResizeObserver(() => {
    requestAnimationFrame(() => {
      mxmChatSyncComposerMetrics();
      mxmChatScheduleStabilizeAfterComposerResize();
    });
  });
  _mxmChatComposerResizeObserver.observe(tools);
  mxmChatSyncComposerMetrics();
}

/** Любое изменение высоты композера (многострочный ввод, удаление смайлика при открытой панели эмодзи и т.д.) */
function mxmChatScheduleStabilizeAfterComposerResize() {
  if (typeof window === 'undefined') return;
  const scroll = $('mxm-chat-thread-scroll');
  if (!scroll) {
    requestAnimationFrame(() => mxmChatLockShellScroll());
    return;
  }
  const near = mxmChatThreadNearBottom(scroll, 220);
  const top = scroll.scrollTop;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      mxmChatStabilizeThreadAfterComposerChange(near, top);
    });
  });
}

/** Кнопка «вниз» отключена — раздражала ложным счётчиком сообщений. */
function mxmChatUpdateScrollDownBtn() {
  const btn = $('mxm-chat-scroll-bottom-btn');
  if (btn) btn.classList.add('hidden');
  const countEl = $('mxm-chat-scroll-bottom-count');
  if (countEl) {
    countEl.textContent = '';
    countEl.classList.add('hidden');
  }
}

window.mxmChatScrollThreadToBottom = function () {
  const wrap = $('mxm-chat-thread-scroll');
  if (!wrap) return;
  mxmChatMaybeStickBottom(wrap, true);
};

function mxmChatBindThreadScroll() {
  const scroll = $('mxm-chat-thread-scroll');
  if (!scroll || scroll.dataset.mxmScrollUiBound === '1') return;
  scroll.dataset.mxmScrollUiBound = '1';
  scroll.addEventListener(
    'scroll',
    () => {
      mxmChatUpdateScrollDownBtn();
    },
    { passive: true }
  );
}

/**
 * В конец ленты при открытии диалога: один синхронный проход + лёгкая догрузка по rAF,
 * если scrollHeight меняется (шрифты, медиа). Без пачки setTimeout — это вызывало рывки и лаги.
 */
function mxmChatApplyInitialThreadScroll(scroll, passedOpenSeq, _myLastReadAtIso) {
  if (!scroll) return;
  const isStale = () =>
    passedOpenSeq !== _mxmChatOpenSeq ||
    !document.contains(scroll) ||
    state.view !== 'messages';
  const stick = () => {
    if (isStale()) return;
    mxmChatMaybeStickBottom(scroll, true);
  };
  let lastH = scroll.scrollHeight;
  let passes = 0;
  const maxPasses = 12;
  const refine = () => {
    if (isStale()) return;
    if (passes++ > maxPasses) return;
    const h = scroll.scrollHeight;
    if (h !== lastH) {
      lastH = h;
      stick();
      requestAnimationFrame(refine);
    }
  };
  requestAnimationFrame(refine);

  const onMediaSettled = () => {
    if (isStale()) return;
    lastH = -1;
    passes = 0;
    refine();
  };
  scroll.querySelectorAll('img').forEach((img) => {
    if (img.complete) return;
    const once = () => onMediaSettled();
    img.addEventListener('load', once, { once: true });
    img.addEventListener('error', once, { once: true });
  });
  scroll.querySelectorAll('video').forEach((v) => {
    if (v.readyState >= 1) return;
    const once = () => onMediaSettled();
    v.addEventListener('loadedmetadata', once, { once: true });
    v.addEventListener('error', once, { once: true });
  });
}

const _MXM_CHAT_VOICE_PLAY_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
const _MXM_CHAT_VOICE_PAUSE_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>';
const _MXM_CHAT_VOICE_LOAD_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="8" opacity=".28"></circle><path d="M20 12a8 8 0 0 0-8-8"></path></svg>';

let _mxmChatActiveVoiceBtn = null;
let _mxmChatActiveVoiceCard = null;
let _mxmChatVoicePendingSeekRatio = null;

function mxmChatFormatVoiceTime(sec) {
  const safe = Number.isFinite(sec) ? Math.max(0, Math.floor(sec)) : 0;
  const mm = Math.floor(safe / 60);
  const ss = safe % 60;
  return mm + ':' + String(ss).padStart(2, '0');
}

function mxmChatBuildVoiceBarsHtml(seedSource) {
  const src = String(seedSource || 'voice');
  let seed = 0;
  for (let i = 0; i < src.length; i++) seed = ((seed * 33) + src.charCodeAt(i)) >>> 0;
  let html = '';
  for (let i = 0; i < 24; i++) {
    seed = ((seed * 1664525) + 1013904223) >>> 0;
    const height = 24 + (seed % 56);
    html += `<span class="mxm-chat-voice-bar" style="--mxm-voice-bar-h:${height};--mxm-voice-bar-i:${i}"></span>`;
  }
  return html;
}

function mxmChatBuildVoiceCardHtml({ duration, attachmentId, src, seedSource }) {
  const safeDuration = Number.isFinite(duration) ? Math.max(0, Math.round(duration)) : 0;
  const timeLabel = mxmChatFormatVoiceTime(safeDuration);
  const sourceAttr = attachmentId
    ? `data-mxm-chat-attachment="${esc(attachmentId)}"`
    : `data-voice-src="${esc(src || '')}"`;
  return `<div class="mxm-chat-voice-card" data-voice-duration="${safeDuration}" data-voice-label="${timeLabel}" style="--mxm-voice-progress:0%">
    <button type="button" class="mxm-chat-voice-play" ${sourceAttr} onclick="mxmChatPlayVoiceBtn(this)" aria-label="Play voice message">${_MXM_CHAT_VOICE_PLAY_SVG}</button>
    <button type="button" class="mxm-chat-voice-wave" onclick="mxmChatVoiceSeek(event,this)" aria-label="Seek voice message" aria-valuemin="0" aria-valuemax="${safeDuration}" aria-valuenow="0">
      <span class="mxm-chat-voice-progress"></span>
      <span class="mxm-chat-voice-bars">${mxmChatBuildVoiceBarsHtml(seedSource)}</span>
    </button>
    <div class="mxm-chat-voice-meta">
      <span class="mxm-chat-voice-state" aria-hidden="true"></span>
      <span class="mxm-chat-voice-dur" data-voice-total="${safeDuration}" data-voice-label="${timeLabel}">${timeLabel}</span>
    </div>
  </div>`;
}

function mxmChatVoiceCardButtonIcon(btn, iconHtml) {
  if (!btn) return;
  btn.innerHTML = iconHtml;
}

function mxmChatVoiceGetCurrentDuration(card) {
  if (!card) return 0;
  const durEl = card.querySelector('.mxm-chat-voice-dur');
  const metaDur = Number(durEl?.dataset.voiceTotal || 0);
  const cardDur = Number(card.dataset.voiceDuration || 0);
  return Number.isFinite(metaDur) && metaDur > 0 ? metaDur : (Number.isFinite(cardDur) ? cardDur : 0);
}

function mxmChatVoiceSyncCard(card, currentTime, duration) {
  if (!card) return;
  const durEl = card.querySelector('.mxm-chat-voice-dur');
  const wave = card.querySelector('.mxm-chat-voice-wave');
  const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : mxmChatVoiceGetCurrentDuration(card);
  const safeCurrent = Number.isFinite(currentTime) ? Math.max(0, currentTime) : 0;
  if (durEl) {
    if (safeDuration > 0) durEl.dataset.voiceTotal = String(safeDuration);
    const baseLabel = durEl.dataset.voiceLabel || card.dataset.voiceLabel || mxmChatFormatVoiceTime(safeDuration);
    if (card.classList.contains('is-active')) {
      durEl.textContent = mxmChatFormatVoiceTime(safeCurrent) + (safeDuration > 0 ? ` / ${mxmChatFormatVoiceTime(safeDuration)}` : '');
      durEl.classList.add('mxm-chat-voice-dur-current');
    } else {
      durEl.textContent = baseLabel;
      durEl.classList.remove('mxm-chat-voice-dur-current');
    }
  }
  const ratio = safeDuration > 0 ? Math.max(0, Math.min(1, safeCurrent / safeDuration)) : 0;
  card.style.setProperty('--mxm-voice-progress', `${(ratio * 100).toFixed(3)}%`);
  if (wave) {
    wave.setAttribute('aria-valuemax', String(Math.round(safeDuration || 0)));
    wave.setAttribute('aria-valuenow', String(Math.round(safeCurrent || 0)));
  }
}

function mxmChatVoiceResetCard(card) {
  if (!card) return;
  card.classList.remove('is-loading', 'is-active', 'is-playing', 'is-paused');
  card.style.setProperty('--mxm-voice-progress', '0%');
  const btn = card.querySelector('.mxm-chat-voice-play');
  if (btn) {
    btn.classList.remove('is-loading');
    mxmChatVoiceCardButtonIcon(btn, _MXM_CHAT_VOICE_PLAY_SVG);
  }
  mxmChatVoiceSyncCard(card, 0, mxmChatVoiceGetCurrentDuration(card));
}

function mxmChatResetAllVoiceCards(exceptCard = null) {
  document.querySelectorAll('.mxm-chat-voice-card').forEach((card) => {
    if (exceptCard && card === exceptCard) return;
    mxmChatVoiceResetCard(card);
  });
}

function mxmChatSyncActiveVoiceCard(audio) {
  const card = _mxmChatActiveVoiceCard;
  if (!audio || !card || !document.body.contains(card)) return;
  if (Number.isFinite(audio.duration) && audio.duration > 0) {
    card.dataset.voiceDuration = String(audio.duration);
    const durEl = card.querySelector('.mxm-chat-voice-dur');
    if (durEl) {
      durEl.dataset.voiceTotal = String(audio.duration);
      if (!durEl.dataset.voiceLabel || durEl.dataset.voiceLabel === '0:00') durEl.dataset.voiceLabel = mxmChatFormatVoiceTime(audio.duration);
    }
  }
  mxmChatVoiceSyncCard(card, audio.currentTime || 0, audio.duration);
}

function mxmChatApplyPendingVoiceSeek(audio) {
  if (!audio || _mxmChatVoicePendingSeekRatio == null) return;
  if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
  audio.currentTime = audio.duration * Math.max(0, Math.min(1, _mxmChatVoicePendingSeekRatio));
  _mxmChatVoicePendingSeekRatio = null;
  mxmChatSyncActiveVoiceCard(audio);
}

function mxmChatEnsureVoiceAudio() {
  let audio = document.getElementById('mxm-chat-voice-audio');
  if (audio) return audio;
  audio = document.createElement('audio');
  audio.id = 'mxm-chat-voice-audio';
  audio.preload = 'metadata';
  audio.playsInline = true;
  audio.setAttribute('playsinline', '');
  audio.addEventListener('loadedmetadata', () => {
    mxmChatApplyPendingVoiceSeek(audio);
    mxmChatSyncActiveVoiceCard(audio);
  });
  audio.addEventListener('timeupdate', () => {
    mxmChatSyncActiveVoiceCard(audio);
  });
  audio.addEventListener('play', () => {
    if (_mxmChatActiveVoiceCard) {
      _mxmChatActiveVoiceCard.classList.remove('is-loading', 'is-paused');
      _mxmChatActiveVoiceCard.classList.add('is-active', 'is-playing');
    }
    if (_mxmChatActiveVoiceBtn) {
      _mxmChatActiveVoiceBtn.classList.remove('is-loading');
      mxmChatVoiceCardButtonIcon(_mxmChatActiveVoiceBtn, _MXM_CHAT_VOICE_PAUSE_SVG);
    }
    mxmChatApplyPendingVoiceSeek(audio);
    mxmChatSyncActiveVoiceCard(audio);
  });
  audio.addEventListener('pause', () => {
    if (audio.ended) return;
    if (_mxmChatActiveVoiceCard) {
      _mxmChatActiveVoiceCard.classList.remove('is-loading', 'is-playing');
      _mxmChatActiveVoiceCard.classList.add('is-active', 'is-paused');
    }
    if (_mxmChatActiveVoiceBtn) {
      _mxmChatActiveVoiceBtn.classList.remove('is-loading');
      mxmChatVoiceCardButtonIcon(_mxmChatActiveVoiceBtn, _MXM_CHAT_VOICE_PLAY_SVG);
    }
    mxmChatSyncActiveVoiceCard(audio);
  });
  audio.addEventListener('ended', () => {
    mxmChatResetAllVoiceCards();
    _mxmChatActiveVoiceBtn = null;
    _mxmChatActiveVoiceCard = null;
    _mxmChatVoicePendingSeekRatio = null;
    delete audio.dataset.voiceUrl;
    audio.currentTime = 0;
  });
  document.body.appendChild(audio);
  return audio;
}

async function mxmChatResolveVoiceUrl(btn) {
  const mid = btn?.getAttribute('data-mxm-chat-attachment');
  let url = btn?.getAttribute('data-voice-src');
  if (mid && state.token) {
    if (!btn._mxmVoiceBlobUrl) {
      const r = await fetch(resolveClientRequestUrl(`/api/chat/attachments/${encodeURIComponent(mid)}`), {
        headers: { Authorization: `Bearer ${state.token}` },
        credentials: 'include',
        cache: 'no-store'
      });
      if (!r.ok) return '';
      btn._mxmVoiceBlobUrl = URL.createObjectURL(await r.blob());
    }
    url = btn._mxmVoiceBlobUrl;
  }
  return url || '';
}

window.mxmChatStopVoicePlayback = function (options = {}) {
  const audio = document.getElementById('mxm-chat-voice-audio');
  if (audio) {
    try {
      audio.pause();
    } catch (_) {}
    if (options.clearSource !== false) {
      audio.removeAttribute('src');
      delete audio.dataset.voiceUrl;
      try {
        audio.load();
      } catch (_) {}
    }
  }
  mxmChatResetAllVoiceCards();
  _mxmChatActiveVoiceBtn = null;
  _mxmChatActiveVoiceCard = null;
  _mxmChatVoicePendingSeekRatio = null;
};

window.mxmChatVoiceSeek = function (event, waveBtn) {
  event?.preventDefault?.();
  event?.stopPropagation?.();
  const card = waveBtn?.closest('.mxm-chat-voice-card');
  const btn = card?.querySelector('.mxm-chat-voice-play');
  if (!card || !btn) return;
  const rect = waveBtn.getBoundingClientRect();
  const point = event?.touches?.[0]?.clientX ?? event?.clientX;
  if (!Number.isFinite(point) || rect.width <= 0) return;
  const ratio = Math.max(0, Math.min(1, (point - rect.left) / rect.width));
  const audio = document.getElementById('mxm-chat-voice-audio');
  const isCurrent = !!audio && btn === _mxmChatActiveVoiceBtn && Number.isFinite(audio.duration) && audio.duration > 0;
  if (isCurrent) {
    audio.currentTime = audio.duration * ratio;
    mxmChatSyncActiveVoiceCard(audio);
    return;
  }
  _mxmChatVoicePendingSeekRatio = ratio;
  void mxmChatPlayVoiceBtn(btn);
};

async function mxmChatPollNewMessages() {
  if (state.view !== 'messages' || !_mxmChatActivePeerId) return;
  const peer = _mxmChatActivePeerId;
  try {
    const res = await GET(`/api/chat/messages?peerUserId=${encodeURIComponent(peer)}&limit=80`);
    const list = res.messages || [];
    if (res.peerLastReadAt) _mxmChatPeerLastReadAt = new Date(res.peerLastReadAt);
    const seen = new Set(_mxmChatMessagesCache.map((m) => m.id));
    let gotIncoming = false;
    for (const m of list) {
      const existing = _mxmChatMessagesCache.find((c) => c.id === m.id);
      if (existing) {
        if (m.edited && !existing.edited) {
          Object.assign(existing, m);
          mxmChatEnsureReplyPreview(existing);
          mxmChatUpdateMsgDom(existing);
        }
        if (m.deleted && !existing.deleted) { _mxmChatMessagesCache = _mxmChatMessagesCache.filter((c) => c.id !== m.id); mxmChatRenderThreadFromCache({ preserveScroll: true }); continue; }
        continue;
      }
      if (m.deleted) continue;
      _mxmChatMessagesCache.push(m);
      mxmChatAppendMessageRow(m);
      if (m.senderId !== state.user?.id) gotIncoming = true;
    }
    if (gotIncoming) POST('/api/chat/read', { peerUserId: peer }).catch(() => {});
    mxmChatRefreshReadTicks();
  } catch (_) {}
}

function mxmChatStartPoll() {
  mxmChatStopPoll();
  function tick() {
    if (state.view !== 'messages' || !_mxmChatActivePeerId) {
      _mxmChatPollTimer = null;
      return;
    }
    mxmChatPollNewMessages().finally(() => {
      if (state.view !== 'messages' || !_mxmChatActivePeerId) {
        _mxmChatPollTimer = null;
        return;
      }
      _mxmChatPollTimer = setTimeout(tick, _mxmChatPollDelayMs());
    });
  }
  _mxmChatPollTimer = setTimeout(tick, _mxmChatPollDelayMs());
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (state.view !== 'messages' || !_mxmChatActivePeerId) return;
    mxmChatPollNewMessages();
  });
}

/* ── Thread UI helpers ─────────────────────────────────────────────────────── */
async function mxmChatHandleHashPeerChange(rawRef) {
  const raw = String(rawRef || '').trim();
  if (!state.token || !raw) return;
  _mxmChatPeerPublicRef = raw;
  let pid = raw;
  if (!looksLikeUserUuidClient(raw)) {
    try {
      const d = await GET(`/api/users/${encodeURIComponent(raw)}/profile`);
      pid = d?.id || null;
    } catch {
      pid = null;
    }
  }
  _mxmChatActivePeerId = pid;
  if (pid) await mxmChatOpenThread(pid);
  else {
    _mxmChatPeerPublicRef = null;
    toast('Пользователь не найден', 'error');
    mxmChatResetThreadUi();
  }
}

function mxmChatResetThreadUi() {
  mxmChatStopPoll();
  _mxmChatMessagesCache = [];
  _mxmChatReplyTo = null;
  _mxmChatEditMsg = null;
  _mxmChatPeerLastReadAt = null;
  _mxmChatPeerTyping = false;
  _mxmChatPeerPublicRef = null;
  if (typeof mxmChatCloseThreadMenu === 'function') mxmChatCloseThreadMenu();
  if (typeof window.mxmChatSearchClose === 'function') window.mxmChatSearchClose({ clear: true });
  if (typeof mxmChatEmojiCloseImmediate === 'function') mxmChatEmojiCloseImmediate();
  if (typeof mxmChatCloseTracksPanel === 'function') mxmChatCloseTracksPanel({ immediate: true });
  if (_mxmChatIsMobile()) mxmChatMobileShowList();
  const placeholder = $('mxm-chat-placeholder');
  const thread = $('mxm-chat-thread');
  const tools = $('mxm-chat-tools');
  const scroll = $('mxm-chat-thread-scroll');
  if (placeholder) placeholder.classList.remove('hidden');
  if (thread) thread.classList.add('hidden');
  if (tools) tools.classList.add('hidden');
  if (scroll) {
    scroll.innerHTML = '';
    scroll.classList.remove('mxm-chat-thread-scroll--preparing');
    scroll.style.removeProperty('--mxm-chat-composer-height');
    clearTimeout(scroll._mxmStickBottomTimer);
    delete scroll.dataset.mxmStickBottom;
  }
  _mxmChatThreadMyLastReadAt = null;
  _mxmChatOpenHadUnread = false;
  const status = $('mxm-chat-thread-status');
  if (status) {
    status.textContent = '';
    status.className = 'mxm-chat-thread-status hidden';
  }
  $('mxm-chat-thread-av')?.classList.remove('mxm-peer-online');
  $('mxm-chat-scroll-bottom-btn')?.classList.add('hidden');
}

function mxmChatOnServerMessage(msg) {
  if (!msg) return;
  if (state.view !== 'messages') { mxmChatUpdateUnreadBadge(); return; }
  const me = state.user?.id;
  const peer = _mxmChatActivePeerId;
  if (!peer || !me) { mxmChatRefreshSidebar(); return; }
  const mine = mxmChatConversationKey(me, peer);
  if (msg.conversationKey !== mine) { mxmChatRefreshSidebar(); return; }
  if (_mxmChatMessagesCache.some((m) => m.id === msg.id)) return;
  _mxmChatMessagesCache.push(msg);
  mxmChatAppendMessageRow(msg);
  if (msg.senderId !== me) POST('/api/chat/read', { peerUserId: peer }).catch(() => {});
  mxmChatRefreshSidebar();
}

function mxmChatOnServerEdit(msg) {
  if (!msg) return;
  if (state.view !== 'messages') return;
  const c = _mxmChatMessagesCache.find((m) => m.id === msg.id);
  if (c) Object.assign(c, msg);
  const merged = _mxmChatMessagesCache.find((m) => m.id === msg.id) || msg;
  mxmChatEnsureReplyPreview(merged);
  mxmChatUpdateMsgDom(merged);
  mxmChatRefreshReadTicks();
  mxmChatRefreshSidebar();
}

function mxmChatOnServerDelete(msgId) {
  if (!msgId) return;
  if (state.view !== 'messages') return;
  _mxmChatMessagesCache = _mxmChatMessagesCache.filter((m) => m.id !== msgId);
  mxmChatRenderThreadFromCache({ preserveScroll: true });
  mxmChatRefreshSidebar();
}

function mxmChatUpdateMsgDom(msg) {
  const row = document.querySelector(`[data-msg-id="${msg.id}"]`);
  if (!row) return;
  const full = _mxmChatMessagesCache.find((m) => m.id === msg.id) || msg;
  mxmChatEnsureReplyPreview(full);
  row.innerHTML = mxmChatBuildBubbleHtml(full);
  const visible = _mxmChatMessagesCache.filter((m) => !m.deleted);
  const idx = visible.findIndex((m) => String(m.id) === String(full.id));
  const prev = idx > 0 ? visible[idx - 1] : null;
  const next = idx >= 0 && idx < visible.length - 1 ? visible[idx + 1] : null;
  mxmChatApplyMessageGroupClasses(row, full, prev, next);
  mxmChatHydrateAttachments(row);
}

function mxmChatAnimateRemoveMsg(msgId) {
  const row = document.querySelector(`[data-msg-id="${msgId}"]`);
  if (!row) return;
  row.style.transition = 'opacity .25s, transform .25s, max-height .3s .05s';
  row.style.overflow = 'hidden';
  row.style.maxHeight = row.offsetHeight + 'px';
  requestAnimationFrame(() => {
    row.style.opacity = '0';
    row.style.transform = 'scale(.95)';
    row.style.maxHeight = '0';
    row.style.marginBottom = '0';
    row.style.paddingTop = '0';
    row.style.paddingBottom = '0';
  });
  setTimeout(() => row.remove(), 350);
}

function mxmChatOnTypingRemote(d) {
  if (String(d.fromUserId) !== String(_mxmChatActivePeerId)) return;
  const el = $('mxm-chat-typing');
  _mxmChatPeerTyping = !!d.active;
  if (el) el.classList.add('hidden');
  clearTimeout(mxmChatOnTypingRemote._t);
  if (_mxmChatPeerTyping) {
    mxmChatRenderThreadPresence(_mxmChatActivePeerId);
    mxmChatOnTypingRemote._t = setTimeout(() => {
      _mxmChatPeerTyping = false;
      mxmChatRenderThreadPresence(_mxmChatActivePeerId);
    }, 2800);
    return;
  }
  mxmChatRenderThreadPresence(_mxmChatActivePeerId);
}

/** Подпись к вложению в payload (caption и запасные ключи). */
function mxmChatPayloadCaptionForMedia(dec) {
  if (!dec || typeof dec !== 'object') return '';
  const tryKeys = ['caption', 'messageCaption', 'textCaption', 'comment'];
  for (const k of tryKeys) {
    if (!Object.prototype.hasOwnProperty.call(dec, k)) continue;
    const v = dec[k];
    if (v == null || v === '') continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return '';
}

/* ── Sidebar ───────────────────────────────────────────────────────────────── */
async function mxmChatRefreshSidebar() {
  const list = $('mxm-chat-peer-list');
  if (!list || !state.user) return;
  try {
    const { conversations } = await GET('/api/chat/conversations');
    _mxmChatConversations = conversations || [];
    mxmChatBindSidebarControls();
    mxmChatRenderSidebar();
    _mxmChatSyncUnreadBadge();
    mxmChatPrefetchConversationStatuses(_mxmChatConversations);
    return;
  } catch (_) {}
}

function mxmChatBindSidebarControls() {
  const input = $('mxm-chat-sidebar-search-input');
  if (input && !input.dataset.mxmBound) {
    input.dataset.mxmBound = '1';
    input.addEventListener('input', () => {
      _mxmChatSidebarQuery = String(input.value || '').trim().toLowerCase();
      mxmChatRenderSidebar();
    });
  }
}

window.mxmChatSetSidebarFilter = function () {
  mxmChatRenderSidebar();
};

window.mxmChatClearSidebarSearch = function () {
  _mxmChatSidebarQuery = '';
  const input = $('mxm-chat-sidebar-search-input');
  if (input) {
    input.value = '';
    input.focus();
  }
  mxmChatRenderSidebar();
};

/** Дата в списке диалогов: сегодня — время, вчера, <7 суток — день нед., иначе дата. */
function mxmChatFormatDialogDate(iso) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const now = new Date();
  if (mxmChatDayStamp(now) === mxmChatDayStamp(d)) {
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (mxmChatDayStamp(yesterday) === mxmChatDayStamp(d)) return 'вчера';
  const start = (t) => {
    const x = new Date(t);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
  };
  const dayDiff = (start(now) - start(d)) / (24 * 60 * 60 * 1000);
  if (dayDiff > 0 && dayDiff < 7) {
    return d.toLocaleDateString('ru-RU', { weekday: 'short' });
  }
  if (now.getFullYear() === d.getFullYear()) {
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  }
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: '2-digit' });
}

function mxmChatConversationPreview(conversation) {
  const payload = conversation?.lastMessage?.payload;
  if (!payload || typeof payload !== 'object') {
    return { iconHtml: '<span class="mxm-chat-peer-kind-dot" aria-hidden="true">·</span>', text: 'Диалог готов к сообщению' };
  }
  const caption = mxmChatPayloadCaptionForMedia(payload).slice(0, 56);
  if (payload.type === 'sticker') {
    return { iconHtml: '<span class="mxm-chat-kind-emoji" aria-hidden="true">🙂</span>', text: 'Стикер' };
  }
  if (payload.type === 'music') {
    return { iconHtml: _MXM_SVG_KNOTE, text: caption || String(payload.title || '').trim() || 'Музыка' };
  }
  if (payload.type === 'combo') {
    return { iconHtml: _MXM_SVG_KNOTE + _MXM_SVG_KCAMERA, text: caption || 'Музыка и фото' };
  }
  if (payload.type === 'image') {
    return { iconHtml: _MXM_SVG_KCAMERA, text: caption || 'Фото' };
  }
  if (payload.type === 'video') {
    return { iconHtml: _MXM_SVG_KPLAY, text: caption || 'Видео' };
  }
  if (payload.type === 'file') {
    return { iconHtml: _MXM_SVG_KFILE, text: caption || String(payload.fileName || '').trim() || 'Файл' };
  }
  if (payload.type === 'voice') {
    return { iconHtml: _MXM_SVG_KMIC, text: 'Голосовое сообщение' };
  }
  return { iconHtml: '', text: String(payload.text || '').trim().slice(0, 64) || 'Сообщение' };
}

function mxmChatRenderSidebar() {
  const list = $('mxm-chat-peer-list');
  if (!list) return;
  const query = _mxmChatSidebarQuery;
  const filtered = _mxmChatConversations.filter((c) => {
    if (!query) return true;
    const preview = mxmChatConversationPreview(c);
    const haystack = [c.peer?.username, c.peer?.login, preview.text]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(query);
  });
  const unreadTotal = _mxmChatConversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  const summary = $('mxm-chat-sidebar-summary');
  const listCount = $('mxm-chat-sidebar-list-count');
  const clearBtn = $('mxm-chat-sidebar-search-clear');
  if (summary) {
    if (_mxmChatConversations.length) {
      const n = _mxmChatConversations.length;
      const chatsWord = n === 1 ? 'чат' : n < 5 ? 'чата' : 'чатов';
      summary.textContent = unreadTotal
        ? `${n} ${chatsWord} · ${unreadTotal} непрочитанных`
        : `${n} ${chatsWord}`;
    } else {
      summary.textContent = 'Нет активных чатов';
    }
  }
  if (listCount) {
    listCount.textContent = String(filtered.length);
    listCount.setAttribute('aria-label', `Показано ${filtered.length}`);
  }
  if (clearBtn) clearBtn.classList.toggle('hidden', !query);
  if (!filtered.length) {
    list.innerHTML = `<li class="mxm-chat-peer-list-empty-item" role="presentation">
    <div class="mxm-chat-peer-list-empty">
      <div class="mxm-chat-peer-list-empty-icon">⌕</div>
      <div class="mxm-chat-peer-list-empty-title">${query ? 'Ничего не найдено' : 'Пока пусто'}</div>
      <p class="mxm-chat-peer-list-empty-text">${query ? 'Попробуйте другой запрос.' : 'Здесь появятся ваши диалоги и новые сообщения.'}</p>
    </div></li>`;
    return;
  }
  const rows = filtered
    .map((c) => {
      const active = String(c.peer.id) === String(_mxmChatActivePeerId);
      const un = (c.unreadCount || 0) > 0;
      const preview = mxmChatConversationPreview(c);
      const peerId = String(c.peer.id);
      const peerOnline = _mxmChatOnlinePeers.has(peerId);
      const rawPeerRef = c.peer.login || c.peer.id;
      const peerRefJs = profileJsId(rawPeerRef);
      const hash = typeof buildHash === 'function' ? buildHash('messages', { id: String(rawPeerRef).trim() }) : 'messages';
      const avHtml = c.peer.avatarUrl
        ? `<img class="mxm-chat-peer-av mxm-chat-peer-av--img" src="${esc(c.peer.avatarUrl)}" alt="" loading="lazy" onerror="this.outerHTML='<span class=\\'mxm-chat-peer-av\\'>${mxmChatPeerInitial(c.peer.username)}</span>'"/>`
        : `<span class="mxm-chat-peer-av">${mxmChatPeerInitial(c.peer.username)}</span>`;
      const onlineDot = peerOnline ? '<span class="online-dot" aria-hidden="true"></span>' : '';
      const handle = c.peer?.login ? `@${esc(String(c.peer.login))}` : '';
      const kindHtml = preview.iconHtml || '';
      const previewLine = `${kindHtml ? `<span class="mxm-chat-peer-kind-inline" aria-hidden="true">${kindHtml}</span>` : ''}<span class="mxm-chat-peer-last">${esc(preview.text)}</span>`;
      return `<li class="mxm-chat-peer-item" role="none" data-peer-id="${esc(peerId)}">
  <a class="mxm-chat-peer-link${active ? ' is-active' : ''}${un ? ' has-unread' : ''}" id="mxm-dialog-${String(c.peer.id)}" role="option" href="#${esc(hash)}" data-peer="${esc(
        c.peer.id
      )}" data-dialog-url="#${esc(hash)}" ${active ? 'aria-selected="true"' : 'aria-selected="false"'} data-mxm-open-peer="1" data-peer-online="${peerOnline ? '1' : '0'}" onclick="event.preventDefault();mxmChatOpenPeer('${peerRefJs}');return false;">
    <div class="mxm-chat-peer-media${peerOnline ? ' is-online' : ''}" data-peer-media="1">
      ${avHtml}
      ${onlineDot}
    </div>
    <div class="mxm-chat-peer-info">
      <div class="mxm-chat-peer-top-row">
        <span class="mxm-chat-peer-name">${esc(c.peer.username)}</span>
        <span class="mxm-chat-peer-time">${mxmChatFormatDialogDate(c.lastMessage?.createdAt)}</span>
      </div>
      ${handle ? `<div class="mxm-chat-peer-handle">${handle}</div>` : ''}
      <div class="mxm-chat-peer-preview-row">
        <span class="mxm-chat-peer-preview-inner">${previewLine}</span>
        ${un ? `<span class="mxm-chat-unread mxm-chat-unread--pill">${c.unreadCount > 99 ? '99+' : c.unreadCount}</span>` : ''}
      </div>
    </div>
  </a>
</li>`;
    })
    .join('');
  list.innerHTML = rows;
}

/** DELETE /api/chat/conversation/:peer — choice: 'self' | 'both' */
async function mxmChatApiDeleteConversation(peerId, choice) {
  if (!state.token || !peerId) return;
  const forBoth = choice === 'both';
  const body = forBoth ? JSON.stringify({ forBoth: true }) : JSON.stringify({});
  const url = resolveClientRequestUrl(`/api/chat/conversation/${encodeURIComponent(peerId)}`);
  const r = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${state.token}` },
    body
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.error || 'Ошибка');
  }
}

function mxmChatCloseAllPeerSwipes() {
  document.querySelectorAll('[data-mxm-swipe-track]').forEach((t) => {
    t.style.transition = 'transform 0.2s ease';
    t.style.transform = 'translate3d(0,0,0)';
    setTimeout(() => { t.style.transition = ''; }, 200);
  });
}

function mxmChatInitPeerListSwipe() {
  const list = $('mxm-chat-peer-list');
  if (!list || list.dataset.mxmPeerSwipe === '1') return;
  list.dataset.mxmPeerSwipe = '1';
  const W = 132;
  const SNAP = 60;
  let st = null;

  list.addEventListener(
    'click',
    (e) => {
      const b = e.target.closest('.mxm-peer-swipe-btn');
      if (!b) return;
      e.stopPropagation();
      e.preventDefault();
      const p = b.getAttribute('data-peer');
      if (!p) return;
      mxmChatCloseAllPeerSwipes();
      if (b.classList.contains('mxm-peer-swipe-archive')) void window.mxmChatArchivePeerFromList(p);
      else void window.mxmChatDeletePeerFromList(p);
    },
    true
  );

  list.addEventListener(
    'pointerdown',
    (e) => {
      if (window.innerWidth > 900) return;
      if (e.target.closest('.mxm-peer-swipe-btn')) return;
      const track = e.target.closest('[data-mxm-swipe-track]');
      if (!track || !list.contains(track)) return;
      document.querySelectorAll('[data-mxm-swipe-track]').forEach((t) => {
        if (t !== track) {
          t.style.transition = 'transform 0.18s ease';
          t.style.transform = 'translate3d(0,0,0)';
        }
      });
      st = { track, x0: e.clientX, y0: e.clientY, pid: e.pointerId, lastTx: 0, moved: false };
      try {
        track.setPointerCapture(e.pointerId);
      } catch (_) {}
    },
    true
  );

  const finish = (e, forceClose) => {
    if (!st || e.pointerId !== st.pid) return;
    const t = st.track;
    const tx = st.lastTx;
    st = null;
    try {
      t.releasePointerCapture(e.pointerId);
    } catch (_) {}
    t.style.transition = 'transform 0.2s ease';
    const open = !forceClose && tx <= -SNAP;
    t.style.transform = `translate3d(${open ? -W : 0}px,0,0)`;
    setTimeout(() => { t.style.transition = ''; }, 200);
  };

  list.addEventListener(
    'pointermove',
    (e) => {
      if (!st || e.pointerId !== st.pid) return;
      const dx = e.clientX - st.x0;
      const dy = e.clientY - st.y0;
      if (!st.moved && Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
        finish(e, true);
        return;
      }
      if (dx > 4) return;
      st.moved = true;
      const tx = Math.max(-W, Math.min(0, dx));
      st.lastTx = tx;
      st.track.style.transition = 'none';
      st.track.style.transform = `translate3d(${tx}px,0,0)`;
    },
    true
  );
  list.addEventListener('pointerup', (e) => finish(e, false), true);
  list.addEventListener('pointercancel', (e) => finish(e, true), true);
}

window.mxmChatFabNew = function () {
  navigate('search');
};

window.mxmChatArchivePeerFromList = async function (peerId) {
  if (!state.token || !peerId) return;
  mxmChatCloseAllPeerSwipes();
  try {
    await mxmChatApiDeleteConversation(peerId, 'self');
    if (String(_mxmChatActivePeerId) === String(peerId)) {
      _mxmChatMessagesCache = [];
      mxmChatResetThreadUi();
    }
    await mxmChatRefreshSidebar();
    toast('Диалог в архиве', 'success');
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
};

window.mxmChatDeletePeerFromList = async function (peerId) {
  if (!state.token || !peerId) return;
  const choice = await mxmChatConfirmDeleteChat();
  if (!choice) return;
  mxmChatCloseAllPeerSwipes();
  try {
    await mxmChatApiDeleteConversation(peerId, choice);
    if (String(_mxmChatActivePeerId) === String(peerId)) {
      _mxmChatMessagesCache = [];
      mxmChatResetThreadUi();
    }
    await mxmChatRefreshSidebar();
    toast(choice === 'both' ? 'Чат удалён у обоих' : 'Чат удалён', 'success');
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
};

/** Клавиатура / visualViewport: отступ снизу и докрутка треда при вводе. */
function mxmChatBindVisualViewport() {
  if (mxmChatBindVisualViewport._on) return;
  mxmChatBindVisualViewport._on = 1;
  const apply = () => {
    if (state.view !== 'messages' || window.innerWidth > 900) {
      try {
        document.documentElement.style.removeProperty('--mxm-chat-kb');
      } catch (_) {}
      return;
    }
    const vv = window.visualViewport;
    const k = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;
    try {
      document.documentElement.style.setProperty('--mxm-chat-kb', `${Math.round(k)}px`);
    } catch (_) {}
    const sc = $('mxm-chat-thread-scroll');
    if (sc && (document.activeElement && document.activeElement.id === 'mxm-chat-input')) {
      if (mxmChatThreadNearBottom(sc, 220)) {
        const m = () => { sc.scrollTop = Math.max(0, sc.scrollHeight - sc.clientHeight); };
        requestAnimationFrame(m);
        requestAnimationFrame(m);
      }
    }
  };
  window.addEventListener('resize', apply, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', apply, { passive: true });
    window.visualViewport.addEventListener('scroll', apply, { passive: true });
  }
  apply();
}

async function mxmChatUpdateUnreadBadge() {
  try {
    const { conversations } = await GET('/api/chat/conversations');
    const total = (conversations || []).reduce((s, c) => s + (c.unreadCount || 0), 0);
    _mxmChatSetBadge(total);
  } catch (_) {}
}

function _mxmChatUnreadLabelRu(n) {
  const c = Math.min(999, Math.max(0, n | 0));
  if (c === 1) return '1 непрочитанное сообщение';
  const mod10 = c % 10;
  const mod100 = c % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${c} непрочитанных сообщения`;
  return `${c} непрочитанных сообщений`;
}

function _mxmChatSetBadge(count) {
  const label = _mxmChatUnreadLabelRu(count);
  const navItem = document.querySelector('.mobile-nav-item[data-view="messages"]');
  if (navItem) {
    let badge = navItem.querySelector('.mxm-nav-badge');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'mxm-nav-badge mxm-nav-badge--messages';
        navItem.appendChild(badge);
      }
      badge.classList.add('mxm-nav-badge--messages');
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.setAttribute('aria-label', label);
    } else if (badge) {
      badge.remove();
    }
    if (count > 0) navItem.setAttribute('aria-label', `Чат, ${label}`);
    else navItem.removeAttribute('aria-label');
  }
  const sidebarItem = document.querySelector('.nav-item[data-view="messages"]');
  if (sidebarItem) {
    let badge = sidebarItem.querySelector('.mxm-nav-badge');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'mxm-nav-badge mxm-nav-badge--messages';
        sidebarItem.appendChild(badge);
      }
      badge.classList.add('mxm-nav-badge--messages');
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.setAttribute('aria-label', label);
    } else if (badge) {
      badge.remove();
    }
    if (count > 0) sidebarItem.setAttribute('aria-label', `Сообщения, ${label}`);
    else sidebarItem.removeAttribute('aria-label');
  }
}

function mxmChatPeerInitial(name) {
  return String(name || '?').trim().slice(0, 1).toUpperCase();
}

/* ── Message rendering ─────────────────────────────────────────────────────── */
function mxmChatBuildMessageRow(msg) {
  if (msg.messageKind === 'sticker' && !_mxmChatStickerManifest) {
    try { fetch('/images/stickers/manifest.json').then(r => r.json()).then(j => { _mxmChatStickerManifest = j; }); } catch (_) {}
  }
  const me = state.user?.id;
  const mine = msg.senderId === me;
  const row = document.createElement('div');
  row.className = `mxm-chat-msg message${mine ? ' own mxm-chat-msg--mine' : ' peer'}`;
  row.setAttribute('role', 'listitem');
  row.setAttribute('data-id', String(msg.id));
  row.setAttribute('data-sender', String(msg.senderId || ''));
  row.dataset.msgId = msg.id;
  row.dataset.senderId = msg.senderId || '';
  row.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    mxmChatShowCtx(e, msg.id, row);
  });
  row.innerHTML = mxmChatBuildBubbleHtml(msg);
  mxmChatHydrateAttachments(row);
  mxmChatBindDoubleTapReaction(row, msg.id);
  if (_mxmChatIsMobile()) {
    mxmChatBindMsgSwipeReply(row, msg.id);
    mxmChatBindLongPress(row, msg.id);
  }
  return row;
}

function mxmChatDayStamp(input) {
  const d = input instanceof Date ? input : new Date(input);
  if (!Number.isFinite(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function mxmChatFormatDayDivider(iso) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const now = new Date();
  if (mxmChatDayStamp(d) === mxmChatDayStamp(now)) return 'Сегодня';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (mxmChatDayStamp(d) === mxmChatDayStamp(yesterday)) return 'Вчера';
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    ...(now.getFullYear() === d.getFullYear() ? {} : { year: 'numeric' })
  });
}

function mxmChatShouldShowDayDivider(prevMsg, msg) {
  if (!msg) return false;
  if (!prevMsg) return true;
  return mxmChatDayStamp(prevMsg.createdAt) !== mxmChatDayStamp(msg.createdAt);
}

function mxmChatCanGroupMessages(a, b) {
  if (!a || !b || a.deleted || b.deleted) return false;
  if (String(a.senderId || '') !== String(b.senderId || '')) return false;
  if (mxmChatDayStamp(a.createdAt) !== mxmChatDayStamp(b.createdAt)) return false;
  const aTime = new Date(a.createdAt).getTime();
  const bTime = new Date(b.createdAt).getTime();
  if (!Number.isFinite(aTime) || !Number.isFinite(bTime)) return false;
  return Math.abs(bTime - aTime) <= 8 * 60 * 1000;
}

function mxmChatCreateDayDivider(iso) {
  const divider = document.createElement('div');
  divider.className = 'mxm-chat-day-divider';
  divider.setAttribute('role', 'none');
  divider.setAttribute('aria-hidden', 'true');
  divider.innerHTML = `<span class="mxm-chat-day-chip">${esc(mxmChatFormatDayDivider(iso))}</span>`;
  return divider;
}

function mxmChatCreateUnreadSeparator() {
  const d = document.createElement('div');
  d.className = 'mxm-chat-unread-separator';
  d.setAttribute('role', 'listitem');
  d.setAttribute('aria-label', 'Новые сообщения');
  d.innerHTML = '<div class="mxm-chat-unread-separator-line"><span>Новые сообщения</span></div>';
  return d;
}

function mxmChatApplyMessageGroupClasses(row, msg, prevMsg, nextMsg) {
  if (!row) return;
  row.classList.toggle('mxm-chat-msg--grouped-top', mxmChatCanGroupMessages(prevMsg, msg));
  row.classList.toggle('mxm-chat-msg--grouped-bottom', mxmChatCanGroupMessages(msg, nextMsg));
  row.classList.toggle('mxm-chat-msg--solo', !mxmChatCanGroupMessages(prevMsg, msg) && !mxmChatCanGroupMessages(msg, nextMsg));
}

/** Первая строка сообщения, заходящая в видимую область (для сохранения позиции при удалении). */
function mxmChatCaptureThreadScrollAnchor(scroll) {
  if (!scroll) return null;
  const st = scroll.scrollTop;
  const edge = 6;
  const rows = scroll.querySelectorAll('.mxm-chat-msg[data-msg-id]');
  for (const row of rows) {
    const top = row.offsetTop;
    const bot = top + row.offsetHeight;
    if (bot > st + edge) {
      const id = row.getAttribute('data-msg-id');
      if (id) return { id, y: top - st };
    }
  }
  return null;
}

function mxmChatRenderThreadFromCache(options = {}) {
  const scroll = options.scroll || $('mxm-chat-thread-scroll');
  if (!scroll) return;
  const keepBottom = options.keepBottom === true;
  const preserveScroll = options.preserveScroll === true;

  let stickToBottom = false;
  let anchor = null;
  let prevScrollTop = 0;
  if (preserveScroll) {
    stickToBottom = mxmChatThreadNearBottom(scroll, _MXM_CHAT_NEAR_BOTTOM_PX);
    anchor = stickToBottom ? null : mxmChatCaptureThreadScrollAnchor(scroll);
    prevScrollTop = scroll.scrollTop;
  }

  const messages = _mxmChatMessagesCache.filter((m) => !m.deleted);
  const me = state.user?.id;
  const myReadT =
    _mxmChatOpenHadUnread && _mxmChatThreadMyLastReadAt
      ? new Date(_mxmChatThreadMyLastReadAt).getTime()
      : 0;
  let placedUnreadSeparator = false;
  scroll.innerHTML = '';
  const frag = document.createDocumentFragment();
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const prev = i > 0 ? messages[i - 1] : null;
    const next = i < messages.length - 1 ? messages[i + 1] : null;
    if (mxmChatShouldShowDayDivider(prev, msg)) frag.appendChild(mxmChatCreateDayDivider(msg.createdAt));
    if (
      !placedUnreadSeparator &&
      myReadT > 0 &&
      msg.senderId !== me &&
      new Date(msg.createdAt).getTime() > myReadT
    ) {
      frag.appendChild(mxmChatCreateUnreadSeparator());
      placedUnreadSeparator = true;
    }
    const row = mxmChatBuildMessageRow(msg);
    mxmChatApplyMessageGroupClasses(row, msg, prev, next);
    frag.appendChild(row);
  }
  scroll.appendChild(frag);

  const applyPreservedScroll = () => {
    const maxThread = () => Math.max(0, scroll.scrollHeight - scroll.clientHeight);
    if (keepBottom) {
      scroll.scrollTop = maxThread();
      mxmChatLockShellScroll();
    } else if (preserveScroll) {
      if (stickToBottom) {
        scroll.scrollTop = maxThread();
        mxmChatLockShellScroll();
      } else if (anchor) {
        const row = scroll.querySelector(`[data-msg-id="${anchor.id}"]`);
        if (row) scroll.scrollTop = Math.max(0, row.offsetTop - anchor.y);
        else {
          const maxS = Math.max(0, scroll.scrollHeight - scroll.clientHeight);
          scroll.scrollTop = Math.min(prevScrollTop, maxS);
        }
      } else {
        const maxS = Math.max(0, scroll.scrollHeight - scroll.clientHeight);
        scroll.scrollTop = Math.min(prevScrollTop, maxS);
      }
    }
    mxmChatUpdateScrollDownBtn();
  };

  applyPreservedScroll();
  if (preserveScroll) {
    requestAnimationFrame(() => applyPreservedScroll());
  }
  mxmChatSyncComposerMetrics();
}

function mxmChatBindDoubleTapReaction(row, msgId) {
  let lastTap = 0;
  row.addEventListener('dblclick', (e) => {
    if (e.target.closest('.mxm-chat-reaction-badge, .mxm-reaction-picker-btn, .mxm-chat-ctx-emoji-btn, .mxm-ctx-item, a, button[onclick]')) return;
    e.preventDefault();
    mxmChatToggleReaction(msgId, '❤️');
  });
  if (_mxmChatIsMobile()) {
    row.addEventListener('touchend', (e) => {
      if (e.target.closest('.mxm-chat-reaction-badge, .mxm-reaction-picker-btn, .mxm-chat-ctx-emoji-btn, .mxm-ctx-item, a, button[onclick]')) return;
      const now = Date.now();
      if (now - lastTap < 300) {
        e.preventDefault();
        mxmChatToggleReaction(msgId, '❤️');
        lastTap = 0;
      } else {
        lastTap = now;
      }
    }, { passive: false });
  }
}

function mxmChatAppendMessageRow(msg, skipScroll) {
  const wrap = $('mxm-chat-thread-scroll');
  if (!wrap) return;
  mxmChatEnsureReplyPreview(msg);
  const stickToBottom = mxmChatThreadNearBottom(wrap, _MXM_CHAT_NEAR_BOTTOM_PX);
  const mine = msg.senderId === state.user?.id;
  const visible = _mxmChatMessagesCache.filter((m) => !m.deleted);
  const idx = visible.findIndex((m) => String(m.id) === String(msg.id));
  const prev = idx > 0 ? visible[idx - 1] : null;
  if (mxmChatShouldShowDayDivider(prev, msg)) wrap.appendChild(mxmChatCreateDayDivider(msg.createdAt));
  if (prev) {
    const prevRow = wrap.querySelector(`[data-msg-id="${prev.id}"]`);
    const prevPrev = idx > 1 ? visible[idx - 2] : null;
    mxmChatApplyMessageGroupClasses(prevRow, prev, prevPrev, msg);
  }
  const row = mxmChatBuildMessageRow(msg);
  mxmChatApplyMessageGroupClasses(row, msg, prev, null);
  wrap.appendChild(row);
  if (!skipScroll && (stickToBottom || mine)) {
    mxmChatMaybeStickBottom(wrap, true);
  }
  mxmChatUpdateScrollDownBtn();
  if (mine) mxmChatRefreshReadTicks();
}

/** 1×1 transparent GIF — плейсхолдер до загрузки вложения (в src не попадает JWT). */
const _MXM_CHAT_MEDIA_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const _MXM_CHAT_IMG_MAX_W = 240;
const _MXM_CHAT_IMG_MAX_H = 240;
const _MXM_CHAT_VID_MAX_W = 280;
const _MXM_CHAT_VID_MAX_H = 200;

function mxmChatMediaBoxSize(dec, kind) {
  const maxW = kind === 'video' ? _MXM_CHAT_VID_MAX_W : _MXM_CHAT_IMG_MAX_W;
  const maxH = kind === 'video' ? _MXM_CHAT_VID_MAX_H : _MXM_CHAT_IMG_MAX_H;
  const w0 = Number(dec?.width) || 0;
  const h0 = Number(dec?.height) || 0;
  if (w0 > 0 && h0 > 0) {
    const scale = Math.min(maxW / w0, maxH / h0, 1);
    return {
      w: Math.max(56, Math.round(w0 * scale)),
      h: Math.max(56, Math.round(h0 * scale))
    };
  }
  if (kind === 'video') {
    return { w: maxW, h: Math.round((maxW * 9) / 16) };
  }
  return { w: maxW, h: Math.round(maxW * 0.75) };
}

function mxmChatWrapMediaElement(innerHtml, dec, kind, pending) {
  const { w, h } = mxmChatMediaBoxSize(dec, kind);
  const pendCls = pending ? ' mxm-chat-media-shell--pending' : '';
  return `<span class="mxm-chat-media-shell${pendCls}" style="width:${w}px;height:${h}px"${pending ? ' aria-busy="true"' : ''}><span class="mxm-chat-media-skeleton" aria-hidden="true"></span>${innerHtml}</span>`;
}

function mxmChatMediaShellSettle(el, readyEvent) {
  const shell = el?.closest?.('.mxm-chat-media-shell');
  const done = () => {
    if (shell) {
      shell.classList.add('is-loaded');
      shell.classList.remove('mxm-chat-media-shell--pending');
      shell.removeAttribute('aria-busy');
    }
    delete el?.dataset?.mxmChatMediaPending;
    const scroll = $('mxm-chat-thread-scroll');
    if (scroll && el && scroll.contains(el)) {
      requestAnimationFrame(() => mxmChatMaybeStickBottom(scroll));
    }
  };
  if (el.tagName === 'IMG' && el.complete && el.naturalWidth > 0) {
    done();
    return;
  }
  if (el.tagName === 'VIDEO' && el.readyState >= 1) {
    done();
    return;
  }
  el.addEventListener(readyEvent, done, { once: true });
  el.addEventListener('error', done, { once: true });
}

function mxmChatInitMediaShells(root) {
  if (!root) return;
  root.querySelectorAll('.mxm-chat-media-shell').forEach((shell) => {
    if (shell.dataset.mxmMediaShellInit === '1') return;
    shell.dataset.mxmMediaShellInit = '1';
    const img = shell.querySelector('img.mxm-chat-media-img');
    const video = shell.querySelector('video.mxm-chat-media-video');
    if (img) mxmChatMediaShellSettle(img, 'load');
    if (video) mxmChatMediaShellSettle(video, 'loadedmetadata');
  });
}

window.mxmChatLightboxImg = function (imgEl) {
  const src = imgEl?.src || '';
  if (!src || src === _MXM_CHAT_MEDIA_PLACEHOLDER) return;
  const shell = imgEl.closest('.mxm-chat-media-shell');
  if (shell && !shell.classList.contains('is-loaded')) return;
  mxmChatLightbox(src);
};

async function mxmChatFetchAttachmentBlobUrl(messageId) {
  if (!messageId || !state.token) return null;
  const r = await fetch(resolveClientRequestUrl(`/api/chat/attachments/${encodeURIComponent(messageId)}`), {
    headers: { Authorization: `Bearer ${state.token}` },
    credentials: 'include',
    cache: 'no-store'
  });
  if (!r.ok) return null;
  return URL.createObjectURL(await r.blob());
}

function mxmChatHydrateAttachments(root) {
  if (!root) return;
  if (state.token) {
  root.querySelectorAll('img.mxm-chat-media-img[data-mxm-chat-attachment]').forEach((el) => {
    if (el.dataset.mxmChatHydrated) return;
    el.dataset.mxmChatHydrated = '1';
    el.dataset.mxmChatMediaPending = '1';
    mxmChatMediaShellSettle(el, 'load');
    const mid = el.getAttribute('data-mxm-chat-attachment');
    mxmChatFetchAttachmentBlobUrl(mid)
      .then((u) => {
        if (u) el.src = u;
        else {
          const shell = el.closest('.mxm-chat-media-shell');
          if (shell) shell.classList.add('is-loaded');
        }
      })
      .catch(() => {
        const shell = el.closest('.mxm-chat-media-shell');
        if (shell) shell.classList.add('is-loaded');
      });
  });
  root.querySelectorAll('video.mxm-chat-media-video[data-mxm-chat-attachment]').forEach((el) => {
    if (el.dataset.mxmChatHydrated) return;
    el.dataset.mxmChatHydrated = '1';
    el.dataset.mxmChatMediaPending = '1';
    mxmChatMediaShellSettle(el, 'loadedmetadata');
    const mid = el.getAttribute('data-mxm-chat-attachment');
    mxmChatFetchAttachmentBlobUrl(mid)
      .then((u) => {
        if (u) el.src = u;
        else {
          const shell = el.closest('.mxm-chat-media-shell');
          if (shell) shell.classList.add('is-loaded');
        }
      })
      .catch(() => {
        const shell = el.closest('.mxm-chat-media-shell');
        if (shell) shell.classList.add('is-loaded');
      });
  });
  }
  mxmChatInitMediaShells(root);
}

/** Если пришёл только replyToId (или assign затёр preview), восстанавливаем replyTo из кэша ленты. */
function mxmChatEnsureReplyPreview(msg) {
  if (!msg || msg.replyTo || !msg.replyToId) return msg;
  const prev = _mxmChatMessagesCache.find((m) => String(m.id) === String(msg.replyToId));
  if (!prev) return msg;
  msg.replyTo = {
    id: prev.id,
    senderId: prev.senderId,
    payload: prev.payload,
    deleted: !!prev.deleted
  };
  return msg;
}

function mxmChatBuildBubbleHtml(msg) {
  const me = state.user?.id;
  const mine = msg.senderId === me;
  if (msg.deleted) return '';
  let body = '';
  if (msg.payload) {
    body = mxmChatFormatPayload(msg.payload, msg.messageKind, msg.id);
  } else {
    body = '<span class="mxm-chat-bubble-err">Не удалось отобразить</span>';
  }
  let replyHtml = '';
  if (msg.replyTo) {
    const rp = msg.replyTo;
    const rpText = rp.deleted ? 'Удалено' : mxmChatPayloadPreviewLine(rp.payload);
    const rpAuthor = rp.senderId === me ? 'Вы' : (mxmChatFindPeerName(rp.senderId) || '');
    replyHtml = `<div class="mxm-chat-reply-preview" onclick="mxmChatScrollToMsg('${esc(rp.id)}')">
      <span class="mxm-chat-reply-author">${esc(rpAuthor)}</span>
      <span class="mxm-chat-reply-text">${esc(String(rpText).slice(0, 60))}</span></div>`;
  }
  const editedMark = msg.edited ? '<span class="mxm-chat-edited">ред.</span>' : '';
  const readHtml = mine ? mxmChatReadStatusHtml(msg) : '';
  const skin = mine ? 'own' : 'peer';
  const bubbleMod =
    msg.messageKind === 'music'
      ? ' mxm-chat-bubble--track'
      : msg.messageKind === 'combo'
        ? ' mxm-chat-bubble--track mxm-chat-bubble--combo'
        : '';
  const reactionsHtml = mxmChatBuildReactionsHtml(msg);
  return `<div class="mxm-chat-bubble ${skin}${bubbleMod}">${replyHtml}<div class="mxm-chat-bubble-body">${body}</div>
    <div class="mxm-chat-bubble-footer"><div class="mxm-chat-bubble-time-row">${editedMark}<span class="mxm-chat-time">${mxmChatFormatTime(
    msg.createdAt
  )}</span>${readHtml}</div></div>${reactionsHtml}</div>`;
}

function mxmChatReadStatusHtml(msg) {
  if (!_mxmChatPeerLastReadAt) return '<span class="mxm-chat-read-status" title="Отправлено">&#10003;</span>';
  const msgTime = new Date(msg.createdAt).getTime();
  const readTime = _mxmChatPeerLastReadAt.getTime();
  if (msgTime <= readTime) return '<span class="mxm-chat-read-status is-read" title="Прочитано">&#10003;&#10003;</span>';
  return '<span class="mxm-chat-read-status" title="Доставлено">&#10003;</span>';
}

function mxmChatRefreshReadTicks() {
  const me = state.user?.id;
  if (!me) return;
  document.querySelectorAll('.mxm-chat-msg--mine .mxm-chat-read-status').forEach((el) => {
    const row = el.closest('[data-msg-id]');
    if (!row) return;
    const msg = _mxmChatMessagesCache.find((m) => m.id === row.dataset.msgId);
    if (!msg) return;
    const msgTime = new Date(msg.createdAt).getTime();
    const readTime = _mxmChatPeerLastReadAt ? _mxmChatPeerLastReadAt.getTime() : 0;
    if (msgTime <= readTime) {
      el.innerHTML = '&#10003;&#10003;';
      el.classList.add('is-read');
      el.title = 'Прочитано';
    }
  });
}

function mxmChatBuildReactionsHtml(msg) {
  if (!msg.reactions || !msg.reactions.length) return '';
  const me = state.user?.id;
  return '<div class="mxm-chat-reactions reactions">' + msg.reactions.map((r) => {
    const isMine = r.userIds && r.userIds.includes(me);
    return `<button type="button" class="mxm-chat-reaction-badge${isMine ? ' is-mine' : ''}" data-emoji="${esc(r.emoji)}" data-msg-id="${esc(msg.id)}" onclick="mxmChatToggleReaction('${esc(msg.id)}','${esc(r.emoji)}')"><span class="mxm-reaction-emoji">${r.emoji}</span><span class="mxm-reaction-count">${r.count}</span></button>`;
  }).join('') + '</div>';
}

window.mxmChatToggleReaction = async function (msgId, emoji) {
  if (!state.token || !msgId || !emoji) return;
  try {
    const res = await fetch(resolveClientRequestUrl(`/api/chat/messages/${encodeURIComponent(msgId)}/react`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${state.token}` },
      body: JSON.stringify({ emoji })
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); toast(d.error || 'Ошибка', 'error'); }
  } catch (e) { toast('Не удалось поставить реакцию', 'error'); }
};

function mxmChatUpdateReactionsForMsg(msgId, reactions) {
  const msg = _mxmChatMessagesCache.find((m) => m.id === msgId);
  if (msg) msg.reactions = reactions;
  const row = document.querySelector(`[data-msg-id="${msgId}"]`);
  if (!row) return;
  const bubble = row.querySelector('.mxm-chat-bubble');
  if (!bubble) return;
  let container = bubble.querySelector('.mxm-chat-reactions');
  if (!reactions || !reactions.length) {
    if (container) container.remove();
    return;
  }
  const me = state.user?.id;
  const html = reactions.map((r) => {
    const isMine = r.userIds && r.userIds.includes(me);
    return `<button type="button" class="mxm-chat-reaction-badge mxm-reaction-anim${isMine ? ' is-mine' : ''}" data-emoji="${esc(r.emoji)}" data-msg-id="${esc(msgId)}" onclick="mxmChatToggleReaction('${esc(msgId)}','${esc(r.emoji)}')"><span class="mxm-reaction-emoji">${r.emoji}</span><span class="mxm-reaction-count">${r.count}</span></button>`;
  }).join('');
  if (container) {
    container.innerHTML = html;
  } else {
    container = document.createElement('div');
    container.className = 'mxm-chat-reactions reactions';
    container.innerHTML = html;
    bubble.appendChild(container);
  }
}

function mxmChatShowReactionPicker(msgId, anchorEl) {
  mxmChatHideReactionPicker();
  const msg = _mxmChatMessagesCache.find((m) => m.id === msgId);
  if (!msg || msg.deleted) return;
  const row = anchorEl.closest('.mxm-chat-msg');
  if (!row) return;
  const picker = document.createElement('div');
  picker.className = 'mxm-reaction-picker';
  picker.id = 'mxm-reaction-picker';
  picker.innerHTML = ['❤️', '🥚'].map((em) =>
    `<button type="button" class="mxm-chat-ctx-emoji-btn mxm-reaction-picker-btn" onclick="mxmChatPickReaction('${esc(msgId)}','${em}')">${em}</button>`
  ).join('');
  const bubble = row.querySelector('.mxm-chat-bubble');
  if (bubble) {
    bubble.style.position = 'relative';
    picker.style.bottom = '100%';
    picker.style.marginBottom = '4px';
    bubble.appendChild(picker);
  }
  setTimeout(() => document.addEventListener('click', mxmChatHideReactionPicker, { once: true }), 10);
}

function mxmChatHideReactionPicker() {
  const p = document.getElementById('mxm-reaction-picker');
  if (p) p.remove();
}

window.mxmChatPickReaction = function (msgId, emoji) {
  mxmChatHideReactionPicker();
  mxmChatHideCtx();
  mxmChatToggleReaction(msgId, emoji);
};

function mxmChatFindPeerName(uid) {
  const c = _mxmChatConversations.find((x) => String(x.peer.id) === String(uid));
  return c ? c.peer.username : '';
}

function mxmChatCaptionBlock(dec) {
  const cap = mxmChatPayloadCaptionForMedia(dec);
  if (!cap) return '';
  return `<div class="mxm-chat-bubble-caption">${esc(cap)}</div>`;
}

/** Строка для превью ответа / панели «Ответ на…» */
function mxmChatPayloadPreviewLine(p) {
  if (!p || typeof p !== 'object') return 'Сообщение';
  if (p.type === 'sticker') return 'Стикер';
  if (p.type === 'combo') return mxmChatPayloadCaptionForMedia(p) || 'Музыка и фото';
  if (p.type === 'music') {
    const cap = mxmChatPayloadCaptionForMedia(p);
    if (cap) return cap.slice(0, 80);
    return String(p.title || '').trim() || 'Музыка';
  }
  if (p.type === 'image' || p.type === 'video') {
    const cap = mxmChatPayloadCaptionForMedia(p);
    if (cap) return cap.slice(0, 80);
    return p.type === 'image' ? 'Фото' : 'Видео';
  }
  if (p.type === 'file') {
    const cap = mxmChatPayloadCaptionForMedia(p);
    if (cap) return cap.slice(0, 80);
    return String(p.fileName || '').trim() || 'Файл';
  }
  if (p.type === 'voice') return 'Голосовое';
  return String(p.text || p.title || p.fileName || '').trim() || 'Сообщение';
}

/** Карточка одного трека (music или элемент combo.tracks) */
function mxmChatFormatMusicTrackCard(dec) {
  const tid = String(dec.shareId || '');
  const lab = esc(dec.title || 'Музыка');
  const artist = esc(dec.artist || '');
  const cover = dec.coverUrl || '';
  const safeId = tid.replace(/'/g, "\\'");
  const coverStyle = cover ? `background-image:url(${esc(cover)})` : '';
  const coverVar = cover ? `--track-cover:url(${esc(cover)})` : '';
  const plainCls = cover ? '' : ' mxm-chat-track-card--plain';
  return `<div class="mxm-chat-track-card${plainCls}" style="${coverVar}"><div class="mxm-chat-track-card-bg" aria-hidden="true"></div><div class="mxm-chat-track-card-inner"><div class="mxm-chat-track-cover" style="${coverStyle}"><button type="button" class="mxm-chat-track-play" onclick="mxmChatPlayTrack('${safeId}')" title="Воспроизвести"><svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></button></div><div class="mxm-chat-track-meta"><div class="mxm-chat-track-title">${lab}</div>${artist ? `<div class="mxm-chat-track-artist">${artist}</div>` : ''}</div><button type="button" class="mxm-chat-track-open" onclick="mxmChatOpenShare('track','${safeId}')" title="Перейти"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg></button></div></div>`;
}

function mxmChatFormatComboMedia(media, messageId) {
  if (!media || !messageId) return '';
  const dec = {
    type: media.type,
    storageKey: media.storageKey,
    fileName: media.fileName,
    fileSize: media.fileSize,
    mimeType: media.mimeType,
    url: media.url
  };
  if (media.type === 'image') {
    if (dec.storageKey) {
      const img = `<img class="mxm-chat-media-img mxm-chat-combo-media" src="${_MXM_CHAT_MEDIA_PLACEHOLDER}" alt="" loading="lazy" data-mxm-chat-attachment="${esc(messageId)}" onclick="mxmChatLightboxImg(this)"/>`;
      return mxmChatWrapMediaElement(img, dec, 'image', true);
    }
    const img = `<img class="mxm-chat-media-img mxm-chat-combo-media" src="${esc(dec.url || '')}" alt="" loading="lazy" onclick="mxmChatLightboxImg(this)"/>`;
    return mxmChatWrapMediaElement(img, dec, 'image', false);
  }
  if (media.type === 'video') {
    if (dec.storageKey) {
      const vid = `<video class="mxm-chat-media-video mxm-chat-combo-media" data-mxm-chat-attachment="${esc(messageId)}" controls preload="metadata"></video>`;
      return mxmChatWrapMediaElement(vid, dec, 'video', true);
    }
    const vid = `<video class="mxm-chat-media-video mxm-chat-combo-media" src="${esc(dec.url || '')}" controls preload="metadata"></video>`;
    return mxmChatWrapMediaElement(vid, dec, 'video', false);
  }
  const name = esc(dec.fileName || 'Файл');
  const size = dec.fileSize ? mxmChatFormatFileSize(dec.fileSize) : '';
  const dlName = profileJsId(dec.fileName || 'file');
  const midJs = profileJsId(messageId);
  if (dec.storageKey) {
    return `<a class="mxm-chat-file-card mxm-chat-combo-media" href="#" role="button" onclick="return mxmChatFileDownloadClick(event,'${midJs}','${dlName}')">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
      <div class="mxm-chat-file-info"><span class="mxm-chat-file-name">${name}</span>${size ? `<span class="mxm-chat-file-size">${size}</span>` : ''}</div></a>`;
  }
  return `<a class="mxm-chat-file-card mxm-chat-combo-media" href="${esc(dec.url || '')}" download="${name}" target="_blank" rel="noopener">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
      <div class="mxm-chat-file-info"><span class="mxm-chat-file-name">${name}</span>${size ? `<span class="mxm-chat-file-size">${size}</span>` : ''}</div></a>`;
}

function mxmChatFormatPayload(dec, kind, messageId) {
  if (!dec || typeof dec !== 'object') return esc(String(dec));
  if (dec.type === 'combo') {
    const tracks = Array.isArray(dec.tracks) ? dec.tracks : [];
    let html = `<div class="mxm-chat-combo-stack">${mxmChatCaptionBlock(dec)}`;
    for (const tr of tracks) {
      html += mxmChatFormatMusicTrackCard(tr);
    }
    if (dec.media) html += mxmChatFormatComboMedia(dec.media, messageId);
    html += '</div>';
    return html;
  }
  if (dec.type === 'sticker' && dec.id) {
    const id = String(dec.id).replace(/[^a-z0-9_-]/gi, '');
    const fromM = (_mxmChatStickerManifest?.stickers) || [];
    const found = fromM.find((x) => x && String(x.id) === id);
    if (found?.char) return `<span class="mxm-chat-sticker-fallback">${found.char}</span>`;
    return `<img class="mxm-chat-sticker-img" src="/images/stickers/${esc(id)}.png" alt="" loading="lazy" onerror="this.replaceWith(Object.assign(document.createElement('span'),{className:'mxm-chat-sticker-fallback',textContent:'sticker'}))"/>`;
  }
  if (dec.type === 'music') {
    return mxmChatCaptionBlock(dec) + mxmChatFormatMusicTrackCard(dec);
  }
  if (dec.type === 'image') {
    if (dec.storageKey && messageId) {
      const img = `<img class="mxm-chat-media-img" src="${_MXM_CHAT_MEDIA_PLACEHOLDER}" alt="" loading="lazy" data-mxm-chat-attachment="${esc(messageId)}" onclick="mxmChatLightboxImg(this)"/>`;
      return `${mxmChatCaptionBlock(dec)}${mxmChatWrapMediaElement(img, dec, 'image', true)}`;
    }
    const img = `<img class="mxm-chat-media-img" src="${esc(dec.url || '')}" alt="" loading="lazy" onclick="mxmChatLightboxImg(this)"/>`;
    return `${mxmChatCaptionBlock(dec)}${mxmChatWrapMediaElement(img, dec, 'image', false)}`;
  }
  if (dec.type === 'video') {
    if (dec.storageKey && messageId) {
      const vid = `<video class="mxm-chat-media-video" data-mxm-chat-attachment="${esc(messageId)}" controls preload="metadata"></video>`;
      return `${mxmChatCaptionBlock(dec)}${mxmChatWrapMediaElement(vid, dec, 'video', true)}`;
    }
    const vid = `<video class="mxm-chat-media-video" src="${esc(dec.url || '')}" controls preload="metadata"></video>`;
    return `${mxmChatCaptionBlock(dec)}${mxmChatWrapMediaElement(vid, dec, 'video', false)}`;
  }
  if (dec.type === 'file') {
    const name = esc(dec.fileName || 'Файл');
    const size = dec.fileSize ? mxmChatFormatFileSize(dec.fileSize) : '';
    const dlName = profileJsId(dec.fileName || 'file');
    if (dec.storageKey && messageId) {
      const midJs = profileJsId(messageId);
      return `${mxmChatCaptionBlock(dec)}<a class="mxm-chat-file-card" href="#" role="button" onclick="return mxmChatFileDownloadClick(event,'${midJs}','${dlName}')">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
      <div class="mxm-chat-file-info"><span class="mxm-chat-file-name">${name}</span>${size ? `<span class="mxm-chat-file-size">${size}</span>` : ''}</div></a>`;
    }
    return `${mxmChatCaptionBlock(dec)}<a class="mxm-chat-file-card" href="${esc(dec.url || '')}" download="${name}" target="_blank" rel="noopener">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
      <div class="mxm-chat-file-info"><span class="mxm-chat-file-name">${name}</span>${size ? `<span class="mxm-chat-file-size">${size}</span>` : ''}</div></a>`;
  }
  if (dec.type === 'voice') {
    const dur = dec.duration ? Math.round(dec.duration) : 0;
    if (dec.storageKey && messageId) {
      return mxmChatBuildVoiceCardHtml({
        duration: dur,
        attachmentId: messageId,
        seedSource: messageId || dec.storageKey || dur
      });
    }
    return mxmChatBuildVoiceCardHtml({
      duration: dur,
      src: dec.url || '',
      seedSource: dec.url || dur
    });
  }
  return esc(dec.text || dec.body || '');
}

function mxmChatFormatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' Б';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' КБ';
  return (bytes / 1048576).toFixed(1) + ' МБ';
}

window.mxmChatLightbox = function (url) {
  let lb = document.getElementById('mxm-chat-lightbox');
  if (lb) lb.remove();
  lb = document.createElement('div');
  lb.id = 'mxm-chat-lightbox';
  lb.className = 'mxm-chat-lightbox';
  lb.innerHTML = `<img src="${url}" alt=""/><button type="button" class="mxm-chat-lightbox-close">&times;</button>`;
  lb.addEventListener('click', (e) => { if (e.target === lb || e.target.classList.contains('mxm-chat-lightbox-close')) lb.remove(); });
  document.body.appendChild(lb);
};

window.mxmChatFileDownloadClick = async function (e, messageId, fileName) {
  e.preventDefault();
  if (!messageId || !state.token) return false;
  try {
    const r = await fetch(resolveClientRequestUrl(`/api/chat/attachments/${encodeURIComponent(messageId)}`), {
      headers: { Authorization: `Bearer ${state.token}` },
      credentials: 'include',
      cache: 'no-store'
    });
    if (!r.ok) {
      toast('Не удалось скачать', 'error');
      return false;
    }
    const blob = await r.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    a.download = fileName || 'file';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 60000);
  } catch {
    toast('Не удалось скачать', 'error');
  }
  return false;
};

window.mxmChatPlayVoiceBtn = async function (btn) {
  const voiceCard = btn?.closest('.mxm-chat-voice-card');
  if (!btn || !voiceCard) return;
  const audio = mxmChatEnsureVoiceAudio();
  const sameVoice = btn === _mxmChatActiveVoiceBtn && !!audio.dataset.voiceUrl;
  if (sameVoice) {
    if (audio.paused) {
      try {
        await audio.play();
      } catch (_) {}
    } else {
      audio.pause();
    }
    return;
  }
  if (_mxmChatActiveVoiceBtn && _mxmChatActiveVoiceBtn !== btn && !audio.paused) {
    try {
      audio.pause();
    } catch (_) {}
  }
  mxmChatResetAllVoiceCards(voiceCard);
  voiceCard.classList.add('is-loading', 'is-active');
  btn.classList.add('is-loading');
  mxmChatVoiceCardButtonIcon(btn, _MXM_CHAT_VOICE_LOAD_SVG);
  let url = '';
  try {
    url = await mxmChatResolveVoiceUrl(btn);
  } catch {
    url = '';
  }
  voiceCard.classList.remove('is-loading');
  btn.classList.remove('is-loading');
  if (!url) {
    mxmChatVoiceResetCard(voiceCard);
    return;
  }
  _mxmChatActiveVoiceBtn = btn;
  _mxmChatActiveVoiceCard = voiceCard;
  if (audio.dataset.voiceUrl !== url) {
    audio.src = url;
    audio.dataset.voiceUrl = url;
    audio.currentTime = 0;
  }
  mxmChatVoiceSyncCard(voiceCard, audio.currentTime || 0, audio.duration);
  try {
    await audio.play();
    mxmChatApplyPendingVoiceSeek(audio);
  } catch {
    if (_mxmChatActiveVoiceBtn === btn) _mxmChatActiveVoiceBtn = null;
    if (_mxmChatActiveVoiceCard === voiceCard) _mxmChatActiveVoiceCard = null;
    delete audio.dataset.voiceUrl;
    mxmChatVoiceResetCard(voiceCard);
  }
};

function mxmChatOpenShare(t, id) {
  const ty = String(t || '');
  const tid = String(id || '');
  navigate(ty || 'track', { id: tid });
}

window.mxmChatPlayTrack = async function (trackId) {
  if (!trackId) return;
  try {
    const t = await GET(`/api/tracks/${encodeURIComponent(trackId)}`);
    if (t) { playTrack(t); mxmChatUpdateMiniPlayer(); }
    else toast('Трек не найден', 'error');
  } catch { toast('Не удалось загрузить трек', 'error'); }
};

window.mxmMiniLikeToggle = function () {
  if (!state.currentTrack) return;
  toggleFavTrack(state.currentTrack.id);
  setTimeout(mxmChatUpdateMiniPlayer, 200);
};

function mxmChatFormatTime(iso) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

window.mxmChatScrollToMsg = function (id) {
  const row = document.querySelector(`[data-msg-id="${id}"]`);
  if (!row) return;
  const scroll = $('mxm-chat-thread-scroll');
  if (scroll) {
    const style = window.getComputedStyle(scroll);
    const topPad = parseFloat(style.paddingTop) || 0;
    const nextTop = Math.max(0, row.offsetTop - (scroll.clientHeight - row.offsetHeight) / 2 - topPad * 0.5);
    scroll.scrollTo({ top: nextTop, behavior: 'smooth' });
  }
  row.classList.add('mxm-chat-msg--highlight');
  setTimeout(() => row.classList.remove('mxm-chat-msg--highlight'), 1600);
  setTimeout(() => mxmChatUpdateScrollDownBtn(), 450);
};

/* ── Context menu ──────────────────────────────────────────────────────────── */
const _MXM_CTX_ICONS = {
  reply:
    '<svg class="mxm-ctx-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 14l-4-4 4-4"/><path d="M5 10h11a4 4 0 014 4v2"/></svg>',
  copy:
    '<svg class="mxm-ctx-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>',
  pin:
    '<svg class="mxm-ctx-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 17v5"/><path d="M9 10.76V6h6v4.76c0 1.06.58 2.03 1.5 2.52l2.5 1.24H5l2.5-1.24A2.8 2.8 0 009 10.76z"/><path d="M12 2v8"/></svg>',
  edit:
    '<svg class="mxm-ctx-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>',
  trash:
    '<svg class="mxm-ctx-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>'
};

function mxmChatCtxActionRow(iconSvg, label, handler, idJs, extraClass) {
  const ec = extraClass ? ` ${extraClass}` : '';
  return `<button type="button" class="mxm-ctx-item${ec}" onclick="${handler}('${idJs}')"><span class="mxm-ctx-item-ic">${iconSvg}</span><span class="mxm-ctx-item-txt">${esc(label)}</span></button>`;
}

function mxmChatBuildCtxReactionsHtml(msgId) {
  const idJs = esc(msgId);
  const emBtns = ['❤️', '🥚']
    .map(
      (em, i) =>
        `<button type="button" class="mxm-chat-ctx-emoji-btn" style="--mxm-ctx-emoji-i:${i}" onclick="mxmChatPickReaction('${idJs}','${em}')" aria-label="Реакция ${em}">${em}</button>`
    )
    .join('');
  return `<div class="mxm-chat-ctx-reactions" role="toolbar" aria-label="Реакции">
    <div class="mxm-chat-ctx-reactions-glass">${emBtns}</div></div>`;
}

function mxmChatBuildCtxActionsHtml(msgId, { isText, isPinned, mine }) {
  const idJs = esc(msgId);
  const msg = _mxmChatMessagesCache.find((m) => m.id === msgId);
  const canCopy = isText || !!mxmChatPayloadCaptionForMedia(msg?.payload);
  let h = mxmChatCtxActionRow(_MXM_CTX_ICONS.reply, 'Ответить', 'mxmChatCtxReply', idJs);
  if (canCopy) h += mxmChatCtxActionRow(_MXM_CTX_ICONS.copy, 'Копировать', 'mxmChatCtxCopy', idJs);
  h += mxmChatCtxActionRow(_MXM_CTX_ICONS.pin, isPinned ? 'Открепить' : 'Закрепить', 'mxmChatCtxPin', idJs);
  if (mine && isText) h += mxmChatCtxActionRow(_MXM_CTX_ICONS.edit, 'Редактировать', 'mxmChatCtxEdit', idJs);
  if (mine) h += mxmChatCtxActionRow(_MXM_CTX_ICONS.trash, 'Удалить', 'mxmChatCtxDelete', idJs, 'mxm-ctx-item--danger');
  return h;
}

function mxmChatPositionCtxPopover(menu, rowEl) {
  if (!menu || !rowEl) return;
  const bubble = rowEl.querySelector('.mxm-chat-bubble');
  const br = (bubble || rowEl).getBoundingClientRect();
  const vv = window.visualViewport;
  const vTop = vv ? vv.offsetTop : 0;
  const vh = vv ? vv.height : window.innerHeight;
  const pad = 8;
  const gap = 4;

  menu.style.position = 'fixed';
  menu.style.left = '-9999px';
  menu.style.top = '0';
  menu.style.zIndex = '10001';
  const mw = menu.offsetWidth;
  const mh = menu.offsetHeight;

  let left = br.left + br.width / 2 - mw / 2;
  left = Math.max(pad, Math.min(left, window.innerWidth - mw - pad));

  let top = br.top - mh - gap;
  const minTop = vTop + pad;
  menu.classList.remove('mxm-chat-ctx--flip-below');
  if (top < minTop) {
    top = br.bottom + gap;
    menu.classList.add('mxm-chat-ctx--flip-below');
  }
  const maxBottom = vTop + vh - pad;
  if (top + mh > maxBottom) top = Math.max(minTop, Math.min(top, maxBottom - mh));

  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;
}

/** Мобильный стек: полоска реакций + карточка действий (как в Telegram). */
function mxmChatPositionCtxStack(stack, rowEl) {
  if (!stack || !rowEl) return;
  const bubble = rowEl.querySelector('.mxm-chat-bubble');
  const br = (bubble || rowEl).getBoundingClientRect();
  const vv = window.visualViewport;
  const vTop = vv ? vv.offsetTop : 0;
  const vh = vv ? vv.height : window.innerHeight;
  const pad = 10;
  const gap = 11;

  stack.style.position = 'fixed';
  stack.style.left = '-9999px';
  stack.style.top = '0';
  stack.style.zIndex = '10002';
  stack.style.visibility = 'hidden';
  void stack.offsetWidth;
  const mw = stack.offsetWidth;
  const mh = stack.offsetHeight;
  stack.style.visibility = '';

  let left = br.left + br.width / 2 - mw / 2;
  left = Math.max(pad, Math.min(left, window.innerWidth - mw - pad));

  let top = br.top - gap - mh;
  stack.classList.remove('mxm-chat-ctx-stack--flip');
  if (top < vTop + pad) {
    top = br.bottom + gap;
    stack.classList.add('mxm-chat-ctx-stack--flip');
  }
  const maxBottom = vTop + vh - pad;
  if (top + mh > maxBottom) top = Math.max(vTop + pad, Math.min(top, maxBottom - mh));

  stack.style.left = `${Math.round(left)}px`;
  stack.style.top = `${Math.round(top)}px`;
}

function mxmChatShowCtx(e, msgId, anchorRow) {
  mxmChatHideCtx();
  const msg = _mxmChatMessagesCache.find((m) => m.id === msgId);
  if (!msg || msg.deleted) return;
  const me = state.user?.id;
  const mine = msg.senderId === me;
  const isText = msg.messageKind === 'text';
  const isPinned = !!msg.pinnedAt;
  const reactionsHtml = mxmChatBuildCtxReactionsHtml(msgId);
  const actionsHtml = mxmChatBuildCtxActionsHtml(msgId, { isText, isPinned, mine });
  const innerDesktop = `${reactionsHtml}<div class="mxm-chat-ctx-actions">${actionsHtml}</div>`;

  const mobile = _mxmChatIsMobile();
  const rowEl = anchorRow || document.querySelector(`[data-msg-id="${msgId}"]`);

  if (mobile) {
    const overlay = document.createElement('div');
    overlay.className = 'mxm-chat-ctx-overlay';
    overlay.id = 'mxm-chat-ctx-overlay';
    overlay.addEventListener('click', mxmChatHideCtx);
    document.body.appendChild(overlay);

    const stack = document.createElement('div');
    stack.id = 'mxm-chat-ctx';
    stack.className = 'mxm-chat-ctx-stack';
    stack.innerHTML = `<div class="mxm-chat-ctx-sheet" role="menu"><div class="mxm-chat-ctx-actions">${actionsHtml}</div></div>${reactionsHtml}`;
    document.body.appendChild(stack);
    mxmChatPositionCtxStack(stack, rowEl);
    if (rowEl) rowEl.classList.add('mxm-chat-msg--ctx-active');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => stack.classList.add('mxm-chat-ctx-stack--visible'));
    });
    return;
  }

  const menu = document.createElement('div');
  menu.id = 'mxm-chat-ctx';
  menu.className = 'mxm-chat-ctx mxm-chat-ctx--desktop';
  menu.innerHTML = innerDesktop;
  document.body.appendChild(menu);
  if (rowEl) rowEl.classList.add('mxm-chat-msg--ctx-active');
  const bw = window.innerWidth;
  const bh = window.innerHeight;
  let x = e.clientX;
  let y = e.clientY;
  menu.style.left = `${x}px`;
  menu.style.top = `${y}px`;
  const rect = menu.getBoundingClientRect();
  if (rect.right > bw) menu.style.left = `${bw - rect.width - 8}px`;
  if (rect.bottom > bh) menu.style.top = `${bh - rect.height - 8}px`;
  setTimeout(() => document.addEventListener('click', mxmChatHideCtx, { once: true }), 10);
}

function mxmChatHideCtx() {
  document.querySelectorAll('.mxm-chat-msg--ctx-active').forEach((el) => el.classList.remove('mxm-chat-msg--ctx-active'));
  document.querySelectorAll('.mxm-chat-ctx, .mxm-chat-ctx-stack').forEach((el) => el.remove());
  document.querySelectorAll('.mxm-chat-ctx-overlay').forEach((el) => el.remove());
}

window.mxmChatCtxReply = function (msgId) {
  mxmChatHideCtx();
  const msg = _mxmChatMessagesCache.find((m) => m.id === msgId);
  if (!msg) return;
  _mxmChatEditMsg = null;
  _mxmChatReplyTo = msg;
  const bar = $('mxm-chat-reply-bar');
  if (!bar) return;
  const scrollPre = $('mxm-chat-thread-scroll');
  const wasNearBottom = scrollPre ? mxmChatThreadNearBottom(scrollPre, 200) : true;
  const preserveTop = scrollPre ? scrollPre.scrollTop : 0;
  const me = state.user?.id;
  bar.querySelector('.mxm-chat-reply-bar-label').textContent = msg.senderId === me ? 'Ответ себе' : ('Ответ ' + (mxmChatFindPeerName(msg.senderId) || ''));
  bar.querySelector('.mxm-chat-reply-bar-text').textContent = mxmChatPayloadPreviewLine(msg.payload);
  bar.classList.remove('hidden');
  mxmChatStabilizeThreadAfterComposerChange(wasNearBottom, preserveTop);
  requestAnimationFrame(() => {
    mxmChatFocusComposer();
    mxmChatStabilizeThreadAfterComposerChange(wasNearBottom, preserveTop);
  });
};

window.mxmChatCtxEdit = function (msgId) {
  mxmChatHideCtx();
  const msg = _mxmChatMessagesCache.find((m) => m.id === msgId);
  if (!msg || msg.senderId !== state.user?.id) return;
  _mxmChatReplyTo = null;
  _mxmChatEditMsg = msg;
  const bar = $('mxm-chat-reply-bar');
  if (!bar) return;
  const scrollPre = $('mxm-chat-thread-scroll');
  const wasNearBottom = scrollPre ? mxmChatThreadNearBottom(scrollPre, 200) : true;
  const preserveTop = scrollPre ? scrollPre.scrollTop : 0;
  bar.querySelector('.mxm-chat-reply-bar-label').textContent = 'Редактирование';
  bar.querySelector('.mxm-chat-reply-bar-text').textContent = msg.payload?.text || '';
  bar.classList.remove('hidden');
  const ta = $('mxm-chat-input');
  if (ta) {
    ta.value = msg.payload?.text || '';
    mxmChatResizeComposerInput(ta, true);
    mxmChatToggleSendVoice();
  }
  mxmChatStabilizeThreadAfterComposerChange(wasNearBottom, preserveTop);
  requestAnimationFrame(() => {
    mxmChatFocusComposer();
    mxmChatStabilizeThreadAfterComposerChange(wasNearBottom, preserveTop);
  });
};

window.mxmChatCtxDelete = async function (msgId) {
  mxmChatHideCtx();
  try {
    await fetch(resolveClientRequestUrl(`/api/chat/messages/${msgId}`), {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${state.token}` }
    });
    _mxmChatMessagesCache = _mxmChatMessagesCache.filter((m) => m.id !== msgId);
    mxmChatRenderThreadFromCache({ preserveScroll: true });
    mxmChatRefreshSidebar();
  } catch (e) { toast('Не удалось удалить', 'error'); }
};

window.mxmChatCtxCopy = function (msgId) {
  mxmChatHideCtx();
  const msg = _mxmChatMessagesCache.find((m) => m.id === msgId);
  const p = msg?.payload;
  const text =
    p?.type === 'text' ? String(p.text || '').trim() : mxmChatPayloadCaptionForMedia(p);
  if (!text) return;
  navigator.clipboard.writeText(text).then(() => toast('Скопировано', 'success')).catch(() => {});
};

/* ── Pin / Unpin ───────────────────────────────────────────────────────────── */
let _mxmChatPinnedMsg = null;
window.mxmChatCtxPin = async function (msgId) {
  mxmChatHideCtx();
  const msg = _mxmChatMessagesCache.find((m) => m.id === msgId);
  if (!msg) return;
  try {
    if (msg.pinnedAt) {
      await fetch(`/api/chat/messages/${msgId}/pin`, { method: 'DELETE', credentials: 'include', headers: { Authorization: `Bearer ${state.token}` } });
      msg.pinnedAt = null;
      _mxmChatPinnedMsg = null;
      mxmChatUpdatePinnedBar();
      toast('Сообщение откреплено', 'success');
    } else {
      const res = await POST(`/api/chat/messages/${msgId}/pin`, {});
      _mxmChatMessagesCache.forEach((m) => { m.pinnedAt = null; });
      msg.pinnedAt = res.pinnedAt || new Date().toISOString();
      _mxmChatPinnedMsg = msg;
      mxmChatUpdatePinnedBar();
      toast('Сообщение закреплено', 'success');
    }
  } catch (e) { toast(e.message || 'Ошибка', 'error'); }
};

window.mxmChatUnpinMessage = async function () {
  if (!_mxmChatPinnedMsg) return;
  try {
    await fetch(`/api/chat/messages/${_mxmChatPinnedMsg.id}/pin`, { method: 'DELETE', credentials: 'include', headers: { Authorization: `Bearer ${state.token}` } });
    const m = _mxmChatMessagesCache.find((x) => x.id === _mxmChatPinnedMsg.id);
    if (m) m.pinnedAt = null;
    _mxmChatPinnedMsg = null;
    mxmChatUpdatePinnedBar();
  } catch (e) { toast(e.message || 'Ошибка', 'error'); }
};

function mxmChatUpdatePinnedBar() {
  const bar = $('mxm-chat-pinned-bar');
  const textEl = $('mxm-chat-pinned-text');
  if (!bar || !textEl) return;
  if (_mxmChatPinnedMsg && !_mxmChatPinnedMsg.deleted) {
    const p = _mxmChatPinnedMsg.payload;
    textEl.textContent =
      p?.type === 'combo'
        ? mxmChatPayloadCaptionForMedia(p) || 'Музыка и фото'
        : mxmChatPayloadCaptionForMedia(p) ||
          p?.text ||
          p?.title ||
          p?.fileName ||
          'Закреплённое сообщение';
    bar.classList.remove('hidden');
  } else {
    bar.classList.add('hidden');
  }
}

window.mxmChatScrollToPinned = function () {
  if (!_mxmChatPinnedMsg) return;
  mxmChatScrollToMsg(_mxmChatPinnedMsg.id);
};

/* ── Search ─────────────────────────────────────────────────────────────────── */
let _mxmChatSearchDebounce = null;
window.mxmChatSearchToggle = function () {
  const bar = $('mxm-chat-search-bar');
  if (!bar) return;
  bar.classList.toggle('hidden');
  const inp = $('mxm-chat-search-input');
  if (!bar.classList.contains('hidden') && inp) {
    inp.focus();
    if (!inp._mxmBound) {
      inp._mxmBound = true;
      inp.addEventListener('input', () => {
        clearTimeout(_mxmChatSearchDebounce);
        _mxmChatSearchDebounce = setTimeout(() => mxmChatDoSearch(inp.value.trim()), 300);
      });
    }
  } else {
    if (inp) inp.value = '';
    const res = $('mxm-chat-search-results');
    if (res) { res.innerHTML = ''; res.classList.add('hidden'); }
  }
};

async function mxmChatDoSearch(q) {
  const res = $('mxm-chat-search-results');
  if (!res) return;
  if (!q || q.length < 2) { res.innerHTML = ''; res.classList.add('hidden'); return; }
  try {
    const data = await GET(`/api/chat/messages/search?peerUserId=${encodeURIComponent(_mxmChatActivePeerId)}&q=${encodeURIComponent(q)}`);
    if (!data.results?.length) {
      res.innerHTML = '<div class="mxm-chat-search-empty">Ничего не найдено</div>';
      res.classList.remove('hidden');
      return;
    }
    res.innerHTML = data.results.map((m) => {
      const txt = esc(m.payload?.text || m.payload?.title || '').substring(0, 80);
      const time = mxmChatFormatTime(m.createdAt);
      return `<button type="button" class="mxm-chat-search-item" onclick="mxmChatScrollToMsg('${esc(m.id)}');mxmChatSearchToggle()"><span class="mxm-chat-search-item-text">${txt}</span><span class="mxm-chat-search-item-time">${time}</span></button>`;
    }).join('');
    res.classList.remove('hidden');
  } catch (_) {}
}

/* ── File upload ────────────────────────────────────────────────────────────── */
/** Файл всегда в превью — подпись из поля уходит при «Отправить» (в т.ч. если сначала вложили фото, потом набрали текст). */
function mxmChatEscapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function mxmChatSearchHighlight(text, query) {
  const source = String(text || '').trim();
  if (!source) return esc('Сообщение');
  const q = String(query || '').trim();
  if (!q) return esc(source);
  const rx = new RegExp(mxmChatEscapeRegExp(q), 'ig');
  let html = '';
  let lastIndex = 0;
  let match;
  while ((match = rx.exec(source)) !== null) {
    html += esc(source.slice(lastIndex, match.index));
    html += `<mark>${esc(match[0])}</mark>`;
    lastIndex = match.index + match[0].length;
  }
  html += esc(source.slice(lastIndex));
  return html;
}

function mxmChatSearchPayloadLabel(payload) {
  if (!payload || typeof payload !== 'object') return 'Сообщение';
  if (payload.type === 'voice') return 'Голосовое';
  if (payload.type === 'music') return 'Музыка';
  if (payload.type === 'combo') return 'Подборка';
  if (payload.type === 'image') return 'Фото';
  if (payload.type === 'video') return 'Видео';
  if (payload.type === 'file') return 'Файл';
  if (payload.type === 'sticker') return 'Стикер';
  return 'Сообщение';
}

function mxmChatSearchSetMeta(text, tone = '') {
  const meta = $('mxm-chat-search-meta');
  if (!meta) return;
  meta.textContent = text || 'Поиск по сообщениям';
  meta.className = `mxm-chat-search-meta${tone ? ` ${tone}` : ''}`;
}

function mxmChatSearchRenderEmpty(message) {
  const res = $('mxm-chat-search-results');
  if (!res) return;
  res.innerHTML = `<div class="mxm-chat-search-empty">${esc(message || 'Ничего не найдено')}</div>`;
  res.classList.remove('hidden');
}

window.mxmChatSearchClose = function ({ clear = true } = {}) {
  const bar = $('mxm-chat-search-bar');
  const inp = $('mxm-chat-search-input');
  const res = $('mxm-chat-search-results');
  if (!bar) return;
  bar.classList.add('hidden');
  if (inp && clear) inp.value = '';
  if (res) {
    res.innerHTML = '';
    res.classList.add('hidden');
  }
  clearTimeout(_mxmChatSearchDebounce);
  _mxmChatSearchReqSeq++;
  mxmChatSearchSetMeta('Поиск по сообщениям');
};

window.mxmChatSearchToggle = function (forceOpen) {
  const bar = $('mxm-chat-search-bar');
  if (!bar) return;
  const inp = $('mxm-chat-search-input');
  const shouldOpen = typeof forceOpen === 'boolean' ? forceOpen : bar.classList.contains('hidden');
  if (!shouldOpen) {
    window.mxmChatSearchClose({ clear: true });
    return;
  }
  if (typeof mxmChatCloseThreadMenu === 'function') mxmChatCloseThreadMenu();
  bar.classList.remove('hidden');
  mxmChatSearchSetMeta('Введите минимум 2 символа');
  if (!inp) return;
  inp.focus();
  inp.select?.();
  if (!inp._mxmBoundEnhanced) {
    inp._mxmBoundEnhanced = true;
    inp.addEventListener('input', () => {
      clearTimeout(_mxmChatSearchDebounce);
      _mxmChatSearchDebounce = setTimeout(() => mxmChatDoSearch(inp.value.trim()), 280);
    });
    inp.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') window.mxmChatSearchClose({ clear: false });
    });
  }
  if (inp.value.trim()) void mxmChatDoSearch(inp.value.trim());
};

async function mxmChatDoSearch(q) {
  const res = $('mxm-chat-search-results');
  if (!res) return;
  if (!q || q.length < 2) {
    res.innerHTML = '';
    res.classList.add('hidden');
    mxmChatSearchSetMeta('Введите минимум 2 символа');
    return;
  }
  const reqId = ++_mxmChatSearchReqSeq;
  mxmChatSearchSetMeta('Ищу…', 'is-loading');
  res.innerHTML = '<div class="mxm-chat-search-empty">Ищу совпадения…</div>';
  res.classList.remove('hidden');
  try {
    const data = await GET(`/api/chat/messages/search?peerUserId=${encodeURIComponent(_mxmChatActivePeerId)}&q=${encodeURIComponent(q)}`);
    if (reqId !== _mxmChatSearchReqSeq) return;
    const results = Array.isArray(data.results) ? data.results : [];
    if (!results.length) {
      mxmChatSearchSetMeta('Совпадений нет');
      mxmChatSearchRenderEmpty('Ничего не найдено');
      return;
    }
    mxmChatSearchSetMeta(`${results.length} результатов`);
    res.innerHTML = results.map((m) => {
      const preview = mxmChatPayloadPreviewLine(m.payload).slice(0, 120);
      const label = mxmChatSearchPayloadLabel(m.payload);
      const time = mxmChatFormatTime(m.createdAt);
      return `<button type="button" class="mxm-chat-search-item" onclick="mxmChatSearchPick('${profileJsId(m.id)}')">
        <span class="mxm-chat-search-item-icon">${esc(label.slice(0, 1))}</span>
        <span class="mxm-chat-search-item-body">
          <span class="mxm-chat-search-item-text">${mxmChatSearchHighlight(preview, q)}</span>
          <span class="mxm-chat-search-item-sub">${esc(label)}</span>
        </span>
        <span class="mxm-chat-search-item-time">${time}</span>
      </button>`;
    }).join('');
    res.classList.remove('hidden');
  } catch (_) {
    if (reqId !== _mxmChatSearchReqSeq) return;
    mxmChatSearchSetMeta('Ошибка поиска', 'is-error');
    mxmChatSearchRenderEmpty('Не удалось выполнить поиск');
  }
}

window.mxmChatSearchPick = function (msgId) {
  mxmChatScrollToMsg(msgId);
  window.mxmChatSearchClose({ clear: true });
};

function mxmChatThreadMenuBlockIcon() {
  return _mxmChatPeerBlocked
    ? '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9 15l6-6"/></svg>'
    : '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M4.93 4.93l14.14 14.14"/></svg>';
}

function mxmChatBindThreadMenuDismiss() {
  if (mxmChatBindThreadMenuDismiss._bound) return;
  mxmChatBindThreadMenuDismiss._bound = true;
  document.addEventListener('click', (e) => {
    const menu = $('mxm-chat-thread-menu');
    const btn = $('mxm-chat-thread-menu-btn');
    if (!menu || menu.classList.contains('hidden')) return;
    if (Date.now() - (_mxmChatThreadMenuOpenedAt || 0) < 400) return;
    if (menu.contains(e.target) || btn?.contains(e.target)) return;
    mxmChatCloseThreadMenu();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (_mxmChatThreadMenuOpen) mxmChatCloseThreadMenu();
  });
}

function mxmChatCloseThreadMenu() {
  const menu = $('mxm-chat-thread-menu');
  const btn = $('mxm-chat-thread-menu-btn');
  const backdrop = $('mxm-chat-thread-menu-backdrop');
  if (menu) {
    menu.classList.remove('is-open');
    menu.classList.add('hidden');
  }
  if (backdrop) {
    backdrop.classList.remove('is-open');
    backdrop.classList.add('hidden');
  }
  if (btn) btn.setAttribute('aria-expanded', 'false');
  _mxmChatThreadMenuOpen = false;
}

window.mxmChatToggleThreadMenu = function (event) {
  event?.preventDefault?.();
  event?.stopPropagation?.();
  const menu = $('mxm-chat-thread-menu');
  const btn = $('mxm-chat-thread-menu-btn');
  const backdrop = $('mxm-chat-thread-menu-backdrop');
  if (!menu || !btn) return;
  mxmChatBindThreadMenuDismiss();
  const nextOpen = menu.classList.contains('hidden');
  if (!nextOpen) {
    mxmChatCloseThreadMenu();
    return;
  }
  if (backdrop && _mxmChatIsMobile()) {
    backdrop.classList.remove('hidden');
    requestAnimationFrame(() => backdrop.classList.add('is-open'));
  }
  _mxmChatThreadMenuOpenedAt = Date.now();
  menu.classList.remove('hidden');
  menu.classList.add('is-open');
  btn.setAttribute('aria-expanded', 'true');
  _mxmChatThreadMenuOpen = true;
};

window.mxmChatThreadMenuSearch = function () {
  mxmChatCloseThreadMenu();
  window.mxmChatSearchToggle(true);
};

window.mxmChatThreadMenuBlock = function () {
  mxmChatCloseThreadMenu();
  void mxmChatBlockPeer();
};

window.mxmChatThreadMenuDelete = function () {
  mxmChatCloseThreadMenu();
  void mxmChatDeleteChat();
};

function mxmChatTrySendOrStageFile(file) {
  if (!file || !_mxmChatActivePeerId) return;
  _mxmChatPendingFiles.forEach((p) => {
    if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
  });
  const previewUrl = file.type && file.type.startsWith('image/') ? URL.createObjectURL(file) : '';
  _mxmChatPendingFiles = [{ file, previewUrl }];
  _mxmChatUpdateAttachedPreview();
  mxmChatToggleSendVoice();
}

async function mxmChatUploadChatMediaFile(file) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(resolveClientRequestUrl('/api/chat/media'), {
    method: 'POST', body: fd, credentials: 'include',
    headers: { Authorization: `Bearer ${state.token}` }
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || 'Ошибка');
  if (!data.storageKey) throw new Error('Нет storageKey');
  return data;
}

async function mxmChatSendMediaFileInternal(file, caption = '', opts) {
  if (!file || !_mxmChatActivePeerId) return;
  const o = opts === undefined ? null : opts;
  const explicitReply = o != null && Object.prototype.hasOwnProperty.call(o, 'replyToId');
  let replyToId;
  if (explicitReply) {
    replyToId = o.replyToId;
  } else if (_mxmChatReplyTo) {
    replyToId = _mxmChatReplyTo.id;
    _mxmChatReplyTo = null;
    $('mxm-chat-reply-bar')?.classList.add('hidden');
  }
  try {
    const data = await mxmChatUploadChatMediaFile(file);
    const kind = data.type === 'voice' ? 'voice' : data.type;
    const mediaBody = {
      peerUserId: _mxmChatActivePeerId,
      messageKind: kind,
      mediaStorageKey: data.storageKey,
      fileName: data.fileName,
      fileSize: data.fileSize,
      mimeType: data.mimeType
    };
    const cap = String(caption || '').trim();
    if (cap) {
      const c = cap.slice(0, 4000);
      mediaBody.caption = c;
      mediaBody.text = c;
    }
    if (replyToId) mediaBody.replyToId = replyToId;
    const msg = await POST('/api/chat/messages', mediaBody);
    if (msg && msg.id && !_mxmChatMessagesCache.some((m) => m.id === msg.id)) {
      _mxmChatMessagesCache.push(msg);
      mxmChatAppendMessageRow(msg);
    }
  } catch (e) {
    toast(e.message || 'Не удалось загрузить', 'error');
  }
}

window.mxmChatFilePickerOpen = function () {
  const inp = document.createElement('input');
  inp.type = 'file';
  inp.multiple = false;
  inp.accept = 'image/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt';
  inp.addEventListener('change', () => {
    const file = inp.files?.[0];
    if (!file || !_mxmChatActivePeerId) return;
    mxmChatTrySendOrStageFile(file);
  });
  inp.click();
};

/* ── Voice messages ─────────────────────────────────────────────────────────── */
let _mxmChatVoiceRec = null;
let _mxmChatVoiceChunks = [];
let _mxmChatVoiceStart = 0;
let _mxmChatVoiceDiscard = false;

function mxmChatVoiceRecordingUi(show) {
  const cancelBtn = $('mxm-chat-voice-cancel-btn');
  if (cancelBtn) cancelBtn.classList.toggle('hidden', !show);
}

window.mxmChatVoiceCancelRecording = function () {
  if (!_mxmChatVoiceRec || _mxmChatVoiceRec.state !== 'recording') return;
  _mxmChatVoiceDiscard = true;
  try { _mxmChatVoiceRec.stop(); } catch (_) {}
};

window.mxmChatVoiceToggle = async function () {
  if (_mxmChatVoiceRec && _mxmChatVoiceRec.state === 'recording') {
    _mxmChatVoiceDiscard = false;
    _mxmChatVoiceRec.stop();
    return;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    _mxmChatVoiceDiscard = false;
    _mxmChatVoiceChunks = [];
    _mxmChatVoiceStart = Date.now();
    _mxmChatVoiceRec = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm' });
    const voiceBtn = $('mxm-chat-voice-btn');
    if (voiceBtn) voiceBtn.classList.add('mxm-voice-recording');
    mxmChatVoiceRecordingUi(true);
    _mxmChatVoiceRec.ondataavailable = (e) => { if (e.data.size > 0) _mxmChatVoiceChunks.push(e.data); };
    _mxmChatVoiceRec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      if (voiceBtn) voiceBtn.classList.remove('mxm-voice-recording');
      mxmChatVoiceRecordingUi(false);
      const discard = _mxmChatVoiceDiscard;
      _mxmChatVoiceDiscard = false;
      _mxmChatVoiceRec = null;
      if (discard) {
        _mxmChatVoiceChunks = [];
        return;
      }
      const duration = (Date.now() - _mxmChatVoiceStart) / 1000;
      if (duration < 0.5 || !_mxmChatVoiceChunks.length) return;
      const blob = new Blob(_mxmChatVoiceChunks, { type: 'audio/webm' });
      const fd = new FormData();
      fd.append('file', blob, 'voice.webm');
      try {
        const r = await fetch('/api/chat/media', {
          method: 'POST', body: fd, credentials: 'include',
          headers: { Authorization: `Bearer ${state.token}` }
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Ошибка');
        if (!data.storageKey) throw new Error('Нет storageKey');
        const voiceBody = {
          peerUserId: _mxmChatActivePeerId,
          messageKind: 'voice',
          mediaStorageKey: data.storageKey,
          duration: Math.round(duration),
          mimeType: data.mimeType || 'audio/webm'
        };
        if (_mxmChatReplyTo) {
          voiceBody.replyToId = _mxmChatReplyTo.id;
          _mxmChatReplyTo = null;
          $('mxm-chat-reply-bar')?.classList.add('hidden');
        }
        await POST('/api/chat/messages', voiceBody);
      } catch (e) { toast(e.message || 'Ошибка', 'error'); }
    };
    _mxmChatVoiceRec.start(250);
  } catch (e) { toast('Микрофон недоступен', 'error'); }
};

/* ── Online status ──────────────────────────────────────────────────────────── */
let _mxmChatOnlinePeers = new Set();

function mxmChatRememberPeerStatus(peerId, online, lastSeenAt) {
  const id = String(peerId || '').trim();
  if (!id) return;
  const wasOnline = _mxmChatOnlinePeers.has(id);
  if (online) _mxmChatOnlinePeers.add(id);
  else _mxmChatOnlinePeers.delete(id);
  if (lastSeenAt) {
    _mxmChatPeerLastSeenMap.set(id, lastSeenAt);
  } else if (!online && wasOnline) {
    _mxmChatPeerLastSeenMap.set(id, new Date().toISOString());
  }
  _mxmChatPeerStatusFetchedAt.set(id, Date.now());
}

function mxmChatPeerPresenceText(peer) {
  if (!peer?.id) return peer?.login ? '@' + peer.login : 'личный чат';
  const id = String(peer.id);
  if (_mxmChatOnlinePeers.has(id)) return 'в сети';
  const lastSeenAt = _mxmChatPeerLastSeenMap.get(id);
  if (lastSeenAt) return 'был(а) ' + mxmChatFormatLastSeen(lastSeenAt);
  return peer.login ? '@' + peer.login : 'личный чат';
}

function mxmChatPatchSidebarPeerStatus(peerId) {
  const id = String(peerId || '').trim();
  if (!id) return false;
  const row = document.querySelector(`.mxm-chat-peer-item[data-peer-id="${id}"]`);
  if (!row) return false;
  const peer = _mxmChatConversations.find((item) => String(item?.peer?.id) === id)?.peer;
  const online = _mxmChatOnlinePeers.has(id);
  const media = row.querySelector('[data-peer-media]');
  const meta = row.querySelector('[data-peer-meta]');
  if (media) {
    media.classList.toggle('is-online', online);
    let dot = media.querySelector('.online-dot');
    if (online && !dot) {
      dot = document.createElement('span');
      dot.className = 'online-dot';
      dot.setAttribute('aria-hidden', 'true');
      media.appendChild(dot);
    } else if (!online && dot) {
      dot.remove();
    }
  }
  if (meta) {
    const text = mxmChatPeerPresenceText(peer);
    meta.textContent = text;
    meta.classList.toggle('is-online', online);
    meta.classList.toggle('hidden', !text);
  }
  const link = row.querySelector('[data-mxm-open-peer]');
  if (link) link.setAttribute('data-peer-online', online ? '1' : '0');
  return true;
}

function mxmChatRenderThreadPresence(peerId = _mxmChatActivePeerId) {
  const id = String(peerId || '').trim();
  const el = $('mxm-chat-thread-status');
  const headerAv = $('mxm-chat-thread-av');
  if (!el) return;
  if (!id) {
    el.textContent = '';
    el.className = 'mxm-chat-thread-status hidden';
    if (headerAv) headerAv.classList.remove('mxm-peer-online');
    return;
  }
  const peer = _mxmChatConversations.find((item) => String(item?.peer?.id) === id)?.peer || null;
  const online = _mxmChatOnlinePeers.has(id);
  const lastSeenAt = _mxmChatPeerLastSeenMap.get(id) || null;
  let text = '';
  let className = 'mxm-chat-thread-status';
  if (_mxmChatPeerTyping && id === String(_mxmChatActivePeerId || '')) {
    text = 'печатает';
    className += ' is-typing';
  } else if (online) {
    text = 'в сети';
    className += ' mxm-status-online';
  } else if (lastSeenAt) {
    text = 'был(а) ' + mxmChatFormatLastSeen(lastSeenAt);
  } else if (peer?.login) {
    text = '@' + peer.login;
    className += ' is-muted';
  } else {
    text = 'личный диалог';
    className += ' is-muted';
  }
  el.textContent = text;
  el.className = className;
  el.classList.toggle('hidden', !text);
  if (headerAv) headerAv.classList.toggle('mxm-peer-online', online);
}

function mxmChatUpdateStatusUi(peerId, online, lastSeenAt, options = {}) {
  if (options.remember !== false) {
    mxmChatRememberPeerStatus(peerId, !!online, lastSeenAt || null);
  }
  if (String(peerId) === String(_mxmChatActivePeerId)) {
    mxmChatRenderThreadPresence(peerId);
  }
  mxmChatPatchSidebarPeerStatus(peerId);
}

async function mxmChatFetchPeerStatus(peerId, options = {}) {
  const id = String(peerId || '').trim();
  if (!id) return null;
  const ttlMs = Number(options.ttlMs) > 0 ? Number(options.ttlMs) : _MXM_CHAT_STATUS_CACHE_TTL_MS;
  const fetchedAt = _mxmChatPeerStatusFetchedAt.get(id) || 0;
  const hasCache = _mxmChatOnlinePeers.has(id) || _mxmChatPeerLastSeenMap.has(id);
  if (!options.force && hasCache && Date.now() - fetchedAt < ttlMs) {
    if (options.render !== false) {
      mxmChatUpdateStatusUi(
        id,
        _mxmChatOnlinePeers.has(id),
        _mxmChatPeerLastSeenMap.get(id) || null,
        { remember: false }
      );
    }
    return {
      online: _mxmChatOnlinePeers.has(id),
      lastSeenAt: _mxmChatPeerLastSeenMap.get(id) || null,
      cached: true
    };
  }
  if (_mxmChatPeerStatusPending.has(id)) return _mxmChatPeerStatusPending.get(id);
  const req = GET(`/api/users/${encodeURIComponent(id)}/status`)
    .then((data) => {
      const safe = data || {};
      mxmChatRememberPeerStatus(id, !!safe.online, safe.lastSeenAt || null);
      if (options.render !== false) {
        mxmChatUpdateStatusUi(id, !!safe.online, safe.lastSeenAt || null, { remember: false });
      }
      return safe;
    })
    .catch(() => null)
    .finally(() => {
      _mxmChatPeerStatusPending.delete(id);
    });
  _mxmChatPeerStatusPending.set(id, req);
  return req;
}

function mxmChatPrefetchConversationStatuses(conversations = _mxmChatConversations) {
  const peers = Array.isArray(conversations) ? conversations : [];
  peers.slice(0, _MXM_CHAT_STATUS_PREFETCH_LIMIT).forEach((item) => {
    const peerId = String(item?.peer?.id || '').trim();
    if (peerId) void mxmChatFetchPeerStatus(peerId, { render: true });
  });
}

function mxmChatFormatLastSeen(dt) {
  const d = new Date(dt);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'только что';
  if (diff < 3600) return Math.floor(diff / 60) + ' мин. назад';
  if (diff < 86400) return Math.floor(diff / 3600) + ' ч. назад';
  return d.toLocaleDateString('ru', { day: 'numeric', month: 'short' });
}

window.mxmChatCancelReplyEdit = function () {
  const scrollPre = $('mxm-chat-thread-scroll');
  const wasNearBottom = scrollPre ? mxmChatThreadNearBottom(scrollPre, 200) : true;
  const preserveTop = scrollPre ? scrollPre.scrollTop : 0;
  const wasEditing = !!_mxmChatEditMsg;
  _mxmChatReplyTo = null;
  _mxmChatEditMsg = null;
  const bar = $('mxm-chat-reply-bar');
  if (bar) bar.classList.add('hidden');
  const ta = $('mxm-chat-input');
  if (ta && wasEditing) {
    mxmChatResetComposerInput(ta, true);
    mxmChatToggleSendVoice();
  }
  mxmChatStabilizeThreadAfterComposerChange(wasNearBottom, preserveTop);
};

/* ── Navigation ────────────────────────────────────────────────────────────── */
window.mxmChatOpenPeer = function (peerId) {
  const id = String(peerId || '').trim();
  if (!id || !state.user) return;
  navigate('messages', { id });
};

window.mxmChatGoToProfile = function (e) {
  if (e) e.preventDefault();
  if (!_mxmChatActivePeerId) return;
  navigate('profile', { id: _mxmChatPeerPublicRef || _mxmChatActivePeerId });
};

/* ── Mobile slide helpers ──────────────────────────────────────────────────── */
function mxmChatMobileShowThread() {
  const app = document.querySelector('.mxm-chat-app');
  if (app) app.classList.add('mxm-chat-thread-open');
  document.body.classList.add('mxm-chat-thread-open');
  _mxmChatMobileThreadOpen = true;
}
function mxmChatMobileShowList() {
  const app = document.querySelector('.mxm-chat-app');
  if (typeof mxmChatCloseThreadMenu === 'function') mxmChatCloseThreadMenu();
  if (typeof window.mxmChatSearchClose === 'function') window.mxmChatSearchClose({ clear: true });
  if (typeof mxmChatEmojiCloseImmediate === 'function') mxmChatEmojiCloseImmediate();
  if (typeof mxmChatCloseTracksPanel === 'function') mxmChatCloseTracksPanel({ immediate: true });
  if (app) app.classList.remove('mxm-chat-thread-open');
  document.body.classList.remove('mxm-chat-thread-open');
  _mxmChatMobileThreadOpen = false;
}
window.mxmChatMobileBack = function () {
  mxmChatMobileShowList();
  navigate('messages');
};

/* ── Swipe-back gesture (edge swipe from left → go back to list) ───────── */
function mxmChatBindSwipeBack() {
  const main = document.querySelector('.mxm-chat-main');
  const sidebar = document.querySelector('.mxm-chat-sidebar');
  if (!main || main._mxmSwipeBackBound) return;
  main._mxmSwipeBackBound = true;
  let startX = 0, startY = 0, tracking = false, moved = false;
  const THRESHOLD = 84;

  const freeSpaceOnly = (target) => {
    if (!(target instanceof HTMLElement)) return false;
    return !target.closest(
      '.mxm-chat-msg, .message, .mxm-chat-bubble, .mxm-chat-thread-head, .mxm-chat-search-bar, .mxm-chat-pinned-bar, #mxm-chat-tools, .mxm-chat-peer-link, button, a, input, textarea, video, audio, .mxm-chat-search-results, .mxm-chat-thread-menu'
    );
  };

  main.addEventListener('touchstart', (e) => {
    if (!_mxmChatIsMobile() || !_mxmChatMobileThreadOpen) return;
    const t = e.touches[0];
    if (!freeSpaceOnly(e.target)) return;
    const viewportLeft = Number(window.visualViewport?.offsetLeft || 0);
    if (t.clientX > viewportLeft + 28) return;
    startX = t.clientX; startY = t.clientY;
    tracking = true; moved = false;
    main.classList.add('mxm-chat-swiping');
    sidebar?.classList.add('mxm-chat-swiping');
  }, { passive: true });

  main.addEventListener('touchmove', (e) => {
    if (!tracking) return;
    const t = e.touches[0];
    const dx = t.clientX - startX;
    const dy = Math.abs(t.clientY - startY);
    if (!moved && dy > Math.abs(dx)) { tracking = false; main.classList.remove('mxm-chat-swiping'); sidebar?.classList.remove('mxm-chat-swiping'); return; }
    moved = true;
    if (dx < 0) return;
    e.preventDefault();
    const pct = Math.min(dx / window.innerWidth, 1);
    main.style.transform = `translateX(${dx}px)`;
    if (sidebar) sidebar.style.transform = `translateX(${-25 + pct * 25}%)`;
    if (sidebar) sidebar.style.opacity = String(0.4 + pct * 0.6);
  }, { passive: false });

  const finish = () => {
    if (!tracking) return;
    tracking = false;
    main.classList.remove('mxm-chat-swiping');
    sidebar?.classList.remove('mxm-chat-swiping');
    const cx = parseFloat(main.style.transform.replace(/[^0-9.-]/g, '')) || 0;
    main.style.transform = '';
    if (sidebar) { sidebar.style.transform = ''; sidebar.style.opacity = ''; }
    if (cx > THRESHOLD) { mxmChatMobileBack(); }
  };
  main.addEventListener('touchend', finish, { passive: true });
  main.addEventListener('touchcancel', finish, { passive: true });
}

/* ── Swipe-left on message to reply ────────────────────────────────────── */
function mxmChatBindMsgSwipeReply(row, msgId) {
  if (!row || row._mxmSwipeBound) return;
  row._mxmSwipeBound = true;

  const icon = document.createElement('span');
  icon.className = 'mxm-swipe-reply-icon';
  icon.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 14l-4-4 4-4"/><path d="M5 10h11a4 4 0 010 8h-1"/></svg>';
  row.appendChild(icon);

  let startX = 0, startY = 0, tracking = false, moved = false;
  const THRESHOLD = 60;

  row.addEventListener('touchstart', (e) => {
    if (!_mxmChatIsMobile()) return;
    if (e.target.closest('button, a, input, textarea, video, audio')) return;
    const t = e.touches[0]; startX = t.clientX; startY = t.clientY;
    tracking = true; moved = false;
  }, { passive: true });

  row.addEventListener('touchmove', (e) => {
    if (!tracking) return;
    const t = e.touches[0];
    const dx = t.clientX - startX;
    const dy = Math.abs(t.clientY - startY);
    if (!moved && dy > Math.abs(dx) * 1.2) { tracking = false; return; }
    if (e.target.closest('button, a, input, textarea, video, audio')) {
      tracking = false;
      return;
    }
    if (dx >= 0) { if (!moved) return; }
    moved = true;
    e.preventDefault();
    const shift = Math.max(-100, Math.min(0, dx));
    row.style.transform = `translateX(${shift}px)`;
    row.style.transition = 'none';
    row.classList.toggle('mxm-swiping', Math.abs(shift) > 18);
  }, { passive: false });

  const finish = () => {
    if (!tracking && !moved) return;
    const cx = parseFloat(row.style.transform?.replace(/[^0-9.-]/g, '')) || 0;
    tracking = false;
    row.style.transition = 'transform .2s ease';
    row.style.transform = '';
    row.classList.remove('mxm-swiping');
    setTimeout(() => { row.style.transition = ''; }, 220);
    if (cx <= -THRESHOLD) {
      mxmChatCtxReply(msgId);
    }
  };
  row.addEventListener('touchend', finish, { passive: true });
  row.addEventListener('touchcancel', finish, { passive: true });
}

/* ── Long-press for context menu (mobile) ──────────────────────────────── */
function mxmChatBindLongPress(row, msgId) {
  if (!row || row._mxmLpBound) return;
  row._mxmLpBound = true;
  let timer = null, sx = 0, sy = 0;

  row.addEventListener('touchstart', (e) => {
    if (!_mxmChatIsMobile()) return;
    const t = e.touches[0]; sx = t.clientX; sy = t.clientY;
    timer = setTimeout(() => {
      timer = null;
      if (navigator.vibrate) navigator.vibrate(30);
      mxmChatShowCtx({ clientX: sx, clientY: sy }, msgId, row);
    }, 500);
  }, { passive: true });

  const cancel = (e) => {
    if (timer) {
      const t = e.touches?.[0];
      if (t && Math.abs(t.clientX - sx) < 10 && Math.abs(t.clientY - sy) < 10) return;
      clearTimeout(timer); timer = null;
    }
  };
  row.addEventListener('touchmove', cancel, { passive: true });
  row.addEventListener('touchend', () => { if (timer) { clearTimeout(timer); timer = null; } }, { passive: true });
  row.addEventListener('touchcancel', () => { if (timer) { clearTimeout(timer); timer = null; } }, { passive: true });
}

async function mxmChatOpenThread(peerId) {
  const content = $('content');
  if (!content || !state.user) return;
  const openSeq = ++_mxmChatOpenSeq;
  requestAnimationFrame(() => {
    if (openSeq !== _mxmChatOpenSeq) return;
    if (content.scrollTop !== 0) content.scrollTop = 0;
  });
  mxmChatStopVoicePlayback();
  if (typeof mxmChatCloseThreadMenu === 'function') mxmChatCloseThreadMenu();
  if (typeof window.mxmChatSearchClose === 'function') window.mxmChatSearchClose({ clear: true });
  if (typeof mxmChatEmojiCloseImmediate === 'function') mxmChatEmojiCloseImmediate();
  if (typeof mxmChatCloseTracksPanel === 'function') mxmChatCloseTracksPanel({ immediate: true });
  _mxmChatActivePeerId = peerId;
  _mxmChatReplyTo = null;
  _mxmChatEditMsg = null;
  _mxmChatPeerLastReadAt = null;
  _mxmChatPeerTyping = false;
  _mxmChatPeerBlocked = false;
  _mxmChatPeerIsFriend = true;
  const shell = content.querySelector('.mxm-chat-app');
  if (!shell) return;
  const thread = $('mxm-chat-thread');
  const placeholder = $('mxm-chat-placeholder');
  const tools = $('mxm-chat-tools');
  const replyBar = $('mxm-chat-reply-bar');
  if (placeholder) placeholder.classList.add('hidden');
  if (thread) thread.classList.remove('hidden');
  if (tools) tools.classList.remove('hidden');
  if (replyBar) replyBar.classList.add('hidden');
  const title = $('mxm-chat-thread-title');
  const meta = $('mxm-chat-thread-meta');
  const headerAv = $('mxm-chat-thread-av');
  const peer = _mxmChatConversations.map((c) => c.peer).find((p) => String(p.id) === String(peerId));
  if (meta) {
    meta.textContent = peer?.login ? '@' + peer.login : 'Личный диалог';
    meta.classList.toggle('hidden', !meta.textContent);
  }
  if (title) title.textContent = peer ? peer.username : 'Чат';
  if (headerAv) {
    if (peer?.avatarUrl) {
      headerAv.innerHTML = `<img src="${esc(peer.avatarUrl)}" alt="" class="mxm-chat-thread-av-img"/>`;
      headerAv.classList.add('mxm-chat-peer-av--has-img');
    } else {
      headerAv.textContent = mxmChatPeerInitial(peer?.username);
      headerAv.classList.remove('mxm-chat-peer-av--has-img');
    }
  }
  mxmChatRenderThreadPresence(peerId);
  if (peer?.login) {
    _mxmChatPeerPublicRef = peer.login;
    const want = buildHash('messages', { id: peer.login });
    const cur = (location.hash || '#').replace(/^#/, '').trim();
    if (cur !== want) {
      try {
        suppressNavStackPush = true;
        history.replaceState(null, '', '#' + want);
      } finally {
        suppressNavStackPush = false;
      }
    }
  } else if (!_mxmChatPeerPublicRef) {
    _mxmChatPeerPublicRef = String(peerId);
  }
  if (_mxmChatIsMobile()) mxmChatMobileShowThread();
  const openConv = _mxmChatConversations.find((c) => String(c.peer.id) === String(peerId));
  _mxmChatOpenHadUnread = (openConv?.unreadCount || 0) > 0;
  _mxmChatThreadMyLastReadAt = null;
  _mxmChatMessagesCache = [];
  const scroll = $('mxm-chat-thread-scroll');
  if (scroll) scroll.innerHTML = '<div class="mxm-chat-loading">Загрузка...</div>';
  mxmChatRefreshSidebar();
  try {
    const statusPromise = mxmChatFetchPeerStatus(peerId);
    const res = await GET(`/api/chat/messages?peerUserId=${encodeURIComponent(peerId)}&limit=80`);
    void statusPromise.catch(() => {});
    if (openSeq !== _mxmChatOpenSeq || String(peerId) !== String(_mxmChatActivePeerId)) return;
    _mxmChatMessagesCache = (res.messages || []).filter((m) => !m.deleted);
    if (res.peerLastReadAt) _mxmChatPeerLastReadAt = new Date(res.peerLastReadAt);
    _mxmChatThreadMyLastReadAt = res.myLastReadAt || null;
    _mxmChatPeerBlocked = !!res.isBlocked;
    _mxmChatPeerIsFriend = res.isFriend !== false;
    mxmChatUpdateBlockBtn();
    mxmChatUpdateInputAccess();
    if (typeof mxmListenSyncChatButton === 'function') mxmListenSyncChatButton();
    _mxmChatPinnedMsg = res.pinnedMessage || null;
    mxmChatUpdatePinnedBar();
    if (scroll) {
      scroll.classList.add('mxm-chat-thread-scroll--preparing');
      mxmChatRenderThreadFromCache({ scroll, keepBottom: true });
      mxmChatBindThreadScroll();
      mxmChatBindComposerResizeObserver();
      await mxmChatPrepareThreadOpenScroll(scroll, openSeq);
    }
    POST('/api/chat/read', { peerUserId: peerId }).catch(() => {});
    mxmChatStartPoll();
  } catch (e) {
    if (scroll) scroll.innerHTML = `<p class="mxm-chat-err">${esc(e.message || 'Ошибка')}</p>`;
  }
}

function mxmTryConsumePendingShare() {
  try {
    const raw = sessionStorage.getItem('mxm_pending_chat_share');
    if (!raw) return;
    sessionStorage.removeItem('mxm_pending_chat_share');
    const p = JSON.parse(raw);
    if (p && _mxmChatActivePeerId && p.shareId) {
      mxmChatSendSharePayload(p.shareType || 'track', p.shareId, p.shareName || '', '', '', {});
    }
  } catch (_) {}
}

/* ── Shell HTML ────────────────────────────────────────────────────────────── */
function mxmChatShellHtml() {
  return `<div class="mxm-chat-app mxm-chat-app--modern">
  <aside class="mxm-chat-sidebar">
    <div class="mxm-chat-sidebar-head">
      <div class="mxm-chat-sidebar-head-row">
        <h1 class="mxm-chat-h1 mxm-chat-sidebar-title">Сообщения</h1>
        <button type="button" class="mxm-chat-sidebar-compose" onclick="mxmChatFabNew()" title="Новое сообщение" aria-label="Новое сообщение">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
        </button>
      </div>
      <p id="mxm-chat-sidebar-summary" class="mxm-chat-sidebar-summary" aria-live="polite">Личные чаты MXM</p>
    </div>
    <div class="mxm-chat-sidebar-scroll">
      <div class="mxm-chat-sidebar-sticky">
        <div class="mxm-chat-sidebar-tools">
          <label class="mxm-chat-sidebar-search">
            ${MXM_SEARCH_ICON_SVG.replace('<svg ', '<svg width="16" height="16" ')}
            <input id="mxm-chat-sidebar-search-input" type="search" autocomplete="off" spellcheck="false" placeholder="Поиск">
            <button type="button" id="mxm-chat-sidebar-search-clear" class="mxm-chat-sidebar-search-clear hidden" onclick="mxmChatClearSidebarSearch()" aria-label="Очистить поиск">×</button>
          </label>
        </div>
        <div class="mxm-chat-sidebar-list-head">
          <span class="mxm-chat-sidebar-list-title">Чаты</span>
          <span id="mxm-chat-sidebar-list-count" class="mxm-chat-sidebar-list-count">0</span>
        </div>
      </div>
      <ul id="mxm-chat-peer-list" class="mxm-chat-peer-list" role="listbox" aria-label="Чаты"></ul>
    </div>
    <div id="mxm-chat-mini-player" class="mxm-chat-mini-player hidden">
      <div class="mxm-mini-cover" id="mxm-mini-cover"></div>
      <div class="mxm-mini-info">
        <div class="mxm-mini-title" id="mxm-mini-title"></div>
        <div class="mxm-mini-artist-row" id="mxm-mini-artist"></div>
      </div>
      <div class="mxm-mini-controls">
        <button type="button" onclick="mxmMiniLikeToggle()" id="mxm-mini-like" title="Нравится" class="mxm-mini-btn"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572"/></svg></button>
        <button type="button" onclick="playPrev()" title="Назад" class="mxm-mini-btn"><svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M6 6h2v12H6zM9.5 12l8.5 6V6z"/></svg></button>
        <button type="button" onclick="togglePlay()" id="mxm-mini-play" title="Воспр." class="mxm-mini-btn mxm-mini-btn--play"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></button>
        <button type="button" onclick="playNext()" title="Далее" class="mxm-mini-btn"><svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M16 18h2V6h-2zM6 18l8.5-6L6 6z"/></svg></button>
      </div>
      <div class="mxm-mini-progress"><div class="mxm-mini-progress-bar" id="mxm-mini-progress-bar"></div></div>
    </div>
  </aside>
  <section class="mxm-chat-main">
    <div id="mxm-chat-placeholder" class="mxm-chat-placeholder">
      <div class="mxm-chat-placeholder-visual" aria-hidden="true">
        <svg viewBox="0 0 48 48" width="56" height="56" fill="none" stroke="currentColor" stroke-width="1.25"><path d="M44 24c0 10.5-8.95 19-20 19a20.7 20.7 0 01-7.6-1.4L4 44l2.8-9A18.4 18.4 0 014 24C4 13.5 12.95 5 24 5s20 8.5 20 19z"/></svg>
      </div>
      <p class="mxm-chat-placeholder-title">Выберите чат</p>
      <p class="mxm-chat-placeholder-sub">Слева список переписок — как в привычных мессенджерах</p>
    </div>
    <div id="mxm-chat-thread" class="mxm-chat-thread hidden">
      <header class="mxm-chat-thread-head">
        <div class="mxm-chat-thread-head-left">
          <button type="button" class="mxm-chat-back-btn" onclick="mxmChatMobileBack()" title="Назад"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg></button>
          <span class="mxm-chat-peer-av" id="mxm-chat-thread-av"></span>
          <div class="mxm-chat-thread-headline">
            <div class="mxm-chat-thread-head-name-wrap">
            <a id="mxm-chat-thread-title" class="mxm-chat-thread-title" href="#" onclick="mxmChatGoToProfile(event)">Чат</a>
            <span id="mxm-chat-thread-status" class="mxm-chat-thread-status hidden" aria-live="polite"></span>
            </div>
            <span id="mxm-chat-thread-meta" class="mxm-chat-thread-meta hidden"></span>
          </div>
        </div>
        <div class="mxm-chat-thread-actions">
          <button type="button" class="mxm-chat-head-btn mxm-chat-listen-btn hidden" id="mxm-chat-listen-btn" onclick="mxmListenInvite(_mxmChatActivePeerId)" title="Слушать вместе" aria-label="Слушать вместе">
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18V5l10-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="16" cy="16" r="3"/><path d="M3 7a4 4 0 014-4M21 7a4 4 0 00-4-4"/></svg>
          </button>
          <button type="button" class="mxm-chat-head-btn" id="mxm-chat-thread-menu-btn" onclick="mxmChatToggleThreadMenu(event)" title="Действия" aria-haspopup="menu" aria-expanded="false">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none"></circle><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"></circle><circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none"></circle></svg>
          </button>
          <button type="button" id="mxm-chat-thread-menu-backdrop" class="mxm-chat-thread-menu-backdrop hidden" aria-hidden="true" tabindex="-1" onclick="mxmChatCloseThreadMenu()"></button>
          <div id="mxm-chat-thread-menu" class="mxm-chat-thread-menu hidden" role="menu">
            <button type="button" class="mxm-chat-thread-menu-item" onclick="mxmChatThreadMenuSearch()">
              <span class="mxm-chat-thread-menu-icon">${MXM_SEARCH_ICON_SVG.replace('<svg ', '<svg width="18" height="18" ')}</span>
              <span>Поиск</span>
            </button>
            <button type="button" class="mxm-chat-thread-menu-item" id="mxm-chat-thread-menu-block" onclick="mxmChatThreadMenuBlock()">
              <span class="mxm-chat-thread-menu-icon" id="mxm-chat-thread-menu-block-icon"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><path d="M4.93 4.93l14.14 14.14"></path></svg></span>
              <span id="mxm-chat-thread-menu-block-label">Заблокировать</span>
            </button>
            <button type="button" class="mxm-chat-thread-menu-item is-danger" onclick="mxmChatThreadMenuDelete()">
              <span class="mxm-chat-thread-menu-icon"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg></span>
              <span>Удалить чат</span>
            </button>
          </div>
        </div>
      </header>
      <div id="mxm-chat-search-bar" class="mxm-chat-search-bar hidden">
        <label class="mxm-chat-search-field">
          ${MXM_SEARCH_ICON_SVG.replace('<svg ', '<svg width="18" height="18" ')}
          <input type="text" id="mxm-chat-search-input" class="mxm-chat-search-input" placeholder="Поиск по сообщениям">
          <button type="button" class="mxm-chat-search-clear" onclick="document.getElementById('mxm-chat-search-input').value='';mxmChatDoSearch('');document.getElementById('mxm-chat-search-input').focus();" aria-label="Очистить поиск">&times;</button>
        </label>
        <span id="mxm-chat-search-meta" class="mxm-chat-search-meta">Поиск по сообщениям</span>
        <button type="button" class="mxm-chat-search-close" onclick="mxmChatSearchToggle(false)" aria-label="Закрыть поиск">&times;</button>
        <div id="mxm-chat-search-results" class="mxm-chat-search-results hidden"></div>
      </div>
      <div id="mxm-chat-pinned-bar" class="mxm-chat-pinned-bar hidden" onclick="mxmChatScrollToPinned()">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 17v5"/><path d="M5 17h14v-1.76a2 2 0 00-1.11-1.79l-1.78-.9A2 2 0 0115 10.76V6h1V3H8v3h1v4.76a2 2 0 01-1.11 1.79l-1.78.9A2 2 0 005 15.24z"/></svg>
        <span id="mxm-chat-pinned-text" class="mxm-chat-pinned-text"></span>
        <button type="button" class="mxm-chat-pinned-close" onclick="event.stopPropagation();mxmChatUnpinMessage();">&times;</button>
      </div>
      <div class="mxm-chat-thread-scroll-wrap">
        <div class="mxm-chat-thread-backdrop" aria-hidden="true">
          <span class="mxm-chat-thread-orb mxm-chat-thread-orb--one"></span>
          <span class="mxm-chat-thread-orb mxm-chat-thread-orb--two"></span>
          <span class="mxm-chat-thread-grid"></span>
        </div>
        <div id="mxm-chat-thread-scroll" class="mxm-chat-thread-scroll" role="list" aria-label="Сообщения чата"></div>
        <button type="button" id="mxm-chat-scroll-bottom-btn" class="mxm-chat-scroll-bottom-btn hidden" onclick="mxmChatScrollThreadToBottom()" title="Вниз к новым" aria-label="Вниз к новым сообщениям">
          <span class="mxm-chat-scroll-bottom-body">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
            <span id="mxm-chat-scroll-bottom-count" class="mxm-chat-scroll-bottom-count hidden" aria-hidden="true"></span>
          </span>
        </button>
      </div>
      <div id="mxm-chat-typing" class="mxm-chat-typing hidden">печатает...</div>
      <div id="mxm-chat-reply-bar" class="mxm-chat-reply-bar hidden">
        <div class="mxm-chat-reply-bar-content">
          <span class="mxm-chat-reply-bar-label"></span>
          <span class="mxm-chat-reply-bar-text"></span>
        </div>
        <button type="button" class="mxm-chat-reply-bar-close" onclick="mxmChatCancelReplyEdit()">&times;</button>
      </div>
      <footer id="mxm-chat-tools" class="mxm-chat-tools hidden">
        <div id="mxm-chat-attach-preview" class="mxm-chat-attach-preview hidden"></div>
        <div class="mxm-chat-input-stack">
        <div id="mxm-chat-tracks-panel" class="mxm-chat-tracks-panel hidden"></div>
        <div class="mxm-chat-input-row mxm-chat-composer-wrap">
          <div class="mxm-chat-drop-overlay" aria-hidden="true">
            <div class="mxm-chat-drop-overlay-inner">
              <svg class="mxm-chat-drop-icon" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              <span class="mxm-chat-drop-title">Отпустите фото сюда</span>
              <span class="mxm-chat-drop-hint">Отправится как вложение к сообщению</span>
            </div>
          </div>
          <button type="button" class="mxm-chat-attach-btn" onclick="mxmChatFilePickerOpen()" title="Вложение"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg></button>
          <button type="button" class="mxm-chat-attach-btn mxm-chat-tracks-btn" onclick="mxmChatToggleTracks()" title="Поделиться треком">${_MXM_SVG_CHAT_MUSIC}</button>
          <div class="mxm-chat-input-field-wrap">
            <textarea id="mxm-chat-input" class="mxm-chat-input" rows="1" placeholder="Сообщение..." maxlength="4000" autocomplete="off" autocorrect="on" autocapitalize="sentences" enterkeyhint="send"></textarea>
            <button type="button" class="mxm-chat-attach-btn mxm-chat-emoji-btn" onclick="mxmChatToggleEmoji()" title="Эмодзи"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg></button>
            <div id="mxm-chat-emoji-slot" class="mxm-chat-panels-slot hidden"></div>
          </div>
          <button type="button" class="mxm-chat-send-btn hidden" id="mxm-chat-send-btn" onclick="mxmChatSendText()" title="Отправить" aria-label="Отправить"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg></button>
          <button type="button" class="mxm-chat-voice-cancel-btn hidden" id="mxm-chat-voice-cancel-btn" onclick="mxmChatVoiceCancelRecording()" title="Отменить запись"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          <button type="button" class="mxm-chat-voice-btn" id="mxm-chat-voice-btn" onclick="mxmChatVoiceToggle()" title="Запись: нажмите ещё раз, чтобы отправить"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg></button>
        </div>
        </div>
      </footer>
    </div>
  </section>
</div>`;
}

/* ── Input / Send ──────────────────────────────────────────────────────────── */
function mxmChatDataTransferLooksLikeImageDrop(dt) {
  if (!dt || !dt.types) return false;
  const types = Array.from(dt.types);
  if (!types.includes('Files')) return false;
  if (!dt.items || !dt.items.length) return true;
  for (let i = 0; i < dt.items.length; i++) {
    const it = dt.items[i];
    if (it.kind !== 'file') continue;
    const ty = String(it.type || '').toLowerCase();
    if (ty.startsWith('image/')) return true;
    if (!ty || ty === 'application/octet-stream') return true;
  }
  return false;
}

function mxmChatBindInput() {
  const ta = $('mxm-chat-input');
  if (!ta) return;
  const tools = $('mxm-chat-tools');
  mxmChatBindComposerResizeObserver();
  if (tools && !tools.dataset.mxmImageDropBound) {
    tools.dataset.mxmImageDropBound = '1';
    const composerWrap = () => tools.querySelector('.mxm-chat-composer-wrap');
    const clearDragUi = () => composerWrap()?.classList.remove('mxm-chat-composer-wrap--drag');
    tools.addEventListener('dragenter', (e) => {
      if (_mxmChatIsMobile()) return;
      if (!_mxmChatActivePeerId || _mxmChatEditMsg) return;
      const wrap = e.target.closest?.('.mxm-chat-composer-wrap');
      if (!wrap || !tools.contains(wrap)) return;
      if (!mxmChatDataTransferLooksLikeImageDrop(e.dataTransfer)) return;
      e.preventDefault();
      wrap.classList.add('mxm-chat-composer-wrap--drag');
    });
    tools.addEventListener('dragover', (e) => {
      if (_mxmChatIsMobile()) return;
      if (!_mxmChatActivePeerId || _mxmChatEditMsg) return;
      const wrap = e.target.closest?.('.mxm-chat-composer-wrap');
      if (!wrap || !tools.contains(wrap)) return;
      if (!mxmChatDataTransferLooksLikeImageDrop(e.dataTransfer)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    });
    tools.addEventListener('dragleave', (e) => {
      const wrap = e.target.closest?.('.mxm-chat-composer-wrap');
      if (!wrap || !tools.contains(wrap)) return;
      const rel = e.relatedTarget;
      if (!rel || !wrap.contains(rel)) wrap.classList.remove('mxm-chat-composer-wrap--drag');
    });
    tools.addEventListener('drop', (e) => {
      if (_mxmChatIsMobile()) return;
      if (!_mxmChatActivePeerId || _mxmChatEditMsg) return;
      const wrap = e.target.closest?.('.mxm-chat-composer-wrap');
      if (!wrap || !tools.contains(wrap)) return;
      wrap.classList.remove('mxm-chat-composer-wrap--drag');
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files || []);
      const img = files.find((f) => String(f.type || '').toLowerCase().startsWith('image/'));
      if (!img) {
        toast('Перетащите изображение', 'info');
        return;
      }
      mxmChatTrySendOrStageFile(img);
    });
    document.addEventListener('dragend', clearDragUi);
  }
  if (!ta.dataset.mxmPasteImageBound) {
    ta.dataset.mxmPasteImageBound = '1';
    ta.addEventListener('paste', (e) => {
      if (_mxmChatIsMobile()) return;
      if (!_mxmChatActivePeerId || _mxmChatEditMsg) return;
      const cd = e.clipboardData;
      if (!cd || !cd.items?.length) return;
      let imageFile = null;
      for (let i = 0; i < cd.items.length; i++) {
        const it = cd.items[i];
        if (it.kind === 'file' && typeof it.type === 'string' && it.type.indexOf('image/') === 0) {
          const f = it.getAsFile();
          if (f) {
            imageFile = f;
            break;
          }
        }
      }
      if (!imageFile) return;
      e.preventDefault();
      mxmChatTrySendOrStageFile(imageFile);
    });
  }
  if (!ta.dataset.mxmComposerKeyBound) {
    ta.dataset.mxmComposerKeyBound = '1';
    ta.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        mxmChatSendText();
      }
    });
  }
  // Telegram-like: тап по «Отправить» не забирает фокус у textarea → клавиатура не закрывается
  const sendBtn = $('mxm-chat-send-btn');
  if (sendBtn && !sendBtn.dataset.mxmKeepFocusBound) {
    sendBtn.dataset.mxmKeepFocusBound = '1';
    sendBtn.addEventListener(
      'pointerdown',
      (e) => {
        if (e.button != null && e.button !== 0) return;
        e.preventDefault();
      },
      { passive: false }
    );
  }
  if (!ta.dataset.mxmComposerInputBound) {
    ta.dataset.mxmComposerInputBound = '1';
    ta.addEventListener('input', () => {
      mxmChatResizeComposerInput(ta);
      mxmChatTyping();
      mxmChatToggleSendVoice();
      requestAnimationFrame(() => {
        if (typeof window !== 'undefined' && window.innerWidth <= 900) {
          syncChatComposerKeyboardAlign();
        }
        mxmChatScheduleStabilizeAfterComposerResize();
      });
    });
  }
  mxmChatResizeComposerInput(ta, true);
  mxmChatToggleSendVoice();
}

function mxmChatToggleSendVoice() {
  const ta = $('mxm-chat-input');
  const send = $('mxm-chat-send-btn');
  const voice = $('mxm-chat-voice-btn');
  if (!send || !voice) return;
  const hasText = ta && ta.value.trim().length > 0;
  const hasTracks = _mxmChatSelectedTracks && _mxmChatSelectedTracks.length > 0;
  const hasPending = _mxmChatPendingFiles && _mxmChatPendingFiles.length > 0;
  const showSend = hasText || hasTracks || hasPending;
  send.classList.toggle('hidden', !showSend);
  voice.classList.toggle('hidden', showSend);
}

window.mxmChatTyping = function () {
  if (!_mxmChatWs || _mxmChatWs.readyState !== 1 || !_mxmChatActivePeerId) return;
  try {
    _mxmChatWs.send(JSON.stringify({ type: 'typing', peerUserId: _mxmChatActivePeerId, active: true }));
    clearTimeout(mxmChatTyping._debounce);
    mxmChatTyping._debounce = setTimeout(() => {
      try { _mxmChatWs.send(JSON.stringify({ type: 'typing', peerUserId: _mxmChatActivePeerId, active: false })); } catch (_) {}
    }, 1200);
  } catch (_) {}
};

window.mxmChatSendText = async function () {
  const ta = $('mxm-chat-input');
  if (!ta || !_mxmChatActivePeerId) return;
  const text = String(ta.value || '').trim();
  const hasTracks = _mxmChatSelectedTracks && _mxmChatSelectedTracks.length > 0;
  const hasPending = _mxmChatPendingFiles && _mxmChatPendingFiles.length > 0;
  if (!text && !hasTracks && !hasPending) return;

  const replyId = _mxmChatReplyTo ? _mxmChatReplyTo.id : null;
  const keepComposerFocused = () => {
    mxmChatFocusComposer();
    requestAnimationFrame(() => mxmChatFocusComposer());
  };

  if (_mxmChatEditMsg) {
    const editId = _mxmChatEditMsg.id;
    _mxmChatEditMsg = null;
    $('mxm-chat-reply-bar')?.classList.add('hidden');
    mxmChatResetComposerInput(ta, true);
    mxmChatToggleSendVoice();
    keepComposerFocused();
    try {
      const res = await fetch(resolveClientRequestUrl(`/api/chat/messages/${editId}`), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${state.token}` },
        body: JSON.stringify({ text })
      });
      const msg = await res.json();
      if (msg && msg.id) {
        const c = _mxmChatMessagesCache.find((m) => m.id === msg.id);
        if (c) Object.assign(c, msg);
        const merged = _mxmChatMessagesCache.find((m) => m.id === msg.id) || msg;
        mxmChatEnsureReplyPreview(merged);
        mxmChatUpdateMsgDom(merged);
      }
    } catch (e) { toast('Не удалось отредактировать', 'error'); }
    keepComposerFocused();
    return;
  }

  if (hasTracks || hasPending) {
    const batchCaption = String(ta.value || '').trim();
    _mxmChatReplyTo = null;
    $('mxm-chat-reply-bar')?.classList.add('hidden');
    mxmChatResetComposerInput(ta, true);
    mxmChatToggleSendVoice();
    keepComposerFocused();
    await _mxmChatFlushComposerAttachments(batchCaption, replyId);
    keepComposerFocused();
    return;
  }

  const peer = _mxmChatActivePeerId;
  const body = { peerUserId: peer, messageKind: 'text', text };
  if (_mxmChatReplyTo) { body.replyToId = _mxmChatReplyTo.id; _mxmChatReplyTo = null; $('mxm-chat-reply-bar')?.classList.add('hidden'); }
  mxmChatResetComposerInput(ta, true);
  mxmChatToggleSendVoice();
  keepComposerFocused();
  try {
    const msg = await POST('/api/chat/messages', body);
    if (msg && !_mxmChatMessagesCache.some((m) => m.id === msg.id)) {
      _mxmChatMessagesCache.push(msg);
      mxmChatAppendMessageRow(msg);
    }
  } catch (e) { toast(e.message || 'Не отправлено', 'error'); }
  keepComposerFocused();
};

/** Одна отправка: треки, затем подготовленные файлы. Подпись и ответ — только у первого сообщения (как в Telegram). */
async function _mxmChatFlushComposerAttachments(caption = '', replyToId = null) {
  const cap = String(caption || '').trim();
  const rid = replyToId || null;
  let gaveCaption = false;
  let gaveReply = false;
  const takeCaption = () => {
    if (gaveCaption || !cap) return '';
    gaveCaption = true;
    return cap;
  };
  const takeReply = () => {
    if (gaveReply || !rid) return null;
    gaveReply = true;
    return rid;
  };

  const tracks = _mxmChatSelectedTracks.map((id) => _mxmChatAllTracks.find((t) => t.id === id)).filter(Boolean);
  _mxmChatSelectedTracks = [];
  _mxmChatUpdateAttachedPreview();
  $('mxm-chat-tracks-panel')?.classList.add('hidden');

  const pending = _mxmChatPendingFiles.slice();
  _mxmChatPendingFiles.forEach((p) => {
    if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
  });
  _mxmChatPendingFiles = [];
  _mxmChatUpdateAttachedPreview();

  if (tracks.length && pending.length) {
    const first = pending[0];
    const rest = pending.slice(1);
    try {
      const up = await mxmChatUploadChatMediaFile(first.file);
      const kind = up.type === 'voice' ? 'voice' : up.type;
      if (kind === 'voice') throw new Error('Голосовое нельзя объединить с треками');
      await mxmChatPostComboMessage({
        caption: takeCaption(),
        replyToId: takeReply(),
        tracks,
        mediaKind: kind,
        storageKey: up.storageKey,
        fileName: up.fileName,
        fileSize: up.fileSize,
        mimeType: up.mimeType
      });
    } catch (e) {
      toast(e.message || 'Не удалось отправить', 'error');
    }
    for (const p of rest) {
      const c = takeCaption();
      const r = takeReply();
      await mxmChatSendMediaFileInternal(p.file, c, { replyToId: r });
    }
    return;
  }

  for (const t of tracks) {
    const opts = {};
    const c = takeCaption();
    if (c) opts.caption = c;
    const r = takeReply();
    if (r) opts.replyToId = r;
    await mxmChatSendSharePayload('track', t.id, t.title, getTrackArtistText(t), t.coverUrl || '', opts);
  }

  for (const p of pending) {
    const c = takeCaption();
    const r = takeReply();
    await mxmChatSendMediaFileInternal(p.file, c, { replyToId: r });
  }
}

async function mxmChatPostComboMessage({
  caption,
  replyToId,
  tracks,
  mediaKind,
  storageKey,
  fileName,
  fileSize,
  mimeType
}) {
  if (!_mxmChatActivePeerId || !tracks?.length || !storageKey) return;
  const peer = _mxmChatActivePeerId;
  try {
    const body = {
      peerUserId: peer,
      messageKind: 'combo',
      tracks: tracks.map((t) => ({
        shareType: 'track',
        shareId: t.id,
        shareTitle: t.title,
        shareArtist: getTrackArtistText(t),
        shareCover: t.coverUrl || ''
      })),
      media: {
        mediaKind,
        mediaStorageKey: storageKey,
        fileName,
        fileSize,
        mimeType
      }
    };
    const cap = String(caption || '').trim();
    if (cap) {
      const c = cap.slice(0, 4000);
      body.caption = c;
      body.text = c;
    }
    if (replyToId) body.replyToId = replyToId;
    const msg = await POST('/api/chat/messages', body);
    if (msg && msg.id && !_mxmChatMessagesCache.some((m) => m.id === msg.id)) {
      _mxmChatMessagesCache.push(msg);
      mxmChatAppendMessageRow(msg);
    }
  } catch (e) {
    toast(e.message || 'Не отправлено', 'error');
  }
}

async function mxmChatSendSharePayload(shareType, shareId, shareName, shareArtist, shareCover, opts = {}) {
  if (!_mxmChatActivePeerId) return;
  const peer = _mxmChatActivePeerId;
  try {
    const body = { peerUserId: peer, messageKind: 'music', shareType, shareId, shareTitle: shareName, shareArtist: shareArtist || '', shareCover: shareCover || '' };
    const cap = String(opts.caption || '').trim();
    if (cap) {
      const c = cap.slice(0, 4000);
      body.caption = c;
      body.text = c;
    }
    if (opts.replyToId) body.replyToId = opts.replyToId;
    const msg = await POST('/api/chat/messages', body);
    if (msg && !_mxmChatMessagesCache.some((m) => m.id === msg.id)) {
      _mxmChatMessagesCache.push(msg);
      mxmChatAppendMessageRow(msg);
    }
  } catch (e) { toast(e.message || 'Не отправлено', 'error'); }
}

function mxmChatConfirm(title, text, confirmLabel) {
  return new Promise((resolve) => {
    let overlay = document.getElementById('mxm-chat-confirm-overlay');
    if (overlay) overlay.remove();
    overlay = document.createElement('div');
    overlay.id = 'mxm-chat-confirm-overlay';
    overlay.className = 'mxm-modal-overlay';
    overlay.innerHTML = `<div class="mxm-modal-box mxm-confirm-box" role="dialog" aria-modal="true">
      <div class="mxm-modal-head"><span class="mxm-modal-title">${esc(title)}</span></div>
      <p class="mxm-confirm-text">${esc(text)}</p>
      <div class="mxm-confirm-actions">
        <button type="button" class="btn-ghost mxm-confirm-cancel">Отмена</button>
        <button type="button" class="btn-danger mxm-confirm-ok">${esc(confirmLabel || 'Подтвердить')}</button>
      </div>
    </div>`;
    document.body.appendChild(overlay);
    const close = (result) => { overlay.remove(); resolve(result); };
    overlay.querySelector('.mxm-confirm-cancel').addEventListener('click', () => close(false));
    overlay.querySelector('.mxm-confirm-ok').addEventListener('click', () => close(true));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(false); });
  });
}

window.mxmChatBlockPeer = async function () {
  if (!_mxmChatActivePeerId) return;
  if (_mxmChatPeerBlocked) {
    const ok = await mxmChatConfirm('Разблокировать', 'Разблокировать этого пользователя?', 'Разблокировать');
    if (!ok) return;
    try {
      await fetch(`/api/chat/block/${encodeURIComponent(_mxmChatActivePeerId)}`, { method: 'DELETE', credentials: 'include' });
      _mxmChatPeerBlocked = false;
      mxmChatUpdateBlockBtn();
      toast('Пользователь разблокирован', 'success');
    } catch (e) { toast(e.message || 'Ошибка', 'error'); }
  } else {
    const ok = await mxmChatConfirm('Заблокировать', 'Заблокировать этого пользователя?', 'Заблокировать');
    if (!ok) return;
    try {
      await POST('/api/chat/block', { userId: _mxmChatActivePeerId });
      _mxmChatPeerBlocked = true;
      mxmChatUpdateBlockBtn();
      toast('Пользователь заблокирован', 'success');
    } catch (e) { toast(e.message || 'Ошибка', 'error'); }
  }
};

function mxmChatUpdateBlockBtn() {
  const btn = document.getElementById('mxm-chat-thread-menu-block');
  const label = document.getElementById('mxm-chat-thread-menu-block-label');
  const icon = document.getElementById('mxm-chat-thread-menu-block-icon');
  if (btn) btn.title = _mxmChatPeerBlocked ? 'Разблокировать' : 'Заблокировать';
  if (label) label.textContent = _mxmChatPeerBlocked ? 'Разблокировать' : 'Заблокировать';
  if (icon) icon.innerHTML = mxmChatThreadMenuBlockIcon();
  mxmChatUpdateInputAccess();
}

function mxmChatUpdateInputAccess() {
  const tools = $('mxm-chat-tools');
  if (!tools) return;
  let notice = tools.querySelector('.mxm-chat-blocked-notice');
  const blocked = _mxmChatPeerBlocked;
  const notFriend = !_mxmChatPeerIsFriend;
  const disabled = blocked || notFriend;
  if (disabled) {
    tools.querySelector('.mxm-chat-input-row')?.classList.add('hidden');
    tools.querySelector('.mxm-chat-panels-slot')?.classList.add('hidden');
    if (typeof mxmChatEmojiCloseImmediate === 'function') mxmChatEmojiCloseImmediate();
    if (typeof mxmChatCloseTracksPanel === 'function') mxmChatCloseTracksPanel({ immediate: true });
    const msg = blocked ? 'Пользователь заблокирован' : 'Добавьте в друзья, чтобы написать';
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'mxm-chat-blocked-notice';
      tools.appendChild(notice);
    }
    notice.textContent = msg;
  } else {
    tools.querySelector('.mxm-chat-input-row')?.classList.remove('hidden');
    if (notice) notice.remove();
  }
}

window.mxmChatDeleteChat = async function () {
  if (!_mxmChatActivePeerId) return;
  const choice = await mxmChatConfirmDeleteChat();
  if (!choice) return;
  try {
    await mxmChatApiDeleteConversation(_mxmChatActivePeerId, choice);
    _mxmChatMessagesCache = [];
    mxmChatResetThreadUi();
    mxmChatRefreshSidebar();
    toast(choice === 'both' ? 'Чат удалён у обоих' : 'Чат удалён', 'success');
  } catch (e) { toast(e.message || 'Ошибка', 'error'); }
};

function mxmChatConfirmDeleteChat() {
  return new Promise((resolve) => {
    let overlay = document.getElementById('mxm-chat-confirm-overlay');
    if (overlay) overlay.remove();
    overlay = document.createElement('div');
    overlay.id = 'mxm-chat-confirm-overlay';
    overlay.className = 'mxm-modal-overlay';
    overlay.innerHTML = `<div class="mxm-modal-box mxm-confirm-box" role="dialog" aria-modal="true">
      <div class="mxm-modal-head"><span class="mxm-modal-title">Удалить чат</span></div>
      <p class="mxm-confirm-text">Как вы хотите удалить этот чат?</p>
      <div class="mxm-confirm-actions" style="flex-direction:column;gap:8px">
        <button type="button" class="btn-danger mxm-del-self" style="width:100%">Удалить у себя</button>
        <button type="button" class="btn-danger mxm-del-both" style="width:100%">Удалить у обоих</button>
        <button type="button" class="btn-ghost mxm-del-cancel" style="width:100%">Отмена</button>
      </div>
    </div>`;
    document.body.appendChild(overlay);
    const close = (v) => { overlay.remove(); resolve(v); };
    overlay.querySelector('.mxm-del-self').addEventListener('click', () => close('self'));
    overlay.querySelector('.mxm-del-both').addEventListener('click', () => close('both'));
    overlay.querySelector('.mxm-del-cancel').addEventListener('click', () => close(null));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(null); });
  });
}

/* ── Emoji ──────────────────────────────────────────────────────────────────── */
let _emojiPickerModulePromise = null;
function ensureEmojiPickerLoaded() {
  try {
    if (typeof customElements !== 'undefined' && customElements.get('emoji-picker')) {
      return Promise.resolve();
    }
  } catch (_) {}
  if (!_emojiPickerModulePromise) {
    _emojiPickerModulePromise = import('https://cdn.jsdelivr.net/npm/emoji-picker-element@1.26.3/+esm').catch((err) => {
      _emojiPickerModulePromise = null;
      return Promise.reject(err);
    });
  }
  return _emojiPickerModulePromise;
}

function mxmChatBlurEmojiPickerFocus(slot) {
  if (!slot) return;
  try {
    const picker = slot.querySelector('emoji-picker');
    const sr = picker && picker.shadowRoot;
    const inner = sr && sr.activeElement;
    if (inner && typeof inner.blur === 'function') inner.blur();
    if (document.activeElement && slot.contains(document.activeElement) && document.activeElement !== $('mxm-chat-input')) {
      document.activeElement.blur();
    }
  } catch (_) {}
}

function mxmChatGetEmojiPortalHost() {
  let host = document.getElementById('mxm-chat-emoji-portal');
  if (!host) {
    host = document.createElement('div');
    host.id = 'mxm-chat-emoji-portal';
    host.className = 'mxm-chat-emoji-portal';
    document.body.appendChild(host);
  }
  return host;
}

function mxmChatClearEmojiPortalHost() {
  const host = document.getElementById('mxm-chat-emoji-portal');
  if (host) host.innerHTML = '';
}

function mxmChatRefreshComposerChrome() {
  try {
    syncMobileViewportInsets();
    requestAnimationFrame(() => syncMobileViewportInsets());
    setTimeout(() => syncMobileViewportInsets(), 120);
  } catch (_) {}
}

function mxmChatTeardownEmojiPositionListeners() {
  const slot = $('mxm-chat-emoji-slot');
  if (!slot || !slot._mxmEmojiPosCleanup) return;
  try {
    slot._mxmEmojiPosCleanup();
  } catch (_) {}
  slot._mxmEmojiPosCleanup = null;
}

function mxmChatPositionEmojiAnchoredPanel() {
  const slot = $('mxm-chat-emoji-slot');
  const btn = document.querySelector('.mxm-chat-emoji-btn');
  const sheet =
    document.querySelector('#mxm-chat-emoji-portal .mxm-chat-emoji-sheet.mxm-chat-emoji-sheet--anchored') ||
    slot?.querySelector('.mxm-chat-emoji-sheet.mxm-chat-emoji-sheet--anchored');
  if (!btn || !sheet) return;

  const r = btn.getBoundingClientRect();
  const gap = 8;
  const topPad = Math.max(8, (window.visualViewport && window.visualViewport.offsetTop) || 0);
  // visualViewport: корректные размеры при открытой клавиатуре / iOS chrome
  let viewportH = window.innerHeight;
  let viewportW = window.innerWidth;
  let offsetTop = 0;
  let offsetLeft = 0;
  try {
    const vv = window.visualViewport;
    if (vv && vv.height > 0) {
      viewportH = vv.height;
      viewportW = vv.width;
      offsetTop = vv.offsetTop || 0;
      offsetLeft = vv.offsetLeft || 0;
    }
  } catch (_) {}

  const fromBottom = Math.max(4, offsetTop + viewportH - r.bottom + gap);
  const fromRight = Math.max(8, offsetLeft + viewportW - r.right);
  const roomAbove = Math.max(120, r.top - offsetTop - gap - topPad);
  const cap = Math.min(viewportH * 0.5, 360);
  const maxH = Math.max(180, Math.min(roomAbove, cap));

  sheet.style.left = 'auto';
  sheet.style.top = 'auto';
  sheet.style.right = `${fromRight}px`;
  sheet.style.bottom = `${fromBottom}px`;
  sheet.style.width = `min(336px, calc(100vw - 20px))`;
  sheet.style.maxHeight = `${Math.round(maxH)}px`;
}

function mxmChatBindEmojiPositionListeners() {
  const slot = $('mxm-chat-emoji-slot');
  if (!slot) return;
  mxmChatTeardownEmojiPositionListeners();
  const onGeom = () => {
    mxmChatPositionEmojiAnchoredPanel();
  };
  window.addEventListener('resize', onGeom, { passive: true });
  let vv;
  try {
    vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', onGeom, { passive: true });
      vv.addEventListener('scroll', onGeom, { passive: true });
    }
  } catch (_) {}
  slot._mxmEmojiPosCleanup = () => {
    window.removeEventListener('resize', onGeom);
    try {
      if (vv) {
        vv.removeEventListener('resize', onGeom);
        vv.removeEventListener('scroll', onGeom);
      }
    } catch (_) {}
  };
}

function mxmChatBindSheetSwipeDismiss(root, sheet, closeFn, handleSelector) {
  if (!root || !sheet || sheet.dataset.mxmSwipeDismissBound === '1') return;
  sheet.dataset.mxmSwipeDismissBound = '1';
  let startX = 0;
  let startY = 0;
  let tracking = false;
  let lastDy = 0;
  const backdrop = () => root.querySelector('.mxm-chat-emoji-backdrop, .mxm-chat-tracks-backdrop');

  sheet.addEventListener('touchstart', (e) => {
    if (!_mxmChatIsMobile()) return;
    if (handleSelector && !e.target.closest(handleSelector)) return;
    if (e.target.closest('input, textarea, button')) return;
    const t = e.touches[0];
    startX = t.clientX;
    startY = t.clientY;
    tracking = true;
    lastDy = 0;
    sheet.style.transition = 'none';
  }, { passive: true });

  sheet.addEventListener('touchmove', (e) => {
    if (!tracking) return;
    const t = e.touches[0];
    const dx = Math.abs(t.clientX - startX);
    const dy = t.clientY - startY;
    if (dx > Math.abs(dy) * 1.15) {
      tracking = false;
      sheet.style.transition = '';
      sheet.style.transform = '';
      const back = backdrop();
      if (back) back.style.opacity = '';
      return;
    }
    if (dy <= 0) return;
    lastDy = dy;
    e.preventDefault();
    sheet.style.transform = `translate3d(0, ${dy}px, 0)`;
    const back = backdrop();
    if (back) back.style.opacity = String(Math.max(0, 1 - dy / 240));
  }, { passive: false });

  const finish = () => {
    if (!tracking) return;
    tracking = false;
    sheet.style.transition = '';
    sheet.style.transform = '';
    const back = backdrop();
    if (back) back.style.opacity = '';
    if (lastDy > 84) closeFn();
  };

  sheet.addEventListener('touchend', finish, { passive: true });
  sheet.addEventListener('touchcancel', finish, { passive: true });
}

function mxmChatEmojiCloseImmediate() {
  const scrollPre = $('mxm-chat-thread-scroll');
  const wasNearBottom = scrollPre ? mxmChatThreadNearBottom(scrollPre, 200) : true;
  const preserveTop = scrollPre ? scrollPre.scrollTop : 0;
  const slot = $('mxm-chat-emoji-slot');
  if (!slot) return;
  mxmChatTeardownEmojiPositionListeners();
  mxmChatBlurEmojiPickerFocus(slot);
  mxmChatBlurEmojiPickerFocus(document.getElementById('mxm-chat-emoji-portal'));
  const t = slot._mxmEmojiCloseTimer;
  if (t) clearTimeout(t);
  slot._mxmEmojiCloseTimer = null;
  slot.classList.remove('mxm-chat-emoji-slot--open', 'mxm-chat-panels-slot--emoji-mobile');
  slot.classList.add('hidden');
  slot.innerHTML = '';
  mxmChatClearEmojiPortalHost();
  mxmChatRefreshComposerChrome();
  mxmChatStabilizeThreadAfterComposerChange(wasNearBottom, preserveTop);
}

function mxmChatEmojiClose() {
  const slot = $('mxm-chat-emoji-slot');
  if (!slot) return;
  const scrollPre = $('mxm-chat-thread-scroll');
  const wasNearBottom = scrollPre ? mxmChatThreadNearBottom(scrollPre, 200) : true;
  const preserveTop = scrollPre ? scrollPre.scrollTop : 0;
  const prevT = slot._mxmEmojiCloseTimer;
  if (prevT) clearTimeout(prevT);
  const isMobileSheet = slot.classList.contains('mxm-chat-panels-slot--emoji-mobile');
  const sheet =
    document.querySelector('#mxm-chat-emoji-portal .mxm-chat-emoji-sheet') ||
    slot.querySelector('.mxm-chat-emoji-sheet');
  const reducedMotion =
    typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cleanup = () => {
    slot._mxmEmojiCloseTimer = null;
    mxmChatTeardownEmojiPositionListeners();
    slot.classList.remove('mxm-chat-emoji-slot--open', 'mxm-chat-panels-slot--emoji-mobile');
    slot.classList.add('hidden');
    slot.innerHTML = '';
    mxmChatClearEmojiPortalHost();
    mxmChatRefreshComposerChrome();
    mxmChatStabilizeThreadAfterComposerChange(wasNearBottom, preserveTop);
  };

  slot.classList.remove('mxm-chat-emoji-slot--open');
  document.getElementById('mxm-chat-emoji-portal')?.classList.remove('mxm-chat-emoji-slot--open');
  mxmChatBlurEmojiPickerFocus(slot);
  mxmChatBlurEmojiPickerFocus(document.getElementById('mxm-chat-emoji-portal'));
  mxmChatRefreshComposerChrome();

  if (isMobileSheet && sheet && !reducedMotion) {
    const onEnd = (e) => {
      if (e.target !== sheet || e.propertyName !== 'transform') return;
      sheet.removeEventListener('transitionend', onEnd);
      cleanup();
    };
    sheet.addEventListener('transitionend', onEnd);
    slot._mxmEmojiCloseTimer = setTimeout(() => {
      sheet.removeEventListener('transitionend', onEnd);
      cleanup();
    }, 420);
    return;
  }

  slot._mxmEmojiCloseTimer = setTimeout(cleanup, isMobileSheet ? 360 : 220);
}

window.mxmChatToggleEmoji = async function () {
  const slot = $('mxm-chat-emoji-slot');
  if (!slot) return;
  $('mxm-chat-tracks-panel')?.classList.add('hidden');
  const portalOpen = !!document.querySelector('#mxm-chat-emoji-portal emoji-picker');
  if (slot.querySelector('emoji-picker') || portalOpen) {
    // Повторный тап по кнопке должен закрывать без задержки.
    mxmChatEmojiCloseImmediate();
    return;
  }
  try {
    await ensureEmojiPickerLoaded();
  } catch (e) {
    toast(e?.message || 'Не удалось загрузить панель эмодзи', 'error');
    return;
  }
  const t = slot._mxmEmojiCloseTimer;
  if (t) clearTimeout(t);
  slot._mxmEmojiCloseTimer = null;
  slot.innerHTML = '';
  mxmChatClearEmojiPortalHost();
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 900;
  const scrollPreEmoji = $('mxm-chat-thread-scroll');
  const wasNearBottomEmoji = scrollPreEmoji ? mxmChatThreadNearBottom(scrollPreEmoji, 200) : true;
  const preserveTopEmoji = scrollPreEmoji ? scrollPreEmoji.scrollTop : 0;
  const onPick = (e, closeAfter) => {
    const ta = $('mxm-chat-input');
    const u = e.detail?.unicode || e.detail?.emoji?.unicode;
    if (ta && u) {
      ta.value = (ta.value || '') + u;
      ta.dispatchEvent(new Event('input', { bubbles: true }));
      mxmChatFocusComposer();
    }
    if (closeAfter) mxmChatEmojiClose();
  };
  const picker = document.createElement('emoji-picker');
  picker.classList.add('mxm-emoji-picker');
  picker.addEventListener('emoji-click', (e) => onPick(e, isMobile));
  if (isMobile) {
    // Нижний sheet на body (не popover): ровная сетка + нормальный скролл внутри emoji-picker
    slot.classList.add('mxm-chat-panels-slot--emoji-mobile');
    picker.classList.add('mxm-emoji-picker--sheet');
    const portal = mxmChatGetEmojiPortalHost();
    const back = document.createElement('div');
    back.className = 'mxm-chat-emoji-backdrop';
    back.addEventListener('click', () => mxmChatEmojiClose());
    const sheet = document.createElement('div');
    sheet.className = 'mxm-chat-emoji-sheet mxm-chat-emoji-sheet--mobile';
    sheet.addEventListener('click', (ev) => ev.stopPropagation());
    const head = document.createElement('div');
    head.className = 'mxm-chat-emoji-sheet-head';
    head.innerHTML = '<span class="mxm-chat-sheet-grabber" aria-hidden="true"></span><span class="mxm-chat-emoji-sheet-title">Эмодзи</span><button type="button" class="mxm-chat-emoji-sheet-close" aria-label="Закрыть">&times;</button>';
    head.querySelector('.mxm-chat-emoji-sheet-close').addEventListener('click', (ev) => {
      ev.stopPropagation();
      mxmChatEmojiClose();
    });
    sheet.appendChild(head);
    sheet.appendChild(picker);
    portal.appendChild(back);
    portal.appendChild(sheet);
    mxmChatBindSheetSwipeDismiss(portal, sheet, mxmChatEmojiClose, '.mxm-chat-emoji-sheet-head');
  } else {
    slot.appendChild(picker);
  }
  slot.classList.remove('hidden');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      slot.classList.add('mxm-chat-emoji-slot--open');
      if (isMobile) {
        document.getElementById('mxm-chat-emoji-portal')?.classList.add('mxm-chat-emoji-slot--open');
      }
      mxmChatStabilizeThreadAfterComposerChange(wasNearBottomEmoji, preserveTopEmoji);
    });
  });
};

/* ── Share tracks panel ────────────────────────────────────────────────────── */
let _mxmChatPendingFiles = []; // { file, previewUrl } — вложение до отправки (с подписью из поля)
let _mxmChatSelectedTracks = [];
let _mxmChatAllTracks = [];
let _mxmChatAllTracksReady = false;
let _mxmChatFavIds = new Set();
let _mxmChatTrackSearchDebounce = null;
let _mxmChatTrackListRenderQuery = '';
let _mxmChatTracksPanelFetchGen = 0;
/** Инкремент рендера списка: отменяет отложенную догрузку строк при новом фильтре */
let _mxmChatRenderTrackListGen = 0;
/** Ниже порога — полный рендер; выше — виртуальный список (плавный скролл) */
const MXM_CHAT_ATTACH_VIRTUAL_THRESHOLD = 32;
const MXM_CHAT_ATTACH_VIRTUAL_OVERSCAN = 10;
let _mxmChatFilteredForList = [];
let _mxmChatVirtualScrollRaf = null;
let _mxmChatVirtualListEl = null;
let _mxmChatVirtualResizeT = null;
window.addEventListener(
  'resize',
  () => {
    clearTimeout(_mxmChatVirtualResizeT);
    _mxmChatVirtualResizeT = setTimeout(() => {
      const p = $('mxm-chat-tracks-panel');
      const le = $('mxm-chat-tracks-list');
      if (!p || p.classList.contains('hidden') || !le) return;
      if (!_mxmChatFilteredForList || _mxmChatFilteredForList.length < MXM_CHAT_ATTACH_VIRTUAL_THRESHOLD) return;
      le._mxmVirtWin = null;
      _mxmChatVirtualPaintWindow(le, le.scrollTop);
    }, 100);
  },
  { passive: true }
);

function _mxmChatTracksSkeletonRowsHtml(count) {
  const n = Math.min(Math.max(count | 0, 8), 18);
  return Array.from({ length: n }, () =>
    '<div class="mxm-chat-track-row mxm-chat-track-row--skeleton" aria-hidden="true"><span class="mxm-chat-track-skel-cb"></span><span class="mxm-chat-track-skel-cover"></span><span class="mxm-chat-track-skel-lines"><span class="mxm-chat-track-skel-t"></span><span class="mxm-chat-track-skel-a"></span></span></div>'
  ).join('');
}

function _mxmChatBindTracksPanelSearch(searchEl) {
  if (!searchEl || searchEl.dataset.mxmTracksSearchBound === '1') return;
  searchEl.dataset.mxmTracksSearchBound = '1';
  searchEl.addEventListener('input', () => {
    clearTimeout(_mxmChatTrackSearchDebounce);
    _mxmChatTrackSearchDebounce = setTimeout(() => {
      if (!_mxmChatAllTracksReady) return;
      _mxmChatRenderTrackList();
    }, 160);
  });
}

function mxmChatAttachTracksPanelToBodyIfNeeded() {
  const pan = $('mxm-chat-tracks-panel');
  if (!pan || !_mxmChatIsMobile()) return;
  if (pan.parentElement === document.body) return;
  _mxmChatTracksPanelAnchor = pan.parentElement;
  document.body.appendChild(pan);
}

function mxmChatRestoreTracksPanelFromBody() {
  const pan = $('mxm-chat-tracks-panel');
  if (!pan || !_mxmChatTracksPanelAnchor) return;
  try {
    _mxmChatTracksPanelAnchor.appendChild(pan);
  } catch (_) {}
  _mxmChatTracksPanelAnchor = null;
}

window.addEventListener(
  'resize',
  () => {
    if (_mxmChatIsMobile()) return;
    mxmChatRestoreTracksPanelFromBody();
  },
  { passive: true }
);

async function _mxmChatFetchTracksForSharePanel(gen) {
  const pan = $('mxm-chat-tracks-panel');
  try {
    const allArr = await GET('/api/tracks/chat-attach');
    if (gen !== _mxmChatTracksPanelFetchGen) return;
    if (!pan || pan.classList.contains('hidden')) return;

    _mxmChatAllTracks = Array.isArray(allArr) ? allArr : [];
    _mxmChatFavIds = new Set(_mxmChatAllTracks.filter((t) => t && t.isFav).map((t) => t.id));
    _mxmChatAllTracksReady = true;
    _mxmChatSortTracksFavFirst();
    _mxmChatRenderTrackList();
  } catch (e) {
    if (gen !== _mxmChatTracksPanelFetchGen) return;
    if (pan && !pan.classList.contains('hidden')) {
      pan.innerHTML = `<div class="mxm-chat-tracks-empty" style="padding:24px">${esc(e.message || 'Ошибка')}</div>`;
    }
  }
}

function mxmChatCloseTracksPanel({ immediate = false } = {}) {
  const pan = $('mxm-chat-tracks-panel');
  if (!pan) return;
  if (_mxmChatVirtualScrollRaf != null) {
    cancelAnimationFrame(_mxmChatVirtualScrollRaf);
    _mxmChatVirtualScrollRaf = null;
  }
  const cleanup = () => {
    clearTimeout(_mxmChatTracksCloseTimer);
    _mxmChatTracksCloseTimer = null;
    pan.classList.add('hidden');
    pan.classList.remove('mxm-chat-tracks-panel--mobile', 'mxm-chat-tracks-panel--open');
    pan.innerHTML = '';
    _mxmChatTrackListRenderQuery = '';
    _mxmChatFilteredForList = [];
    _mxmChatTracksPanelFetchGen++;
    mxmChatRefreshComposerChrome();
    mxmChatRestoreTracksPanelFromBody();
  };
  if (!_mxmChatIsMobile() || immediate || !pan.classList.contains('mxm-chat-tracks-panel--mobile')) {
    cleanup();
    return;
  }
  pan.classList.remove('mxm-chat-tracks-panel--open');
  _mxmChatTracksCloseTimer = setTimeout(cleanup, 320);
}

function _mxmChatMountTracksPanelChrome(loading) {
  const pan = $('mxm-chat-tracks-panel');
  if (!pan) return;
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 900;
  const skelN = isMobile ? 8 : 10;
  const listInner = loading
    ? `${_mxmChatTracksSkeletonRowsHtml(skelN)}<div class="mxm-chat-tracks-loading-inline">Загрузка каталога…</div>`
    : '';
  const listCls = loading ? 'mxm-chat-tracks-list mxm-chat-tracks-list--loading' : 'mxm-chat-tracks-list';
  const chrome = `<div class="mxm-chat-tracks-head">
    ${isMobile ? '<span class="mxm-chat-sheet-grabber" aria-hidden="true"></span>' : ''}
    <input type="text" class="mxm-chat-tracks-search" id="mxm-chat-tracks-search" placeholder="Поиск треков…" value="" autocomplete="off" spellcheck="false"/>
    <span class="mxm-chat-tracks-count" id="mxm-chat-tracks-count">${_mxmChatSelectedTracks.length ? _mxmChatSelectedTracks.length + '/10' : ''}</span>
  </div>
  <div class="${listCls}" id="mxm-chat-tracks-list">${listInner}</div>`;
  if (isMobile) {
    pan.classList.add('mxm-chat-tracks-panel--mobile');
    pan.innerHTML = `<div class="mxm-chat-tracks-backdrop"></div><div class="mxm-chat-tracks-sheet">${chrome}</div>`;
    pan.querySelector('.mxm-chat-tracks-backdrop')?.addEventListener('click', () => mxmChatCloseTracksPanel());
    const sheet = pan.querySelector('.mxm-chat-tracks-sheet');
    mxmChatBindSheetSwipeDismiss(pan, sheet, () => mxmChatCloseTracksPanel(), '.mxm-chat-tracks-head');
    requestAnimationFrame(() => pan.classList.add('mxm-chat-tracks-panel--open'));
  } else {
    pan.classList.remove('mxm-chat-tracks-panel--mobile', 'mxm-chat-tracks-panel--open');
    pan.innerHTML = chrome;
  }
  _mxmChatBindTracksPanelSearch($('mxm-chat-tracks-search'));
}

function _mxmChatMountTracksLoadingShell() {
  _mxmChatMountTracksPanelChrome(true);
}

window.mxmChatToggleTracks = function () {
  const pan = $('mxm-chat-tracks-panel');
  if (!pan) return;
  mxmChatEmojiCloseImmediate();
  if (!pan.classList.contains('hidden')) {
    mxmChatCloseTracksPanel();
    return;
  }
  clearTimeout(_mxmChatTracksCloseTimer);
  mxmChatAttachTracksPanelToBodyIfNeeded();
  pan.classList.remove('hidden');
  _mxmChatSelectedTracks = [];

  if (_mxmChatAllTracksReady && _mxmChatAllTracks.length) {
    _mxmChatSortTracksFavFirst();
    if (!$('mxm-chat-tracks-search')) {
      _mxmChatMountTracksPanelChrome(false);
    }
    _mxmChatRenderTrackList();
    return;
  }

  _mxmChatTracksPanelFetchGen++;
  const gen = _mxmChatTracksPanelFetchGen;
  _mxmChatMountTracksLoadingShell();
  void _mxmChatFetchTracksForSharePanel(gen);
};

function _mxmChatSortTracksFavFirst() {
  _mxmChatAllTracks.sort((a, b) => {
    const aF = (a.isFav || _mxmChatFavIds.has(a.id)) ? 0 : 1;
    const bF = (b.isFav || _mxmChatFavIds.has(b.id)) ? 0 : 1;
    return aF - bF;
  });
}

function _mxmChatVirtualRowHeightPx() {
  return typeof window !== 'undefined' && window.innerWidth <= 900 ? 60 : 54;
}

function _mxmChatTrackCoverHtml(t) {
  if (!t.coverUrl) return '<span class="mxm-chat-track-cover"></span>';
  const src = esc(resolveMediaUrl(t.coverUrl));
  // Без lazy: в нижнем sheet и виртуальном списке Safari/iOS часто не стартует загрузку.
  return `<span class="mxm-chat-track-cover mxm-chat-track-cover--img"><img src="${src}" alt="" loading="eager" decoding="async" fetchpriority="low"/></span>`;
}

function _mxmChatTrackRowHtml(t, extraClass = '') {
  const checked = _mxmChatSelectedTracks.includes(t.id);
  const fav = !!(t.isFav || _mxmChatFavIds.has(t.id));
  const cls = `mxm-chat-track-row${checked ? ' is-checked' : ''}${extraClass ? ` ${extraClass}` : ''}`;
  return `<label class="${cls}" data-track-id="${esc(t.id)}">
      <span class="mxm-chat-track-cb-wrap"><input type="checkbox" class="mxm-chat-track-cb-real" value="${esc(t.id)}"${checked ? ' checked' : ''} onchange="mxmChatTrackCheckChanged(event)"/><span class="mxm-chat-track-cb-icon"><svg viewBox="0 0 20 20" width="20" height="20"><rect x="1" y="1" width="18" height="18" rx="5" fill="none" stroke="currentColor" stroke-width="1.5" class="mxm-cb-box"/><path d="M5 10l3.5 3.5L15 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mxm-cb-check"/></svg></span></span>
      ${_mxmChatTrackCoverHtml(t)}
      <span class="mxm-chat-track-info"><span class="mxm-chat-track-title">${esc(t.title)}</span><span class="mxm-chat-track-artist">${fav ? '❤️ ' : ''}${esc(getTrackArtistText(t))}</span></span>
    </label>`;
}

function _mxmChatUnbindVirtualScroll(listEl) {
  if (_mxmChatVirtualScrollRaf != null && (!listEl || _mxmChatVirtualListEl === listEl)) {
    cancelAnimationFrame(_mxmChatVirtualScrollRaf);
    _mxmChatVirtualScrollRaf = null;
  }
  if (!listEl || listEl.dataset.mxmVirtualScrollBound !== '1') return;
  listEl.removeEventListener('scroll', _mxmChatOnVirtualListScroll);
  listEl.dataset.mxmVirtualScrollBound = '0';
  if (_mxmChatVirtualListEl === listEl) _mxmChatVirtualListEl = null;
}

function _mxmChatOnVirtualListScroll() {
  if (!_mxmChatFilteredForList || _mxmChatFilteredForList.length < MXM_CHAT_ATTACH_VIRTUAL_THRESHOLD) return;
  const p = $('mxm-chat-tracks-panel');
  if (!p || p.classList.contains('hidden')) return;
  if (_mxmChatVirtualScrollRaf != null) return;
  _mxmChatVirtualScrollRaf = requestAnimationFrame(() => {
    _mxmChatVirtualScrollRaf = null;
    const le = $('mxm-chat-tracks-list');
    if (!le) return;
    _mxmChatVirtualPaintWindow(le, null);
  });
}

function _mxmChatBindVirtualScroll(listEl) {
  if (!listEl || listEl.dataset.mxmVirtualScrollBound === '1') return;
  listEl.dataset.mxmVirtualScrollBound = '1';
  _mxmChatVirtualListEl = listEl;
  listEl.addEventListener('scroll', _mxmChatOnVirtualListScroll, { passive: true });
}

/** Виртуальное окно: slice + translate; preserveScroll = число — выставить scrollTop после отрисовки */
function _mxmChatVirtualPaintWindow(listEl, preserveScroll) {
  const list = _mxmChatFilteredForList;
  const total = list.length;
  if (!listEl || total < MXM_CHAT_ATTACH_VIRTUAL_THRESHOLD) return;

  const ROW = _mxmChatVirtualRowHeightPx();
  const st = typeof preserveScroll === 'number' ? preserveScroll : listEl.scrollTop;
  const vh = Math.max(listEl.clientHeight || 0, 120);
  const rawStart = Math.floor(st / ROW) - MXM_CHAT_ATTACH_VIRTUAL_OVERSCAN;
  const start = Math.max(0, rawStart);
  const rawEnd = Math.ceil((st + vh) / ROW) + MXM_CHAT_ATTACH_VIRTUAL_OVERSCAN;
  const end = Math.min(total, Math.max(start + 1, rawEnd));

  const prev = listEl._mxmVirtWin;
  if (prev && prev.start === start && prev.end === end && prev.total === total && prev.row === ROW) {
    return;
  }
  listEl._mxmVirtWin = { start, end, total, row: ROW };

  const slice = list.slice(start, end);
  const y = start * ROW;
  const totalH = total * ROW;

  listEl.innerHTML = `<div class="mxm-chat-vlist" style="height:${totalH}px;min-height:${totalH}px;position:relative;contain:layout style">
    <div class="mxm-chat-vlist-slice" style="position:absolute;left:0;right:0;top:0;will-change:transform;transform:translate3d(0,${y}px,0)">
      ${slice.map((t) => _mxmChatTrackRowHtml(t, 'mxm-chat-track-row--virtual')).join('')}
    </div>
  </div>`;

  listEl.scrollTop = st;
}

function _mxmChatRenderTrackList() {
  const pan = $('mxm-chat-tracks-panel');
  if (!pan || pan.classList.contains('hidden')) return;
  if (!_mxmChatAllTracksReady) return;
  ++_mxmChatRenderTrackListGen;

  let listEl = $('mxm-chat-tracks-list');
  const searchEl = $('mxm-chat-tracks-search');
  if (!listEl || !searchEl) {
    _mxmChatMountTracksPanelChrome(false);
    listEl = $('mxm-chat-tracks-list');
  }
  if (!listEl || !searchEl) return;

  const q = String(searchEl.value || '').toLowerCase().trim();
  const keepScroll = _mxmChatTrackListRenderQuery === q;
  const savedScrollTop = keepScroll ? listEl.scrollTop : 0;
  _mxmChatTrackListRenderQuery = q;

  let list = _mxmChatAllTracks;
  if (q) {
    list = list.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        (t.artist || '').toLowerCase().includes(q) ||
        getTrackArtistText(t).toLowerCase().includes(q)
    );
  }
  _mxmChatFilteredForList = list;

  listEl.classList.remove('mxm-chat-tracks-list--loading');
  const loadLine = listEl.querySelector('.mxm-chat-tracks-loading-inline');
  if (loadLine) loadLine.remove();

  const cnt = $('mxm-chat-tracks-count');
  if (cnt) cnt.textContent = _mxmChatSelectedTracks.length ? _mxmChatSelectedTracks.length + '/10' : '';

  if (!list.length) {
    _mxmChatUnbindVirtualScroll(listEl);
    listEl._mxmVirtWin = null;
    listEl.innerHTML = '<div class="mxm-chat-tracks-empty">Ничего не найдено</div>';
    return;
  }

  if (list.length >= MXM_CHAT_ATTACH_VIRTUAL_THRESHOLD) {
    _mxmChatBindVirtualScroll(listEl);
    listEl._mxmVirtWin = null;
    const st = keepScroll ? savedScrollTop : 0;
    _mxmChatVirtualPaintWindow(listEl, st);
    requestAnimationFrame(() => {
      const le = $('mxm-chat-tracks-list');
      if (le && _mxmChatFilteredForList.length >= MXM_CHAT_ATTACH_VIRTUAL_THRESHOLD) {
        le.scrollTop = keepScroll ? savedScrollTop : 0;
        _mxmChatVirtualPaintWindow(le, le.scrollTop);
      }
    });
    return;
  }

  _mxmChatUnbindVirtualScroll(listEl);
  listEl._mxmVirtWin = null;
  listEl.innerHTML = list.map((t) => _mxmChatTrackRowHtml(t)).join('');

  if (keepScroll) {
    requestAnimationFrame(() => {
      const le = $('mxm-chat-tracks-list');
      if (le) le.scrollTop = savedScrollTop;
    });
  }
}

window.mxmChatTrackCheckChanged = function (ev) {
  const target = ev && ev.target;
  if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox' || !target.classList.contains('mxm-chat-track-cb-real')) {
    return;
  }
  const id = String(target.value || '').trim();
  if (!id) return;

  if (target.checked) {
    if (_mxmChatSelectedTracks.length >= 10 && !_mxmChatSelectedTracks.includes(id)) {
      target.checked = false;
      toast('Максимум 10 треков', 'error');
      return;
    }
    if (!_mxmChatSelectedTracks.includes(id)) _mxmChatSelectedTracks.push(id);
  } else {
    _mxmChatSelectedTracks = _mxmChatSelectedTracks.filter((x) => x !== id);
  }

  const row = target.closest('.mxm-chat-track-row');
  if (row) row.classList.toggle('is-checked', target.checked);

  const cnt = $('mxm-chat-tracks-count');
  if (cnt) cnt.textContent = _mxmChatSelectedTracks.length ? _mxmChatSelectedTracks.length + '/10' : '';
  _mxmChatUpdateAttachedPreview();
  mxmChatToggleSendVoice();
};

function _mxmChatUpdateAttachedPreview() {
  const hasT = _mxmChatSelectedTracks && _mxmChatSelectedTracks.length > 0;
  const hasF = _mxmChatPendingFiles && _mxmChatPendingFiles.length > 0;
  let bar = $('mxm-chat-attach-preview');
  if (!hasT && !hasF) {
    if (bar) {
      bar.innerHTML = '';
      bar.classList.add('hidden');
    }
    mxmChatToggleSendVoice();
    return;
  }
  if (!bar) {
    bar = document.createElement('div');
    bar.id = 'mxm-chat-attach-preview';
    bar.className = 'mxm-chat-attach-preview';
    const inputRow = document.querySelector('.mxm-chat-input-row');
    if (inputRow) inputRow.parentNode.insertBefore(bar, inputRow);
  }
  bar.classList.remove('hidden');
  const parts = [];
  if (hasT) {
    const tracks = _mxmChatSelectedTracks.map((id) => _mxmChatAllTracks.find((t) => t.id === id)).filter(Boolean);
    for (const t of tracks) {
      const coverBg = t.coverUrl
        ? `url('${esc(resolveMediaUrl(t.coverUrl))}')`
        : 'none';
      parts.push(
        `<span class="mxm-attach-chip"><span class="mxm-attach-chip-cover" style="background-image:${coverBg}"></span><span class="mxm-attach-chip-name">${esc(t.title)}</span><button type="button" class="mxm-attach-chip-x" onclick="mxmChatDetachTrack('${esc(t.id)}')">&times;</button></span>`
      );
    }
  }
  if (hasF) {
    _mxmChatPendingFiles.forEach((p, i) => {
      const name = esc(p.file.name || 'Файл');
      const bg = p.previewUrl ? ` style="background-image:url('${esc(p.previewUrl)}')"` : '';
      parts.push(
        `<span class="mxm-attach-chip mxm-attach-chip--file"><span class="mxm-attach-chip-cover"${bg}></span><span class="mxm-attach-chip-name">${name}</span><button type="button" class="mxm-attach-chip-x" onclick="mxmChatDetachPendingFile(${i})">&times;</button></span>`
      );
    });
  }
  bar.innerHTML = parts.join('');
  mxmChatToggleSendVoice();
}

window.mxmChatDetachPendingFile = function (idx) {
  if (idx == null || _mxmChatPendingFiles.length <= 1) {
    _mxmChatPendingFiles.forEach((p) => {
      if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    });
    _mxmChatPendingFiles = [];
  } else {
    const p = _mxmChatPendingFiles[idx];
    if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
    _mxmChatPendingFiles.splice(idx, 1);
  }
  _mxmChatUpdateAttachedPreview();
  mxmChatToggleSendVoice();
};

window.mxmChatDetachTrack = function (id) {
  _mxmChatSelectedTracks = _mxmChatSelectedTracks.filter((t) => t !== id);
  const cb = document.querySelector(`.mxm-chat-track-cb-real[value="${id}"]`);
  if (cb) { cb.checked = false; cb.closest('.mxm-chat-track-row')?.classList.remove('is-checked'); }
  const cnt = $('mxm-chat-tracks-count');
  if (cnt) cnt.textContent = _mxmChatSelectedTracks.length ? _mxmChatSelectedTracks.length + '/10' : '';
  _mxmChatUpdateAttachedPreview();
  mxmChatToggleSendVoice();
};

/* ── Mini player ───────────────────────────────────────────────────────────── */
function mxmChatBindMiniPlayerInteractions() {
  const cover = $('mxm-mini-cover');
  if (!cover || cover.dataset.mxmMiniCoverBound === '1') return;
  cover.dataset.mxmMiniCoverBound = '1';
  cover.setAttribute('role', 'button');
  cover.setAttribute('tabindex', '0');
  cover.setAttribute('aria-label', 'Полноэкранный плеер');
  const isDesktopMini = () =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(min-width: 901px)').matches;
  const onActivate = (e) => {
    if (!isDesktopMini()) return;
    e.preventDefault();
    e.stopPropagation();
    if (!state.currentTrack) return;
    openFullscreenPlayer();
  };
  cover.addEventListener('click', onActivate);
  cover.addEventListener('keydown', (e) => {
    if (!isDesktopMini()) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate(e);
    }
  });
}

function mxmChatStartMiniPlayer() {
  mxmChatBindMiniPlayerInteractions();
  mxmChatUpdateMiniPlayer();
  mxmChatStopMiniPlayer();
  _mxmChatMiniPlayerTimer = setInterval(mxmChatUpdateMiniPlayer, 1000);
  audio.addEventListener('play', mxmChatUpdateMiniPlayer);
  audio.addEventListener('pause', mxmChatUpdateMiniPlayer);
}

function mxmChatStopMiniPlayer() {
  if (_mxmChatMiniPlayerTimer) { clearInterval(_mxmChatMiniPlayerTimer); _mxmChatMiniPlayerTimer = null; }
  audio.removeEventListener('play', mxmChatUpdateMiniPlayer);
  audio.removeEventListener('pause', mxmChatUpdateMiniPlayer);
}

function mxmChatMiniPlayerExpand(wrap) {
  if (!wrap) return;
  try {
    clearTimeout(wrap._mxmMiniHideTimer);
  } catch (_) {}
  wrap._mxmMiniHideTimer = null;
  const needsEnter = wrap.classList.contains('hidden') || wrap.classList.contains('mxm-chat-mini-player--collapsed');
  wrap.classList.remove('hidden');
  if (needsEnter) {
    wrap.classList.add('mxm-chat-mini-player--collapsed');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => wrap.classList.remove('mxm-chat-mini-player--collapsed'));
    });
  }
}

function mxmChatMiniPlayerCollapse(wrap) {
  if (!wrap) return;
  try {
    clearTimeout(wrap._mxmMiniHideTimer);
  } catch (_) {}
  wrap._mxmMiniHideTimer = null;
  if (wrap.classList.contains('hidden')) return;
  wrap.classList.remove('hidden');
  wrap.classList.add('mxm-chat-mini-player--collapsed');
  wrap._mxmMiniHideTimer = setTimeout(() => {
    wrap.classList.add('hidden');
    wrap._mxmMiniHideTimer = null;
  }, 420);
}

function mxmChatUpdateMiniPlayer() {
  const wrap = $('mxm-chat-mini-player');
  if (!wrap) return;
  const t = state.currentTrack;
  if (!t) {
    mxmChatMiniPlayerCollapse(wrap);
    return;
  }
  mxmChatMiniPlayerExpand(wrap);
  const titleEl = $('mxm-mini-title');
  const artistEl = $('mxm-mini-artist');
  const coverEl = $('mxm-mini-cover');
  const progressEl = $('mxm-mini-progress-bar');
  const playBtn = $('mxm-mini-play');
  if (titleEl) titleEl.textContent = t.title || '';
  if (artistEl) {
    const desktop =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(min-width: 901px)').matches;
    const artists = getTrackArtists(t);
    if (desktop && artists.length) {
      artistEl.innerHTML = renderTrackArtistsHtml(t, {
        className: 'mxm-mini-artist-link',
        stopPropagation: false,
      });
    } else {
      artistEl.textContent = getTrackArtistText(t);
    }
  }
  if (coverEl) coverEl.style.backgroundImage = t.coverUrl ? `url(${t.coverUrl})` : '';
  if (progressEl && audio.duration && Number.isFinite(audio.duration)) {
    progressEl.style.width = ((audio.currentTime / audio.duration) * 100) + '%';
  } else if (progressEl) { progressEl.style.width = '0'; }
  if (playBtn) {
    playBtn.innerHTML = audio.paused
      ? '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>'
      : '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
  }
  const likeBtn = $('mxm-mini-like');
  if (likeBtn) {
    const liked = t && isFavT(t.id);
    likeBtn.classList.toggle('is-liked', !!liked);
    likeBtn.innerHTML = liked
      ? '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" fill="currentColor"/></svg>'
      : '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572"/></svg>';
  }
}

/* ── Render entry ──────────────────────────────────────────────────────────── */
async function renderMessages(peerRef) {
  const content = $('content');
  if (!content) return;
  if (!state.user) {
    content.innerHTML = `<div class="section mxm-chat-guest"><h2>Сообщения</h2><p>Войдите, чтобы писать в личные чаты.</p>
      <button class="btn-primary" onclick="openAuthModal('login')">Войти</button></div>`;
    return;
  }
  const raw = peerRef ? String(peerRef).trim() : '';
  let resolvedPeerId = null;
  if (raw) {
    _mxmChatPeerPublicRef = raw;
    if (looksLikeUserUuidClient(raw)) {
      resolvedPeerId = raw;
    } else {
      try {
        const d = await GET(`/api/users/${encodeURIComponent(raw)}/profile`);
        resolvedPeerId = d?.id || null;
      } catch {
        resolvedPeerId = null;
      }
    }
  } else {
    _mxmChatPeerPublicRef = null;
  }
  _mxmChatActivePeerId = resolvedPeerId;
  content.innerHTML = mxmChatShellHtml();
  mxmChatBindInput();
  mxmChatBindThreadScroll();
  mxmChatBindSwipeBack();
  await mxmChatRefreshSidebar();
  mxmChatConnectWs();
  mxmChatStartMiniPlayer();
  mxmChatBindVisualViewport();
  if (_mxmChatActivePeerId) await mxmChatOpenThread(_mxmChatActivePeerId);
  else mxmChatStopPoll();
  mxmTryConsumePendingShare();
}

// ═══ LISTEN TOGETHER — 1:1 synchronized playback ═════════════════════════════
let _mxmListenApplyingRemote = false;
let _mxmListenSuppressSeekUntil = 0;
let _mxmListenApplySeq = 0;
let _mxmListenSyncTimer = null;
let _mxmListenDriftTimer = null;
let _mxmListenSeekTimer = null;
let _mxmListenPeerOfflineTimer = null;
let _mxmListenPeerOfflineShown = false;
let _mxmListenPendingMessages = [];
const _mxmListenTrackCache = new Map();
const MXM_LISTEN_SOFT_DRIFT_MS = 1500;
const MXM_LISTEN_HARD_DRIFT_MS = 10_000;
const MXM_LISTEN_CATCHUP_RATE = 1.03;
const MXM_LISTEN_SLOWDOWN_RATE = 0.97;

function mxmListenWsSend(payload) {
  if (!_mxmChatWs || _mxmChatWs.readyState !== WebSocket.OPEN) {
    if (payload?.type !== 'listen_sync') {
      _mxmListenPendingMessages.push(payload);
      _mxmListenPendingMessages = _mxmListenPendingMessages.slice(-20);
    }
    mxmChatConnectWs();
    return false;
  }
  _mxmChatWs.send(JSON.stringify(payload));
  return true;
}

function mxmListenFlushPending() {
  if (!_mxmChatWs || _mxmChatWs.readyState !== WebSocket.OPEN) return;
  const pending = _mxmListenPendingMessages;
  _mxmListenPendingMessages = [];
  pending.forEach((payload) => {
    try { _mxmChatWs.send(JSON.stringify(payload)); } catch (_) {}
  });
}

function mxmListenQueuePayload() {
  const tracks = Array.isArray(state.queue) ? state.queue : [];
  return {
    trackIds: tracks.map((track) => String(track?.id || '')).filter(Boolean),
    index: Number.isInteger(state.queueIndex) ? state.queueIndex : -1
  };
}

function mxmListenExpectedPositionMs(session = state.listenSession) {
  if (!session) return 0;
  if (Number.isFinite(session._baseExpectedPositionMs) && Number.isFinite(session._receivedAtMs)) {
    return Math.max(
      0,
      session._baseExpectedPositionMs +
        (session.isPlaying ? Math.max(0, Date.now() - session._receivedAtMs) : 0)
    );
  }
  const base = Math.max(0, Number(session.positionMs) || 0);
  const updatedAt = Number(session.updatedAtMs) || Date.now();
  return Math.max(0, base + (session.isPlaying ? Math.max(0, Date.now() - updatedAt) : 0));
}

function mxmListenStampServerClock(session) {
  if (!session) return session;
  const position = Math.max(0, Number(session.positionMs) || 0);
  const serverNow = Number(session.serverNowMs);
  const updatedAt = Number(session.updatedAtMs);
  const age =
    session.isPlaying && Number.isFinite(serverNow) && Number.isFinite(updatedAt)
      ? Math.max(0, Math.min(30_000, serverNow - updatedAt))
      : 0;
  session._baseExpectedPositionMs = position + age;
  session._receivedAtMs = Date.now();
  return session;
}

async function mxmListenGetTrack(trackId) {
  const id = String(trackId || '');
  if (!id) return null;
  if (id === MXM_RADIO_TRACK_ID) return getRadioPseudoTrack();
  const local = state.queue?.find((track) => String(track?.id) === id);
  if (local) return local;
  if (_mxmListenTrackCache.has(id)) return _mxmListenTrackCache.get(id);
  try {
    const track = await GET(`/api/tracks/${encodeURIComponent(id)}`);
    if (track?.id) _mxmListenTrackCache.set(id, track);
    return track || null;
  } catch (_) {
    return null;
  }
}

async function mxmListenResolveQueue(session) {
  const ids = Array.isArray(session?.queue?.trackIds) ? session.queue.trackIds : [];
  const tracks = (await Promise.all(ids.map(mxmListenGetTrack))).filter(Boolean);
  let index = Number(session?.queue?.index);
  if (!Number.isInteger(index) || index < 0 || index >= tracks.length) {
    index = tracks.findIndex((track) => String(track.id) === String(session?.trackId || ''));
  }
  return { tracks, index };
}

function mxmListenSetPosition(seconds) {
  if (!Number.isFinite(seconds) || !audio.duration) return;
  const clamped = Math.max(0, Math.min(seconds, audio.duration));
  if (Math.abs((audio.currentTime || 0) - clamped) < 0.08) return;
  _mxmListenSuppressSeekUntil = Date.now() + 800;
  audio.currentTime = clamped;
}

function mxmListenSchedulePosition(seconds) {
  if (audio.readyState >= 1 && Number.isFinite(audio.duration)) {
    mxmListenSetPosition(seconds);
    return;
  }
  const apply = () => mxmListenSetPosition(seconds);
  audio.addEventListener('loadedmetadata', apply, { once: true });
}

function mxmListenResetPlaybackRate() {
  if (Math.abs((audio.playbackRate || 1) - 1) > 0.001) audio.playbackRate = 1;
}

function mxmListenCorrectDrift(expectedMs, { allowHardSeek = false } = {}) {
  if (!Number.isFinite(expectedMs) || !audio.duration || audio.paused) {
    mxmListenResetPlaybackRate();
    return;
  }
  const localMs = (audio.currentTime || 0) * 1000;
  const driftMs = expectedMs - localMs;
  const absDrift = Math.abs(driftMs);
  if (allowHardSeek && absDrift > MXM_LISTEN_HARD_DRIFT_MS) {
    mxmListenResetPlaybackRate();
    mxmListenSchedulePosition(expectedMs / 1000);
    return;
  }
  if (absDrift < MXM_LISTEN_SOFT_DRIFT_MS) {
    mxmListenResetPlaybackRate();
    return;
  }
  audio.playbackRate = driftMs > 0 ? MXM_LISTEN_CATCHUP_RATE : MXM_LISTEN_SLOWDOWN_RATE;
}

function mxmListenRender() {
  const session = state.listenSession;
  const bar = $('listen-together-bar');
  const player = $('player');
  const chip = $('fullscreen-listen-chip');
  const queueBadge = $('queue-shared-badge');
  if (!bar) return;

  const visible = !!session && (session.status === 'pending' || session.status === 'active');
  bar.classList.toggle('hidden', !visible);
  player?.classList.toggle('player--listen-together', !!session && session.status === 'active');
  queueBadge?.classList.toggle('hidden', !(session && session.status === 'active'));
  chip?.classList.toggle('hidden', !(session && session.status === 'active'));
  document.documentElement.classList.toggle('mxm-listen-active', !!session && session.status === 'active');
  if (!visible) return;

  const peer = session.peer || {};
  const avatar = $('listen-together-avatar');
  if (avatar) {
    avatar.innerHTML = peer.avatarUrl
      ? `<img src="${esc(peer.avatarUrl)}" alt="">`
      : `<span>${esc(String(peer.username || '?').trim().charAt(0).toUpperCase())}</span>`;
  }
  const name = String(peer.username || 'друг');
  const label =
    session.status === 'active'
      ? `Слушаете вместе с ${name}. Нажмите, чтобы выйти`
      : `Ожидаем ответа от ${name}. Нажмите, чтобы отменить`;
  bar.title = label;
  bar.setAttribute('aria-label', label);
  bar.classList.toggle('is-pending', session.status === 'pending');
  if (chip) chip.textContent = `с ${name}`;
  if (session.status === 'active') setQueuePlayingFromLabel(`Вместе с ${name}`);
}

function mxmListenRenderInvite(session) {
  const invite = $('listen-together-invite');
  if (!invite) return;
  if (!session || session.status !== 'pending' || session.role !== 'guest') {
    invite.classList.add('hidden');
    return;
  }
  const peer = session.peer || {};
  const avatar = $('listen-invite-avatar');
  if (avatar) {
    avatar.innerHTML = peer.avatarUrl
      ? `<img src="${esc(peer.avatarUrl)}" alt="">`
      : `<span>${esc(String(peer.username || '?').trim().charAt(0).toUpperCase())}</span>`;
  }
  const text = $('listen-invite-text');
  if (text) text.textContent = `${peer.username || 'Друг'} приглашает слушать музыку вместе`;
  invite.classList.remove('hidden');
}

function mxmListenSyncChatButton() {
  const button = $('mxm-chat-listen-btn');
  if (!button) return;
  const unavailable =
    !_mxmChatActivePeerId ||
    !_mxmChatPeerIsFriend ||
    _mxmChatPeerBlocked ||
    !!state.listenSession;
  button.classList.toggle('hidden', unavailable);
}

function mxmListenStartTimers() {
  clearInterval(_mxmListenSyncTimer);
  clearInterval(_mxmListenDriftTimer);
  _mxmListenSyncTimer = setInterval(() => {
    const session = state.listenSession;
    if (!session || session.status !== 'active' || session.role !== 'host' || !state.currentTrack) return;
    if (session.trackId === MXM_RADIO_TRACK_ID) return;
    mxmListenWsSend({
      type: 'listen_sync',
      sessionId: session.id,
      positionMs: Math.round((audio.currentTime || 0) * 1000),
      isPlaying: !audio.paused
    });
  }, 8000);
  _mxmListenDriftTimer = setInterval(() => {
    const session = state.listenSession;
    if (!session || session.status !== 'active' || session.role !== 'guest' || !session.trackId) return;
    if (session.trackId === MXM_RADIO_TRACK_ID) return;
    if (String(state.currentTrack?.id || '') !== String(session.trackId)) return;
    mxmListenCorrectDrift(mxmListenExpectedPositionMs(session), { allowHardSeek: true });
  }, 4000);
}

async function mxmListenApplySession(session, options = {}) {
  if (!session?.id) return;
  mxmListenStampServerClock(session);
  const seq = ++_mxmListenApplySeq;
  state.listenSession = session;
  mxmListenRender();
  mxmListenRenderInvite(session);
  mxmListenSyncChatButton();
  mxmListenStartTimers();
  if (session.status !== 'active') return;

  if (isRadioPlaybackActive() && session.trackId !== MXM_RADIO_TRACK_ID) {
    try { audio.pause(); } catch (_) {}
    state.radioActive = false;
  }

  const queue = await mxmListenResolveQueue(session);
  if (seq !== _mxmListenApplySeq || String(state.listenSession?.id) !== String(session.id)) return;
  const track = await mxmListenGetTrack(session.trackId);
  if (seq !== _mxmListenApplySeq || !track) return;

  _mxmListenApplyingRemote = true;
  try {
    if (queue.tracks.length) {
      setQueueState(queue.tracks, Math.max(0, queue.index), { preserveBefore: true });
      if (isQueueSheetOpen()) renderQueueSheet();
    }
    const changedTrack = String(state.currentTrack?.id || '') !== String(track.id);
    const isRadioSession = String(session.trackId || '') === MXM_RADIO_TRACK_ID;
    if (changedTrack) {
      mxmListenResetPlaybackRate();
      if (session.isPlaying) playTrack(track);
      else {
        prepareTrack(track);
        if (isRadioSession) {
          prepareRadioAudioPlayback();
          if (typeof startRadioPlaybackPolling === 'function') startRadioPlaybackPolling();
        }
      }
    }
    const expectedSeconds = mxmListenExpectedPositionMs(session) / 1000;
    const seekWasRemote =
      options.action === 'seek' &&
      String(options.actorUserId || '') !== String(state.user?.id || '');
    if (!isRadioSession) {
      if (changedTrack || options.initial || seekWasRemote) {
        mxmListenSchedulePosition(expectedSeconds);
      } else {
        mxmListenCorrectDrift(expectedSeconds, { allowHardSeek: false });
      }
    }
    if (session.isPlaying && audio.paused) {
      audio.play().catch(() => toast('Нажмите Play, чтобы продолжить вместе', 'error'));
    } else if (!session.isPlaying && !audio.paused) {
      audio.pause();
      mxmListenResetPlaybackRate();
    }
  } finally {
    setTimeout(() => { _mxmListenApplyingRemote = false; }, 0);
  }
}

async function mxmListenHandleWs(message) {
  if (!message) return;
  if (message.type === 'listen_invite') {
    await mxmListenApplySession(message.session, { initial: true });
    return;
  }
  if (message.type === 'listen_state') {
    const initial =
      !state.listenSession || String(state.listenSession.id) !== String(message.session?.id || '');
    await mxmListenApplySession(message.session, {
      initial,
      action: message.action || '',
      actorUserId: message.actorUserId || ''
    });
    return;
  }
  if (message.type === 'listen_sync') {
    const session = state.listenSession;
    if (!session || String(session.id) !== String(message.sessionId)) return;
    session.positionMs = Number(message.positionMs) || 0;
    session.updatedAtMs = Number(message.updatedAtMs) || Date.now();
    session.serverNowMs = Number(message.serverNowMs) || session.updatedAtMs;
    session.isPlaying = message.isPlaying !== false;
    mxmListenStampServerClock(session);
    mxmListenCorrectDrift(mxmListenExpectedPositionMs(session), { allowHardSeek: true });
    return;
  }
  if (message.type === 'listen_peer_status') {
    const session = state.listenSession;
    if (!session || String(session.id) !== String(message.sessionId || '')) return;
    clearTimeout(_mxmListenPeerOfflineTimer);
    const badge = $('listen-together-bar');
    if (message.online) {
      badge?.classList.remove('peer-offline');
      if (_mxmListenPeerOfflineShown) toast('Друг вернулся — слушаете вместе', 'success');
      _mxmListenPeerOfflineShown = false;
    } else {
      _mxmListenPeerOfflineTimer = setTimeout(() => {
        if (!state.listenSession || String(state.listenSession.id) !== String(message.sessionId || '')) return;
        badge?.classList.add('peer-offline');
        _mxmListenPeerOfflineShown = true;
        toast('Друг отключился. Ждём переподключения…');
      }, 1500);
    }
    return;
  }
  if (message.type === 'listen_ended') {
    if (state.listenSession && String(state.listenSession.id) !== String(message.sessionId)) return;
    mxmListenClear(message.reason);
    return;
  }
  if (message.type === 'listen_error') toast(message.error || 'Ошибка совместного прослушивания', 'error');
}

async function mxmListenRestoreActive() {
  if (!state.user) return;
  try {
    const data = await GET('/api/listen/sessions/active');
    if (data?.session) await mxmListenApplySession(data.session, { initial: !state.listenSession });
    else if (state.listenSession) mxmListenClear();
  } catch (_) {}
}

async function mxmListenInvite(friendUserId) {
  if (!state.user) return openAuthModal('login');
  const id = String(friendUserId || '').trim();
  if (!id) return;
  try {
    mxmChatConnectWs();
    const data = await POST('/api/listen/sessions', {
      friendUserId: id,
      trackId: state.currentTrack?.id || null,
      positionMs: Math.round((audio.currentTime || 0) * 1000),
      isPlaying: !!state.currentTrack && !audio.paused,
      queue: mxmListenQueuePayload()
    });
    await mxmListenApplySession(data.session, { initial: true });
    toast('Приглашение отправлено', 'success');
  } catch (err) {
    toast(err.message || 'Не удалось пригласить', 'error');
  }
}

async function mxmListenAccept() {
  const session = state.listenSession;
  if (!session || session.role !== 'guest') return;
  try {
    const data = await POST(`/api/listen/sessions/${encodeURIComponent(session.id)}/accept`, {});
    $('listen-together-invite')?.classList.add('hidden');
    await mxmListenApplySession(data.session, { initial: true });
  } catch (err) {
    toast(err.message || 'Не удалось принять приглашение', 'error');
  }
}

async function mxmListenDecline() {
  const session = state.listenSession;
  if (!session) return;
  try {
    await POST(`/api/listen/sessions/${encodeURIComponent(session.id)}/decline`, {});
  } catch (_) {}
  mxmListenClear('declined');
}

async function mxmListenAcceptNotification(sessionId, notificationId) {
  try {
    const data = await POST(`/api/listen/sessions/${encodeURIComponent(sessionId)}/accept`, {});
    if (notificationId) await POST('/api/me/notifications/read', { id: notificationId }).catch(() => {});
    closeNotifPanel();
    refreshNotifBadge();
    await mxmListenApplySession(data.session);
  } catch (err) {
    toast(err.message || 'Приглашение уже недействительно', 'error');
  }
}

async function mxmListenDeclineNotification(sessionId, notificationId) {
  try {
    await POST(`/api/listen/sessions/${encodeURIComponent(sessionId)}/decline`, {});
    if (notificationId) await POST('/api/me/notifications/read', { id: notificationId }).catch(() => {});
    if (notifPanelOpen) await loadNotificationsList();
    refreshNotifBadge();
  } catch (err) {
    toast(err.message || 'Приглашение уже недействительно', 'error');
  }
  if (String(state.listenSession?.id || '') === String(sessionId)) mxmListenClear('declined');
}

async function mxmListenLeave() {
  const session = state.listenSession;
  if (!session) return;
  try {
    await POST(`/api/listen/sessions/${encodeURIComponent(session.id)}/leave`, {});
  } catch (_) {}
  mxmListenClear('left');
}

function mxmListenClear(reason = '') {
  const previousStatus = state.listenSession?.status;
  const hadActive = previousStatus === 'active';
  state.listenSession = null;
  _mxmListenApplySeq++;
  clearInterval(_mxmListenSyncTimer);
  clearInterval(_mxmListenDriftTimer);
  clearTimeout(_mxmListenSeekTimer);
  clearTimeout(_mxmListenPeerOfflineTimer);
  _mxmListenPeerOfflineShown = false;
  _mxmListenPendingMessages = [];
  mxmListenResetPlaybackRate();
  mxmListenRender();
  mxmListenRenderInvite(null);
  mxmListenSyncChatButton();
  if (state.queuePlayingFrom?.title?.startsWith('Вместе с ')) setQueuePlayingFromLabel('');
  if (previousStatus === 'pending' && reason === 'expired') toast('Приглашение истекло');
  if (hadActive && reason === 'disconnected') toast('Друг отключился', 'error');
  else if (hadActive && reason && reason !== 'left') toast('Совместное прослушивание завершено');
}

function mxmListenOnLocalTrack(track) {
  const session = state.listenSession;
  if (_mxmListenApplyingRemote || !session || session.status !== 'active' || !track?.id) return;
  mxmListenWsSend({
    type: 'listen_cmd',
    action: 'play',
    sessionId: session.id,
    trackId: track.id,
    positionMs: 0,
    queue: mxmListenQueuePayload()
  });
}

function mxmListenOnLocalToggle(willPlay) {
  const session = state.listenSession;
  if (_mxmListenApplyingRemote || !session || session.status !== 'active') return;
  mxmListenWsSend({
    type: 'listen_cmd',
    action: willPlay ? 'resume' : 'pause',
    sessionId: session.id,
    positionMs: Math.round((audio.currentTime || 0) * 1000)
  });
}

function mxmListenOnLocalQueueChanged() {
  const session = state.listenSession;
  if (_mxmListenApplyingRemote || !session || session.status !== 'active') return;
  const queue = mxmListenQueuePayload();
  mxmListenWsSend({
    type: 'listen_cmd',
    action: 'set_queue',
    sessionId: session.id,
    trackIds: queue.trackIds,
    index: queue.index
  });
}

function mxmListenRequestStep(action) {
  const session = state.listenSession;
  if (_mxmListenApplyingRemote || !session || session.status !== 'active') return false;
  mxmListenWsSend({ type: 'listen_cmd', action, sessionId: session.id });
  return true;
}

audio.addEventListener('seeked', () => {
  mxmListenResetPlaybackRate();
  if (_mxmListenApplyingRemote || Date.now() < _mxmListenSuppressSeekUntil) return;
  const session = state.listenSession;
  if (!session || session.status !== 'active') return;
  clearTimeout(_mxmListenSeekTimer);
  _mxmListenSeekTimer = setTimeout(() => {
    mxmListenWsSend({
      type: 'listen_cmd',
      action: 'seek',
      sessionId: session.id,
      positionMs: Math.round((audio.currentTime || 0) * 1000)
    });
  }, 120);
});

window.mxmListenHandleWs = mxmListenHandleWs;
window.mxmListenFlushPending = mxmListenFlushPending;
window.mxmListenRestoreActive = mxmListenRestoreActive;
window.mxmListenInvite = mxmListenInvite;
window.mxmListenAccept = mxmListenAccept;
window.mxmListenDecline = mxmListenDecline;
window.mxmListenLeave = mxmListenLeave;
window.mxmListenAcceptNotification = mxmListenAcceptNotification;
window.mxmListenDeclineNotification = mxmListenDeclineNotification;

// ─── Страница /sync — импорт из Яндекс.Музыки (лайки + плейлисты) ─────────────

const YANDEX_MUSIC_OAUTH_AUTHORIZE_URL =
  'https://oauth.yandex.ru/authorize?response_type=token&client_id=23cabbbdc6cd418abb4b39c32c41195d';
const YANDEX_OAUTH_REDIRECT_URL_EXAMPLE =
  'https://music.yandex.ru/#access_token=AQAAAAYc***&token_type=bearer&expires_in=31535645';

let yandexSyncUi = {
  syncMode: 'likes',
  step: 'connect',
  linkStatus: { linked: false },
  jobId: null,
  items: [],
  selected: new Set(),
  playlists: [],
  selectedPlaylists: new Set(),
  playlistResults: [],
  pollTimer: null
};

function formatYandexSyncProgress(progress, syncMode) {
  if (!progress) return 'Подготовка…';
  const { phase, current, total } = progress;
  if (phase === 'fetch_playlists') {
    return total ? `Загружаем плейлисты… ${current}/${total}` : 'Загружаем плейлисты…';
  }
  if (phase === 'fetch_likes') {
    return total ? `Загружаем «Мне нравится»… ${current}/${total}` : 'Загружаем «Мне нравится»…';
  }
  if (phase === 'resolve_meta') {
    return total ? `Сопоставляем с каталогом… ${current}/${total}` : 'Сопоставляем…';
  }
  if (phase === 'import') {
    return total ? `Загрузка треков… ${current} / ${total}` : 'Параллельная загрузка…';
  }
  if (phase === 'sync') {
    return total ? `Проверка… ${current} / ${total}` : 'Проверка…';
  }
  return syncMode === 'playlists' ? 'Импорт плейлистов…' : 'Синхронизация…';
}

function syncProgressPercent(progress, syncMode) {
  if (!progress || !progress.total) {
    if (progress?.phase === 'fetch_playlists') return 10;
    return progress?.phase === 'fetch_likes' ? 8 : 4;
  }
  const cur = Number(progress.current) || 0;
  const tot = Number(progress.total) || 1;
  let base = 0;
  if (progress.phase === 'fetch_playlists') base = 14;
  else if (progress.phase === 'fetch_likes') base = 12;
  else if (progress.phase === 'resolve_meta') base = 28;
  else if (progress.phase === 'sync') base = 30;
  else if (progress.phase === 'import') base = 32;
  const span =
    progress.phase === 'import' ? 62 : progress.phase === 'sync' ? 12 : progress.phase === 'resolve_meta' ? 16 : 12;
  return Math.min(98, base + Math.round((cur / tot) * span));
}

function setYandexSyncMode(mode) {
  const next = mode === 'playlists' ? 'playlists' : 'likes';
  if (yandexSyncUi.syncMode === next) return;
  stopYandexSyncPoll();
  yandexSyncUi.syncMode = next;
  yandexSyncUi.step = 'connect';
  yandexSyncUi.jobId = null;
  yandexSyncUi.items = [];
  yandexSyncUi.selected = new Set();
  yandexSyncUi.playlists = [];
  yandexSyncUi.selectedPlaylists = new Set();
  yandexSyncUi.playlistResults = [];
  mountSyncPageContent();
}

async function refreshYandexSyncLinkStatus() {
  try {
    yandexSyncUi.linkStatus = await GET('/api/me/yandex-music/status');
  } catch (_) {
    yandexSyncUi.linkStatus = { linked: false };
  }
  return yandexSyncUi.linkStatus;
}

function parseYandexAccessTokenFromInput(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  const m =
    s.match(/access_token=([^&]+)/i) ||
    s.match(/[#?&]access_token=([^&]+)/);
  if (m && m[1]) {
    try {
      return decodeURIComponent(m[1].trim());
    } catch (_) {
      return m[1].trim();
    }
  }
  return s;
}

function openYandexOAuthAuthorize() {
  const w = window.open(YANDEX_MUSIC_OAUTH_AUTHORIZE_URL, '_blank', 'noopener,noreferrer');
  if (!w) {
    toast('Разрешите всплывающие окна или откройте ссылку ниже', 'info');
    return;
  }
  toast('После входа скопируйте URL из адресной строки новой вкладки', 'info');
}

function copyYandexOAuthAuthorizeLink() {
  const url = YANDEX_MUSIC_OAUTH_AUTHORIZE_URL;
  void copyPlainTextToClipboard(url).then((ok) => {
    if (ok) toast('Ссылка скопирована', 'success');
    else toast('Не удалось скопировать', 'error');
  });
}

function handleYandexTokenPaste(event) {
  const input = event.target;
  if (!input) return;
  setTimeout(() => {
    const parsed = parseYandexAccessTokenFromInput(input.value);
    if (parsed && parsed !== input.value) {
      input.value = parsed;
      toast('Токен извлечён из URL', 'success');
    }
  }, 0);
}

function renderSyncExampleUrlHtml() {
  return `
    <div class="sync-guide-url-visual" role="note" aria-label="Какой фрагмент URL копировать">
      <p class="sync-url-visual-caption">Скопируйте выделенный фрагмент из адресной строки:</p>
      <div class="sync-guide-url-box">
        <code class="sync-guide-url-code">https://music.yandex.ru/#<span class="sync-url-copy">access_token=AQAAAAYc***</span><span class="sync-url-bound">&amp;token_type</span><span class="sync-url-rest">=bearer&amp;expires_in=31535645</span></code>
      </div>
    </div>`;
}

function renderSyncTokenGuideHtml() {
  const oauthLink = esc(YANDEX_MUSIC_OAUTH_AUTHORIZE_URL);
  return `
    <div class="sync-guide" role="region" aria-label="Как получить токен">
      <p class="sync-guide-lead">Нужен параметр <code>access_token</code> из адреса после входа в Яндекс.</p>
      <ol class="sync-guide-steps">
        <li class="sync-guide-step">
          <span class="sync-guide-step-num" aria-hidden="true">1</span>
          <div class="sync-guide-step-body">
            <strong>Откройте страницу авторизации</strong>
            <p class="sync-guide-step-text">Войдите в Яндекс и разрешите доступ приложению.</p>
            <div class="sync-guide-step-actions">
              <button type="button" class="btn-primary sync-btn-glow" onclick="openYandexOAuthAuthorize()">Открыть авторизацию</button>
              <button type="button" class="btn-ghost" onclick="copyYandexOAuthAuthorizeLink()">Скопировать ссылку</button>
            </div>
            <a class="sync-guide-link" href="${oauthLink}" target="_blank" rel="noopener noreferrer">${oauthLink}</a>
          </div>
        </li>
        <li class="sync-guide-step">
          <span class="sync-guide-step-num" aria-hidden="true">2</span>
          <div class="sync-guide-step-body">
            <strong>Скопируйте фрагмент из адресной строки</strong>
            <p class="sync-guide-step-text">После входа URL будет примерно таким. Скопируйте его <em>сразу</em>, пока страница не перешла дальше:</p>
            ${renderSyncExampleUrlHtml()}
          </div>
        </li>
        <li class="sync-guide-step">
          <span class="sync-guide-step-num" aria-hidden="true">3</span>
          <div class="sync-guide-step-body">
            <strong>Вставьте токен ниже</strong>
            <p class="sync-guide-step-text">Можно вставить весь URL — мы возьмём значение <code>access_token</code> автоматически.</p>
          </div>
        </li>
      </ol>
      <details class="sync-guide-alt">
        <summary>Другой способ: через F12 (cookie)</summary>
        <ol class="sync-guide-alt-list">
          <li>Откройте <a href="https://music.yandex.ru" target="_blank" rel="noopener noreferrer">music.yandex.ru</a> и войдите в аккаунт.</li>
          <li>Нажмите <kbd>F12</kbd> → вкладка <strong>Application</strong> (Chrome) или <strong>Хранилище</strong> (Firefox).</li>
          <li><strong>Cookies</strong> → <code>music.yandex.ru</code> → найдите cookie с OAuth-токеном.</li>
          <li>Скопируйте значение и вставьте в поле ниже.</li>
        </ol>
      </details>
    </div>`;
}

function renderSyncModeTabs() {
  const mode = yandexSyncUi.syncMode;
  return `
    <div class="sync-mode-tabs" role="tablist" aria-label="Тип синхронизации">
      <button type="button" class="sync-mode-tab${mode === 'likes' ? ' is-active' : ''}" role="tab" aria-selected="${mode === 'likes'}"
        onclick="setYandexSyncMode('likes')">Мне нравится</button>
      <button type="button" class="sync-mode-tab${mode === 'playlists' ? ' is-active' : ''}" role="tab" aria-selected="${mode === 'playlists'}"
        onclick="setYandexSyncMode('playlists')">Мои плейлисты</button>
    </div>`;
}

function renderSyncStepIndicator(step, syncMode) {
  const steps =
    syncMode === 'playlists'
      ? [
          { id: 'connect', label: 'Токен' },
          { id: 'pick_playlists', label: 'Плейлисты' },
          { id: 'progress', label: 'Импорт' },
          { id: 'playlist_review', label: 'Готово' }
        ]
      : [
          { id: 'connect', label: 'Токен' },
          { id: 'progress', label: 'Синхронизация' },
          { id: 'review', label: 'Выбор' }
        ];
  const idx = steps.findIndex((s) => s.id === step);
  return `
    <ol class="sync-steps" aria-label="Этапы">
      ${steps
        .map((s, i) => {
          const done = i < idx;
          const active = i === idx;
          return `<li class="sync-steps-item${done ? ' is-done' : ''}${active ? ' is-active' : ''}">
            <span class="sync-steps-dot" aria-hidden="true">${done ? '✓' : i + 1}</span>
            <span class="sync-steps-label">${esc(s.label)}</span>
          </li>`;
        })
        .join('')}
    </ol>`;
}

function renderSyncLinkedTokenFieldHtml(st) {
  const login = esc(st.login || st.displayName || 'Аккаунт Яндекс.Музыки');
  return `
    <div class="sync-token-row sync-token-row--linked" role="status" aria-live="polite">
      <div class="sync-ym-access-field">
        <span class="sync-ym-access-label">Яндекс.Музыка</span>
        <div class="sync-ym-access-linked" id="sync-yandex-linked" aria-label="Подключённый аккаунт">
          <span class="sync-linked-dot" aria-hidden="true" title="Подключено"></span>
          <span class="sync-linked-login">${login}</span>
        </div>
      </div>
      <div class="sync-token-row-btns">
        <button type="button" class="btn-ghost" onclick="unlinkYandexSyncAccount()">Отвязать</button>
      </div>
    </div>`;
}

function renderSyncTokenFormHtml() {
  return `
    <div class="sync-token-row">
      <label class="sync-ym-access-field" for="sync-yandex-token">
        <span class="sync-ym-access-label">Токен доступа</span>
        <input
          type="text"
          id="sync-yandex-token"
          class="sync-ym-access-input"
          name="mxm_yandex_music_access_token"
          placeholder="access_token или полный URL после авторизации"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          inputmode="verbatim"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          onpaste="handleYandexTokenPaste(event)"
        />
      </label>
      <div class="sync-token-row-btns">
        <button type="button" class="btn-primary" onclick="saveYandexSyncToken()">Сохранить</button>
      </div>
    </div>`;
}

function renderSyncConnectStep(st) {
  const linked = !!st.linked;
  const mode = yandexSyncUi.syncMode;
  const title =
    mode === 'playlists'
      ? linked
        ? 'Импорт плейлистов'
        : 'Подключите Яндекс.Музыку'
      : linked
        ? 'Яндекс.Музыка подключена'
        : 'Подключите Яндекс.Музыку';
  const desc =
    mode === 'playlists'
      ? 'Перенесите свои плейлисты с Яндекс.Музыки в пользовательские плейлисты MXM.'
      : 'Импортируйте треки из «Мне нравится» в избранное MXM.';
  const startLabel = mode === 'playlists' ? 'Выбрать плейлисты' : 'Начать синхронизацию';
  const startAction = mode === 'playlists' ? 'openYandexPlaylistPicker()' : 'startYandexSyncJob()';
  return `
    <div class="sync-panel sync-panel--connect sync-step-enter${linked ? ' sync-panel--linked' : ''}">
      ${renderSyncModeTabs()}
      ${renderSyncStepIndicator('connect', mode)}
      ${
        linked
          ? ''
          : `<div class="sync-hero-icon" aria-hidden="true">
        <div class="sync-pulse-ring"></div>
        <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor"><path d="M12 3C7.03 3 3 7.03 3 12s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9zm0 16.5c-4.14 0-7.5-3.36-7.5-7.5S7.86 4.5 12 4.5s7.5 3.36 7.5 7.5-3.36 7.5-7.5 7.5z"/><path d="M9.5 8.2v7.6l6.5-3.8-6.5-3.8z"/></svg>
      </div>`
      }
      <h2 class="sync-panel-title">${title}</h2>
      <p class="sync-panel-desc">${desc}</p>
      ${linked ? '' : renderSyncTokenGuideHtml()}
      ${linked ? renderSyncLinkedTokenFieldHtml(st) : renderSyncTokenFormHtml()}
      <button type="button" class="btn-primary sync-start-btn" id="sync-start-btn" onclick="${startAction}" ${linked ? '' : 'disabled'}>
        ${startLabel}
      </button>
    </div>`;
}

function renderSyncProgressStep(progress, statusText) {
  const pct = syncProgressPercent(progress, yandexSyncUi.syncMode);
  return `
    <div class="sync-panel sync-panel--progress sync-step-enter">
      ${renderSyncModeTabs()}
      ${renderSyncStepIndicator('progress', yandexSyncUi.syncMode)}
      <div class="sync-progress-visual" aria-hidden="true">
        <div class="sync-progress-ring">
          <svg viewBox="0 0 120 120"><circle class="sync-progress-ring-bg" cx="60" cy="60" r="52"/><circle class="sync-progress-ring-fg" cx="60" cy="60" r="52" style="stroke-dashoffset:${328 - (328 * pct) / 100}px"/></svg>
          <span class="sync-progress-pct">${pct}%</span>
        </div>
      </div>
      <h2 class="sync-panel-title">Синхронизация</h2>
      <p class="sync-progress-status" id="sync-progress-status" role="status">${esc(statusText || 'Запуск…')}</p>
      <div class="sync-progress-bar" aria-hidden="true"><div class="sync-progress-bar-inner" style="width:${pct}%"></div></div>
      <button type="button" class="btn-ghost sync-cancel-btn" id="sync-cancel-btn" onclick="cancelYandexSyncJob()">Остановить</button>
    </div>`;
}

function syncStatusBadge(status, already) {
  if (already) return '<span class="sync-track-badge sync-track-badge--fav">В избранном</span>';
  if (status === 'imported') return '<span class="sync-track-badge sync-track-badge--new">Новый в MXM</span>';
  return '<span class="sync-track-badge sync-track-badge--cat">В каталоге</span>';
}

function renderSyncPlaylistPickerStep() {
  const playlists = yandexSyncUi.playlists || [];
  const sel = yandexSyncUi.selectedPlaylists;
  const allSelected = playlists.length > 0 && playlists.every((pl) => sel.has(pl.kind));
  const cards = playlists
    .map((pl, i) => {
      const checked = sel.has(pl.kind);
      const cover = pl.coverUrl
        ? `<img src="${esc(pl.coverUrl)}" alt="" loading="lazy" decoding="async" />`
        : `<div class="sync-playlist-cover-fallback" style="background:linear-gradient(${esc(pickGrad(String(pl.kind)))})"></div>`;
      return `
        <label class="sync-playlist-card sync-stagger" style="--sync-i:${i}">
          <input type="checkbox" class="sync-playlist-check" ${checked ? 'checked' : ''}
            onchange="toggleYandexPlaylistSelection(${Number(pl.kind)}, this.checked)" />
          <span class="sync-playlist-cover">${cover}</span>
          <span class="sync-playlist-meta">
            <span class="sync-playlist-title">${esc(pl.title)}</span>
            <span class="sync-playlist-count">${Number(pl.trackCount) || 0} треков</span>
          </span>
        </label>`;
    })
    .join('');

  return `
    <div class="sync-panel sync-panel--playlists sync-step-enter">
      ${renderSyncModeTabs()}
      ${renderSyncStepIndicator('pick_playlists', 'playlists')}
      <div class="sync-review-head">
        <h2 class="sync-panel-title">Выберите плейлисты</h2>
        <p class="sync-panel-desc">Отметьте плейлисты, которые нужно перенести в MXM целиком.</p>
      </div>
      <div class="sync-review-toolbar">
        <button type="button" class="btn-ghost" onclick="toggleYandexPlaylistSelectAll(${allSelected ? 'false' : 'true'})">
          ${allSelected ? 'Снять выделение' : 'Выбрать все'}
        </button>
        <span class="sync-review-count">${sel.size} выбрано</span>
      </div>
      <div class="sync-playlist-grid" id="sync-playlist-grid">
        ${cards || '<p class="sync-empty">Собственных плейлистов не найдено</p>'}
      </div>
      <div class="sync-review-actions">
        <button type="button" class="btn-primary sync-apply-btn" id="sync-playlist-start-btn" onclick="startYandexPlaylistSyncJob()">
          Импортировать (${sel.size})
        </button>
        <button type="button" class="btn-ghost" onclick="resetYandexSyncPage()">Назад</button>
      </div>
    </div>`;
}

function renderSyncPlaylistReviewStep() {
  const playlists = (yandexSyncUi.playlistResults || []).slice();
  const sel = yandexSyncUi.selectedPlaylists;
  const selected = playlists.filter((pl) => sel.has(pl.kind));
  const cards = selected
    .map((pl, i) => {
      const cover = pl.coverUrl
        ? `<img src="${esc(pl.coverUrl)}" alt="" loading="lazy" decoding="async" />`
        : `<div class="sync-playlist-cover-fallback" style="background:linear-gradient(${esc(pickGrad(String(pl.kind)))})"></div>`;
      const errNote = pl.errorCount ? `<span class="sync-playlist-errors">${pl.errorCount} не импортировано</span>` : '';
      return `
        <div class="sync-playlist-result sync-stagger" style="--sync-i:${i}">
          <span class="sync-playlist-cover">${cover}</span>
          <span class="sync-playlist-meta">
            <span class="sync-playlist-title">${esc(pl.title)}</span>
            <span class="sync-playlist-count">${pl.resolvedCount || 0} из ${pl.trackCount || 0} треков</span>
            ${errNote}
          </span>
        </div>`;
    })
    .join('');

  return `
    <div class="sync-panel sync-panel--review sync-step-enter">
      ${renderSyncModeTabs()}
      ${renderSyncStepIndicator('playlist_review', 'playlists')}
      <div class="sync-review-head">
        <h2 class="sync-panel-title">Создать плейлисты в MXM</h2>
        <p class="sync-panel-desc">Будут созданы ваши плейлисты с перенесёнными треками.</p>
      </div>
      <div class="sync-playlist-grid sync-playlist-grid--review">${cards || '<p class="sync-empty">Нет данных</p>'}</div>
      <div class="sync-review-actions">
        <button type="button" class="btn-primary sync-apply-btn" id="sync-playlist-apply-btn" onclick="applyYandexPlaylistSync()">
          Создать плейлисты (${selected.length})
        </button>
        <button type="button" class="btn-ghost" onclick="navigate('library')">В медиатеку</button>
        <button type="button" class="btn-ghost" onclick="resetYandexSyncPage()">Синхронизировать снова</button>
      </div>
    </div>`;
}

function renderSyncReviewStep() {
  const items = (yandexSyncUi.items || []).slice().sort((a, b) => {
    const ai = Number.isFinite(a.likeOrder) ? a.likeOrder : 1e9;
    const bi = Number.isFinite(b.likeOrder) ? b.likeOrder : 1e9;
    return ai - bi;
  });
  const sel = yandexSyncUi.selected;
  const selectable = items.filter((it) => !it.alreadyInFavorites);
  const allSelected = selectable.length > 0 && selectable.every((it) => sel.has(it.trackId));
  const selectedCount = items.filter((it) => sel.has(it.trackId)).length;
  const cards = items
    .map((it, i) => {
      const checked = sel.has(it.trackId);
      const disabled = !!it.alreadyInFavorites;
      const grad = it.coverGradient || pickGrad(it.trackId);
      const cover = it.coverUrl
        ? `<img src="${esc(resolveMediaUrl(it.coverUrl))}" alt="" loading="lazy" decoding="async" />`
        : `<div class="sync-track-cover-fallback" style="background:linear-gradient(${esc(grad)})"></div>`;
      return `
        <label class="sync-track-card sync-stagger${disabled ? ' is-disabled' : ''}" style="--sync-i:${i}" data-track-id="${esc(it.trackId)}">
          <input type="checkbox" class="sync-track-check" ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}
            onchange="toggleYandexSyncTrackSelection(this.closest('.sync-track-card').dataset.trackId, this.checked)" />
          <span class="sync-track-cover">${cover}</span>
          <span class="sync-track-meta">
            <span class="sync-track-title">${esc(it.title)}</span>
            <span class="sync-track-artist">${esc(it.artist)}</span>
            ${syncStatusBadge(it.status, it.alreadyInFavorites)}
          </span>
        </label>`;
    })
    .join('');

  return `
    <div class="sync-panel sync-panel--review sync-step-enter">
      ${renderSyncModeTabs()}
      ${renderSyncStepIndicator('review', 'likes')}
      <div class="sync-review-head">
        <h2 class="sync-panel-title">Выберите треки для избранного</h2>
        <p class="sync-panel-desc">Отметьте, что добавить в ♥ на MXM. Уже в избранном показаны отдельно.</p>
      </div>
      <div class="sync-review-toolbar">
        <button type="button" class="btn-ghost" onclick="toggleYandexSyncSelectAll(${allSelected ? 'false' : 'true'})">
          ${allSelected ? 'Снять выделение' : 'Выбрать все новые'}
        </button>
        <span class="sync-review-count">${selectedCount} выбрано</span>
      </div>
      <div class="sync-track-grid" id="sync-track-grid">${cards || '<p class="sync-empty">Нет треков для отображения</p>'}</div>
      <div class="sync-review-actions">
        <button type="button" class="btn-primary sync-apply-btn" id="sync-apply-btn" onclick="applyYandexSyncFavorites()">
          Добавить в избранное (${selectedCount})
        </button>
        <button type="button" class="btn-ghost" onclick="navigate('library')">В медиатеку</button>
        <button type="button" class="btn-ghost" onclick="resetYandexSyncPage()">Синхронизировать снова</button>
      </div>
    </div>`;
}

function renderSyncPageInner() {
  const step = yandexSyncUi.step;
  if (step === 'progress') {
    return renderSyncProgressStep(yandexSyncUi.lastProgress, yandexSyncUi.progressLabel);
  }
  if (step === 'pick_playlists') return renderSyncPlaylistPickerStep();
  if (step === 'playlist_review') return renderSyncPlaylistReviewStep();
  if (step === 'review') return renderSyncReviewStep();
  return renderSyncConnectStep(yandexSyncUi.linkStatus || {});
}

function mountSyncPageContent() {
  const host = $('sync-page-host');
  if (!host) return;
  host.innerHTML = renderSyncPageInner();
}

async function renderSyncPage() {
  const content = $('content');
  content.classList.add('content-sync-view');
  setQueuePlayingFromLabel('Синхронизация');

  if (!state.user) {
    content.innerHTML = `
      <div class="sync-page sync-page--guest">
        <div class="sync-guest-card sync-step-enter">
          <h1 class="view-title">Синхронизация</h1>
          <p class="view-subtitle">Импорт «Мне нравится» и плейлистов из Яндекс.Музыки</p>
          <p>Войдите в аккаунт MXM, чтобы продолжить.</p>
          <button type="button" class="btn-primary" onclick="openAuthModal('login')">Войти</button>
        </div>
      </div>`;
    return;
  }

  await refreshYandexSyncLinkStatus();
  if (yandexSyncUi.step === 'connect' && !yandexSyncUi.jobId) {
    yandexSyncUi.step = 'connect';
  }

  content.innerHTML = `
    <div class="sync-page">
      <header class="sync-page-header">
        <button type="button" class="nav-back-btn" onclick="navigate('library')" title="Медиатека">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div>
          <h1 class="view-title">Синхронизация</h1>
          <p class="view-subtitle">Яндекс.Музыка → избранное и плейлисты MXM</p>
        </div>
      </header>
      <div id="sync-page-host"></div>
    </div>`;
  mountSyncPageContent();
}

async function saveYandexSyncToken() {
  const input = $('sync-yandex-token');
  const token = parseYandexAccessTokenFromInput(input ? input.value : '');
  if (!token) {
    toast('Вставьте access_token или URL после авторизации', 'error');
    return;
  }
  if (input) input.value = token;
  try {
    await POST('/api/me/yandex-music/link', { token });
    if (input) input.value = '';
    await refreshYandexSyncLinkStatus();
    yandexSyncUi.step = 'connect';
    mountSyncPageContent();
    toast('Токен сохранён', 'success');
  } catch (e) {
    toast(e.message || 'Не удалось сохранить', 'error');
  }
}

async function unlinkYandexSyncAccount() {
  try {
    await DEL('/api/me/yandex-music/link');
    yandexSyncUi.linkStatus = { linked: false };
    mountSyncPageContent();
    toast('Аккаунт отвязан', 'info');
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  }
}

function stopYandexSyncPoll() {
  if (yandexSyncUi.pollTimer) {
    clearTimeout(yandexSyncUi.pollTimer);
    yandexSyncUi.pollTimer = null;
  }
}

async function pollYandexSyncJobOnce() {
  const jobId = yandexSyncUi.jobId;
  if (!jobId) return null;
  const path =
    yandexSyncUi.syncMode === 'playlists'
      ? `/api/me/yandex-music/sync-playlists/${encodeURIComponent(jobId)}`
      : `/api/me/yandex-music/sync-likes/${encodeURIComponent(jobId)}`;
  return GET(path);
}

async function runYandexSyncPollLoop() {
  stopYandexSyncPoll();
  const tick = async () => {
    if (yandexSyncUi.step !== 'progress' || state.view !== 'sync') return;
    try {
      const st = await pollYandexSyncJobOnce();
      if (!st) return;
      yandexSyncUi.lastProgress = st.progress;
      yandexSyncUi.progressLabel = formatYandexSyncProgress(st.progress, yandexSyncUi.syncMode);
      const statusEl = $('sync-progress-status');
      const pct = syncProgressPercent(st.progress, yandexSyncUi.syncMode);
      if (statusEl) statusEl.textContent = yandexSyncUi.progressLabel;
      const bar = document.querySelector('.sync-progress-bar-inner');
      if (bar) bar.style.width = `${pct}%`;
      const ringFg = document.querySelector('.sync-progress-ring-fg');
      if (ringFg) ringFg.style.strokeDashoffset = `${328 - (328 * pct) / 100}px`;
      const pctEl = document.querySelector('.sync-progress-pct');
      if (pctEl) pctEl.textContent = `${pct}%`;

      if (st.status === 'done') {
        stopYandexSyncPoll();
        if (yandexSyncUi.syncMode === 'playlists') {
          yandexSyncUi.playlistResults = (st.result && st.result.playlists) || [];
          yandexSyncUi.step = 'playlist_review';
          mountSyncPageContent();
          const sum = st.result && st.result.summary;
          if (sum) {
            toast(
              `Готово: ${sum.playlistsReady || 0} плейлистов, ${sum.tracksTotal || 0} треков${sum.errors ? `, ошибок: ${sum.errors}` : ''}`,
              sum.errors ? 'info' : 'success'
            );
          }
          return;
        }
        const items = (st.result && st.result.items) || [];
        yandexSyncUi.items = items;
        yandexSyncUi.selected = new Set(
          items.filter((it) => it.selectedByDefault !== false && !it.alreadyInFavorites).map((it) => it.trackId)
        );
        yandexSyncUi.step = 'review';
        mountSyncPageContent();
        const sum = st.result && st.result.summary;
        if (sum) {
          const limitNote =
            sum.limited && sum.likedTotalInYandex
              ? ` (в Яндексе ${sum.likedTotalInYandex}, обработано ${sum.syncLimit || 300})`
              : '';
          toast(
            `Готово: ${sum.readyToAdd || 0} можно добавить, ${sum.alreadyFavorite || 0} уже в ♥${sum.errors ? `, ошибок: ${sum.errors}` : ''}${limitNote}`,
            sum.errors || sum.limited ? 'info' : 'success'
          );
        }
        return;
      }
      if (st.status === 'failed' || st.status === 'cancelled') {
        stopYandexSyncPoll();
        yandexSyncUi.step = yandexSyncUi.syncMode === 'playlists' ? 'pick_playlists' : 'connect';
        yandexSyncUi.jobId = null;
        mountSyncPageContent();
        toast(st.error || 'Синхронизация прервана', 'error');
        return;
      }
    } catch (e) {
      stopYandexSyncPoll();
      yandexSyncUi.step = 'connect';
      yandexSyncUi.jobId = null;
      mountSyncPageContent();
      toast(e.message || 'Ошибка опроса', 'error');
      return;
    }
    yandexSyncUi.pollTimer = setTimeout(tick, 1100);
  };
  await tick();
}

async function startYandexSyncJob() {
  const st = yandexSyncUi.linkStatus;
  if (!st || !st.linked) {
    toast('Сначала сохраните токен', 'error');
    return;
  }
  stopYandexSyncPoll();
  yandexSyncUi.step = 'progress';
  yandexSyncUi.lastProgress = { phase: 'queued', current: 0, total: 0 };
  yandexSyncUi.progressLabel = 'Запуск…';
  mountSyncPageContent();
  try {
    const started = await POST('/api/me/yandex-music/sync-likes', {});
    yandexSyncUi.jobId = started && started.jobId;
    if (!yandexSyncUi.jobId) throw new Error('Нет id задачи');
    void runYandexSyncPollLoop();
  } catch (e) {
    yandexSyncUi.step = 'connect';
    yandexSyncUi.jobId = null;
    mountSyncPageContent();
    toast(e.message || 'Не удалось запустить', 'error');
  }
}

async function cancelYandexSyncJob() {
  const jobId = yandexSyncUi.jobId;
  if (!jobId) {
    yandexSyncUi.step = yandexSyncUi.syncMode === 'playlists' ? 'pick_playlists' : 'connect';
    mountSyncPageContent();
    return;
  }
  const btn = $('sync-cancel-btn');
  if (btn) btn.disabled = true;
  try {
    const path =
      yandexSyncUi.syncMode === 'playlists'
        ? `/api/me/yandex-music/sync-playlists/${encodeURIComponent(jobId)}`
        : `/api/me/yandex-music/sync-likes/${encodeURIComponent(jobId)}`;
    await DEL(path);
    stopYandexSyncPoll();
    yandexSyncUi.step = yandexSyncUi.syncMode === 'playlists' ? 'pick_playlists' : 'connect';
    yandexSyncUi.jobId = null;
    mountSyncPageContent();
    toast('Синхронизация остановлена', 'info');
  } catch (e) {
    toast(e.message || 'Ошибка', 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

function toggleYandexSyncTrackSelection(trackId, on) {
  const id = String(trackId || '');
  if (!id) return;
  if (on) yandexSyncUi.selected.add(id);
  else yandexSyncUi.selected.delete(id);
  const countEl = document.querySelector('.sync-review-count');
  const applyBtn = $('sync-apply-btn');
  const n = yandexSyncUi.items.filter((it) => yandexSyncUi.selected.has(it.trackId)).length;
  if (countEl) countEl.textContent = `${n} выбрано`;
  if (applyBtn) applyBtn.textContent = `Добавить в избранное (${n})`;
}

function toggleYandexSyncSelectAll(select) {
  const items = yandexSyncUi.items || [];
  yandexSyncUi.selected = new Set();
  if (select) {
    for (const it of items) {
      if (!it.alreadyInFavorites) yandexSyncUi.selected.add(it.trackId);
    }
  }
  mountSyncPageContent();
}

async function applyYandexSyncFavorites() {
  const ids = [...yandexSyncUi.selected];
  if (!ids.length) {
    toast('Выберите хотя бы один трек', 'error');
    return;
  }
  const btn = $('sync-apply-btn');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Сохраняем…';
  }
  try {
    const jobId = yandexSyncUi.jobId;
    if (!jobId) {
      toast('Сессия синхронизации устарела — запустите снова', 'error');
      return;
    }
    const res = await POST('/api/me/yandex-music/sync-likes/apply', { jobId, trackIds: ids });
    yandexSyncUi.jobId = null;
    const extra =
      res.rejected > 0 ? `, отклонено: ${res.rejected}` : '';
    toast(`Добавлено в избранное: ${res.added || 0}${extra}`, 'success');
    navigate('library');
  } catch (e) {
    toast(e.message || 'Не удалось сохранить', 'error');
    if (btn) {
      btn.disabled = false;
      btn.textContent = `Добавить в избранное (${ids.length})`;
    }
  }
}

function resetYandexSyncPage() {
  stopYandexSyncPoll();
  const mode = yandexSyncUi.syncMode;
  yandexSyncUi = {
    syncMode: mode,
    step: 'connect',
    linkStatus: yandexSyncUi.linkStatus || { linked: false },
    jobId: null,
    items: [],
    selected: new Set(),
    playlists: [],
    selectedPlaylists: new Set(),
    playlistResults: [],
    pollTimer: null
  };
  void refreshYandexSyncLinkStatus().then(() => {
    mountSyncPageContent();
  });
}

async function openYandexPlaylistPicker() {
  const st = yandexSyncUi.linkStatus;
  if (!st || !st.linked) {
    toast('Сначала сохраните токен', 'error');
    return;
  }
  const btn = $('sync-start-btn');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Загрузка…';
  }
  try {
    const data = await GET('/api/me/yandex-music/playlists');
    yandexSyncUi.playlists = (data && data.playlists) || [];
    yandexSyncUi.selectedPlaylists = new Set(
      yandexSyncUi.playlists.filter((pl) => (pl.trackCount || 0) > 0).map((pl) => pl.kind)
    );
    yandexSyncUi.step = 'pick_playlists';
    mountSyncPageContent();
    if (!yandexSyncUi.playlists.length) {
      toast('Собственных плейлистов не найдено', 'info');
    }
  } catch (e) {
    toast(e.message || 'Не удалось загрузить плейлисты', 'error');
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Выбрать плейлисты';
    }
  }
}

function toggleYandexPlaylistSelection(kind, on) {
  const k = Number(kind);
  if (!Number.isFinite(k)) return;
  if (on) yandexSyncUi.selectedPlaylists.add(k);
  else yandexSyncUi.selectedPlaylists.delete(k);
  const countEl = document.querySelector('.sync-review-count');
  const startBtn = $('sync-playlist-start-btn');
  const n = yandexSyncUi.selectedPlaylists.size;
  if (countEl) countEl.textContent = `${n} выбрано`;
  if (startBtn) startBtn.textContent = `Импортировать (${n})`;
}

function toggleYandexPlaylistSelectAll(select) {
  yandexSyncUi.selectedPlaylists = new Set();
  if (select) {
    for (const pl of yandexSyncUi.playlists || []) {
      yandexSyncUi.selectedPlaylists.add(pl.kind);
    }
  }
  mountSyncPageContent();
}

async function startYandexPlaylistSyncJob() {
  const kinds = [...yandexSyncUi.selectedPlaylists];
  if (!kinds.length) {
    toast('Выберите хотя бы один плейлист', 'error');
    return;
  }
  stopYandexSyncPoll();
  yandexSyncUi.step = 'progress';
  yandexSyncUi.lastProgress = { phase: 'queued', current: 0, total: 0 };
  yandexSyncUi.progressLabel = 'Запуск…';
  mountSyncPageContent();
  try {
    const started = await POST('/api/me/yandex-music/sync-playlists', { kinds });
    yandexSyncUi.jobId = started && started.jobId;
    if (!yandexSyncUi.jobId) throw new Error('Нет id задачи');
    void runYandexSyncPollLoop();
  } catch (e) {
    yandexSyncUi.step = 'pick_playlists';
    yandexSyncUi.jobId = null;
    mountSyncPageContent();
    toast(e.message || 'Не удалось запустить', 'error');
  }
}

async function applyYandexPlaylistSync() {
  const kinds = [...yandexSyncUi.selectedPlaylists];
  if (!kinds.length) {
    toast('Нет плейлистов для создания', 'error');
    return;
  }
  const btn = $('sync-playlist-apply-btn');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Создаём…';
  }
  try {
    const jobId = yandexSyncUi.jobId;
    if (!jobId) {
      toast('Сессия синхронизации устарела — запустите снова', 'error');
      return;
    }
    const res = await POST('/api/me/yandex-music/sync-playlists/apply', { jobId, playlistKinds: kinds });
    yandexSyncUi.jobId = null;
    toast(`Создано плейлистов: ${res.created || 0}, треков: ${res.tracksAdded || 0}`, 'success');
    navigate('library');
  } catch (e) {
    toast(e.message || 'Не удалось создать плейлисты', 'error');
    if (btn) {
      btn.disabled = false;
      btn.textContent = `Создать плейлисты (${kinds.length})`;
    }
  }
}

window.setYandexSyncMode = setYandexSyncMode;
window.openYandexPlaylistPicker = openYandexPlaylistPicker;
window.toggleYandexPlaylistSelection = toggleYandexPlaylistSelection;
window.toggleYandexPlaylistSelectAll = toggleYandexPlaylistSelectAll;
window.startYandexPlaylistSyncJob = startYandexPlaylistSyncJob;
window.applyYandexPlaylistSync = applyYandexPlaylistSync;
window.openYandexOAuthAuthorize = openYandexOAuthAuthorize;
window.copyYandexOAuthAuthorizeLink = copyYandexOAuthAuthorizeLink;
window.handleYandexTokenPaste = handleYandexTokenPaste;
window.saveYandexSyncToken = saveYandexSyncToken;
window.unlinkYandexSyncAccount = unlinkYandexSyncAccount;
window.startYandexSyncJob = startYandexSyncJob;
window.cancelYandexSyncJob = cancelYandexSyncJob;
window.toggleYandexSyncTrackSelection = toggleYandexSyncTrackSelection;
window.toggleYandexSyncSelectAll = toggleYandexSyncSelectAll;
window.applyYandexSyncFavorites = applyYandexSyncFavorites;
window.resetYandexSyncPage = resetYandexSyncPage;
window.stopYandexSyncPoll = stopYandexSyncPoll;

// ─── Страница «Памяти Oliver Tree» ───────────────────────────────────────────

const OLIVER_MEMORIAL_PORTRAIT = '/images/oliver-tree-memorial.png?v=2';

const OLIVER_MEMORIAL_CLIPS = [
  {
    title: "I'm Gone",
    video: '/uploads/video/Oliver_Tree-Im_Gone.mp4',
    trackId: '419131a3-0d2f-46a4-becb-23bb52869c05',
    ambientIntervalMs: 3000
  },
  {
    title: 'Flowers',
    video: '/uploads/video/Oliver_Tree-Flowers.mp4',
    trackId: '790c4de4-7e3d-4aa6-b9e2-04d5faf7aa62',
    ambientIntervalMs: 10000
  },
  {
    title: 'Hurt',
    video: '/uploads/video/Oliver_Tree-Hurt.mp4',
    trackId: 'a8d62b9f-b91e-4ed8-84f5-fb2b08408658',
    ambientIntervalMs: 10000
  }
];

const _oliverMemorialTrackCache = new Map();
const _memorialVideoPosterCache = new Map();

function mxmMemorialPortraitUrl() {
  return typeof resolveMediaUrl === 'function'
    ? resolveMediaUrl(OLIVER_MEMORIAL_PORTRAIT)
    : OLIVER_MEMORIAL_PORTRAIT;
}

function mxmOpenMemorialPage() {
  if (typeof navigate === 'function') {
    navigate('oliver-memorial');
    return;
  }
  if (typeof openSameAppInNewTab === 'function') {
    openSameAppInNewTab('oliver-memorial');
  }
}

function mxmMemorialPlaqueHtml(variant = 'sidebar') {
  const portrait = esc(mxmMemorialPortraitUrl());
  if (variant === 'desktop-app') {
    return `
    <button type="button" class="mxm-memorial-compact" aria-label="Памяти Oliver Tree">
      <span class="mxm-memorial-compact-thumb" aria-hidden="true">
        <img src="${portrait}" alt="" loading="lazy" decoding="async" />
      </span>
      <span class="mxm-memorial-compact-body">
        <span class="mxm-memorial-compact-eyebrow">In memory of</span>
        <span class="mxm-memorial-compact-title">Oliver Tree</span>
      </span>
    </button>
  `;
  }
  const cls = `mxm-memorial-plaque mxm-memorial-plaque--${variant}`;
  return `
    <button type="button" class="${cls}" onclick="mxmOpenMemorialPage()">
      <span class="mxm-memorial-plaque-media" aria-hidden="true">
        <img src="${portrait}" alt="" loading="lazy" decoding="async" />
        <span class="mxm-memorial-plaque-shade"></span>
      </span>
      <span class="mxm-memorial-plaque-body">
        <span class="mxm-memorial-plaque-eyebrow">In memory of</span>
        <span class="mxm-memorial-plaque-title">Oliver Tree</span>
        <span class="mxm-memorial-plaque-sub">Памяти артиста</span>
      </span>
      <span class="mxm-memorial-plaque-arrow" aria-hidden="true">→</span>
    </button>
  `;
}

function mountSidebarMemorialPlaque() {
  const host = document.getElementById('sidebar-memorial-plaque');
  if (!host) return;
  // Плашку в сайдбаре убрали — остаётся только ссылка в подвале.
  host.hidden = true;
  host.classList.add('hidden');
  host.setAttribute('aria-hidden', 'true');
  host.innerHTML = '';
}

function bindDesktopMemorialPlaqueInteraction() {
  if (window._mxmDesktopMemorialClickBound) return;
  window._mxmDesktopMemorialClickBound = true;
  document.addEventListener('click', (e) => {
    const btn = e.target?.closest?.('.mxm-memorial-compact');
    if (!btn) return;
    e.preventDefault();
    mxmOpenMemorialPage();
  });
}

function mountDesktopAppMemorialPlaque() {
  // Плашку справа убрали — остаётся ссылка «Памяти Oliver Tree» внизу страницы.
  const host = document.getElementById('mxm-desktop-memorial-plaque');
  if (host) {
    host.hidden = true;
    host.innerHTML = '';
    host.remove();
  }
}

window.mxmOpenMemorialPage = mxmOpenMemorialPage;
window.mountSidebarMemorialPlaque = mountSidebarMemorialPlaque;
window.mountDesktopAppMemorialPlaque = mountDesktopAppMemorialPlaque;

function stopOliverMemorialVideo() {
  teardownMemorialAmbient();
  document.querySelectorAll('.oliver-memorial-video-player').forEach((v) => {
    try {
      v.pause();
    } catch (_) {}
  });
}

// ─── Ambient фон от видео (YouTube-style) ───────────────────────────────────

const MEMORIAL_AMBIENT_INTERVAL_DEFAULT_MS = 10000;
const MEMORIAL_AMBIENT_CROSSFADE_MS = 2600;
const MEMORIAL_AMBIENT_INTRO_MS = 2600;
const MEMORIAL_AMBIENT_EDGE_RATIO = 0.06;
const MEMORIAL_AMBIENT_SAMPLE_COLS = 6;
const MEMORIAL_AMBIENT_SAMPLE_ROWS = 36;

const _memorialAmbient = {
  timer: null,
  video: null,
  canvas: null,
  ctx: null,
  bgLayerActive: 'a',
  intensity: 0,
  left: { r: 59, g: 91, b: 219 },
  right: { r: 255, g: 77, b: 166 },
  mid: { r: 120, g: 96, b: 200 }
};

function memorialAmbientIntervalForVideo(video) {
  const raw = Number(video?.getAttribute('data-ambient-interval'));
  return Number.isFinite(raw) && raw >= 1000 ? raw : MEMORIAL_AMBIENT_INTERVAL_DEFAULT_MS;
}

function memorialAmbientReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) {
    return false;
  }
}

function getMemorialBgRoot() {
  return document.querySelector('[data-memorial-bg]');
}

function ensureMemorialBgLayers() {
  const root = getMemorialBgRoot();
  if (!root) return null;
  if (!root.querySelector('.oliver-memorial-bg-layer--a')) {
    root.innerHTML = `
      <div class="oliver-memorial-bg-layer oliver-memorial-bg-layer--a is-active"></div>
      <div class="oliver-memorial-bg-layer oliver-memorial-bg-layer--b"></div>
    `;
  }
  return root;
}

function memorialAmbientCssColor(rgb, alpha = 0.78) {
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function buildMemorialPageBackground(left, right, mid, intensity = 0) {
  const i = Math.min(1, Math.max(0, intensity));
  const side = 0.52 + i * 0.4;
  const core = 0.44 + i * 0.46;
  const wash = 0.28 + i * 0.42;

  return [
    `radial-gradient(ellipse 135% 100% at 50% 44%, ${memorialAmbientCssColor(mid, core)} 0%, transparent 66%)`,
    `radial-gradient(ellipse 95% 105% at -4% 50%, ${memorialAmbientCssColor(left, side)} 0%, transparent 60%)`,
    `radial-gradient(ellipse 95% 105% at 104% 50%, ${memorialAmbientCssColor(right, side)} 0%, transparent 60%)`,
    `linear-gradient(180deg, ${memorialAmbientCssColor(mid, wash)} 0%, ${memorialAmbientCssColor(left, wash * 0.72)} 38%, #070b16 100%)`
  ].join(', ');
}

function memorialRgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  switch (max) {
    case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
    case g: h = ((b - r) / d + 2) / 6; break;
    default: h = ((r - g) / d + 4) / 6; break;
  }
  return { h, s, l };
}

function memorialHslToRgb(h, s, l) {
  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255)
  };
}

function memorialColorLuminance(rgb) {
  return (rgb.r * 0.2126 + rgb.g * 0.7152 + rgb.b * 0.0722) / 255;
}

function memorialBlendRgb(a, b, t = 0.5) {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t)
  };
}

function memorialPixelWarmth(r, g, b) {
  const max = Math.max(r, g, b);
  if (max < 18) return 0;
  const hsl = memorialRgbToHsl(r, g, b);
  const h = hsl.h;
  const isWarmHue = h <= 0.15 || h >= 0.94;
  if (!isWarmHue) return hsl.s * 0.28;

  const orangeHue = h >= 0.94 ? 0.02 : h;
  const hueWeight = Math.max(0.12, 1 - Math.abs(orangeHue - 0.075) / 0.11);
  const sandPenalty = hsl.l > 0.68 && hsl.s < 0.42 ? 0.28 : 1;
  const yellowPenalty = hsl.h > 0.1 && hsl.h < 0.17 && hsl.l > 0.55 ? 0.55 : 1;

  return hsl.s * hueWeight * sandPenalty * yellowPenalty * (0.35 + hsl.l * 0.65);
}

function memorialAveragePixels(data, width, height, colStart, colEnd, { weighted = false, warm = false, minLum = 0.003 } = {}) {
  let r = 0;
  let g = 0;
  let b = 0;
  let weight = 0;
  for (let y = 0; y < height; y++) {
    for (let x = colStart; x < colEnd; x++) {
      const idx = (y * width + x) * 4;
      if (data[idx + 3] < 16) continue;
      const pr = data[idx];
      const pg = data[idx + 1];
      const pb = data[idx + 2];
      const lum = (pr * 0.2126 + pg * 0.7152 + pb * 0.0722) / 255;
      if (lum < minLum) continue;
      let w = 1;
      if (warm) {
        w = Math.max(0.06, memorialPixelWarmth(pr, pg, pb) * (0.2 + lum));
      } else if (weighted) {
        w = Math.max(0.08, lum * lum * 3);
      }
      r += pr * w;
      g += pg * w;
      b += pb * w;
      weight += w;
    }
  }
  if (!weight) return null;
  return { r: Math.round(r / weight), g: Math.round(g / weight), b: Math.round(b / weight) };
}

function memorialExtractWarmAccent(data, width, height, colStart, colEnd) {
  const picks = [];
  for (let y = 0; y < height; y++) {
    for (let x = colStart; x < colEnd; x++) {
      const idx = (y * width + x) * 4;
      if (data[idx + 3] < 16) continue;
      const pr = data[idx];
      const pg = data[idx + 1];
      const pb = data[idx + 2];
      const warmth = memorialPixelWarmth(pr, pg, pb);
      if (warmth > 0.1) picks.push({ r: pr, g: pg, b: pb, warmth });
    }
  }
  if (picks.length < 4) return null;
  picks.sort((a, b) => b.warmth - a.warmth);
  const top = picks.slice(0, Math.max(4, Math.ceil(picks.length * 0.24)));
  let r = 0;
  let g = 0;
  let b = 0;
  let weight = 0;
  for (const px of top) {
    r += px.r * px.warmth;
    g += px.g * px.warmth;
    b += px.b * px.warmth;
    weight += px.warmth;
  }
  if (!weight) return null;
  return { r: Math.round(r / weight), g: Math.round(g / weight), b: Math.round(b / weight) };
}

function memorialStripWarmthScore(data, width, height, colStart, colEnd) {
  let score = 0;
  let n = 0;
  for (let y = 0; y < height; y++) {
    for (let x = colStart; x < colEnd; x++) {
      const idx = (y * width + x) * 4;
      if (data[idx + 3] < 16) continue;
      score += memorialPixelWarmth(data[idx], data[idx + 1], data[idx + 2]);
      n += 1;
    }
  }
  return n ? score / n : 0;
}

function enhanceMemorialAmbientColor(rgb, intensity = 0, warmScene = false) {
  const i = Math.min(1, Math.max(0, intensity));
  const hsl = memorialRgbToHsl(rgb.r, rgb.g, rgb.b);
  const isWarm = warmScene || hsl.h <= 0.15 || hsl.h >= 0.94;

  if (isWarm && i > 0.18) {
    let hue = hsl.h >= 0.94 ? 0.03 : hsl.h;
    if (hue > 0.095 && hue < 0.18) hue = 0.06 + (hue - 0.095) * 0.35;
    hsl.h = hue;
    hsl.s = Math.min(1, hsl.s * (1.42 + i * 0.38) + 0.24);
    hsl.l = Math.min(0.34 + i * 0.54, Math.max(0.16, hsl.l * (1.02 + i * 0.18) + i * 0.12));
  } else {
    hsl.s = Math.min(1, hsl.s * (1.28 + i * 0.45) + 0.16);
    hsl.l = Math.min(0.24 + i * 0.46, Math.max(0.12, hsl.l * (1.04 + i * 0.22) + i * 0.1));
  }

  let out = memorialHslToRgb(hsl.h, hsl.s, hsl.l);
  if (isWarm && i > 0.18) {
    out.r = Math.min(255, Math.round(out.r * (1.06 + i * 0.14)));
    out.g = Math.round(out.g * (0.9 + i * 0.06));
    out.b = Math.round(out.b * (0.72 + i * 0.04));
  }
  return out;
}

function memorialPrepareAmbientSample(rawLeft, rawRight, warmScene = false) {
  const leftLum = memorialColorLuminance(rawLeft);
  const rightLum = memorialColorLuminance(rawRight);
  const edgeLum = (leftLum + rightLum) / 2;
  let intensity = Math.min(1, Math.max(0, (edgeLum - 0.03) / 0.45));
  if (warmScene) intensity = Math.min(1, intensity * 1.12 + 0.14);

  const left = enhanceMemorialAmbientColor(rawLeft, intensity, warmScene);
  const right = enhanceMemorialAmbientColor(rawRight, intensity, warmScene);
  const mid = enhanceMemorialAmbientColor(memorialBlendRgb(left, right, 0.5), intensity, warmScene);

  return { left, right, mid, intensity };
}

function memorialSampleEdgeStrip(data, width, height, colStart, colEnd, warmScene) {
  if (warmScene) {
    return (
      memorialExtractWarmAccent(data, width, height, colStart, colEnd) ||
      memorialAveragePixels(data, width, height, colStart, colEnd, { warm: true, minLum: 0.002 })
    );
  }
  return memorialAveragePixels(data, width, height, colStart, colEnd, { weighted: true, minLum: 0.002 });
}

function getMemorialAmbientSampler() {
  if (!_memorialAmbient.canvas) {
    _memorialAmbient.canvas = document.createElement('canvas');
    _memorialAmbient.ctx = _memorialAmbient.canvas.getContext('2d', { willReadFrequently: true });
  }
  return _memorialAmbient;
}

function sampleMemorialVideoAmbient(video) {
  if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return null;
  const { canvas, ctx } = getMemorialAmbientSampler();
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const stripW = Math.max(2, Math.round(vw * MEMORIAL_AMBIENT_EDGE_RATIO));
  const cols = MEMORIAL_AMBIENT_SAMPLE_COLS;
  const rows = MEMORIAL_AMBIENT_SAMPLE_ROWS;
  canvas.width = cols * 2;
  canvas.height = rows;
  try {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(video, 0, 0, stripW, vh, 0, 0, cols, rows);
    ctx.drawImage(video, vw - stripW, 0, stripW, vh, cols, 0, cols, rows);
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const warmLeft = memorialStripWarmthScore(data, canvas.width, canvas.height, 0, cols);
    const warmRight = memorialStripWarmthScore(data, canvas.width, canvas.height, cols, cols * 2);
    const warmScene = (warmLeft + warmRight) / 2 > 0.14;
    let rawLeft = memorialSampleEdgeStrip(data, canvas.width, canvas.height, 0, cols, warmScene);
    let rawRight = memorialSampleEdgeStrip(data, canvas.width, canvas.height, cols, cols * 2, warmScene);
    if (!rawLeft && !rawRight) return null;
    if (!rawLeft) rawLeft = rawRight;
    if (!rawRight) rawRight = rawLeft;
    return memorialPrepareAmbientSample(rawLeft, rawRight, warmScene);
  } catch (_) {
    return null;
  }
}

function applyMemorialBgCrossfade(root, pageBg, isFirst = false) {
  const layerA = root.querySelector('.oliver-memorial-bg-layer--a');
  const layerB = root.querySelector('.oliver-memorial-bg-layer--b');
  if (!layerA || !layerB) return;

  if (isFirst) {
    layerB.style.background = '';
    layerA.style.background = pageBg;
    layerA.classList.remove('is-active');
    layerB.classList.remove('is-active');
    _memorialAmbient.bgLayerActive = 'a';
    void root.offsetWidth;
    requestAnimationFrame(() => {
      layerA.classList.add('is-active');
    });
    return;
  }

  const activeIsA = _memorialAmbient.bgLayerActive === 'a';
  const next = activeIsA ? layerB : layerA;
  const current = activeIsA ? layerA : layerB;

  next.style.background = pageBg;
  next.classList.add('is-active');
  current.classList.remove('is-active');
  _memorialAmbient.bgLayerActive = activeIsA ? 'b' : 'a';
}

function applyMemorialGlowColors(left, right, intensity = 0, isFirst = false) {
  const i = Math.min(1, Math.max(0, intensity));
  const targetOpacity = 0.18 + i * 0.42;
  const pink = document.querySelector('.oliver-memorial-glow--pink');
  const blue = document.querySelector('.oliver-memorial-glow--blue');

  const paint = () => {
    if (pink) {
      pink.style.background = memorialAmbientCssColor(right, 1);
      pink.style.opacity = String(targetOpacity);
    }
    if (blue) {
      blue.style.background = memorialAmbientCssColor(left, 1);
      blue.style.opacity = String(targetOpacity);
    }
  };

  if (isFirst) {
    if (pink) {
      pink.style.background = memorialAmbientCssColor(right, 1);
      pink.style.opacity = '0';
    }
    if (blue) {
      blue.style.background = memorialAmbientCssColor(left, 1);
      blue.style.opacity = '0';
    }
    void (pink || blue)?.offsetWidth;
    requestAnimationFrame(() => {
      if (pink) pink.style.opacity = String(targetOpacity);
      if (blue) blue.style.opacity = String(targetOpacity);
    });
    return;
  }

  paint();
}

function applyMemorialVideoAmbient(left, right, mid, intensity = 0) {
  const root = ensureMemorialBgLayers();
  if (!root) return;

  const isFirst = !root.classList.contains('is-live');
  const pageBg = buildMemorialPageBackground(left, right, mid, intensity);
  applyMemorialBgCrossfade(root, pageBg, isFirst);
  applyMemorialGlowColors(left, right, intensity, isFirst);
  root.classList.add('is-live');

  if (isFirst) {
    root.classList.remove('is-ambient-visible');
    window.setTimeout(() => {
      if (root.classList.contains('is-live')) root.classList.add('is-ambient-visible');
    }, MEMORIAL_AMBIENT_INTRO_MS);
  }

  _memorialAmbient.left = left;
  _memorialAmbient.right = right;
  _memorialAmbient.mid = mid;
  _memorialAmbient.intensity = intensity;
  document.body.classList.add('mxm-memorial-ambient-live');
  document.body.classList.toggle('mxm-memorial-ambient-bright', intensity > 0.18);
}

function tickMemorialVideoAmbient() {
  const video = _memorialAmbient.video;
  if (!video || video.paused || video.ended) return;
  const sample = sampleMemorialVideoAmbient(video);
  if (sample) applyMemorialVideoAmbient(sample.left, sample.right, sample.mid, sample.intensity);
}

function startMemorialVideoAmbient(video) {
  if (memorialAmbientReducedMotion()) return;
  stopMemorialVideoAmbientTimer();
  _memorialAmbient.video = video;
  const run = () => tickMemorialVideoAmbient();
  const intervalMs = memorialAmbientIntervalForVideo(video);
  run();
  _memorialAmbient.timer = window.setInterval(run, intervalMs);
}

function stopMemorialVideoAmbientTimer() {
  if (_memorialAmbient.timer) {
    clearInterval(_memorialAmbient.timer);
    _memorialAmbient.timer = null;
  }
}

function stopMemorialVideoAmbient() {
  stopMemorialVideoAmbientTimer();
}

function teardownMemorialAmbient() {
  stopMemorialVideoAmbientTimer();
  _memorialAmbient.video = null;
  _memorialAmbient.bgLayerActive = 'a';
  _memorialAmbient.intensity = 0;
  const root = getMemorialBgRoot();
  if (root) {
    root.classList.remove('is-live', 'is-ambient-visible');
    root.innerHTML = '';
  }
  document.querySelector('.oliver-memorial-glow--pink')?.style.removeProperty('background');
  document.querySelector('.oliver-memorial-glow--pink')?.style.removeProperty('opacity');
  document.querySelector('.oliver-memorial-glow--blue')?.style.removeProperty('background');
  document.querySelector('.oliver-memorial-glow--blue')?.style.removeProperty('opacity');
  document.getElementById('memorial-ambient-root')?.remove();
  document.body.classList.remove('mxm-memorial-ambient-live');
  document.body.classList.remove('mxm-memorial-ambient-bright');
}



function memorialVideoSourceKey(video) {
  return video?.currentSrc || video?.querySelector('source')?.src || video?.getAttribute('src') || '';
}

function ensureMemorialVideoFirstFramePoster(video) {
  if (!video || video.getAttribute('data-poster-ready') === '1' || video._mxmPosterPending) return;

  const src = memorialVideoSourceKey(video);
  if (src && _memorialVideoPosterCache.has(src)) {
    video.poster = _memorialVideoPosterCache.get(src);
    video.setAttribute('data-poster-ready', '1');
    return;
  }

  video._mxmPosterPending = true;

  const finish = (dataUrl) => {
    if (dataUrl && src) _memorialVideoPosterCache.set(src, dataUrl);
    if (dataUrl && video.paused) video.poster = dataUrl;
    video.setAttribute('data-poster-ready', '1');
    video._mxmPosterPending = false;
  };

  const captureFrame = () => {
    if (!video.videoWidth || !video.videoHeight) {
      video._mxmPosterPending = false;
      return;
    }
    try {
      const maxW = 960;
      const scale = video.videoWidth > maxW ? maxW / video.videoWidth : 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      finish(canvas.toDataURL('image/jpeg', 0.84));
    } catch (_) {
      video._mxmPosterPending = false;
    }
  };

  const seekAndCapture = () => {
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked);
      captureFrame();
      if (video.paused) {
        try {
          video.currentTime = 0;
        } catch (_) {}
      }
    };
    video.addEventListener('seeked', onSeeked);
    try {
      video.currentTime = 0.001;
    } catch (_) {
      captureFrame();
    }
  };

  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    seekAndCapture();
    return;
  }

  const onLoaded = () => {
    video.removeEventListener('loadeddata', onLoaded);
    seekAndCapture();
  };
  video.addEventListener('loadeddata', onLoaded, { once: true });
  if (video.preload !== 'auto') {
    video.preload = 'auto';
    try {
      video.load();
    } catch (_) {}
  }
}

function bindMemorialVideoFirstFramePosters() {
  document.querySelectorAll('.oliver-memorial-video-player').forEach((video) => {
    if (video._mxmPosterBound) return;
    video._mxmPosterBound = true;
    ensureMemorialVideoFirstFramePoster(video);
  });
}

async function loadOliverMemorialTrack(trackId) {
  const id = String(trackId || '').trim();
  if (!id) return null;
  if (_oliverMemorialTrackCache.has(id)) return _oliverMemorialTrackCache.get(id);
  const track = await GET(`/api/tracks/${id}`);
  _oliverMemorialTrackCache.set(id, track);
  return track;
}

function oliverMemorialClipHtml(clip, index, total) {
  const videoSrc = esc(resolveMediaUrl(clip.video));
  const trackId = esc(String(clip.trackId || ''));
  const ambientInterval = Number(clip.ambientIntervalMs) || MEMORIAL_AMBIENT_INTERVAL_DEFAULT_MS;
  return `
    <article
      class="oliver-memorial-video-card${index === 0 ? ' is-active' : ''}"
      aria-label="Клип ${esc(clip.title)}"
      data-clip-index="${index}"
    >
      <div class="oliver-memorial-video-head">
        <h2 class="oliver-memorial-video-title">${esc(clip.title)}</h2>
        <p class="oliver-memorial-video-sub">Oliver Tree</p>
      </div>
      <div class="oliver-memorial-video-frame">
        <video
          class="oliver-memorial-video-player"
          controls
          playsinline
          preload="auto"
          crossorigin="anonymous"
          data-track-id="${trackId}"
          data-ambient-interval="${ambientInterval}"
        >
          <source src="${videoSrc}" type="video/mp4" />
          Ваш браузер не поддерживает воспроизведение видео.
        </video>
      </div>
    </article>
  `;
}

function oliverMemorialVideosCarouselHtml(clips) {
  const total = clips.length;
  const cards = clips
    .map((clip, i) => oliverMemorialClipHtml(clip, i, total))
    .join('');
  const dots = clips
    .map(
      (_, i) =>
        `<button type="button" class="oliver-memorial-videos-dot${i === 0 ? ' is-active' : ''}" data-clip-dot="${i}" aria-label="Клип ${i + 1}"></button>`
    )
    .join('');

  return `
    <section class="oliver-memorial-videos" aria-label="Клипы Oliver Tree">
      <div class="oliver-memorial-videos-toolbar">
        <p class="oliver-memorial-videos-label">Клипы</p>
        <div class="oliver-memorial-videos-controls">
          <button type="button" class="oliver-memorial-videos-arrow" data-clip-prev aria-label="Предыдущий клип">‹</button>
          <span class="oliver-memorial-videos-counter" data-clip-counter>1 / ${total}</span>
          <button type="button" class="oliver-memorial-videos-arrow" data-clip-next aria-label="Следующий клип">›</button>
        </div>
      </div>
      <div class="oliver-memorial-videos-track" data-clip-track tabindex="0">
        ${cards}
      </div>
      <div class="oliver-memorial-videos-dots" data-clip-dots>${dots}</div>
    </section>
  `;
}

function bindOliverMemorialVideosCarousel() {
  const track = document.querySelector('[data-clip-track]');
  if (!track || track._mxmCarouselBound) return;
  track._mxmCarouselBound = true;

  const cards = () => Array.from(track.querySelectorAll('.oliver-memorial-video-card'));
  const counter = document.querySelector('[data-clip-counter]');
  const dots = Array.from(document.querySelectorAll('[data-clip-dot]'));
  const prevBtn = document.querySelector('[data-clip-prev]');
  const nextBtn = document.querySelector('[data-clip-next]');
  let activeIndex = 0;

  const showIndex = (index) => {
    const list = cards();
    if (!list.length) return;
    activeIndex = Math.max(0, Math.min(list.length - 1, index));
    list.forEach((card, i) => {
      card.classList.toggle('is-active', i === activeIndex);
      if (i !== activeIndex) {
        const inactiveVideo = card.querySelector('.oliver-memorial-video-player');
        if (inactiveVideo && !inactiveVideo.paused) {
          try {
            inactiveVideo.pause();
          } catch (_) {}
        }
      }
    });
    if (counter) counter.textContent = `${activeIndex + 1} / ${list.length}`;
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === activeIndex));
    const activeVideo = list[activeIndex]?.querySelector('.oliver-memorial-video-player');
    if (activeVideo) ensureMemorialVideoFirstFramePoster(activeVideo);
  };

  prevBtn?.addEventListener('click', () => showIndex(activeIndex - 1));
  nextBtn?.addEventListener('click', () => showIndex(activeIndex + 1));

  dots.forEach((dot) => {
    dot.addEventListener('click', () => {
      const idx = Number(dot.getAttribute('data-clip-dot'));
      if (Number.isFinite(idx)) showIndex(idx);
    });
  });

  showIndex(0);
}

function bindOliverMemorialVideoPlayers() {
  document.querySelectorAll('.oliver-memorial-video-player').forEach((video) => {
    if (video._mxmMemorialBound) return;
    video._mxmMemorialBound = true;
    video.volume = 0.5;
    video.addEventListener('loadedmetadata', () => {
      video.volume = 0.5;
    });

    video.addEventListener('play', async () => {
      startMemorialVideoAmbient(video);
      const trackId = video.getAttribute('data-track-id');
      if (!trackId) return;
      try {
        document.querySelectorAll('.oliver-memorial-video-player').forEach((other) => {
          if (other !== video && !other.paused) {
            try {
              other.pause();
            } catch (_) {}
          }
        });
        const track = await loadOliverMemorialTrack(trackId);
        if (!track || typeof prepareTrack !== 'function') return;
        if (typeof audio !== 'undefined' && audio && !audio.paused) {
          audio.pause();
        }
        prepareTrack(track);
        if (typeof syncRadioPlayButtonState === 'function') syncRadioPlayButtonState();
        if (typeof updatePlayingRows === 'function') updatePlayingRows();
      } catch (_) {}
    });

    video.addEventListener('pause', () => {
      if (_memorialAmbient.video === video) stopMemorialVideoAmbientTimer();
    });

    video.addEventListener('ended', () => {
      if (_memorialAmbient.video === video) stopMemorialVideoAmbientTimer();
    });
  });
}

function renderOliverMemorialPage() {
  const content = $('content');
  if (!content) return;

  const portrait = esc(mxmMemorialPortraitUrl());
  const clipsHtml = oliverMemorialVideosCarouselHtml(OLIVER_MEMORIAL_CLIPS);

  content.innerHTML = `
    <div class="oliver-memorial-page oliver-memorial-page--enter">
      <div class="oliver-memorial-bg" data-memorial-bg aria-hidden="true"></div>
      <div class="oliver-memorial-glow oliver-memorial-glow--pink" aria-hidden="true"></div>
      <div class="oliver-memorial-glow oliver-memorial-glow--blue" aria-hidden="true"></div>

      <header class="oliver-memorial-hero">
        <div class="oliver-memorial-portrait-ring" aria-hidden="true"></div>
        <div class="oliver-memorial-portrait-wrap oliver-memorial-portrait-wrap--wide">
          <img
            class="oliver-memorial-portrait"
            src="${portrait}"
            alt="Oliver Tree"
            width="960"
            height="420"
            decoding="async"
          />
        </div>
        <p class="oliver-memorial-eyebrow">In memory of</p>
        <h1 class="oliver-memorial-title">Памяти <span>Oliver Tree</span></h1>
        <p class="oliver-memorial-lead">Будем помнить тебя всегда</p>
      </header>

      <article class="oliver-memorial-letter" aria-label="Слово создателя сайта">
        <div class="oliver-memorial-letter-mark" aria-hidden="true">“</div>
        <div class="oliver-memorial-letter-body">
          <p>Я благодарю Оливера за его творчество.</p>
          <p>Единственный артист, у которого не было песен, которые я мог бы перемотать в своём плейлисте.</p>
          <p>Он заряжал меня своей энергией и вдохновлял в любой момент жизни.</p>
          <p class="oliver-memorial-letter-closing">Будем помнить тебя всегда.</p>
        </div>
        <p class="oliver-memorial-signature">— Создатель MXM Music</p>
      </article>

      ${clipsHtml}

      <footer class="oliver-memorial-page-footer">
        <button type="button" class="oliver-memorial-back btn-ghost" onclick="navigate('home')">
          ← На главную
        </button>
        <button type="button" class="oliver-memorial-artist btn-primary">
          Слушать Oliver Tree
        </button>
      </footer>
    </div>
  `;

  bindOliverMemorialArtistButton();
  bindOliverMemorialVideoPlayers();
  bindMemorialVideoFirstFramePosters();
  bindOliverMemorialVideosCarousel();
  bindOliverMemorialPortraitFade();
}

function bindOliverMemorialPortraitFade() {
  const img = document.querySelector('.oliver-memorial-portrait');
  if (!img) return;
  const reveal = () => img.classList.add('is-loaded');
  if (img.complete) reveal();
  else img.addEventListener('load', reveal, { once: true });
}

async function bindOliverMemorialArtistButton() {
  const btn = document.querySelector('.oliver-memorial-artist');
  if (!btn) return;
  try {
    const artists = await GET('/api/artists');
    const oliver = (artists || []).find((a) => /oliver tree/i.test(String(a.name || '')));
    if (oliver?.id) {
      btn.onclick = () => navigate('artist', { id: oliver.id });
    } else {
      btn.onclick = () => navigate('search');
    }
  } catch {
    btn.onclick = () => navigate('search');
  }
}

/* ─── MXM AI (optional; remove this chunk + lib/mxm-ai to delete feature) ─── */

const MXM_AI_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/><path d="M5 16l.8 2.2L8 19l-2.2.8L5 22l-.8-2.2L2 19l2.2-.8L5 16z"/><path d="M18 14l.6 1.6L20 16l-1.4.6L18 18l-.6-1.4L16 16l1.4-.6L18 14z"/></svg>`;

let mxmAiStatusCache = null;
let mxmAiStatusFetchedAt = 0;
let mxmAiPanelEl = null;
let mxmAiFabEl = null;
let mxmAiMenuOpen = false;
let mxmAiState = {
  open: false,
  mode: 'discover',
  artistId: null,
  artistName: '',
  messages: [],
  busy: false
};

async function fetchMxmAiStatus(force = false) {
  const now = Date.now();
  if (!force && mxmAiStatusCache && now - mxmAiStatusFetchedAt < 60_000) {
    return mxmAiStatusCache;
  }
  try {
    const s = await GET('/api/mxm-ai/status');
    mxmAiStatusCache = s;
    mxmAiStatusFetchedAt = now;
    return s;
  } catch (_) {
    mxmAiStatusCache = { enabled: false, artistChat: false, smartSearch: false };
    mxmAiStatusFetchedAt = now;
    return mxmAiStatusCache;
  }
}

function mxmAiHistoryQuery() {
  const mode = mxmAiState.mode === 'artist' ? 'artist' : 'discover';
  const q = new URLSearchParams({ mode });
  if (mode === 'artist' && mxmAiState.artistId) q.set('artistId', mxmAiState.artistId);
  return q.toString();
}

async function loadMxmAiHistory() {
  if (!state.user) return [];
  try {
    const data = await GET(`/api/mxm-ai/history?${mxmAiHistoryQuery()}`);
    return Array.isArray(data?.messages) ? data.messages : [];
  } catch (_) {
    return [];
  }
}

async function clearMxmAiHistoryRemote() {
  if (!state.user) return;
  try {
    await fetch(`/api/mxm-ai/history?${mxmAiHistoryQuery()}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
  } catch (_) {}
}

function ensureMxmAiFab() {
  if (mxmAiFabEl && document.body.contains(mxmAiFabEl)) return mxmAiFabEl;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.id = 'mxm-ai-fab';
  btn.className = 'mxm-ai-fab hidden';
  btn.setAttribute('aria-label', 'Открыть MXM AI');
  btn.innerHTML = `
    <span class="mxm-ai-fab-aura" aria-hidden="true">
      <span class="mxm-ai-fab-blob mxm-ai-fab-blob--violet"></span>
      <span class="mxm-ai-fab-blob mxm-ai-fab-blob--orange"></span>
      <span class="mxm-ai-fab-blob mxm-ai-fab-blob--mix"></span>
    </span>
    <span class="mxm-ai-fab-icon">${MXM_AI_ICON_SVG}</span>
  `;
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (mxmAiState.open) {
      closeMxmAi();
      return;
    }
    openMxmAi({ mode: 'discover' });
  });
  document.body.appendChild(btn);
  mxmAiFabEl = btn;
  return btn;
}

async function syncMxmAiFab() {
  const fab = ensureMxmAiFab();
  const status = await fetchMxmAiStatus();
  const isPhone = typeof window !== 'undefined' && window.innerWidth <= 900;
  const fsOpen =
    (typeof fullscreenActive === 'boolean' && fullscreenActive) ||
    document.documentElement.classList.contains('mxm-fullscreen-player-open');
  const show = !!(status.enabled && !mxmAiState.open && !isPhone && !fsOpen);
  fab.classList.toggle('hidden', !show);
  fab.classList.toggle('mxm-ai-fab--guest', !state.user);
}

function ensureMxmAiPanel() {
  if (mxmAiPanelEl && document.body.contains(mxmAiPanelEl)) return mxmAiPanelEl;
  const el = document.createElement('div');
  el.id = 'mxm-ai-panel';
  el.className = 'mxm-ai-panel hidden';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-label', 'MXM AI');
  el.innerHTML = `
    <div class="mxm-ai-overlay" aria-hidden="true"></div>
    <div class="mxm-ai-sheet">
      <header class="mxm-ai-header">
        <div class="mxm-ai-brand">
          <span class="mxm-ai-brand-mark">${MXM_AI_ICON_SVG}</span>
          <div>
            <div class="mxm-ai-brand-name">MXM AI</div>
            <div class="mxm-ai-brand-sub" id="mxm-ai-subtitle">ИИ-подбор MXM</div>
          </div>
        </div>
        <div class="mxm-ai-header-actions">
          <button type="button" class="mxm-ai-icon-btn" id="mxm-ai-more" aria-label="Ещё" aria-haspopup="true" aria-expanded="false">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><circle cx="5" cy="12" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="19" cy="12" r="1.7"/></svg>
          </button>
          <button type="button" class="mxm-ai-icon-btn mxm-ai-close" data-mxm-ai-close="1" aria-label="Закрыть">
            <svg viewBox="0 0 24 24" width="18" height="18"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
          <div class="mxm-ai-menu hidden" id="mxm-ai-menu" role="menu">
            <button type="button" class="mxm-ai-menu-item" data-mxm-ai-clear="1" role="menuitem">Очистить историю</button>
          </div>
        </div>
      </header>
      <div class="mxm-ai-messages" id="mxm-ai-messages"></div>
      <div class="mxm-ai-chips" id="mxm-ai-chips"></div>
      <form class="mxm-ai-compose" id="mxm-ai-compose">
        <input type="text" id="mxm-ai-input" maxlength="2000" placeholder="Введите сообщение…" autocomplete="off" enterkeyhint="send" />
        <button type="submit" class="mxm-ai-send" aria-label="Отправить">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
        </button>
      </form>
    </div>
  `;
  document.body.appendChild(el);

  const closeMenu = () => {
    mxmAiMenuOpen = false;
    el.querySelector('#mxm-ai-menu')?.classList.add('hidden');
    el.querySelector('#mxm-ai-more')?.setAttribute('aria-expanded', 'false');
  };

  el.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest('[data-mxm-ai-close]')) {
      e.preventDefault();
      e.stopPropagation();
      closeMenu();
      closeMxmAi();
      return;
    }
    if (t.closest('[data-mxm-ai-clear]')) {
      e.preventDefault();
      closeMenu();
      void clearMxmAiConversation();
      return;
    }
    if (!t.closest('.mxm-ai-header-actions')) closeMenu();
  });

  el.querySelector('#mxm-ai-more')?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    mxmAiMenuOpen = !mxmAiMenuOpen;
    el.querySelector('#mxm-ai-menu')?.classList.toggle('hidden', !mxmAiMenuOpen);
    el.querySelector('#mxm-ai-more')?.setAttribute('aria-expanded', mxmAiMenuOpen ? 'true' : 'false');
  });

  el.querySelector('#mxm-ai-compose').addEventListener('submit', (e) => {
    e.preventDefault();
    const input = el.querySelector('#mxm-ai-input');
    const text = (input?.value || '').trim();
    if (!text || mxmAiState.busy) return;
    if (input) input.value = '';
    void mxmAiSend(text);
  });
  mxmAiPanelEl = el;
  return el;
}

const MXM_AI_DISCOVER_CHIP_POOL = [
  'Тревожное под ночной автобус',
  'Энергичное для зала',
  'Грустный клауд',
  'Мрачный witch house',
  'Хардкорный трэп, но не шумный',
  'Что-то как Oxxymiron, но свежее',
  'Спокойное под работу',
  'Танцевальное на пятницу',
  'Депрессивный рэп с духовыми',
  'Джерси-клаб для ночи',
  'Грайм с баттловым вайбом',
  'Пост-панк под дождь',
  'Инди-рок без пафоса',
  'Hyperpop, но мелодичный',
  'Детройт / phonk без мемности',
  'Ло-фай хип-хоп для учёбы',
  'Агрессивный drill',
  'Меланхоличный R&B',
  'Синтвейв в машину',
  'Что-то атмосферное и тёмное',
  'Весёлое без попсы',
  'Русский рэп с текстом',
  'Англоязычное под бег',
  'Медленное и тёплое',
  'Быстрое и злое',
  'Как три дня дождя, но страннее',
  'Что-то экспериментальное',
  'Подборка без известных имён',
  'Найди похожих на Oxxxymiron',
  'Найди похожих на Kanye',
  'Что послушать, если любишь Travis Scott',
  'Вайб как у The Weeknd',
  'Грустное под окно',
  'Для тренировки в зале',
  'Для сна почти, но с битом',
  'Шуточное / ироничное',
  'Серьёзное и социальное',
  'Минимал техно',
  'Хаус для кухни',
  'Trap soul',
  'Cloud rap с мелодией',
  'Эмо-рэп',
  'Что-то в духе Versus / баттлов',
  'Альбомный вайб, не синглы',
  'Короткий микс на 30 минут',
  'Только свежие треки',
  'Классика жанра из каталога',
  'Неочевидные артисты',
  'Подборка «с чего начать рэп»',
  'Что поставить друзьям в машине'
];

const MXM_AI_ARTIST_CHIP_POOL = [
  'С чего начать слушать?',
  'На что похож?',
  'Для какого настроения?',
  'Лучшие треки',
  'Какой альбом первый?',
  'Есть ли альтер-эго / псевдонимы?',
  'Что самое грустное?',
  'Что самое энергичное?',
  'Топ-5 треков по вайбу',
  'Чем он отличается от похожих?',
  'Под какое настроение зайдёт?',
  'Есть ли фиты, которые стоит знать?',
  'Что поставить новичку?',
  'Что поставить фанату жанра?',
  'Самое экспериментальное',
  'Самое «хитовое»',
  'Есть ли мрачные треки?',
  'Есть ли танцевальные?',
  'Расскажи коротко кто это',
  'Почему его слушают?',
  'Что слушать ночью?',
  'Что слушать в зале?',
  'Что слушать в дороге?',
  'Похожие артисты из каталога',
  'Связанные амплуа / ники',
  'Какой релиз ключевой?',
  'Что самое текстовое?',
  'Что самое мелодичное?',
  'Есть ли баттловый вайб?',
  'Есть ли ирония / стёб?',
  'Что из раннего творчества?',
  'Что из позднего периода?',
  'Подборка на 5 треков',
  'Подборка на 10 треков',
  'Запусти что-нибудь характерное',
  'Найди треки в том же вайбе',
  'Сравни с похожими',
  'Для какого возраста / вайба аудитория?',
  'Что не похоже на остальное?',
  'Самое недооценённое',
  'Что чаще всего советуют?',
  'Есть ли спокойные треки?',
  'Есть ли агрессивные?',
  'Что поставить на вечеринку?',
  'Что поставить под работу?',
  'Коротко: 3 причины послушать',
  'Разложи по настроениям',
  'Что слушать, если уже знаю хиты?',
  'Дай маршрут: от простого к сложному',
  'Собери мини-плейлист из его треков'
];

function mxmAiShuffleInPlace(arr) {
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

function mxmAiPickChips(pool, count = 6) {
  const list = mxmAiShuffleInPlace([...(pool || [])]);
  return list.slice(0, Math.min(count, list.length));
}

function mxmAiQuickChips(mode) {
  if (mode === 'artist') {
    return mxmAiPickChips(MXM_AI_ARTIST_CHIP_POOL, 3);
  }
  return mxmAiPickChips(MXM_AI_DISCOVER_CHIP_POOL, 6);
}

function renderMxmAiChips() {
  const wrap = document.getElementById('mxm-ai-chips');
  if (!wrap) return;
  // В режиме артиста чипы-подсказки всегда доступны (доп. вопросы про артиста).
  // В умном поиске — только до первого сообщения.
  if (mxmAiState.mode !== 'artist' && mxmAiState.messages.length > 0) {
    wrap.innerHTML = '';
    wrap.classList.add('hidden');
    return;
  }
  wrap.classList.remove('hidden');
  wrap.innerHTML = mxmAiQuickChips(mxmAiState.mode)
    .map((t) => `<button type="button" class="mxm-ai-chip">${esc(t)}</button>`)
    .join('');
  wrap.querySelectorAll('.mxm-ai-chip').forEach((btn) => {
    btn.onclick = () => void mxmAiSend(btn.textContent || '');
  });
}

function mxmAiFormatDuration(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n <= 0) return '';
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function mxmAiStripIdsClient(text) {
  let out = String(text || '')
    .replace(/<{2,3}\s*MXM[_ ]?META\b[\s\S]*?(?:>{2,3}|$)/gi, '')
    .replace(/["'«»]\s*[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\s*["'«»]/gi, '')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, '')
    .replace(/\bid\s*=\s*[0-9a-f-]{8,}/gi, '')
    .replace(/(^|\n)\s*[-•*]?\s*(?:track|artist|playlist)\s+/gim, '$1')
    .replace(/\s*\|\s*(?:genre|moods?|plays?|bio|txt|kw|lang)\s*:[^|\n]*/gi, '')
    .replace(/\b(?:genre|moods?|plays?|bio|txt|kw|lang)\s*:\s*[^\n|;,]*/gi, '')
    .replace(/\(\s*[^)\n]*(?:moods?|plays?)\s*:[^)\n]*\)/gi, '')
    .replace(/\s*·\s*сниппет\s*:\s*[^\n]*/gi, '')
    .replace(/\s*\|\s*(?=\n|$)/g, '')
    .replace(/[ \t]{2,}/g, ' ');
  out = out.replace(/""+/g, '').replace(/''+/g, '').replace(/«\s*»/g, '');
  out = out.replace(/"([^"\n]{1,100})"/g, '«$1»');
  out = out.replace(/(\S)"(?=\s*[—–·|,;:]|\s*$)/gm, '$1');
  out = out.replace(/(^[-•*]\s+)"([^"\n«»]+?)(?=\s*[—–·]|\s*$)/gm, '$1«$2»');
  out = out.replace(/(^[-•*]\s+)([^«»\n"]{1,100})»(?=\s*[—–])/gm, '$1«$2»');
  return out.trim();
}

function mxmAiFormatReplyHtml(text) {
  let s = esc(mxmAiStripIdsClient(text));
  // Не трогаем подчёркивания внутри обычного текста — только **жирный**.
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/__(.+?)__/g, '<strong>$1</strong>');
  s = s.replace(/(^|\s)[\*]{1,3}(?=\s|$)/g, '$1');
  s = s.replace(/^[-•]\s+(.+)$/gm, '<span class="mxm-ai-li">$1</span>');
  s = s.replace(/\n{2,}/g, '<br><br>');
  s = s.replace(/\n/g, '<br>');
  return s;
}

function mxmAiFormatTime(at) {
  const d = new Date(Number(at) || Date.now());
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (_) {
    return '';
  }
}

function mxmAiTrackCardsHtml(tracks) {
  if (!tracks || !tracks.length) return '';
  const qKey = storeQueue(tracks);
  const playAll =
    tracks.length > 1
      ? `<button type="button" class="mxm-ai-play-all" data-q="${qKey}" data-i="0">▶ Слушать всё</button>`
      : '';
  return `<div class="mxm-ai-media">
    ${playAll}
    <div class="mxm-ai-cards mxm-ai-cards--tracks">${tracks
      .map((t, i) => {
        const cover = t.coverUrl
          ? `<img src="${esc(t.coverUrl)}" alt="" loading="lazy" />`
          : `<div class="mxm-ai-card-ph"></div>`;
        const dur = mxmAiFormatDuration(t.duration);
        return `<button type="button" class="mxm-ai-card mxm-ai-card--track" data-q="${qKey}" data-i="${i}" title="Слушать: ${esc(t.title)}">
        <div class="mxm-ai-card-cover">
          ${cover}
          <span class="mxm-ai-card-play" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          </span>
        </div>
        <div class="mxm-ai-card-meta">
          <div class="mxm-ai-card-title">${esc(t.title)}</div>
          <div class="mxm-ai-card-sub">${esc(t.artist || '')}</div>
        </div>
        ${dur ? `<span class="mxm-ai-card-dur">${dur}</span>` : ''}
      </button>`;
      })
      .join('')}
    </div>
  </div>`;
}

function mxmAiArtistCardsHtml(artists) {
  if (!artists || !artists.length) return '';
  return `<div class="mxm-ai-cards mxm-ai-cards--artists">${artists
    .map((a) => {
      const photo = a.photoUrl
        ? `<img src="${esc(a.photoUrl)}" alt="" loading="lazy" />`
        : `<div class="mxm-ai-card-ph circle"></div>`;
      return `<button type="button" class="mxm-ai-card mxm-ai-card--artist" data-artist="${esc(a.id)}">
        <div class="mxm-ai-card-cover circle">${photo}</div>
        <div class="mxm-ai-card-meta">
          <div class="mxm-ai-card-title">${esc(a.name)}</div>
          <div class="mxm-ai-card-sub">${esc(a.genre || 'Открыть страницу')}</div>
        </div>
      </button>`;
    })
    .join('')}</div>`;
}

function bindMxmAiCardClicks(root) {
  const playFrom = (btn) => {
    const qKey = btn.getAttribute('data-q');
    const i = Number(btn.getAttribute('data-i') || 0);
    const tracks = getQueue(qKey);
    if (tracks && tracks[i]) {
      setQueueState(tracks, i);
      playTrack(tracks[i]);
    }
  };
  root.querySelectorAll('.mxm-ai-card[data-q], .mxm-ai-play-all[data-q]').forEach((btn) => {
    btn.onclick = (e) => {
      e.preventDefault();
      playFrom(btn);
    };
  });
  root.querySelectorAll('.mxm-ai-card[data-artist]').forEach((btn) => {
    btn.onclick = () => {
      const id = btn.getAttribute('data-artist');
      closeMxmAi();
      if (id && typeof navigate === 'function') navigate('artist', { id });
    };
  });
}

function appendMxmAiMessage(role, text, extras = {}) {
  const box = document.getElementById('mxm-ai-messages');
  if (!box) return;
  const row = document.createElement('div');
  row.className = `mxm-ai-msg mxm-ai-msg--${role}`;
  if (role === 'assistant') {
    const av = document.createElement('div');
    av.className = 'mxm-ai-msg-avatar';
    av.innerHTML = MXM_AI_ICON_SVG;
    row.appendChild(av);
  }
  const bubble = document.createElement('div');
  bubble.className = 'mxm-ai-bubble';
  const clean = text;
  bubble.innerHTML = `<div class="mxm-ai-bubble-text">${
    role === 'assistant' ? mxmAiFormatReplyHtml(clean) : esc(clean).replace(/\n/g, '<br>')
  }</div>`;
  if (extras.tracksHtml || extras.artistsHtml) {
    const cards = document.createElement('div');
    cards.className = 'mxm-ai-bubble-cards';
    cards.innerHTML = (extras.tracksHtml || '') + (extras.artistsHtml || '');
    bubble.appendChild(cards);
    bindMxmAiCardClicks(cards);
  }
  if (extras.at) {
    const time = document.createElement('div');
    time.className = 'mxm-ai-msg-time';
    time.textContent = mxmAiFormatTime(extras.at);
    bubble.appendChild(time);
  }
  row.appendChild(bubble);
  box.appendChild(row);
  box.scrollTop = box.scrollHeight;
}

function renderMxmAiWelcome() {
  appendMxmAiMessage(
    'assistant',
    'Привет! Я MXM AI — помогу подобрать треки по вайбу, настроению или найти похожих артистов. Спроси что угодно про каталог.',
    { at: Date.now() }
  );
}

function renderStoredMxmAiMessages(messages) {
  const box = document.getElementById('mxm-ai-messages');
  if (box) box.innerHTML = '';
  if (!messages.length) {
    renderMxmAiWelcome();
    return;
  }
  for (const m of messages) {
    appendMxmAiMessage(m.role, m.content, {
      at: m.at,
      tracksHtml: m.role === 'assistant' ? mxmAiTrackCardsHtml(m.tracks) : '',
      artistsHtml: m.role === 'assistant' ? mxmAiArtistCardsHtml(m.artists) : ''
    });
  }
}

function setMxmAiBusy(busy) {
  mxmAiState.busy = busy;
  const input = document.getElementById('mxm-ai-input');
  const send = document.querySelector('.mxm-ai-send');
  if (input) input.disabled = busy;
  if (send) send.disabled = busy;
  const box = document.getElementById('mxm-ai-messages');
  let typing = document.getElementById('mxm-ai-typing');
  if (busy) {
    if (!typing && box) {
      typing = document.createElement('div');
      typing.id = 'mxm-ai-typing';
      typing.className = 'mxm-ai-msg mxm-ai-msg--assistant';
      typing.innerHTML = `<div class="mxm-ai-msg-avatar">${MXM_AI_ICON_SVG}</div><div class="mxm-ai-bubble mxm-ai-bubble--typing"><span></span><span></span><span></span></div>`;
      box.appendChild(typing);
      box.scrollTop = box.scrollHeight;
    }
  } else if (typing) {
    typing.remove();
  }
}

async function clearMxmAiConversation() {
  await clearMxmAiHistoryRemote();
  mxmAiState.messages = [];
  renderStoredMxmAiMessages([]);
  renderMxmAiChips();
  toast('История очищена', 'success');
}

async function mxmAiSend(text, opts = {}) {
  const content = String(text || '').trim();
  if (!content || mxmAiState.busy) return;
  if (!opts.silentUser) {
    mxmAiState.messages.push({ role: 'user', content });
    appendMxmAiMessage('user', content, { at: Date.now() });
  } else {
    mxmAiState.messages.push({ role: 'user', content });
  }
  renderMxmAiChips();

  setMxmAiBusy(true);
  try {
    const body = {
      mode: mxmAiState.mode,
      artistId: mxmAiState.artistId,
      messages: mxmAiState.messages.map((m) => ({ role: m.role, content: m.content })),
      intro: !!opts.intro
    };
    const data = await POST('/api/mxm-ai/chat', body);
    const reply = data.reply || '…';
    mxmAiState.messages.push({ role: 'assistant', content: reply });
    appendMxmAiMessage('assistant', reply, {
      at: Date.now(),
      tracksHtml: mxmAiTrackCardsHtml(data.tracks),
      artistsHtml: mxmAiArtistCardsHtml(data.artists)
    });
    if (data.suggestPlay && data.tracks && data.tracks[0]) {
      setQueueState(data.tracks, 0);
      playTrack(data.tracks[0]);
    }
  } catch (err) {
    const raw = String(err?.message || '');
    const msg =
      /gigachat|giga.?chat|sber/i.test(raw) || /timeout/i.test(raw)
        ? 'MXM AI сейчас отвечает дольше обычного. Попробуй ещё раз.'
        : raw || 'MXM AI сейчас недоступен. Попробуй ещё раз.';
    appendMxmAiMessage('assistant', msg, { at: Date.now() });
  } finally {
    setMxmAiBusy(false);
    if (mxmAiState.mode === 'artist') renderMxmAiChips();
  }
}

async function openMxmAi(opts = {}) {
  if (!state.user) {
    toast('MXM AI доступен после входа', 'error');
    if (typeof openAuthModal === 'function') openAuthModal('login');
    return;
  }

  const status = await fetchMxmAiStatus(true);
  const mode = opts.mode === 'artist' ? 'artist' : 'discover';
  if (!status.enabled) {
    toast('MXM AI выключен', 'error');
    return;
  }
  if (mode === 'artist' && !status.artistChat) {
    toast('Чат по артисту выключен', 'error');
    return;
  }
  if (mode === 'discover' && !status.smartSearch) {
    toast('Умный поиск выключен', 'error');
    return;
  }

  ensureMxmAiPanel();
  mxmAiState = {
    open: true,
    mode,
    artistId: opts.artistId || null,
    artistName: opts.artistName || '',
    messages: [],
    busy: false
  };
  const panel = mxmAiPanelEl;
  panel.classList.remove('hidden');
  void panel.offsetWidth;
  panel.classList.add('open');
  document.body.classList.add('mxm-ai-open');
  syncMxmAiFab();

  const sub = document.getElementById('mxm-ai-subtitle');
  if (sub) {
    sub.textContent =
      mode === 'artist' && mxmAiState.artistName
        ? `Про ${mxmAiState.artistName}`
        : 'ИИ-подбор MXM';
  }

  const stored = await loadMxmAiHistory();
  mxmAiState.messages = stored.map((m) => ({ role: m.role, content: m.content }));
  renderStoredMxmAiMessages(stored);
  renderMxmAiChips();

  const input = document.getElementById('mxm-ai-input');
  if (input) {
    input.value = opts.prefill || '';
    setTimeout(() => input.focus(), 80);
  }

  const hasRealHistory = stored.some((m) => m.role === 'user');
  if (mode === 'artist' && opts.autoIntro !== false && !hasRealHistory) {
    void mxmAiSend(
      'Коротко представь этого артиста по-человечески: кто это, чем интересен, для каких настроений зайдёт. Если уместно — с чего начать слушать.',
      { silentUser: true, intro: true }
    );
  } else if (opts.prefill && opts.autoSend) {
    const t = opts.prefill;
    if (input) input.value = '';
    void mxmAiSend(t);
  }
}

function closeMxmAi() {
  mxmAiMenuOpen = false;
  mxmAiState.open = false;
  mxmAiState.busy = false;
  document.body.classList.remove('mxm-ai-open');
  if (mxmAiPanelEl) {
    mxmAiPanelEl.classList.remove('open');
    mxmAiPanelEl.classList.add('hidden');
    mxmAiPanelEl.querySelector('#mxm-ai-menu')?.classList.add('hidden');
  }
  syncMxmAiFab();
}

async function injectMxmAiArtistButton(artist) {
  const status = await fetchMxmAiStatus();
  if (!status.enabled || !status.artistChat) return;
  if (!state.user) return;
  const actions = document.querySelector('.artist-page .hero-actions');
  if (!actions || actions.querySelector('.mxm-ai-artist-btn')) return;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'mxm-ai-artist-btn';
  btn.title = 'Узнать больше — MXM AI';
  btn.setAttribute('aria-label', 'Узнать больше про артиста с MXM AI');
  btn.innerHTML = `${MXM_AI_ICON_SVG}<span>Узнать больше</span>`;
  btn.onclick = (e) => {
    e.stopPropagation();
    openMxmAi({ mode: 'artist', artistId: artist.id, artistName: artist.name });
  };
  const share = actions.querySelector('.hero-share-btn');
  if (share) actions.insertBefore(btn, share);
  else actions.appendChild(btn);
}

async function injectMxmAiSearchEntry() {
  const status = await fetchMxmAiStatus();
  if (!status.enabled || !status.smartSearch) return;
  const prompt = document.querySelector('.search-prompt');
  if (!prompt || prompt.querySelector('.mxm-ai-search-portal')) return;

  const loggedIn = !!state.user;
  const examples = [
    'тревожное под ночной автобус',
    'хардкорный трэп, но не шумный',
    'грустный клауд как у…',
    'что-то энергичное для зала',
    'мрачный witch house'
  ];

  const portal = document.createElement('div');
  portal.className = 'mxm-ai-search-portal' + (loggedIn ? '' : ' mxm-ai-search-portal--guest');
  portal.innerHTML = `
    <button type="button" class="mxm-ai-search-portal-btn" aria-label="${
      loggedIn ? 'Открыть MXM AI поиск' : 'Войти, чтобы пользоваться MXM AI'
    }">
      <span class="mxm-ai-search-portal-aura" aria-hidden="true">
        <span class="mxm-ai-search-portal-blob mxm-ai-search-portal-blob--violet"></span>
        <span class="mxm-ai-search-portal-blob mxm-ai-search-portal-blob--orange"></span>
      </span>
      <span class="mxm-ai-search-portal-body">
        <span class="mxm-ai-search-portal-kicker">MXM AI</span>
        <span class="mxm-ai-search-portal-title">${
          loggedIn ? 'Искать по вайбу' : 'Войди, чтобы искать с AI'
        }</span>
        <span class="mxm-ai-search-portal-hint">
          ${
            loggedIn
              ? `<span class="mxm-ai-search-rotator" data-mxm-ai-rotator></span>`
              : 'Умный подбор — только для аккаунта'
          }
        </span>
      </span>
    </button>
  `;

  const btn = portal.querySelector('.mxm-ai-search-portal-btn');
  btn.onclick = () => {
    if (!state.user) {
      toast('MXM AI доступен после входа', 'error');
      if (typeof openAuthModal === 'function') openAuthModal('login');
      return;
    }
    openMxmAi({ mode: 'discover' });
  };

  if (loggedIn) {
    const rotator = portal.querySelector('[data-mxm-ai-rotator]');
    let idx = 0;
    if (rotator) {
      rotator.textContent = `«${examples[0]}»`;
      rotator.classList.add('is-in');
    }
    const setExample = (i) => {
      if (!rotator) return;
      rotator.classList.remove('is-in');
      rotator.classList.add('is-out');
      window.setTimeout(() => {
        rotator.textContent = `«${examples[i % examples.length]}»`;
        rotator.classList.remove('is-out');
        rotator.classList.add('is-in');
      }, 220);
    };
    const timer = window.setInterval(() => {
      if (!document.body.contains(portal)) {
        window.clearInterval(timer);
        return;
      }
      idx += 1;
      setExample(idx);
    }, 3200);
    portal._mxmAiRotatorTimer = timer;
  }

  prompt.appendChild(portal);
  requestAnimationFrame(() => portal.classList.add('is-ready'));
}

function refreshMxmAiUiAfterAuth() {
  mxmAiStatusCache = null;
  mxmAiStatusFetchedAt = 0;
  if (state.view === 'artist') {
    const id =
      state.id ||
      state.params?.id ||
      document.querySelector('.hero-play-btn[data-id]')?.getAttribute('data-id');
    if (id && state.user) {
      const name = document.querySelector('.artist-page .hero-title')?.textContent?.trim() || '';
      document.querySelector('.mxm-ai-artist-btn')?.remove();
      injectMxmAiArtistButton({ id, name }).catch(() => {});
    } else {
      document.querySelector('.mxm-ai-artist-btn')?.remove();
    }
  }
  if (state.view === 'search') {
    document.querySelector('.mxm-ai-search-portal')?.remove();
    injectMxmAiSearchEntry().catch(() => {});
  }
  if (!state.user && mxmAiState.open) closeMxmAi();
  syncMxmAiFab();
}

document.addEventListener('mxm:user-changed', () => {
  refreshMxmAiUiAfterAuth();
});

document.addEventListener('DOMContentLoaded', () => {
  syncMxmAiFab().catch(() => {});
});

window.addEventListener(
  'resize',
  () => {
    if (mxmAiFabEl) syncMxmAiFab().catch(() => {});
  },
  { passive: true }
);

/** Patch hooks live in 04-views (injectMxmAiArtistButton / injectMxmAiSearchEntry). */

window.openMxmAi = openMxmAi;
window.closeMxmAi = closeMxmAi;
window.fetchMxmAiStatus = fetchMxmAiStatus;
window.injectMxmAiArtistButton = injectMxmAiArtistButton;
window.injectMxmAiSearchEntry = injectMxmAiSearchEntry;
window.syncMxmAiFab = syncMxmAiFab;


// ─── INIT ─────────────────────────────────────────────────────────────────────
let _appGlobalListenersBound = false;
let _appMainBootDone = false;

function bindAppGlobalListeners() {
  if (_appGlobalListenersBound) return;
  _appGlobalListenersBound = true;

  window.addEventListener(
    'resize',
    () => {
      if (state.view === 'home') {
        clearTimeout(_homeLayoutResizeTimer);
        _homeLayoutResizeTimer = setTimeout(() => {
          refreshHomePopularAlbumsGrid();
          if (allArtists.length > 0 && !isMxMDesktopApp()) updateHomeArtists(allArtists);
        }, 100);
      }
      if (notifPanelOpen) positionNotifPanel();
      if (typeof mountSidebarDesktopAppCta === 'function') mountSidebarDesktopAppCta();
  if (typeof mountSidebarMemorialPlaque === 'function') mountSidebarMemorialPlaque();
      const portal = $('home-random-portal');
      if (!portal?.classList.contains('open')) return;
      const mobile = window.matchMedia('(max-width: 900px)').matches;
      portal.classList.toggle('mobile-mode', mobile);
      const pop = $('home-random-popover');
      if (mobile) {
        if (pop) {
          pop.style.left = '';
          pop.style.top = '';
          pop.style.width = '';
          pop.style.maxHeight = '';
          pop.style.transform = '';
          pop.style.transition = '';
        }
      } else {
        if (pop) {
          pop.style.transform = '';
          pop.style.transition = '';
        }
        positionHomeRandomPopover();
      }
    },
    { passive: true }
  );
  $('content')?.addEventListener(
    'scroll',
    () => {
      const portal = $('home-random-portal');
      if (portal?.classList.contains('open') && !portal.classList.contains('mobile-mode')) {
        positionHomeRandomPopover();
      }
    },
    { passive: true }
  );
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.autocomplete-wrapper')) {
      $('artist-suggestions')?.classList.add('hidden');
      $('album-suggestions')?.classList.add('hidden');
      $('aup-artist-suggestions')?.classList.add('hidden');
      $('aup-album-suggestions')?.classList.add('hidden');
    }
    const hrPortal = $('home-random-portal');
    const hrPop = $('home-random-popover');
    const hrBtn = $('home-random-settings-btn');
    if (hrPortal?.classList.contains('open') && !hrPortal.contains(e.target) && !hrBtn?.contains(e.target)) {
      closeHomeRandomPopover();
    }
    if (
      notifPanelOpen &&
      !e.target.closest('#notif-panel') &&
      !e.target.closest('#notif-bell-btn') &&
      !e.target.closest('#notif-panel-backdrop')
    ) {
      closeNotifPanel();
    }
  });
  bindAuthStorageSync();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (state.user) refreshNotifBadge();
    if (state.token && !state.user) void bootstrapAuthFromServer();
  });
  window.addEventListener('focus', () => {
    if (state.user) refreshNotifBadge();
    if (state.token && !state.user) void bootstrapAuthFromServer();
  });
  window.addEventListener('beforeunload', saveTrackLoudnessCache, { passive: true });
}

function isDesktopAuthGateRequired() {
  if (typeof isMxMDesktopApp !== 'function' || !isMxMDesktopApp()) return false;
  if (state.user) return false;
  try {
    const { view } = parseHash();
    if (view === 'reset-password') return false;
  } catch (_) {}
  return true;
}

async function continueAppMainBoot() {
  if (_appMainBootDone) return;
  _appMainBootDone = true;

  loadAutocompleteData();
  mxmNormalizeSyncPath();
  const { view, params } = parseHash();
  void loadAllArtists();
  await loadView(view, params);
  bindQueueSheetDragClose();
  bindQueueListReorder();
  bindFullscreenMobileSwipeClose();
  bindFullscreenResponsiveBehavior();

  startRadioUpdates();

  if (view === 'home') {
    setTimeout(updateRadioNowPlaying, 500);
  }
  // Громкость из localStorage — до restoreLastTrack/автогейна (иначе HTML value=80).
  syncVolumeFromStorage();
  await restoreLastTrack();
  if (state.user) {
    mxmChatConnectWs();
    await mxmListenRestoreActive();
  }
  // Ещё раз после prepareTrack: автогейн уже мог посчитаться — пересчитать под сохранённый уровень.
  syncVolumeFromStorage();
  refreshAutoGainForUserVolume();

  const coverWrap = document.querySelector('.player-cover-wrap');
  if (coverWrap) {
    coverWrap.setAttribute('role', 'button');
    coverWrap.setAttribute('tabindex', '0');
    coverWrap.setAttribute('aria-label', 'Развернуть плеер');
    coverWrap.addEventListener('click', () => {
      if (state.currentTrack) openFullscreenPlayer();
    });
    coverWrap.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      if (state.currentTrack) openFullscreenPlayer();
    });
  }

  mountQueueSheetForViewport();
  syncPlayerClickBlockState();
}

async function init() {
  bindAppGlobalListeners();
  if (typeof mountSidebarDesktopAppCta === 'function') mountSidebarDesktopAppCta();
  if (typeof mountSidebarMemorialPlaque === 'function') mountSidebarMemorialPlaque();
  window.toggleTrackMoreMenu = toggleTrackMoreMenu;
  window.closeAllTrackMoreMenus = closeAllTrackMoreMenus;
  window.closeShareModal = closeShareModal;
  initModifierNavShortcuts();
  initPerfMonitoring();
  loadTrackLoudnessCache();
  bindViewportInsetsSync();
  registerNativeImageCacheServiceWorker();
  audio.playsInline = true;
  audio.setAttribute('playsinline', '');
  audio.setAttribute('webkit-playsinline', '');
  audio.setAttribute('x-webkit-airplay', 'allow');

  // Проверяем сессию ДО показа gate — не сносим токен при временных сетевых сбоях
  await bootstrapAuthFromServer();

  const desktopGate = isDesktopAuthGateRequired();

  if (!desktopGate && typeof isMxMDesktopApp === 'function' && isMxMDesktopApp()) {
    try {
      const { view } = parseHash();
      const bootContent = $('content');
      if (bootContent && typeof getViewLoadingSkeleton === 'function') {
        bootContent.innerHTML = getViewLoadingSkeleton(view || 'home');
        bootContent.classList.add('mxm-desktop-booting');
      }
    } catch (_) {}
  }

  if (desktopGate) {
    if (typeof window.mxmDesktopShowAuthGate === 'function') {
      window.mxmDesktopShowAuthGate('login');
    } else if (typeof window.applyDesktopAuthGateShell === 'function') {
      window.applyDesktopAuthGateShell('login');
    } else {
      openAuthModal('login');
    }
    markPerf('appInit', performance.now() - PERF_STATS.navStart, {
      view: 'auth-gate',
      tier: PERF_STATS.tier
    });
    if (typeof window.mxmDesktopDismissSplash === 'function') await window.mxmDesktopDismissSplash();
    return;
  }

  await continueAppMainBoot();

  markPerf('appInit', performance.now() - PERF_STATS.navStart, { view: state.view, tier: PERF_STATS.tier });

  if (typeof isMxMDesktopApp === 'function' && isMxMDesktopApp()) {
    $('content')?.classList.remove('mxm-desktop-booting');
    if (typeof window.mxmDesktopClearBootShell === 'function') window.mxmDesktopClearBootShell();
    if (typeof window.mxmDesktopDismissSplash === 'function') await window.mxmDesktopDismissSplash();
  }
}

window.continueAppMainBoot = continueAppMainBoot;
window.isDesktopAuthGateRequired = isDesktopAuthGateRequired;

/** Desktop: init() вызывается из 07-electron после регистрации gate. */
if (!(typeof isMxMDesktopApp === 'function' && isMxMDesktopApp())) {
  init();
}

// ─── ELECTRON DESKTOP BRIDGE (v4 — top-layer overlays + lightweight) ─────────
(function initMxMDesktopBridge() {
  const api = typeof window !== 'undefined' ? window.mxmDesktop : null;
  if (!api || !api.isDesktop) return;

  function refreshElectronShellClass() {
    try {
      document.documentElement.classList.add('mxm-electron-shell');
      document.documentElement.classList.add('mxm-desktop-shell');
    } catch (_) {}
  }

  function loadDesktopStylesheet() {
    const href = '/css/desktop-shell.css?v=desktop-v38';
    let link = document.querySelector('link[data-mxm-desktop-css]');
    if (link) {
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
      return link;
    }
    link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-mxm-desktop-css', '1');
    document.head.appendChild(link);
    return link;
  }

  function whenStylesheetReady(link, cb) {
    if (!link) {
      cb();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      cb();
    };
    const isReady = () => {
      try {
        return !!link.sheet;
      } catch (_) {
        return true;
      }
    };
    if (isReady()) {
      finish();
      return;
    }
    link.addEventListener('load', finish, { once: true });
    link.addEventListener('error', finish, { once: true });
    let ticks = 0;
    const poll = setInterval(() => {
      ticks += 1;
      if (isReady() || ticks > 80) {
        clearInterval(poll);
        finish();
      }
    }, 25);
  }

  function removeLegacyAmbient() {
    const el = document.getElementById('mxm-desktop-ambient');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  /**
   * Поднимаем плавающие меню/панели в <body>, чтобы backdrop-filter и contain
   * на родителях (sidebar/player) НЕ создавали containing block для их fixed
   * позиционирования. Это решает «меню обрезается контейнером».
   * Каждый узел получает атрибут data-mxm-floating-host="sidebar|player|main".
   */
  const FLOATING_IDS = [
    'user-menu',
    'notif-panel',
    'notif-panel-backdrop',
    'player-menu',
    'queue-sheet-overlay',
    'queue-sheet'
  ];

  function getFloatingHost() {
    if (!document.body) return null;
    let host = document.getElementById('mxm-desktop-floating-host');
    if (!host) {
      host = document.createElement('div');
      host.id = 'mxm-desktop-floating-host';
      host.className = 'mxm-desktop-floating-host';
      document.body.appendChild(host);
    }
    return host;
  }

  function liftFloatingOverlaysToBody() {
    const host = getFloatingHost();
    if (!host) return;
    FLOATING_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el && el.parentElement !== host) {
        /* overlay под панелью очереди */
        if (id === 'queue-sheet-overlay') host.prepend(el);
        else host.appendChild(el);
      }
    });
  }

  /** Скругление всех углов: blur обрезается #mxm-player-shell, а не fixed #player. */
  let _desktopAuthGateActive = false;

  function ensureAuthModalOnTop() {
    const modal = document.getElementById('auth-modal');
    if (!modal || !document.body) return;
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
  }

  function showDesktopAuthGate(tab = 'login') {
    _desktopAuthGateActive = true;
    ensureAuthModalOnTop();
    if (typeof window.applyDesktopAuthGateShell === 'function') {
      window.applyDesktopAuthGateShell(tab);
    } else {
      document.documentElement.classList.add('mxm-desktop-auth-locked');
      const modal = document.getElementById('auth-modal');
      if (modal) {
        modal.classList.remove('hidden');
        modal.setAttribute('aria-hidden', 'false');
      }
      if (typeof window.resetDesktopAuthGateForms === 'function') window.resetDesktopAuthGateForms(tab);
      else if (typeof switchAuthTab === 'function') switchAuthTab(tab);
    }
    if (typeof window.mxmDesktopStopPlayback === 'function') window.mxmDesktopStopPlayback();
    const content = document.getElementById('content');
    if (content) {
      content.innerHTML = '';
      content.classList.remove('mxm-desktop-booting');
    }
    requestAnimationFrame(() => {
      const email = document.getElementById('login-email');
      if (tab === 'login' && email) email.focus();
      else if (tab === 'register') document.getElementById('reg-login')?.focus();
    });
  }

  function hideDesktopAuthGate() {
    _desktopAuthGateActive = false;
    document.documentElement.classList.remove('mxm-desktop-auth-locked');
    const modal = document.getElementById('auth-modal');
    if (modal) {
      modal.classList.add('hidden');
      modal.setAttribute('aria-hidden', 'true');
    }
  }

  function patchNavigateForAuthGate() {
    if (window.__mxmDesktopNavigatePatched) return;
    const orig = typeof navigate === 'function' ? navigate : null;
    if (!orig) return;
    window.__mxmDesktopNavigatePatched = true;
    window.navigate = function mxmDesktopNavigateGate(view, params, ...rest) {
      if (_desktopAuthGateActive) return;
      return orig.apply(this, [view, params, ...rest]);
    };
  }

  window.mxmDesktopAuthGateActive = () => _desktopAuthGateActive;
  window.mxmDesktopShowAuthGate = showDesktopAuthGate;
  window.mxmDesktopHideAuthGate = hideDesktopAuthGate;
  window.mxmDesktopOnAuthSuccess = async function mxmDesktopOnAuthSuccess() {
    hideDesktopAuthGate();
    if (typeof window.continueAppMainBoot === 'function') {
      await window.continueAppMainBoot();
    }
    if (typeof refreshActiveViewAfterAuth === 'function') {
      await refreshActiveViewAfterAuth();
    }
    if (typeof navigate === 'function') navigate('home');
    document.getElementById('content')?.classList.remove('mxm-desktop-booting');
    if (typeof window.mxmDesktopClearBootShell === 'function') window.mxmDesktopClearBootShell();
    markPerf('appInit', performance.now() - PERF_STATS.navStart, {
      view: state.view,
      tier: PERF_STATS.tier
    });
  };

  function wrapPlayerRoundedShell() {
    const player = document.getElementById('player');
    if (!player || player.parentElement?.id === 'mxm-player-shell') return;
    const parent = player.parentNode;
    if (!parent) return;
    const shell = document.createElement('div');
    shell.id = 'mxm-player-shell';
    shell.className = 'mxm-player-shell';
    parent.insertBefore(shell, player);
    shell.appendChild(player);
    player.style.background = '';
    player.style.backgroundImage = '';
    player.style.boxShadow = '';
    player.style.border = '';
    player.style.backdropFilter = '';
    player.style.webkitBackdropFilter = '';
  }

  function patchQueueSheetMount() {
    if (window.__mxmDesktopQueuePatched) return;
    window.__mxmDesktopQueuePatched = true;
    const orig = typeof window.mountQueueSheetForViewport === 'function'
      ? window.mountQueueSheetForViewport
      : null;
    window.mountQueueSheetForViewport = function mxmDesktopMountQueueSheet() {
      const sheet = document.getElementById('queue-sheet');
      const overlay = document.getElementById('queue-sheet-overlay');
      const host = getFloatingHost();
      if (!sheet || !overlay || !host) {
        if (orig) orig();
        return;
      }
      if (window.innerWidth > 900) {
        liftFloatingOverlaysToBody();
        sheet.classList.remove('mobile-mode');
      } else if (orig) {
        orig();
      }
    };
  }

  /** Меню ⋯ — позиция у кнопки + не закрываем сразу из-за bubble. */
  function positionDesktopPlayerMenu() {
    const menu = document.getElementById('player-menu');
    const btn = document.getElementById('player-menu-btn');
    if (!menu || !btn || !menu.classList.contains('show')) return;
    const r = btn.getBoundingClientRect();
    menu.style.right = Math.max(12, window.innerWidth - r.right) + 'px';
    menu.style.bottom = Math.max(12, window.innerHeight - r.top + 8) + 'px';
    menu.style.left = 'auto';
    menu.style.top = 'auto';
  }

  function patchPlayerMenuToggle() {
    if (window.__mxmDesktopPlayerMenuPatched) return;
    window.__mxmDesktopPlayerMenuPatched = true;

    const origToggle = typeof window.togglePlayerMenu === 'function' ? window.togglePlayerMenu : null;
    if (!origToggle) return;
    window.togglePlayerMenu = function mxmDesktopTogglePlayerMenu() {
      origToggle();
      requestAnimationFrame(positionDesktopPlayerMenu);
    };

    window.addEventListener('resize', () => {
      if (document.getElementById('player-menu')?.classList.contains('show')) {
        positionDesktopPlayerMenu();
      }
    }, { passive: true });
  }

  function mountDesktopChrome() {
    if (typeof window.__mxmMountDesktopChrome === 'function') window.__mxmMountDesktopChrome();
    const sidebar = document.getElementById('sidebar');
    const userArea = document.getElementById('user-area');
    if (!sidebar || !userArea) return;
    let mount = document.getElementById('mxm-desktop-user-mount');
    if (!mount) {
      mount = document.createElement('div');
      mount.id = 'mxm-desktop-user-mount';
      mount.className = 'mxm-desktop-user-mount';
      sidebar.appendChild(mount);
    }
    if (userArea.parentElement !== mount) mount.appendChild(userArea);
    ensureFavAlbumsHost(sidebar, mount);
    if (typeof mountDesktopAppMemorialPlaque === 'function') mountDesktopAppMemorialPlaque();
    ensureDesktopSidebarOrder(sidebar);
    ensureUserTagline();
  }

  window.ensureDesktopSidebarOrder = ensureDesktopSidebarOrder;

  /** Добавляем «у тебя отличный вкус!» под ником, не трогая HTML страницы. */
  function ensureUserTagline() {
    const btn = document.querySelector('.mxm-desktop-user-mount .avatar-btn');
    if (!btn) return;
    const userName = btn.querySelector('#user-name');
    if (!userName) return;

    let stack = btn.querySelector('.mxm-user-stack');
    if (!stack) {
      stack = document.createElement('span');
      stack.className = 'mxm-user-stack';
      userName.before(stack);
      stack.appendChild(userName);
      const tagline = document.createElement('span');
      tagline.className = 'mxm-user-tagline';
      tagline.textContent = 'У тебя отличный вкус!';
      stack.appendChild(tagline);
    }
  }

  /** Порядок в sidebar: nav → альбомы → spacer → память → профиль. */
  function ensureDesktopSidebarOrder(sidebar) {
    const nav = sidebar.querySelector('.sidebar-nav');
    const favHost = document.getElementById('mxm-desktop-fav-albums');
    const stretch = sidebar.querySelector('.sidebar-stretch');
    const memorial = document.getElementById('mxm-desktop-memorial-plaque');
    const userMount = document.getElementById('mxm-desktop-user-mount');

    if (nav && favHost && favHost.previousElementSibling !== nav) {
      nav.insertAdjacentElement('afterend', favHost);
    }
    const afterFav = favHost || nav;
    if (afterFav && stretch && stretch.parentElement === sidebar && stretch.previousElementSibling !== afterFav) {
      afterFav.insertAdjacentElement('afterend', stretch);
    }
    const afterStretch = stretch || afterFav;
    if (memorial && afterStretch && memorial.parentElement === sidebar && memorial.previousElementSibling !== afterStretch) {
      afterStretch.insertAdjacentElement('afterend', memorial);
    }
    if (userMount && userMount.parentElement === sidebar && sidebar.lastElementChild !== userMount) {
      sidebar.appendChild(userMount);
    }
  }

  /** Блок «Любимые альбомы» — после навигации, до пользовательского блока. */
  function ensureFavAlbumsHost(sidebar, userMount) {
    let host = document.getElementById('mxm-desktop-fav-albums');
    if (!host) {
      host = document.createElement('div');
      host.id = 'mxm-desktop-fav-albums';
      host.className = 'mxm-desktop-fav-albums';
      host.hidden = true;
      host.innerHTML =
        '<div class="mxm-desktop-fav-albums-title">Любимые альбомы</div>' +
        '<div class="mxm-desktop-fav-albums-list" id="mxm-desktop-fav-albums-list"></div>';
    }
    if (host.parentElement !== sidebar || host.nextElementSibling !== userMount) {
      sidebar.insertBefore(host, userMount);
    }
  }

  /** Тянем избранные альбомы один раз после логина и при возврате на главную. */
  let favAlbumsLastUserId = null;
  let favAlbumsInFlight = null;
  async function refreshFavAlbums() {
    const host = document.getElementById('mxm-desktop-fav-albums');
    const list = document.getElementById('mxm-desktop-fav-albums-list');
    if (!host || !list) return;
    const user = (typeof state !== 'undefined' && state) ? state.user : null;
    if (!user) {
      host.hidden = true;
      list.innerHTML = '';
      favAlbumsLastUserId = null;
      const memorial = document.getElementById('mxm-desktop-memorial-plaque');
      if (memorial) memorial.hidden = true;
      return;
    }
    if (favAlbumsLastUserId === user.id && list.children.length) {
      const memorial = document.getElementById('mxm-desktop-memorial-plaque');
      if (memorial) memorial.hidden = false;
      return;
    }
    if (favAlbumsInFlight) return;
    if (typeof GET !== 'function') return;
    favAlbumsInFlight = (async () => {
      try {
        const data = await GET('/api/favorites');
        const albums = Array.isArray(data?.albums) ? data.albums.slice(0, 4) : [];
        favAlbumsLastUserId = user.id;
        if (!albums.length) {
          host.hidden = true;
          list.innerHTML = '';
          if (typeof mountDesktopAppMemorialPlaque === 'function') mountDesktopAppMemorialPlaque();
          return;
        }
        list.innerHTML = albums
          .map((a) => {
            const cover = a?.coverUrl ? (typeof resolveMediaUrl === 'function' ? resolveMediaUrl(a.coverUrl) : a.coverUrl) : '';
            const title = String(a?.title || a?.name || 'Без названия');
            const artistName = a?.artistName ||
              (Array.isArray(a?.artists) && a.artists.map((x) => x?.name).filter(Boolean).join(', ')) ||
              '';
            return (
              '<button type="button" class="mxm-desktop-fav-album" data-album-id="' + String(a.id || '').replace(/"/g, '&quot;') + '" title="' + title.replace(/"/g, '&quot;') + '">' +
                '<div class="mxm-desktop-fav-album-cover">' +
                  (cover ? '<img src="' + cover + '" alt="" loading="lazy" decoding="async" />' : '') +
                '</div>' +
                '<div class="mxm-desktop-fav-album-info">' +
                  '<div class="mxm-desktop-fav-album-title">' + escapeText(title) + '</div>' +
                  (artistName ? '<div class="mxm-desktop-fav-album-artist">' + escapeText(artistName) + '</div>' : '') +
                '</div>' +
              '</button>'
            );
          })
          .join('');
        host.hidden = false;
        if (typeof mountDesktopAppMemorialPlaque === 'function') mountDesktopAppMemorialPlaque();
      } catch (_) {
        host.hidden = true;
      } finally {
        favAlbumsInFlight = null;
      }
    })();
  }

  function escapeText(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Делегированный клик по альбому в sidebar — навигация на страницу альбома. */
  document.addEventListener('click', (e) => {
    const btn = e.target?.closest?.('.mxm-desktop-fav-album');
    if (!btn) return;
    const id = btn.getAttribute('data-album-id');
    if (!id) return;
    if (typeof navigate === 'function') navigate('album', { id });
    else window.location.hash = '#album/' + id;
  });

  const HIDE_SELECTORS = [
    '.sidebar-nav .nav-item[data-view="upload"]',
    '.sidebar-nav .nav-item[data-view="messages"]',
    '#user-menu a[href="#upload"]',
    '#user-menu a[href="#messages"]',
    '#desktop-site-footer',
    '#desktop-site-footer-parking',
    '#mobile-nav',
    '#topnav',
    '.topnav-mobile-logo',
    '#menu-toggle',
    '.mxm-desktop-open-on-site'
  ];

  function pruneDesktopUI() {
    for (const sel of HIDE_SELECTORS) {
      const list = document.querySelectorAll(sel);
      for (let i = 0; i < list.length; i++) {
        const el = list[i];
        if (el && el.style.display !== 'none') el.style.display = 'none';
      }
    }
    try {
      const hash = (window.location.hash || '').replace(/^#/, '');
      const view = hash.split('/')[0];
      if (view === 'upload' || view === 'messages') {
        if (typeof navigate === 'function') navigate('home', {});
        else window.location.hash = 'home';
      }
    } catch (_) {}
  }

  let pruneScheduled = false;
  function schedulePrune() {
    if (pruneScheduled) return;
    pruneScheduled = true;
    setTimeout(() => {
      pruneScheduled = false;
      mountDesktopChrome();
      liftFloatingOverlaysToBody();
      pruneDesktopUI();
      /* Лёгкий триггер обновления избранных альбомов вместо постоянного интервала. */
      void refreshFavAlbums();
    }, 250);
  }

  function installDomObserver() {
    if (window.__mxmDesktopObserver) return;
    const root = document.getElementById('app') || document.body;
    if (!root) return;
    const obs = new MutationObserver(schedulePrune);
    obs.observe(root, { childList: true, subtree: true });
    window.__mxmDesktopObserver = obs;
  }

  const DESKTOP_SPLASH_MIN_MS = 2800;
  const DESKTOP_SPLASH_ARM_DELAY_MS = 380;
  let desktopSplashStartedAt = 0;
  let desktopSplashDismissPromise = null;

  function armDesktopSplashAnimations() {
    const el = document.getElementById('mxm-desktop-splash');
    if (!el || el.classList.contains('mxm-desktop-splash--ready')) return;
    el.classList.add('mxm-desktop-splash--ready');
    if (!desktopSplashStartedAt) desktopSplashStartedAt = Date.now();
  }

  function scheduleDesktopSplashArm() {
    let armed = false;
    const arm = () => {
      if (armed) return;
      armed = true;
      armDesktopSplashAnimations();
    };
    const cssLink = loadDesktopStylesheet();
    whenStylesheetReady(cssLink, () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(arm, DESKTOP_SPLASH_ARM_DELAY_MS);
        });
      });
    });
    setTimeout(arm, DESKTOP_SPLASH_ARM_DELAY_MS + 1000);
  }

  function mxmDesktopSplashWordsHtml() {
    const words = [
      { text: 'Музыка,', mod: 'lead', i: 0 },
      { text: 'которая', mod: 'soft', i: 1 },
      { text: 'нравится', mod: 'hero', i: 2 },
      { text: 'именно', mod: 'mid', i: 3 },
      { text: 'тебе', mod: 'accent', i: 4 }
    ];
    const spans = words
      .map(
        (w) =>
          `<span class="mxm-splash-word mxm-splash-word--${w.mod}" style="--word-i:${w.i}">${w.text}</span>`
      )
      .join('');
    return `<p class="mxm-desktop-splash-words" aria-label="Музыка, которая нравится именно тебе">${spans}</p>`;
  }

  function mxmDesktopSplashHtml() {
    return `<div id="mxm-desktop-splash" class="mxm-desktop-splash" role="status" aria-live="polite" aria-busy="true" aria-label="Загрузка MXM Music">
      <div class="mxm-desktop-splash-bg" aria-hidden="true"></div>
      <div class="mxm-desktop-splash-inner">
        ${mxmDesktopSplashWordsHtml()}
      </div>
    </div>`;
  }

  function mountDesktopSplash() {
    if (document.getElementById('mxm-desktop-splash')) return;
    if (!document.body) return;
    document.body.insertAdjacentHTML('afterbegin', mxmDesktopSplashHtml());
    desktopSplashStartedAt = 0;
    scheduleDesktopSplashArm();
  }

  function dismissDesktopSplash() {
    if (desktopSplashDismissPromise) return desktopSplashDismissPromise;
    desktopSplashDismissPromise = new Promise((resolve) => {
      const el = document.getElementById('mxm-desktop-splash');
      if (!el) {
        resolve();
        return;
      }
      const startedAt = desktopSplashStartedAt;
      const wait = startedAt
        ? Math.max(0, DESKTOP_SPLASH_MIN_MS - (Date.now() - startedAt))
        : DESKTOP_SPLASH_MIN_MS + DESKTOP_SPLASH_ARM_DELAY_MS;
      const finish = () => {
        el.classList.add('mxm-desktop-splash--out');
        el.setAttribute('aria-busy', 'false');
        const remove = () => {
          try {
            el.remove();
          } catch (_) {}
          resolve();
        };
        el.addEventListener('transitionend', remove, { once: true });
        setTimeout(remove, 650);
      };
      if (wait > 0) setTimeout(finish, wait);
      else finish();
    });
    return desktopSplashDismissPromise;
  }

  window.mxmDesktopDismissSplash = dismissDesktopSplash;

  function boot() {
    refreshElectronShellClass();
    loadDesktopStylesheet();
    mountDesktopSplash();
    patchNavigateForAuthGate();
    wrapPlayerRoundedShell();
    removeLegacyAmbient();
    mountDesktopChrome();
    liftFloatingOverlaysToBody();
    patchQueueSheetMount();
    patchPlayerMenuToggle();
    if (typeof window.mountQueueSheetForViewport === 'function') {
      window.mountQueueSheetForViewport();
    }
    pruneDesktopUI();
    installDomObserver();
    overrideShareForDesktop();
    silenceBackgroundPollers();
    void refreshFavAlbums();
    if (!window.__mxmAppInitStarted && typeof init === 'function') {
      window.__mxmAppInitStarted = true;
      void init();
    }
  }

  /**
   * В desktop отключаем фоновые поллеры:
   *  — бейдж радио на главной (фон)
   *  — счётчик уведомлений (каждые 22с)
   *  — рандомизация блока исполнителей на главной (каждую минуту)
   * Сохраняем: запись истории прослушивания, синхронизация плеера в Electron.
   */
  function silenceBackgroundPollers() {
    if (window.__mxmDesktopSilenced) return;
    window.__mxmDesktopSilenced = true;

    try { if (window.radioUpdateTimer) { clearInterval(window.radioUpdateTimer); window.radioUpdateTimer = null; } } catch (_) {}
    try { if (window.notifPollTimer)   { clearInterval(window.notifPollTimer);   window.notifPollTimer = null; } } catch (_) {}
    try { if (window.homeArtistsTimer) { clearInterval(window.homeArtistsTimer); window.homeArtistsTimer = null; } } catch (_) {}

    const noop = function () {};
    const noopAsync = async function () { return undefined; };

    /* Перебиваем глобальные функции, чтобы при следующем заходе на home
       поллеры не стартовали заново. Никаких side-effects, кроме no-op. */
    if (typeof window.startRadioUpdates === 'function') window.startRadioUpdates = noop;
    if (typeof window.refreshNotifBadge === 'function')     window.refreshNotifBadge = noopAsync;
    if (typeof window.startNotifPolling === 'function')     window.startNotifPolling = noop;
    if (typeof window.updateHomeArtists === 'function') {
      /* Оставляем функцию (она используется при первичном рендере), но
         перехватываем таймер: первый вызов отработает один раз и больше
         таймер не плодим. Достаточно очистить homeArtistsTimer выше. */
    }
  }

  /** Перезагружаем избранные альбомы при логине/логауте — без интервалов. */
  function bindFavAlbumsToAuthEvents() {
    const trigger = () => { void refreshFavAlbums(); };
    document.addEventListener('mxm:user-changed', trigger);
    document.addEventListener('mxm:favorites-changed', trigger);
    /* На случай если событий нет — обновляем при возврате окна в фокус. */
    window.addEventListener('focus', trigger);
  }

  /** В desktop: «Поделиться» без модалки — сразу копируем ссылку. */
  function overrideShareForDesktop() {
    if (typeof window.shareItem !== 'function') return;
    if (window.__mxmDesktopShareOverridden) return;
    window.__mxmDesktopShareOverridden = true;
    const original = window.shareItem;
    window.shareItem = function mxmDesktopShareItem(type, id, name = '') {
      try {
        const sid = String(id || '').trim();
        if (!sid) return;
        const buildLink = typeof window.buildShareDeepLink === 'function' ? window.buildShareDeepLink : null;
        const copy = typeof window.copyUrlToClipboardOrShare === 'function' ? window.copyUrlToClipboardOrShare : null;
        if (!buildLink || !copy) return original.call(this, type, id, name);
        const link = buildLink(type, sid);
        if (typeof window.closePlayerMenu === 'function') window.closePlayerMenu();
        void copy(link, { shareTitle: String(name || '').trim() || 'MXM' });
      } catch {
        return original.call(this, type, id, name);
      }
    };
  }

  refreshElectronShellClass();
  if (document.body) { boot(); bindFavAlbumsToAuthEvents(); }
  document.addEventListener('DOMContentLoaded', () => { boot(); bindFavAlbumsToAuthEvents(); });

  function primaryArtistLabel(track) {
    if (!track) return '';
    if (track.artistName) return String(track.artistName);
    if (Array.isArray(track.artists) && track.artists.length) {
      return track.artists.map((a) => a?.name).filter(Boolean).join(', ');
    }
    return '';
  }

  function coverForTrack(track) {
    if (!track?.coverUrl) return '';
    try {
      return typeof resolveMediaUrl === 'function' ? resolveMediaUrl(track.coverUrl) : track.coverUrl;
    } catch {
      return track.coverUrl;
    }
  }

  let lastNotifyTrackId = null;

  function reportPlaybackToDesktop() {
    if (!api.setPlaybackState) return;
    const track = typeof state !== 'undefined' ? state.currentTrack : null;
    const audioEl = typeof audio !== 'undefined' ? audio : null;
    if (!track || !audioEl) {
      api.setPlaybackState({ playing: false, title: '', artist: '', coverUrl: '' });
      return;
    }
    api.setPlaybackState({
      playing: !audioEl.paused,
      title: track.title || '',
      artist: primaryArtistLabel(track),
      coverUrl: coverForTrack(track),
      position: audioEl.currentTime || 0,
      duration: Number.isFinite(audioEl.duration) ? audioEl.duration : 0,
    });
  }

  function maybeNotifyTrackChange() {
    const track = typeof state !== 'undefined' ? state.currentTrack : null;
    if (!track?.id || track.id === lastNotifyTrackId) return;
    lastNotifyTrackId = track.id;
    if (typeof document !== 'undefined' && !document.hidden) return;
    api.notify?.({
      title: track.title || 'MXM Music',
      body: primaryArtistLabel(track),
      silent: false,
    });
  }

  function bindAudioDesktopSync() {
    const audioEl = typeof audio !== 'undefined' ? audio : null;
    if (!audioEl) return;
    const events = ['play', 'pause', 'ended', 'loadedmetadata'];
    events.forEach((ev) => {
      audioEl.addEventListener(
        ev,
        () => {
          reportPlaybackToDesktop();
          if (ev === 'play') maybeNotifyTrackChange();
        },
        { passive: true }
      );
    });
    let lastReport = 0;
    audioEl.addEventListener(
      'timeupdate',
      () => {
        const now = Date.now();
        if (now - lastReport < 1000) return;
        lastReport = now;
        reportPlaybackToDesktop();
      },
      { passive: true }
    );
  }

  api.onMediaAction?.((action) => {
    if (action === 'play-pause' && typeof togglePlay === 'function') togglePlay();
    else if (action === 'next' && typeof playNext === 'function') playNext();
    else if (action === 'prev' && typeof playPrev === 'function') playPrev();
  });

  api.onNavigate?.((payload) => {
    if (!payload) return;
    if (payload.action === 'back' && typeof history !== 'undefined') { history.back(); return; }
    if (payload.action === 'forward') { history.forward(); return; }
    const hash = payload.hash;
    if (typeof hash === 'string' && hash.startsWith('#')) {
      if (typeof navigate === 'function') {
        const parts = hash.slice(1).split('/');
        const view = parts[0] || 'home';
        const params = parts[1] ? { id: parts[1] } : {};
        navigate(view, params);
      } else {
        window.location.hash = hash.slice(1);
      }
      api.saveRoute?.(hash);
    }
  });

  api.onOpenSettings?.(() => {
    if (typeof toast === 'function') {
      toast('Настройки desktop — в меню приложения (Файл → Настройки)', 'info');
    }
  });

  const origNavigate = typeof navigate === 'function' ? navigate : null;
  if (origNavigate) {
    window.navigate = function mxmDesktopNavigate(view, params, ...rest) {
      const result = origNavigate.apply(this, [view, params, ...rest]);
      try {
        const id = params?.id;
        const hash = id ? `#${view}/${id}` : `#${view}`;
        api.saveRoute?.(hash);
      } catch (_) {}
      return result;
    };
  }

  window.addEventListener('hashchange', () => {
    try {
      api.saveRoute?.(`#${window.location.hash.slice(1) || 'home'}`);
    } catch (_) {}
  });

  document.addEventListener('click', (e) => {
    const a = e.target?.closest?.('a[href]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
    try {
      const u = new URL(href, window.location.href);
      if (u.origin !== window.location.origin) {
        e.preventDefault();
        api.openExternal?.(u.href);
      }
    } catch (_) {}
  });

  document.addEventListener('DOMContentLoaded', () => {
    bindAudioDesktopSync();
    reportPlaybackToDesktop();
  });

  if (document.readyState !== 'loading') {
    bindAudioDesktopSync();
    reportPlaybackToDesktop();
  }

  window.isMxMDesktopApp = () => true;

  window.mxmDesktopClearBootShell = () => {
    document.getElementById('content')?.classList.remove('mxm-desktop-booting');
  };

  window.mxmDesktopStopPlayback = () => {
    try {
      if (typeof audio !== 'undefined' && audio) {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      }
      if (typeof state !== 'undefined' && state) state.currentTrack = null;
    } catch (_) {}
  };

  window.mxmDesktopShowViewSkeleton = (view) => {
    const content = document.getElementById('content');
    if (!content || typeof getViewLoadingSkeleton !== 'function') return;
    content.innerHTML = getViewLoadingSkeleton(view || 'home');
    content.classList.add('mxm-desktop-booting');
  };
})();
